/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// Header is provided by AppLayout; per-page import removed.
import axios from 'axios';
import './GeradorPerfis.css';
import './GeradorPerfisWizard.css';
import DataTable, { ActionMenu, DataTableColumn } from './DataTable';
import { Icons } from './ActionIcons';
import AppModal from './AppModal';
import AppToastContainer, { AppToastItem } from './AppToastContainer';
import ProgressBar from './ProgressBar';
import StepWizard from './StepWizard';
import * as XLSX from 'xlsx';

// base tables for 'Atividade Simples'
type TableMeta = { key: string; title: string; desc: string; columns: string[] };
const BASE_TABLES: TableMeta[] = [
  { key: 'logs', title: 'Log de acessos cliente', desc: 'Registros de acesso do cliente', columns: ['user', 'code'] },
  { key: 'userxa', title: 'Usuário x Area', desc: 'Vínculo de usuário a áreas', columns: ['user', 'area'] },
  { key: 'userdetails', title: 'Detalhes do usuário', desc: 'Mapeamento do logon para o nome real do usuário', columns: ['user', 'name'] },
  { key: 'codez', title: 'Carga de Tcodes/Apps Z', desc: 'Lista de códigos / aplicações', columns: ['code', 'description'] },
  { key: 'upcasis', title: 'Perfis Atuais (AS-IS)', desc: 'Usuário x Perfil x Código atual do cliente', columns: ['user', 'profile', 'code'] },
];

// tables variant for 'Posição/Cargo Simples' (user x posição)
const POS_TABLES: TableMeta[] = [
  { key: 'logs', title: 'Log de acessos cliente', desc: 'Registros de acesso do cliente', columns: ['user', 'code'] },
  { key: 'userxpos', title: 'Usuário x Posição/Cargo (RH)', desc: 'Vínculo de usuário a posições e níveis (N1..N4)', columns: ['user', 'n1', 'n2', 'n3', 'n4'] },
  { key: 'userdetails', title: 'Detalhes do usuário', desc: 'Mapeamento do logon para o nome real do usuário', columns: ['user', 'name'] },
  { key: 'upcasis', title: 'Perfis Atuais (AS-IS)', desc: 'Usuário x Perfil x Código atual do cliente', columns: ['user', 'profile', 'code'] },
];

// combined variant: Posição Composto + Atividade Simples -> show all 5 uploads
const COMBINED_TABLES: TableMeta[] = [
  { key: 'logs', title: 'Log de acessos cliente', desc: 'Registros de acesso do cliente', columns: ['user', 'code'] },
  { key: 'userxa', title: 'Usuário x Area', desc: 'Vínculo de usuário a áreas', columns: ['user', 'area'] },
  { key: 'userxpos', title: 'Usuário x Posição/Cargo (RH)', desc: 'Vínculo de usuário a posições e níveis (N1..N4)', columns: ['user', 'n1', 'n2', 'n3', 'n4'] },
  { key: 'userdetails', title: 'Detalhes do usuário', desc: 'Mapeamento do logon para o nome real do usuário', columns: ['user', 'name'] },
  { key: 'codez', title: 'Carga de Tcodes/Apps Z', desc: 'Lista de códigos / aplicações', columns: ['code', 'description'] },
  { key: 'upcasis', title: 'Perfis Atuais (AS-IS)', desc: 'Usuário x Perfil x Código atual do cliente', columns: ['user', 'profile', 'code'] },
];

// Helper functions for wizard configuration based on profile model
const getStepCountForModel = (profileModel: string | undefined): number => {
  if (profileModel === 'Posição/Cargo Simples') {
    return 4; // Posição/Cargo Simples tem 4 passos
  }
  return 6; // Atividade Simples e Posição Composto + Atividade Simples agora têm 6 passos
};

const getStepLabelsForModel = (profileModel: string | undefined): string[] => {
  if (profileModel === 'Posição/Cargo Simples') {
    return ['Realizar carga de dados', 'Definir parâmetros gerais', 'Classificar nomenclatura de Perfis', 'Validação e Download'];
  }
  return ['Realizar carga de dados','Definir parâmetros gerais','Classificar ações customizadas','Classificar ações do log','Classificar nomenclatura de Perfis','Validação e Download'];
};

const getStepTitleForModel = (step: number, profileModel: string | undefined): string => {
  if (profileModel === 'Posição/Cargo Simples') {
    if (step === 1) return 'Realizar carga de dados para analise e geração de perfis de acesso';
    if (step === 2) return 'Definir parâmetros gerais de geração';
    if (step === 3) return 'Classificar nomenclatura de Perfis';
    if (step === 4) return 'Validação e Download';
    return `Passo ${step}`;
  }
  
  // Default model titles - Atividade Simples e Posição Composto + Atividade Simples
  if (step === 1) return 'Realizar carga de dados para analise e geração de perfis de acesso';
  if (step === 2) return 'Definir parâmetros gerais de geração';
  if (step === 3) return 'Classificar ações customizadas';
  if (step === 4) return 'Classificar ações do log';
  if (step === 5) return 'Classificar nomenclatura de Perfis';
  if (step === 6) return 'Validação e Download';
  return `Passo ${step}`;
};

// Risk sanitation element interface
interface RiskSanitationElement {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
  conditional?: boolean; // For elements that only show under certain conditions
}

const GeradorPerfisWizard: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const normalizeClientIdValue = (value: any) => decodeURIComponent(String(value || '')).trim().toLowerCase();
  const findExactClientByClientId = useCallback((items: any[], targetClientId?: string) => {
    const normalizedTarget = normalizeClientIdValue(targetClientId);
    if (!normalizedTarget || !Array.isArray(items)) return null;
    return items.find((item: any) => {
      const current = String(item?.clientId ?? item?.id ?? '').trim().toLowerCase();
      return current === normalizedTarget;
    }) || null;
  }, []);
  // theme is handled globally in AppLayout

  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState<AppToastItem[]>([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Carregando dados do cliente...');
  const [client, setClient] = useState<any>(null);
  const theme = localStorage.getItem('phoenixTheme') || 'light';
  // select tables metadata based on client profileModel
  let tables: TableMeta[] = BASE_TABLES;
  if (client && String(client.profileModel) === 'Posição/Cargo Simples') tables = POS_TABLES;
  if (client && String(client.profileModel) === 'Posição Composto + Atividade Simples') tables = COMBINED_TABLES;

  // ensure all required uploads (every table except codez) have at least one record
  const requiredUploadsSatisfied = () => {
    const required = tables.filter(t => t.key !== 'codez' && t.key !== 'userdetails');
    return required.every(t => (indicators[t.key] && indicators[t.key].count > 0));
  };
  const [step, setStep] = useState(1);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const pushToast = useCallback((payload: Omit<AppToastItem, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => [...prev, { id, ...payload }]);
  }, []);
  // Step-1: data load state per table
  const [indicators, setIndicators] = useState<Record<string, any>>({});
  const [previews, setPreviews] = useState<Record<string, any[]>>({});
  // stable local reference for previews['logs'] to satisfy hook dependency checks
  // useMemo ensures a stable reference between renders for hook dependencies
  // depend on the whole `previews` object so eslint can statically verify dependencies
  const logsPreview = useMemo(() => (previews && previews['logs'] ? previews['logs'] : []), [previews]);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [uploadProgressOpen, setUploadProgressOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadProgressTable, setUploadProgressTable] = useState<string>('');
  const [uploadProgressMessage, setUploadProgressMessage] = useState<string>('');
  const [csvDownloadProgress, setCsvDownloadProgress] = useState<Record<string, { loading: boolean; progress: number }>>({});
  const [logSyncLoading, setLogSyncLoading] = useState(false);
  const [logSyncToastOpen, setLogSyncToastOpen] = useState(false);
  const [logSyncResult, setLogSyncResult] = useState<any>(null);
  const [logSyncProgress, setLogSyncProgress] = useState<number>(0);
  const [logSyncProgressMessage, setLogSyncProgressMessage] = useState<string>('');
  const [clearToastOpen, setClearToastOpen] = useState(false);
  const [clearProgress, setClearProgress] = useState<{ table: string; progress: number; message: string; done: boolean }>({ table: '', progress: 0, message: '', done: false });
  const [noLogsAlertOpen, setNoLogsAlertOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewLoadingAll, setPreviewLoadingAll] = useState(false);
  const [previewLoadingProgress, setPreviewLoadingProgress] = useState({ loaded: 0, total: 0 });
  const [dataLoadingProgress, setDataLoadingProgress] = useState<{ table: string; loaded: number; total: number } | null>(null);
  const [asisLoadingProgress, setAsisLoadingProgress] = useState<{ loaded: number; total: number } | null>(null);
  
  const [previewModalTable, setPreviewModalTable] = useState<string | null>(null);
  const [previewPage, setPreviewPage] = useState<number>(1);
  const [previewPageSize, setPreviewPageSize] = useState<number>(Number(localStorage.getItem('gpw_preview_pageSize') || '10'));
  const PREVIEW_PAGE_SIZE = 10;
  const [uploadResultModalOpen, setUploadResultModalOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [dashboardModelView, setDashboardModelView] = useState<string>('visao-geral');
  const [uploadResult, setUploadResult] = useState<{ success: boolean; message: string; tableKey?: string; insertedCount?: number } | null>(null);
  const [confirmClearModalOpen, setConfirmClearModalOpen] = useState(false);
  const [confirmClearTable, setConfirmClearTable] = useState<string | null>(null);
  // IA params (reuse defaults similar to benchmark)
  const [iaModel, setIaModel] = useState<string>(localStorage.getItem('gpw_model') || 'gpt-4o-mini');
  const [iaBatchSize, setIaBatchSize] = useState<number>(Number(localStorage.getItem('gpw_batchSize') || '4'));
  const [iaTemperature, setIaTemperature] = useState<number>(Number(localStorage.getItem('gpw_temperature') || '0.2'));
  const [iaPrompt, setIaPrompt] = useState<string>(localStorage.getItem('gpw_prompt') || `Você é um consultor SAP Sênior e precisa classificar transações/APPs customizados em uma estrutura de processo, subprocesso e atividades.

## Definições Precisas
**Processo (macro)**: Domínio de negócio de alto nível (ex.: Financeiro, Contábil, Compras, Vendas, Produção, TI).

**Subprocesso**: Etapa/área funcional dentro do processo com objetivo claro (ex.: Compras → Requisição, Cotação, Pedido, Recebimento, Devolução, Cadastro de Fornecedor, Aprovação).

**Atividade**: Unidade mínima, coesa e orientada à finalidade de negócio, que poderá se tornar um perfil de acesso.

## Taxonomia Exemplo (Processo → exemplos de Subprocesso)
Financeiro: Contas a Pagar, Contas a Receber, Tesouraria, Gestão Fiscal, Controladoria, Fechamento Financeiro
Contábil: Lançamentos, Conciliações, Fechamento Contábil, Relatórios Contábeis
Compras: Requisição, Cotação, Pedido, Recebimento, Devolução, Cadastro de Fornecedores, Aprovações
Vendas: Cadastro de Clientes, Pedido, Expedição, Faturamento, Devolução, Aprovações
Gestão de Materiais: Administração de Estoques, Transferências, Inventário, Movimentações, Armazenagem
Produção: Planejamento de Produção, Ordens de Produção, Apontamentos, Controle de Qualidade em Produção, Manutenção da Produção
Gestão da Qualidade: Inspeção de Recebimento, Inspeção em Processo, Inspeção de Produto Final, Tratamento de Não Conformidades, Certificação
Gestão de Manutenção: Planejamento Preventivo, Execução Corretiva, Ordens de Manutenção, Gestão de Ativos, Inspeções
Gestão de Projetos: Estrutura Analítica, Planejamento de Custos, Controle Orçamentário, Acompanhamento de Atividades, Alocação de Recursos
Controle de Custos: Centros de Custo, Centros de Lucro, Rateio, Margem de Contribuição, Planejamento Orçamentário
Gestão Tributária: Apuração de Impostos, Obrigações Acessórias, Retenções, Escrituração Fiscal, Emissão de Notas, Relatórios Fiscais
Recursos Humanos: Administração de Pessoal, Folha de Pagamento, Recrutamento, Treinamento, Benefícios, Avaliação de Desempenho
Gestão Comercial: Cadastro de Produtos, Precificação, Promoções, Reposição de Estoques, Relatórios Comerciais
Gestão de Serviços: Chamados de Serviço, Ordens de Serviço, Programação de Equipes, Controle de Atendimento, Relatórios de Serviços
Gestão de Suprimentos: Planejamento de Suprimentos, Administração de Fornecedores, Avaliação de Fornecedores, Compras Estratégicas
Planejamento Empresarial: Previsão de Demanda, Planejamento de Vendas e Operações, Planejamento de Distribuição, Planejamento de Produção
Gestão de Ativos: Registro de Ativos, Depreciação, Inventário Patrimonial, Alienação de Ativos, Relatórios de Ativos
Tecnologia da Informação: Gestão de Acessos, Administração de Sistema, Monitoramento, Auditoria, Transportes, Integrações

## Segregação por camadas (OBRIGATÓRIA)
- Se a transação ou fiori app permite criar/alterar/excluir um mesmo objeto, classifique na mesma atividade nomeada como "Administrar <Objeto>" (ex.: "Administrar Pedido de Compra").
- Se a transação ou fiori app é somente consulta/relatório, classifique como atividade separada nomeada "Visualizar <Objeto>" (ex.: "Visualizar Pedido de Compra").
- Se a transação ou fiori app for de aprovação/liberação, classifique como atividade separada nomeada "Aprovar <Objeto>".
- NUNCA misture "Administrar" com "Visualizar" na mesma atividade.
- Considere o nome da transação para se orientar também a finalidade (CO, MM, SD etc.) e auxiliar na definição do processo que pertence.

**Quando incerto**: use rótulos "Não classificado" no processo, subprocesso e atividade e traga justificativa detalhada do motivo.

RESPONDA SOMENTE O JSON ARRAY COM OS CAMPOS code, process, subprocess, activity, purpose e justification!!

Exemplo de retorno:

  "items": [
    {
      "code": "Z040",
      "process": "Tecnologia da Informação",
      "subprocess": "Administração de Sistema",
      "activity": "Administrar Implementação de BAdI",
      "purpose": "Permite a implementação do Business Add-In (BAdI) /ACCGO/BADI_CAK_PP, utilizado para personalizar documentos contábeis no módulo de Gestão de Caixa (Cash Management) do SAP.",
      "justification": "Esta transação é técnica e específica para a implementação de um BAdI."
    }...`);
  const [codezItems, setCodezItems] = useState<any[]>([]);
  const [editingCodez, setEditingCodez] = useState<any | null>(null);
  const [classifyProgressOpen, setClassifyProgressOpen] = useState(false);
  const [classifyLines, setClassifyLines] = useState<string[]>([]);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const classifyAbortRef = useRef<AbortController | null>(null);
  // selection state for codez table
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [codezPage, setCodezPage] = useState<number>(1);
  const [codezPageSize, setCodezPageSize] = useState<number>(Number(localStorage.getItem('gpw_codez_pageSize') || '25'));
  // Step-4 pagination
  const [step4Page, setStep4Page] = useState<number>(1);
  const [step4PageSize, setStep4PageSize] = useState<number>(Number(localStorage.getItem('gpw_step4_pageSize') || '25'));
  const [step4SortColumn, setStep4SortColumn] = useState<string>('');
  const [step4SortDir, setStep4SortDir] = useState<'asc'|'desc'>('asc');
  // ids that were marked as 'Não classificado' to highlight rows
  const [unclassifiedIds, setUnclassifiedIds] = useState<Set<string>>(new Set());
  // Step-4 selection and classification state
  const [selectedLogCodes, setSelectedLogCodes] = useState<Set<string>>(new Set());
  const [classifiedFromCustom, setClassifiedFromCustom] = useState<Record<string, boolean>>({});
  const [classifyLogsMenuOpen, setClassifyLogsMenuOpen] = useState(false);
  const [classifyBenchmarkMenuOpen, setClassifyBenchmarkMenuOpen] = useState(false);
  const [confirmClassifyOpen, setConfirmClassifyOpen] = useState(false);
  const [confirmClassifyMode, setConfirmClassifyMode] = useState<'custom'|'benchmark'|null>(null);
  const [confirmClassifyScope, setConfirmClassifyScope] = useState<'all'|'selected'>('all');
  const [confirmClearClassificationsOpen, setConfirmClearClassificationsOpen] = useState(false);
  const [benchmarkResults, setBenchmarkResults] = useState<Record<string, { proc: string; sub: string; act: string; fonte: string }>>({});
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  // whether any classification action was triggered in this session (custom/benchmark/manual)
  const [step4HasClassified, setStep4HasClassified] = useState(false);
  // IA log modal state (mirror benchmark structure)
  const [showLogModal, setShowLogModal] = useState(false);
  const [editingLogRow, setEditingLogRow] = useState<any | null>(null);
  const [step4ClassifyOpen, setStep4ClassifyOpen] = useState(false);
  
  // Prefix patterns modal state
  const [prefixPatternsModalOpen, setPrefixPatternsModalOpen] = useState(false);
  const [prefixPatternsSuccessModalOpen, setPrefixPatternsSuccessModalOpen] = useState(false);
  const [riskPrefixModalOpen, setRiskPrefixModalOpen] = useState(false);
  const [riskPrefixSuccessModalOpen, setRiskPrefixSuccessModalOpen] = useState(false);
  
  // Risk sanitation parameters
  const [sanRiskBenchmark, setSanRiskBenchmark] = useState(false);
  const [sanRiskCliente, setSanRiskCliente] = useState(false);
  const [sanRiskAttrModel, setSanRiskAttrModel] = useState(false);
  const [uploadingRiskMatrix, setUploadingRiskMatrix] = useState(false);
  
  // Organizational level derivation parameters
  const [derivationStatus, setDerivationStatus] = useState(false);
  const [derivationOrgLevel, setDerivationOrgLevel] = useState<string>('');
  const [derivationUserStatus, setDerivationUserStatus] = useState(false);
  
  // Organizational level modal states
  const [orgLevelModalOpen, setOrgLevelModalOpen] = useState(false);
  const [orgLevelsData, setOrgLevelsData] = useState<any[]>([]);
  const [selectedOrgLevel, setSelectedOrgLevel] = useState<string>('');
  const [loadingOrgLevels, setLoadingOrgLevels] = useState(false);
  
  // Organizational level values states
  const [orgLevelValues, setOrgLevelValues] = useState<string[]>([]);
  const [orgLevelValuesModalOpen, setOrgLevelValuesModalOpen] = useState(false);
  
  // OrgLevel values modal search and pagination states
  const [orgLevelValuesSearchQ, setOrgLevelValuesSearchQ] = useState<string>('');
  const [orgLevelValuesSearchColumn, setOrgLevelValuesSearchColumn] = useState<string>('');
  const [orgLevelValuesPage, setOrgLevelValuesPage] = useState<number>(1);
  const [orgLevelValuesPageSize, setOrgLevelValuesPageSize] = useState<number>(25);
  
  // CSV upload states for organizational level values
  const [orgLevelUploadLoading, setOrgLevelUploadLoading] = useState(false);
  
  // User-orglevel mapping states
  const [userOrgLevelMappings, setUserOrgLevelMappings] = useState<{user: string, value: string}[]>([]);
  const [userOrgLevelUploadLoading, setUserOrgLevelUploadLoading] = useState(false);
  const [userOrgLevelModalOpen, setUserOrgLevelModalOpen] = useState(false);
  
  // UserOrgLevel mappings modal search and pagination states
  const [userOrgLevelSearchQ, setUserOrgLevelSearchQ] = useState<string>('');
  const [userOrgLevelSearchColumn, setUserOrgLevelSearchColumn] = useState<string>('');
  const [userOrgLevelPage, setUserOrgLevelPage] = useState<number>(1);
  const [userOrgLevelPageSize, setUserOrgLevelPageSize] = useState<number>(25);
  
  // User-orglevel validation states
  const [userOrgLevelValidationIssues, setUserOrgLevelValidationIssues] = useState<{
    invalidValues: {user: string, value: string}[];
    missingUsers: string[];
    unmappedUsers: string[];
    hasIssues: boolean;
  }>({ invalidValues: [], missingUsers: [], unmappedUsers: [], hasIssues: false });
  const [userOrgLevelDiagnosticModalOpen, setUserOrgLevelDiagnosticModalOpen] = useState(false);
  
  // User-Area diagnostic modal and validation state
  const [userAreaValidationIssues, setUserAreaValidationIssues] = useState<{
    usersInAreaNotInLogs: string[];
    usersInLogsNotInArea: string[];
    usersWithMultipleAreas: {user: string, areas: string[]}[];
    hasIssues: boolean;
  }>({ usersInAreaNotInLogs: [], usersInLogsNotInArea: [], usersWithMultipleAreas: [], hasIssues: false });
  const [userAreaDiagnosticModalOpen, setUserAreaDiagnosticModalOpen] = useState(false);
  
  // User-Position diagnostic modal and validation state
  const [userPositionValidationIssues, setUserPositionValidationIssues] = useState<{
    usersInPositionNotInLogs: string[];
    usersInLogsNotInPosition: string[];
    usersWithMultiplePositions: {user: string, positions: string[]}[];
    hasIssues: boolean;
  }>({ usersInPositionNotInLogs: [], usersInLogsNotInPosition: [], usersWithMultiplePositions: [], hasIssues: false });
  const [userPositionDiagnosticModalOpen, setUserPositionDiagnosticModalOpen] = useState(false);
  
  // Total users count from logs (used in diagnostic modals)
  const [totalUsersInLogs, setTotalUsersInLogs] = useState(0);
  const [totalUsersInArea, setTotalUsersInArea] = useState(0);
  const [totalUsersInPosition, setTotalUsersInPosition] = useState(0);
  
  // Diagnostic modals pagination and loading states
  const [diagnosticLoadingUserArea, setDiagnosticLoadingUserArea] = useState(false);
  const [diagnosticLoadingUserPosition, setDiagnosticLoadingUserPosition] = useState(false);
  const [diagnosticLoadingUserOrgLevel, setDiagnosticLoadingUserOrgLevel] = useState(false);
  
  // Diagnostic pagination states
  const [userAreaDiagPage1, setUserAreaDiagPage1] = useState(1); // usersInAreaNotInLogs
  const [userAreaDiagPage2, setUserAreaDiagPage2] = useState(1); // usersInLogsNotInArea
  const [userAreaDiagPage3, setUserAreaDiagPage3] = useState(1); // usersWithMultipleAreas
  const [userPositionDiagPage1, setUserPositionDiagPage1] = useState(1); // usersInPositionNotInLogs
  const [userPositionDiagPage2, setUserPositionDiagPage2] = useState(1); // usersInLogsNotInPosition
  const [userPositionDiagPage3, setUserPositionDiagPage3] = useState(1); // usersWithMultiplePositions
  const [userOrgLevelDiagPage1, setUserOrgLevelDiagPage1] = useState(1); // invalidValues
  const [userOrgLevelDiagPage2, setUserOrgLevelDiagPage2] = useState(1); // missingUsers
  const [userOrgLevelDiagPage3, setUserOrgLevelDiagPage3] = useState(1); // unmappedUsers
  const [diagnosticPageSize, setDiagnosticPageSize] = useState<number>(Number(localStorage.getItem('gpw_diagnostic_pageSize') || '10'));
  
  // Organizational derivation alert and confirmation modals
  const [orgDerivationAlertModalOpen, setOrgDerivationAlertModalOpen] = useState(false);
  const [orgDerivationAlertMessage, setOrgDerivationAlertMessage] = useState('');
  const [orgDerivationAlertType, setOrgDerivationAlertType] = useState<'error' | 'success' | 'info'>('info');
  const [orgDerivationConfirmModalOpen, setOrgDerivationConfirmModalOpen] = useState(false);
  const [orgDerivationConfirmMessage, setOrgDerivationConfirmMessage] = useState('');
  const [orgDerivationConfirmAction, setOrgDerivationConfirmAction] = useState<(() => void) | null>(null);
  
  // Risk matrix visualization states
  const [riskMatrixData, setRiskMatrixData] = useState<any[]>([]);
  const [riskMatrixModalOpen, setRiskMatrixModalOpen] = useState(false);
  const [riskMatrixFilterColumn, setRiskMatrixFilterColumn] = useState<string>('');
  const [riskMatrixFilterValue, setRiskMatrixFilterValue] = useState<string>('');
  
  // Nomenclature classification step states
  const [nomenclatureMappingData, setNomenclatureMappingData] = useState<any[]>([]);
  const [nomenclatureMappingLoading, setNomenclatureMappingLoading] = useState(false);
  
  // Search and sorting states for nomenclature mapping
  const [nomenclatureSearchQ, setNomenclatureSearchQ] = useState<string>('');
  const [nomenclatureSearchColumn, setNomenclatureSearchColumn] = useState<string>('');
  const [nomenclatureSortColumn, setNomenclatureSortColumn] = useState<string>('');
  const [nomenclatureSortDirection, setNomenclatureSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Pagination states for nomenclature mapping
  const [nomenclaturePage, setNomenclaturePage] = useState<number>(1);
  const [nomenclaturePageSize, setNomenclaturePageSize] = useState<number>(Number(localStorage.getItem('gpw_nomenclature_pageSize') || '25'));

  // Isolated search/sort/pagination states for derived profiles table
  const [derivedSearchQ, setDerivedSearchQ] = useState<string>('');
  const [derivedSearchColumn, setDerivedSearchColumn] = useState<string>('');
  const [derivedSortColumn, setDerivedSortColumn] = useState<string>('');
  const [derivedSortDirection, setDerivedSortDirection] = useState<'asc' | 'desc'>('asc');
  const [derivedPage, setDerivedPage] = useState<number>(1);
  const [derivedPageSize, setDerivedPageSize] = useState<number>(Number(localStorage.getItem('gpw_derived_pageSize') || '25'));

  // Search/sort/pagination states for composite positions table
  const [compositeSearchQ, setCompositeSearchQ] = useState<string>('');
  const [compositeSearchColumn, setCompositeSearchColumn] = useState<string>('');
  const [compositeSortColumn, setCompositeSortColumn] = useState<string>('');
  const [compositeSortDirection, setCompositeSortDirection] = useState<'asc' | 'desc'>('asc');
  const [compositePage, setCompositePage] = useState<number>(1);
  const [compositePageSize, setCompositePageSize] = useState<number>(Number(localStorage.getItem('gpw_composite_pageSize') || '25'));
  
  // Accordion state for nomenclature classification sections
  const [nomenclatureSectionExpanded, setNomenclatureSectionExpanded] = useState({
    visaoConsolidada: false, // Classificar ações e gerar nomenclatura de perfis simples - sempre inicia recolhida
    perfisDerivados: false, // Gerar nomenclatura de perfis derivados - sempre inicia recolhida
    perfisCompostos: false  // Gerar nomenclatura de perfis compostos (para modelo combinado)
  });
  
  // Risk classification states
  const [riskClassificationData, setRiskClassificationData] = useState<{[code: string]: boolean}>({});
  const [isRiskClassificationEnabled, setIsRiskClassificationEnabled] = useState<boolean>(false);
  const [showRiskAlert, setShowRiskAlert] = useState<boolean>(false);
  
  // Master/Derived classification states
  const [masterDerivedClassificationData, setMasterDerivedClassificationData] = useState<{[code: string]: boolean}>({});
  const [masterDerivedPropagatedData, setMasterDerivedPropagatedData] = useState<{[code: string]: boolean}>({});
  const [isMasterDerivedClassificationEnabled, setIsMasterDerivedClassificationEnabled] = useState<boolean>(false);
  
  // Consolidation states
  const [hasConsolidationData, setHasConsolidationData] = useState<boolean>(false);
  const [consolidationLoading, setConsolidationLoading] = useState<boolean>(false);
  const [savingProgress, setSavingProgress] = useState<{ current: number; total: number; message: string } | null>(null);
  const [savingModalOpen, setSavingModalOpen] = useState<boolean>(false);
  const savingCancelRef = useRef<boolean>(false);
  // Filtered master records for perfis derivados visibility and display
  const [masterRecords, setMasterRecords] = useState<any[]>([]);
  const [masterRecordsLoading, setMasterRecordsLoading] = useState<boolean>(false);
  
  // Consolidation alert modal states
  const [consolidationAlertOpen, setConsolidationAlertOpen] = useState<boolean>(false);
  const [consolidationAlertMessage, setConsolidationAlertMessage] = useState<string>('');
  const [consolidationAlertType, setConsolidationAlertType] = useState<'success' | 'error' | 'info'>('info');
  
  // Consolidation confirm modal states
  const [consolidationConfirmOpen, setConsolidationConfirmOpen] = useState<boolean>(false);
  const [consolidationConfirmMessage, setConsolidationConfirmMessage] = useState<string>('');
  const [consolidationConfirmAction, setConsolidationConfirmAction] = useState<(() => void) | null>(null);

  // Step 6: Validation rows (Usuário x Área x Processo x Subprocesso x Atividade x Código x Perfil)
  const [validationRows, setValidationRows] = useState<any[]>([]);
  const [validationLoading, setValidationLoading] = useState<boolean>(false);
  const [validationSaving, setValidationSaving] = useState<boolean>(false);
  const [validationDeleting, setValidationDeleting] = useState<boolean>(false);
  const [validationLoadedFromDb, setValidationLoadedFromDb] = useState<boolean>(false);
  const [validationSavedLoadInProgress, setValidationSavedLoadInProgress] = useState<boolean>(false);
  const [validationSavedLoadMessage, setValidationSavedLoadMessage] = useState<string>('');
  const [validationSavedLoadCurrent, setValidationSavedLoadCurrent] = useState<number>(0);
  const [validationSavedLoadTotal, setValidationSavedLoadTotal] = useState<number>(0);
  const [validationPage, setValidationPage] = useState<number>(1);
  // AS-IS data from profilegen-upc-asis
  const [upcasisRows, setUpcasisRows] = useState<any[]>([]);
  const [validationPageSize, setValidationPageSize] = useState<number>(Number(localStorage.getItem('gpw_validation_pageSize') || '25'));
  // Search/sort for validation table
  const [validationSearchQ, setValidationSearchQ] = useState<string>('');
  const [validationSearchColumn, setValidationSearchColumn] = useState<string>('');
  const [validationSortColumn, setValidationSortColumn] = useState<string>('');
  const [validationSortDirection, setValidationSortDirection] = useState<'asc' | 'desc'>('asc');
  const [volumetriaTableSortColumn, setVolumetriaTableSortColumn] = useState<'perfil' | 'transactionsCount' | 'usersCount'>('transactionsCount');
  const [volumetriaTableSortDirection, setVolumetriaTableSortDirection] = useState<'asc' | 'desc'>('desc');
  const [volumetriaTablePage, setVolumetriaTablePage] = useState<number>(1);
  const [volumetriaTablePageSize, setVolumetriaTablePageSize] = useState<number>(Number(localStorage.getItem('gpw_volumetria_pageSize') || '10'));
  const [volumetriaUniverseProfiles, setVolumetriaUniverseProfiles] = useState<Array<{
    perfil: string;
    transactionsCount: number;
    isRisk: boolean;
    sampleCode: string;
    n1: string;
    n2: string;
    n3: string;
    n4: string;
    area: string;
    processo: string;
    subprocesso: string;
    atividade: string;
  }>>([]);
  
  // Master/Derived classification states
  const [masterDerivedLoading, setMasterDerivedLoading] = useState<boolean>(false);
  
  // Step 6 validation loaded state
  const [validationDataLoaded, setValidationDataLoaded] = useState<boolean>(false);
  
  // Volumetria indicators from consolidation
  const [volumetriaIndicators, setVolumetriaIndicators] = useState<{
    totalProfiles: number;
    currentTotalProfiles: number;
    compositeProfiles: number;
    riskProfiles: number;
    masterProfiles: number;
    derivedProfiles: number;
    simpleProfiles: number;
    currentProfiles1Transaction: number;
    currentProfiles2to10Transactions: number;
    currentProfiles11to50Transactions: number;
    currentProfiles51to100Transactions: number;
    currentProfilesOver100Transactions: number;
    profiles1Transaction: number;
    profiles2to10Transactions: number;
    profiles11to50Transactions: number;
    profiles51to100Transactions: number;
    profilesOver100Transactions: number;
  }>({
    totalProfiles: 0,
    currentTotalProfiles: 0,
    compositeProfiles: 0,
    riskProfiles: 0,
    masterProfiles: 0,
    derivedProfiles: 0,
    simpleProfiles: 0,
    currentProfiles1Transaction: 0,
    currentProfiles2to10Transactions: 0,
    currentProfiles11to50Transactions: 0,
    currentProfiles51to100Transactions: 0,
    currentProfilesOver100Transactions: 0,
    profiles1Transaction: 0,
    profiles2to10Transactions: 0,
    profiles11to50Transactions: 0,
    profiles51to100Transactions: 0,
    profilesOver100Transactions: 0
  });
  
  // Dashboard alert modal states
  const [dashboardAlertOpen, setDashboardAlertOpen] = useState<boolean>(false);
  
  // Pie chart tooltip state
  const [pieTooltipAsIs, setPieTooltipAsIs] = useState<{ show: boolean; text: string; x: number; y: number }>({ 
    show: false, 
    text: '', 
    x: 0, 
    y: 0 
  });
  const [pieTooltipToBe, setPieTooltipToBe] = useState<{ show: boolean; text: string; x: number; y: number }>({ 
    show: false, 
    text: '', 
    x: 0, 
    y: 0 
  });
  const [pieAnimationTriggered, setPieAnimationTriggered] = useState(false);
  const [asisIgnoreDuplicates, setAsisIgnoreDuplicates] = useState(false);
  
  // Dashboard filter states
  const [dashboardFilterPosition, setDashboardFilterPosition] = useState<string>('');
  const [dashboardFilterArea, setDashboardFilterArea] = useState<string>('');
  const [dashboardFilterUser, setDashboardFilterUser] = useState<string>('');
  
  // Detailed attribution analysis states
  const [detailedAnalysisUsers, setDetailedAnalysisUsers] = useState<string[]>([]);
  const [expandedProfiles, setExpandedProfiles] = useState<Set<string>>(new Set());
  const [expandedCompositeProfiles, setExpandedCompositeProfiles] = useState<Set<string>>(new Set());
  const [showProfileDetails, setShowProfileDetails] = useState<boolean>(false);
  const [detailedProfilesView, setDetailedProfilesView] = useState<'detailed' | 'clean'>('clean');
  const [userSearchFilter, setUserSearchFilter] = useState<string>('');
  const [userDropdownOpen, setUserDropdownOpen] = useState<boolean>(false);
  const [profileTypesMap, setProfileTypesMap] = useState<Map<string, {risk: boolean, master: boolean, derived: boolean}>>(new Map());
  
  // AI Conversion Modal states
  const [aiConversionModalOpen, setAiConversionModalOpen] = useState<boolean>(false);
  const [aiConversionActiveTab, setAiConversionActiveTab] = useState<'area' | 'processo' | 'subprocesso' | 'atividade' | 'n1' | 'n2' | 'n3' | 'n4'>('area');
  const [aiConversionMode, setAiConversionMode] = useState<'simple' | 'composite'>('simple');
  const [aiConversionData, setAiConversionData] = useState<{[key: string]: any[]}>({
    area: [],
    processo: [],
    subprocesso: [],
    atividade: [],
    n1: [],
    n2: [],
    n3: [],
    n4: []
  });
  
  // Client AI conversions state was removed; loadSavedAiResults now returns fresh lookup data
  
  // AI Conversion Modal pagination and search states
  const [aiConversionPage, setAiConversionPage] = useState(1);
  const [aiConversionPageSize, setAiConversionPageSize] = useState(10);
  const [aiConversionSearchQ, setAiConversionSearchQ] = useState('');
  const [aiConversionSearchColumn, setAiConversionSearchColumn] = useState('');
  
  // AI Processing states
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiProcessingProgress, setAiProcessingProgress] = useState(0);
  const [aiProcessingStatus, setAiProcessingStatus] = useState('');
  const [aiModalLoading, setAiModalLoading] = useState(false);
  const [aiModalLoadingProgress, setAiModalLoadingProgress] = useState(0);
  const [aiModalLoadingMessage, setAiModalLoadingMessage] = useState('');
  const [showOnlyAiDuplicates, setShowOnlyAiDuplicates] = useState(false);
  const [showOnlyAiUnconverted, setShowOnlyAiUnconverted] = useState(false);
  const [aiLogModalOpen, setAiLogModalOpen] = useState(false);
  const [aiLogData, setAiLogData] = useState<any>(null);
  
  // AI Edit functionality states
  const [aiEditingItems, setAiEditingItems] = useState<{[key: string]: string}>({});
  const [aiConversionSelected, setAiConversionSelected] = useState<{[key: string]: boolean}>({});
  const [aiConfirmModalOpen, setAiConfirmModalOpen] = useState(false);
  const [aiConfirmMessage, setAiConfirmMessage] = useState('');
  const [aiConfirmCallback, setAiConfirmCallback] = useState<(() => void) | null>(null);
  
  // Clear AI conversions states
  const [clearAiConversionsLoading, setClearAiConversionsLoading] = useState(false);
  const ALGORITHMIC_DEFAULT_MAX_CHARS = 3;
  const AI_CONVERSION_MAX_BATCH_SIZE = 50;
  
  const [riskMatrixPage, setRiskMatrixPage] = useState(1);
  const [loadingRiskMatrix, setLoadingRiskMatrix] = useState(false);
  
  // Risk sanitation elements order and drag state
  const [riskSanitationElements, setRiskSanitationElements] = useState<RiskSanitationElement[]>([]);
  
  const [prefixPatterns, setPrefixPatterns] = useState({
    simples: '',
    mestre: '',
    derivado: '',
    risco: ''
  });
  const [riskPrefixPattern, setRiskPrefixPattern] = useState('');
  const [riskPrefixPatternMestre, setRiskPrefixPatternMestre] = useState('');
  const [riskPrefixPatternDerivado, setRiskPrefixPatternDerivado] = useState('');
  const [step4ClassifyText, setStep4ClassifyText] = useState('');
  const [step4ClassifyProgress, setStep4ClassifyProgress] = useState(0);
  const [step4ClassifyMessage, setStep4ClassifyMessage] = useState('');
  const [clearClassificationsProgressOpen, setClearClassificationsProgressOpen] = useState(false);
  const [clearClassificationsProgress, setClearClassificationsProgress] = useState(0);
  const [clearClassificationsMessage, setClearClassificationsMessage] = useState('');
  
  // Step 2: Profile naming constructor modals
  const [step2ErrorModalOpen, setStep2ErrorModalOpen] = useState(false);
  const [step2ErrorMessage, setStep2ErrorMessage] = useState('');
  const [step2SuccessModalOpen, setStep2SuccessModalOpen] = useState(false);
  const [step2SuccessMessage, setStep2SuccessMessage] = useState('');
  
  // Step 2: Profile naming constructor state
  type NameElement = {
    id: string;
    type: 'prefix' | 'suffix' | 'data' | 'separator';
    value: string;
    label: string;
    maxLength: number;
    displayChar: string; // Character to represent the element in preview
  };
  const [constructorElements, setConstructorElements] = useState<NameElement[]>([]);
  const [maxTotalLength, setMaxTotalLength] = useState(30);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  // Centralized max chars for profile name (persisted to profilegen-clients.pattern_name_max_char)
  const [patternNameMaxChar, setPatternNameMaxChar] = useState<number | null>(null);
  const persistTimeoutRef = useRef<any>(null);

  const schedulePersistPatternNameMaxChar = (value: number) => {
    // debounce multiple quick changes
    try { if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current); } catch (_) {}
    persistTimeoutRef.current = setTimeout(async () => {
      try {
        await persistPatternNameMaxChar(value);
      } catch (err) {
        console.warn('Erro ao persistir pattern_name_max_char (scheduled)', err);
      }
    }, 700);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { try { if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current); } catch (_) {} };
  }, []);

  // Keep individual max states in sync when centralized value changes
  useEffect(() => {
    if (patternNameMaxChar != null && !Number.isNaN(Number(patternNameMaxChar))) {
      setMaxTotalLength(patternNameMaxChar);
      setCompositeMaxTotalLength(patternNameMaxChar);
      setRiskMaxTotalLength(patternNameMaxChar);
    }
  }, [patternNameMaxChar]);

  useEffect(() => {
    const calculateAsisTransactionBuckets = () => {
      const asisProfileTransactionsMap = new Map<string, Set<string>>();

      upcasisRows.forEach((row: any) => {
        const profileName = String(row.profile || row.profilename || row.profile_name || row.composite_name || '').trim().toUpperCase();
        const code = String(row.code || row.codigo || '').trim().toUpperCase();

        if (!profileName || profileName === '-' || !code || code === '-') {
          return;
        }

        if (!asisProfileTransactionsMap.has(profileName)) {
          asisProfileTransactionsMap.set(profileName, new Set());
        }

        asisProfileTransactionsMap.get(profileName)!.add(code);
      });

      let currentProfiles1Transaction = 0;
      let currentProfiles2to10Transactions = 0;
      let currentProfiles11to50Transactions = 0;
      let currentProfiles51to100Transactions = 0;
      let currentProfilesOver100Transactions = 0;

      asisProfileTransactionsMap.forEach((codes) => {
        const txCount = codes.size;
        if (txCount === 1) currentProfiles1Transaction++;
        else if (txCount >= 2 && txCount <= 10) currentProfiles2to10Transactions++;
        else if (txCount >= 11 && txCount <= 50) currentProfiles11to50Transactions++;
        else if (txCount >= 51 && txCount <= 100) currentProfiles51to100Transactions++;
        else if (txCount > 100) currentProfilesOver100Transactions++;
      });

      return {
        currentTotalProfiles: asisProfileTransactionsMap.size,
        currentProfiles1Transaction,
        currentProfiles2to10Transactions,
        currentProfiles11to50Transactions,
        currentProfiles51to100Transactions,
        currentProfilesOver100Transactions
      };
    };

    const asisBuckets = calculateAsisTransactionBuckets();
    const emptyIndicators = {
      totalProfiles: 0,
      currentTotalProfiles: asisBuckets.currentTotalProfiles,
      compositeProfiles: 0,
      riskProfiles: 0,
      masterProfiles: 0,
      derivedProfiles: 0,
      simpleProfiles: 0,
      currentProfiles1Transaction: asisBuckets.currentProfiles1Transaction,
      currentProfiles2to10Transactions: asisBuckets.currentProfiles2to10Transactions,
      currentProfiles11to50Transactions: asisBuckets.currentProfiles11to50Transactions,
      currentProfiles51to100Transactions: asisBuckets.currentProfiles51to100Transactions,
      currentProfilesOver100Transactions: asisBuckets.currentProfilesOver100Transactions,
      profiles1Transaction: 0,
      profiles2to10Transactions: 0,
      profiles11to50Transactions: 0,
      profiles51to100Transactions: 0,
      profilesOver100Transactions: 0
    };

    if (!validationDataLoaded || !clientId) {
      setVolumetriaUniverseProfiles([]);
      setVolumetriaIndicators(emptyIndicators);
      return;
    }

    const calculateVolumetriaIndicators = async () => {
      try {
        const profileModel = String(client?.profileModel || '');
        const isPositionComposite = profileModel === 'Posição Composto + Atividade Simples';
        const profileUniverseMap = new Map<string, {
          transactions: Set<string>;
          sampleCode: string;
          isRisk: boolean;
          n1: string;
          n2: string;
          n3: string;
          n4: string;
          area: string;
          processo: string;
          subprocesso: string;
          atividade: string;
        }>();
        const compositeTransactionsMap = new Map<string, Set<string>>();

        const pickValue = (...values: any[]) => {
          for (const value of values) {
            const normalized = String(value || '').trim();
            if (normalized && normalized !== '-') return normalized;
          }
          return '';
        };

        const normalizeName = (value: any) => String(value || '').trim().toUpperCase();
        const isValidName = (value: string) => Boolean(value && value !== '-');
        const riskPrefixes = [
          String(client?.pattern_r_prefixo_risco || '').trim().toUpperCase(),
          String(client?.pattern_r_prefixo_risco_mestre || '').trim().toUpperCase(),
          String(client?.pattern_r_prefixo_risco_derivado || '').trim().toUpperCase()
        ].filter(Boolean);

        const isRiskByName = (profileName: string) => {
          if (!profileName) return false;
          if (riskPrefixes.some((prefix) => profileName.startsWith(prefix))) return true;
          return profileName.startsWith('ZR:') || profileName.startsWith('ZR-') || profileName.startsWith('ZR_');
        };

        validationRows.forEach((row: any) => {
          const profilename = normalizeName(row.perfil || row.profile || row.profilename);
          const compositeName = normalizeName(row.perfil_composto || row.composite_name);
          const code = normalizeName(row.codigo || row.code);

          if (isValidName(profilename)) {
            const typeInfo = profileTypesMap.get(profilename) || { risk: false, master: false, derived: false };
            const isRisk = row?.risk === true || row?.risk === 'true' || row?.risk === 1 || typeInfo.risk || isRiskByName(profilename);

            const universeCurrent = profileUniverseMap.get(profilename) || {
              transactions: new Set<string>(),
              sampleCode: '',
              isRisk: false,
              n1: '',
              n2: '',
              n3: '',
              n4: '',
              area: '',
              processo: '',
              subprocesso: '',
              atividade: ''
            };

            if (isValidName(code)) {
              universeCurrent.transactions.add(code);
              universeCurrent.sampleCode = universeCurrent.sampleCode || code;
            }

            universeCurrent.isRisk = universeCurrent.isRisk || isRisk;
            universeCurrent.n1 = universeCurrent.n1 || pickValue(row.n1);
            universeCurrent.n2 = universeCurrent.n2 || pickValue(row.n2);
            universeCurrent.n3 = universeCurrent.n3 || pickValue(row.n3);
            universeCurrent.n4 = universeCurrent.n4 || pickValue(row.n4);
            universeCurrent.area = universeCurrent.area || pickValue(row.area_perfil, row.area);
            universeCurrent.processo = universeCurrent.processo || pickValue(row.processo, row.process);
            universeCurrent.subprocesso = universeCurrent.subprocesso || pickValue(row.subprocesso, row.subprocess);
            universeCurrent.atividade = universeCurrent.atividade || pickValue(row.atividade, row.activity);

            profileUniverseMap.set(profilename, universeCurrent);
          }

          if (isPositionComposite && isValidName(compositeName) && compositeName !== 'DIRECT_ATTRIBUTION') {
            const existingCompositeCodes = compositeTransactionsMap.get(compositeName) || new Set<string>();
            if (isValidName(code)) {
              existingCompositeCodes.add(code);
            }
            compositeTransactionsMap.set(compositeName, existingCompositeCodes);
          }
        });

        let profiles1Tx = 0;
        let profiles2to10Tx = 0;
        let profiles11to50Tx = 0;
        let profiles51to100Tx = 0;
        let profilesOver100Tx = 0;

        const addBucket = (txCount: number) => {
          if (txCount === 1) profiles1Tx++;
          else if (txCount >= 2 && txCount <= 10) profiles2to10Tx++;
          else if (txCount >= 11 && txCount <= 50) profiles11to50Tx++;
          else if (txCount >= 51 && txCount <= 100) profiles51to100Tx++;
          else if (txCount > 100) profilesOver100Tx++;
        };

        // Build transaction buckets from validationRows (for the table)
        // Only count simple profiles (including risk, master, derived), NOT composite profiles
        // Composite profiles don't have direct transactions - they're just containers
        profileUniverseMap.forEach((data) => {
          addBucket(data.transactions.size);
        });

        // Build indicator counts from ALL mapped profiles (profileTypesMap + validationRows type info)
        // This ensures we get risk/master/derived counts even if profileTypesMap is empty
        const allProfileTypesMap = new Map<string, { risk: boolean; master: boolean; derived: boolean }>(profileTypesMap);
        
        validationRows.forEach((row: any) => {
          const profilename = normalizeName(row.perfil || row.profile || row.profilename);
          if (isValidName(profilename)) {
            const typeInfo = allProfileTypesMap.get(profilename) || { risk: false, master: false, derived: false };
            const isRisk = row?.risk === true || row?.risk === 'true' || row?.risk === 1 || typeInfo.risk || isRiskByName(profilename);
            const isMaster = row?.master === true || row?.master === 'true' || row?.master === 1 || typeInfo.master;
            const isDerived = row?.derived === true || row?.derived === 'true' || row?.derived === 1 || typeInfo.derived;
            
            allProfileTypesMap.set(profilename, {
              risk: typeInfo.risk || isRisk,
              master: typeInfo.master || isMaster,
              derived: typeInfo.derived || isDerived
            });
          }
        });

        let indicatorRiskCount = 0;
        let indicatorMasterCount = 0;
        let indicatorDerivedCount = 0;
        let indicatorSimpleCount = 0;

        allProfileTypesMap.forEach((typeInfo) => {
          if (typeInfo.risk) indicatorRiskCount++;
          if (typeInfo.master) indicatorMasterCount++;
          if (typeInfo.derived) indicatorDerivedCount++;
          if (!typeInfo.risk && !typeInfo.master && !typeInfo.derived) indicatorSimpleCount++;
        });

        // Count all distinct composite profiles from validationRows
        const compositeNamesSet = new Set<string>();
        validationRows.forEach((row: any) => {
          const compositeName = normalizeName(row.perfil_composto || row.composite_name);
          if (isValidName(compositeName) && compositeName !== 'DIRECT_ATTRIBUTION') {
            compositeNamesSet.add(compositeName);
          }
        });
        const indicatorCompositeCount = compositeNamesSet.size;

        // Build universe rows for the table (showing assigned users and transactions)
        const universeRows = Array.from(profileUniverseMap.entries()).map(([perfil, data]) => ({
          perfil,
          transactionsCount: data.transactions.size,
          isRisk: data.isRisk,
          sampleCode: data.sampleCode,
          n1: data.n1,
          n2: data.n2,
          n3: data.n3,
          n4: data.n4,
          area: data.area,
          processo: data.processo,
          subprocesso: data.subprocesso,
          atividade: data.atividade
        }));

        setVolumetriaUniverseProfiles(universeRows);
        setVolumetriaIndicators({
          totalProfiles: allProfileTypesMap.size + indicatorCompositeCount,
          currentTotalProfiles: asisBuckets.currentTotalProfiles,
          compositeProfiles: indicatorCompositeCount,
          riskProfiles: indicatorRiskCount,
          masterProfiles: indicatorMasterCount,
          derivedProfiles: indicatorDerivedCount,
          simpleProfiles: indicatorSimpleCount,
          currentProfiles1Transaction: asisBuckets.currentProfiles1Transaction,
          currentProfiles2to10Transactions: asisBuckets.currentProfiles2to10Transactions,
          currentProfiles11to50Transactions: asisBuckets.currentProfiles11to50Transactions,
          currentProfiles51to100Transactions: asisBuckets.currentProfiles51to100Transactions,
          currentProfilesOver100Transactions: asisBuckets.currentProfilesOver100Transactions,
          profiles1Transaction: profiles1Tx,
          profiles2to10Transactions: profiles2to10Tx,
          profiles11to50Transactions: profiles11to50Tx,
          profiles51to100Transactions: profiles51to100Tx,
          profilesOver100Transactions: profilesOver100Tx
        });
      } catch (error) {
        console.error('[Volumetria] Error calculating indicators:', error);
        setVolumetriaUniverseProfiles([]);
        setVolumetriaIndicators(emptyIndicators);
      }
    };

    calculateVolumetriaIndicators();
  }, [validationDataLoaded, clientId, client, upcasisRows, validationRows, profileTypesMap]);

  // Build validation rows using same logic as fetchNomenclatureMappingData's resulting mapping
  const fetchAllTableRows = useCallback(async (
    tableKey: string,
    chunkSize: number = 5000,
    progress?: { enabled: boolean; label: string; from: number; to: number }
  ): Promise<any[]> => {
    if (!clientId) return [];

    const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const indicatorsRes = await axios.get(
      `${baseUrl}/api/profilegen-data/${tableKey}/${clientId}?indicatorsOnly=true`,
      { withCredentials: true }
    );

    const totalCount = indicatorsRes.data?.indicators?.count || 0;
    if (!totalCount) {
      if (progress?.enabled) {
        setValidationSavedLoadCurrent(Math.round(progress.to));
        setValidationSavedLoadTotal(100);
        setValidationSavedLoadMessage(`${progress.label}: nenhum registro encontrado.`);
      }
      return [];
    }

    const totalPages = Math.ceil(totalCount / chunkSize);
    const rows: any[] = [];

    for (let page = 1; page <= totalPages; page++) {
      const res = await axios.get(
        `${baseUrl}/api/profilegen-data/${tableKey}/${clientId}?page=${page}&limit=${chunkSize}`,
        { withCredentials: true }
      );
      const items = res.data?.items || [];
      rows.push(...items);

      if (progress?.enabled) {
        const ratio = totalCount > 0 ? rows.length / totalCount : 1;
        const current = Math.round(progress.from + (progress.to - progress.from) * Math.min(1, Math.max(0, ratio)));
        setValidationSavedLoadCurrent(current);
        setValidationSavedLoadTotal(100);
        setValidationSavedLoadMessage(
          `${progress.label}: ${rows.length.toLocaleString()} / ${totalCount.toLocaleString()} registros (${Math.round(ratio * 100)}%)`
        );
      }
    }

    return rows;
  }, [clientId]);

  const loadSavedFinalMappingRows = useCallback(async (options?: { withProgress?: boolean }): Promise<any[]> => {
    if (!clientId) return [];

    const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const chunkSize = 100000;
    const showProgress = options?.withProgress === true;

    if (showProgress) {
      setValidationSavedLoadInProgress(true);
      setValidationSavedLoadMessage('Verificando dados salvos no banco...');
      setValidationSavedLoadCurrent(0);
      setValidationSavedLoadTotal(0);
    }

    const indicatorsRes = await axios.get(
      `${baseUrl}/api/profilegen-data/profilegen-final-mapping/${clientId}?indicatorsOnly=true&fastCountOnly=true`,
      { withCredentials: true }
    );

    const totalCount = indicatorsRes.data?.indicators?.count || 0;
    if (!totalCount) {
      if (showProgress) {
        setValidationSavedLoadMessage('Nenhum dado salvo encontrado.');
        setValidationSavedLoadCurrent(0);
        setValidationSavedLoadTotal(0);
      }
      return [];
    }

    const totalPages = Math.ceil(totalCount / chunkSize);
    const rows: any[] = [];

    if (showProgress) {
      setValidationSavedLoadMessage(`Carregando ${totalCount.toLocaleString()} registros salvos...`);
      setValidationSavedLoadCurrent(0);
      setValidationSavedLoadTotal(totalCount);
    }

    for (let page = 1; page <= totalPages; page++) {
      const res = await axios.get(
        `${baseUrl}/api/profilegen-data/profilegen-final-mapping/${clientId}?page=${page}&limit=${chunkSize}&skipIndicators=true&compact=1`,
        { withCredentials: true }
      );

      const items = res.data?.items || [];
      rows.push(...items);

      if (showProgress) {
        const loadedCount = Math.min(rows.length, totalCount);
        setValidationSavedLoadCurrent(loadedCount);
        setValidationSavedLoadTotal(totalCount);
        setValidationSavedLoadMessage(
          `Carregando dados salvos (${loadedCount.toLocaleString()} / ${totalCount.toLocaleString()})...`
        );
      }
    }

    if (showProgress) {
      setValidationSavedLoadCurrent(rows.length);
      setValidationSavedLoadTotal(totalCount);
      setValidationSavedLoadMessage(`Dados salvos carregados (${rows.length.toLocaleString()} registros).`);
    }
    return rows;
  }, [clientId]);

  const loadProfileTypesMapFromConsolidation = useCallback(async () => {
    if (!clientId) {
      setProfileTypesMap(new Map());
      return;
    }

    try {
      const consolidationItems = await fetchAllTableRows('profilegen-consolidation', 100000);
      const profileTypes = new Map<string, { risk: boolean; master: boolean; derived: boolean }>();

      consolidationItems.forEach((item: any) => {
        const profileName = String(item?.profilename || '').trim().toUpperCase();
        if (!profileName || profileName === '-') return;

        const isRisk = item?.risk === true || item?.risk === 'true' || item?.risk === 1;
        const isMaster = item?.master === true || item?.master === 'true' || item?.master === 1;
        const isDerived = item?.derived === true || item?.derived === 'true' || item?.derived === 1;

        const existing = profileTypes.get(profileName) || { risk: false, master: false, derived: false };
        profileTypes.set(profileName, {
          risk: existing.risk || isRisk,
          master: existing.master || isMaster,
          derived: existing.derived || isDerived
        });
      });

      setProfileTypesMap(profileTypes);
    } catch (err) {
      console.error('loadProfileTypesMapFromConsolidation error:', err);
      setProfileTypesMap(new Map());
    }
  }, [clientId, fetchAllTableRows]);

  const loadValidationRows = useCallback(async (options?: { ignoreSaved?: boolean; withProgress?: boolean }) => {
    if (!clientId) return;
    const showProgress = options?.withProgress === true;

    const setProgress = (current: number, message: string) => {
      if (!showProgress) return;
      setValidationSavedLoadCurrent(Math.max(0, Math.min(100, Math.round(current))));
      setValidationSavedLoadTotal(100);
      setValidationSavedLoadMessage(message);
    };
    const yieldToUI = async () => {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    };

    if (showProgress) {
      setValidationSavedLoadInProgress(true);
      setValidationSavedLoadCurrent(0);
      setValidationSavedLoadTotal(100);
      setValidationSavedLoadMessage('Iniciando carregamento da validação...');
    }

    setValidationLoading(true);
    try {
      if (!options?.ignoreSaved) {
        setProgress(5, 'Verificando dados salvos no banco...');
        const savedRows = await loadSavedFinalMappingRows({ withProgress: showProgress });
        if (savedRows.length > 0) {
          setProgress(90, 'Aplicando dados salvos e preparando tabela...');
          await loadProfileTypesMapFromConsolidation();
          setValidationRows(savedRows);
          setValidationPage(1);
          setValidationDataLoaded(true);
          setValidationLoadedFromDb(true);
          setProgress(100, `Tabela pronta com ${savedRows.length.toLocaleString()} registros salvos.`);
          return;
        }
      }

      const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      
      const profileModel = String(client?.profileModel || '');
      const isPositionComposite = profileModel === 'Posição Composto + Atividade Simples';
      const isPositionSimple = profileModel === 'Posição/Cargo Simples';
      const riskDirectAttribution = client?.san_risk_attrb_model === false; // Risk profiles attributed directly to user
      
      console.log('[loadValidationRows] Starting validation load');
      console.log('[loadValidationRows] Profile model:', profileModel);
      console.log('[loadValidationRows] Is position composite:', isPositionComposite);
      console.log('[loadValidationRows] Is position simple:', isPositionSimple);
      console.log('[loadValidationRows] Risk direct attribution:', riskDirectAttribution);
      
      // Step A: build base relation Usuário x Área x Código from logs + userxa/userxpos
      
      // Load logs progressively (all data)
      setProgress(10, 'Carregando logs de acesso...');
      console.log('[loadValidationRows] Loading logs progressively...');
      const logs = await fetchAllTableRows('profilegen-logs', 5000, {
        enabled: showProgress,
        label: 'Carregando logs de acesso',
        from: 10,
        to: 35
      });
      console.log('[loadValidationRows] Logs loaded:', logs.length);
      
      setProgress(36, 'Carregando mapeamentos de nível organizacional...');
      const userOrgLevelResponse = await axios.get(`${baseUrl}/api/profilegen-userorglevel/${clientId}`, { withCredentials: true }).catch((err) => {
        console.warn('Could not load user orglevel mappings:', err.response?.status, err.response?.data);
        return { data: { mappings: [] } };
      });

      setProgress(37, 'Carregando detalhes do usuário...');
      const userDetailsData = await fetchAllTableRows('profilegen-userdetails', 5000, {
        enabled: showProgress,
        label: 'Carregando detalhes do usuário (nomes)',
        from: 37,
        to: 40
      });
      const userNameMap = new Map<string, string>();
      userDetailsData.forEach((detail: any) => {
        // Normalize: trim, uppercase, remove extra spaces
        const userRaw = String(detail.user || '').trim();
        const user = userRaw.toUpperCase().replace(/\s+/g, ' ');
        const name = String(detail.name || '').trim();
        
        if (user && name) {
          userNameMap.set(user, name);
        }
      });
      console.log('[loadValidationRows] User name map loaded:', userNameMap.size, 'users');

      let userPositions: any[] = [];
      let userAreas: any[] = [];

      if (isPositionComposite) {
        console.log('[loadValidationRows] Loading userxpos data for position composite model');
        userPositions = await fetchAllTableRows('profilegen-userxpos', 5000, {
          enabled: showProgress,
          label: 'Carregando estrutura de posições (userxpos)',
          from: 36,
          to: 45
        });
        console.log('[loadValidationRows] Loading userxa data for position composite model');
        userAreas = await fetchAllTableRows('profilegen-userxa', 5000, {
          enabled: showProgress,
          label: 'Carregando áreas de usuários (userxa)',
          from: 45,
          to: 52
        });
      } else if (isPositionSimple) {
        console.log('[loadValidationRows] Loading userxpos data for position simple model');
        userPositions = await fetchAllTableRows('profilegen-userxpos', 5000, {
          enabled: showProgress,
          label: 'Carregando estrutura de posições (userxpos)',
          from: 36,
          to: 52
        });
      } else {
        console.log('[loadValidationRows] Loading userxa data for activity simple model');
        userAreas = await fetchAllTableRows('profilegen-userxa', 5000, {
          enabled: showProgress,
          label: 'Carregando áreas de usuários (userxa)',
          from: 36,
          to: 52
        });
      }

      const userOrgLevels = userOrgLevelResponse.data?.mappings || [];
      
      console.log('[loadValidationRows] User org level mappings loaded:', userOrgLevels.length);
      
      // Build maps based on profile model
      let userAreaMap = new Map<string, string>();
      let userPositionMap = new Map<string, { n1: string; n2: string; n3: string; n4: string; composite_name: string }>();
      let positionKey = (n1: string, n2: string, n3: string, n4: string) => `${n1}||${n2}||${n3}||${n4}`;
      
      if (isPositionComposite) {
        // Build user -> position info map
        userPositions.forEach((up: any) => {
          if (up.user) {
            const normalizedUser = String(up.user || '').trim();
            if (normalizedUser) {
              userPositionMap.set(normalizedUser, {
                n1: up.n1 || '',
                n2: up.n2 || '',
                n3: up.n3 || '',
                n4: up.n4 || '',
                composite_name: up.composite_name || ''
              });
            }
          }
        });
        console.log('[loadValidationRows] User position map built:', userPositionMap.size, 'users');
        
        // Build user -> area map (for position composite model, we also need user areas)
        userAreas.forEach((ua: any) => { 
          if (ua.user && ua.area) {
            const normalizedUser = String(ua.user || '').trim();
            if (normalizedUser) {
              userAreaMap.set(normalizedUser, ua.area);
            }
          }
        });
        console.log('[loadValidationRows] User area map built:', userAreaMap.size, 'users');
      } else if (isPositionSimple) {
        // Build user -> position info map for position simple model
        userPositions.forEach((up: any) => {
          if (up.user) {
            const normalizedUser = String(up.user || '').trim();
            if (normalizedUser) {
              userPositionMap.set(normalizedUser, {
                n1: up.n1 || '',
                n2: up.n2 || '',
                n3: up.n3 || '',
                n4: up.n4 || '',
                composite_name: up.composite_name || ''
              });
            }
          }
        });
        console.log('[loadValidationRows] User position map built (position simple):', userPositionMap.size, 'users');
      } else {
        // Build user -> area map
        userAreas.forEach((ua: any) => { 
          if (ua.user && ua.area) {
            const normalizedUser = String(ua.user || '').trim();
            if (normalizedUser) {
              userAreaMap.set(normalizedUser, ua.area);
            }
          }
        });
      }
      
      // Build map of user -> array of orglevel values
      const userOrgLevelMap = new Map<string, string[]>();
      userOrgLevels.forEach((uol: any) => {
        if (uol.user && uol.value) {
          const normalizedUser = String(uol.user || '').trim();
          if (normalizedUser) {
            const values = userOrgLevelMap.get(normalizedUser) || [];
            values.push(String(uol.value).trim());
            userOrgLevelMap.set(normalizedUser, values);
          }
        }
      });
      
      console.log('[loadValidationRows] User org level map built:', userOrgLevelMap.size, 'users');

      // Build set of ACTUAL usage: usuario||codigo (position composite) or usuario||area||codigo (activity simple)
      const actualUsageSet = new Set<string>();
      let debugUserCount = 0;
      logs.forEach((log: any) => {
        const usuario = String(log.user || '').trim();
        const codigo = String(log.code || '').trim().toUpperCase();
        if (usuario && codigo) {
          if (isPositionComposite) {
            // For position composite: just user + code (no area)
            const usageKey = `${usuario}||${codigo}`;
            actualUsageSet.add(usageKey);
          } else {
            // For activity simple: user + area + code
            // Use normalized user to lookup area
            const area = userAreaMap.get(usuario) || '-';
            const usageKey = `${usuario}||${String(area).trim()}||${codigo}`;
            actualUsageSet.add(usageKey);
            // Log for users starting with underscore to debug
            if (usuario.startsWith('_') && debugUserCount < 5) {
              console.log('[DEBUG actualUsageSet ACT] User:', usuario, 'User length:', usuario.length, 'Area:', area, 'Code:', codigo, 'Key:', usageKey);
              debugUserCount++;
            }
          }
        }
      });

      // Build unique pairs (user, area, code) with utilizada flag
      const uniqueKeys = new Set<string>();
      const baseRows: Array<{ usuario: string; area: string; codigo: string; utilizada: string }> = [];
      logs.forEach((log: any) => {
        const usuario = String(log.user || '').trim();
        const area = isPositionComposite ? '' : (userAreaMap.get(usuario) || '-');
        const codigo = String(log.code || '').trim();
        const dedupeKey = `${usuario}||${String(area).trim()}||${codigo}`;
        if (!uniqueKeys.has(dedupeKey)) {
          uniqueKeys.add(dedupeKey);
          baseRows.push({ usuario, area, codigo, utilizada: 'S' }); // Used in log
        }
      });

      // Step B: fetch consolidation and build lookup by area||code AND by profilename
      setProgress(53, 'Carregando consolidação de perfis...');
      const consolidationItems = await fetchAllTableRows('profilegen-consolidation', 5000, {
        enabled: showProgress,
        label: 'Carregando consolidação de perfis',
        from: 53,
        to: 72
      });
      console.log('[loadValidationRows] Consolidation items loaded:', consolidationItems.length);
      
      const consolidationLookup = new Map<string, any[]>();
      const consolidationByCode = new Map<string, any[]>();
      const consolidationByProfile = new Map<string, any[]>(); // profilename -> array of consolidation records
      
      consolidationItems.forEach((it: any) => {
        const key = `${String(it.area || '').trim().toLowerCase()}||${String(it.code || it.codigo || '').trim().toLowerCase()}`;
        const arr = consolidationLookup.get(key) || [];
        arr.push(it);
        consolidationLookup.set(key, arr);

        const codeKey = String(it.code || it.codigo || '').trim().toLowerCase();
        if (codeKey) {
          const byCode = consolidationByCode.get(codeKey) || [];
          byCode.push(it);
          consolidationByCode.set(codeKey, byCode);
        }
        
        // Also index by profilename for later lookup of all codes in a profile
        const profileName = String(it.profilename || '').trim().toUpperCase();
        if (profileName && profileName !== '-') {
          const profArr = consolidationByProfile.get(profileName) || [];
          profArr.push(it);
          consolidationByProfile.set(profileName, profArr);
        }
      });
      
      console.log('[loadValidationRows] Consolidation lookup size:', consolidationLookup.size);
      console.log('[loadValidationRows] Consolidation by profile size:', consolidationByProfile.size);
      
      // Build profile types map (risk, master, derived)
      const profileTypes = new Map<string, {risk: boolean, master: boolean, derived: boolean}>();
      consolidationByProfile.forEach((records, profileName) => {
        // Check if any record has risk=true, master=true, or derived=true
        const hasRisk = records.some(r => r.risk === true || r.risk === 'true' || r.risk === 1);
        const hasMaster = records.some(r => r.master === true || r.master === 'true' || r.master === 1);
        const hasDerived = records.some(r => r.derived === true || r.derived === 'true' || r.derived === 1);
        
        if (hasRisk || hasMaster || hasDerived) {
          profileTypes.set(profileName, { risk: hasRisk, master: hasMaster, derived: hasDerived });
          console.log(`[loadValidationRows] Profile ${profileName}: risk=${hasRisk}, master=${hasMaster}, derived=${hasDerived}`);
        }
      });
      setProfileTypesMap(profileTypes);
      console.log('[loadValidationRows] Profile types map size:', profileTypes.size);
      
      // Debug: Show sample keys from consolidation lookup
      if (consolidationLookup.size > 0) {
        const sampleKeys = Array.from(consolidationLookup.keys()).slice(0, 5);
        console.log('[loadValidationRows] Sample consolidation keys:', sampleKeys);
      }

      // Step C: Process based on profile model
      setProgress(73, 'Processando relacionamentos e regras de atribuição...');
      const rows: any[] = [];
      
      if (isPositionComposite) {
        console.log('[loadValidationRows] Processing POSITION COMPOSITE model');
        
        // POSITION COMPOSITE MODEL: Group users by position and aggregate their profiles
        
        // 1. Group users by position structure (n1-n2-n3-n4)
        const positionUsersMap = new Map<string, string[]>(); // positionKey -> array of users
        const positionInfoMap = new Map<string, { n1: string; n2: string; n3: string; n4: string; composite_name: string }>();
        
        userPositionMap.forEach((posInfo, usuario) => {
          const pKey = positionKey(posInfo.n1, posInfo.n2, posInfo.n3, posInfo.n4);
          const users = positionUsersMap.get(pKey) || [];
          users.push(usuario);
          positionUsersMap.set(pKey, users);
          positionInfoMap.set(pKey, posInfo);
        });
        
        console.log('[loadValidationRows] Position groups:', positionUsersMap.size);
        
        // 2. For each position, aggregate all transactions from users in that position
        const positionTransactionsMap = new Map<string, Set<string>>(); // positionKey -> set of codes
        
        logs.forEach((log: any) => {
          const usuario = String(log.user || '').trim();
          const posInfo = userPositionMap.get(usuario);
          if (!posInfo) return;
          
          const pKey = positionKey(posInfo.n1, posInfo.n2, posInfo.n3, posInfo.n4);
          const codes = positionTransactionsMap.get(pKey) || new Set<string>();
          codes.add(String(log.code || '').trim().toUpperCase());
          positionTransactionsMap.set(pKey, codes);
        });
        
        console.log('[loadValidationRows] Position transactions map size:', positionTransactionsMap.size);
        
        // 3. For each position, determine which profiles (simple) should be included
        // First, build a map of position -> set of user areas in that position
        const positionAreasMap = new Map<string, Set<string>>(); // positionKey -> set of areas (lowercase)
        let positionAreaDebugCount = 0;
        positionUsersMap.forEach((users, pKey) => {
          const areasInPosition = new Set<string>();
          users.forEach(usuario => {
            const userArea = userAreaMap.get(usuario);
            if (userArea) areasInPosition.add(userArea.toLowerCase().trim());
          });
          positionAreasMap.set(pKey, areasInPosition);
          if (positionAreaDebugCount < 20) {
            console.log('[loadValidationRows] Position:', pKey, 'has areas:', Array.from(areasInPosition).join(', '));
          } else if (positionAreaDebugCount === 20) {
            console.log('[loadValidationRows] Position area logs truncated to avoid console flood');
          }
          positionAreaDebugCount += 1;
        });
        
        const positionProfilesMap = new Map<string, Set<string>>(); // positionKey -> set of profile names
        
        const positionTransactionEntries = Array.from(positionTransactionsMap.entries());
        let processedPositions = 0;
        for (let posIdx = 0; posIdx < positionTransactionEntries.length; posIdx++) {
          const [pKey, codes] = positionTransactionEntries[posIdx];
          const profilesForPosition = new Set<string>();
          const allowedAreas = positionAreasMap.get(pKey) || new Set<string>();
          
          // Intentionally silent in hot loop to avoid console flood.

          let processedCodes = 0;
          const codesList = Array.from(codes);
          for (let codeIdx = 0; codeIdx < codesList.length; codeIdx++) {
            const codigo = codesList[codeIdx];
            // Find ALL profiles for this code in ALL allowed areas (not just the first match)
            const codigoLower = codigo.toLowerCase();
            const matchingProfiles: any[] = [];

            // Fast path: direct lookup by allowed areas + code
            if (allowedAreas.size > 0) {
              const allowedAreasList = Array.from(allowedAreas);
              for (let areaIdx = 0; areaIdx < allowedAreasList.length; areaIdx++) {
                const areaFromKey = allowedAreasList[areaIdx];
                const matches = consolidationLookup.get(`${areaFromKey}||${codigoLower}`) || [];
                if (matches.length > 0) {
                  const profileItem = matches.find((m: any) => m.profilename && String(m.profilename).trim() !== '') || matches[0];
                  if (profileItem) matchingProfiles.push(profileItem);
                }
              }
            } else {
              const anyAreaMatches = consolidationByCode.get(codigoLower) || [];
              if (anyAreaMatches.length > 0) {
                const groupedByArea = new Map<string, any[]>();
                for (let itemIdx = 0; itemIdx < anyAreaMatches.length; itemIdx++) {
                  const item = anyAreaMatches[itemIdx];
                  const areaKey = String(item?.area || '').trim().toLowerCase();
                  const grouped = groupedByArea.get(areaKey) || [];
                  grouped.push(item);
                  groupedByArea.set(areaKey, grouped);
                }
                const groupedValues = Array.from(groupedByArea.values());
                for (let groupedIdx = 0; groupedIdx < groupedValues.length; groupedIdx++) {
                  const grouped = groupedValues[groupedIdx];
                  const profileItem = grouped.find((m: any) => m.profilename && String(m.profilename).trim() !== '') || grouped[0];
                  if (profileItem) matchingProfiles.push(profileItem);
                }
              }
            }
            
            if (matchingProfiles.length === 0) continue;
            
            // Process ALL matching profiles (not just one)
            matchingProfiles.forEach(chosen => {
              // Check if this is a risk profile
              const isRiskProfile = chosen.risk === true;
              
              // If risk profiles have direct attribution (not through composite), skip them here
              if (isRiskProfile && riskDirectAttribution) {
                // Intentionally silent in hot loop to avoid console flood.
                return; // Don't add risk profiles to position composite when direct attribution is enabled
              }
              
              // Handle master/derived profiles
              const isMaster = chosen.master === true;
              if (isMaster) {
                // Get users in this position WHO ACTUALLY USED THIS CODE
                const usersInPosition = positionUsersMap.get(pKey) || [];
                const usersWhoUsedThisCode: string[] = [];
                
                // Filter to only users who used THIS specific code
                usersInPosition.forEach(u => {
                  const usageKey = `${u}||${codigo}`;
                  if (actualUsageSet.has(usageKey)) {
                    usersWhoUsedThisCode.push(u);
                  }
                });
                
                // Collect org level values ONLY from users who used this code
                const relevantOrgLevelValues = new Set<string>();
                let hasUserWithoutOrgLevel = false;
                
                usersWhoUsedThisCode.forEach(u => {
                  const userOrgValues = userOrgLevelMap.get(u) || [];
                  if (userOrgValues.length > 0) {
                    userOrgValues.forEach(v => relevantOrgLevelValues.add(v));
                  } else {
                    hasUserWithoutOrgLevel = true;
                  }
                });
                
                // If we have org level values from users who used this code, add derived profiles
                if (relevantOrgLevelValues.size > 0) {
                  const masterProfileName = String(chosen.profilename || '').trim().toUpperCase();
                  relevantOrgLevelValues.forEach(orgLevelValue => {
                    const derivedProfile = consolidationItems.find((item: any) => 
                      item.derived === true &&
                      String(item.master_name || '').trim().toUpperCase() === masterProfileName &&
                      String(item.derived_orglevel_value || '').trim() === orgLevelValue
                    );
                    if (derivedProfile) {
                      const derivedProfileName = String(derivedProfile.profilename || '').trim().toUpperCase();
                      if (derivedProfileName && derivedProfileName !== '-') {
                        profilesForPosition.add(derivedProfileName);
                      }
                    }
                  });
                }
                
                // If ANY user who used this code doesn't have org level mapping, also add master profile
                if (hasUserWithoutOrgLevel) {
                  const masterProfileName = String(chosen.profilename || '').trim().toUpperCase();
                  if (masterProfileName && masterProfileName !== '-') {
                    profilesForPosition.add(masterProfileName);
                  }
                }
              } else {
                // Regular profile
                let finalProfileName = String(chosen.profilename || '').trim().toUpperCase();
                if (finalProfileName && finalProfileName !== '-') {
                  profilesForPosition.add(finalProfileName);
                }
              }
            });
            processedCodes += 1;
            if (processedCodes % 200 === 0) {
              await yieldToUI();
            }
          }
          
          positionProfilesMap.set(pKey, profilesForPosition);
          processedPositions += 1;
          if (processedPositions % 20 === 0) {
            setProgress(73 + (processedPositions / Math.max(1, positionTransactionEntries.length)) * 20, `Processando posições (${processedPositions}/${positionTransactionEntries.length})...`);
            await yieldToUI();
          }
        }
        
        console.log('[loadValidationRows] Position profiles map size:', positionProfilesMap.size);
        
        // 4. Build final rows: for each user in each position, show all codes from position's profiles
        const rowDedupeSet = new Set<string>();
        
        const positionUserEntries = Array.from(positionUsersMap.entries());
        const masterProfilesSet = new Set<string>();
        consolidationItems.forEach((item: any) => {
          if (item?.master === true) {
            const masterName = String(item?.profilename || '').trim().toUpperCase();
            if (masterName) masterProfilesSet.add(masterName);
          }
        });
        const totalPositionsForFinalRows = Math.max(1, positionUserEntries.length);
        let emittedRows = 0;
        for (let pIdx = 0; pIdx < positionUserEntries.length; pIdx++) {
          const [pKey, users] = positionUserEntries[pIdx];
          const posInfo = positionInfoMap.get(pKey);
          if (!posInfo) continue;

          const profilesForPosition = positionProfilesMap.get(pKey) || new Set<string>();
          const profileNames = Array.from(profilesForPosition);

          for (let uIdx = 0; uIdx < users.length; uIdx++) {
            const usuario = users[uIdx];
            const userHasOrgLevel = userOrgLevelMap.has(usuario) && (userOrgLevelMap.get(usuario) || []).length > 0;

            for (let pnIdx = 0; pnIdx < profileNames.length; pnIdx++) {
              const profileName = profileNames[pnIdx];
              const profileCodes = consolidationByProfile.get(profileName) || [];

              const isMasterProfile = masterProfilesSet.has(profileName);

              const masterWithoutOrgLevel = isMasterProfile && !userHasOrgLevel;

              for (let pcIdx = 0; pcIdx < profileCodes.length; pcIdx++) {
                const codeItem = profileCodes[pcIdx];
                const codigo = String(codeItem.code || codeItem.codigo || '').trim().toUpperCase();
                const usageKey = `${usuario}||${codigo}`;
                const utilizada = actualUsageSet.has(usageKey) ? 'S' : 'N';

                const rowKey = `${usuario}||${pKey}||${codigo}||${profileName}`;
                if (rowDedupeSet.has(rowKey)) continue;
                rowDedupeSet.add(rowKey);

                const usuarioUpper = String(usuario || '').trim().toUpperCase().replace(/\s+/g, ' ');
                const usuarioName = userNameMap.has(usuarioUpper) ? userNameMap.get(usuarioUpper) : '';

                rows.push({
                  usuario: usuario || '-',
                  usuario_name: usuarioName || '-',
                  area_usuario: userAreaMap.get(usuario) || '-',
                  n1: posInfo.n1 || '-',
                  n2: posInfo.n2 || '-',
                  n3: posInfo.n3 || '-',
                  n4: posInfo.n4 || '-',
                  perfil_composto: posInfo.composite_name || '-',
                  area_perfil: codeItem.area || '-',
                  processo: codeItem.process || codeItem.processo || '-',
                  subprocesso: codeItem.subprocess || codeItem.subprocesso || '-',
                  atividade: codeItem.activity || codeItem.atividade || '-',
                  codigo: codigo || '-',
                  perfil: profileName || '-',
                  utilizada: utilizada,
                  master_sem_orglevel: masterWithoutOrgLevel
                });
                emittedRows += 1;
                if (emittedRows % 1200 === 0) {
                  await yieldToUI();
                }
              }
            }

            if (uIdx % 30 === 0) {
              await yieldToUI();
            }
          }

          if (pIdx % 10 === 0) {
            const finalRowsProgress = 93 + ((pIdx + 1) / totalPositionsForFinalRows) * 4;
            setProgress(finalRowsProgress, `Montando linhas finais (${pIdx + 1}/${totalPositionsForFinalRows})...`);
            await yieldToUI();
          }
        }
        
        console.log('[loadValidationRows] Position composite model - rows generated:', rows.length);
        
        // RISK PROFILES WITH DIRECT ATTRIBUTION (for position composite model)
        if (riskDirectAttribution) {
          console.log('[loadValidationRows] Processing risk profiles with DIRECT ATTRIBUTION');
          
          const riskRowDedupeSet = new Set<string>();
          
          // For each user, process only the codes they actually used
          for (let lIdx = 0; lIdx < logs.length; lIdx++) {
            const log: any = logs[lIdx];
            const usuario = String(log.user || '').trim();
            const codigo = String(log.code || '').trim().toUpperCase();
            const userArea = userAreaMap.get(usuario);
            const userOrgValues = userOrgLevelMap.get(usuario) || [];
            const userOrgValueSet = new Set(userOrgValues.map(v => String(v || '').trim()));
            const userHasOrgLevel = userOrgValueSet.size > 0;
            
            if (!usuario || !codigo || !userArea) {
              if (lIdx % 500 === 0) await yieldToUI();
              continue;
            }
            
            // Look for risk profiles matching this user's area and code
            const codigoLower = codigo.toLowerCase();
            const areaLower = userArea.toLowerCase().trim();
            const key = `${areaLower}||${codigoLower}`;
            
            const matches = consolidationLookup.get(key) || [];
            
            for (let mi = 0; mi < matches.length; mi++) {
              const item = matches[mi];
              // Only process risk profiles
              if (item.risk !== true) continue;

              const isMasterProfile = item.master === true;
              const isDerivedProfile = item.derived === true;
              const derivedOrgValue = String(item.derived_orglevel_value || '').trim();

              // Apply org level filter for master/derived outside composite
              if (isDerivedProfile) {
                if (!userHasOrgLevel || !derivedOrgValue || !userOrgValueSet.has(derivedOrgValue)) continue;
              } else if (isMasterProfile) {
                if (userHasOrgLevel) continue;
              }
              
              const profileName = String(item.profilename || '').trim().toUpperCase();
              if (!profileName || profileName === '-') continue;
              
              // Get all codes for this risk profile
              const profileCodes = consolidationByProfile.get(profileName) || [];
              
              for (let pci = 0; pci < profileCodes.length; pci++) {
                const codeItem = profileCodes[pci];
                const perfilCodigo = String(codeItem.code || codeItem.codigo || '').trim().toUpperCase();
                
                // Check if user actually used THIS code
                const usageKey = `${usuario}||${perfilCodigo}`;
                const utilizada = actualUsageSet.has(usageKey) ? 'S' : 'N';
                
                // Dedupe by user + code + profile
                const rowKey = `${usuario}||RISK_DIRECT||${perfilCodigo}||${profileName}`;
                if (riskRowDedupeSet.has(rowKey)) continue;
                riskRowDedupeSet.add(rowKey);
                
                const usuarioUpper = String(usuario || '').trim().toUpperCase().replace(/\s+/g, ' ');
                const usuarioName = userNameMap.has(usuarioUpper) ? userNameMap.get(usuarioUpper) : '';
                
                rows.push({
                  usuario: usuario || '-',
                  usuario_name: usuarioName || '-',
                  area_usuario: userArea || '-',
                  n1: '-',
                  n2: '-',
                  n3: '-',
                  n4: '-',
                  perfil_composto: 'DIRECT_ATTRIBUTION', // Special marker for direct attribution
                  area_perfil: codeItem.area || '-',
                  processo: codeItem.process || codeItem.processo || '-',
                  subprocesso: codeItem.subprocess || codeItem.subprocesso || '-',
                  atividade: codeItem.activity || codeItem.atividade || '-',
                  codigo: perfilCodigo || '-',
                  perfil: profileName || '-',
                  utilizada: utilizada,
                  master_sem_orglevel: false // Risk profiles don't have master/derived logic
                });
              }
            }

            if (lIdx % 300 === 0) {
              await yieldToUI();
            }
          }
          
          console.log('[loadValidationRows] Risk direct attribution - rows generated:', riskRowDedupeSet.size);
        }
        
      } else if (isPositionSimple) {
        console.log('[loadValidationRows] Processing POSITION SIMPLE model');
        
        // POSITION SIMPLE MODEL: Show ALL codes in profiles for each user's position
        // Mark which codes were actually used (utilizada = S/N)
        // Data sources:
        // - userPositionMap: user -> {n1, n2, n3, n4} from profilegen-userxpos
        // - logs: user -> code from profilegen-logs (to mark as "used")
        // - consolidationItems: profiles by clientId with n1-n4 and code from profilegen-consolidation
        
        console.log('[loadValidationRows] User position map size:', userPositionMap.size);
        console.log('[loadValidationRows] Consolidation items:', consolidationItems.length);
        
        // Build set of codes that each user actually used
        const userUsedCodesSet = new Map<string, Set<string>>(); // user -> set of codes (uppercase)
        logs.forEach((log: any) => {
          const usuario = String(log.user || '').trim();
          const codigo = String(log.code || '').trim().toUpperCase();
          if (usuario && codigo) {
            const codes = userUsedCodesSet.get(usuario) || new Set<string>();
            codes.add(codigo);
            userUsedCodesSet.set(usuario, codes);
          }
        });
        console.log('[loadValidationRows] User used codes map built:', userUsedCodesSet.size, 'users');
        
        // Group consolidation items by position structure (n1||n2||n3||n4)
        // Also track which profiles are risk profiles
        const positionProfileCodesMap = new Map<string, Array<{profilename: string, code: string, isRisk: boolean}>>(); 
        
        consolidationItems.forEach((item: any) => {
          const profileName = String(item.profilename || '').trim().toUpperCase();
          const code = String(item.code || item.codigo || '').trim().toUpperCase();
          const n1 = String(item.n1 || '').trim();
          const n2 = String(item.n2 || '').trim();
          const n3 = String(item.n3 || '').trim();
          const n4 = String(item.n4 || '').trim();
          const isRisk = item.risk === true || item.risk === 'true' || item.risk === 1;
          
          if (!profileName || profileName === '-' || !code) return;
          
          // Create key: position structure (without code, since we want all codes for this position)
          const positionKey = `${n1}||${n2}||${n3}||${n4}`;
          const entries = positionProfileCodesMap.get(positionKey) || [];
          entries.push({ profilename: profileName, code: code, isRisk: isRisk });
          positionProfileCodesMap.set(positionKey, entries);
        });
        
        console.log('[loadValidationRows] Position profile-codes map built:', positionProfileCodesMap.size, 'positions');
        
        // Sample some entries for debugging
        if (positionProfileCodesMap.size > 0) {
          const firstKey = Array.from(positionProfileCodesMap.keys())[0];
          const firstEntries = positionProfileCodesMap.get(firstKey);
          console.log('[loadValidationRows] Sample position:', firstKey, 'has', firstEntries?.length, 'profile-code entries');
        }
        
        // Now iterate through each user with a position
        const rowDedupeSet = new Set<string>();
        
        userPositionMap.forEach((posInfo, usuario) => {
          const positionKey = `${posInfo.n1}||${posInfo.n2}||${posInfo.n3}||${posInfo.n4}`;
          const profileCodeEntries = positionProfileCodesMap.get(positionKey) || [];
          
          if (profileCodeEntries.length === 0) {
            // Intentionally silent: this can happen in bulk and flood the console.
            return;
          }
          
          // Get codes this user actually used
          const usedCodes = userUsedCodesSet.get(usuario) || new Set<string>();
          
          // For each profile-code combination in this position, create a row
          profileCodeEntries.forEach(entry => {
            const { profilename, code, isRisk } = entry;
            
            // Check if user used this code
            const utilizada = usedCodes.has(code) ? 'S' : 'N';
            
            // RISK PROFILE FILTER: If this code belongs to a risk profile and user did NOT use it, skip it
            if (isRisk && utilizada === 'N') {
              // Intentionally silent: this can happen in bulk and flood the console.
              return; // Don't include this code/profile combination
            }
            
            // Dedupe by user + position + code + profile
            const rowKey = `${usuario}||${posInfo.n1}||${posInfo.n2}||${posInfo.n3}||${posInfo.n4}||${code}||${profilename}`;
            if (!rowDedupeSet.has(rowKey)) {
              rowDedupeSet.add(rowKey);
              const usuarioUpper = String(usuario || '').trim().toUpperCase().replace(/\s+/g, ' ');
              const usuarioName = userNameMap.has(usuarioUpper) ? userNameMap.get(usuarioUpper) : '';
              rows.push({
                usuario: usuario,
                usuario_name: usuarioName || '-',
                n1: posInfo.n1 || '-',
                n2: posInfo.n2 || '-',
                n3: posInfo.n3 || '-',
                n4: posInfo.n4 || '-',
                codigo: code,
                perfil: profilename,
                utilizada: utilizada
              });
            }
          });
        });
        
        console.log('[loadValidationRows] Position simple rows generated:', rows.length);
        
      } else {
        console.log('[loadValidationRows] Processing ACTIVITY SIMPLE model');
        
        // ACTIVITY SIMPLE MODEL: Original logic
        
        // Process each user's log entries to determine their profiles
        const userProfilesMap = new Map<string, Set<string>>(); // user -> set of profile names
        
        for (const br of baseRows) {
          const key = `${String(br.area || '').trim().toLowerCase()}||${String(br.codigo || '').trim().toLowerCase()}`;
          const matches = consolidationLookup.get(key) || [];
          let chosen: any = null;
          if (matches.length > 0) {
            chosen = matches.find((m: any) => m.profilename && String(m.profilename).trim() !== '') || matches[0];
          }
          if (!chosen) continue;

          const isMaster = chosen.master === true;
          if (isMaster) {
            // This user USED this code (since it's in baseRows which comes from logs)
            const userOrgLevelValues = userOrgLevelMap.get(br.usuario) || [];
            if (userOrgLevelValues.length > 0) {
              // User has org level mapping - add derived profiles
              const masterProfileName = String(chosen.profilename || '').trim().toUpperCase();
              for (const orgLevelValue of userOrgLevelValues) {
                const derivedProfile = consolidationItems.find((item: any) => 
                  item.derived === true &&
                  String(item.master_name || '').trim().toUpperCase() === masterProfileName &&
                  String(item.derived_orglevel_value || '').trim() === orgLevelValue
                );
                if (derivedProfile) {
                  const derivedProfileName = String(derivedProfile.profilename || '').trim().toUpperCase();
                  if (derivedProfileName && derivedProfileName !== '-') {
                    const userProfiles = userProfilesMap.get(br.usuario) || new Set<string>();
                    userProfiles.add(derivedProfileName);
                    userProfilesMap.set(br.usuario, userProfiles);
                  }
                }
              }
              continue; // Já adicionou todos os derivados, não adiciona o master
            } else {
              // User USED this code but has NO org level mapping - assign master profile with warning
              let finalProfileName = String(chosen.profilename || '').trim().toUpperCase();
              if (finalProfileName && finalProfileName !== '-') {
                const userProfiles = userProfilesMap.get(br.usuario) || new Set<string>();
                userProfiles.add(finalProfileName);
                userProfilesMap.set(br.usuario, userProfiles);
              }
              continue;
            }
          }
          // Se não for master, adiciona o perfil normal
          let finalProfileName = String(chosen.profilename || '').trim().toUpperCase();
          if (finalProfileName && finalProfileName !== '-') {
            const userProfiles = userProfilesMap.get(br.usuario) || new Set<string>();
            userProfiles.add(finalProfileName);
            userProfilesMap.set(br.usuario, userProfiles);
          }
        }

        // Build final rows - for each user and their assigned profiles, add ALL codes
        const rowDedupeSet = new Set<string>();
        
        userProfilesMap.forEach((profileNames, usuario) => {
          const area = userAreaMap.get(usuario) || '-';
          
          // Debug log for users starting with underscore
          // Intentionally silent in hot loop to avoid console flood.
          
          // Check if this user has org level mapping
          const userHasOrgLevel = userOrgLevelMap.has(usuario) && (userOrgLevelMap.get(usuario) || []).length > 0;
          
          profileNames.forEach(profileName => {
            const profileCodes = consolidationByProfile.get(profileName) || [];
            
            if (profileCodes.length === 0) {
              return; // Skip this profile
            }
            
            // Check if this profile is a master profile
            const isMasterProfile = consolidationItems.some((item: any) => 
              String(item.profilename || '').trim().toUpperCase() === profileName && item.master === true
            );
            
            // Flag: master profile assigned to user without org level mapping
            const masterWithoutOrgLevel = isMasterProfile && !userHasOrgLevel;
            
            // Add one row for each code in the profile
            for (const codeItem of profileCodes) {
              const codigo = String(codeItem.code || codeItem.codigo || '').trim().toUpperCase();
              
              // Check if THIS specific user actually used THIS code in THIS area
              const usageKey = `${usuario}||${area}||${codigo}`;
              const utilizada = actualUsageSet.has(usageKey) ? 'S' : 'N';
              
              // Debug log for users starting with underscore
              // Intentionally silent in hot loop to avoid console flood.
              
              // Dedupe by user + area + code + profile
              const rowKey = `${usuario}||${area}||${codigo}||${profileName}`;
              if (rowDedupeSet.has(rowKey)) continue;
              rowDedupeSet.add(rowKey);
              
              const usuarioUpper = String(usuario || '').trim().toUpperCase().replace(/\s+/g, ' ');
              const usuarioName = userNameMap.has(usuarioUpper) ? userNameMap.get(usuarioUpper) : '';
              
              rows.push({
                usuario: usuario || '-',
                usuario_name: usuarioName || '-',
                area: area || '-',
                processo: codeItem.process || codeItem.processo || '-',
                subprocesso: codeItem.subprocess || codeItem.subprocesso || '-',
                atividade: codeItem.activity || codeItem.atividade || '-',
                codigo: codigo || '-',
                perfil: profileName || '-',
                utilizada: utilizada,
                master_sem_orglevel: masterWithoutOrgLevel
              });
            }
          });
        });
      }

      console.log('[loadValidationRows] Final rows generated:', rows.length);
      setProgress(97, 'Finalizando e montando tabela de validação...');
      setValidationRows(rows);
      setValidationPage(1);
      setValidationDataLoaded(true); // Mark validation data as loaded
      setValidationLoadedFromDb(false);
      setProgress(100, `Tabela pronta com ${rows.length.toLocaleString()} registros.`);
    } catch (err) {
      console.error('loadValidationRows error:', err);
      setValidationRows([]);
      setValidationLoadedFromDb(false);
      if (showProgress) {
        setValidationSavedLoadMessage('Falha ao carregar validação. Verifique os logs e tente novamente.');
      }
    } finally {
      setValidationLoading(false);
      if (showProgress) {
        window.setTimeout(() => {
          setValidationSavedLoadInProgress(false);
          setValidationSavedLoadMessage('');
          setValidationSavedLoadCurrent(0);
          setValidationSavedLoadTotal(0);
        }, 450);
      }
    }
  }, [clientId, client?.profileModel, client?.san_risk_attrb_model, fetchAllTableRows, loadSavedFinalMappingRows, loadProfileTypesMapFromConsolidation]);

  const saveValidationRows = useCallback(async () => {
    if (!clientId) {
      pushToast({ variant: 'danger', title: 'Erro', message: 'Cliente não carregado.', durationMs: 5000 });
      return;
    }

    if (!Array.isArray(validationRows) || validationRows.length === 0) {
      pushToast({ variant: 'info', title: 'Informação', message: 'Nenhum dado de validação para salvar.', durationMs: 4500 });
      return;
    }

    setValidationSaving(true);
    try {
      const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const url = `${baseUrl}/api/profilegen-data/profilegen-final-mapping/${clientId}`;
      const uploadProgressUrl = `${baseUrl}/api/profilegen-data/profilegen-final-mapping/${clientId}/upload-progress`;
      let chunkSize = 20000;
      let sentTotal = 0;
      let chunkIndex = 0;
      let remainingRows = validationRows.slice();
      const totalRows = validationRows.length;

      setValidationSavedLoadInProgress(true);
      setValidationSavedLoadCurrent(0);
      setValidationSavedLoadTotal(totalRows);
      setValidationSavedLoadMessage(`Preparando salvamento de ${totalRows.toLocaleString()} registros...`);

      while (remainingRows.length > 0) {
        const batch = remainingRows.slice(0, chunkSize);
        chunkIndex += 1;

        try {
          setValidationSavedLoadCurrent(sentTotal);
          setValidationSavedLoadTotal(totalRows);
          setValidationSavedLoadMessage(
            `Salvando lote ${chunkIndex} (${batch.length.toLocaleString()} registros, ${sentTotal.toLocaleString()}/${totalRows.toLocaleString()} enviados)...`
          );

          const currentChunkIndex = chunkIndex;
          const currentSentTotal = sentTotal;
          const currentBatchLength = batch.length;

          const uploadRes = await axios.post(
            `${url}?background=1`,
            {
              replace: sentTotal === 0,
              rows: batch,
              background: true
            },
            {
              withCredentials: true,
              timeout: 300000
            }
          );

          if (uploadRes.status === 202 || uploadRes.data?.ok) {
            await new Promise<void>((resolve, reject) => {
              let uploadCompleted = false;
              const pollInterval = setInterval(async () => {
                try {
                  const progRes = await axios.get(uploadProgressUrl, { withCredentials: true });
                  const { progress, message, done, error } = progRes.data || {};
                  const chunkProgress = Math.max(0, Math.min(100, Number(progress || 0)));
                  const globalCurrent = Math.min(totalRows, currentSentTotal + Math.round((currentBatchLength * chunkProgress) / 100));

                  setValidationSavedLoadCurrent(globalCurrent);
                  setValidationSavedLoadTotal(totalRows);
                  setValidationSavedLoadMessage(
                    `Lote ${currentChunkIndex}: ${message || `processando ${chunkProgress}%`} (${globalCurrent.toLocaleString()}/${totalRows.toLocaleString()})`
                  );

                  if (done && !uploadCompleted) {
                    uploadCompleted = true;
                    clearInterval(pollInterval);
                    if (error) reject(new Error(String(error)));
                    else resolve();
                  }
                } catch (pollErr) {
                  console.warn('saveValidationRows poll error (ignorado):', pollErr);
                }
              }, 500);
            });
          }

          sentTotal += batch.length;
          remainingRows = remainingRows.slice(batch.length);
          setValidationSavedLoadCurrent(sentTotal);
          setValidationSavedLoadMessage(
            `Lote ${currentChunkIndex} concluído (${sentTotal.toLocaleString()}/${totalRows.toLocaleString()} registros enviados).`
          );
        } catch (err: any) {
          const status = err?.response?.status;
          if (status === 413 && chunkSize > 50) {
            const newChunkSize = Math.max(50, Math.floor(chunkSize / 2));
            chunkIndex -= 1;
            setValidationSavedLoadMessage(
              `Payload muito grande no lote atual. Reduzindo lote de ${chunkSize.toLocaleString()} para ${newChunkSize.toLocaleString()} registros e tentando novamente...`
            );
            chunkSize = newChunkSize;
            continue;
          }
          throw err;
        }
      }

      setValidationSavedLoadMessage('Verificando salvamento no banco...');
      const indicatorsRes = await axios.get(
        `${url}?indicatorsOnly=true&fastCountOnly=true`,
        { withCredentials: true, timeout: 60000 }
      );
      const savedCount = Number(indicatorsRes.data?.indicators?.count || 0);

      if (savedCount < totalRows) {
        throw new Error(`salvamento incompleto: ${savedCount} de ${totalRows} registros persistidos`);
      }

      setValidationLoadedFromDb(true);
      setValidationSavedLoadCurrent(totalRows);
      setValidationSavedLoadTotal(totalRows);
      setValidationSavedLoadMessage(`Salvamento concluído (${totalRows.toLocaleString()} registros).`);
      pushToast({
        variant: 'success',
        title: 'Sucesso',
        message: `Mapeamento final salvo com sucesso (${validationRows.length} registros).`,
        durationMs: 5000
      });
    } catch (err: any) {
      console.error('saveValidationRows error:', err);
      pushToast({
        variant: 'danger',
        title: 'Erro',
        message: `Erro ao salvar mapeamento final: ${err?.response?.data?.message || err?.message || 'erro desconhecido'}`,
        durationMs: 7000
      });
    } finally {
      setValidationSaving(false);
      window.setTimeout(() => {
        setValidationSavedLoadInProgress(false);
        setValidationSavedLoadMessage('');
        setValidationSavedLoadCurrent(0);
        setValidationSavedLoadTotal(0);
      }, 800);
    }
  }, [clientId, validationRows, pushToast]);

  const clearSavedValidationRows = useCallback(async () => {
    if (!clientId) {
      pushToast({ variant: 'danger', title: 'Erro', message: 'Cliente não carregado.', durationMs: 5000 });
      return;
    }

    setValidationDeleting(true);
    setValidationSavedLoadInProgress(true);
    setValidationSavedLoadMessage('Iniciando remoção...');
    setValidationSavedLoadCurrent(0);
    setValidationSavedLoadTotal(100);

    try {
      const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const res = await axios.delete(
        `${baseUrl}/api/profilegen-data/profilegen-final-mapping/${clientId}`,
        { withCredentials: true }
      );

      // Backend returns { deletedCount: 0 } synchronously when nothing exists (no background job)
      if (!res.data?.ok) {
        setValidationLoadedFromDb(false);
        setValidationRows([]);
        setValidationPage(1);
        setValidationDataLoaded(false);
        pushToast({ variant: 'info', title: 'Info', message: 'Nenhum registro encontrado para remover.', durationMs: 4000 });
        return;
      }

      const totalCount: number = res.data?.totalCount ?? 0;
      setValidationSavedLoadCurrent(5);
      setValidationSavedLoadMessage(`Removendo ${totalCount.toLocaleString('pt-BR')} registros...`);

      // Poll clear-progress until backend signals done
      const progressUrl = `${baseUrl}/api/profilegen-data/profilegen-final-mapping/${clientId}/clear-progress`;
      let clearCompleted = false;

      await new Promise<void>((resolve, reject) => {
        const pollInterval = setInterval(async () => {
          try {
            const progRes = await axios.get(progressUrl, { withCredentials: true });
            const { progress, message, done, error } = progRes.data;

            setValidationSavedLoadCurrent(progress as number);
            setValidationSavedLoadMessage((message as string) || `Removendo... ${progress}%`);

            if (done && !clearCompleted) {
              clearCompleted = true;
              clearInterval(pollInterval);
              if (error) reject(new Error(String(error)));
              else resolve();
            }
          } catch (pollErr) {
            console.warn('clearSavedValidationRows poll error (ignorado):', pollErr);
          }
        }, 500);
      });

      setValidationLoadedFromDb(false);
      setValidationRows([]);
      setValidationPage(1);
      setValidationDataLoaded(false);
      pushToast({
        variant: 'success',
        title: 'Sucesso',
        message: 'Registros salvos do passo Validação e Download removidos. Clique em Carregar para gerar novamente.',
        durationMs: 6000
      });
    } catch (err: any) {
      console.error('clearSavedValidationRows error:', err);
      pushToast({
        variant: 'danger',
        title: 'Erro',
        message: `Erro ao apagar mapeamento final salvo: ${err?.response?.data?.message || err?.message || 'erro desconhecido'}`,
        durationMs: 7000
      });
    } finally {
      setValidationDeleting(false);
      setValidationSavedLoadInProgress(false);
      setValidationSavedLoadMessage('');
      setValidationSavedLoadCurrent(0);
      setValidationSavedLoadTotal(0);
    }
  }, [clientId, pushToast]);
  
  // Step 2: Field configuration states
  const [fieldConfigs, setFieldConfigs] = useState({
    prefixo: { qtd: 2, char: 'Z' },
    area: { qtd: 3, char: 'A' },
    processo: { qtd: 6, char: 'P' },
    subprocesso: { qtd: 4, char: 'S' },
    atividade: { qtd: 8, char: 'T' },
    sufixo: { qtd: 2, char: 'X' }
  });
  
  // Step 2: Composite profiles constructor state and field configs
  const [compositeConstructorElements, setCompositeConstructorElements] = useState<NameElement[]>([]);
  const [compositeMaxTotalLength, setCompositeMaxTotalLength] = useState(30);
  const [compositeDraggedItemIndex, setCompositeDraggedItemIndex] = useState<number | null>(null);
  
  const [compositeFieldConfigs, setCompositeFieldConfigs] = useState({
    prefixo: { char: 'X', qtd: 2 },
    n1: { char: '1', qtd: 4 },
    n2: { char: '2', qtd: 4 },
    n3: { char: '3', qtd: 4 },
    n4: { char: '4', qtd: 4 },
    sufixo: { char: 'Z', qtd: 2 }
  });
  
  // Step 2: Risk profiles constructor state and field configs
  const [riskConstructorElements, setRiskConstructorElements] = useState<NameElement[]>([]);
  const [riskMaxTotalLength, setRiskMaxTotalLength] = useState(30);
  const [riskDraggedItemIndex, setRiskDraggedItemIndex] = useState<number | null>(null);
  
  const [riskFieldConfigs, setRiskFieldConfigs] = useState({
    prefixo: { qtd: 2, char: 'R' },
    area: { qtd: 3, char: 'A' },
    processo: { qtd: 6, char: 'P' },
    subprocesso: { qtd: 4, char: 'S' },
    atividade: { qtd: 8, char: 'T' },
    n1: { qtd: 3, char: '1' },
    n2: { qtd: 3, char: '2' },
    n3: { qtd: 3, char: '3' },
    n4: { qtd: 3, char: '4' },
    acao: { qtd: 4, char: 'C' },
    sufixo: { qtd: 2, char: 'X' }
  });
  
  // Composite prefix patterns modal state
  const [compositePrefixPatternsModalOpen, setCompositePrefixPatternsModalOpen] = useState(false);
  const [compositePrefixPatternsSuccessModalOpen, setCompositePrefixPatternsSuccessModalOpen] = useState(false);
  const [confirmClearRiskMatrixModalOpen, setConfirmClearRiskMatrixModalOpen] = useState(false);
  
  // Accordion state for Step 2 sections
  const [step2SectionExpanded, setStep2SectionExpanded] = useState({
    nomenclaturaSimples: false,   // Padrão de nomenclatura - Perfis Simples/Mestre/Derivados
    nomenclaturaCompostos: false, // Padrão de nomenclatura - Perfis Compostos
    saneamentoRisco: false,       // Parâmetros de Saneamento de Risco
    nomenclaturaRisco: false,     // Padrão de nomenclatura - Perfis de Risco
    derivacaoOrganizacional: false // Parâmetros de derivação por nível organizacional
  });
  
  const [compositePrefixPatterns, setCompositePrefixPatterns] = useState({
    composto: ''
  });

  // Composite user positions (N1..N4) loaded from profilegen-userxpos for the client
  const [compositeUserPositions, setCompositeUserPositions] = useState<any[]>([]);
  const [compositeUserPositionsLoading, setCompositeUserPositionsLoading] = useState<boolean>(false);
  // Flag to indicate whether the composite nomenclature has been generated (controls table display)
  const [compositeNomenclatureGenerated, setCompositeNomenclatureGenerated] = useState<boolean>(false);

  // For Posição/Cargo Simples: local caches for userxpos and logs if previews are not present
  const [simpleUserPositions, setSimpleUserPositions] = useState<any[]>([]);
  const [simpleLogs, setSimpleLogs] = useState<any[]>([]);
  const [simplePositionsLoading, setSimplePositionsLoading] = useState<boolean>(false);
  const [simpleSearchQ, _setSimpleSearchQ] = useState<string>('');
  const [simpleSearchColumn, _setSimpleSearchColumn] = useState<string>('');
  const [simpleSortColumn, _setSimpleSortColumn] = useState<string>('');
  const [_simpleSortDirection, _setSimpleSortDirection] = useState<'asc'|'desc'>('asc');
  // Pagination for simple positions table
  const [simplePage, setSimplePage] = useState<number>(1);
  const [simplePageSize, setSimplePageSize] = useState<number>(Number(localStorage.getItem('gpw_simple_pageSize') || '25'));
  // Generated nomenclature for simple positions: map key (code or n1||n2||n3||n4||code) -> {nomenclatura, exceeds}
  const [simplePositionNomenclature, setSimplePositionNomenclature] = useState<Record<string, { nomenclatura: string; nomenclaturaExceedsLimit: boolean }>>({});

  // Fetch simple model data: userxpos and logs (used in Posição/Cargo Simples UI)
  const fetchSimplePositionsAndLogs = useCallback(async () => {
    if (!clientId) return;
    setSimplePositionsLoading(true);
    try {
      // Load logs progressively (all data)
      console.log('[fetchSimplePositionsAndLogs] Loading logs progressively...');
      const logsData = await fetchAllTableRows('profilegen-logs');
      console.log('[fetchSimplePositionsAndLogs] Logs loaded:', logsData.length);
      
      // Load userxpos
      const userxpos = await fetchAllTableRows('profilegen-userxpos');

      setSimpleUserPositions(userxpos.map((it: any) => ({ user: it.user || it.usuario || '', n1: it.n1 || '', n2: it.n2 || '', n3: it.n3 || '', n4: it.n4 || '' })));
      setSimpleLogs(logsData.map((l: any) => ({ user: l.user || '', code: l.code || '' })));
    } catch (err) {
      console.error('fetchSimplePositionsAndLogs error:', err);
      setSimpleUserPositions([]);
      setSimpleLogs([]);
    } finally {
      setSimplePositionsLoading(false);
    }
  }, [clientId, fetchAllTableRows]);

  // Compute aggregated positions with codes: group by n1..n4, collect unique codes across users in that position
  const positionsWithCodes = useMemo(() => {
    const map = new Map<string, { n1: string; n2: string; n3: string; n4: string; users: string[]; codes: string[] }>();

    // Build map user -> position key
    const userPos = new Map<string, string>();
    simpleUserPositions.forEach((u: any) => {
      const key = `${String(u.n1 || '').trim()}||${String(u.n2 || '').trim()}||${String(u.n3 || '').trim()}||${String(u.n4 || '').trim()}`;
      const normalizedUser = String(u.user || '').trim();
      if (normalizedUser) {
        userPos.set(normalizedUser, key);
        if (!map.has(key)) {
          map.set(key, { n1: u.n1 || '', n2: u.n2 || '', n3: u.n3 || '', n4: u.n4 || '', users: [], codes: [] });
        }
        map.get(key)!.users.push(normalizedUser);
      }
    });

    // Aggregate codes per position using logs lookup by user
    simpleLogs.forEach((l: any) => {
      const user = String(l.user || '').trim();
      const code = String(l.code || '');
      const posKey = userPos.get(user);
      if (!posKey) return; // log user not mapped to a position
      const entry = map.get(posKey);
      if (!entry) return;
      if (code && !entry.codes.includes(code)) entry.codes.push(code);
    });

    return Array.from(map.values()).map(v => ({ ...v, codes: v.codes.sort() }));
  }, [simpleUserPositions, simpleLogs]);

  // Load consolidation risk flags for Posição/Cargo Simples (only uses risk field to mark codes)
  const loadConsolidationRiskForSimple = useCallback(async () => {
    if (!clientId) return false;
    try {
      const items = await fetchAllConsolidationData();
      if (items.length > 0) {
        const savedRiskData: { [code: string]: boolean } = {};
        const savedProfilenameMap: Record<string, { nomenclatura: string; nomenclaturaExceedsLimit: boolean }> = {};
        items.forEach((item: any) => {
          if (item && item.code) {
            savedRiskData[item.code] = (savedRiskData[item.code] === true) || (item.risk === true);
          }
          // Build composite key for profilename map (always include code + N1..N4)
          // This must match the key format used by the generator and the table render
          try {
            const codePart = String(item?.code || '').trim();
            const n1p = String(item?.n1 || '').trim();
            const n2p = String(item?.n2 || '').trim();
            const n3p = String(item?.n3 || '').trim();
            const n4p = String(item?.n4 || '').trim();
            const compKey = `${codePart}||${n1p}||${n2p}||${n3p}||${n4p}`;
            const keyWithoutCode = `${n1p}||${n2p}||${n3p}||${n4p}`;
            // store under composite key and also under legacy keys (code-only and n1||n2||n3||n4)
            if (item && item.profilename && String(item.profilename).trim() !== '') {
              const value = { nomenclatura: String(item.profilename), nomenclaturaExceedsLimit: false };
              savedProfilenameMap[compKey] = value;
              if (codePart) savedProfilenameMap[codePart] = value; // legacy single-code key
              if (!codePart) savedProfilenameMap[keyWithoutCode] = value; // legacy n1||n2||n3||n4 key
            }
          } catch (e) { /* ignore malformed item */ }
        });
        setRiskClassificationData(savedRiskData);
        setIsRiskClassificationEnabled(Object.values(savedRiskData).some(v => v));
        // apply saved profilenames to state so the simple table shows them
        if (Object.keys(savedProfilenameMap).length > 0) {
          setSimplePositionNomenclature(prev => ({ ...(prev || {}), ...savedProfilenameMap }));
        } else {
          // no profilenames saved -> clear any existing in-memory nomenclature so UI reflects DB
          setSimplePositionNomenclature({});
        }
        setHasConsolidationData(true);
        return true;
      }
    } catch (err) {
      console.error('loadConsolidationRiskForSimple error:', err);
    }
    // no saved consolidation rows -> clear state that depends on saved data
    setHasConsolidationData(false);
    setRiskClassificationData({});
    setIsRiskClassificationEnabled(false);
    setSimplePositionNomenclature({});
    return false;
  }, [clientId]);

  // Auto-load simple positions and any saved consolidation risk when navigating to step 3 for Posição/Cargo Simples
  useEffect(() => {
    try {
      if (step === 3 && String(client?.profileModel) === 'Posição/Cargo Simples') {
        // Load saved consolidation risk flags (if any) so the simple table shows 'Ação de risco?'
        (async () => { await loadConsolidationRiskForSimple(); })();

        // Avoid refetching source files if we already have data
        if ((simpleUserPositions.length === 0 && simpleLogs.length === 0) || (simpleUserPositions.length === 0 && simpleLogs.length > 0)) {
          fetchSimplePositionsAndLogs();
        }
      }
    } catch (err) {
      // swallow
    }
  }, [step, client?.profileModel, clientId, fetchSimplePositionsAndLogs, simpleUserPositions.length, simpleLogs.length, loadConsolidationRiskForSimple]);
  
  type LogEntry = { id: number; ts: string; direction: 'sent'|'received'; title: string; raw: any; usage?: any; expanded?: boolean };
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const nextLogIdRef = useRef<number>(1);
  const addLogEntry = (direction: 'sent'|'received', title: string, raw: any, usage?: any) => {
    const id = nextLogIdRef.current++;
    const entry: LogEntry = { id, ts: new Date().toLocaleString(), direction, title, raw, usage, expanded: false };
    setLogEntries(prev => [entry, ...prev]);
    return id;
  };
  const toggleLogEntry = (id: number) => setLogEntries(prev => prev.map(e => e.id === id ? { ...e, expanded: !e.expanded } : e));

  // Toggle Step 2 section expansion
  const toggleStep2Section = (section: keyof typeof step2SectionExpanded) => {
    setStep2SectionExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  

  // reset preview page when table changes
  useEffect(() => { setPreviewPage(1); }, [previewModalTable]);

  // reset risk matrix page when filters change
  useEffect(() => { setRiskMatrixPage(1); }, [riskMatrixFilterColumn, riskMatrixFilterValue]);

  // reset step4 pagination when logs preview updates
  useEffect(() => { setStep4Page(1); }, [logsPreview]);

  // reset simple positions pagination when filters or source change
  useEffect(() => { setSimplePage(1); }, [simpleSearchQ, simpleSearchColumn, simpleSortColumn, positionsWithCodes.length]);

  // reconstruct constructor elements from saved client data
  useEffect(() => {
    if (!client) {
      setConstructorElements([]);
      setRiskConstructorElements([]);
      return;
    }

    console.log('🔄 Reconstruindo elementos do construtor a partir dos dados do cliente:', client);
    
    const reconstructedElements: NameElement[] = [];
    
    // Collect all pattern data
    const patterns: Array<{type: string, qtd: number, char: string, ord: number}> = [];
    
    // Field types in order
    const reconstructFieldTypes = ['prefixo', 'area', 'processo', 'subprocesso', 'atividade', 'sufixo'];
    
    // Collect field elements
    reconstructFieldTypes.forEach(type => {
      const qtd = client[`pattern_s_${type}_qtd`];
      const char = client[`pattern_s_${type}_char`];
      const ord = client[`pattern_s_${type}_ord`];
      
      if (qtd !== null && qtd !== undefined && char && ord !== null && ord !== undefined) {
        patterns.push({ type, qtd, char, ord });
      }
    });
    
    // Collect separator elements
    for (let i = 1; i <= 6; i++) {
      const char = client[`pattern_s_sep${i}_char`];
      const ord = client[`pattern_s_sep${i}_ord`];
      
      if (char && ord !== null && ord !== undefined) {
        patterns.push({ type: `sep${i}`, qtd: 1, char, ord });
      }
    }
    
    // Sort by order and create elements
    patterns.sort((a, b) => a.ord - b.ord);
    
    patterns.forEach((pattern, index) => {
      if (pattern.type.startsWith('sep')) {
        // Separator element
        reconstructedElements.push({
          id: `reconstructed_${Date.now()}_${index}`,
          type: 'separator',
          value: pattern.char,
          label: pattern.char,
          maxLength: 1,
          displayChar: pattern.char
        });
      } else {
        // Field element
        const fieldName = pattern.type as 'prefixo' | 'area' | 'processo' | 'subprocesso' | 'atividade' | 'sufixo';
        
        // Map field names to correct types
        let elementType: 'prefix' | 'suffix' | 'data';
        let elementValue: string;
        let elementLabel: string;
        
        if (fieldName === 'prefixo') {
          elementType = 'prefix';
          elementValue = 'prefixo';
          elementLabel = 'Prefixo';
        } else if (fieldName === 'sufixo') {
          elementType = 'suffix'; 
          elementValue = 'sufixo';
          elementLabel = 'Sufixo';
        } else {
          elementType = 'data';
          elementValue = fieldName;
          elementLabel = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
        }
        
        reconstructedElements.push({
          id: `reconstructed_${Date.now()}_${index}`,
          type: elementType,
          value: elementValue,
          label: elementLabel,
          maxLength: pattern.qtd,
          displayChar: pattern.char
        });
      }
    });

    // Sync centralized pattern max char from client (if present)
    try {
      const clientMax = Number(client.pattern_name_max_char);
      const resolved = (clientMax && !Number.isNaN(clientMax)) ? clientMax : 30;
      setPatternNameMaxChar(resolved);
      setMaxTotalLength(resolved);
      setCompositeMaxTotalLength(resolved);
      setRiskMaxTotalLength(resolved);
    } catch (err) {
      // ignore
    }
    
    console.log('✅ Elementos reconstruídos:', reconstructedElements);
    setConstructorElements(reconstructedElements);
    
    // Load field configurations from saved data
    setFieldConfigs(prevConfigs => {
      const newFieldConfigs = { ...prevConfigs };
      const configFieldTypes = ['prefixo', 'area', 'processo', 'subprocesso', 'atividade', 'sufixo'] as const;
      
      configFieldTypes.forEach(type => {
        const qtd = client[`pattern_s_${type}_qtd`];
        const char = client[`pattern_s_${type}_char`];
        
        if (qtd !== null && qtd !== undefined) {
          newFieldConfigs[type].qtd = qtd;
        }
        if (char) {
          newFieldConfigs[type].char = char;
        }
      });
      
      console.log('✅ Configurações de campos carregadas:', newFieldConfigs);
      return newFieldConfigs;
    });
    
    // Load prefix patterns from saved data
    setPrefixPatterns({
      simples: client.pattern_s_prefixo_simples || '',
      mestre: client.pattern_s_prefixo_mestre || '',
      derivado: client.pattern_s_prefixo_derivado || '',
      risco: client.pattern_s_prefixo_risco || ''
    });
    
    // Load risk prefix pattern from saved data
    setRiskPrefixPattern(client.pattern_r_prefixo_risco || '');
    setRiskPrefixPatternMestre(client.pattern_r_prefixo_risco_mestre || '');
    setRiskPrefixPatternDerivado(client.pattern_r_prefixo_risco_derivado || '');
  }, [client]);

  // Reconstruct risk constructor elements from saved data
  useEffect(() => {
    if (!client) return;
    
    console.log('🔄 Reconstruindo elementos de risco salvos...');
    
    const reconstructedElements: NameElement[] = [];
    const elementFieldMap: Array<{ type: string, field: string, prefix?: string, label: string }> = [];
    
    // Collect all risk pattern elements with their order
    const riskElementTypes = ['prefixo', 'area', 'processo', 'subprocesso', 'atividade', 'n1', 'n2', 'n3', 'n4', 'acao', 'sufixo'];
    
    riskElementTypes.forEach(type => {
      const ord = client[`pattern_r_${type}_ord`];
      if (ord !== null && ord !== undefined) {
        let prefix: string | undefined;
        let label = type.charAt(0).toUpperCase() + type.slice(1);
        
        if (type === 'prefixo') {
          prefix = 'prefix';
          label = 'Prefixo';
        } else if (type === 'sufixo') {
          prefix = 'suffix';
          label = 'Sufixo';
        } else if (type === 'acao') {
          label = 'Ação';
        }
        
        elementFieldMap.push({ type, field: type, prefix, label });
      }
    });
    
    // Collect separators
    for (let i = 1; i <= 6; i++) {
      const ord = client[`pattern_r_sep${i}_ord`];
      if (ord !== null && ord !== undefined) {
        const char = client[`pattern_r_sep${i}_char`];
        elementFieldMap.push({ 
          type: 'separator', 
          field: `sep${i}`, 
          label: `Separador: ${char}` 
        });
      }
    }
    
    // Sort by order and reconstruct elements
    elementFieldMap.sort((a, b) => {
      const aOrd = a.type === 'separator' ? 
        client[`pattern_r_${a.field}_ord`] : 
        client[`pattern_r_${a.type}_ord`];
      const bOrd = b.type === 'separator' ? 
        client[`pattern_r_${b.field}_ord`] : 
        client[`pattern_r_${b.type}_ord`];
      return (aOrd || 0) - (bOrd || 0);
    });
    
    elementFieldMap.forEach(({ type, field, prefix, label }, index) => {
      if (type === 'separator') {
        const char = client[`pattern_r_${field}_char`];
        if (char) {
          reconstructedElements.push({
            id: `risk_reconstructed_${Date.now()}_${index}`,
            type: 'separator',
            value: char,
            label: `Separador: ${char}`,
            maxLength: 1,
            displayChar: char
          });
        }
        return;
      }
      
      const fieldName = type;
      const qtd = client[`pattern_r_${fieldName}_qtd`];
      const char = client[`pattern_r_${fieldName}_char`];
      
      if (qtd !== null && qtd !== undefined && char) {
        let elementType: 'prefix' | 'suffix' | 'data' | 'separator';
        let elementValue: string;
        let elementLabel: string;
        
        if (fieldName === 'prefixo') {
          elementType = 'prefix';
          elementValue = 'prefixo';
          elementLabel = 'Prefixo';
        } else if (fieldName === 'sufixo') {
          elementType = 'suffix'; 
          elementValue = 'sufixo';
          elementLabel = 'Sufixo';
        } else {
          elementType = 'data';
          elementValue = fieldName;
          // Converter n1, n2, n3, n4 para "Nível 1", "Nível 2", etc.
          if (fieldName === 'n1') {
            elementLabel = 'Nível 1';
          } else if (fieldName === 'n2') {
            elementLabel = 'Nível 2';
          } else if (fieldName === 'n3') {
            elementLabel = 'Nível 3';
          } else if (fieldName === 'n4') {
            elementLabel = 'Nível 4';
          } else if (fieldName === 'acao') {
            elementLabel = 'Ação';
          } else {
            elementLabel = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
          }
        }
        
        reconstructedElements.push({
          id: `risk_reconstructed_${Date.now()}_${index}`,
          type: elementType,
          value: elementValue,
          label: elementLabel,
          maxLength: qtd,
          displayChar: char
        });
      }
    });
    
    console.log('✅ Elementos de risco reconstruídos:', reconstructedElements);
    setRiskConstructorElements(reconstructedElements);
    
    // Load risk field configurations from saved data
    setRiskFieldConfigs(prevConfigs => {
      const newFieldConfigs = { ...prevConfigs };
      const configFieldTypes = ['prefixo', 'area', 'processo', 'subprocesso', 'atividade', 'n1', 'n2', 'n3', 'n4', 'acao', 'sufixo'] as const;
      
      configFieldTypes.forEach(type => {
        const qtd = client[`pattern_r_${type}_qtd`];
        const char = client[`pattern_r_${type}_char`];
        
        if (qtd !== null && qtd !== undefined) {
          newFieldConfigs[type].qtd = qtd;
        }
        if (char) {
          newFieldConfigs[type].char = char;
        }
      });
      
      console.log('✅ Configurações de campos de risco carregadas:', newFieldConfigs);
      return newFieldConfigs;
    });
  }, [client]);

  // compute whether any modal/overlay is open so a single backdrop can be shown
  const anyModalOpen = previewModalOpen || uploadResultModalOpen || confirmClearModalOpen || confirmClearClassificationsOpen || confirmClassifyOpen || editingCodez || !!editingLogRow || classifyProgressOpen || showLogModal || initialLoading || step4ClassifyOpen || uploadProgressOpen || clearClassificationsProgressOpen || step2ErrorModalOpen || step2SuccessModalOpen || prefixPatternsModalOpen || prefixPatternsSuccessModalOpen || compositePrefixPatternsModalOpen || compositePrefixPatternsSuccessModalOpen || riskPrefixModalOpen || riskPrefixSuccessModalOpen || riskMatrixModalOpen || confirmClearRiskMatrixModalOpen || orgLevelModalOpen || showRiskAlert || consolidationAlertOpen || consolidationConfirmOpen || aiConversionModalOpen || aiConfirmModalOpen || dashboardAlertOpen;

  // helpers
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  // Function to fetch preview data with pagination for large datasets
  const _fetchPreviewData = useCallback(async (tableKey: string, page: number = 1, limit: number = 100) => {
    if (!clientId) return;
    try {
      const url = `${base}/api/profilegen-data/${tableKey}/${clientId}?page=${page}&limit=${limit}`;
      console.debug('fetchPreviewData', { tableKey, page, limit });
      
      const res = await axios.get(url, { 
        withCredentials: true,
        timeout: 30000 // 30 seconds timeout
      });
      
      if (res && res.data) {
        const items = res.data.items || [];
        console.debug('fetchPreviewData result', { tableKey, itemsCount: items.length, page, limit });
        
        // Update preview data
        setPreviews(prev => ({ ...prev, [tableKey]: items }));
        
        // Update indicators if returned
        if (res.data.indicators) {
          setIndicators(prev => ({ ...prev, [tableKey]: res.data.indicators }));
        }
        
        return { items, pagination: res.data.pagination };
      }
    } catch (err: any) {
      console.error('fetchPreviewData error', err);
      throw err;
    }
  }, [clientId, base]);

  // Function to load all data progressively in chunks for large datasets
  const fetchAllDataProgressively = useCallback(async (tableKey: string, setState: (data: any[]) => void) => {
    if (!clientId) return [];
    
    try {
      // First, get total count
      const indicatorsRes = await axios.get(
        `${base}/api/profilegen-data/${tableKey}/${clientId}?indicatorsOnly=true`,
        { withCredentials: true, timeout: 10000 }
      );
      
      const totalCount = indicatorsRes.data?.indicators?.count || 0;
      console.log(`[fetchAllDataProgressively] ${tableKey}: total=${totalCount}`);
      
      if (totalCount === 0) {
        setState([]);
        return [];
      }
      
      // Load in chunks of 100k
      const CHUNK_SIZE = 100000;
      const totalPages = Math.ceil(totalCount / CHUNK_SIZE);
      let allData: any[] = [];
      
      setLoadingMessage(`Carregando ${tableKey === 'upcasis' ? 'AS-IS' : tableKey}...`);
      setLoadingProgress(0);
      setDataLoadingProgress({ table: tableKey, loaded: 0, total: totalCount });
      if (tableKey === 'upcasis') {
        setAsisLoadingProgress({ loaded: 0, total: totalCount });
      }
      
      for (let page = 1; page <= totalPages; page++) {
        const url = `${base}/api/profilegen-data/${tableKey}/${clientId}?page=${page}&limit=${CHUNK_SIZE}`;
        
        const res = await axios.get(url, { 
          withCredentials: true,
          timeout: 60000 // 60 seconds per chunk
        });
        
        const items = res.data?.items || [];
        allData = [...allData, ...items];
        
        // Update progress
        const progress = Math.round((allData.length / totalCount) * 100);
        setLoadingProgress(progress);
        setLoadingMessage(`Carregando ${tableKey === 'upcasis' ? 'AS-IS' : tableKey}: ${allData.length.toLocaleString()} / ${totalCount.toLocaleString()} registros`);
        setDataLoadingProgress({ table: tableKey, loaded: allData.length, total: totalCount });
        if (tableKey === 'upcasis') {
          setAsisLoadingProgress({ loaded: allData.length, total: totalCount });
        }
        
        console.log(`[fetchAllDataProgressively] ${tableKey}: loaded ${allData.length}/${totalCount} (${((allData.length/totalCount)*100).toFixed(1)}%)`);
        
        // Update state incrementally so UI can show progress
        setState(allData);
        
        // Small delay to avoid overwhelming the browser
        if (page < totalPages) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }
      
      setDataLoadingProgress(null);
      if (tableKey === 'upcasis') {
        setAsisLoadingProgress(null);
      }
      console.log(`[fetchAllDataProgressively] ${tableKey}: completed, total items=${allData.length}`);
      
      return allData;
    } catch (err) {
      setDataLoadingProgress(null);
      if (tableKey === 'upcasis') {
        setAsisLoadingProgress(null);
      }
      console.error(`[fetchAllDataProgressively] ${tableKey}: error`, err);
      throw err;
    }
  }, [clientId, base]);

  // Function to load all consolidation data progressively
  const fetchAllConsolidationData = useCallback(async (): Promise<any[]> => {
    if (!clientId) return [];
    
    try {
      // First, get total count
      const indicatorsRes = await axios.get(
        `${base}/api/profilegen-data/profilegen-consolidation/${clientId}?indicatorsOnly=true`,
        { withCredentials: true }
      );
      
      const totalCount = indicatorsRes.data?.indicators?.count || 0;
      console.log(`[fetchAllConsolidationData] total=${totalCount}`);
      
      if (totalCount === 0) {
        return [];
      }
      
      // Load in chunks of 5000
      const CHUNK_SIZE = 5000;
      const totalPages = Math.ceil(totalCount / CHUNK_SIZE);
      let allData: any[] = [];
      
      for (let page = 1; page <= totalPages; page++) {
        const url = `${base}/api/profilegen-data/profilegen-consolidation/${clientId}?page=${page}&limit=${CHUNK_SIZE}`;
        
        const res = await axios.get(url, { 
          withCredentials: true,
          timeout: 60000
        });
        
        const items = res.data?.items || [];
        allData = [...allData, ...items];
        
        console.log(`[fetchAllConsolidationData] loaded ${allData.length}/${totalCount} (${((allData.length/totalCount)*100).toFixed(1)}%)`);
      }
      
      console.log(`[fetchAllConsolidationData] completed, total items=${allData.length}`);
      return allData;
    } catch (err) {
      console.error(`[fetchAllConsolidationData] error`, err);
      return [];
    }
  }, [clientId, base]);

  // Load AS-IS data and full logs when dashboard is opened (only if not already loaded)
  useEffect(() => {
    if (dashboardOpen && clientId) {
      // Reset animation
      setPieAnimationTriggered(false);
      setTimeout(() => setPieAnimationTriggered(true), 100);
      
      (async () => {
        try {
          // Load all AS-IS data progressively for dashboard functionality
          if (upcasisRows.length === 0) {
            await fetchAllDataProgressively('upcasis', setUpcasisRows);
          }
          
          // Always load full logs and codez data for accurate dashboard indicators
          // This ensures computeDistinctUsersCount() and computeDistinctCodesCount() use complete datasets
          console.log('[Dashboard] Loading complete logs and codez data for accurate indicators...');
          
          // Load full logs progressively
          const logsIndicatorsRes = await axios.get(
            `${base}/api/profilegen-data/logs/${clientId}?indicatorsOnly=true`,
            { withCredentials: true, timeout: 10000 }
          );
          const totalLogs = logsIndicatorsRes.data?.indicators?.count || 0;
          if (totalLogs > 0) {
            const fullLogs = await fetchAllDataProgressively('logs', (data: any[]) => setPreviews(prev => ({ ...prev, logs: data })));
            console.log('[Dashboard] Loaded full logs:', fullLogs.length);
          }
          
          // Load full codez progressively
          const codezIndicatorsRes = await axios.get(
            `${base}/api/profilegen-data/codez/${clientId}?indicatorsOnly=true`,
            { withCredentials: true, timeout: 10000 }
          );
          const totalCodez = codezIndicatorsRes.data?.indicators?.count || 0;
          if (totalCodez > 0) {
            const fullCodez = await fetchAllDataProgressively('codez', (data: any[]) => {
              setPreviews(prev => ({ ...prev, codez: data }));
              setCodezItems(data);
            });
            console.log('[Dashboard] Loaded full codez:', fullCodez.length);
          }
        } catch (error) {
          console.error('Error loading dashboard data:', error);
        }
      })();
    }
  }, [dashboardOpen, clientId, fetchAllDataProgressively, upcasisRows.length, base]);

  function parseCSV(text: string): { rows: any[]; headers: string[] } {
    // remove BOM if present
    text = text.replace(/^\uFEFF/, '');
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length);
  if (!lines.length) return { rows: [], headers: [] };
    // detect delimiter by counting occurrences in the first few lines
    const sampleLines = lines.slice(0, Math.min(5, lines.length)).join('\n');
  const counts: Record<string, number> = { ',': (sampleLines.match(/,/g) || []).length, ';': (sampleLines.match(/;/g) || []).length, '\t': (sampleLines.match(/\t/g) || []).length };
  let delim: ',' | ';' | '\t' = ',';
  if (counts[';'] > counts[',']) delim = ';';
  if (counts['\t'] > counts[delim]) delim = '\t';
  const header = lines[0].split(delim).map(h => h.trim().replace(/^"|"$/g, ''));
  // normalize header keys to simpler identifiers (lowercase, trimmed)
  const headerKeys = header.map(h => h.toLowerCase().replace(/[^a-z0-9_]/g, '').trim());
    const rows = lines.slice(1).map(line => {
      const cols = line.split(delim);
      const obj: any = {};
      for (let i = 0; i < headerKeys.length; i++) {
        const key = headerKeys[i] || `col${i}`;
        obj[key] = (cols[i] || '').replace(/^"|"$/g, '').trim();
      }
      return obj;
    });
  return { rows, headers: headerKeys };
  }

  function normalizeImportedHeaders(headers: string[]): string[] {
    return (headers || []).map(h => String(h).toLowerCase().replace(/[^a-z0-9_]/g, '').trim());
  }

  function parseExcel(arrayBuffer: ArrayBuffer): { rows: any[]; headers: string[] } {
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) return { rows: [], headers: [] };
    const worksheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' }) as any[];
    const headers = rows.length ? normalizeImportedHeaders(Object.keys(rows[0] || {})) : [];
    return { rows, headers };
  }

  // Helper function to get maximum available length for a field
  const getMaxAvailableLength = (fieldType: 'prefixo' | 'area' | 'processo' | 'subprocesso' | 'atividade' | 'sufixo') => {
    const isInComposition = constructorElements.some(el => el.value === fieldType);
    if (!isInComposition) {
      // If not in composition, can use up to the total maximum
      return maxTotalLength;
    }
    
    // If in composition, calculate remaining space
    const currentLength = calculateTotalLength();
    const currentElementLength = constructorElements.find(el => el.value === fieldType)?.maxLength || 0;
    const remainingSpace = maxTotalLength - (currentLength - currentElementLength);
    return remainingSpace;
  };

  // Profile naming constructor functions
  const validateUniqueChar = (newChar: string, excludeId?: string, elementType?: string): boolean => {
    // Separators can have duplicate characters
    if (elementType === 'separator') return true;
    
    return !constructorElements.some(el => 
      el.id !== excludeId && 
      el.type !== 'separator' && // Don't check against separators
      el.displayChar && el.displayChar.toUpperCase() === newChar.toUpperCase()
    );
  };

  const addElementToConstructor = (element: Omit<NameElement, 'id'>) => {
    // Check for duplicate non-separator elements
    if (element.type !== 'separator') {
      const exists = constructorElements.some(el => 
        el.type === element.type && 
        (element.type === 'data' ? el.value === element.value : true)
      );
      if (exists) {
        setStep2ErrorMessage(`Elemento "${element.label}" já foi adicionado à composição.`);
        setStep2ErrorModalOpen(true);
        return;
      }
    }

    // Check separator limit (max 6)
    if (element.type === 'separator') {
      const separatorCount = constructorElements.filter(el => el.type === 'separator').length;
      if (separatorCount >= 6) {
        setStep2ErrorMessage('Máximo de 6 separadores permitidos na composição.');
        setStep2ErrorModalOpen(true);
        return;
      }
    }

    // Check for unique display character (only for non-separators)
    if (element.displayChar && !validateUniqueChar(element.displayChar, undefined, element.type)) {
      const duplicateElement = constructorElements.find(el => 
        el.type !== 'separator' && // Only check non-separators for duplicates
        el.displayChar && el.displayChar.toUpperCase() === element.displayChar!.toUpperCase()
      );
      const duplicateLabel = duplicateElement?.label || 'elemento existente';
      
      setStep2ErrorMessage(`O caractere "${element.displayChar.toUpperCase()}" já está sendo usado por ${duplicateLabel}. Cada elemento deve ter um caractere único.`);
      setStep2ErrorModalOpen(true);
      return;
    }

    // Check maximum length before adding
    const currentLength = calculateTotalLength();
    const newElementLength = element.maxLength || 1;
    if (currentLength + newElementLength > maxTotalLength) {
      setStep2ErrorMessage(`Adicionar este elemento excederia o comprimento máximo de ${maxTotalLength} caracteres. Comprimento atual: ${currentLength}, elemento adiciona: ${newElementLength}.`);
      setStep2ErrorModalOpen(true);
      return;
    }

    const newElement: NameElement = {
      ...element,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
    };
    setConstructorElements(prev => [...prev, newElement]);
  };

  const removeElementFromConstructor = (id: string) => {
    setConstructorElements(prev => prev.filter(el => el.id !== id));
  };

  // Function to reorder elements in constructor
  const reorderConstructorElements = async (fromIndex: number, toIndex: number) => {
    const newElements = [...constructorElements];
    const [removed] = newElements.splice(fromIndex, 1);
    newElements.splice(toIndex, 0, removed);
    setConstructorElements(newElements);
    
    // Auto-save the new positions to database
    try {
      await savePatternCompositionInternal(newElements);
    } catch (error) {
      console.error('Erro ao salvar posições dos elementos:', error);
      // Optionally show error to user or revert changes
    }
  };

  // Function to update element in constructor when settings change
  const updateElementInConstructor = (elementType: string, elementValue: string, newLength: number, newChar: string) => {
    // Find the element to update
    const elementToUpdate = constructorElements.find(el => {
      if (elementType === 'data') {
        return el.type === 'data' && el.value === elementValue;
      } else if (elementType === 'prefix') {
        return el.type === 'prefix';
      } else if (elementType === 'suffix') {
        return el.type === 'suffix';
      }
      return false;
    });

    // Check for unique display character (excluding the element being updated, only for non-separators)
    if (elementToUpdate && newChar && elementToUpdate.type !== 'separator' && !validateUniqueChar(newChar, elementToUpdate.id, elementToUpdate.type)) {
      const duplicateElement = constructorElements.find(el => 
        el.id !== elementToUpdate.id && 
        el.type !== 'separator' && // Only check non-separators for duplicates
        el.displayChar && el.displayChar.toUpperCase() === newChar.toUpperCase()
      );
      const duplicateLabel = duplicateElement?.label || 'elemento existente';
      
      setStep2ErrorMessage(`O caractere "${newChar.toUpperCase()}" já está sendo usado por ${duplicateLabel}. Cada elemento deve ter um caractere único.`);
      setStep2ErrorModalOpen(true);
      return;
    }

    // Check maximum length with the new length
    if (elementToUpdate) {
      const currentLength = calculateTotalLength();
      const oldLength = elementToUpdate.maxLength || 1;
      const lengthDifference = newLength - oldLength;
      
      if (currentLength + lengthDifference > maxTotalLength) {
        setStep2ErrorMessage(`Alterar o comprimento deste elemento excederia o limite máximo de ${maxTotalLength} caracteres. Comprimento atual: ${currentLength}, diferença: +${lengthDifference}.`);
        setStep2ErrorModalOpen(true);
        return;
      }
    }

    setConstructorElements(prev => 
      prev.map(el => {
        // For data elements, match by type and value
        if (elementType === 'data' && el.type === 'data' && el.value === elementValue) {
          return {
            ...el,
            maxLength: newLength,
            displayChar: newChar.toUpperCase()
          };
        }
        // For prefix/suffix, match by type only since they don't have specific values
        else if ((elementType === 'prefix' && el.type === 'prefix') ||
                 (elementType === 'suffix' && el.type === 'suffix')) {
          return {
            ...el,
            maxLength: newLength,
            displayChar: newChar.toUpperCase()
          };
        }
        return el;
      })
    );
  };

  // Internal function to save pattern composition with custom elements
  const savePatternCompositionInternal = async (elements: NameElement[]) => {
    if (!clientId) {
      console.error('Nenhum cliente selecionado.');
      return;
    }

    if (elements.length === 0) {
      console.error('Nenhum elemento para salvar.');
      return;
    }

    try {
      console.log('Salvando composição para cliente:', clientId);
      console.log('Elementos da composição:', elements);

      // Prepare data object with all pattern fields
      const patternData: Record<string, any> = {};
      
      // Initialize all fields as empty
      const elementTypes = ['prefixo', 'area', 'processo', 'subprocesso', 'atividade', 'sufixo'];
      elementTypes.forEach(type => {
        patternData[`pattern_s_${type}_qtd`] = null;
        patternData[`pattern_s_${type}_char`] = null;
        patternData[`pattern_s_${type}_ord`] = null;
      });

      for (let i = 1; i <= 6; i++) {
        patternData[`pattern_s_sep${i}_char`] = null;
        patternData[`pattern_s_sep${i}_ord`] = null;
      }

      // Fill data based on constructor elements
      let separatorIndex = 1;
      elements.forEach((element, index) => {
        const order = index + 1;

        if (element.type === 'separator') {
          if (separatorIndex <= 6) {
            patternData[`pattern_s_sep${separatorIndex}_char`] = element.value;
            patternData[`pattern_s_sep${separatorIndex}_ord`] = order;
            separatorIndex++;
          }
        } else {
          let fieldName = '';
          if (element.type === 'prefix') fieldName = 'prefixo';
          else if (element.type === 'suffix') fieldName = 'sufixo';
          else if (element.type === 'data') fieldName = element.value;

          if (fieldName) {
            patternData[`pattern_s_${fieldName}_qtd`] = element.maxLength;
            patternData[`pattern_s_${fieldName}_char`] = element.displayChar;
            patternData[`pattern_s_${fieldName}_ord`] = order;
          }
        }
      });

      // Get the client record to find the MongoDB _id
      const clientResponse = await axios.get(`${base}/api/profilegen-clients`, { 
        params: { q: clientId, searchColumn: 'clientId' },
        withCredentials: true 
      });

      if (!clientResponse.data || !clientResponse.data.items || clientResponse.data.items.length === 0) {
        throw new Error(`Cliente ${clientId} não encontrado na base de dados.`);
      }

      const clientRecord = clientResponse.data.items[0];
      const mongoId = clientRecord._id;

      // Update using the MongoDB _id
      await axios.put(`${base}/api/profilegen-clients/${mongoId}`, patternData, { withCredentials: true });

      console.log('Posições dos elementos salvas com sucesso');
    } catch (error: any) {
      console.error('Erro ao salvar posições dos elementos:', error);
      throw error;
    }
  };

  // Persist centralized pattern max char to profilegen-clients
  const persistPatternNameMaxChar = async (value: number) => {
    if (!clientId) throw new Error('Nenhum clientId');
    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const clientResponse = await axios.get(`${base}/api/profilegen-clients`, { params: { q: clientId, searchColumn: 'clientId' }, withCredentials: true });
      if (!clientResponse.data || !clientResponse.data.items || clientResponse.data.items.length === 0) throw new Error('cliente não encontrado');
      const clientRecord = clientResponse.data.items[0];
      const mongoId = clientRecord._id;
      const payload: any = { pattern_name_max_char: Number(value) };
      await axios.put(`${base}/api/profilegen-clients/${mongoId}`, payload, { withCredentials: true });
  // Update local client state for immediate reflection
  setClient((prev: any) => ({ ...(prev || {}), pattern_name_max_char: Number(value) }));
    } catch (err) {
      console.error('Erro ao persistir pattern_name_max_char', err);
      throw err;
    }
  };

  // Function to save pattern composition
  const savePatternComposition = async () => {
    if (!clientId) {
      showOrgDerivationAlert('Nenhum cliente selecionado.', 'error');
      return;
    }

    if (constructorElements.length === 0) {
      setStep2ErrorMessage('Adicione pelo menos um elemento à composição antes de salvar.');
      setStep2ErrorModalOpen(true);
      return;
    }

    // Validate maximum length
    const totalLength = constructorElements.reduce((sum, element) => {
      return sum + (element.maxLength || 1);
    }, 0);

    if (totalLength > maxTotalLength) {
      setStep2ErrorMessage(`A composição excede o comprimento máximo permitido de ${maxTotalLength} caracteres. Comprimento atual: ${totalLength} caracteres.`);
      setStep2ErrorModalOpen(true);
      return;
    }

    try {
      await savePatternCompositionInternal(constructorElements);
      setStep2SuccessMessage('Composição do padrão de nomenclatura salva com sucesso!');
      setStep2SuccessModalOpen(true);
    } catch (error: any) {
      console.error('Erro detalhado ao salvar composição:', error);
      console.error('Error response:', error.response);
      
      let errorMessage = 'Erro ao salvar a composição.';
      if (error.response) {
        errorMessage += ` Detalhes: Erro HTTP: ${error.response.status}`;
        if (error.response.data && error.response.data.message) {
          errorMessage += ` - ${error.response.data.message}`;
        }
      } else if (error.message) {
        errorMessage += ` Detalhes: ${error.message}`;
      }
      
      setStep2ErrorMessage(errorMessage + ' Verifique o console para mais detalhes.');
      setStep2ErrorModalOpen(true);
    }
  };

  // Risk profiles pattern composition functions
  const addRiskElementToConstructor = (element: Omit<NameElement, 'id'>) => {
    const newElement = { ...element, id: `risk_${Date.now()}_${Math.random()}` };
    
    if (element.type === 'separator') {
      const separatorCount = riskConstructorElements.filter(el => el.type === 'separator').length;
      if (separatorCount >= 6) {
        setStep2ErrorMessage('Máximo de 6 separadores permitidos.');
        setStep2ErrorModalOpen(true);
        return;
      }
      // Separadores podem ser repetidos - sem verificação de duplicata
    }

    if (element.type !== 'separator') {
      const exists = riskConstructorElements.some(el => 
        el.type === element.type && el.value === element.value
      );
      
      if (exists) {
        setStep2ErrorMessage(`O elemento "${element.label}" já foi adicionado à composição.`);
        setStep2ErrorModalOpen(true);
        return;
      }

      const totalLengthAfterAdd = calculateRiskTotalLength() + (element.maxLength || 1);
      if (totalLengthAfterAdd > riskMaxTotalLength) {
        setStep2ErrorMessage(`Adicionar este elemento excederia o limite máximo de ${riskMaxTotalLength} caracteres.`);
        setStep2ErrorModalOpen(true);
        return;
      }
    }

    setRiskConstructorElements(prev => [...prev, newElement]);
  };

  const removeRiskElementFromConstructor = (id: string) => {
    setRiskConstructorElements(prev => prev.filter(el => el.id !== id));
  };

  const reorderRiskConstructorElements = async (fromIndex: number, toIndex: number) => {
    const newElements = [...riskConstructorElements];
    const [removed] = newElements.splice(fromIndex, 1);
    newElements.splice(toIndex, 0, removed);
    setRiskConstructorElements(newElements);
  };

  const updateRiskElementConfig = async (type: string, field: 'qtd' | 'char', value: string | number) => {
    const elementToUpdate = riskConstructorElements.find(el => {
      if (type === 'prefixo' || type === 'sufixo') {
        return el.type === (type === 'prefixo' ? 'prefix' : 'suffix');
      }
      return el.type === 'data' && el.value === type;
    });

    if (!elementToUpdate) return;

    const newLength = field === 'qtd' ? Number(value) : elementToUpdate.maxLength || 1;
    const newChar = field === 'char' ? String(value).toUpperCase() : elementToUpdate.displayChar || 'X';

    if (field === 'char') {
      const duplicateElement = riskConstructorElements.find(el => 
        el.id !== elementToUpdate.id && el.displayChar?.toUpperCase() === newChar.toUpperCase()
      );
      
      if (duplicateElement) {
        let duplicateLabel = duplicateElement.label;
        if (duplicateElement.type === 'prefix') duplicateLabel = 'Prefixo';
        else if (duplicateElement.type === 'suffix') duplicateLabel = 'Sufixo';
        
        setStep2ErrorMessage(`O caractere "${newChar.toUpperCase()}" já está sendo usado por ${duplicateLabel}. Cada elemento deve ter um caractere único.`);
        setStep2ErrorModalOpen(true);
        return;
      }
    }

    if (elementToUpdate) {
      const currentLength = calculateRiskTotalLength();
      const oldLength = elementToUpdate.maxLength || 1;
      const lengthDifference = newLength - oldLength;
      
      if (currentLength + lengthDifference > riskMaxTotalLength) {
        setStep2ErrorMessage(`Alterar o comprimento deste elemento excederia o limite máximo de ${riskMaxTotalLength} caracteres. Comprimento atual: ${currentLength}, diferença: +${lengthDifference}.`);
        setStep2ErrorModalOpen(true);
        return;
      }
    }

    setRiskConstructorElements(prev => 
      prev.map(el => {
        if (el.id === elementToUpdate.id) {
          return {
            ...el,
            maxLength: newLength,
            displayChar: newChar
          };
        }
        return el;
      })
    );

    setRiskFieldConfigs(prev => ({
      ...prev,
      [type]: {
        ...prev[type as keyof typeof prev],
        [field]: field === 'qtd' ? newLength : newChar
      }
    }));
  };

  const calculateRiskTotalLength = () => {
    return riskConstructorElements.reduce((total, el) => total + (el.maxLength || 1), 0);
  };

  const generateRiskPreviewPattern = () => {
    if (riskConstructorElements.length === 0) {
      return 'Arraste elementos para criar o padrão';
    }
    
    return riskConstructorElements.map(el => {
      if (el.type === 'separator') {
        return el.value;
      }
      return el.displayChar?.repeat(el.maxLength || 1) || 'X'.repeat(el.maxLength || 1);
    }).join('');
  };

  const saveRiskPatternCompositionInternal = async (elements: NameElement[]) => {
    if (elements.length === 0) {
      console.error('❌ Nenhum elemento para salvar.');
      return;
    }

    try {
      console.log('💾 Salvando composição de perfis de risco para cliente:', clientId);
      console.log('📋 Elementos da composição:', elements);

      const patternData: Record<string, any> = {};
      
      // Initialize all risk pattern fields as empty
      const elementTypes = ['prefixo', 'area', 'processo', 'subprocesso', 'atividade', 'n1', 'n2', 'n3', 'n4', 'acao', 'sufixo'];
      elementTypes.forEach(type => {
        patternData[`pattern_r_${type}_qtd`] = null;
        patternData[`pattern_r_${type}_char`] = null;
        patternData[`pattern_r_${type}_ord`] = null;
      });

      for (let i = 1; i <= 6; i++) {
        patternData[`pattern_r_sep${i}_char`] = null;
        patternData[`pattern_r_sep${i}_ord`] = null;
      }

      console.log('🧹 Campos inicializados como null:', patternData);

      let separatorIndex = 0;
      elements.forEach((element, index) => {
        const order = index + 1;
        
        if (element.type === 'separator') {
          separatorIndex++;
          if (separatorIndex <= 6) {
            patternData[`pattern_r_sep${separatorIndex}_char`] = element.value;
            patternData[`pattern_r_sep${separatorIndex}_ord`] = order;
            console.log(`📌 Separador ${separatorIndex}: char="${element.value}", ord=${order}`);
          }
        } else {
          let fieldName = '';
          if (element.type === 'prefix') {
            fieldName = 'prefixo';
          } else if (element.type === 'suffix') {
            fieldName = 'sufixo';
          } else if (element.type === 'data') {
            fieldName = element.value;
          }
          
          if (fieldName && elementTypes.includes(fieldName)) {
            patternData[`pattern_r_${fieldName}_qtd`] = element.maxLength;
            patternData[`pattern_r_${fieldName}_char`] = element.displayChar;
            patternData[`pattern_r_${fieldName}_ord`] = order;
            console.log(`📌 Campo ${fieldName}: qtd=${element.maxLength}, char="${element.displayChar}", ord=${order}`);
          }
        }
      });

      console.log('📦 Dados finais para envio:', patternData);

      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      
      console.log('🔍 Buscando cliente por ID:', clientId);
      const clientResponse = await axios.get(`${base}/api/profilegen-clients`, { 
        params: { q: clientId, searchColumn: 'clientId' }, 
        withCredentials: true 
      });

      if (!clientResponse.data.items || clientResponse.data.items.length === 0) {
        console.error('❌ Cliente não encontrado');
        return;
      }

      const clientRecord = clientResponse.data.items[0];
      const mongoId = clientRecord._id;
      console.log('✅ Cliente encontrado, MongoDB ID:', mongoId);

      console.log('🚀 Enviando PUT request para salvar...');
      console.log('🔗 URL da requisição:', `${base}/api/profilegen-clients/${mongoId}`);
      console.log('📦 Payload completo:', JSON.stringify(patternData, null, 2));
      
      const updateResponse = await axios.put(`${base}/api/profilegen-clients/${mongoId}`, patternData, { 
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Status da resposta:', updateResponse.status);
      console.log('✅ Headers da resposta:', updateResponse.headers);
      console.log('✅ Resposta do servidor:', updateResponse.data);
      
      // Verificar se os campos foram realmente salvos
      console.log('🔍 Verificando se os campos pattern_r_* foram salvos...');
      const verificationResponse = await axios.get(`${base}/api/profilegen-clients`, { 
        params: { q: clientId, searchColumn: 'clientId' }, 
        withCredentials: true 
      });
      
      if (verificationResponse.data?.items?.[0]) {
        const updatedClient = verificationResponse.data.items[0];
        console.log('🧪 Verificação - campos pattern_r_* no banco:');
        Object.keys(patternData).forEach(key => {
          if (key.startsWith('pattern_r_')) {
            console.log(`   ${key}: ${updatedClient[key]} (enviado: ${patternData[key]})`);
          }
        });
      }
      
      console.log('✅ Composição de perfis de risco salva com sucesso');
    } catch (error: any) {
      console.error('❌ Erro ao salvar composição de perfis de risco:', error);
      if (error.response) {
        console.error('❌ Status da resposta:', error.response.status);
        console.error('❌ Dados da resposta:', error.response.data);
      }
      throw error;
    }
  };

  const saveRiskPatternComposition = async () => {
    if (!clientId) {
      showOrgDerivationAlert('Nenhum cliente selecionado.', 'error');
      return;
    }

    if (riskConstructorElements.length === 0) {
      setStep2ErrorMessage('Adicione pelo menos um elemento à composição antes de salvar.');
      setStep2ErrorModalOpen(true);
      return;
    }

    const totalLength = riskConstructorElements.reduce((sum, element) => {
      return sum + (element.maxLength || 1);
    }, 0);

    if (totalLength > riskMaxTotalLength) {
      setStep2ErrorMessage(`A composição excede o comprimento máximo permitido de ${riskMaxTotalLength} caracteres. Comprimento atual: ${totalLength} caracteres.`);
      setStep2ErrorModalOpen(true);
      return;
    }

    try {
      console.log('🚀 Iniciando salvamento de composição de risco...');
      console.log('Cliente ID:', clientId);
      console.log('Elementos do construtor:', riskConstructorElements);
      
      await saveRiskPatternCompositionInternal(riskConstructorElements);
      
      console.log('✅ Composição de risco salva com sucesso!');
      setStep2SuccessMessage('Composição do padrão de nomenclatura de perfis de risco salva com sucesso!');
      setStep2SuccessModalOpen(true);
    } catch (error: any) {
      console.error('❌ Erro detalhado ao salvar composição de perfis de risco:', error);
      console.error('Error response:', error.response);
      
      let errorMessage = 'Erro ao salvar a composição de perfis de risco.';
      if (error.response) {
        errorMessage += ` Detalhes: Erro HTTP: ${error.response.status}`;
        if (error.response.data && error.response.data.message) {
          errorMessage += ` - ${error.response.data.message}`;
        }
      } else if (error.message) {
        errorMessage += ` Detalhes: ${error.message}`;
      }
      
      setStep2ErrorMessage(errorMessage + ' Verifique o console para mais detalhes.');
      setStep2ErrorModalOpen(true);
    }
  };

  // Function to save prefix patterns
  const savePrefixPatterns = async () => {
    if (!clientId) {
      showOrgDerivationAlert('Nenhum cliente selecionado.', 'error');
      return;
    }

    try {
      console.log('Salvando padrões de prefixo para cliente:', clientId);
      console.log('Padrões de prefixo:', prefixPatterns);

      // Prepare data object with prefix pattern fields
      const prefixData = {
        pattern_s_prefixo_simples: prefixPatterns.simples,
        pattern_s_prefixo_mestre: prefixPatterns.mestre,
        pattern_s_prefixo_derivado: prefixPatterns.derivado
      };

      // Get the client record to find the MongoDB _id
      const clientResponse = await axios.get(`${base}/api/profilegen-clients`, { 
        params: { q: clientId, searchColumn: 'clientId' },
        withCredentials: true 
      });

      if (!clientResponse.data || !clientResponse.data.items || clientResponse.data.items.length === 0) {
        throw new Error(`Cliente ${clientId} não encontrado na base de dados.`);
      }

      const clientRecord = clientResponse.data.items[0];
      const mongoId = clientRecord._id;

      // Update using the MongoDB _id
      const response = await axios.put(`${base}/api/profilegen-clients/${mongoId}`, prefixData, { withCredentials: true });

      console.log('Padrões de prefixo salvos com sucesso:', response.data);
      setPrefixPatternsModalOpen(false);
      setPrefixPatternsSuccessModalOpen(true);
    } catch (error: any) {
      console.error('Erro ao salvar padrões de prefixo:', error);
      let errorMessage = 'Erro ao salvar os padrões de prefixo.';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage += ` Detalhes: ${error.response.data.message}`;
      }
      showOrgDerivationAlert(errorMessage, 'error');
    }
  };

  // Function to save risk prefix pattern
  const saveRiskPrefixPattern = async () => {
    if (!clientId) {
      showOrgDerivationAlert('Nenhum cliente selecionado.', 'error');
      return;
    }

    try {
      console.log('Salvando padrão de prefixo de risco para cliente:', clientId);
      console.log('Padrão de prefixo de risco:', riskPrefixPattern);
      console.log('Padrão de prefixo de risco mestre:', riskPrefixPatternMestre);
      console.log('Padrão de prefixo de risco derivado:', riskPrefixPatternDerivado);

      // Prepare data object with risk prefix pattern fields
      const riskPrefixData = {
        pattern_r_prefixo_risco: riskPrefixPattern,
        pattern_r_prefixo_risco_mestre: riskPrefixPatternMestre,
        pattern_r_prefixo_risco_derivado: riskPrefixPatternDerivado
      };

      // Get the client record to find the MongoDB _id
      const clientResponse = await axios.get(`${base}/api/profilegen-clients`, { 
        params: { q: clientId, searchColumn: 'clientId' },
        withCredentials: true 
      });

      if (!clientResponse.data || !clientResponse.data.items || clientResponse.data.items.length === 0) {
        throw new Error(`Cliente ${clientId} não encontrado na base de dados.`);
      }

      const clientRecord = clientResponse.data.items[0];
      const mongoId = clientRecord._id;

      // Update using the MongoDB _id
      const response = await axios.put(`${base}/api/profilegen-clients/${mongoId}`, riskPrefixData, { withCredentials: true });

      console.log('Padrão de prefixo de risco salvo com sucesso:', response.data);
      setRiskPrefixModalOpen(false);
      setRiskPrefixSuccessModalOpen(true);
    } catch (error: any) {
      console.error('Erro ao salvar padrão de prefixo de risco:', error);
      let errorMessage = 'Erro ao salvar o padrão de prefixo de risco.';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage += ` Detalhes: ${error.response.data.message}`;
      }
      showOrgDerivationAlert(errorMessage, 'error');
    }
  };

  // Function to save derivation status
  const saveDerivationStatus = async (status: boolean) => {
    if (!clientId) {
      console.error('Cliente não selecionado para salvar status de derivação');
      return;
    }

    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      
      // Get client record to find MongoDB _id
      const clientResponse = await axios.get(`${base}/api/profilegen-clients`, { 
        params: { q: clientId, searchColumn: 'clientId' }, 
        withCredentials: true 
      });

      if (!clientResponse.data.items || clientResponse.data.items.length === 0) {
        console.error('Cliente não encontrado');
        return;
      }

      const clientRecord = clientResponse.data.items[0];
      const mongoId = clientRecord._id;

      const derivationData = {
        derivation_status: status
      };

      await axios.put(`${base}/api/profilegen-clients/${mongoId}`, derivationData, { withCredentials: true });
      
      console.log('Status de derivação salvo com sucesso:', status);
    } catch (error: any) {
      console.error('Erro ao salvar status de derivação:', error);
      let errorMessage = 'Erro ao salvar o status de derivação.';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage += ` Detalhes: ${error.response.data.message}`;
      }
      showOrgDerivationAlert(errorMessage, 'error');
    }
  };

  // Function to save derivation user status to database
  const saveDerivationUserStatus = async (status: boolean) => {
    if (!clientId) {
      console.error('Cliente não selecionado para salvar status de derivação de usuário');
      return;
    }

    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      
      // Get client record to find MongoDB _id
      const clientResponse = await axios.get(`${base}/api/profilegen-clients`, { 
        params: { q: clientId, searchColumn: 'clientId' }, 
        withCredentials: true 
      });

      if (!clientResponse.data.items || clientResponse.data.items.length === 0) {
        console.error('Cliente não encontrado');
        return;
      }

      const clientRecord = clientResponse.data.items[0];
      const mongoId = clientRecord._id;

      const derivationData = {
        derivation_user_status: status
      };

      await axios.put(`${base}/api/profilegen-clients/${mongoId}`, derivationData, { withCredentials: true });
      
      console.log('Status de derivação de usuário salvo com sucesso:', status);
    } catch (error: any) {
      console.error('Erro ao salvar status de derivação de usuário:', error);
      let errorMessage = 'Erro ao salvar o status de derivação de usuário.';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage += ` Detalhes: ${error.response.data.message}`;
      }
      showOrgDerivationAlert(errorMessage, 'error');
    }
  };

  // Handle derivation status toggle change
  const handleDerivationStatusChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStatus = event.target.checked;
    setDerivationStatus(newStatus);
    await saveDerivationStatus(newStatus);
  };

  // Handle derivation user status toggle change
  const handleDerivationUserStatusChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStatus = event.target.checked;
    setDerivationUserStatus(newStatus);
    await saveDerivationUserStatus(newStatus);
  };

  // Helper functions for organizational derivation modals
  const showOrgDerivationAlert = (message: string, type: 'error' | 'success' | 'info' = 'info') => {
    setOrgDerivationAlertMessage(message);
    setOrgDerivationAlertType(type);
    pushToast({
      variant: type === 'error' ? 'danger' : type === 'success' ? 'success' : 'info',
      title: type === 'error' ? 'Erro' : type === 'success' ? 'Sucesso' : 'Informação',
      message,
      durationMs: 6500
    });
  };

  const showOrgDerivationConfirm = (message: string, action: () => void) => {
    setOrgDerivationConfirmMessage(message);
    setOrgDerivationConfirmAction(() => action);
    setOrgDerivationConfirmModalOpen(true);
  };

  // Helper function for consolidation alerts
  const showConsolidationAlert = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setConsolidationAlertMessage(message);
    setConsolidationAlertType(type);
    pushToast({
      variant: type === 'error' ? 'danger' : type === 'success' ? 'success' : 'info',
      title: type === 'error' ? 'Erro' : type === 'success' ? 'Sucesso' : 'Informação',
      message,
      durationMs: 6500
    });
  }, [pushToast]);

  // Helper function for consolidation confirmation
  const showConsolidationConfirm = (message: string, action: () => void) => {
    setConsolidationConfirmMessage(message);
    setConsolidationConfirmAction(() => action);
    setConsolidationConfirmOpen(true);
  };

  // Helper function for AI confirmation
  const showAiConfirm = useCallback((message: string, action: () => void) => {
    setAiConfirmMessage(message);
    setAiConfirmCallback(() => action);
    setAiConfirmModalOpen(true);
  }, []);

  // Function to load organizational levels for the modal
  const loadOrgLevels = async () => {
    setLoadingOrgLevels(true);
    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const response = await axios.get(`${base}/api/sap-actions-orglevel/distinct-levels`, { withCredentials: true });
      setOrgLevelsData(response.data.items || []);
    } catch (error: any) {
      console.error('Erro ao carregar níveis organizacionais:', error);
      showOrgDerivationAlert('Erro ao carregar níveis organizacionais.', 'error');
    } finally {
      setLoadingOrgLevels(false);
    }
  };

  // Function to open organizational level modal
  const openOrgLevelModal = () => {
    setSelectedOrgLevel(derivationOrgLevel); // Pre-select current value
    setOrgLevelModalOpen(true);
    loadOrgLevels();
  };

  // Function to save selected organizational level
  const saveSelectedOrgLevel = async () => {
    if (!selectedOrgLevel) {
      showOrgDerivationAlert('Selecione um nível organizacional.', 'error');
      return;
    }

    if (!clientId) {
      console.error('Cliente não selecionado para salvar nível organizacional');
      return;
    }

    // Check if the selected level is different from current level
    const isLevelChanging = selectedOrgLevel !== derivationOrgLevel;
    
    // Check if there are loaded data that will be lost
    const hasOrgLevelValues = orgLevelValues.length > 0;
    const hasUserMappings = userOrgLevelMappings.length > 0;
    const hasDataToLose = hasOrgLevelValues || hasUserMappings;

    // If level is changing and there's data, show confirmation
    if (isLevelChanging && hasDataToLose) {
      let warningMessage = `Atenção! Alterar o nível organizacional de "${derivationOrgLevel}" para "${selectedOrgLevel}" resultará na perda dos seguintes dados:\n\n`;
      
      if (hasOrgLevelValues) {
        warningMessage += `• ${orgLevelValues.length} valores de nível organizacional carregados\n`;
      }
      
      if (hasUserMappings) {
        warningMessage += `• ${userOrgLevelMappings.length} mapeamentos de usuário carregados\n`;
      }
      
      warningMessage += '\nTodos esses dados serão permanentemente removidos.\n\nDeseja continuar?';
      
      showOrgDerivationConfirm(warningMessage, async () => {
        await performOrgLevelChange();
      });
    } else {
      // No data to lose or level not changing, proceed directly
      await performOrgLevelChange();
    }
  };

  // Helper function to perform the actual organizational level change
  const performOrgLevelChange = async () => {
    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      
      // Get client record to find MongoDB _id
      const clientResponse = await axios.get(`${base}/api/profilegen-clients`, { 
        params: { q: clientId, searchColumn: 'clientId' }, 
        withCredentials: true 
      });

      if (!clientResponse.data.items || clientResponse.data.items.length === 0) {
        console.error('Cliente não encontrado');
        return;
      }

      const clientRecord = clientResponse.data.items[0];
      const mongoId = clientRecord._id;

      // Clear existing data if level is changing
      const isLevelChanging = selectedOrgLevel !== derivationOrgLevel;
      if (isLevelChanging) {
        try {
          // Clear organizational level values
          if (orgLevelValues.length > 0) {
            await saveOrgLevelValues([]);
            setOrgLevelValues([]);
          }
          
          // Clear user-org level mappings
          if (userOrgLevelMappings.length > 0) {
            await axios.delete(`${base}/api/profilegen-userorglevel/${clientId}/${derivationOrgLevel}`, { 
              withCredentials: true 
            });
            setUserOrgLevelMappings([]);
          }
          
          // Clear validation issues
          setUserOrgLevelValidationIssues({ invalidValues: [], missingUsers: [], unmappedUsers: [], hasIssues: false });
        } catch (clearError: any) {
          console.error('Erro ao limpar dados anteriores:', clearError);
          // Continue with level change even if clearing fails
        }
      }

      const derivationData = {
        derivation_orglevel: selectedOrgLevel
      };

      await axios.put(`${base}/api/profilegen-clients/${mongoId}`, derivationData, { withCredentials: true });
      
      setDerivationOrgLevel(selectedOrgLevel);
      setOrgLevelModalOpen(false);
      
      if (isLevelChanging && (orgLevelValues.length > 0 || userOrgLevelMappings.length > 0)) {
        showOrgDerivationAlert(`Nível organizacional alterado para "${selectedOrgLevel}" e dados anteriores foram removidos com sucesso.`, 'success');
      } else {
        console.log('Nível organizacional salvo com sucesso:', selectedOrgLevel);
      }
    } catch (error: any) {
      console.error('Erro ao salvar nível organizacional:', error);
      let errorMessage = 'Erro ao salvar o nível organizacional.';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage += ` Detalhes: ${error.response.data.message}`;
      }
      showOrgDerivationAlert(errorMessage, 'error');
    }
  };

  // Function to save organizational level values to database
  const saveOrgLevelValues = async (values: string[]) => {
    if (!clientId) {
      console.error('Cliente não selecionado para salvar valores organizacionais');
      return;
    }

    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      
      // Get client record to find MongoDB _id
      const clientResponse = await axios.get(`${base}/api/profilegen-clients`, { 
        params: { q: clientId, searchColumn: 'clientId' }, 
        withCredentials: true 
      });

      if (!clientResponse.data.items || clientResponse.data.items.length === 0) {
        console.error('Cliente não encontrado');
        return;
      }

      const clientRecord = clientResponse.data.items[0];
      const mongoId = clientRecord._id;

      const derivationData = {
        derivation_orglevel_values: values.join(',')
      };

      await axios.put(`${base}/api/profilegen-clients/${mongoId}`, derivationData, { withCredentials: true });
      
      console.log('Valores organizacionais salvos com sucesso:', values);
    } catch (error: any) {
      console.error('Erro ao salvar valores organizacionais:', error);
      throw error;
    }
  };

  // Function to clear organizational level values
  const clearOrgLevelValuesData = async () => {
    if (orgLevelValues.length === 0) {
      showOrgDerivationAlert('Não há dados para limpar.', 'info');
      return;
    }

    showOrgDerivationConfirm('Tem certeza que deseja limpar todos os valores organizacionais? Esta ação não pode ser desfeita.', async () => {
      try {
        await saveOrgLevelValues([]); // Save empty array
        setOrgLevelValues([]);
        showOrgDerivationAlert('Valores organizacionais limpos com sucesso.', 'success');
      } catch (error: any) {
        console.error('Erro ao limpar valores organizacionais:', error);
        showOrgDerivationAlert('Erro ao limpar os valores organizacionais.', 'error');
      }
    });
  };

  // Functions for organizational level values CSV upload
  const handleOrgLevelFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setOrgLevelUploadLoading(true);

    try {
      const text = await file.text();
      const lines = text.split('\n').map(line => line.trim()).filter(line => line);
      
      if (lines.length === 0) {
        showOrgDerivationAlert('O arquivo CSV está vazio.', 'error');
        return;
      }

      // Parse CSV - assume first column contains the values
      const values = lines.map(line => {
        // Handle comma-separated values, quotes, etc.
        const columns = line.split(',').map(col => col.trim().replace(/^["']|["']$/g, ''));
        return columns[0]; // Take first column
      }).filter(value => value); // Remove empty values

      if (values.length === 0) {
        showOrgDerivationAlert('Nenhum valor válido encontrado no arquivo CSV.', 'error');
        return;
      }

      // Validate against suffix length if available
      if (client?.pattern_s_sufixo_qtd) {
        const invalidValues = values.filter(v => v.length > client.pattern_s_sufixo_qtd!);
        if (invalidValues.length > 0) {
          const message = `${invalidValues.length} valores excedem o limite de ${client.pattern_s_sufixo_qtd} caracteres. Deseja continuar mesmo assim?`;
          showOrgDerivationConfirm(message, () => {
            setOrgLevelValues(values);
            saveOrgLevelValues(values);
            showOrgDerivationAlert(`${values.length} valores carregados com sucesso!`, 'success');
          });
          return;
        }
      }

      setOrgLevelValues(values);
      await saveOrgLevelValues(values);
      
      showOrgDerivationAlert(`${values.length} valores carregados com sucesso!`, 'success');
    } catch (error: any) {
      console.error('Erro ao processar arquivo CSV:', error);
      showOrgDerivationAlert('Erro ao processar o arquivo CSV. Verifique o formato e tente novamente.', 'error');
    } finally {
      setOrgLevelUploadLoading(false);
      // Clear file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const visualizeOrgLevelValues = () => {
    if (orgLevelValues.length === 0) {
      showOrgDerivationAlert('Nenhum valor carregado para visualizar.', 'info');
      return;
    }
    // Reset search and pagination when opening modal
    setOrgLevelValuesSearchQ('');
    setOrgLevelValuesSearchColumn('');
    setOrgLevelValuesPage(1);
    setOrgLevelValuesModalOpen(true);
  };

  const clearOrgLevelValues = () => {
    clearOrgLevelValuesData();
  };

  // Function to get users from logs (optimized - fetches only distinct users, not all log records)
  const getUsersFromLogs = useCallback(async (): Promise<string[]> => {
    if (!clientId) return [];

    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      // Use optimized endpoint that returns only distinct users (not limited by pagination)
      const response = await axios.get(`${base}/api/profilegen-data/profilegen-logs/${clientId}/distinct-users`, {
        withCredentials: true
      });

      if (response.data && Array.isArray(response.data.users)) {
        setTotalUsersInLogs(response.data.users.length);
        return response.data.users;
      }
      
      console.warn('Unexpected response structure from distinct-users endpoint:', response.data);
      setTotalUsersInLogs(0);
      return [];
    } catch (error: any) {
      console.error('Erro ao buscar usuários distintos dos logs:', error);
      setTotalUsersInLogs(0);
      return [];
    }
  }, [clientId]);

  // Function to validate user-orglevel mappings
  const validateUserOrgLevelMappings = async (mappings: {user: string, value: string}[]) => {
    const issues = {
      invalidValues: [] as {user: string, value: string}[],
      missingUsers: [] as string[],
      unmappedUsers: [] as string[],
      hasIssues: false
    };

    // 1. Validate that all orglevel_values exist in the loaded organizational level values
    const availableValues = orgLevelValues;
    for (const mapping of mappings) {
      if (!availableValues.includes(mapping.value)) {
        issues.invalidValues.push(mapping);
      }
    }

    // 2. Get users from logs and validate coverage
    const logUsers = await getUsersFromLogs();
    const mappingUsers = mappings.map(m => m.user);

    // Find users in mappings that don't exist in logs
    issues.missingUsers = mappingUsers.filter(user => !logUsers.includes(user));

    // Find users in logs that don't have mappings
    issues.unmappedUsers = logUsers.filter(user => !mappingUsers.includes(user));

    // Check if there are any issues
    issues.hasIssues = issues.invalidValues.length > 0 || issues.missingUsers.length > 0 || issues.unmappedUsers.length > 0;

    setUserOrgLevelValidationIssues(issues);
    return issues;
  };

  // Function to validate user-area data
  const fetchAllRowsForValidation = useCallback(async (tableKey: string): Promise<any[]> => {
    if (!clientId) return [];

    const indicatorsRes = await axios.get(
      `${base}/api/profilegen-data/${tableKey}/${clientId}?indicatorsOnly=true`,
      { withCredentials: true, timeout: 10000 }
    );

    const totalCount = indicatorsRes.data?.indicators?.count || 0;
    if (!totalCount) return [];

    const CHUNK_SIZE = 100000;
    const totalPages = Math.ceil(totalCount / CHUNK_SIZE);
    const allRows: any[] = [];

    for (let page = 1; page <= totalPages; page++) {
      const res = await axios.get(
        `${base}/api/profilegen-data/${tableKey}/${clientId}?page=${page}&limit=${CHUNK_SIZE}`,
        { withCredentials: true, timeout: 120000 }
      );
      const items = res.data?.items || [];
      allRows.push(...items);
    }

    return allRows;
  }, [clientId, base]);

  const validateUserAreaData = useCallback(async () => {
    const issues = {
      usersInAreaNotInLogs: [] as string[],
      usersInLogsNotInArea: [] as string[],
      usersWithMultipleAreas: [] as {user: string, areas: string[]}[],
      hasIssues: false
    };

    try {
      // Get users from logs
      const logUsers = await getUsersFromLogs();

      // Get full users from userxa (User x Area) data (all pages)
      const userAreaData = await fetchAllRowsForValidation('userxa');
      const userAreaMap = new Map<string, string[]>();
      
      // Build map of users and their areas
      userAreaData.forEach((item: any) => {
        const user = item.user;
        const area = item.area;
        if (!userAreaMap.has(user)) {
          userAreaMap.set(user, []);
        }
        userAreaMap.get(user)!.push(area);
      });

      const areaUsers = Array.from(userAreaMap.keys());
      setTotalUsersInArea(areaUsers.length);

      // 1. Find users in User x Area but not in logs
      issues.usersInAreaNotInLogs = areaUsers.filter(user => !logUsers.includes(user));

      // 2. Find users in logs but not in User x Area
      issues.usersInLogsNotInArea = logUsers.filter(user => !areaUsers.includes(user));

      // 3. Find users with multiple areas
      userAreaMap.forEach((areas, user) => {
        if (areas.length > 1) {
          issues.usersWithMultipleAreas.push({
            user,
            areas: Array.from(new Set(areas)) // Remove duplicates
          });
        }
      });

      // Check if there are any issues
      issues.hasIssues = issues.usersInAreaNotInLogs.length > 0 || 
                         issues.usersInLogsNotInArea.length > 0 || 
                         issues.usersWithMultipleAreas.length > 0;

      setUserAreaValidationIssues(issues);
      return issues;
    } catch (error: any) {
      console.error('Erro ao validar dados de Usuário x Area:', error);
      return issues;
    }
  }, [getUsersFromLogs, fetchAllRowsForValidation]);

  // Function to validate user-position data
  const validateUserPositionData = useCallback(async () => {
    const issues = {
      usersInPositionNotInLogs: [] as string[],
      usersInLogsNotInPosition: [] as string[],
      usersWithMultiplePositions: [] as {user: string, positions: string[]}[],
      hasIssues: false
    };

    try {
      // Get users from logs
      const logUsers = await getUsersFromLogs();

      // Get full users from userxpos (User x Position) data (all pages)
      const userPositionData = await fetchAllRowsForValidation('userxpos');
      const userPositionMap = new Map<string, string[]>();
      
      // Build map of users and their position structures (combine n1,n2,n3,n4 into a single identifier)
      userPositionData.forEach((item: any) => {
        const user = item.user;
        const positionStructure = `${item.n1 || ''}-${item.n2 || ''}-${item.n3 || ''}-${item.n4 || ''}`;
        if (!userPositionMap.has(user)) {
          userPositionMap.set(user, []);
        }
        userPositionMap.get(user)!.push(positionStructure);
      });

      const positionUsers = Array.from(userPositionMap.keys());
      setTotalUsersInPosition(positionUsers.length);

      // 1. Find users in User x Position but not in logs
      issues.usersInPositionNotInLogs = positionUsers.filter(user => !logUsers.includes(user));

      // 2. Find users in logs but not in User x Position
      issues.usersInLogsNotInPosition = logUsers.filter(user => !positionUsers.includes(user));

      // 3. Find users with multiple position structures
      userPositionMap.forEach((positions, user) => {
        // Remove duplicates and check if user has multiple different position structures
        const uniquePositions = Array.from(new Set(positions));
        if (uniquePositions.length > 1) {
          issues.usersWithMultiplePositions.push({
            user,
            positions: uniquePositions
          });
        }
      });

      // Check if there are any issues
      issues.hasIssues = issues.usersInPositionNotInLogs.length > 0 || 
                         issues.usersInLogsNotInPosition.length > 0 || 
                         issues.usersWithMultiplePositions.length > 0;

      setUserPositionValidationIssues(issues);
      return issues;
    } catch (error: any) {
      console.error('Erro ao validar dados de Usuário x Posição/Cargo:', error);
      return issues;
    }
  }, [getUsersFromLogs, fetchAllRowsForValidation]);

  // Functions for user-orglevel CSV upload
  const handleUserOrgLevelFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUserOrgLevelUploadLoading(true);

    try {
      const text = await file.text();
      const lines = text.split('\n').map(line => line.trim()).filter(line => line);
      
      if (lines.length === 0) {
        showOrgDerivationAlert('O arquivo CSV está vazio.', 'error');
        return;
      }

      // Parse CSV - expect two columns: user, orglevel_value
      // Support both comma and semicolon as separators
      const mappings: {user: string, value: string}[] = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        
        // Try semicolon first, then comma as separator
        let columns: string[];
        if (line.includes(';')) {
          columns = line.split(';').map(col => col.trim().replace(/^["']|["']$/g, ''));
        } else {
          columns = line.split(',').map(col => col.trim().replace(/^["']|["']$/g, ''));
        }
        
        if (columns.length < 2) {
          console.warn(`Linha ${i + 1} ignorada: menos de 2 colunas`);
          continue;
        }
        
        const user = columns[0].trim();
        const value = columns[1].trim();
        
        // Skip empty rows or rows that might be headers
        if (!user || !value || 
            (user.toLowerCase() === 'user' || user.toLowerCase() === 'usuario') ||
            (value.toLowerCase() === 'value' || value.toLowerCase() === 'valor' || value.toLowerCase() === 'orglevel_value')) {
          continue;
        }
        
        mappings.push({ user, value });
      }

      if (mappings.length === 0) {
        showOrgDerivationAlert('Nenhum mapeamento válido encontrado no arquivo CSV.\n\nFormato esperado:\n- Duas colunas: user e orglevel_value\n- Separadores aceitos: vírgula (,) ou ponto e vírgula (;)\n- Cabeçalho opcional', 'error');
        return;
      }

      // Validate values against suffix length if available
      if (client?.pattern_s_sufixo_qtd) {
        const invalidMappings = mappings.filter(m => m.value.length > client.pattern_s_sufixo_qtd!);
        if (invalidMappings.length > 0) {
          const message = `${invalidMappings.length} valores excedem o limite de ${client.pattern_s_sufixo_qtd} caracteres. Deseja continuar mesmo assim?`;
          showOrgDerivationConfirm(message, async () => {
            await processMappingsWithValidation(mappings);
          });
          return;
        }
      }

      await processMappingsWithValidation(mappings);
    } catch (error: any) {
      console.error('Erro ao processar arquivo CSV de usuários:', error);
      showOrgDerivationAlert('Erro ao processar o arquivo CSV.\n\nVerifique o formato:\n- Duas colunas: user e orglevel_value\n- Separadores aceitos: vírgula (,) ou ponto e vírgula (;)\n- Cabeçalho opcional', 'error');
    } finally {
      setUserOrgLevelUploadLoading(false);
      // Clear file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Helper function to process mappings with validation
  const processMappingsWithValidation = async (mappings: {user: string, value: string}[]) => {
    // Validate mappings against organizational level values and log users
    const validationIssues = await validateUserOrgLevelMappings(mappings);
    
    if (validationIssues.hasIssues) {
      // Show confirmation dialog for issues instead of blocking
      let warningMessage = 'Foram encontradas inconsistências nos dados:\n\n';
      
      if (validationIssues.invalidValues.length > 0) {
        warningMessage += `❌ Valores organizacionais não encontrados: ${validationIssues.invalidValues.length}\n`;
      }
      
      if (validationIssues.missingUsers.length > 0) {
        warningMessage += `❌ Usuários não encontrados nos logs: ${validationIssues.missingUsers.length}\n`;
      }
      
      if (validationIssues.unmappedUsers.length > 0) {
        warningMessage += `⚠️ Usuários dos logs sem mapeamento: ${validationIssues.unmappedUsers.length}\n`;
      }
      
      warningMessage += '\n✅ Deseja carregar mesmo assim?\n(Use o botão de diagnóstico vermelho para ver detalhes)';
      
      showOrgDerivationConfirm(warningMessage, async () => {
        await saveUserOrgLevelMappings(mappings);
        setUserOrgLevelMappings(mappings);
        showOrgDerivationAlert(`${mappings.length} mapeamentos carregados com sucesso!`, 'success');
      });
    } else {
      // No issues, proceed directly
      await saveUserOrgLevelMappings(mappings);
      setUserOrgLevelMappings(mappings);
      showOrgDerivationAlert(`${mappings.length} mapeamentos carregados com sucesso!`, 'success');
    }
  };

  const saveUserOrgLevelMappings = async (mappings: {user: string, value: string}[]) => {
    if (!clientId || !derivationOrgLevel) {
      console.error('Cliente ou nível organizacional não selecionado');
      return;
    }

    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      await axios.post(`${base}/api/profilegen-userorglevel`, {
        clientId,
        orglevel: derivationOrgLevel,
        mappings
      }, { withCredentials: true });
      
      console.log('Mapeamentos de usuário-nível organizacional salvos com sucesso');
    } catch (error: any) {
      console.error('Erro ao salvar mapeamentos:', error);
      throw error;
    }
  };

  const loadUserOrgLevelMappings = useCallback(async () => {
    if (!clientId || !derivationOrgLevel) return;

    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const response = await axios.get(`${base}/api/profilegen-userorglevel/${clientId}/${derivationOrgLevel}`, { 
        withCredentials: true 
      });
      
      const mappings = response.data.mappings.map((m: any) => ({
        user: m.user,
        value: m.value
      }));
      
      setUserOrgLevelMappings(mappings);
    } catch (error: any) {
      console.error('Erro ao carregar mapeamentos de usuário:', error);
      // Don't show alert for loading errors
    }
  }, [clientId, derivationOrgLevel]);

  // Load user-orglevel mappings when derivation user status is enabled and org level is selected
  useEffect(() => {
    if (derivationUserStatus && derivationOrgLevel && clientId) {
      loadUserOrgLevelMappings();
    }
  }, [derivationUserStatus, derivationOrgLevel, clientId, loadUserOrgLevelMappings]);

  // Auto-validate user-area data when previews change
  useEffect(() => {
    const userAreaData = previews?.userxa || [];
    const logsData = previews?.logs || [];
    
    if (userAreaData.length > 0 || logsData.length > 0) {
      // Run validation automatically when data is available
      validateUserAreaData();
    }
  }, [previews?.userxa, previews?.logs, validateUserAreaData]);

  // Auto-validate user-position data when previews change
  useEffect(() => {
    const userPositionData = previews?.userxpos || [];
    const logsData = previews?.logs || [];
    
    if (userPositionData.length > 0 || logsData.length > 0) {
      // Run validation automatically when data is available
      validateUserPositionData();
    }
  }, [previews?.userxpos, previews?.logs, validateUserPositionData]);

  const visualizeUserOrgLevelMappings = () => {
    if (userOrgLevelMappings.length === 0) {
      showOrgDerivationAlert('Nenhum mapeamento carregado para visualizar.', 'info');
      return;
    }
    // Reset search and pagination when opening modal
    setUserOrgLevelSearchQ('');
    setUserOrgLevelSearchColumn('');
    setUserOrgLevelPage(1);
    setUserOrgLevelModalOpen(true);
  };

  const showValidationDiagnostic = async () => {
    if (userOrgLevelMappings.length === 0) {
      showOrgDerivationAlert('Nenhum mapeamento carregado para diagnosticar.', 'info');
      return;
    }
    
    setDiagnosticLoadingUserOrgLevel(true);
    
    try {
      // Reset pagination
      setUserOrgLevelDiagPage1(1);
      setUserOrgLevelDiagPage2(1);
      setUserOrgLevelDiagPage3(1);
      
      // Re-run validation to get latest state
      await validateUserOrgLevelMappings(userOrgLevelMappings);
      setUserOrgLevelDiagnosticModalOpen(true);
    } finally {
      setDiagnosticLoadingUserOrgLevel(false);
    }
  };

  const showUserAreaDiagnostic = async () => {
    const userAreaCount = Number(indicators?.userxa?.count || 0);
    const logsCount = Number(indicators?.logs?.count || 0);
    
    if (userAreaCount === 0 && logsCount === 0) {
      showOrgDerivationAlert('Nenhum dado carregado nos blocos "Usuário x Area" ou "Log de acessos do cliente" para diagnosticar.', 'info');
      return;
    }
    
    setDiagnosticLoadingUserArea(true);
    
    try {
      // Reset pagination
      setUserAreaDiagPage1(1);
      setUserAreaDiagPage2(1);
      setUserAreaDiagPage3(1);
      
      // Re-run validation to get latest state
      await validateUserAreaData();
      setUserAreaDiagnosticModalOpen(true);
    } finally {
      setDiagnosticLoadingUserArea(false);
    }
  };

  const showUserPositionDiagnostic = async () => {
    const userPositionCount = Number(indicators?.userxpos?.count || 0);
    const logsCount = Number(indicators?.logs?.count || 0);
    
    if (userPositionCount === 0 && logsCount === 0) {
      showOrgDerivationAlert('Nenhum dado carregado nos blocos "Usuário x Posição/Cargo (RH)" ou "Log de acessos do cliente" para diagnosticar.', 'info');
      return;
    }
    
    setDiagnosticLoadingUserPosition(true);
    
    try {
      // Reset pagination
      setUserPositionDiagPage1(1);
      setUserPositionDiagPage2(1);
      setUserPositionDiagPage3(1);
      
      // Re-run validation to get latest state
      await validateUserPositionData();
      setUserPositionDiagnosticModalOpen(true);
    } finally {
      setDiagnosticLoadingUserPosition(false);
    }
  };

  const clearUserOrgLevelMappings = async () => {
    if (userOrgLevelMappings.length === 0) {
      showOrgDerivationAlert('Não há mapeamentos para limpar.', 'info');
      return;
    }

    showOrgDerivationConfirm('Tem certeza que deseja limpar todos os mapeamentos de usuário? Esta ação não pode ser desfeita.', async () => {
      try {
        const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        await axios.delete(`${base}/api/profilegen-userorglevel/${clientId}/${derivationOrgLevel}`, { 
          withCredentials: true 
        });
        
        setUserOrgLevelMappings([]);
        showOrgDerivationAlert('Mapeamentos de usuário limpos com sucesso.', 'success');
      } catch (error: any) {
        console.error('Erro ao limpar mapeamentos de usuário:', error);
        showOrgDerivationAlert('Erro ao limpar os mapeamentos de usuário.', 'error');
      }
    });
  };

  // Composite profiles functions
  const getCompositeMaxAvailableLength = (fieldType: 'prefixo' | 'n1' | 'n2' | 'n3' | 'n4' | 'sufixo') => {
    const usedLengthByOthers = compositeConstructorElements
      .filter(el => {
        const isCurrentField = (
          (el.type === 'prefix' && fieldType === 'prefixo') ||
          (el.type === 'suffix' && fieldType === 'sufixo') ||
          (el.type === 'data' && el.value === fieldType)
        );
        return !isCurrentField;
      })
      .reduce((total, el) => total + el.maxLength, 0);
    
    return Math.max(1, compositeMaxTotalLength - usedLengthByOthers);
  };

  const addCompositeElementToConstructor = (element: Omit<NameElement, 'id'>) => {
    // Check if adding this element would exceed the maximum length
    const currentLength = calculateCompositeTotalLength();
    if (currentLength + element.maxLength > compositeMaxTotalLength) {
      setStep2ErrorMessage(`Adicionar este elemento excederia o comprimento máximo de ${compositeMaxTotalLength} caracteres. Comprimento atual: ${currentLength}, elemento: +${element.maxLength}.`);
      setStep2ErrorModalOpen(true);
      return;
    }

    // Check for character uniqueness (excluding separators)
    if (element.type !== 'separator') {
      const duplicateElement = compositeConstructorElements.find(el => 
        el.type !== 'separator' && 
        el.displayChar && el.displayChar.toUpperCase() === element.displayChar.toUpperCase()
      );
      
      if (duplicateElement) {
        const duplicateLabel = duplicateElement.label || 'elemento existente';
        setStep2ErrorMessage(`O caractere "${element.displayChar.toUpperCase()}" já está sendo usado por ${duplicateLabel}. Cada elemento deve ter um caractere único.`);
        setStep2ErrorModalOpen(true);
        return;
      }
    }

    // Check separator limit (max 6)
    if (element.type === 'separator') {
      const separatorCount = compositeConstructorElements.filter(el => el.type === 'separator').length;
      if (separatorCount >= 6) {
        setStep2ErrorMessage('Máximo de 6 separadores permitidos.');
        setStep2ErrorModalOpen(true);
        return;
      }
    }

    const newElement: NameElement = {
      ...element,
      id: `${element.type}_${Date.now()}_${Math.random()}`
    };
    setCompositeConstructorElements(prev => [...prev, newElement]);
  };

  const removeCompositeElementFromConstructor = (elementId: string) => {
    setCompositeConstructorElements(prev => prev.filter(el => el.id !== elementId));
  };

  const reorderCompositeConstructorElements = async (fromIndex: number, toIndex: number) => {
    const newElements = [...compositeConstructorElements];
    const [moved] = newElements.splice(fromIndex, 1);
    newElements.splice(toIndex, 0, moved);
    setCompositeConstructorElements(newElements);
    
    // Auto-save the new positions to database
    try {
      await saveCompositePatternCompositionInternal(newElements);
    } catch (error) {
      console.error('Erro ao salvar posições dos elementos compostos:', error);
      // Optionally show error to user or revert changes
    }
  };

  const updateCompositeElementInConstructor = (elementType: 'prefix' | 'suffix' | 'data', elementValue: string, newLength: number, newChar: string) => {
    const elementToUpdate = compositeConstructorElements.find(el => {
      if (elementType === 'prefix' && el.type === 'prefix') return true;
      if (elementType === 'suffix' && el.type === 'suffix') return true;
      if (elementType === 'data' && el.type === 'data' && el.value === elementValue) return true;
      return false;
    });

    if (!elementToUpdate) return;

    // Check for character uniqueness (excluding separators and the current element)
    if (newChar) {
      const duplicateElement = compositeConstructorElements.find(el => 
        el.id !== elementToUpdate.id && 
        el.type !== 'separator' && 
        el.displayChar && el.displayChar.toUpperCase() === newChar.toUpperCase()
      );
      const duplicateLabel = duplicateElement?.label || 'elemento existente';
      
      if (duplicateElement) {
        setStep2ErrorMessage(`O caractere "${newChar.toUpperCase()}" já está sendo usado por ${duplicateLabel}. Cada elemento deve ter um caractere único.`);
        setStep2ErrorModalOpen(true);
        return;
      }
    }

    // Check maximum length with the new length
    if (elementToUpdate) {
      const currentLength = calculateCompositeTotalLength();
      const oldLength = elementToUpdate.maxLength || 1;
      const lengthDifference = newLength - oldLength;
      
      if (currentLength + lengthDifference > compositeMaxTotalLength) {
        setStep2ErrorMessage(`Alterar o comprimento deste elemento excederia o limite máximo de ${compositeMaxTotalLength} caracteres. Comprimento atual: ${currentLength}, diferença: +${lengthDifference}.`);
        setStep2ErrorModalOpen(true);
        return;
      }
    }

    setCompositeConstructorElements(prev => prev.map(el => {
      if (el.id === elementToUpdate.id) {
        return {
          ...el,
          maxLength: newLength,
          displayChar: newChar || el.displayChar
        };
      }
      return el;
    }));
  };

  const calculateCompositeTotalLength = () => {
    return compositeConstructorElements.reduce((total, el) => total + el.maxLength, 0);
  };

  const generateCompositePreviewPattern = () => {
    if (compositeConstructorElements.length === 0) return 'Adicione elementos para ver o preview';
    return compositeConstructorElements.map(el => {
      switch (el.type) {
        case 'prefix':
        case 'suffix':
        case 'data':
          return el.displayChar.repeat(el.maxLength);
        case 'separator': 
          return el.value;
        default: 
          return 'X';
      }
    }).join('');
  };

  // Risk sanitation elements management
  const initializeRiskSanitationElementsWithOrder = useCallback(() => {
    const elements: RiskSanitationElement[] = [
      {
        id: 'san_risk_benchmark',
        label: 'Saneamento de risco com base em benchmark',
        description: 'Utiliza dados de benchmark para identificar e sanear riscos nos perfis',
        enabled: sanRiskBenchmark
      },
      {
        id: 'san_risk_cliente',
        label: 'Saneamento de risco com base em matriz cliente',
        description: 'Utiliza matriz de risco específica do cliente para saneamento',
        enabled: sanRiskCliente
      },
      {
        id: 'san_risk_attrb_model',
        label: 'Perfil de risco dentro da composta?',
        description: 'Inclui perfis de risco diretamente na estrutura do perfil composto',
        enabled: sanRiskAttrModel,
        conditional: true
      }
    ];
    
    // Sort elements based on saved order (_ord fields) from client data, with fallback to default order
    if (client) {
      return elements.sort((a, b) => {
        const ordA = client[`${a.id}_ord`] || (a.id === 'san_risk_benchmark' ? 1 : a.id === 'san_risk_cliente' ? 2 : 3);
        const ordB = client[`${b.id}_ord`] || (b.id === 'san_risk_benchmark' ? 1 : b.id === 'san_risk_cliente' ? 2 : 3);
        return ordA - ordB;
      });
    }
    
    return elements;
  }, [sanRiskBenchmark, sanRiskCliente, sanRiskAttrModel, client]);

  // Initialize default order positions in database if not present
  const initializeDefaultRiskPositions = useCallback(async () => {
    try {
      if (!client || !client._id) return;
      
      // Check if positions are already set
      const hasPositions = client.san_risk_benchmark_ord || client.san_risk_cliente_ord || client.san_risk_attrb_model_ord;
      
      if (!hasPositions) {
        const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const defaultPositions = {
          san_risk_benchmark_ord: 1,
          san_risk_cliente_ord: 2,
          san_risk_attrb_model_ord: 3
        };

        await axios.put(
          `${base}/api/profilegen-clients/${client._id}`,
          defaultPositions,
          { withCredentials: true }
        );
        console.log('Default risk sanitation positions initialized');
      }
    } catch (err) {
      console.error('Error initializing default risk positions:', err);
    }
  }, [client]);

  // Internal function to save composite pattern composition with custom elements
  const saveCompositePatternCompositionInternal = async (elements: NameElement[]) => {
    if (!clientId) {
      console.error('Nenhum cliente selecionado.');
      return;
    }

    if (elements.length === 0) {
      console.error('Nenhum elemento para salvar.');
      return;
    }

    try {
      console.log('Salvando composição de perfis compostos para cliente:', clientId);
      console.log('Elementos da composição:', elements);

      // Prepare data object with all composite pattern fields
      const patternData: Record<string, any> = {};
      
      // Initialize all composite fields as empty
      const elementTypes = ['prefixo', 'n1', 'n2', 'n3', 'n4', 'sufixo'];
      elementTypes.forEach(type => {
        patternData[`pattern_c_${type}_qtd`] = null;
        patternData[`pattern_c_${type}_char`] = null;
        patternData[`pattern_c_${type}_ord`] = null;
      });

      for (let i = 1; i <= 6; i++) {
        patternData[`pattern_c_sep${i}_char`] = null;
        patternData[`pattern_c_sep${i}_ord`] = null;
      }

      // Fill data based on constructor elements
      let separatorIndex = 1;
      elements.forEach((element, index) => {
        const order = index + 1;

        if (element.type === 'separator') {
          if (separatorIndex <= 6) {
            patternData[`pattern_c_sep${separatorIndex}_char`] = element.value;
            patternData[`pattern_c_sep${separatorIndex}_ord`] = order;
            separatorIndex++;
          }
        } else {
          let fieldName = '';
          if (element.type === 'prefix') fieldName = 'prefixo';
          else if (element.type === 'suffix') fieldName = 'sufixo';
          else if (element.type === 'data') fieldName = element.value;

          if (fieldName) {
            patternData[`pattern_c_${fieldName}_qtd`] = element.maxLength;
            patternData[`pattern_c_${fieldName}_char`] = element.displayChar;
            patternData[`pattern_c_${fieldName}_ord`] = order;
          }
        }
      });

      // Get the client record to find the MongoDB _id
      const clientResponse = await axios.get(`${base}/api/profilegen-clients`, { 
        params: { q: clientId, searchColumn: 'clientId' },
        withCredentials: true 
      });

      if (!clientResponse.data || !clientResponse.data.items || clientResponse.data.items.length === 0) {
        throw new Error(`Cliente ${clientId} não encontrado na base de dados.`);
      }

      const clientRecord = clientResponse.data.items[0];
      const mongoId = clientRecord._id;

      // Update using the MongoDB _id
      await axios.put(`${base}/api/profilegen-clients/${mongoId}`, patternData, { withCredentials: true });

      console.log('Posições dos elementos compostos salvas com sucesso');
    } catch (error: any) {
      console.error('Erro ao salvar posições dos elementos compostos:', error);
      throw error;
    }
  };

  // Save composite pattern composition
  const saveCompositePatternComposition = async () => {
    if (!clientId) {
      showOrgDerivationAlert('Nenhum cliente selecionado.', 'error');
      return;
    }

    if (compositeConstructorElements.length === 0) {
      setStep2ErrorMessage('Adicione pelo menos um elemento à composição antes de salvar.');
      setStep2ErrorModalOpen(true);
      return;
    }

    // Validate maximum length
    const totalLength = compositeConstructorElements.reduce((sum, element) => {
      return sum + (element.maxLength || 1);
    }, 0);

    if (totalLength > compositeMaxTotalLength) {
      setStep2ErrorMessage(`A composição excede o comprimento máximo permitido de ${compositeMaxTotalLength} caracteres. Comprimento atual: ${totalLength} caracteres.`);
      setStep2ErrorModalOpen(true);
      return;
    }

    try {
      await saveCompositePatternCompositionInternal(compositeConstructorElements);
      setStep2SuccessMessage('Composição do padrão de nomenclatura para perfis compostos salva com sucesso!');
      setStep2SuccessModalOpen(true);
    } catch (error: any) {
      console.error('Erro ao salvar composição de perfis compostos:', error);
      let errorMessage = 'Erro ao salvar a composição de perfis compostos.';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage += ` Detalhes: ${error.response.data.message}`;
      }
      setStep2ErrorMessage(errorMessage + ' Verifique o console para mais detalhes.');
      setStep2ErrorModalOpen(true);
    }
  };

  // Save composite prefix patterns
  const saveCompositePrefixPatterns = async () => {
    if (!clientId) {
      showOrgDerivationAlert('Nenhum cliente selecionado.', 'error');
      return;
    }

    try {
      console.log('Salvando padrões de prefixo para perfis compostos:', compositePrefixPatterns);

      // Prepare data object with composite prefix pattern fields
      const prefixData = {
        pattern_c_prefixo_composto: compositePrefixPatterns.composto
      };

      // Get the client record to find the MongoDB _id
      const clientResponse = await axios.get(`${base}/api/profilegen-clients`, { 
        params: { q: clientId, searchColumn: 'clientId' },
        withCredentials: true 
      });

      if (!clientResponse.data || !clientResponse.data.items || clientResponse.data.items.length === 0) {
        throw new Error(`Cliente ${clientId} não encontrado na base de dados.`);
      }

      const clientRecord = clientResponse.data.items[0];
      const mongoId = clientRecord._id;

      // Update using the MongoDB _id
      const response = await axios.put(`${base}/api/profilegen-clients/${mongoId}`, prefixData, { withCredentials: true });

      console.log('Padrões de prefixo para perfis compostos salvos com sucesso:', response.data);
      setCompositePrefixPatternsModalOpen(false);
      setCompositePrefixPatternsSuccessModalOpen(true);
    } catch (error: any) {
      console.error('Erro ao salvar padrões de prefixo para perfis compostos:', error);
      let errorMessage = 'Erro ao salvar os padrões de prefixo para perfis compostos.';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage += ` Detalhes: ${error.response.data.message}`;
      }
      showOrgDerivationAlert(errorMessage, 'error');
    }
  };

  const calculateTotalLength = () => {
    return constructorElements.reduce((total, el) => total + el.maxLength, 0);
  };

  const generatePreviewPattern = () => {
    if (constructorElements.length === 0) return 'Adicione elementos para ver o preview';
    return constructorElements.map(el => {
      switch (el.type) {
        case 'prefix':
        case 'suffix':
        case 'data':
          return el.displayChar.repeat(el.maxLength);
        case 'separator': 
          return el.value;
        default: 
          return el.value;
      }
    }).join('');
  };

  async function uploadRows(tableKey: string, rows: any[]) {
    if (!clientId) return;
    
    try {
      setUploading(prev => ({ ...prev, [tableKey]: true }));
      
      // Show progress modal
      const tableName = tables.find(t => t.key === tableKey)?.title || tableKey;
      setUploadProgressTable(tableName);
      setUploadProgress(0);
      setUploadProgressMessage('Iniciando upload...');
      setUploadProgressOpen(true);
      
      const url = `${base}/api/profilegen-data/${tableKey}/${clientId}`;
      
      // Start upload (may respond immediately for large datasets)
      const res = await axios.post(url, { rows }, { 
        withCredentials: true,
        timeout: 300000 // 5 minutes timeout for large uploads
      });
      
      console.log('uploadRows response', res.data);
      
      // Check if it's a small dataset that was processed immediately (201 status)
      if (res.status === 201 && res.data?.insertedCount !== undefined) {
        setUploadProgress(100);
        setUploadProgressMessage('Upload concluído com sucesso!');
        
        setTimeout(() => {
          setUploadProgressOpen(false);
        }, 800);
        
        setIndicators(prev => ({ ...prev, [tableKey]: { count: res.data.insertedCount } }));
        await fetchIndicators(tableKey);
        return res.data;
      }
      
      // For large datasets (202 status), start polling for progress
      if (res.status === 202) {
        let uploadCompleted = false;
        const progressUrl = `${base}/api/profilegen-data/${tableKey}/${clientId}/upload-progress`;
        
        const pollInterval = setInterval(async () => {
          try {
            const progRes = await axios.get(progressUrl, { withCredentials: true });
            const { progress, message, done, result, error } = progRes.data;
            
            setUploadProgress(progress);
            setUploadProgressMessage(message);
            
            if (done && progress === 100 && !uploadCompleted) {
              uploadCompleted = true;
              clearInterval(pollInterval);
              
              setUploadProgress(100);
              setUploadProgressMessage('Upload concluído com sucesso!');
              
              setTimeout(() => {
                setUploadProgressOpen(false);
              }, 800);
              
              if (result?.insertedCount !== undefined) {
                setIndicators(prev => ({ ...prev, [tableKey]: { count: result.insertedCount } }));
              }
              await fetchIndicators(tableKey);
            } else if (done && error && !uploadCompleted) {
              uploadCompleted = true;
              clearInterval(pollInterval);
              
              setUploadProgress(0);
              setUploadProgressMessage(`Erro: ${error}`);
              
              setTimeout(() => {
                setUploadProgressOpen(false);
              }, 2000);
              
              throw new Error(error);
            }
          } catch (pollErr) {
            console.error('Polling error:', pollErr);
          }
        }, 500);
        
        return res.data;
      }
      
      // Fallback for unexpected responses
      setUploadProgress(100);
      setUploadProgressMessage('Upload concluído!');
      setTimeout(() => {
        setUploadProgressOpen(false);
      }, 800);
      
      await fetchIndicators(tableKey);
      return res.data;
      
    } catch (err) {
      console.error('uploadRows error', err);
      setUploadProgress(0);
      setUploadProgressMessage('Erro durante o upload');
      
      setTimeout(() => {
        setUploadProgressOpen(false);
      }, 2000);
      throw err;
    } finally {
      setUploading(prev => ({ ...prev, [tableKey]: false }));
    }
  }

  const fetchIndicators = useCallback(async (tableKey: string, retries = 1, forceFullLoad = false) => {
    if (!clientId) return;
    try {
      // Check if it's a large table that needs special handling
      const isLargeTable = tableKey === 'upcasis' || tableKey === 'logs';
      
      // First get indicators to check size
      const indicatorsRes = await axios.get(
        `${base}/api/profilegen-data/${tableKey}/${clientId}?indicatorsOnly=true`,
        { withCredentials: true, timeout: 10000 }
      );
      
      const totalCount = indicatorsRes.data?.indicators?.count || 0;
      setIndicators(prev => ({ ...prev, [tableKey]: indicatorsRes.data?.indicators || { count: 0 } }));
      
      console.debug('fetchIndicators', { tableKey, totalCount, isLargeTable, forceFullLoad });

      // Step 3 relies on the full custom-actions universe (codez). Always load all rows.
      if (tableKey === 'codez' && totalCount > 0) {
        const fullCodez = await fetchAllDataProgressively(tableKey, (data: any[]) => {
          setPreviews(prev => ({ ...prev, [tableKey]: data }));
        });
        setCodezItems(fullCodez);
        return;
      }
      
      // For large datasets (>5000 records), load progressively if forced
      if (totalCount > 5000 && (forceFullLoad || tableKey === 'logs')) {
        console.log(`[fetchIndicators] Loading ${tableKey} progressively (${totalCount} records)`);
        
        const setStateFunction = tableKey === 'logs' 
          ? (data: any[]) => setPreviews(prev => ({ ...prev, [tableKey]: data }))
          : (data: any[]) => setPreviews(prev => ({ ...prev, [tableKey]: data }));
        
        await fetchAllDataProgressively(tableKey, setStateFunction);
        
        if (tableKey === 'codez') {
          const fullData = await fetchAllDataProgressively(tableKey, (data) => {});
          setCodezItems(fullData);
        }
      } else if (totalCount > 0 && totalCount <= 5000) {
        // For smaller datasets, load normally
        const explicitLimit = Math.min(Math.max(totalCount, 1), 100000);
        const url = `${base}/api/profilegen-data/${tableKey}/${clientId}?page=1&limit=${explicitLimit}`;
        const res = await axios.get(url, { withCredentials: true, timeout: 30000 });
        
        if (res && res.data) {
          const items = res.data.items || [];
          setPreviews(prev => ({ ...prev, [tableKey]: items }));
          if (tableKey === 'codez') setCodezItems(items);
        }
      }
    } catch (err: any) {
      console.error('fetchIndicators', err);
      const status = err && err.response && err.response.status;
      if (status === 401 && retries > 0) {
        console.debug('fetchIndicators got 401, retrying once after delay', { tableKey });
        setTimeout(() => { try { fetchIndicators(tableKey, retries - 1, forceFullLoad); } catch (_) { /* ignore */ } }, 1000);
      }
    }
  }, [clientId, base, fetchAllDataProgressively]);

  // Function to load saved AI conversion results
  const loadSavedAiResults = useCallback(async (): Promise<Record<string, string>> => {
    if (!clientId) return {};

    try {
      const response = await axios.get(`${base}/api/profilegen-nomenclatura-ia/${clientId}`, {
        withCredentials: true
      });

      if (response.status === 200 && response.data?.items) {
        const savedResults = response.data.items;
        console.log('[DEBUG] Loaded saved AI results:', savedResults);

        // Note: groupedResults was removed — we use savedResults directly below

        // Apply saved results to current aiConversionData state
        // Use a tolerant comparison (numeric compare for character counts) to avoid mismatches
        setAiConversionData(currentData => {
          const updatedData = { ...currentData };
          Object.keys(updatedData).forEach(tab => {
            if (Array.isArray(updatedData[tab]) && updatedData[tab].length > 0) {
              updatedData[tab] = updatedData[tab].map((item: any) => {
                const foundSaved = savedResults.find((sr: any) => {
                  try {
                    const srType = String(sr.element_type_rs || '').trim();
                    const srValue = String(sr.element_value_raw || '').trim();
                    const srNum = typeof sr.num_char === 'string' ? parseInt(sr.num_char || '0') : (sr.num_char || 0);
                    const itemNum = typeof item.characters === 'string' ? parseInt(item.characters || '0') : (item.characters || 0);
                    return String(tab) === String(sr.element_type) && srType === String(item.type) && srValue === String(item.value) && Number(srNum) === Number(itemNum);
                  } catch (e) {
                    return false;
                  }
                });

                if (foundSaved) {
                  return { ...item, resultado: foundSaved.result };
                }
                return item;
              });
            }
          });
          return updatedData;
        });

  console.log('[DEBUG] Applied saved results to aiConversionData');

  // Create a lookup map for quick access to IA results (returned for immediate use)
        const aiResultsLookup: {[key: string]: string} = {};
        
        savedResults.forEach((item: any) => {
          // Create a unique key for each IA result: elementType_rawValue_typeRs_numChar
          const key = `${item.element_type}_${item.element_value_raw}_${item.element_type_rs}_${item.num_char}`;
          aiResultsLookup[key] = item.result;
        });

        // Return the lookup data for immediate use
        return aiResultsLookup;
      }
    } catch (error) {
      console.error('Error loading saved AI results:', error);
      // Silently fail - not critical
    }
    
    return {};
  }, [clientId, base]);

  const resolvePatternElementValue = useCallback((cfg: any, patternPrefix: string, field: string, providedValue?: any): string => {
    let value = String(providedValue ?? '').trim();
    const qty = Number(cfg?.[`${patternPrefix}${field}_qtd`] || 0);

    if (!value && field === 'sufixo') {
      const suffixChar = String(cfg?.[`${patternPrefix}sufixo_char`] || '').trim();
      if (suffixChar && qty > 0) {
        value = suffixChar.repeat(qty);
      }
    }

    if (qty > 0 && value.length > qty) {
      value = value.substring(0, qty);
    }

    return value;
  }, []);

  const normalizeRawFallbackValue = useCallback((rawValue: any, maxLength?: number): string => {
    const ascii = String(rawValue ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const compact = ascii.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (maxLength && Number(maxLength) > 0) {
      return compact.substring(0, Number(maxLength));
    }
    return compact;
  }, []);

  const buildNomenclatureByPatternOrder = useCallback((cfg: any, patternPrefix: string, rawElements: Array<{ type: string; value?: any }>): string => {
    const elements = rawElements
      .map((el) => {
        const ord = cfg?.[`${patternPrefix}${el.type}_ord`];
        const resolved = resolvePatternElementValue(cfg, patternPrefix, el.type, el.value);
        return { ord: ord == null ? null : Number(ord), value: resolved };
      })
      .filter((el) => el.ord != null && Number.isFinite(el.ord as number) && String(el.value || '') !== '');

    const elementOrds = elements.map((el) => Number(el.ord));

    const separators: Array<{ ord: number; value: string }> = [];
    for (let i = 1; i <= 6; i++) {
      const sepChar = cfg?.[`${patternPrefix}sep${i}_char`];
      const sepOrd = cfg?.[`${patternPrefix}sep${i}_ord`];
      if (sepChar && sepOrd != null) {
        const ordNum = Number(sepOrd);
        const hasLeftElement = elementOrds.some((ord) => ord < ordNum);
        const hasRightElement = elementOrds.some((ord) => ord > ordNum);
        if (hasLeftElement && hasRightElement) {
          separators.push({ ord: ordNum, value: String(sepChar) });
        }
      }
    }

    const tokens = [
      ...elements.map((el) => ({ ord: Number(el.ord), value: String(el.value) })),
      ...separators
    ].sort((a, b) => a.ord - b.ord);

    const nomenclature = tokens.map((t) => t.value).join('').replace(/([-_.:])\1+/g, '$1');
    return nomenclature;
  }, [resolvePatternElementValue]);

  // Generate derived nomenclature rows based on master records and client's derivation values
  const generateDerivedNomenclature = useCallback(async () => {
    if (!client && !clientId) {
      showConsolidationAlert('Cliente não carregado.', 'error');
      return;
    }

    // Fetch fresh client configuration to use the most recent pattern parameters
    let currentClient: any = client;
    try {
      if (clientId) {
        const listRes = await axios.get(`${base}/api/profilegen-clients`, {
          params: { q: clientId, searchColumn: 'clientId', _ts: Date.now() },
          withCredentials: true,
          headers: { 'Cache-Control': 'no-cache' }
        });

        if (listRes && listRes.data && Array.isArray(listRes.data.items) && listRes.data.items.length > 0) {
          currentClient = listRes.data.items[0];
        } else {
          console.warn('[WARN] No client record found for clientId when attempting to refresh client config');
        }
      }
    } catch (err) {
      console.warn('Could not fetch latest client configuration for derived generation, using local client state', err);
      currentClient = client || currentClient;
    }

    const valuesRaw = currentClient?.derivation_orglevel_values || '';
    const values = String(valuesRaw).split(',').map((v: string) => v.trim()).filter((v: string) => v);
    if (values.length === 0) {
      showConsolidationAlert('Nenhum valor configurado em derivation_orglevel_values para este cliente.', 'error');
      return;
    }

    // Use only the master rows as source (perfilMestre === true)
    const masters = masterRecords.filter(r => r.perfilMestre === true || r.master === true || r.perfilMestre === 'S');

    if (!masters.length) {
      showConsolidationAlert('Nenhum perfil mestre encontrado para gerar derivados.', 'error');
      return;
    }

    const newRows: any[] = [];

  // load saved AI conversions like the Section 1 generator does
    let aiConversions: any = {};
    try {
      const fresh = await loadSavedAiResults();
      aiConversions = fresh || {};
    } catch (err) {
      console.warn('Could not load saved AI conversions for derived generation, proceeding without IA', err);
      aiConversions = {};
    }

    // helper to reuse Section 1 AI conversion lookup (use currentClient)
    const getAiConvertedValue = (elementType: string, rawValue: string, fallback: string, isRiskAction: boolean) => {
      if (!rawValue) return fallback;
      const patternPrefix = isRiskAction ? 'pattern_r_' : 'pattern_s_';
      const qtdField = `${patternPrefix}${elementType}_qtd`;
      const numChar = currentClient ? (currentClient[qtdField] || 0) : 0;
      if (numChar > 0) {
        const typeRs = isRiskAction ? 'Risco' : 'Simples';
        const key = `${elementType}_${rawValue}_${typeRs}_${numChar}`;
        const aiValue = aiConversions[key];
        if (aiValue) return aiValue;
      }
      return normalizeRawFallbackValue(fallback, Number(numChar) || undefined);
    };

    masters.forEach((m) => {
      // keep the original master row
      newRows.push({ ...m });

      const isRisk = Boolean(m.risco || m.risk || m.isRisk);
  const patternPrefix = isRisk ? 'pattern_r_' : 'pattern_s_';

  // derived prefix must be used instead of master prefix
  const derivedPrefixField = isRisk ? 'pattern_r_prefixo_risco_derivado' : 'pattern_s_prefixo_derivado';
  const derivedPrefix = String(currentClient ? (currentClient[derivedPrefixField] || '') : '').trim();

      // For building the derived name, reuse the same element-by-element logic from generateNomenclature
      // Use values from the master row (m) for area/processo/subprocesso/atividade and codigo when needed

      values.forEach((val) => {
        const orgVal = String(val).trim();

        // Build element values similar to generateNomenclature
  // Use IA-converted values like generateNomenclature
  const area = getAiConvertedValue('area', String(m.area || ''), String(m.area || ''), isRisk);
  const processo = getAiConvertedValue('processo', String(m.processo || m.process || ''), String(m.processo || m.process || ''), isRisk);
  const subprocesso = getAiConvertedValue('subprocesso', String(m.subprocesso || m.subprocess || ''), String(m.subprocesso || m.subprocess || ''), isRisk);
  const atividade = getAiConvertedValue('atividade', String(m.atividade || m.activity || ''), String(m.atividade || m.activity || ''), isRisk);

        // Start composing elements array with ordinals from client pattern
        const elements: Array<{ type: string; value: string }> = [
          { type: 'prefixo', value: derivedPrefix || '' },
          { type: 'area', value: area },
          { type: 'processo', value: processo },
          { type: 'subprocesso', value: subprocesso },
          { type: 'atividade', value: atividade },
          { type: 'sufixo', value: orgVal || '' }
        ];

        // Add action element for risk if configured
        if (isRisk && currentClient && currentClient[`${patternPrefix}acao_ord`] != null) {
          const acaoVal = String(m.codigo || m.code || '');
          const acaoQtd = currentClient[`${patternPrefix}acao_qtd`] || 0;
          let acaoFinal = acaoVal;
          if (acaoQtd > 0 && acaoFinal.length > acaoQtd) acaoFinal = acaoFinal.substring(0, acaoQtd);
          elements.push({ type: 'acao', value: acaoFinal });
        }

        const derivedName = buildNomenclatureByPatternOrder(currentClient, patternPrefix, elements);

        const derivedRow = {
          ...m,
          perfilMestre: false,
          nomenclatura_derivado: derivedName,
          derivedNomenclatura: derivedName,
          derived_orglevel_value: orgVal
        };

        newRows.push(derivedRow);
      });
    });

  // Remove duplicate derived rows by `nomenclatura_derivado` (keep first occurrence)
  try {
      const seen = new Set<string>();
      const filteredRows: any[] = [];
      for (const r of newRows) {
        const d = String(r.nomenclatura_derivado || r.derivedNomenclatura || '').trim();
        if (d) {
          if (!seen.has(d)) {
            seen.add(d);
            filteredRows.push(r);
          } else {
            // skip duplicate derived row
          }
        } else {
          // keep rows without derived nomenclature (masters)
          filteredRows.push(r);
        }
      }

        // compute distinct master profiles displayed in the derived table
        const masterKeySet = new Set<string>();
        for (const r of filteredRows) {
          const isMasterRow = r.perfilMestre === true || r.master === true || r.perfilMestre === 'S';
          if (isMasterRow) {
            const key = `${String(r.area||'')}|${String(r.processo||r.process||'')}|${String(r.subprocesso||r.subprocess||'')}|${String(r.atividade||r.activity||'')}|${String(r.codigo||r.code||'')}`;
            masterKeySet.add(key);
          }
        }
        setMasterRecords(filteredRows);
      } catch (err) {
        // fallback: set raw rows and approximate master count from original masters array
        setMasterRecords(newRows);
        showConsolidationAlert('Nomenclaturas derivadas geradas.', 'success');
        return;
      }

    showConsolidationAlert('Nomenclaturas derivadas geradas.', 'success');
  }, [client, masterRecords, setMasterRecords, showConsolidationAlert, base, clientId, loadSavedAiResults, buildNomenclatureByPatternOrder, normalizeRawFallbackValue]);

  // Function to check if consolidation data exists and load it
  const checkAndLoadConsolidationData = useCallback(async () => {
    if (!clientId) return;

    try {
      console.debug('Checking for existing consolidation data for clientId:', clientId);

      const consolidationItems = await fetchAllConsolidationData();

      if (consolidationItems.length > 0) {
        console.debug('Found existing consolidation data:', { count: consolidationItems.length });
        
        // Load the saved data instead of fetching from original sources
        const savedData = consolidationItems.map((item: any) => ({
          area: item.area || '',
          processo: item.process || '',
          subprocesso: item.subprocess || '',
          atividade: item.activity || '',
          codigo: item.code || '',
          nomenclatura: item.profilename || ''
        }));

        // Load risk classification data
        const savedRiskData: {[code: string]: boolean} = {};
        const savedMasterData: {[code: string]: boolean} = {};
        consolidationItems.forEach((item: any) => {
          if (item.code) {
            // Preserve truthy values when multiple documents exist for same code (e.g. master + derived duplicates)
            savedRiskData[item.code] = (savedRiskData[item.code] === true) || (item.risk === true);
            savedMasterData[item.code] = (savedMasterData[item.code] === true) || (item.master === true);
          }
        });

        setNomenclatureMappingData(savedData);
        setRiskClassificationData(savedRiskData);
        setIsRiskClassificationEnabled(Object.values(savedRiskData).some(risk => risk));
        setMasterDerivedClassificationData(savedMasterData);
        setIsMasterDerivedClassificationEnabled(Object.values(savedMasterData).some(master => master));
        // Build masterRecords to include master rows and derived rows (unique per master+derived pair)
        const mastersArr: any[] = [];
        const derivedArr: any[] = [];
        const seenDerivedPairs = new Set<string>();
        const masterNamesSet = new Set<string>();

        // Collect explicit master rows
        consolidationItems.forEach((item: any) => {
          if (item.master === true && item.profilename) {
            const name = String(item.profilename || '').trim();
            mastersArr.push({
              area: item.area || '',
              processo: item.process || '',
              subprocesso: item.subprocess || '',
              atividade: item.activity || '',
              codigo: item.code || '',
              risco: item.risk === true,
              perfilMestre: true,
              nomenclatura: name
            });
            if (name) masterNamesSet.add(name);
          }
        });

        // Collect derived rows (unique per master_name + derived profilename)
        consolidationItems.forEach((item: any) => {
          if (item.derived === true) {
            const masterName = String(item.master_name || item.profilename || '').trim();
            const derivedName = String(item.profilename || '').trim();
            if (!derivedName) return;
            const key = `${masterName}||${derivedName}`;
            if (seenDerivedPairs.has(key)) return;
            seenDerivedPairs.add(key);
            if (masterName) masterNamesSet.add(masterName);

            derivedArr.push({
              area: item.area || '',
              processo: item.process || '',
              subprocesso: item.subprocess || '',
              atividade: item.activity || '',
              codigo: item.code || '',
              risco: item.risk === true,
              perfilMestre: false,
              nomenclatura: masterName,
              nomenclatura_derivado: derivedName
            });
          }
        });

        // Ensure master rows exist for masterNames found in derivedArr
        masterNamesSet.forEach(name => {
          const alreadyHas = mastersArr.some(m => String(m.nomenclatura || '').trim() === String(name).trim());
          if (!alreadyHas) {
            mastersArr.push({ area: '', processo: '', subprocesso: '', atividade: '', codigo: '', risco: false, perfilMestre: true, nomenclatura: name });
          }
        });

        // Final combined: masters first, then derived rows
        const combined = mastersArr.concat(derivedArr);
        setMasterRecords(combined);
        setMasterRecordsLoading(false);
        setHasConsolidationData(true);
        
        return true; // Indicate that saved data was loaded
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('checkAndLoadConsolidationData error:', err);
      }
      // On error, ensure master records are cleared
      setMasterRecords([]);
      setMasterRecordsLoading(false);
    }
    
    // No saved data found - clear any previous master records
    setHasConsolidationData(false);
    setMasterRecords([]);
    setMasterRecordsLoading(false);
    return false; // Indicate that no saved data was found
  }, [clientId, base]);

  // Fetch nomenclature mapping data for consolidated view
  const fetchNomenclatureMappingData = useCallback(async () => {
    if (!clientId) return;
    // Only run for supported models: Atividade Simples or Posição Composto + Atividade Simples
    const profileModelStr = String(client?.profileModel);
    if (!(profileModelStr === 'Atividade Simples' || profileModelStr === 'Posição Composto + Atividade Simples')) return;
    
    setNomenclatureMappingLoading(true);
    try {
      // First, check if there's saved consolidation data
      const hasSavedData = await checkAndLoadConsolidationData();
      if (hasSavedData) {
        console.debug('Loaded saved consolidation data, skipping fresh data fetch');
        setNomenclatureMappingLoading(false);
        return;
      }

      // If no saved data, fetch fresh data from original sources
      console.debug('No saved consolidation data found, fetching fresh data');
      
      // Load logs progressively (all data)
      console.debug('[fetchNomenclatureMappingData] Loading logs progressively...');
      const logsData = await fetchAllTableRows('profilegen-logs');
      console.debug('[fetchNomenclatureMappingData] Logs loaded:', logsData.length);
      
      // Fetch full user-area data
      const userAreas = await fetchAllTableRows('profilegen-userxa');
      
      console.debug('fetchNomenclatureMappingData - logs loaded:', { 
        logsCount: logsData.length
      });
      console.debug('fetchNomenclatureMappingData - userxa response:', { 
        userAreaCount: userAreas.length || 0 
      });
      
      // Create a map of user to area for quick lookup
      const userAreaMap = new Map();
      userAreas.forEach((ua: any) => {
        if (ua.user && ua.area) {
          const normalizedUser = String(ua.user || '').trim();
          if (normalizedUser) {
            userAreaMap.set(normalizedUser, ua.area);
          }
        }
      });
      
      // Process logs and combine with user area data
      const mappingData = logsData.map((log: any) => {
        const normalizedUser = String(log.user || '').trim();
        return {
          usuario: normalizedUser || '-',
          area: userAreaMap.get(normalizedUser) || '-',
          processo: log.process || '-',
          subprocesso: log.subprocess || '-',
          atividade: log.activity || '-',
          codigo: log.code || '-'
        };
      });
      
      console.debug('fetchNomenclatureMappingData result', { mappingCount: mappingData.length });
      setNomenclatureMappingData(mappingData);
    } catch (err: any) {
      console.error('fetchNomenclatureMappingData', err);
      setNomenclatureMappingData([]);
    } finally {
      setNomenclatureMappingLoading(false);
    }
  }, [clientId, client?.profileModel, checkAndLoadConsolidationData, fetchAllTableRows, fetchAllConsolidationData]);

  // Function to classify risk actions
  const classifyRiskActions = useCallback(async (codesOverride?: string[]) => {
    if (!clientId) {
      console.warn('classifyRiskActions: Missing clientId');
      return;
    }

    try {
      // Always fetch fresh client data to ensure we have the latest parameters
      console.debug('classifyRiskActions - fetching fresh client data for clientId:', clientId);
      
      const clientResponse = await axios.get(`${base}/api/profilegen-clients`, {
        params: { clientId },
        withCredentials: true
      });

      if (clientResponse.status !== 200 || !clientResponse.data?.items?.length) {
        console.warn('classifyRiskActions: No client data found');
        return;
      }

      // Defensive: filter returned clients by clientId param if backend returned multiple items
      let freshClientData: any = null;
      if (Array.isArray(clientResponse.data.items)) {
        freshClientData = clientResponse.data.items.find((c: any) => String(c.clientId || c.id) === String(clientId)) || clientResponse.data.items[0];
      } else {
        freshClientData = clientResponse.data.items;
      }
      console.debug('classifyRiskActions - fresh client data:', { 
        clientId, 
        freshClientData,
        san_risk_benchmark: freshClientData.san_risk_benchmark,
        san_risk_cliente: freshClientData.san_risk_cliente
      });

      const sanRiskBenchmark = freshClientData.san_risk_benchmark === 'true' || freshClientData.san_risk_benchmark === true;
      const sanRiskCliente = freshClientData.san_risk_cliente === 'true' || freshClientData.san_risk_cliente === true;

      // Check case 3: Neither risk option is enabled
      if (!sanRiskBenchmark && !sanRiskCliente) {
        setShowRiskAlert(true);
        setIsRiskClassificationEnabled(false);
        return;
      }

      // Get all codes from the current nomenclature table or from override
      const currentCodes = (Array.isArray(codesOverride) && codesOverride.length > 0)
        ? codesOverride.filter(code => code && code !== '-')
        : nomenclatureMappingData.map(item => item.codigo).filter(code => code && code !== '-');
      if (currentCodes.length === 0) {
        console.warn('No codes found in nomenclature table');
        return;
      }

      const riskCodes = new Set<string>();

      // Case 1: Benchmark risk matrix
      if (sanRiskBenchmark) {
        try {
          // Fetch ALL risk codes from benchmark (no pagination limit)
          const benchmarkResponse = await axios.get(`${base}/api/sap-actions-risk`, { 
            params: { pageSize: 50000 }, // Request all records (backend max is 500 per page, need to fetch multiple times if needed)
            withCredentials: true 
          });
          if (benchmarkResponse.status === 200 && benchmarkResponse.data?.items) {
            benchmarkResponse.data.items.forEach((risk: any) => {
              if (risk.code && currentCodes.includes(risk.code)) {
                riskCodes.add(risk.code);
              }
            });
          }
        } catch (err) {
          console.error('Error fetching benchmark risk data:', err);
        }
      }

      // Case 2: Client-specific risk matrix
      if (sanRiskCliente) {
        try {
          const clientRiskResponse = await axios.get(`${base}/api/profilegen-clients/riskmatrix/${clientId}`, { 
            withCredentials: true 
          });
          if (clientRiskResponse.status === 200 && clientRiskResponse.data?.items) {
            clientRiskResponse.data.items.forEach((risk: any) => {
              if (risk.code && currentCodes.includes(risk.code)) {
                riskCodes.add(risk.code);
              }
            });
          }
        } catch (err) {
          console.error('Error fetching client risk data:', err);
        }
      }

      // Convert set to object for easier lookup
      const riskClassification: {[code: string]: boolean} = {};
      riskCodes.forEach(code => {
        riskClassification[code] = true;
      });

      setRiskClassificationData(riskClassification);
      setIsRiskClassificationEnabled(true);
      
      // Show success modal with classification results
      showConsolidationAlert(
        `Classificação de ações de risco concluída. ${riskCodes.size} códigos foram classificados como ações de risco.`,
        'success'
      );
      
      console.debug('Risk classification completed', { 
        totalCodes: currentCodes.length, 
        riskCodes: riskCodes.size,
        sanRiskBenchmark,
        sanRiskCliente
      });

    } catch (err: any) {
      console.error('classifyRiskActions error:', err);
      showConsolidationAlert('Erro durante a classificação de ações de risco.', 'error');
      setRiskClassificationData({});
      setIsRiskClassificationEnabled(false);
    }
  }, [clientId, nomenclatureMappingData, base, showConsolidationAlert]);

  // Function to classify master/derived actions
  const classifyMasterDerivedActions = useCallback(async () => {
    if (!clientId) {
      console.warn('classifyMasterDerivedActions: Missing clientId');
      return;
    }

    setMasterDerivedLoading(true);

    try {
      // Fetch fresh client data to get derivation_status and derivation_orglevel
      console.debug('classifyMasterDerivedActions - fetching fresh client data for clientId:', clientId);
      
      const clientResponse = await axios.get(`${base}/api/profilegen-clients`, {
        params: { clientId },
        withCredentials: true
      });

      if (clientResponse.status !== 200 || !clientResponse.data?.items?.length) {
        console.warn('classifyMasterDerivedActions: No client data found');
        return;
      }

      // Defensive: filter returned clients by clientId param if backend returned multiple items
      let freshClientData: any = null;
      if (Array.isArray(clientResponse.data.items)) {
        freshClientData = clientResponse.data.items.find((c: any) => String(c.clientId || c.id) === String(clientId)) || clientResponse.data.items[0];
      } else {
        freshClientData = clientResponse.data.items;
      }
      console.debug('classifyMasterDerivedActions - fresh client data:', { 
        clientId, 
        freshClientData,
        derivation_status: freshClientData.derivation_status,
        derivation_orglevel: freshClientData.derivation_orglevel
      });

      const derivationStatus = freshClientData.derivation_status === 'true' || freshClientData.derivation_status === true;

      // Case 2: Derivation is not enabled
      if (!derivationStatus) {
        showConsolidationAlert('A derivação por nível organizacional não foi habilitada no passo 2 (Definir parâmetros gerais).', 'error');
        setIsMasterDerivedClassificationEnabled(false);
        return;
      }

      // Case 1: Derivation is enabled
      const derivationOrgLevel = freshClientData.derivation_orglevel;
      if (!derivationOrgLevel) {
        showConsolidationAlert('Nível organizacional de derivação não foi definido.', 'error');
        setIsMasterDerivedClassificationEnabled(false);
        return;
      }

      // Get all codes from the current nomenclature table
      const currentCodes = nomenclatureMappingData.map(item => item.codigo).filter(code => code && code !== '-');
      if (currentCodes.length === 0) {
        console.warn('No codes found in nomenclature table');
        return;
      }

      // Fetch transactions from sapactionorglevels table for the specified orglevel
      try {
        const orgLevelResponse = await axios.get(`${base}/api/sap-actions-orglevel`, {
          params: { orglevel: derivationOrgLevel },
          withCredentials: true
        });

        if (orgLevelResponse.status !== 200 || !orgLevelResponse.data?.items) {
          console.warn('No organizational level data found for:', derivationOrgLevel);
          setIsMasterDerivedClassificationEnabled(false);
          return;
        }

        const masterCodes = new Set<string>();
        const originalMasterCodes = new Set<string>();
        orgLevelResponse.data.items.forEach((item: any) => {
          if (item.code && currentCodes.includes(item.code)) {
            masterCodes.add(item.code);
            originalMasterCodes.add(item.code);
          }
        });

        // Propagate master classification to other codes in the same profile grouping
        // When a code is classified as master and has risk classification = "-" (false),
        // other codes in the same grouping (area, processo, subprocesso, atividade) 
        // that also have risk classification = "-" should also be marked as master
        const profileGroups = new Map<string, string[]>();
        const propagatedCodes = new Set<string>();
        
        // Group codes by their profile key (area-processo-subprocesso-atividade)
        nomenclatureMappingData.forEach(item => {
          const profileKey = `${item.area || ''}-${item.processo || ''}-${item.subprocesso || ''}-${item.atividade || ''}`;
          if (!profileGroups.has(profileKey)) {
            profileGroups.set(profileKey, []);
          }
          if (item.codigo) {
            profileGroups.get(profileKey)!.push(item.codigo);
          }
        });

        // For each profile group, if it contains a master code that is not a risk action,
        // mark all other non-risk codes in the same group as master
        profileGroups.forEach((codesInGroup, profileKey) => {
          // Check if this group has any master codes that are not risk actions
          const hasMasterNonRisk = codesInGroup.some(code => 
            originalMasterCodes.has(code) && !(isRiskClassificationEnabled && riskClassificationData[code])
          );
          
          if (hasMasterNonRisk) {
            // Mark all non-risk codes in this group as master
            codesInGroup.forEach(code => {
              if (!(isRiskClassificationEnabled && riskClassificationData[code])) {
                if (!originalMasterCodes.has(code)) {
                  propagatedCodes.add(code);
                }
                masterCodes.add(code);
              }
            });
          }
        });

        // Convert set to object for easier lookup
        const masterClassification: {[code: string]: boolean} = {};
        masterCodes.forEach(code => {
          masterClassification[code] = true;
        });

        // Convert propagated codes to object for easier lookup
        const propagatedClassification: {[code: string]: boolean} = {};
        propagatedCodes.forEach(code => {
          propagatedClassification[code] = true;
        });

        setMasterDerivedClassificationData(masterClassification);
        setMasterDerivedPropagatedData(propagatedClassification);
        setIsMasterDerivedClassificationEnabled(true);
        
        showConsolidationAlert(
          `Classificação mestre/derivado concluída. ${masterCodes.size} códigos foram classificados como perfil mestre.`,
          'success'
        );

        console.debug('Master/Derived classification completed', { 
          totalCodes: currentCodes.length, 
          masterCodes: masterCodes.size,
          derivationOrgLevel
        });

      } catch (err) {
        console.error('Error fetching organizational level data:', err);
        showConsolidationAlert('Erro ao buscar dados de nível organizacional.', 'error');
        setIsMasterDerivedClassificationEnabled(false);
      }

    } catch (err: any) {
      console.error('classifyMasterDerivedActions error:', err);
      showConsolidationAlert('Erro durante a classificação mestre/derivado.', 'error');
      setMasterDerivedClassificationData({});
      setIsMasterDerivedClassificationEnabled(false);
    } finally {
      setMasterDerivedLoading(false);
    }
  }, [clientId, nomenclatureMappingData, riskClassificationData, isRiskClassificationEnabled, base, showConsolidationAlert]);

  // Function to save current classification to profilegen-consolidation table
  const saveCurrentClassification = useCallback(async () => {
    if (!clientId || nomenclatureMappingData.length === 0) {
      console.warn('saveCurrentClassification: Missing clientId or no data to save');
      return;
    }

    setConsolidationLoading(true);
    setSavingModalOpen(true);
    savingCancelRef.current = false;
    const expectedCount = nomenclatureMappingData.length;
    let attempt = 1;

    while (!savingCancelRef.current) {
      try {
        setSavingProgress({ current: 0, total: expectedCount, message: `Tentativa ${attempt}: Preparando dados...` });

        // Prepare data for saving
        const consolidationData = nomenclatureMappingData.map(item => ({
          clientId: clientId,
          area: item.area || '',
          process: item.processo || '',
          subprocess: item.subprocesso || '',
          activity: item.atividade || '',
          code: item.codigo || '',
          risk: riskClassificationData[item.codigo] || false,
          master: masterDerivedClassificationData[item.codigo] || false,
          profilename: item.nomenclatura || ''
        }));

        console.debug('Saving consolidation data:', { clientId, count: consolidationData.length, attempt });

        // First clear existing data, then save new data
        setSavingProgress({ current: 0, total: expectedCount, message: `Tentativa ${attempt}: Limpando dados anteriores...` });
        await axios.delete(`${base}/api/profilegen-data/profilegen-consolidation/${clientId}`, { 
          withCredentials: true 
        });

        if (savingCancelRef.current) break;

        setSavingProgress({ current: Math.floor(expectedCount * 0.3), total: expectedCount, message: `Tentativa ${attempt}: Salvando ${consolidationData.length} registros...` });
        const response = await axios.post(`${base}/api/profilegen-data/profilegen-consolidation/${clientId}`, {
          rows: consolidationData
        }, { 
          withCredentials: true 
        });

        if (response.status === 200 || response.status === 201) {
          // Verify that all data was saved
          setSavingProgress({ current: Math.floor(expectedCount * 0.7), total: expectedCount, message: `Tentativa ${attempt}: Verificando salvamento...` });
          
          if (savingCancelRef.current) break;
          
          const savedData = await fetchAllConsolidationData();
          const savedCount = savedData.length;
          const percentComplete = (savedCount / expectedCount) * 100;
          
          console.log(`[saveCurrentClassification] Verification: saved ${savedCount}/${expectedCount} (${percentComplete.toFixed(1)}%)`);
          
          if (percentComplete >= 100) {
            // Success!
            setHasConsolidationData(true);
            setSavingProgress({ current: expectedCount, total: expectedCount, message: 'Salvamento concluído com sucesso!' });
            setTimeout(() => {
              setSavingProgress(null);
              setSavingModalOpen(false);
            }, 2000);
            console.log('Classification saved successfully');
            showConsolidationAlert(`Classificação salva com sucesso! ${savedCount} registros salvos.`, 'success');
            break; // Exit the while loop
          } else {
            // Incomplete, retry
            console.warn(`[saveCurrentClassification] Incomplete save (${percentComplete.toFixed(1)}%), retrying... (attempt ${attempt})`);
            setSavingProgress({ current: savedCount, total: expectedCount, message: `Salvamento incompleto (${percentComplete.toFixed(1)}%). Retentando em 2s...` });
            await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before retry
            attempt++;
            continue; // Retry
          }
        } else {
          throw new Error(`Resposta inesperada: ${response.status}`);
        }
      } catch (err: any) {
        console.error('saveCurrentClassification error:', err);
        if (savingCancelRef.current) break;
        console.warn(`[saveCurrentClassification] Error on attempt ${attempt}, retrying...`);
        setSavingProgress({ current: 0, total: expectedCount, message: `Erro na tentativa ${attempt}. Retentando em 2s...` });
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempt++;
      }
    }

    // Cleanup
    setSavingProgress(null);
    setSavingModalOpen(false);
    setConsolidationLoading(false);
    
    if (savingCancelRef.current) {
      showConsolidationAlert('Salvamento cancelado pelo usuário.', 'info');
    }
  }, [clientId, nomenclatureMappingData, riskClassificationData, masterDerivedClassificationData, base, showConsolidationAlert, fetchAllConsolidationData]);

  // Function to execute the deletion of current classification
  const executeDeleteClassification = useCallback(async () => {
    if (!clientId) {
      console.warn('executeDeleteClassification: Missing clientId');
      return;
    }

    setConsolidationLoading(true);
    try {
      console.debug('Deleting consolidation data for clientId:', clientId);

      const response = await axios.delete(`${base}/api/profilegen-data/profilegen-consolidation/${clientId}`, { 
        withCredentials: true 
      });

      if (response.status === 200) {
        setHasConsolidationData(false);
        setRiskClassificationData({});
        setIsRiskClassificationEnabled(false);
        setMasterDerivedClassificationData({});
        setMasterDerivedPropagatedData({});
        setIsMasterDerivedClassificationEnabled(false);
        // Clear master records so derived section updates immediately
        setMasterRecords([]);
        
        // Reload nomenclature data from original source
        await fetchNomenclatureMappingData();
        // Also reload consolidation mapping so saved profilenames are cleared from UI immediately
        try { await loadConsolidationRiskForSimple(); } catch (e) { /* ignore */ }
        
        console.log('Classification deleted successfully');
        showConsolidationAlert('Classificação apagada com sucesso!', 'success');
      }
    } catch (err: any) {
      console.error('executeDeleteClassification error:', err);
      showConsolidationAlert('Erro ao apagar classificação: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setConsolidationLoading(false);
    }
  }, [clientId, base, fetchNomenclatureMappingData, loadConsolidationRiskForSimple, showConsolidationAlert]);
  

  // Function to show confirmation modal for deleting current classification
  const deleteCurrentClassification = useCallback(() => {
    if (!clientId) {
      console.warn('deleteCurrentClassification: Missing clientId');
      return;
    }

    showConsolidationConfirm(
      'Tem certeza que deseja apagar a classificação atual? Esta ação não pode ser desfeita.',
      executeDeleteClassification
    );
  }, [clientId, executeDeleteClassification]);

  // Fetch composite user positions (userxpos) for the client
  const fetchCompositeUserPositions = useCallback(async () => {
    if (!clientId) return;
    setCompositeUserPositionsLoading(true);
    try {
      const itemsRaw = await fetchAllTableRows('profilegen-userxpos');
      if (Array.isArray(itemsRaw)) {
        // Normalize to expected fields n1..n4 and include any saved composite_name
        const items = itemsRaw.map((it: any) => ({
          user: it.user || it.usuario || '',
          n1: it.n1 || '',
          n2: it.n2 || '',
          n3: it.n3 || '',
          n4: it.n4 || '',
          composite_name: it.composite_name || it.compositeName || it.nomenclatura || ''
        }));
        setCompositeUserPositions(items);
        // If any saved composite_name exists, expose nomenclaturas immediately
        const anySaved = items.some((it: any) => it.composite_name && String(it.composite_name).trim() !== '');
        if (anySaved) {
          // copy composite_name into nomenclatura so table displays it
          setCompositeUserPositions(prev => (prev || []).map(r => ({ ...r, nomenclatura: r.composite_name || '' })));
          setCompositeNomenclatureGenerated(true);
        } else {
          // Reset generated flag when reloading source positions if no saved names
          setCompositeNomenclatureGenerated(false);
        }
      } else {
        setCompositeUserPositions([]);
        setCompositeNomenclatureGenerated(false);
      }
    } catch (err) {
      console.error('fetchCompositeUserPositions error:', err);
      setCompositeUserPositions([]);
      setCompositeNomenclatureGenerated(false);
    } finally {
      setCompositeUserPositionsLoading(false);
    }
  }, [clientId, fetchAllTableRows]);

  // Filter and sort composite user positions
  const getFilteredAndSortedCompositePositions = useCallback(() => {
    // Clone source
    let list = Array.isArray(compositeUserPositions) ? [...compositeUserPositions] : [];
    const q = (compositeSearchQ || '').toLowerCase().trim();

    // Compute nomenclatura for each row. If nomenclature has already been generated (via IA/Generate action)
    // prefer the stored item.nomenclatura; otherwise fall back to a simple joined representation.
    const prefix = String(compositePrefixPatterns.composto || client?.pattern_c_prefixo_composto || '').trim();
    const withNomen = list.map(item => {
      if (compositeNomenclatureGenerated && item && item.nomenclatura) {
        return item;
      }
      const parts = [item.n1, item.n2, item.n3, item.n4].map((p: any) => (p || '').toString().trim()).filter((v: string) => v !== '');
      const joined = parts.join('-');
      const nome = prefix ? (joined ? `${prefix}-${joined}` : prefix) : (joined || '');
      return { ...item, nomenclatura: nome };
    });

    // Deduplicate by identical N1..N4 values (keep first occurrence)
    const seen = new Set<string>();
    let uniqueList: any[] = [];
    for (const it of withNomen) {
      const key = `${String(it.n1 || '')}||${String(it.n2 || '')}||${String(it.n3 || '')}||${String(it.n4 || '')}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueList.push(it);
      }
    }

    // Apply search/filter
    if (q) {
      const col = compositeSearchColumn;
      if (col) {
        uniqueList = uniqueList.filter(item => String(item[col] || '').toLowerCase().includes(q));
      } else {
        uniqueList = uniqueList.filter(item => [item.n1, item.n2, item.n3, item.n4, item.nomenclatura].some(v => String(v || '').toLowerCase().includes(q)));
      }
    }

    // Apply sort
    if (compositeSortColumn) {
      uniqueList.sort((a, b) => {
        const aVal = String(a[compositeSortColumn] || '');
        const bVal = String(b[compositeSortColumn] || '');
        return compositeSortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
    } else {
      uniqueList.sort((a, b) => String(a.n1 || '').localeCompare(String(b.n1 || '')));
    }

    return uniqueList;
  }, [compositeUserPositions, compositeSearchQ, compositeSearchColumn, compositeSortColumn, compositeSortDirection, compositePrefixPatterns, client, compositeNomenclatureGenerated]);

  // Execute deletion of composite_name values for this client (clears saved composite nomenclatures)
  const executeDeleteCompositeClassification = useCallback(async () => {
    if (!clientId) {
      console.warn('executeDeleteCompositeClassification: Missing clientId');
      return;
    }

    setConsolidationLoading(true);
    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

      // Use filtered/deduped source (prefers generated nomenclaturas) to determine which N1..N4 combos to clear
      const source = getFilteredAndSortedCompositePositions();
      const uniqKeys = new Map<string, { n1: string; n2: string; n3: string; n4: string }>();
      (source || []).forEach((it: any) => {
        const n1 = String(it.n1 || '').trim();
        const n2 = String(it.n2 || '').trim();
        const n3 = String(it.n3 || '').trim();
        const n4 = String(it.n4 || '').trim();
        const key = `${n1}||${n2}||${n3}||${n4}`;
        if (!uniqKeys.has(key)) uniqKeys.set(key, { n1, n2, n3, n4 });
      });

      // If no deduped rows found (edge-case), fall back to raw compositeUserPositions
      if (uniqKeys.size === 0 && Array.isArray(compositeUserPositions) && compositeUserPositions.length > 0) {
        (compositeUserPositions || []).forEach((it: any) => {
          const n1 = String(it.n1 || '').trim();
          const n2 = String(it.n2 || '').trim();
          const n3 = String(it.n3 || '').trim();
          const n4 = String(it.n4 || '').trim();
          const key = `${n1}||${n2}||${n3}||${n4}`;
          if (!uniqKeys.has(key)) uniqKeys.set(key, { n1, n2, n3, n4 });
        });
      }

      const rows = Array.from(uniqKeys.values()).map(v => ({ ...v, composite_name: '' }));

      if (rows.length === 0) {
        showConsolidationAlert('Nenhuma nomenclatura salva encontrada para apagar.', 'info');
        setConsolidationLoading(false);
        return;
      }

      const res = await axios.patch(`${base}/api/profilegen-data/profilegen-userxpos/composite-name/${clientId}`, { rows }, { withCredentials: true });
      if (res.status === 200) {
        // Refresh positions to reflect cleared composite_name
        await fetchCompositeUserPositions();
        showConsolidationAlert('Nomenclaturas compostas apagadas com sucesso.', 'success');
      } else {
        showConsolidationAlert('Não foi possível apagar as nomenclaturas compostas.', 'error');
      }
    } catch (err: any) {
      console.error('executeDeleteCompositeClassification error:', err);
      showConsolidationAlert('Erro ao apagar nomenclaturas compostas: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setConsolidationLoading(false);
    }
  }, [clientId, getFilteredAndSortedCompositePositions, compositeUserPositions, fetchCompositeUserPositions, showConsolidationAlert]);

  // Show confirmation modal and execute composite deletion
  const deleteCompositeClassification = useCallback(() => {
    if (!clientId) {
      console.warn('deleteCompositeClassification: Missing clientId');
      return;
    }
    showConsolidationConfirm(
      'Tem certeza que deseja apagar as nomenclaturas compostas salvas para este cliente? Esta ação irá remover os valores em composite_name e não pode ser desfeita.',
      executeDeleteCompositeClassification
    );
  }, [clientId, executeDeleteCompositeClassification]);

  // Function to save only derived profiles into consolidation table
  const saveDerivedClassification = useCallback(async () => {
    if (!clientId) {
      console.warn('saveDerivedClassification: Missing clientId');
      return;
    }

    if (!masterRecords || masterRecords.length === 0) {
      showConsolidationAlert('Nenhuma nomenclatura derivada disponível para salvar.', 'error');
      return;
    }

    setConsolidationLoading(true);
    try {
      // Build derived rows: for each derived nomenclature, find the master name and all master codes
      const derivedRows: any[] = [];

      // Build a map of masterName => array of codes from nomenclatureMappingData
      const masterCodesMap: { [masterName: string]: string[] } = {};
      (nomenclatureMappingData || []).forEach((it: any) => {
        const name = String(it.nomenclatura || '').trim();
        const code = String(it.codigo || '').trim();
        if (!name || !code) return;
        if (!masterCodesMap[name]) masterCodesMap[name] = [];
        if (!masterCodesMap[name].includes(code)) masterCodesMap[name].push(code);
      });

      for (const r of masterRecords) {
        const derivedName = String(r.nomenclatura_derivado || r.derivedNomenclatura || '').trim();
        if (!derivedName) continue; // skip master rows

        const masterName = String(r.nomenclatura || '').trim();
        const codes = masterCodesMap[masterName] || [];

        const derivedOrgLevelValue = String(r.derived_orglevel_value || '').trim();

        if (codes.length === 0) {
          // If no master codes found, still save one row with empty code to keep trace
          derivedRows.push({
            clientId,
            area: r.area || '',
            process: r.processo || r.process || '',
            subprocess: r.subprocesso || r.subprocess || '',
            activity: r.atividade || r.activity || '',
            code: r.codigo || r.code || '',
            profilename: derivedName,
            risk: Boolean(r.risco || r.risk || false),
            master: false,
            derived: true,
            master_name: masterName,
            derived_orglevel_value: derivedOrgLevelValue
          });
        } else {
          // Create one row per master code
          for (const c of codes) {
            derivedRows.push({
              clientId,
              area: r.area || '',
              process: r.processo || r.process || '',
              subprocess: r.subprocesso || r.subprocess || '',
              activity: r.atividade || r.activity || '',
              code: c,
              profilename: derivedName,
              risk: Boolean(r.risco || r.risk || riskClassificationData[c] || false),
              master: false,
              derived: true,
              master_name: masterName,
              derived_orglevel_value: derivedOrgLevelValue
            });
          }
        }
      }

      if (derivedRows.length === 0) {
        showConsolidationAlert('Nenhuma nomenclatura derivada foi encontrada para salvar.', 'error');
        return;
      }

      // Avoid inserting duplicates: fetch existing consolidation rows and filter
      let rowsToInsert = derivedRows;
      try {
        const existingItems = await fetchAllConsolidationData();
        if (existingItems.length > 0) {
          const existsSet = new Set<string>();
          existingItems.forEach(it => {
            const k = `${String(it.master_name || '')}||${String(it.profilename || '')}||${String(it.code || '')}`;
            existsSet.add(k);
          });

          rowsToInsert = derivedRows.filter(dr => {
            const k = `${String(dr.master_name || '')}||${String(dr.profilename || '')}||${String(dr.code || '')}`;
            return !existsSet.has(k);
          });
        }
      } catch (err) {
        console.warn('Could not fetch existing consolidation rows to dedupe derived inserts, proceeding to insert all', err);
        // proceed with all rows if check fails
        rowsToInsert = derivedRows;
      }

      if (rowsToInsert.length === 0) {
        showConsolidationAlert('Nenhuma nomenclatura derivada nova para salvar (todas já existem).', 'info');
      } else {
        const response = await axios.post(`${base}/api/profilegen-data/profilegen-consolidation/${clientId}`, {
          rows: rowsToInsert
        }, { withCredentials: true });

        if (response.status === 200 || response.status === 201) {
          showConsolidationAlert(`Foram salvas ${rowsToInsert.length} nomenclaturas derivadas.`, 'success');
          setHasConsolidationData(true);
        }
      }
    } catch (err: any) {
      console.error('saveDerivedClassification error:', err);
      showConsolidationAlert('Erro ao salvar nomenclaturas derivadas: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setConsolidationLoading(false);
    }
  }, [clientId, masterRecords, nomenclatureMappingData, riskClassificationData, base, showConsolidationAlert, fetchAllConsolidationData]);


  // Auto-load IA conversions when entering step 5
  useEffect(() => {
    if (step === 5 && clientId && nomenclatureMappingData.length > 0) {
      console.log('[DEBUG] Auto-loading IA conversions for step 5');
      loadSavedAiResults();
    }
  }, [step, clientId, nomenclatureMappingData.length, loadSavedAiResults]);

 

  // When composite accordion opens, load the userxpos data
  useEffect(() => {
    if (nomenclatureSectionExpanded.perfisCompostos && String(client?.profileModel) === 'Posição Composto + Atividade Simples') {
      fetchCompositeUserPositions();
    }
  }, [nomenclatureSectionExpanded.perfisCompostos, client?.profileModel, fetchCompositeUserPositions]);

 

  // Generate composite nomenclature preview (simple generator using configured composite prefix)
  const generateCompositeNomenclature = useCallback(() => {
    // Behavior: build nomenclatures for unique N1..N4 combinations using the same logic as simple generator
    (async () => {
      if (!clientId) {
        showConsolidationAlert('Cliente não carregado.', 'error');
        return;
      }

      // Get deduped unique N1..N4 rows
      const uniqueRows = getFilteredAndSortedCompositePositions();
      if (!uniqueRows || uniqueRows.length === 0) {
        showConsolidationAlert('Nenhum dado de posições disponível para gerar nomenclaturas compostas.', 'error');
        return;
      }

      setConsolidationLoading(true);
      try {
        // Load saved IA conversions lookup (fresh)
        let aiConversions: any = {};
        try { aiConversions = await loadSavedAiResults() || {}; } catch (err) { aiConversions = {}; }

        // Try to refresh client configuration to get the latest pattern_c_* fields
        let currentClient = client;
        try {
          if (clientId) {
            const listRes = await axios.get((process.env.REACT_APP_API_URL || '').replace(/\/$/, '') + '/api/profilegen-clients', {
              params: { q: clientId, searchColumn: 'clientId', _ts: Date.now() }, withCredentials: true, headers: { 'Cache-Control': 'no-cache' }
            });
            if (listRes && listRes.data && Array.isArray(listRes.data.items) && listRes.data.items.length > 0) {
              currentClient = listRes.data.items[0];
            }
          }
        } catch (err) {
          console.warn('Could not refresh client config for composite nomenclature, using local client state', err);
        }

        const patternPrefix = 'pattern_c_';
        const prefix = String(compositePrefixPatterns.composto || currentClient?.pattern_c_prefixo_composto || '').trim();

        const updatedMap = new Map<string, { nomenclatura: string; nomenclaturaExceedsLimit: boolean }>();

        for (const row of uniqueRows) {
          const n1raw = String(row.n1 || '').trim();
          const n2raw = String(row.n2 || '').trim();
          const n3raw = String(row.n3 || '').trim();
          const n4raw = String(row.n4 || '').trim();

          // For each level, attempt to use AI conversion if available
          const levels = ['n1', 'n2', 'n3', 'n4'];
          const converted: string[] = [];
          for (let i = 0; i < levels.length; i++) {
            const lv = levels[i];
            const raw = [n1raw, n2raw, n3raw, n4raw][i] || '';
            if (!raw) { converted.push(''); continue; }

            const qtyField = `${patternPrefix}${lv}_qtd`;
            const numChar = currentClient ? currentClient[qtyField] : null;
            const numericQty = (numChar === null || numChar === undefined) ? null : Number(numChar);

            let finalVal = normalizeRawFallbackValue(raw, numericQty || undefined);
            if (numericQty && !Number.isNaN(numericQty) && numericQty > 0) {
              const typeRs = 'Composto';
              const key = `${lv}_${raw}_${typeRs}_${numericQty}`;
              if (aiConversions && aiConversions[key]) {
                finalVal = aiConversions[key];
              }
            }

            converted.push(finalVal);
          }

            const nomenclature = buildNomenclatureByPatternOrder(currentClient, patternPrefix, [
              { type: 'prefixo', value: prefix || '' },
              { type: 'n1', value: converted[0] || n1raw || '' },
              { type: 'n2', value: converted[1] || n2raw || '' },
              { type: 'n3', value: converted[2] || n3raw || '' },
              { type: 'n4', value: converted[3] || n4raw || '' },
              { type: 'sufixo', value: '' }
            ]);

          // Check against max length
          const resolvedMax = Number(patternNameMaxChar || currentClient?.pattern_name_max_char || 30);
          const exceeds = nomenclature.length > resolvedMax;

          const key = `${n1raw}||${n2raw}||${n3raw}||${n4raw}`;
          updatedMap.set(key, { nomenclatura: nomenclature || '', nomenclaturaExceedsLimit: exceeds });
        }

        // Apply updates to compositeUserPositions state
        setCompositeUserPositions(prev => (prev || []).map(row => {
          const key = `${String(row.n1 || '').trim()}||${String(row.n2 || '').trim()}||${String(row.n3 || '').trim()}||${String(row.n4 || '').trim()}`;
          if (updatedMap.has(key)) {
            const v = updatedMap.get(key)!;
            return { ...row, nomenclatura: v.nomenclatura, nomenclaturaExceedsLimit: v.nomenclaturaExceedsLimit };
          }
          return row;
        }));
        // Mark that nomenclature generation completed so table may display values
        setCompositeNomenclatureGenerated(true);

        showConsolidationAlert(`Geradas ${updatedMap.size} nomenclaturas compostas (pré-visualização).`, 'success');
      } catch (err: any) {
        console.error('generateCompositeNomenclature error:', err);
        showConsolidationAlert('Erro ao gerar nomenclaturas compostas: ' + (err.response?.data?.message || err.message), 'error');
      } finally {
        setConsolidationLoading(false);
      }
    })();
  }, [clientId, client, compositePrefixPatterns, showConsolidationAlert, getFilteredAndSortedCompositePositions, loadSavedAiResults, patternNameMaxChar, buildNomenclatureByPatternOrder, normalizeRawFallbackValue]);

  // Generate nomenclature for simple positions (Posição/Cargo Simples) using N1..N4
  const generateSimplePositionNomenclature = useCallback(async () => {
    if (!clientId || !client) {
      showConsolidationAlert('Cliente não carregado.', 'error');
      return;
    }

    // Build a flattened list of position rows (one per code) from positionsWithCodes
    const sourceRows: any[] = [];
    (positionsWithCodes || []).forEach((p: any) => {
      const codes = p.codes || [];
      if (!codes.length) {
        sourceRows.push({ n1: p.n1 || '', n2: p.n2 || '', n3: p.n3 || '', n4: p.n4 || '', code: '' });
      } else {
        codes.forEach((c: string) => sourceRows.push({ n1: p.n1 || '', n2: p.n2 || '', n3: p.n3 || '', n4: p.n4 || '', code: c }));
      }
    });

    if (sourceRows.length === 0) {
      showConsolidationAlert('Nenhuma posição/código disponível para gerar nomenclatura.', 'error');
      return;
    }

    setConsolidationLoading(true);
    try {
  // Load IA conversions
  const aiConversions = await loadSavedAiResults().catch(() => ({} as Record<string, string>)) || ({} as Record<string, string>);

      // Refresh client config if possible
      let currentClient = client;
      try {
        const listRes = await axios.get(`${base}/api/profilegen-clients`, { params: { q: clientId, searchColumn: 'clientId', _ts: Date.now() }, withCredentials: true, headers: { 'Cache-Control': 'no-cache' } });
        if (listRes.status === 200 && Array.isArray(listRes.data?.items) && listRes.data.items.length > 0) {
          currentClient = listRes.data.items[0];
        }
      } catch (err) { /* ignore */ }

      const patternPrefixRisk = 'pattern_r_';
      const patternPrefixSimple = 'pattern_c_'; // using pattern_c_* for simple positions non-risk nomenclature

      const updatedMap: Record<string, { nomenclatura: string; nomenclaturaExceedsLimit: boolean }> = {};

      for (const row of sourceRows) {
        const n1raw = String(row.n1 || '').trim();
        const n2raw = String(row.n2 || '').trim();
        const n3raw = String(row.n3 || '').trim();
        const n4raw = String(row.n4 || '').trim();
        const code = String(row.code || '').trim();

        // Determine risk based on consolidation saved flags (by code)
        const isRisk = Boolean(code && riskClassificationData[code]);

        const patternPrefix = isRisk ? patternPrefixRisk : patternPrefixSimple;

        // Determine prefix
        let prefix = '';
        if (isRisk) prefix = currentClient[`pattern_r_prefixo_risco`] || '';
        else prefix = currentClient[`pattern_c_prefixo_composto`] || currentClient[`pattern_c_prefixo_composto`] || '';

        // For each n level, attempt to use IA conversion (typeRs = 'Risco' or 'Simples')
        const levels = ['n1', 'n2', 'n3', 'n4'];
        const converted: string[] = [];
        for (let i = 0; i < levels.length; i++) {
          const lv = levels[i];
          const raw = [n1raw, n2raw, n3raw, n4raw][i] || '';
          if (!raw) { converted.push(''); continue; }

          const qtdField = `${patternPrefix}${lv}_qtd`;
          const numChar = currentClient ? currentClient[qtdField] : null;
          const numericQty = (numChar === null || numChar === undefined) ? null : Number(numChar);

          let finalVal = normalizeRawFallbackValue(raw, numericQty || undefined);
          if (numericQty && !Number.isNaN(numericQty) && numericQty > 0) {
            const typeRs = isRisk ? 'Risco' : 'Simples';
            const key = `${lv}_${raw}_${typeRs}_${numericQty}`;
            if (aiConversions && aiConversions[key]) finalVal = aiConversions[key];
          }

          converted.push(finalVal);
        }

        const elements: Array<{ type: string; value: string }> = [
          { type: 'prefixo', value: prefix || '' },
          { type: 'n1', value: converted[0] || n1raw || '' },
          { type: 'n2', value: converted[1] || n2raw || '' },
          { type: 'n3', value: converted[2] || n3raw || '' },
          { type: 'n4', value: converted[3] || n4raw || '' }
        ];

        // Add action element for risk profiles if configured (same logic as Atividade Simples)
        if (isRisk && code && currentClient && currentClient[`pattern_r_acao_ord`] != null) {
          let acaoVal = String(code || '');
          const acaoQtd = currentClient[`pattern_r_acao_qtd`] || 0;
          if (acaoQtd > 0 && acaoVal.length > acaoQtd) acaoVal = acaoVal.substring(0, acaoQtd);
          elements.push({ type: 'acao', value: acaoVal });
        } 
        const nomenclature = buildNomenclatureByPatternOrder(currentClient, patternPrefix, elements);
        const resolvedMax = Number(patternNameMaxChar || currentClient?.pattern_name_max_char || 30);
        const exceeds = nomenclature.length > resolvedMax;

        // Use a composite key including code and the N1..N4 values so that
        // the preview map is unique per position occurrence. If the same
        // code appears in multiple N1..N4 groups, using only `code` would
        // cause later rows to overwrite earlier ones.
        const compKey = `${code || ''}||${n1raw}||${n2raw}||${n3raw}||${n4raw}`;
        updatedMap[compKey] = { nomenclatura: nomenclature || '', nomenclaturaExceedsLimit: exceeds };
      }

      // Update state
      setSimplePositionNomenclature(updatedMap);
      showConsolidationAlert(`Geradas ${Object.keys(updatedMap).length} nomenclaturas simples (pré-visualização).`, 'success');
    } catch (err: any) {
      console.error('generateSimplePositionNomenclature error:', err);
      showConsolidationAlert('Erro ao gerar nomenclaturas simples: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setConsolidationLoading(false);
    }
  }, [clientId, client, positionsWithCodes, riskClassificationData, loadSavedAiResults, patternNameMaxChar, base, showConsolidationAlert, buildNomenclatureByPatternOrder, normalizeRawFallbackValue]);

  // Save composite classification rows into consolidation table (one row per mapping item)
  const saveCompositeClassification = useCallback(async () => {
    if (!clientId) {
      console.warn('saveCompositeClassification: Missing clientId');
      return;
    }

    const isCompositeModel = String(client?.profileModel) === 'Posição Composto + Atividade Simples';
    if (!isCompositeModel) {
      if (!nomenclatureMappingData || nomenclatureMappingData.length === 0) {
        showConsolidationAlert('Nenhum dado disponível para salvar perfis simples.', 'error');
        return;
      }
    } else {
      // For composite model require nomenclature generation and source positions
      if (!compositeNomenclatureGenerated) {
        showConsolidationAlert('Nenhuma nomenclatura gerada — clique em "Gerar nomenclatura (compostos)" antes de salvar.', 'error');
        return;
      }
      if (!Array.isArray(compositeUserPositions) || compositeUserPositions.length === 0) {
        showConsolidationAlert('Nenhum dado de posições carregado para este cliente.', 'error');
        return;
      }
    }

    const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    setConsolidationLoading(true);
    try {
      // For non-composite (simple) saving we still require a prefix to build profilename
      let prefix = '';
      if (!isCompositeModel) {
        prefix = String(compositePrefixPatterns.composto || client?.pattern_c_prefixo_composto || '').trim();
        if (!prefix) {
          showConsolidationAlert('Prefixo composto não configurado para este cliente.', 'error');
          return;
        }
      } else {
        // For composites we will persist the already-generated names, no prefix required here
        prefix = String(compositePrefixPatterns.composto || client?.pattern_c_prefixo_composto || '').trim();
      }

      const rows: any[] = (nomenclatureMappingData || []).map((it: any) => ({
        clientId,
        area: it.area || '',
        process: it.processo || it.process || '',
        subprocess: it.subprocesso || it.subprocess || '',
        activity: it.atividade || it.activity || '',
        code: it.codigo || it.code || '',
        profilename: `${prefix}${prefix ? '-' : ''}${String(it.codigo || '')}`,
        risk: Boolean(riskClassificationData[it.codigo] || false),
        master: false,
        derived: false,
        master_name: ''
      }));

      if (rows.length === 0) {
        showConsolidationAlert('Nenhum registro foi gerado para salvar.', 'error');
        return;
      }

      // Dedupe against existing consolidation documents
      let rowsToInsert = rows;
      try {
        const existingItems = await fetchAllConsolidationData();
        const existsSet = new Set<string>();
        existingItems.forEach((it: any) => {
          const k = `${String(it.profilename || '')}||${String(it.code || '')}`;
          existsSet.add(k);
        });

        rowsToInsert = rows.filter(r => {
          const k = `${String(r.profilename || '')}||${String(r.code || '')}`;
          return !existsSet.has(k);
        });
      } catch (err) {
        console.warn('Could not fetch existing consolidation rows to dedupe composite inserts, proceeding to insert all', err);
        rowsToInsert = rows;
      }

      // If this is the simple model (non-composite), persist into consolidation and return
      if (!isCompositeModel) {
        const expectedCount = rowsToInsert.length;
        if (expectedCount === 0) {
          showConsolidationAlert('Nenhuma nomenclatura nova para salvar (todas já existem).', 'info');
          return;
        }

        setSavingModalOpen(true);
        savingCancelRef.current = false;
        let attempt = 1;

        while (!savingCancelRef.current) {
          try {
            setSavingProgress({ current: 0, total: expectedCount, message: `Tentativa ${attempt}: Salvando ${expectedCount} registros...` });
            
            const resp = await axios.post(`${base}/api/profilegen-data/profilegen-consolidation/${clientId}`, { rows: rowsToInsert }, { withCredentials: true });
            
            if (savingCancelRef.current) break;
            
            if (resp.status === 200 || resp.status === 201) {
              // Verify that all data was saved
              setSavingProgress({ current: Math.floor(expectedCount * 0.7), total: expectedCount, message: `Tentativa ${attempt}: Verificando salvamento...` });
              
              const savedData = await fetchAllConsolidationData();
              const savedCount = savedData.length;
              const percentComplete = (savedCount / expectedCount) * 100;
              
              console.log(`[saveCompositeClassification-simple] Verification: saved ${savedCount}/${expectedCount} (${percentComplete.toFixed(1)}%)`);
              
              if (percentComplete >= 100 || savedCount >= expectedCount) {
                // Success!
                setHasConsolidationData(true);
                setSavingProgress({ current: expectedCount, total: expectedCount, message: 'Salvamento concluído com sucesso!' });
                setTimeout(() => {
                  setSavingProgress(null);
                  setSavingModalOpen(false);
                }, 2000);
                showConsolidationAlert(`Foram gravadas ${savedCount} nomenclaturas em profilegen-consolidation.`, 'success');
                break; // Exit the while loop
              } else {
                // Incomplete, retry
                console.warn(`[saveCompositeClassification-simple] Incomplete save (${percentComplete.toFixed(1)}%), retrying... (attempt ${attempt})`);
                setSavingProgress({ current: savedCount, total: expectedCount, message: `Salvamento incompleto (${percentComplete.toFixed(1)}%). Retentando em 2s...` });
                await new Promise(resolve => setTimeout(resolve, 2000));
                attempt++;
                continue;
              }
            } else {
              throw new Error(`Resposta inesperada: ${resp.status}`);
            }
          } catch (err: any) {
            console.error('Erro saving consolidation rows', err);
            if (savingCancelRef.current) break;
            console.warn(`[saveCompositeClassification-simple] Error on attempt ${attempt}, retrying...`);
            setSavingProgress({ current: 0, total: expectedCount, message: `Erro na tentativa ${attempt}. Retentando em 2s...` });
            await new Promise(resolve => setTimeout(resolve, 2000));
            attempt++;
          }
        }
        
        // Cleanup
        setSavingProgress(null);
        setSavingModalOpen(false);
        
        if (savingCancelRef.current) {
          showConsolidationAlert('Salvamento cancelado pelo usuário.', 'info');
        }
        return;
      }

      // Persist generated nomenclaturas into profilegen-userxpos
      try {
        // Use the deduplicated filtered composite positions (which prefer generated nomenclaturas)
        const sourceRows = getFilteredAndSortedCompositePositions();
        const uniq = new Map<string, { n1: string; n2: string; n3: string; n4: string; composite_name: string }>();
        (sourceRows || []).forEach((it: any) => {
          const n1 = String(it.n1 || '').trim();
          const n2 = String(it.n2 || '').trim();
          const n3 = String(it.n3 || '').trim();
          const n4 = String(it.n4 || '').trim();
          const name = it.nomenclatura;
          if (name && name !== '-') {
            const key = `${n1}||${n2}||${n3}||${n4}`;
            if (!uniq.has(key)) {
              uniq.set(key, { n1, n2, n3, n4, composite_name: name });
            }
          }
        });

        const updatedRows = Array.from(uniq.values());

        if (updatedRows.length === 0) {
          showConsolidationAlert('Nenhuma nomenclatura gerada para persistir em profilegen-userxpos.', 'info');
        } else {
          const res = await axios.patch(`${base}/api/profilegen-data/profilegen-userxpos/composite-name/${clientId}`, { rows: updatedRows }, { withCredentials: true });
          if (res.status === 200) {
            showConsolidationAlert(`Foram gravadas ${updatedRows.length} nomenclaturas em profilegen-userxpos.`, 'success');
          } else {
            showConsolidationAlert('Não foi possível persistir as nomenclaturas em profilegen-userxpos.', 'error');
          }
        }
      } catch (err: any) {
        console.error('Erro ao persistir composite_name em profilegen-userxpos', err);
        showConsolidationAlert('Erro ao salvar nomenclaturas compostas no servidor: ' + (err.response?.data?.message || err.message), 'error');
      }
    } catch (err: any) {
      console.error('saveCompositeClassification error:', err);
      showConsolidationAlert('Erro ao salvar perfis compostos: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setConsolidationLoading(false);
    }
  }, [clientId, nomenclatureMappingData, compositePrefixPatterns, client, riskClassificationData, showConsolidationAlert, compositeNomenclatureGenerated, compositeUserPositions, getFilteredAndSortedCompositePositions]);

  // Save current classification derived from simple positions (Posição/Cargo Simples)
  const saveSimpleClassification = useCallback(async () => {
    if (!clientId) {
      console.warn('saveSimpleClassification: Missing clientId');
      return;
    }

    setConsolidationLoading(true);
    setSavingModalOpen(true);
    savingCancelRef.current = false;

    try {
      // Build rows: one row per code with N1..N4 and risk flag
      const rows: any[] = [];
      (positionsWithCodes || []).forEach((p: any) => {
        const n1 = p.n1 || '';
        const n2 = p.n2 || '';
        const n3 = p.n3 || '';
        const n4 = p.n4 || '';
        // users intentionally read from p.users when needed elsewhere; avoid unused variable warning
        const codes = p.codes || [];
        if (!codes || codes.length === 0) {
          // save an empty-code row to keep trace
          rows.push({ clientId, n1, n2, n3, n4, code: '', risk: false });
        } else {
          codes.forEach((c: string) => {
            rows.push({ clientId, n1, n2, n3, n4, code: c, risk: Boolean(riskClassificationData[String(c)] || false) });
          });
        }
      });

      if (rows.length === 0) {
        showConsolidationAlert('Nenhuma posição/código disponível para salvar.', 'error');
        return;
      }

      // Overwrite existing consolidation rows for this client with the new data (including generated nomenclaturas)
      const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

      // Helper to build a composite normalized key for matching between UI map and DB rows.
      // Always include code and the N1..N4 values to uniquely identify a position occurrence.
      const makeKey = (n1: any, n2: any, n3: any, n4: any, code: any) => {
        const c = String(code || '').trim();
        const k1 = String(n1 || '').trim();
        const k2 = String(n2 || '').trim();
        const k3 = String(n3 || '').trim();
        const k4 = String(n4 || '').trim();
        return `${c}||${k1}||${k2}||${k3}||${k4}`;
      };

      // Enrich rows with generated nomenclature preview if available (use normalized keys)
      const enrichedRows = rows.map(r => {
        const key = makeKey(r.n1, r.n2, r.n3, r.n4, r.code);
        const prof = (simplePositionNomenclature && simplePositionNomenclature[key] && simplePositionNomenclature[key].nomenclatura) ? simplePositionNomenclature[key].nomenclatura : '';
        return { ...r, profilename: prof };
      });

      // Try to fetch existing consolidation rows to preserve previously saved profilename values
      let existingMap: Record<string, string> = {};
      try {
        const existingItems = await fetchAllConsolidationData();
        existingItems.forEach((it: any) => {
          const key = makeKey(it.n1, it.n2, it.n3, it.n4, it.code);
          if (it.profilename && String(it.profilename).trim() !== '') existingMap[key] = String(it.profilename);
        });
      } catch (err) {
        // If we can't read existing rows, proceed; we will overwrite with enrichedRows only
        console.warn('Could not read existing consolidation rows prior to save; proceeding with overwrite', err);
      }

      // Merge enrichedRows with existingMap: prefer generated profilename, otherwise keep existing saved profilename
      const mergedRows = enrichedRows.map(r => {
        const key = makeKey(r.n1, r.n2, r.n3, r.n4, r.code);
        const existingPro = existingMap[key] || '';
        const finalPro = (r.profilename && String(r.profilename).trim() !== '') ? r.profilename : existingPro;
        return { ...r, profilename: finalPro };
      });

      const expectedCount = mergedRows.length;
      let attempt = 1;

      while (!savingCancelRef.current) {
        try {
          setSavingProgress({ current: 0, total: expectedCount, message: `Tentativa ${attempt}: Limpando dados anteriores...` });
          try {
            // Delete any existing consolidation rows for this client to perform an overwrite
            await axios.delete(`${baseUrl}/api/profilegen-data/profilegen-consolidation/${clientId}`, { withCredentials: true });
          } catch (err) {
            console.warn('Could not delete existing consolidation rows before overwrite, proceeding to post merged rows', err);
          }

          if (savingCancelRef.current) break;

          setSavingProgress({ current: Math.floor(expectedCount * 0.3), total: expectedCount, message: `Tentativa ${attempt}: Salvando ${expectedCount} registros...` });
          // Debug: log a small sample of what we'll send
          try { console.debug('[DEBUG] Saving consolidation rows (sample 10):', mergedRows.slice(0, 10)); } catch (err) { }

          const resp = await axios.post(`${baseUrl}/api/profilegen-data/profilegen-consolidation/${clientId}`, { rows: mergedRows }, { withCredentials: true });

          if (savingCancelRef.current) break;

          if (resp.status === 200 || resp.status === 201) {
            setSavingProgress({ current: Math.floor(expectedCount * 0.7), total: expectedCount, message: `Tentativa ${attempt}: Verificando salvamento...` });

            const saved = await fetchAllConsolidationData();
            const savedCount = saved.length;
            const percentComplete = (savedCount / expectedCount) * 100;
            console.debug('[DEBUG] Saved consolidation rows count:', savedCount);

            if (percentComplete >= 100 || savedCount >= expectedCount) {
              setHasConsolidationData(true);
              setSavingProgress({ current: expectedCount, total: expectedCount, message: 'Salvamento concluido com sucesso!' });
              setTimeout(() => {
                setSavingProgress(null);
                setSavingModalOpen(false);
              }, 2000);

              // Build map from saved rows and update local simplePositionNomenclature accordingly
              const savedMap: Record<string, { nomenclatura: string; nomenclaturaExceedsLimit: boolean }> = { ...(simplePositionNomenclature || {}) };
              saved.forEach((s: any) => {
                const key = makeKey(s.n1, s.n2, s.n3, s.n4, s.code);
                if (s.profilename && String(s.profilename).trim() !== '') {
                  savedMap[key] = { nomenclatura: String(s.profilename), nomenclaturaExceedsLimit: false };
                }
              });
              setSimplePositionNomenclature(savedMap);

              showConsolidationAlert(`Foram gravados ${savedCount} registros em profilegen-consolidation (sobrescrito).`, 'success');
              break;
            }

            setSavingProgress({ current: savedCount, total: expectedCount, message: `Salvamento incompleto (${percentComplete.toFixed(1)}%). Retentando em 2s...` });
            await new Promise(resolve => setTimeout(resolve, 2000));
            attempt++;
            continue;
          }

          showConsolidationAlert('Nao foi possivel salvar a classificacao simples.', 'error');
          setSavingProgress(null);
          setSavingModalOpen(false);
          break;
        } catch (err: any) {
          console.error('saveSimpleClassification error:', err);
          if (savingCancelRef.current) break;
          setSavingProgress({ current: 0, total: expectedCount, message: `Erro na tentativa ${attempt}. Retentando em 2s...` });
          await new Promise(resolve => setTimeout(resolve, 2000));
          attempt++;
        }
      }
    } catch (err: any) {
      console.error('saveSimpleClassification error:', err);
      showConsolidationAlert('Erro ao salvar classificacao: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setSavingProgress(null);
      setSavingModalOpen(false);
      setConsolidationLoading(false);

      if (savingCancelRef.current) {
        showConsolidationAlert('Salvamento cancelado pelo usuario.', 'info');
      }
    }
  }, [clientId, positionsWithCodes, riskClassificationData, simplePositionNomenclature, showConsolidationAlert, fetchAllConsolidationData]);

  // Function to start editing an AI result
  const startEditAiResult = useCallback((item: any) => {
    const key = `${item.value}|||${item.type}|||${item.characters}`;
    setAiEditingItems(prev => ({
      ...prev,
      [key]: item.resultado || ''
    }));
  }, []);

  // Function to cancel editing an AI result
  const cancelEditAiResult = useCallback((item: any) => {
    const key = `${item.value}|||${item.type}|||${item.characters}`;
    setAiEditingItems(prev => {
      const newItems = { ...prev };
      delete newItems[key];
      return newItems;
    });
  }, []);

  // Function to save edited AI result
  const saveEditAiResult = useCallback(async (item: any) => {
    const key = `${item.value}|||${item.type}|||${item.characters}`;
    const editedValue = aiEditingItems[key];
    
    if (!editedValue || editedValue.trim() === '') {
      alert('O resultado não pode estar vazio');
      return;
    }

    // Convert to uppercase and trim
    const finalValue = editedValue.trim().toUpperCase();

    if (finalValue.length > item.characters) {
      alert(`O resultado não pode ter mais que ${item.characters} caracteres`);
      return;
    }

    try {
      // Save to database
      const requestData = {
        clientId,
        element_type: aiConversionActiveTab,
        element_value_raw: item.value,
        element_type_rs: item.type,
        element_value_return: finalValue,
        num_char: item.characters,
        result: finalValue
      };

      await axios.post(`${base}/api/profilegen-nomenclatura-ia`, {
        items: [requestData]
      }, {
        withCredentials: true
      });

      // Update local state
      setAiConversionData(prev => ({
        ...prev,
        [aiConversionActiveTab]: prev[aiConversionActiveTab].map((dataItem: any) => 
          dataItem.value === item.value && 
          dataItem.type === item.type && 
          dataItem.characters === item.characters
            ? { ...dataItem, resultado: finalValue }
            : dataItem
        )
      }));

      // Remove from editing items
      setAiEditingItems(prev => {
        const newItems = { ...prev };
        delete newItems[key];
        return newItems;
      });

    } catch (error) {
      console.error('Error saving edited AI result:', error);
      alert('Erro ao salvar o resultado editado');
    }
  }, [aiEditingItems, clientId, base, aiConversionActiveTab]);

  // Function to open AI conversion modal and prepare data
  const openAiConversionModal = useCallback(async () => {
    if (!clientId || !client) {
      console.warn('openAiConversionModal: Missing clientId or client data');
      return;
    }

    setAiModalLoading(true);
    setAiModalLoadingProgress(8);
    setAiModalLoadingMessage('Carregando dados de consolidação...');

    try {
      // Fetch consolidation data to get distinct values
      const items = await fetchAllConsolidationData();
      setAiModalLoadingProgress(45);
      setAiModalLoadingMessage('Preparando elementos para conversão...');

      if (items.length > 0) {
        // Fetch fresh client configuration to ensure pattern_* fields are up-to-date
        let freshClient = client;
        try {
          setAiModalLoadingProgress(58);
          setAiModalLoadingMessage('Atualizando parâmetros do cliente...');
          const clientListRes = await axios.get(`${base}/api/profilegen-clients`, {
            params: { q: clientId, searchColumn: 'clientId', _ts: Date.now() },
            withCredentials: true,
            headers: { 'Cache-Control': 'no-cache' }
          });
          if (clientListRes.status === 200 && Array.isArray(clientListRes.data?.items) && clientListRes.data.items.length > 0) {
            freshClient = clientListRes.data.items[0];
            console.debug('[DEBUG] openAiConversionModal: using fresh client config', freshClient._id || freshClient.clientId);
          }
        } catch (err) {
          console.warn('openAiConversionModal: could not refresh client config, using local client state', err);
        }

        // Helper function to prepare data for each element type (using freshClient)
        const prepareElementData = (elementType: 'area' | 'processo' | 'subprocesso' | 'atividade') => {
          const fieldMap = {
            area: 'area',
            processo: 'process',
            subprocesso: 'subprocess', 
            atividade: 'activity'
          };
          
          const fieldName = fieldMap[elementType];
          const distinctValues = Array.from(new Set(items.map((item: any) => item[fieldName]).filter(Boolean)));
          
          return distinctValues.map(value => {
            // Get distinct risk types for this value
            const hasRisk = items.some((item: any) => item[fieldName] === value && item.risk === true);
            const hasSimple = items.some((item: any) => item[fieldName] === value && item.risk === false);
            
            const rows = [];
            
            if (hasRisk) {
              const qtyField = `pattern_r_${elementType}_qtd`;
              const qty = freshClient[qtyField];
              const numericQty = (qty === null || qty === undefined) ? null : Number(qty);
              rows.push({
                value,
                type: 'Risco',
                characters: numericQty === null || Number.isNaN(numericQty) ? 'N/A' : numericQty,
                isInvalid: numericQty === null || Number.isNaN(numericQty),
                resultado: ''
              });
            }
            
            if (hasSimple) {
              const qtyField = `pattern_s_${elementType}_qtd`;
              const qty = freshClient[qtyField];
              const numericQty = (qty === null || qty === undefined) ? null : Number(qty);
              rows.push({
                value,
                type: 'Simples',
                characters: numericQty === null || Number.isNaN(numericQty) ? 'N/A' : numericQty,
                isInvalid: numericQty === null || Number.isNaN(numericQty),
                resultado: ''
              });
            }
            
            return rows;
          }).flat();
        };

        // Prepare data for all element types
        const conversionData = {
          area: prepareElementData('area'),
          processo: prepareElementData('processo'),
          subprocesso: prepareElementData('subprocesso'),
          atividade: prepareElementData('atividade')
        };

        setAiModalLoadingProgress(85);
        setAiModalLoadingMessage('Finalizando abertura do modal...');
        setAiConversionData(conversionData);
        setAiConversionMode('simple');
        setAiConversionActiveTab('area');
        setAiConversionPage(1);
        setAiConversionSearchQ('');
        setAiConversionSearchColumn('');
        setAiConversionModalOpen(true);
        setAiModalLoadingProgress(100);
        
        // Load saved AI results after modal is opened
        setTimeout(() => {
          loadSavedAiResults();
        }, 100);
      } else {
        showConsolidationAlert('Nenhum dado disponível para conversão com IA.', 'info');
      }
    } catch (err: any) {
      console.error('openAiConversionModal error:', err);
      showConsolidationAlert('Erro ao carregar dados para conversão com IA: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setAiModalLoading(false);
      setAiModalLoadingProgress(0);
      setAiModalLoadingMessage('');
    }
  }, [clientId, client, base, showConsolidationAlert, loadSavedAiResults]);

  // Open AI Conversion modal for composite N1..N4 values
  const openAiConversionModalCompostos = useCallback(async () => {
    if (!clientId || !client) {
      console.warn('openAiConversionModalCompostos: Missing clientId or client data');
      return;
    }

    setAiModalLoading(true);
    setAiModalLoadingProgress(8);
    setAiModalLoadingMessage('Carregando posições compostas...');

    try {
      // Ensure composite positions are loaded
      if (!Array.isArray(compositeUserPositions) || compositeUserPositions.length === 0) {
        await fetchCompositeUserPositions();
      }
      setAiModalLoadingProgress(55);
      setAiModalLoadingMessage('Preparando níveis N1..N4...');

      // Build distinct lists for n1..n4
      const items = Array.isArray(compositeUserPositions) ? compositeUserPositions : [];
      const distinct = (col: string): string[] => Array.from(new Set(items.map((it: any) => String(it[col] || '').trim()).filter(Boolean)));

      const prepareForN = (nKey: string) => {
        const values = distinct(nKey);
        return values.map((value: any) => {
          const qtyField = `pattern_c_${nKey}_qtd`;
          const qty = client ? client[qtyField] : null;
          const numericQty = (qty === null || qty === undefined) ? null : Number(qty);
          return {
            value,
            type: 'Composto',
            characters: numericQty === null || Number.isNaN(numericQty) ? 'N/A' : numericQty,
            isInvalid: numericQty === null || Number.isNaN(numericQty),
            resultado: ''
          };
        });
      };

      const conversionData = {
        n1: prepareForN('n1'),
        n2: prepareForN('n2'),
        n3: prepareForN('n3'),
        n4: prepareForN('n4')
      } as any;

      // Merge into aiConversionData base structure
      setAiConversionData(prev => ({ ...prev, ...conversionData }));
      setAiConversionMode('composite');
      setAiConversionActiveTab('n1');
      setAiConversionPage(1);
      setAiConversionSearchQ('');
      setAiConversionSearchColumn('');
      setAiModalLoadingProgress(90);
      setAiModalLoadingMessage('Abrindo modal de conversão...');
      setAiConversionModalOpen(true);
      setAiModalLoadingProgress(100);

      // Apply saved AI results if any
      setTimeout(() => { loadSavedAiResults(); }, 100);
    } catch (err: any) {
      console.error('openAiConversionModalCompostos error:', err);
      showConsolidationAlert('Erro ao preparar dados para conversão com IA dos compostos: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setAiModalLoading(false);
      setAiModalLoadingProgress(0);
      setAiModalLoadingMessage('');
    }
  }, [clientId, client, compositeUserPositions, fetchCompositeUserPositions, loadSavedAiResults, showConsolidationAlert]);

  // Open AI Conversion modal for simple positions (Posição/Cargo Simples) using profilegen-consolidation n1..n4
  const openAiConversionModalPosicoesSimples = useCallback(async () => {
    if (!clientId || !client) {
      console.warn('openAiConversionModalPosicoesSimples: Missing clientId or client data');
      return;
    }

    setAiModalLoading(true);
    setAiModalLoadingProgress(8);
    setAiModalLoadingMessage('Carregando consolidação para posições simples...');

    try {
      // Ensure consolidated rows are available
      const items = await fetchAllConsolidationData();
      setAiModalLoadingProgress(55);
      setAiModalLoadingMessage('Preparando níveis N1..N4...');
      if (items.length === 0) {
        showConsolidationAlert('Nenhum dado de consolidação encontrado para preparar conversão de posições simples.', 'info');
        return;
      }

      // Build distinct lists for n1..n4 from consolidation rows
      const distinct = (col: string): string[] => Array.from(new Set(items.map((it: any) => String(it[col] || '').trim()).filter(Boolean)));

      const prepareForN = (nKey: string) => {
        const values = distinct(nKey);
        return values.map((value: any) => {
          // Determine if any consolidation rows for this value have risk=true or risk=false
          const hasRisk = items.some((it: any) => String(it[nKey] || '').trim() === value && it.risk === true);
          const hasNonRisk = items.some((it: any) => String(it[nKey] || '').trim() === value && it.risk === false);

          const rows: any[] = [];
          if (hasRisk) {
            const qtyField = `pattern_r_${nKey}_qtd`;
            const qty = client ? client[qtyField] : null;
            const numericQty = (qty === null || qty === undefined) ? null : Number(qty);
            rows.push({
              value,
              type: 'Risco',
              characters: numericQty === null || Number.isNaN(numericQty) ? 'N/A' : numericQty,
              isInvalid: numericQty === null || Number.isNaN(numericQty),
              resultado: ''
            });
          }
          if (hasNonRisk) {
            const qtyField = `pattern_c_${nKey}_qtd`;
            const qty = client ? client[qtyField] : null;
            const numericQty = (qty === null || qty === undefined) ? null : Number(qty);
            rows.push({
              value,
              type: 'Simples',
              characters: numericQty === null || Number.isNaN(numericQty) ? 'N/A' : numericQty,
              isInvalid: numericQty === null || Number.isNaN(numericQty),
              resultado: ''
            });
          }

          return rows;
        }).flat();
      };

      const conversionData = {
        n1: prepareForN('n1'),
        n2: prepareForN('n2'),
        n3: prepareForN('n3'),
        n4: prepareForN('n4')
      } as any;

      // Merge into aiConversionData base structure
      setAiConversionData(prev => ({ ...prev, ...conversionData }));
      setAiConversionMode('composite');
      setAiConversionActiveTab('n1');
      setAiConversionPage(1);
      setAiConversionSearchQ('');
      setAiConversionSearchColumn('');
      setAiModalLoadingProgress(90);
      setAiModalLoadingMessage('Abrindo modal de conversão...');
      setAiConversionModalOpen(true);
      setAiModalLoadingProgress(100);

      // Apply saved AI results if any
      setTimeout(() => { loadSavedAiResults(); }, 100);
    } catch (err: any) {
      console.error('openAiConversionModalPosicoesSimples error:', err);
      showConsolidationAlert('Erro ao preparar dados para conversão com IA das posições simples: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setAiModalLoading(false);
      setAiModalLoadingProgress(0);
      setAiModalLoadingMessage('');
    }
  }, [clientId, client, base, loadSavedAiResults, showConsolidationAlert]);

  // Function to get status text for search functionality
  const getStatusText = useCallback((item: any) => {
    if (!item.resultado || item.resultado === '-') return 'sem resultado';
    if (item.isInvalid) return 'n/a';
    
    const resultLength = item.resultado.length;
    const maxLength = item.characters;
    
    if (resultLength <= maxLength) {
      return `ok ${resultLength}/${maxLength}`;
    } else {
      return `erro ${resultLength}/${maxLength}`;
    }
  }, []);

  const normalizeAiResult = useCallback((value: any): string => {
    return String(value || '').trim().toUpperCase();
  }, []);

  const isAiUnconvertedResult = useCallback((value: any): boolean => {
    const normalized = normalizeAiResult(value);
    return !normalized || normalized === '-';
  }, [normalizeAiResult]);

  const getAiResultCollisionKeys = useCallback((rows: any[]): Set<string> => {
    const byResult = new Map<string, any[]>();
    (rows || []).forEach((row: any) => {
      if (!row || row.isInvalid) return;
      const normalizedResult = normalizeAiResult(row.resultado);
      if (!normalizedResult || normalizedResult === '-') return;
      if (!byResult.has(normalizedResult)) byResult.set(normalizedResult, []);
      byResult.get(normalizedResult)!.push(row);
    });

    const collisionKeys = new Set<string>();

    byResult.forEach((groupRows) => {
      const distinctNames = new Set(groupRows.map((r: any) => String(r.value || '').trim().toUpperCase()).filter(Boolean));
      if (distinctNames.size <= 1) return;

      groupRows.forEach((row: any) => {
        const chars = row.characters;
        const parsed = typeof chars === 'string' ? parseInt(chars || '0', 10) : chars;
        const normalizedChars = Number.isNaN(parsed) ? chars : parsed;
        collisionKeys.add(`${String(row.value)}|||${String(row.type)}|||${normalizedChars}`);
      });
    });

    return collisionKeys;
  }, [normalizeAiResult]);

  const aiDuplicateSummary = useMemo(() => {
    const rows = aiConversionData?.[aiConversionActiveTab] || [];
    const byResult = new Map<string, any[]>();
    rows.forEach((row: any) => {
      if (!row || row.isInvalid) return;
      const normalizedResult = normalizeAiResult(row.resultado);
      if (!normalizedResult || normalizedResult === '-') return;
      if (!byResult.has(normalizedResult)) byResult.set(normalizedResult, []);
      byResult.get(normalizedResult)!.push(row);
    });

    const duplicateGroups: Array<{ result: string; rows: any[]; distinctNames: string[] }> = [];
    byResult.forEach((groupRows, result) => {
      const distinctNames = Array.from(new Set(groupRows.map((r: any) => String(r.value || '').trim()).filter(Boolean)));
      if (distinctNames.length > 1) {
        duplicateGroups.push({ result, rows: groupRows, distinctNames });
      }
    });

    const duplicateKeys = getAiResultCollisionKeys(rows);
    return {
      duplicateGroups,
      duplicateKeys,
      duplicateGroupCount: duplicateGroups.length,
      duplicateRowCount: duplicateKeys.size
    };
  }, [aiConversionData, aiConversionActiveTab, normalizeAiResult, getAiResultCollisionKeys]);

  const aiUnconvertedSummary = useMemo(() => {
    const rows = aiConversionData?.[aiConversionActiveTab] || [];
    const unconvertedRows = rows.filter((row: any) => !row?.isInvalid && isAiUnconvertedResult(row?.resultado));
    return {
      unconvertedRowCount: unconvertedRows.length
    };
  }, [aiConversionData, aiConversionActiveTab, isAiUnconvertedResult]);

  const sortAiRowsByName = useCallback((rows: any[]): any[] => {
    return [...(rows || [])].sort((a: any, b: any) => {
      const nameA = String(a?.value || '').trim();
      const nameB = String(b?.value || '').trim();
      const byName = nameA.localeCompare(nameB, 'pt-BR', { sensitivity: 'base' });
      if (byName !== 0) return byName;

      const typeA = String(a?.type || '').trim();
      const typeB = String(b?.type || '').trim();
      const byType = typeA.localeCompare(typeB, 'pt-BR', { sensitivity: 'base' });
      if (byType !== 0) return byType;

      const charA = Number(a?.characters || 0);
      const charB = Number(b?.characters || 0);
      return charA - charB;
    });
  }, []);

  const normalizeAlgorithmSource = useCallback((value: any): string => {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .trim();
  }, []);

  const buildAlgorithmicUniqueResult = useCallback((
    rawValue: any,
    maxChars: number,
    usedByResult: Map<string, Set<string>>,
    normalizedName: string
  ): string => {
    const safeMax = Math.max(1, Number(maxChars || 0));
    const normalized = normalizeAlgorithmSource(rawValue);
    const words = normalized.split(/[^A-Z0-9]+/).filter(Boolean);
    const compact = words.join('');

    const canUseCandidate = (candidate: string) => {
      const holders = usedByResult.get(candidate);
      if (!holders || holders.size === 0) return true;
      return holders.size === 1 && holders.has(normalizedName);
    };

    const reserveCandidate = (candidate: string) => {
      if (!usedByResult.has(candidate)) usedByResult.set(candidate, new Set<string>());
      usedByResult.get(candidate)!.add(normalizedName);
      return candidate;
    };

    const candidateSet = new Set<string>();
    const candidates: string[] = [];
    const pushCandidate = (value: string) => {
      const cleaned = String(value || '').replace(/[^A-Z0-9]/g, '').slice(0, safeMax);
      if (!cleaned || candidateSet.has(cleaned)) return;
      candidateSet.add(cleaned);
      candidates.push(cleaned);
    };

    if (compact) {
      pushCandidate(compact.slice(0, safeMax));
    }

    const initials = words.map(w => w.charAt(0)).join('');
    if (initials) {
      pushCandidate(initials);
      pushCandidate((initials + compact).slice(0, safeMax));
    }

    const maxWordLen = words.reduce((max, w) => Math.max(max, w.length), 0);
    for (let offset = 0; offset < maxWordLen; offset++) {
      let built = '';
      for (let w = 0; w < words.length; w++) {
        const ch = words[w].charAt(offset);
        if (ch) built += ch;
        if (built.length >= safeMax) break;
      }
      pushCandidate(built);
      if (built.length < safeMax) pushCandidate((built + compact).slice(0, safeMax));
    }

    if (compact) {
      for (let stride = 2; stride <= Math.min(6, compact.length); stride++) {
        let stepped = '';
        for (let i = 0; i < compact.length && stepped.length < safeMax; i += stride) {
          stepped += compact.charAt(i);
        }
        pushCandidate(stepped);
        pushCandidate((stepped + compact).slice(0, safeMax));
      }
    }

    for (const candidate of candidates) {
      if (canUseCandidate(candidate)) {
        return reserveCandidate(candidate);
      }
    }

    const baseCompact = compact || normalized.replace(/[^A-Z0-9]/g, '') || 'X';
    for (let i = 1; i <= 9999; i++) {
      const suffix = i.toString(36).toUpperCase();
      const prefixLen = Math.max(0, safeMax - suffix.length);
      const prefix = prefixLen > 0 ? baseCompact.slice(0, prefixLen) : '';
      const candidate = (prefix + suffix).slice(0, safeMax);
      if (!candidate) continue;
      if (canUseCandidate(candidate)) {
        return reserveCandidate(candidate);
      }
    }

    const fallback = (baseCompact.slice(0, Math.max(0, safeMax - 1)) + 'Z').slice(0, safeMax) || 'X';
    return reserveCandidate(fallback);
  }, [normalizeAlgorithmSource]);

  // Separated function for actual AI processing (after confirmation)
  const processAiConversionConfirmed = useCallback(async (forcedSelectedKeys?: string[]) => {
    try {
      setAiProcessing(true);
      setAiProcessingProgress(0);
      setAiProcessingStatus('Carregando parâmetros de IA...');

      // Get AI parameters
      const paramsResponse = await axios.get(`${base}/api/ia-params`, {
        params: { key: 'gerador-perfis' },
        withCredentials: true
      });

      if (paramsResponse.status !== 200 || !paramsResponse.data?.items?.length) {
        throw new Error('Parâmetros de IA não encontrados para "gerador-perfis"');
      }

      const aiParams = paramsResponse.data.items[0];
      const { model, temperature, maxTokens } = aiParams;

      setAiProcessingStatus('Preparando dados para processamento...');

  // Get current filtered data
  let currentData = aiConversionData[aiConversionActiveTab] || [];
      if (aiConversionSearchQ) {
        const searchTerm = aiConversionSearchQ.toLowerCase();
        currentData = currentData.filter(item => {
          if (aiConversionSearchColumn) {
            const fieldValue = aiConversionSearchColumn === 'elemento' ? item.value :
                             aiConversionSearchColumn === 'tipo' ? item.type :
                             aiConversionSearchColumn === 'caracteres' ? String(item.characters) :
                             aiConversionSearchColumn === 'resultado' ? item.resultado :
                             aiConversionSearchColumn === 'status' ? getStatusText(item) : '';
            return String(fieldValue).toLowerCase().includes(searchTerm);
          } else {
            return String(item.value).toLowerCase().includes(searchTerm) ||
                   String(item.type).toLowerCase().includes(searchTerm) ||
                   String(item.characters).toLowerCase().includes(searchTerm) ||
                   String(item.resultado).toLowerCase().includes(searchTerm) ||
                   getStatusText(item).toLowerCase().includes(searchTerm);
          }
        });
      }

      if (showOnlyAiDuplicates) {
        const duplicateKeys = getAiResultCollisionKeys(currentData);
        currentData = currentData.filter((item: any) => {
          const chars = item.characters;
          const parsed = typeof chars === 'string' ? parseInt(chars || '0', 10) : chars;
          const normalizedChars = Number.isNaN(parsed) ? chars : parsed;
          const key = `${String(item.value)}|||${String(item.type)}|||${normalizedChars}`;
          return duplicateKeys.has(key);
        });
      }

      if (showOnlyAiUnconverted) {
        currentData = currentData.filter((item: any) => isAiUnconvertedResult(item?.resultado));
      }

      currentData = sortAiRowsByName(currentData);

      // Decide which items to process: if user selected rows, process them; otherwise process current page
      const start = (aiConversionPage - 1) * aiConversionPageSize;
      const pageItems = currentData.slice(start, start + aiConversionPageSize);

      // Build a map of filteredData by key for quick lookup - normalize characters to numbers when possible
      const filteredMap: {[key: string]: any} = {};
      currentData.forEach(item => {
        const itemNum = typeof item.characters === 'string' ? parseInt(item.characters || '0') : (item.characters || 0);
        const normalizedChars = Number.isNaN(itemNum) ? String(item.characters) : itemNum;
        const key = `${String(item.value)}|||${String(item.type)}|||${normalizedChars}`;
        filteredMap[key] = item;
      });

  // Determine selected keys (use forced snapshot if provided)
  const selectedKeys = Array.isArray(forcedSelectedKeys) ? forcedSelectedKeys : Object.keys(aiConversionSelected).filter(k => aiConversionSelected[k]);

      let itemsToProcess = [] as any[];
      if (selectedKeys.length > 0) {
        // Use selections (only those present in current filtered data)
        itemsToProcess = selectedKeys.map(k => filteredMap[k]).filter(Boolean);
      } else {
        itemsToProcess = pageItems;
      }

      console.debug('[DEBUG] AI selection keys:', selectedKeys);
      console.debug('[DEBUG] itemsToProcess count:', itemsToProcess.length, 'pageItems count:', pageItems.length, 'filteredData count:', currentData.length);
      console.debug('[DEBUG] itemsToProcess keys:', itemsToProcess.map((it: any) => `${it.value}|||${it.type}|||${it.characters}`).slice(0,50));

      if (itemsToProcess.length === 0) {
        throw new Error('Nenhum item selecionado ou disponível para processar');
      }

      // Filter out invalid items (N/A characters)
      const validItems = itemsToProcess.filter(item => !item.isInvalid);
      if (validItems.length === 0) {
        throw new Error('Todos os itens selecionados/tabela têm configuração inválida (N/A)');
      }

      const selectedCollisionGroups = (() => {
        const byExistingResult = new Map<string, any[]>();
        validItems.forEach((item: any) => {
          const normalized = normalizeAiResult(item.resultado);
          if (!normalized || normalized === '-') return;
          if (!byExistingResult.has(normalized)) byExistingResult.set(normalized, []);
          byExistingResult.get(normalized)!.push(item);
        });

        const groups: Array<{ result: string; items: any[]; distinctNames: string[] }> = [];
        byExistingResult.forEach((groupItems, result) => {
          const distinctNames = Array.from(new Set(groupItems.map((it: any) => String(it.value || '').trim()).filter(Boolean)));
          if (distinctNames.length > 1) {
            groups.push({ result, items: groupItems, distinctNames });
          }
        });

        return groups;
      })();

      const dedupedGroupsMap = validItems.reduce((acc, item) => {
        const charCount = item.characters;
        const key = `${item.value}|||${charCount}`;

        if (!acc[key]) {
          acc[key] = {
            text: item.value,
            characters: charCount,
            items: [] as any[]
          };
        }
        acc[key].items.push(item);
        return acc;
      }, {} as {[key: string]: {text: string; characters: number; items: any[]}});

      const dedupedGroups: Array<{text: string; characters: number; items: any[]}> = Object.values(dedupedGroupsMap);
      const totalUniqueItems = dedupedGroups.length;
      const chunkSize = AI_CONVERSION_MAX_BATCH_SIZE;
      const totalBatches = Math.max(1, Math.ceil(totalUniqueItems / chunkSize));

      const parseAiResponse = (assistantText: string) => {
        let jsonText = String(assistantText || '').trim();
        const jsonMatch = jsonText.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
        if (jsonMatch) jsonText = jsonMatch[1];

        if (!jsonText.trim().startsWith('{')) {
          const jsonStart = jsonText.indexOf('{');
          const jsonEnd = jsonText.lastIndexOf('}');
          if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
            jsonText = jsonText.substring(jsonStart, jsonEnd + 1);
          }
        }

        const parsed = JSON.parse(jsonText);
        if (!parsed?.conversoes) {
          throw new Error('Formato de resposta inválido - propriedade "conversoes" não encontrada');
        }
        return parsed;
      };

      const buildPromptForBatch = (batchGroups: Array<{text: string; characters: number; items: any[]}>, batchIndex: number) => {
        const groupedByCharCount = batchGroups.reduce((acc, group) => {
          const charCount = group.characters;
          const key = `${group.text}|||${charCount}`;
          if (!acc[charCount]) acc[charCount] = {};
          acc[charCount][key] = group;
          return acc;
        }, {} as {[charCount: string]: {[key: string]: {text: string; characters: number; items: any[]}}});

        let prompt = `Você deve converter os textos abaixo para que tenham NO MÁXIMO o número de caracteres especificado, mas APROVEITE AO MÁXIMO o espaço disponível para melhor legibilidade. Regras obrigatórias:

1. Use APENAS LETRAS MAIÚSCULAS (A-Z)
2. NÃO use espaços, hífens, underscores ou qualquer separador
2.1. PROIBIDO QUALQUER ESPAÇO EM BRANCO: sem espaço no início, no meio ou no fim
3. O resultado deve ter NO MÁXIMO o número de caracteres especificado (NUNCA ultrapassar)
4. APROVEITE ao máximo o espaço disponível para melhor compreensão
5. Mantenha o significado original sempre que possível
6. Use abreviações inteligentes quando necessário
7. UNICIDADE OBRIGATÓRIA: se os textos originais forem diferentes, os resultados DEVEM ser diferentes dentro deste lote
8. EXCEÇÃO ÚNICA: o mesmo texto original pode repetir o mesmo resultado quando existir em tipos diferentes (ex.: Simples e Risco)

IMPORTANTE: Responda APENAS em formato JSON válido com a chave única incluindo o número de caracteres:
{
  "conversoes": {
    "TEXTO_ORIGINAL|||NUMERO_CARACTERES": {
      "resultado": "RESULTADO_OTIMIZADO",
      "caracteres_solicitados": NUMERO_CARACTERES,
      "caracteres_resultado": TAMANHO_DO_RESULTADO
    }
  }
}

LOTE ${batchIndex + 1}/${totalBatches} - TEXTOS PARA CONVERTER:
`;

        Object.entries(groupedByCharCount).forEach(([charCount, textGroups]) => {
          prompt += `\n=== TEXTOS COM MÁXIMO DE ${charCount} CARACTERES ===\n`;
          Object.values(textGroups).forEach((group: any) => {
            const uniqueKey = `${group.text}|||${charCount}`;
            prompt += `"${uniqueKey}" → converter "${group.text}" para máximo ${charCount} caracteres\n`;
          });
        });

        const batchNames = new Set(batchGroups.map((g) => String(g.text || '').trim()));
        const batchCollisionGroups = selectedCollisionGroups.filter((group) =>
          group.distinctNames.some((name) => batchNames.has(name))
        );

        if (batchCollisionGroups.length > 0) {
          prompt += `\n\nATENÇÃO: houve colisões em conversões anteriores para itens deste lote. REPROCESSE garantindo resultados DISTINTOS para textos diferentes:\n`;
          batchCollisionGroups.forEach((group, idx) => {
            prompt += `\nCOLISÃO ${idx + 1} (resultado repetido: "${group.result}"):\n`;
            group.distinctNames.forEach((name) => {
              if (batchNames.has(name)) {
                prompt += `- "${name}" deve receber resultado exclusivo\n`;
              }
            });
          });
        }

        prompt += `\n\nLEMBRE-SE:
- NUNCA ultrapasse o limite de caracteres
- APROVEITE ao máximo o espaço disponível
- NUNCA retorne resultado com espaços (ex.: "TIG RE", " TIGRE", "TIGRE ")
- Se o texto tiver múltiplas palavras, compacte sem espaço (ex.: "TIGRE PERU" -> "TIGPER" ou similar)
- NUNCA retorne o mesmo "resultado" para textos diferentes no mesmo lote
- Use a chave única "TEXTO|||CARACTERES" exatamente como fornecida
- Inclua todas as informações solicitadas no JSON de resposta`;

        return prompt;
      };

      const currentTabData = [...(aiConversionData[aiConversionActiveTab] || [])];
      let totalUpdatedRows = 0;
      let processedUniqueItems = 0;

      const updateProgressByVolume = () => {
        const pct = totalUniqueItems > 0
          ? Math.min(99, 10 + Math.round((processedUniqueItems / totalUniqueItems) * 85))
          : 99;
        setAiProcessingProgress(pct);
      };

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const startIdx = batchIndex * chunkSize;
        const endIdx = Math.min(startIdx + chunkSize, totalUniqueItems);
        const batchGroups = dedupedGroups.slice(startIdx, endIdx);

        setAiProcessingStatus(`Lote ${batchIndex + 1}/${totalBatches}: construindo prompt...`);
        const prompt = buildPromptForBatch(batchGroups, batchIndex);

        const requestPayload = {
          model,
          temperature: parseFloat(temperature) || 0.7,
          max_tokens: parseInt(maxTokens) || 2000,
          messages: [{ role: 'user', content: prompt }]
        };

        setAiProcessingStatus(`Lote ${batchIndex + 1}/${totalBatches}: enviando para IA...`);
        updateProgressByVolume();

        const aiResponse = await axios.post(`${base}/api/ai/chat`, requestPayload, { withCredentials: true });
        if (aiResponse.status !== 200 || !aiResponse.data?.assistantText) {
          throw new Error(`Erro na resposta da IA no lote ${batchIndex + 1}`);
        }

        const aiResult = parseAiResponse(aiResponse.data.assistantText);

        setAiLogData({
          timestamp: new Date().toISOString(),
          deduplication: {
            totalItemsInPage: validItems.length,
            uniqueTexts: totalUniqueItems,
            duplicatesRemoved: validItems.length - totalUniqueItems
          },
          request: {
            url: `${base}/api/ai/chat`,
            payload: requestPayload,
            prompt
          },
          response: {
            status: aiResponse.status,
            data: aiResponse.data,
            assistantText: aiResponse.data.assistantText
          }
        });

        setAiProcessingStatus(`Lote ${batchIndex + 1}/${totalBatches}: aplicando resultados...`);

        const batchKeySet = new Set(batchGroups.map((g) => `${String(g.text)}|||${String(g.characters)}`));
        const batchUpdatedItems: any[] = [];

        Object.entries(aiResult.conversoes).forEach(([uniqueKey, conversionData]) => {
          if (!batchKeySet.has(uniqueKey)) return;

          const [originalText, charCountStr] = uniqueKey.split('|||');
          const charCount = parseInt(charCountStr || '0', 10);
          const rawResult = typeof conversionData === 'string'
            ? conversionData
            : (conversionData as any)?.resultado || String(conversionData);

          const adjustedResult = String(rawResult || '').substring(0, Math.max(0, charCount || 0));

          currentTabData.forEach((row: any, idx: number) => {
            const sameText = String(row.value || '') === String(originalText || '');
            const rowNum = typeof row.characters === 'string' ? parseInt(row.characters || '0', 10) : Number(row.characters || 0);
            const sameCharCount = !Number.isNaN(rowNum) && rowNum === charCount;
            if (sameText && sameCharCount) {
              currentTabData[idx] = { ...row, resultado: adjustedResult };
              batchUpdatedItems.push(currentTabData[idx]);
            }
          });
        });

        if (batchUpdatedItems.length > 0) {
          setAiConversionData(prev => ({
            ...prev,
            [aiConversionActiveTab]: [...currentTabData]
          }));

          try {
            await axios.post(`${base}/api/profilegen-nomenclatura-ia`, {
              items: batchUpdatedItems.map(item => ({
                clientId,
                element_type: aiConversionActiveTab,
                element_value_raw: item.value,
                element_type_rs: item.type,
                element_value_return: item.resultado,
                num_char: item.characters,
                result: item.resultado
              }))
            }, { withCredentials: true });
          } catch (saveError) {
            console.error(`Erro ao salvar lote ${batchIndex + 1} no banco:`, saveError);
          }
        }

        totalUpdatedRows += batchUpdatedItems.length;
        processedUniqueItems += batchGroups.length;
        setAiProcessingStatus(`Lote ${batchIndex + 1}/${totalBatches}: concluído (${processedUniqueItems}/${totalUniqueItems})`);
        updateProgressByVolume();
      }

      const remainingDuplicateCount = getAiResultCollisionKeys(currentTabData).size;

      setAiProcessingProgress(100);
      setAiProcessingStatus('Concluído!');

      showConsolidationAlert(
        remainingDuplicateCount > 0
          ? `Processamento com IA concluído! ${totalUpdatedRows} elementos convertidos. Ainda restam ${remainingDuplicateCount} registros em duplicidade de resultado; selecione-os e processe novamente.`
          : `Processamento com IA concluído! ${totalUpdatedRows} elementos convertidos em ${totalBatches} lote(s) de até ${AI_CONVERSION_MAX_BATCH_SIZE} itens.`,
        'success'
      );

    } catch (err: any) {
      console.error('processAiConversion error:', err);
      showConsolidationAlert(
        'Erro durante o processamento com IA: ' + (err.response?.data?.message || err.message),
        'error'
      );
    } finally {
      setAiProcessing(false);
      setAiProcessingProgress(0);
      setAiProcessingStatus('');
    }
  }, [clientId, aiConversionData, aiConversionActiveTab, aiConversionPage, aiConversionPageSize, 
    aiConversionSearchQ, aiConversionSearchColumn, base, showConsolidationAlert, getStatusText, aiConversionSelected, normalizeAiResult, getAiResultCollisionKeys, showOnlyAiDuplicates, showOnlyAiUnconverted, isAiUnconvertedResult, AI_CONVERSION_MAX_BATCH_SIZE, sortAiRowsByName]);

  // Function to process AI conversion for current page data
  const processAiConversion = useCallback(async () => {
    if (!clientId || aiProcessing) return;

    try {
      // Check for existing results before processing
      let checkCurrentData = aiConversionData[aiConversionActiveTab] || [];
      if (aiConversionSearchQ) {
        const searchTerm = aiConversionSearchQ.toLowerCase();
        checkCurrentData = checkCurrentData.filter(item => {
          if (aiConversionSearchColumn) {
            const fieldValue = aiConversionSearchColumn === 'elemento' ? item.value :
                             aiConversionSearchColumn === 'tipo' ? item.type :
                             aiConversionSearchColumn === 'caracteres' ? String(item.characters) :
                             aiConversionSearchColumn === 'resultado' ? item.resultado :
                             aiConversionSearchColumn === 'status' ? getStatusText(item) : '';
            return String(fieldValue).toLowerCase().includes(searchTerm);
          } else {
            return String(item.value).toLowerCase().includes(searchTerm) ||
                   String(item.type).toLowerCase().includes(searchTerm) ||
                   String(item.characters).toLowerCase().includes(searchTerm) ||
                   String(item.resultado).toLowerCase().includes(searchTerm) ||
                   getStatusText(item).toLowerCase().includes(searchTerm);
          }
        });
      }

      if (showOnlyAiDuplicates) {
        const duplicateKeys = getAiResultCollisionKeys(checkCurrentData);
        checkCurrentData = checkCurrentData.filter((item: any) => {
          const chars = item.characters;
          const parsed = typeof chars === 'string' ? parseInt(chars || '0', 10) : chars;
          const normalizedChars = Number.isNaN(parsed) ? chars : parsed;
          const key = `${String(item.value)}|||${String(item.type)}|||${normalizedChars}`;
          return duplicateKeys.has(key);
        });
      }

      if (showOnlyAiUnconverted) {
        checkCurrentData = checkCurrentData.filter((item: any) => isAiUnconvertedResult(item?.resultado));
      }

      checkCurrentData = sortAiRowsByName(checkCurrentData);

      // Get current page items
      const checkStart = (aiConversionPage - 1) * aiConversionPageSize;
      const checkPageItems = checkCurrentData.slice(checkStart, checkStart + aiConversionPageSize);

      // Check for existing results
      // Determine snapshot of selected keys to use in confirm and processing
      const selectedSnapshot = Object.keys(aiConversionSelected).filter(k => aiConversionSelected[k]);
      const itemsToCheck = selectedSnapshot.length > 0 ? selectedSnapshot.map(k => {
        const parts = k.split('|||');
        return { value: parts[0], type: parts[1], characters: parseInt(parts[2] || '0') };
      }).filter(Boolean) : checkPageItems;

      const itemsWithResults = itemsToCheck.filter((item: any) => {
        // if item is from snapshot it may not have resultado in the checked collection; check against current data
        if (item && item.value && item.characters != null) {
          // find in current tab data
          const found = (aiConversionData[aiConversionActiveTab] || []).find(it => it.value === item.value && it.characters === item.characters && it.type === item.type);
          return found && found.resultado && found.resultado !== '-';
        }
        return false;
      });

      if (itemsWithResults.length > 0) {
        const confirmMessage = `${itemsWithResults.length} elementos já possuem conversão feita. Executar novamente sobreescreverá os resultados gravados. Deseja continuar?`;
        showAiConfirm(confirmMessage, () => {
          // Continue with processing - call the actual processing function with the snapshot
          processAiConversionConfirmed(selectedSnapshot.length > 0 ? selectedSnapshot : undefined);
        });
        return;
      }

      // No existing results, continue processing
      await processAiConversionConfirmed();
    } catch (err: any) {
      console.error('processAiConversion error:', err);
      setAiProcessingStatus(`Erro: ${err.message || 'Erro desconhecido'}`);
      setAiProcessing(false);
    }
  }, [clientId, aiProcessing, aiConversionData, aiConversionActiveTab, aiConversionSearchQ, aiConversionSearchColumn, aiConversionPage, aiConversionPageSize, getStatusText, showAiConfirm, processAiConversionConfirmed, aiConversionSelected, showOnlyAiDuplicates, showOnlyAiUnconverted, getAiResultCollisionKeys, isAiUnconvertedResult, sortAiRowsByName]);

  const processAlgorithmicConversionConfirmed = useCallback(async (forcedSelectedKeys?: string[]) => {
    try {
      setAiProcessing(true);
      setAiProcessingProgress(0);
      setAiProcessingStatus('Preparando conversão algorítmica...');

      let currentData = aiConversionData[aiConversionActiveTab] || [];
      if (aiConversionSearchQ) {
        const searchTerm = aiConversionSearchQ.toLowerCase();
        currentData = currentData.filter(item => {
          if (aiConversionSearchColumn) {
            const fieldValue = aiConversionSearchColumn === 'elemento' ? item.value :
                             aiConversionSearchColumn === 'tipo' ? item.type :
                             aiConversionSearchColumn === 'caracteres' ? String(item.characters) :
                             aiConversionSearchColumn === 'resultado' ? item.resultado :
                             aiConversionSearchColumn === 'status' ? getStatusText(item) : '';
            return String(fieldValue).toLowerCase().includes(searchTerm);
          }

          return String(item.value).toLowerCase().includes(searchTerm) ||
                 String(item.type).toLowerCase().includes(searchTerm) ||
                 String(item.characters).toLowerCase().includes(searchTerm) ||
                 String(item.resultado).toLowerCase().includes(searchTerm) ||
                 getStatusText(item).toLowerCase().includes(searchTerm);
        });
      }

      if (showOnlyAiDuplicates) {
        const duplicateKeys = getAiResultCollisionKeys(currentData);
        currentData = currentData.filter((item: any) => {
          const chars = item.characters;
          const parsed = typeof chars === 'string' ? parseInt(chars || '0', 10) : chars;
          const normalizedChars = Number.isNaN(parsed) ? chars : parsed;
          const key = `${String(item.value)}|||${String(item.type)}|||${normalizedChars}`;
          return duplicateKeys.has(key);
        });
      }

      if (showOnlyAiUnconverted) {
        currentData = currentData.filter((item: any) => isAiUnconvertedResult(item?.resultado));
      }

      currentData = sortAiRowsByName(currentData);

      const start = (aiConversionPage - 1) * aiConversionPageSize;
      const pageItems = currentData.slice(start, start + aiConversionPageSize);

      const filteredMap: {[key: string]: any} = {};
      currentData.forEach(item => {
        const itemNum = typeof item.characters === 'string' ? parseInt(item.characters || '0', 10) : (item.characters || 0);
        const normalizedChars = Number.isNaN(itemNum) ? String(item.characters) : itemNum;
        const key = `${String(item.value)}|||${String(item.type)}|||${normalizedChars}`;
        filteredMap[key] = item;
      });

      const selectedKeys = Array.isArray(forcedSelectedKeys) ? forcedSelectedKeys : Object.keys(aiConversionSelected).filter(k => aiConversionSelected[k]);
      let itemsToProcess = [] as any[];
      if (selectedKeys.length > 0) {
        itemsToProcess = selectedKeys.map(k => filteredMap[k]).filter(Boolean);
      } else {
        itemsToProcess = pageItems;
      }

      const processableItems = sortAiRowsByName(itemsToProcess.filter(Boolean));
      if (processableItems.length === 0) {
        throw new Error('Nenhum item disponível para processar com algoritmo.');
      }

      const resolveAlgorithmicMaxChars = (row: any) => {
        const directRaw = typeof row.characters === 'string' ? parseInt(row.characters || '0', 10) : Number(row.characters || 0);
        if (Number.isFinite(directRaw) && directRaw > 0) {
          return { maxChars: directRaw, usedFallback: false };
        }

        const existingResult = normalizeAiResult(row?.resultado);
        if (existingResult && existingResult !== '-') {
          return { maxChars: Math.max(1, existingResult.length), usedFallback: true };
        }

        return { maxChars: ALGORITHMIC_DEFAULT_MAX_CHARS, usedFallback: true };
      };

      const rowsWithConfig = processableItems.map((row: any) => {
        const cfg = resolveAlgorithmicMaxChars(row);
        return {
          ...row,
          __algoMaxChars: cfg.maxChars,
          __algoUsedFallback: cfg.usedFallback
        };
      });

      const fallbackCount = rowsWithConfig.filter((row: any) => row.__algoUsedFallback).length;

      const makeKey = (item: any) => {
        const itemNum = typeof item.characters === 'string' ? parseInt(item.characters || '0', 10) : (item.characters || 0);
        const normalizedChars = Number.isNaN(itemNum) ? String(item.characters) : itemNum;
        return `${String(item.value)}|||${String(item.type)}|||${normalizedChars}`;
      };

      const itemsToProcessKeys = new Set(rowsWithConfig.map((item: any) => makeKey(item)));
      const currentTabData = [...(aiConversionData[aiConversionActiveTab] || [])];
      const usedByResult = new Map<string, Set<string>>();

      currentTabData.forEach((row: any) => {
        if (!row) return;
        const rowKey = makeKey(row);
        if (itemsToProcessKeys.has(rowKey)) return;

        const result = normalizeAiResult(row.resultado);
        if (!result || result === '-') return;
        const normalizedName = normalizeAlgorithmSource(row.value).replace(/[^A-Z0-9]/g, '');
        if (!normalizedName) return;

        if (!usedByResult.has(result)) usedByResult.set(result, new Set<string>());
        usedByResult.get(result)!.add(normalizedName);
      });

      const allUpdatedItems: any[] = [];
      const totalItems = rowsWithConfig.length;
      let processed = 0;

      for (const row of rowsWithConfig) {
        const maxChars = Number(row.__algoMaxChars || ALGORITHMIC_DEFAULT_MAX_CHARS);
        const normalizedName = normalizeAlgorithmSource(row.value).replace(/[^A-Z0-9]/g, '') || 'X';
        const generated = buildAlgorithmicUniqueResult(row.value, maxChars, usedByResult, normalizedName);
        const rowKey = makeKey(row);

        currentTabData.forEach((item: any, index: number) => {
          if (makeKey(item) !== rowKey) return;
          if (!itemsToProcessKeys.has(rowKey)) return;
          currentTabData[index] = { ...item, resultado: generated };
          allUpdatedItems.push(currentTabData[index]);
        });

        processed += 1;
        const pct = Math.min(95, Math.round((processed / totalItems) * 95));
        setAiProcessingProgress(pct);
        setAiProcessingStatus(`Convertendo sem IA... ${processed}/${totalItems}`);

        if (processed % 20 === 0 || processed === totalItems) {
          setAiConversionData(prev => ({
            ...prev,
            [aiConversionActiveTab]: [...currentTabData]
          }));
        }
      }

      setAiProcessingStatus('Salvando resultados no banco de dados...');
      setAiProcessingProgress(97);

      const dataToSave = allUpdatedItems.map(item => ({
        clientId,
        element_type: aiConversionActiveTab,
        element_value_raw: item.value,
        element_type_rs: item.type,
        element_value_return: item.resultado,
        num_char: (() => {
          const raw = typeof item.characters === 'string' ? parseInt(item.characters || '0', 10) : Number(item.characters || 0);
          if (Number.isFinite(raw) && raw > 0) return raw;
          const existing = normalizeAiResult(item.resultado);
          return existing && existing !== '-' ? Math.max(1, existing.length) : ALGORITHMIC_DEFAULT_MAX_CHARS;
        })(),
        result: item.resultado
      }));

      if (dataToSave.length > 0) {
        await axios.post(`${base}/api/profilegen-nomenclatura-ia`, { items: dataToSave }, { withCredentials: true });
      }

      setAiConversionData(prev => ({
        ...prev,
        [aiConversionActiveTab]: [...currentTabData]
      }));

      setAiProcessingProgress(100);
      setAiProcessingStatus('Concluído!');

      showConsolidationAlert(
        `Conversão sem IA concluída! ${rowsWithConfig.length} elemento(s) processado(s) com algoritmo de unicidade.${fallbackCount > 0 ? ` ${fallbackCount} item(ns) usaram fallback de caracteres.` : ''} O resultado pode exigir ajustes manuais.`,
        'success'
      );
    } catch (err: any) {
      console.error('processAlgorithmicConversionConfirmed error:', err);
      showConsolidationAlert('Erro durante conversão sem IA: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setAiProcessing(false);
      setAiProcessingProgress(0);
      setAiProcessingStatus('');
    }
  }, [
    aiConversionData,
    aiConversionActiveTab,
    aiConversionSearchQ,
    aiConversionSearchColumn,
    aiConversionPage,
    aiConversionPageSize,
    aiConversionSelected,
    showOnlyAiDuplicates,
    showOnlyAiUnconverted,
    getStatusText,
    getAiResultCollisionKeys,
    isAiUnconvertedResult,
    sortAiRowsByName,
    normalizeAiResult,
    normalizeAlgorithmSource,
    buildAlgorithmicUniqueResult,
    ALGORITHMIC_DEFAULT_MAX_CHARS,
    clientId,
    base,
    showConsolidationAlert
  ]);

  const processAlgorithmicConversion = useCallback(async () => {
    if (!clientId || aiProcessing) return;

    const selectedSnapshot = Object.keys(aiConversionSelected).filter(k => aiConversionSelected[k]);
    const scopeMessage = selectedSnapshot.length > 0
      ? `${selectedSnapshot.length} elemento(s) selecionado(s)`
      : 'elementos da página atual (respeitando os filtros ativos)';

    showAiConfirm(
      `Esta conversão será feita SEM IA, usando algoritmo local com limite de caracteres e validação de colisão. O resultado pode não ser o ideal semântico. Deseja continuar para ${scopeMessage}?`,
      () => {
        processAlgorithmicConversionConfirmed(selectedSnapshot.length > 0 ? selectedSnapshot : undefined);
      }
    );
  }, [clientId, aiProcessing, aiConversionSelected, showAiConfirm, processAlgorithmicConversionConfirmed]);

  // Function to clear AI conversions for the current client
  const clearAiConversions = useCallback(async () => {
    if (!clientId) return;

    setClearAiConversionsLoading(true);
    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      // Determine which element types to delete based on current mode
      let keysToClear: string[] = [];
      if (aiConversionMode === 'composite') {
        keysToClear = ['n1', 'n2', 'n3', 'n4'];
      } else {
        keysToClear = ['area', 'processo', 'subprocesso', 'atividade'];
      }

      // Call backend with optional elementTypes query param so server deletes only matching types
      await axios.delete(`${base}/api/profilegen-nomenclatura-ia/${clientId}`, {
        withCredentials: true,
        params: { elementTypes: keysToClear.join(',') }
      });

      // Clear only the relevant keys in local state
      setAiConversionData(prev => {
        const copy: any = { ...(prev || {}) };
        keysToClear.forEach(k => { copy[k] = []; });
        return copy;
      });

      // Reset pagination
      setAiConversionPage(1);

      showConsolidationAlert('Conversões de IA limpas com sucesso!', 'success');
    } catch (error: any) {
      console.error('Erro ao limpar conversões de IA:', error);
      showConsolidationAlert('Erro ao limpar conversões de IA. Verifique o console.', 'error');
    } finally {
      setClearAiConversionsLoading(false);
    }
  }, [clientId, showConsolidationAlert, aiConversionMode]);

  // Function to generate nomenclature using IA converted values and client patterns
  const generateNomenclature = useCallback(async () => {
    if (!clientId || !client || nomenclatureMappingData.length === 0) {
      showConsolidationAlert('Dados insuficientes para gerar nomenclatura.', 'error');
      return;
    }

    try {
      // Load saved IA conversions and get fresh data
      const freshAiConversions = await loadSavedAiResults();

      // Use fresh data instead of state (which might not be updated yet)
      const aiConversions = freshAiConversions || {};

      // Fetch latest client configuration from server to ensure we use the most recent pattern parameters
      // Strategy: Use listing endpoint to resolve the MongoDB _id from clientId, then fetch by _id with cache-buster
      let currentClient = client;
      try {
        if (clientId) {
          const listRes = await axios.get(`${base}/api/profilegen-clients`, {
            params: { q: clientId, searchColumn: 'clientId', _ts: Date.now() },
            withCredentials: true,
            headers: { 'Cache-Control': 'no-cache' }
          });

          if (listRes && listRes.data && Array.isArray(listRes.data.items) && listRes.data.items.length > 0) {
            // Use the first item returned by the listing endpoint as the latest client record
            const clientRecord = listRes.data.items[0];
            currentClient = clientRecord;
            console.log('[DEBUG] Using client record returned by list endpoint for nomenclature generation');
          } else {
            console.warn('[WARN] No client record found for clientId when attempting to refresh client config');
          }
        }
      } catch (err) {
        console.warn('Could not fetch latest client configuration, using local client state', err);
      }

      console.log('[DEBUG] Using fresh AI conversions for nomenclature generation:', aiConversions);

      // Build nomenclature for each item
      const updatedData = nomenclatureMappingData.map(item => {
        const codigo = item.codigo;
        if (!codigo || codigo === '-') {
          return { ...item, nomenclatura: '-' };
        }

        // Determine if it's a risk action
        const isRiskAction = isRiskClassificationEnabled && riskClassificationData[codigo];
        
        // Determine if it's a master profile
        const isMasterProfile = isMasterDerivedClassificationEnabled && masterDerivedClassificationData[codigo];

        // Select pattern type based on risk classification
        const patternPrefix = isRiskAction ? 'pattern_r_' : 'pattern_s_';

        // Get prefix based on risk and master status from the freshly fetched currentClient
        let prefix = '';
        if (isRiskAction) {
          prefix = isMasterProfile ? 
            (currentClient[`pattern_r_prefixo_risco_mestre`] || '') :
            (currentClient[`pattern_r_prefixo_risco`] || '');
        } else {
          prefix = isMasterProfile ? 
            (currentClient[`pattern_s_prefixo_mestre`] || '') :
            (currentClient[`pattern_s_prefixo_simples`] || '');
        }

        // Get IA converted values for this item using specific parameters
        const aiResultsLookup = aiConversions || {};
        const typeRs = isRiskAction ? 'Risco' : 'Simples';
        
        // Helper function to get IA converted value for an element
        const getAiConvertedValue = (elementType: string, rawValue: string, fallback: string) => {
          if (!rawValue) return fallback;
          
          // Get the pattern configuration for this element type
            const qtdField = `${patternPrefix}${elementType}_qtd`;
            const numChar = currentClient[qtdField] || 0;
          
          if (numChar > 0) {
            // Create lookup key for IA result
            const key = `${elementType}_${rawValue}_${typeRs}_${numChar}`;
            const aiValue = aiResultsLookup[key];
            if (aiValue) {
              console.log(`[DEBUG] Found IA value for ${key}: ${aiValue}`);
              return aiValue;
            } else {
              console.log(`[DEBUG] No IA value found for ${key}, using fallback: ${fallback}`);
            }
          }

          return normalizeRawFallbackValue(fallback, Number(numChar) || undefined);
        };
        
        const area = getAiConvertedValue('area', item.area, item.area || '');
        const processo = getAiConvertedValue('processo', item.processo, item.processo || '');
        const subprocesso = getAiConvertedValue('subprocesso', item.subprocesso, item.subprocesso || '');
        const atividade = getAiConvertedValue('atividade', item.atividade, item.atividade || '');

        console.log(`[DEBUG] Code ${codigo}, isRisk: ${isRiskAction}, typeRs: ${typeRs}, final values:`, { area, processo, subprocesso, atividade });

        // Build ordered elements array with their ordering and separators
        const elements: Array<{ type: string; value: string }> = [
          { type: 'prefixo', value: prefix },
          { type: 'area', value: area },
          { type: 'processo', value: processo },
          { type: 'subprocesso', value: subprocesso },
          { type: 'atividade', value: atividade }
        ];

        // Add "Ação" element for risk profiles if configured (use latest client config)
        if (isRiskAction && currentClient[`pattern_r_acao_ord`] != null) {
          let acaoValue = codigo; // Use the code as the action value
          
          // Apply character limit for action element if specified
          const acaoQtd = currentClient[`pattern_r_acao_qtd`] || 0;
          if (acaoQtd > 0 && acaoValue.length > acaoQtd) {
            // Cut from the end to the beginning (keep the first characters)
            acaoValue = acaoValue.substring(0, acaoQtd);
          }
          
          elements.push({
            type: 'acao',
            value: acaoValue
          });
        }
        const nomenclature = buildNomenclatureByPatternOrder(currentClient, patternPrefix, elements);

  // Check if nomenclature exceeds configured max characters
  const resolvedMaxForClient = Number(patternNameMaxChar || client?.pattern_name_max_char || 30);
  const exceedsLimit = nomenclature.length > resolvedMaxForClient;

        return { 
          ...item, 
          nomenclatura: nomenclature || '-',
          nomenclaturaExceedsLimit: exceedsLimit
        };
      });

      // Update the state with generated nomenclature
      setNomenclatureMappingData(updatedData);

      showConsolidationAlert(
        `Nomenclatura gerada com sucesso para ${updatedData.filter(item => item.nomenclatura && item.nomenclatura !== '-').length} itens!`,
        'success'
      );

    } catch (error: any) {
      console.error('Error generating nomenclature:', error);
      showConsolidationAlert('Erro ao gerar nomenclatura. Verifique o console.', 'error');
    }
  }, [clientId, client, nomenclatureMappingData, isRiskClassificationEnabled, riskClassificationData, 
    isMasterDerivedClassificationEnabled, masterDerivedClassificationData, 
    loadSavedAiResults, showConsolidationAlert, base, patternNameMaxChar, buildNomenclatureByPatternOrder, normalizeRawFallbackValue]);

  // Function to handle nomenclature table sorting
  const handleNomenclatureSort = (column: string, direction?: 'asc' | 'desc') => {
    if (direction) {
      setNomenclatureSortColumn(column);
      setNomenclatureSortDirection(direction);
    } else if (nomenclatureSortColumn === column) {
      setNomenclatureSortDirection(nomenclatureSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setNomenclatureSortColumn(column);
      setNomenclatureSortDirection('asc');
    }
  };

  // Alphanumeric comparison function for better code sorting
  const alphanumericCompare = (a: string, b: string): number => {
    // Convert to uppercase for case-insensitive comparison
    const aUpper = a.toUpperCase();
    const bUpper = b.toUpperCase();
    
    // Split into chunks of letters and numbers
    const chunksA = aUpper.match(/(\d+|\D+)/g) || [];
    const chunksB = bUpper.match(/(\d+|\D+)/g) || [];
    
    const maxLength = Math.max(chunksA.length, chunksB.length);
    
    for (let i = 0; i < maxLength; i++) {
      const chunkA = chunksA[i] || '';
      const chunkB = chunksB[i] || '';
      
      // If both chunks are numbers, compare numerically
      if (/^\d+$/.test(chunkA) && /^\d+$/.test(chunkB)) {
        const numA = parseInt(chunkA, 10);
        const numB = parseInt(chunkB, 10);
        if (numA !== numB) {
          return numA - numB;
        }
      } else {
        // Compare as strings
        const comparison = chunkA.localeCompare(chunkB);
        if (comparison !== 0) {
          return comparison;
        }
      }
    }
    
    return 0;
  };

  // Function to filter and sort nomenclature data
  const _getFilteredAndSortedNomenclatureData = () => {
    let filtered = [...nomenclatureMappingData];
    
    // Remove duplicates based on the combination of displayed columns
    const uniqueMap = new Map();
    filtered = filtered.filter(item => {
      const key = `${item.area || ''}-${item.processo || ''}-${item.subprocesso || ''}-${item.atividade || ''}-${item.codigo || ''}`;
      if (uniqueMap.has(key)) {
        return false; // Skip duplicate
      }
      uniqueMap.set(key, true);
      return true;
    });
    
    // Apply search filter
    const q = (nomenclatureSearchQ || '').toLowerCase().trim();
    if (q) {
      const col = nomenclatureSearchColumn;
      if (col) {
        filtered = filtered.filter(item => {
          if (col === 'acaoRisco') {
            // Special handling for risk action column
            const isRisk = isRiskClassificationEnabled && riskClassificationData[item.codigo];
            const riskText = isRisk ? 's' : '-';
            return riskText.toLowerCase().includes(q);
          } else if (col === 'nomenclatura') {
            // Handle nomenclatura column
            const val = String(item.nomenclatura || '').toLowerCase();
            return val.includes(q);
          } else {
            const val = String(item[col] || '').toLowerCase();
            return val.includes(q);
          }
        });
      } else {
        filtered = filtered.filter(item => {
          // Include risk classification and nomenclatura in general search
          const isRisk = isRiskClassificationEnabled && riskClassificationData[item.codigo];
          const riskText = isRisk ? 's' : '-';
          const nomenclaturaText = item.nomenclatura || '';
          const searchValues = Object.values(item).concat([riskText, nomenclaturaText]);
          
          return searchValues.some(val => 
            String(val || '').toLowerCase().includes(q)
          );
        });
      }
    }
    
    // Apply sorting
    if (nomenclatureSortColumn) {
      // User-selected sorting
      filtered.sort((a, b) => {
        const aVal = String(a[nomenclatureSortColumn] || '');
        const bVal = String(b[nomenclatureSortColumn] || '');
        
        if (nomenclatureSortDirection === 'asc') {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      });
    } else {
      // Default multi-level sorting: area, processo, subprocesso, atividade, codigo
      filtered.sort((a, b) => {
        const priorities = ['area', 'processo', 'subprocesso', 'atividade', 'codigo'];
        
        for (const field of priorities) {
          const aVal = String(a[field] || '');
          const bVal = String(b[field] || '');
          
          let comparison = 0;
          
          if (field === 'codigo') {
            // Special alphanumeric sorting for codes
            comparison = alphanumericCompare(aVal, bVal);
          } else {
            // Regular string comparison for other fields
            comparison = aVal.localeCompare(bVal);
          }
          
          if (comparison !== 0) {
            return comparison; // Return on first difference
          }
        }
        
        return 0; // All fields are equal
      });
    }
    
    return filtered;
  };

  // Function to filter and sort masterRecords for the derived profiles table (uses derived-specific controls)
  const getFilteredAndSortedMasterRecords = () => {
    let filtered = [...masterRecords];

    // Remove duplicates based on area/process/subprocess/activity and acaoRisco
    // Keep derived rows (they have `nomenclatura_derivado` or `derivedNomenclatura`) so generated
    // nomenclatures are not filtered out. Only deduplicate non-derived master rows.
    const uniqueMap = new Map();
    filtered = filtered.filter(item => {
      const isDerived = Boolean(item.nomenclatura_derivado || item.derivedNomenclatura);
      const isRisk = isRiskClassificationEnabled && riskClassificationData[item.codigo];
      const riskFlag = isRisk ? 'S' : '-';
      const key = `${item.area || ''}-${item.processo || ''}-${item.subprocesso || ''}-${item.atividade || ''}-${riskFlag}`;

      // Always include derived rows (they represent different derivation values)
      if (isDerived) return true;

      if (uniqueMap.has(key)) return false;
      uniqueMap.set(key, true);
      return true;
    });

    // Apply search filter using derived states
    const q = (derivedSearchQ || '').toLowerCase().trim();
    if (q) {
      const col = derivedSearchColumn;
      if (col) {
        filtered = filtered.filter(item => {
          if (col === 'acaoRisco') {
            const isRisk = isRiskClassificationEnabled && riskClassificationData[item.codigo];
            const riskText = isRisk ? 's' : '-';
            return riskText.toLowerCase().includes(q);
          } else if (col === 'nomenclatura' || col === 'nomenclatura_mestre' || col === 'nomenclatura_derivado') {
            const masterVal = String(item.nomenclatura_mestre || item.nomenclatura || '');
            const derivedVal = String(item.nomenclatura_derivado || item.derivedNomenclatura || '');
            return masterVal.toLowerCase().includes(q) || derivedVal.toLowerCase().includes(q);
          } else {
            const val = String(item[col] || '').toLowerCase();
            return val.includes(q);
          }
        });
      } else {
        filtered = filtered.filter(item => {
          const isRisk = isRiskClassificationEnabled && riskClassificationData[item.codigo];
          const riskText = isRisk ? 's' : '-';
          const nomenclaturaMasterText = item.nomenclatura_mestre || item.nomenclatura || '';
          const nomenclaturaDerivedText = item.nomenclatura_derivado || item.derivedNomenclatura || '';
          const searchValues = [item.area, item.processo, item.subprocesso, item.atividade, nomenclaturaMasterText, nomenclaturaDerivedText, riskText];
          return searchValues.some(val => String(val || '').toLowerCase().includes(q));
        });
      }
    }

    // Apply sorting using derived states
    if (derivedSortColumn) {
      filtered.sort((a, b) => {
        let aVal = '';
        let bVal = '';
        if (derivedSortColumn === 'nomenclatura' || derivedSortColumn === 'nomenclatura_mestre') {
          aVal = String(a.nomenclatura_mestre || a.nomenclatura || '');
          bVal = String(b.nomenclatura_mestre || b.nomenclatura || '');
        } else if (derivedSortColumn === 'nomenclatura_derivado') {
          aVal = String(a.nomenclatura_derivado || a.derivedNomenclatura || '');
          bVal = String(b.nomenclatura_derivado || b.derivedNomenclatura || '');
        } else {
          aVal = String(a[derivedSortColumn] || '');
          bVal = String(b[derivedSortColumn] || '');
        }
        if (derivedSortDirection === 'asc') return aVal.localeCompare(bVal);
        return bVal.localeCompare(aVal);
      });
    } else {
      // Default sorting by area, processo, subprocesso, atividade
      filtered.sort((a, b) => {
        const priorities = ['area', 'processo', 'subprocesso', 'atividade'];
        for (const field of priorities) {
          const aVal = String(a[field] || '');
          const bVal = String(b[field] || '');
          const comparison = aVal.localeCompare(bVal);
          if (comparison !== 0) return comparison;
        }
        return 0;
      });
    }

    return filtered;
  };

  // load codez items when entering step 3 (was step 2)
  useEffect(() => {
    if (step === 3 && clientId) {
      (async () => { try { await fetchIndicators('codez'); } catch (e) { /* ignore */ } })();
    }
  }, [step, clientId, fetchIndicators]);

  // always refresh logs from server when entering Step 4 (was step 3) so we read the authoritative DB state
  useEffect(() => {
    if (step === 4 && clientId) {
      console.debug('entering step 4, fetching logs with full data');
      (async () => { try { await fetchIndicators('logs', 1, true); } catch (e) { /* ignore */ } })();
    }
  }, [step, clientId, fetchIndicators]);

  // if we entered step 4 (was step 3) but previews are empty, try fetching (covers F5 / cold start where previous effect may not run)
  useEffect(() => {
    if (step === 4 && clientId && Array.isArray(logsPreview) && logsPreview.length === 0) {
      console.debug('step4 entered and logsPreview empty - auto-fetching logs with full data');
      (async () => { try { await fetchIndicators('logs', 1, true); } catch (e) { /* ignore */ } })();
    }
  }, [step, clientId, logsPreview, fetchIndicators]);

  // ensure we fetch logs on mount / when clientId changes so a cold reload (F5) populates previews
  useEffect(() => {
    if (clientId) {
      (async () => { try { await fetchIndicators('logs'); } catch (e) { /* ignore */ } })();
    }
  }, [clientId, fetchIndicators, findExactClientByClientId]);

  // reset pagination when codezItems change
  useEffect(() => { setCodezPage(1); setSelectedIds(new Set()); }, [codezItems]);

  // load nomenclature mapping data when entering the nomenclature classification step for Atividade Simples model
  useEffect(() => {
    const isNomenclatureStep = (step === 3 && String(client?.profileModel) === 'Posição/Cargo Simples') || 
                               (step === 5 && String(client?.profileModel) !== 'Posição/Cargo Simples');
    if (isNomenclatureStep && (String(client?.profileModel) === 'Atividade Simples' || String(client?.profileModel) === 'Posição Composto + Atividade Simples') && clientId) {
      fetchNomenclatureMappingData();
    }
  }, [step, client?.profileModel, clientId, fetchNomenclatureMappingData]);

  useEffect(() => {
    const profileModel = String(client?.profileModel || '');
    const isValidationStep =
      (step === 4 && profileModel === 'Posição/Cargo Simples') ||
      (step === 6 && (profileModel === 'Atividade Simples' || profileModel === 'Posição Composto + Atividade Simples'));

    if (!isValidationStep || !clientId || validationLoading || validationRows.length > 0) return;

    (async () => {
      try {
        const savedRows = await loadSavedFinalMappingRows({ withProgress: true });
        if (savedRows.length > 0) {
          await loadProfileTypesMapFromConsolidation();
          setValidationRows(savedRows);
          setValidationPage(1);
          setValidationDataLoaded(true);
          setValidationLoadedFromDb(true);
        }
      } catch (err) {
        console.error('auto-load final mapping error:', err);
      } finally {
        setValidationSavedLoadInProgress(false);
        window.setTimeout(() => {
          setValidationSavedLoadMessage('');
          setValidationSavedLoadCurrent(0);
          setValidationSavedLoadTotal(0);
        }, 350);
      }
    })();
  }, [step, client?.profileModel, clientId, validationLoading, validationRows.length, loadSavedFinalMappingRows, loadProfileTypesMapFromConsolidation]);

  // reset nomenclature page when search changes
  useEffect(() => { setNomenclaturePage(1); }, [nomenclatureSearchQ, nomenclatureSearchColumn, nomenclatureMappingData]);

  // listen for gpw params updates (so saving params in GeradorPerfis modal updates this wizard state)
  useEffect(() => {
    const handler = () => {
      const m = localStorage.getItem('gpw_model'); if (m) setIaModel(m);
      const bs = localStorage.getItem('gpw_batchSize'); if (bs) setIaBatchSize(Number(bs));
      const temp = localStorage.getItem('gpw_temperature'); if (temp) setIaTemperature(Number(temp));
      const p = localStorage.getItem('gpw_prompt'); if (p) setIaPrompt(p);
    };
    window.addEventListener('gpw_params_updated', handler);
    return () => { window.removeEventListener('gpw_params_updated', handler); };
  }, []);

  // Reset AI conversion modal page when tab or search changes
  useEffect(() => { 
    setAiConversionPage(1); 
  }, [aiConversionActiveTab, aiConversionSearchQ, aiConversionSearchColumn]);

  useEffect(() => {
    if (!aiConversionModalOpen) {
      setShowOnlyAiDuplicates(false);
      setShowOnlyAiUnconverted(false);
    }
  }, [aiConversionModalOpen]);

  const toggleSelect = (id?: string) => {
    if (!id) return;
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleSelectAllVisible = (visibleIds: string[]) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      const allSelected = visibleIds.every(id => s.has(id));
      if (allSelected) visibleIds.forEach(id => s.delete(id)); else visibleIds.forEach(id => s.add(id));
      return s;
    });
  };

  const toggleSelectLog = (code?: string) => {
    if (!code) return;
    setSelectedLogCodes(prev => {
      const s = new Set(prev);
      if (s.has(code)) s.delete(code); else s.add(code);
      return s;
    });
  };

  const toggleSelectAllLogsVisible = (visibleCodes: string[]) => {
    setSelectedLogCodes(prev => {
      const s = new Set(prev);
      const allSelected = visibleCodes.every(id => s.has(id));
      if (allSelected) visibleCodes.forEach(id => s.delete(id)); else visibleCodes.forEach(id => s.add(id));
      return s;
    });
  };

  const handleStep4Sort = (col: string, direction?: 'asc' | 'desc') => {
    if (!col) return;
    if (direction) {
      setStep4SortColumn(col);
      setStep4SortDir(direction);
    } else if (step4SortColumn === col) setStep4SortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setStep4SortColumn(col); setStep4SortDir('asc'); }
  };

  const buildLogRows = useCallback(() => {
    try {
      // small trace for debugging
      console.debug('buildLogRows called', { benchCount: Object.keys(benchmarkResults || {}).length, logsCount: logsPreview.length });
    } catch (e) { /* ignore */ }
    const logs = logsPreview;
    const uniqueMap = new Map<string, any>();
    for (const lg of logs) {
      const c = String((lg || {}).code || '').trim();
      if (!c) continue;
      if (!uniqueMap.has(c)) uniqueMap.set(c, lg);
    }
    return Array.from(uniqueMap.entries()).map(([code, lg]) => {
      const matched = (codezItems || []).find(z => String(z.code || '').trim() === String(code).trim());
      // prefer any saved classification in the logs table (lg)
      const lgProc = (lg && (lg.process || lg.processo)) ? (lg.process || lg.processo) : '';
      const lgSub = (lg && (lg.subprocess || lg.subprocesso)) ? (lg.subprocess || lg.subprocesso) : '';
      const lgAct = (lg && (lg.activity || lg.atividade || lg.activity_desc)) ? (lg.activity || lg.atividade || lg.activity_desc) : '';
      const lgFonte = (lg && lg.fonte) ? lg.fonte : (lg && (lg.source || lg.area || lg.origin) ? (lg.source || lg.area || lg.origin) : null);
      const procFromCodez = matched && matched.process ? matched.process : '';
      const subFromCodez = matched && matched.subprocess ? matched.subprocess : '';
      const actFromCodez = matched && matched.activity ? matched.activity : '';
      const isCustom = Boolean(matched);
      // benchmark override takes precedence over everything
      const bm = benchmarkResults && benchmarkResults[code];
      let fonte: string;
      let procFinal: string;
      let subFinal: string;
      let actFinal: string;
  // only show classifications when:
  // - there is an explicit benchmark result (bm),
  // - the code was explicitly classified from custom actions (classifiedFromCustom),
  // - the user triggered any classification action in this session (step4HasClassified),
  // - OR there is already a saved classification in the logs table (lgProc/lgSub/lgAct)
  const hasSavedLogClassification = Boolean(lgProc || lgSub || lgAct);
  const showClass = Boolean(bm) || Boolean(classifiedFromCustom[code]) || step4HasClassified || hasSavedLogClassification;
      if (bm) {
        fonte = bm.fonte;
        procFinal = bm.proc;
        subFinal = bm.sub;
        actFinal = bm.act;
      } else if (showClass && (lgProc || lgSub || lgAct)) {
        // use saved log classification
        fonte = lgFonte || (classifiedFromCustom[code] ? 'Classificada a partir de ações customizadas' : 'Log');
        procFinal = lgProc;
        subFinal = lgSub;
        actFinal = lgAct;
      } else if (showClass && isCustom && classifiedFromCustom[code]) {
        // use codez classification only when the code was explicitly marked as classified from custom actions
        fonte = 'Classificada a partir de ações customizadas';
        procFinal = procFromCodez;
        subFinal = subFromCodez;
        actFinal = actFromCodez;
      } else {
        // hide classification fields until user triggers classification
        fonte = '';
        procFinal = '';
        subFinal = '';
        actFinal = '';
      }
      // Only mark as unclassified after classifications are being shown for this code
      let isUn = false;
      if (showClass) {
        const vals = [procFinal, subFinal, actFinal].map(v => String(v || '').toLowerCase().trim());
        const notClassifiedSet = ['não classificado','nao classificado','não-classificado','nao-classificado','unclassified','not classified'];
        isUn = vals.some(v => notClassifiedSet.includes(v));
      } else {
        isUn = false;
      }
      return { code, proc: procFinal, sub: subFinal, act: actFinal, isCustom, isUnclassified: isUn, fonte };
    });
  }, [logsPreview, codezItems, classifiedFromCustom, benchmarkResults, step4HasClassified]);

  const classifyWithBenchmark = async (mode: 'all' | 'selected') => {
    const rows = buildLogRows();
    let targets = rows.filter(r => !r.isCustom);
    if (mode === 'selected') targets = targets.filter(r => selectedLogCodes.has(r.code));
    if (!targets.length) {
      setUploadResult({ success: true, message: 'Nenhuma code não-customizada disponível para classificar via Benchmark.', tableKey: 'logs' });
      setUploadResultModalOpen(true);
      return;
    }
    setStep4ClassifyText('Classificando via Benchmark...');
    setStep4ClassifyProgress(0);
    setStep4ClassifyMessage('Iniciando classificação...');
    setStep4ClassifyOpen(true);
    const codes = targets.map(t => t.code);
    try {
      setBenchmarkLoading(true);
      const newBench: typeof benchmarkResults = { ...benchmarkResults };
      const totalCodes = codes.length;
      let completed = 0;
      
      // Create progress update function to avoid no-loop-func warning
      const updateProgress = () => {
        completed++;
        const progressPercent = Math.round((completed / totalCodes) * 80); // Up to 80% for classification
        setStep4ClassifyProgress(progressPercent);
        if (progressPercent < 30) {
          setStep4ClassifyMessage(`Consultando benchmark (${completed}/${totalCodes})...`);
        } else if (progressPercent < 60) {
          setStep4ClassifyMessage(`Processando classificações...`);
        } else {
          setStep4ClassifyMessage(`Finalizando classificações...`);
        }
      };
      
      // simple concurrency pool
      const concurrency = 6;
      let idx = 0;
      const codesList = codes;
      const worker = async () => {
        while (idx < codesList.length) {
          const i = idx++;
          const code = codesList[i];
            try {
            // request exact match for code
            const res = await axios.get(`${base}/api/sap-transactions`, { params: { q: code, searchColumn: 'code', pageSize: 1, exact: '1' }, withCredentials: true });
            const items = res && res.data && (res.data.items || []);
            if (items && items.length) {
              const it = items[0];
              const proc = it.process || it.proc || it.processo || '';
              const sub = it.subprocess || it.subprocesso || it.sub || '';
              const act = it.activity || it.activity_desc || it.atividade || '';
              newBench[code] = { proc: proc || 'Não classificado', sub: sub || 'Não classificado', act: act || 'Não classificado', fonte: 'Classificada a partir de Benchmark' };
            } else {
              newBench[code] = { proc: 'Não classificado', sub: 'Não classificado', act: 'Não classificado', fonte: 'Nenhum registro encontrado em Benchmark' };
            }
          } catch (err) {
            console.error('benchmark single code error', code, err);
            // on error, mark as not found
            newBench[code] = { proc: 'Não classificado', sub: 'Não classificado', act: 'Não classificado', fonte: 'Erro ao consultar Benchmark' };
          }
          
          // Update progress
          updateProgress();
        }
      };
      const runners = [] as Promise<void>[];
      for (let w = 0; w < concurrency; w++) runners.push(worker());
      await Promise.all(runners);
  try { console.debug('classifyWithBenchmark finished, newBench size', Object.keys(newBench || {}).length, Object.entries(newBench || {}).slice(0,6)); } catch (e) {}
  setBenchmarkResults(newBench);
      
      // Update progress for saving
      setStep4ClassifyProgress(85);
      setStep4ClassifyMessage('Salvando classificações...');
      
      // persist benchmark results into profilegen-logs so classifications survive
      try {
        const saves: Promise<any>[] = [];
        for (const [code, v] of Object.entries(newBench || {})) {
          const payload = { process: v.proc || '', subprocess: v.sub || '', activity: v.act || '', fonte: v.fonte || 'Classificada a partir de Benchmark', manualEditedAt: new Date().toISOString(), manualEditedBy: 'WEB' };
          const url = `${base}/api/profilegen-data/profilegen-logs/by-code/${clientId}/${encodeURIComponent(code)}`;
          saves.push(axios.patch(url, payload, { withCredentials: true }).catch(err => { console.error('persist benchmark by-code error', code, err); }));
        }
        // run saves with concurrency via Promise.all (they're already small)
        await Promise.all(saves);
  setStep4HasClassified(true);
      } catch (err) {
        console.error('error persisting benchmark results', err);
      }
      
      // Final progress
      setStep4ClassifyProgress(100);
      setStep4ClassifyMessage('Classificação concluída!');
      
      // Wait a moment to show completion
      setTimeout(() => {
        setStep4ClassifyOpen(false);
        setStep4ClassifyText('');
        setStep4ClassifyProgress(0);
        setStep4ClassifyMessage('');
      }, 1500);
      
      setSelectedLogCodes(new Set());
      setClassifyBenchmarkMenuOpen(false);
      // refresh server-side logs view
      try { await fetchIndicators('logs'); } catch (e) { /* ignore */ }
    } catch (err) {
      console.error('benchmark classify error', err);
      setUploadResult({ success: false, message: 'Erro ao consultar Benchmark. Veja console.' });
      setUploadResultModalOpen(true);
      setStep4ClassifyOpen(false);
      setStep4ClassifyText('');
      setStep4ClassifyProgress(0);
      setStep4ClassifyMessage('');
    } finally {
      setBenchmarkLoading(false);
    }
  };

  const classifyCustomizadas = (mode: 'all' | 'selected') => {
    const rows = buildLogRows();
    let targets = rows.filter(r => r.isCustom);
    if (mode === 'selected') targets = targets.filter(r => selectedLogCodes.has(r.code));
    if (!targets.length) {
      setUploadResult({ success: true, message: 'Nenhuma code customizada disponível para classificar.', tableKey: 'logs' });
      setUploadResultModalOpen(true);
      return;
    }
    setStep4ClassifyText('Classificando codes customizadas...');
    setStep4ClassifyProgress(0);
    setStep4ClassifyMessage('Iniciando classificação...');
    setStep4ClassifyOpen(true);
    setClassifiedFromCustom(prev => {
      const copy = { ...prev };
      for (const t of targets) copy[t.code] = true;
      return copy;
    });
    
    // persist custom classifications into profilegen-logs
    (async () => {
      try {
        setStep4ClassifyProgress(20);
        setStep4ClassifyMessage('Processando ações customizadas...');
        
        const saves: Promise<any>[] = [];
        const totalTargets = targets.length;
        let completed = 0;
        
        // Create progress update function to avoid no-loop-func warning
        const updateProgress = () => {
          completed++;
          const progressPercent = 20 + Math.round((completed / totalTargets) * 60); // 20-80%
          setStep4ClassifyProgress(progressPercent);
          if (progressPercent < 50) {
            setStep4ClassifyMessage(`Salvando classificações (${completed}/${totalTargets})...`);
          } else {
            setStep4ClassifyMessage('Finalizando classificações...');
          }
        };
        
        for (const t of targets) {
          // try to source process/sub/act from codezItems when available
          const matched = (codezItems || []).find(z => String(z.code || '').trim() === String(t.code || '').trim());
          const payload: any = { process: matched?.process || t.proc || '', subprocess: matched?.subprocess || t.sub || '', activity: matched?.activity || t.act || '', fonte: 'Classificada a partir de ações customizadas', isCustom: true, manualEditedAt: new Date().toISOString(), manualEditedBy: 'WEB' };
          const url = `${base}/api/profilegen-data/profilegen-logs/by-code/${clientId}/${encodeURIComponent(t.code)}`;
          
          const savePromise = axios.patch(url, payload, { withCredentials: true })
            .catch(err => { console.error('persist custom by-code error', t.code, err); })
            .finally(updateProgress);
          
          saves.push(savePromise);
        }
        
        await Promise.all(saves);
        
        setStep4ClassifyProgress(90);
        setStep4ClassifyMessage('Atualizando indicadores...');
        
        await fetchIndicators('logs');
        setStep4HasClassified(true);
        
        setStep4ClassifyProgress(100);
        setStep4ClassifyMessage('Classificação concluída!');
        
        // Wait a moment to show completion
        setTimeout(() => {
          setStep4ClassifyOpen(false);
          setStep4ClassifyText('');
          setStep4ClassifyProgress(0);
          setStep4ClassifyMessage('');
        }, 1500);
        
      } catch (err) { 
        console.error('error persisting custom classifications', err);
        setStep4ClassifyOpen(false);
        setStep4ClassifyText('');
        setStep4ClassifyProgress(0);
        setStep4ClassifyMessage('');
      }
    })();
    // clear selection after action
    setSelectedLogCodes(new Set());
    setClassifyLogsMenuOpen(false);
  };

  // helper: compute displayed page items
  const displayedCodez = (() => {
    return (codezItems || []);
  })();

  // helper: compute displayed orgLevelValues for modal with search and pagination
  const displayedOrgLevelValues = (() => {
    const raw = orgLevelValues || [];
    const q = (orgLevelValuesSearchQ || '').toLowerCase().trim();
    const filtered = raw.filter((value, index) => {
      if (!q) return true;
      const col = orgLevelValuesSearchColumn;
      const searchTarget = col === 'index' 
        ? String(index + 1) 
        : col === 'value' 
          ? value.toLowerCase() 
          : `${index + 1} ${value}`.toLowerCase();
      return searchTarget.indexOf(q) !== -1;
    });
    return filtered;
  })();

  // helper: compute displayed userOrgLevelMappings for modal with search and pagination
  const displayedUserOrgLevelMappings = (() => {
    const raw = userOrgLevelMappings || [];
    const q = (userOrgLevelSearchQ || '').toLowerCase().trim();
    const filtered = raw.filter((mapping, index) => {
      if (!q) return true;
      const col = userOrgLevelSearchColumn;
      const searchTarget = col === 'index' 
        ? String(index + 1) 
        : col === 'user' 
          ? mapping.user.toLowerCase() 
          : col === 'value' 
            ? mapping.value.toLowerCase() 
            : `${index + 1} ${mapping.user} ${mapping.value}`.toLowerCase();
      return searchTarget.indexOf(q) !== -1;
    });
    return filtered;
  })();

  const handleDiagnosticPageSizeChange = (size: number) => {
    setDiagnosticPageSize(size);
    localStorage.setItem('gpw_diagnostic_pageSize', String(size));
    setUserOrgLevelDiagPage1(1);
    setUserOrgLevelDiagPage2(1);
    setUserOrgLevelDiagPage3(1);
    setUserAreaDiagPage1(1);
    setUserAreaDiagPage2(1);
    setUserAreaDiagPage3(1);
    setUserPositionDiagPage1(1);
    setUserPositionDiagPage2(1);
    setUserPositionDiagPage3(1);
  };

  // helper to compute distinct users count from profilegen-logs previews
  const _computeDistinctUsersCount = () => {
    try {
      const logs = previews['logs'] || [];
      const set = new Set<string>();
      for (const lg of logs) {
        if (!lg) continue;
        const candidates = [lg.user, lg.username, lg.userId, lg.usuario, lg.usuario_id, lg.clientUser, lg.user_name, lg.userId];
        let val = null;
        for (const c of candidates) { if (typeof c !== 'undefined' && c !== null && String(c).trim() !== '') { val = String(c).trim(); break; } }
        // also try fields like 'user' inside nested objects
        if (!val && lg.user && typeof lg.user === 'object' && lg.user.id) val = String(lg.user.id);
        if (val) set.add(val);
      }
      return set.size;
    } catch (e) { return 0; }
  };

  // helper to compute distinct codes count from profilegen-logs previews
  const _computeDistinctCodesCount = () => {
    try {
      const logs = previews['logs'] || [];
      const set = new Set<string>();
      for (const lg of logs) {
        if (!lg) continue;
        const code = lg.code || lg.codigo || lg.transaction || lg.tcode || lg.codez || null;
        if (code && String(code).trim() !== '') set.add(String(code).trim());
      }
      return set.size;
    } catch (e) { return 0; }
  };

  // helper to compute distinct codes count from profilegen-codez table (authoritative for custom actions)
  const _computeDistinctCodezCount = () => {
    try {
      const items = codezItems || [];
      const set = new Set<string>();
      for (const it of items) {
        if (!it) continue;
        const code = it.code || it.codigo || null;
        if (code && String(code).trim() !== '') set.add(String(code).trim());
      }
      return set.size;
    } catch (e) { return 0; }
  };

  // when dashboard is open, prevent the page body from scrolling so only modal scrolls
  useEffect(() => {
    if (dashboardOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
    return;
  }, [dashboardOpen]);

  // recompute unclassifiedIds whenever items change (in case fetched from server)
  useEffect(() => {
    const s = new Set<string>();
    for (const it of codezItems || []) {
      const proc = String(it.process || '').toLowerCase().trim();
      const sub = String(it.subprocess || '').toLowerCase().trim();
      const act = String(it.activity || '').toLowerCase().trim();
      const isUn = ['não classificado','nao classificado','não-classificado','nao-classificado','unclassified'].includes(proc) || ['não classificado','nao classificado','não-classificado','nao-classificado','unclassified'].includes(sub) || ['não classificado','nao classificado','não-classificado','nao-classificado','unclassified'].includes(act);
      if (isUn && it._id) s.add(it._id);
    }
    setUnclassifiedIds(s);
  }, [codezItems]);

  // classify menu
  const [classifyMenuOpen, setClassifyMenuOpen] = useState(false);

  // helper: perform classification on provided items array
  const classifyItems = async (itemsToClassify: { code: string; description?: string; _id?: string }[]) => {
    if (!itemsToClassify || itemsToClassify.length === 0) {
      setUploadResult({ success: true, message: 'Nenhum item disponível para classificar', tableKey: '' });
      setUploadResultModalOpen(true);
      return;
    }
    // batching
    const batches: any[][] = [];
    for (let i = 0; i < itemsToClassify.length; i += iaBatchSize) batches.push(itemsToClassify.slice(i, i + iaBatchSize));
    classifyAbortRef.current = new AbortController();
    const signal = classifyAbortRef.current.signal;
    setClassifyLines([]);
    setClassifyProgressOpen(true);
    setProcessedCount(0);
    setTotalCount(itemsToClassify.length);
    for (let bi = 0; bi < batches.length; bi++) {
      if (signal.aborted) { setClassifyLines(l => [...l, 'Classificação abortada pelo usuário']); break; }
      const b = batches[bi];
      setClassifyLines(l => [...l, `Enviando lote ${bi + 1}/${batches.length} (${b.length} itens)`]);
      try {
        const url = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '') + '/api/ai/classify';
        const payload: any = { items: b.map(x => ({ code: x.code, description: x.description })), prompt: iaPrompt, model: iaModel, temperature: iaTemperature };
        addLogEntry('sent', `Classify -> POST ${url}`, payload);
        const res = await axios.post(url, payload, { withCredentials: true, signal });
        const data = res.data;
        addLogEntry('received', `Classify <- response ${url}`, data, data?.usage);
        const parsed = data && data.items && Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : (data && data.parsed ? data.parsed : null));
        if (!parsed) {
          setClassifyLines(l => [...l, `Lote ${bi + 1} sem resposta parseável`]);
        } else {
          for (const ai of parsed) {
            const found = b.find((x: any) => x.code === ai.code);
            if (found && found._id) {
              try {
                // normalize unclassified markers
                const normalizeUn = (v: any) => {
                  if (v === null || typeof v === 'undefined') return '';
                  const s = String(v).trim();
                  const low = s.toLowerCase();
                  if (['não classificado','nao classificado','não-classificado','nao-classificado','unclassified','not classified','n/a','none'].includes(low)) return 'Não classificado';
                  if (s === '') return '';
                  return s;
                };
                const proc = normalizeUn(ai.process || '');
                const sub = normalizeUn(ai.subprocess || '');
                const act = normalizeUn(ai.activity || '');
                await axios.patch((process.env.REACT_APP_API_URL || '').replace(/\/$/, '') + `/api/profilegen-data/codez/item/${found._id}`, { process: proc, subprocess: sub, activity: act, purpose: normalizeUn(ai.purpose || ''), justification: normalizeUn(ai.justification || '') }, { withCredentials: true, signal });
                setClassifyLines(l => [...l, `Salvo: ${found.code}`]);
                // if any of proc/sub/act is 'Não classificado', mark id for highlight
                if ((proc === 'Não classificado' || sub === 'Não classificado' || act === 'Não classificado') && found._id) {
                  setUnclassifiedIds(prev => { const s = new Set(prev); s.add(found._id); return s; });
                }
              } catch (err: any) {
                if (err?.name === 'CanceledError' || err?.message === 'canceled' || err?.code === 'ERR_CANCELED') { setClassifyLines(l => [...l, `Abortado ao salvar ${found.code}`]); break; }
                setClassifyLines(l => [...l, `Erro salvar ${found.code}`]);
              }
            }
          }
        }
      } catch (err: any) {
        if (err?.name === 'CanceledError' || err?.message === 'canceled' || err?.code === 'ERR_CANCELED') { setClassifyLines(l => [...l, `Lote ${bi + 1} cancelado`]); break; }
        setClassifyLines(l => [...l, `Erro no lote ${bi + 1}: ${String(err || '')}`]);
      }
      setProcessedCount(p => p + b.length);
    }
    classifyAbortRef.current = null;
    setClassifyLines(l => [...l, 'Classificação concluída']);
    await fetchIndicators('codez');
    setTimeout(() => setClassifyProgressOpen(false), 1500);
  };

  useEffect(() => {
    if (!clientId) return;
    (async () => {
      setInitialLoading(true);
      setLoading(true);
      setLoadingProgress(0);
      setLoadingMessage('Carregando dados do cliente...');
      
      try {
        const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        
        // Step 1: Fetch client data (25%)
        setLoadingProgress(25);
        setLoadingMessage('Buscando informações do cliente...');
        const res = await axios.get(`${base}/api/profilegen-clients`, { params: { q: clientId, searchColumn: 'clientId' }, withCredentials: true });
        
        if (res && res.data) {
          // API may return paginated shape or array
          const items = Array.isArray(res.data) ? res.data : (res.data.items || []);
          const found = findExactClientByClientId(items, clientId);
          setClient(found);
          
          // Load risk sanitation settings from client data with business rule validation
          if (found) {
            const loadedBenchmark = Boolean(found.san_risk_benchmark);
            const loadedCliente = Boolean(found.san_risk_cliente);
            const loadedAttrModel = Boolean(found.san_risk_attrb_model);
            
            // Validate business rules on load and correct if needed
            let correctedBenchmark = loadedBenchmark;
            let correctedCliente = loadedCliente;
            let correctedAttrModel = loadedAttrModel;
            
            // Rule 1: san_risk_benchmark and san_risk_cliente are mutually exclusive
            if (loadedBenchmark && loadedCliente) {
              // If both are active, keep only benchmark (priority rule)
              correctedCliente = false;
              console.warn('Business rule violation detected on load: benchmark and cliente cannot be both active. Disabling cliente.');
            }
            
            // Rule 2: If san_risk_attrb_model is true, at least one of the other two must be true
            if (loadedAttrModel && !loadedBenchmark && !loadedCliente) {
              // If only attrb_model is active, disable it
              correctedAttrModel = false;
              console.warn('Business rule violation detected on load: composite risk profile requires at least one base option. Disabling composite.');
            }
            
            setSanRiskBenchmark(correctedBenchmark);
            setSanRiskCliente(correctedCliente);
            setSanRiskAttrModel(correctedAttrModel);
            
            // Load organizational level derivation settings
            setDerivationStatus(Boolean(found.derivation_status));
            setDerivationOrgLevel(found.derivation_orglevel || '');
            setDerivationUserStatus(Boolean(found.derivation_user_status));
            
            // Load organizational level values if they exist
            if (found.derivation_orglevel_values) {
              const values = found.derivation_orglevel_values.split(',').map((v: string) => v.trim()).filter((v: string) => v);
              setOrgLevelValues(values);
            }
            
            // If corrections were made, save the corrected state
            if (correctedBenchmark !== loadedBenchmark || 
                correctedCliente !== loadedCliente || 
                correctedAttrModel !== loadedAttrModel) {
              setTimeout(async () => {
                console.log('Auto-correcting business rule violations in database...');
                try {
                  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
                  await axios.put(
                    `${base}/api/profilegen-clients/${found._id}`,
                    {
                      san_risk_benchmark: correctedBenchmark,
                      san_risk_cliente: correctedCliente,
                      san_risk_attrb_model: correctedAttrModel
                    },
                    { withCredentials: true }
                  );
                  console.log('Business rule corrections saved successfully');
                } catch (error) {
                  console.error('Error saving business rule corrections:', error);
                }
              }, 1000); // Delay to ensure state is set first
            }
          }
          
          // Step 2: Determine tables for this client (40%)
          setLoadingProgress(40);
          setLoadingMessage('Configurando tabelas do modelo...');
          const tablesForClient: TableMeta[] = (found && String(found.profileModel) === 'Posição/Cargo Simples') ? POS_TABLES : (found && String(found.profileModel) === 'Posição Composto + Atividade Simples') ? COMBINED_TABLES : BASE_TABLES;
          
          // Step 3: Load existing constructor elements and field configurations (60%)
          setLoadingProgress(60);
          setLoadingMessage('Carregando configurações de padrões...');
          if (found) {
            // Load simple patterns
            const simpleElements: NameElement[] = [
              { id: 'prefixo', type: 'prefix' as const, value: '', label: 'Prefixo', maxLength: found.pattern_s_prefixo_qtd || 3, displayChar: found.pattern_s_prefixo_char || '-' },
              { id: 'area', type: 'data' as const, value: '', label: 'Área', maxLength: found.pattern_s_area_qtd || 3, displayChar: found.pattern_s_area_char || '-' },
              { id: 'processo', type: 'data' as const, value: '', label: 'Processo', maxLength: found.pattern_s_processo_qtd || 3, displayChar: found.pattern_s_processo_char || '-' },
              { id: 'subprocesso', type: 'data' as const, value: '', label: 'Subprocesso', maxLength: found.pattern_s_subprocesso_qtd || 3, displayChar: found.pattern_s_subprocesso_char || '-' },
              { id: 'atividade', type: 'data' as const, value: '', label: 'Atividade', maxLength: found.pattern_s_atividade_qtd || 3, displayChar: found.pattern_s_atividade_char || '-' },
              { id: 'sep1', type: 'separator' as const, value: found.pattern_s_sep1_char || '-', label: 'Separador 1', maxLength: 1, displayChar: found.pattern_s_sep1_char || '-' },
              { id: 'sep2', type: 'separator' as const, value: found.pattern_s_sep2_char || '-', label: 'Separador 2', maxLength: 1, displayChar: found.pattern_s_sep2_char || '-' },
              { id: 'sep3', type: 'separator' as const, value: found.pattern_s_sep3_char || '-', label: 'Separador 3', maxLength: 1, displayChar: found.pattern_s_sep3_char || '-' },
              { id: 'sep4', type: 'separator' as const, value: found.pattern_s_sep4_char || '-', label: 'Separador 4', maxLength: 1, displayChar: found.pattern_s_sep4_char || '-' },
              { id: 'sep5', type: 'separator' as const, value: found.pattern_s_sep5_char || '-', label: 'Separador 5', maxLength: 1, displayChar: found.pattern_s_sep5_char || '-' },
              { id: 'sep6', type: 'separator' as const, value: found.pattern_s_sep6_char || '-', label: 'Separador 6', maxLength: 1, displayChar: found.pattern_s_sep6_char || '-' }
            ].filter(e => {
              // Para elementos de dados (não separadores), verificar se qtd > 0
              if (e.id === 'prefixo' || e.id === 'area' || e.id === 'processo' || e.id === 'subprocesso' || e.id === 'atividade') {
                const qtdValue = e.id === 'prefixo' ? (found.pattern_s_prefixo_qtd || 0) :
                                e.id === 'area' ? (found.pattern_s_area_qtd || 0) :
                                e.id === 'processo' ? (found.pattern_s_processo_qtd || 0) :
                                e.id === 'subprocesso' ? (found.pattern_s_subprocesso_qtd || 0) :
                                (found.pattern_s_atividade_qtd || 0);
                return qtdValue > 0;
              }
              // Para separadores, verificar se existe char válido
              const charValue = e.id === 'sep1' ? (found.pattern_s_sep1_char || '') :
                               e.id === 'sep2' ? (found.pattern_s_sep2_char || '') :
                               e.id === 'sep3' ? (found.pattern_s_sep3_char || '') :
                               e.id === 'sep4' ? (found.pattern_s_sep4_char || '') :
                               e.id === 'sep5' ? (found.pattern_s_sep5_char || '') :
                               (found.pattern_s_sep6_char || '');
              return charValue.length > 0;
            }).sort((a, b) => {
              const ordA = a.id === 'prefixo' ? (found.pattern_s_prefixo_ord || 0) :
                          a.id === 'area' ? (found.pattern_s_area_ord || 0) :
                          a.id === 'processo' ? (found.pattern_s_processo_ord || 0) :
                          a.id === 'subprocesso' ? (found.pattern_s_subprocesso_ord || 0) :
                          a.id === 'atividade' ? (found.pattern_s_atividade_ord || 0) :
                          a.id === 'sep1' ? (found.pattern_s_sep1_ord || 0) :
                          a.id === 'sep2' ? (found.pattern_s_sep2_ord || 0) :
                          a.id === 'sep3' ? (found.pattern_s_sep3_ord || 0) :
                          a.id === 'sep4' ? (found.pattern_s_sep4_ord || 0) :
                          a.id === 'sep5' ? (found.pattern_s_sep5_ord || 0) :
                          (found.pattern_s_sep6_ord || 0);
              const ordB = b.id === 'prefixo' ? (found.pattern_s_prefixo_ord || 0) :
                          b.id === 'area' ? (found.pattern_s_area_ord || 0) :
                          b.id === 'processo' ? (found.pattern_s_processo_ord || 0) :
                          b.id === 'subprocesso' ? (found.pattern_s_subprocesso_ord || 0) :
                          b.id === 'atividade' ? (found.pattern_s_atividade_ord || 0) :
                          b.id === 'sep1' ? (found.pattern_s_sep1_ord || 0) :
                          b.id === 'sep2' ? (found.pattern_s_sep2_ord || 0) :
                          b.id === 'sep3' ? (found.pattern_s_sep3_ord || 0) :
                          b.id === 'sep4' ? (found.pattern_s_sep4_ord || 0) :
                          b.id === 'sep5' ? (found.pattern_s_sep5_ord || 0) :
                          (found.pattern_s_sep6_ord || 0);
              return ordA - ordB;
            });
            
            // Load composite patterns
            const compositeElements: NameElement[] = [
              { id: 'prefixo', type: 'prefix' as const, value: 'prefixo', label: 'Prefixo', maxLength: found.pattern_c_prefixo_qtd || 3, displayChar: found.pattern_c_prefixo_char || 'X' },
              { id: 'n1', type: 'data' as const, value: 'n1', label: 'Nível 1', maxLength: found.pattern_c_n1_qtd || 3, displayChar: found.pattern_c_n1_char || '1' },
              { id: 'n2', type: 'data' as const, value: 'n2', label: 'Nível 2', maxLength: found.pattern_c_n2_qtd || 3, displayChar: found.pattern_c_n2_char || '2' },
              { id: 'n3', type: 'data' as const, value: 'n3', label: 'Nível 3', maxLength: found.pattern_c_n3_qtd || 3, displayChar: found.pattern_c_n3_char || '3' },
              { id: 'n4', type: 'data' as const, value: 'n4', label: 'Nível 4', maxLength: found.pattern_c_n4_qtd || 3, displayChar: found.pattern_c_n4_char || '4' },
              { id: 'sufixo', type: 'suffix' as const, value: 'sufixo', label: 'Sufixo', maxLength: found.pattern_c_sufixo_qtd || 3, displayChar: found.pattern_c_sufixo_char || 'Z' },
              { id: 'sep1', type: 'separator' as const, value: found.pattern_c_sep1_char || '-', label: 'Separador 1', maxLength: 1, displayChar: found.pattern_c_sep1_char || '-' },
              { id: 'sep2', type: 'separator' as const, value: found.pattern_c_sep2_char || '-', label: 'Separador 2', maxLength: 1, displayChar: found.pattern_c_sep2_char || '-' },
              { id: 'sep3', type: 'separator' as const, value: found.pattern_c_sep3_char || '-', label: 'Separador 3', maxLength: 1, displayChar: found.pattern_c_sep3_char || '-' },
              { id: 'sep4', type: 'separator' as const, value: found.pattern_c_sep4_char || '-', label: 'Separador 4', maxLength: 1, displayChar: found.pattern_c_sep4_char || '-' },
              { id: 'sep5', type: 'separator' as const, value: found.pattern_c_sep5_char || '-', label: 'Separador 5', maxLength: 1, displayChar: found.pattern_c_sep5_char || '-' },
              { id: 'sep6', type: 'separator' as const, value: found.pattern_c_sep6_char || '-', label: 'Separador 6', maxLength: 1, displayChar: found.pattern_c_sep6_char || '-' }
            ].filter(e => {
              // Para elementos de dados (não separadores), verificar se qtd > 0
              if (e.id === 'prefixo' || e.id === 'n1' || e.id === 'n2' || e.id === 'n3' || e.id === 'n4' || e.id === 'sufixo') {
                const qtdValue = e.id === 'prefixo' ? (found.pattern_c_prefixo_qtd || 0) :
                                e.id === 'n1' ? (found.pattern_c_n1_qtd || 0) :
                                e.id === 'n2' ? (found.pattern_c_n2_qtd || 0) :
                                e.id === 'n3' ? (found.pattern_c_n3_qtd || 0) :
                                e.id === 'n4' ? (found.pattern_c_n4_qtd || 0) :
                                (found.pattern_c_sufixo_qtd || 0);
                return qtdValue > 0;
              }
              // Para separadores, verificar se existe char válido
              const charValue = e.id === 'sep1' ? (found.pattern_c_sep1_char || '') :
                               e.id === 'sep2' ? (found.pattern_c_sep2_char || '') :
                               e.id === 'sep3' ? (found.pattern_c_sep3_char || '') :
                               e.id === 'sep4' ? (found.pattern_c_sep4_char || '') :
                               e.id === 'sep5' ? (found.pattern_c_sep5_char || '') :
                               (found.pattern_c_sep6_char || '');
              return charValue.length > 0;
            }).sort((a, b) => {
              const ordA = a.id === 'prefixo' ? (found.pattern_c_prefixo_ord || 0) :
                          a.id === 'n1' ? (found.pattern_c_n1_ord || 0) :
                          a.id === 'n2' ? (found.pattern_c_n2_ord || 0) :
                          a.id === 'n3' ? (found.pattern_c_n3_ord || 0) :
                          a.id === 'n4' ? (found.pattern_c_n4_ord || 0) :
                          a.id === 'sufixo' ? (found.pattern_c_sufixo_ord || 0) :
                          a.id === 'sep1' ? (found.pattern_c_sep1_ord || 0) :
                          a.id === 'sep2' ? (found.pattern_c_sep2_ord || 0) :
                          a.id === 'sep3' ? (found.pattern_c_sep3_ord || 0) :
                          a.id === 'sep4' ? (found.pattern_c_sep4_ord || 0) :
                          a.id === 'sep5' ? (found.pattern_c_sep5_ord || 0) :
                          (found.pattern_c_sep6_ord || 0);
              const ordB = b.id === 'prefixo' ? (found.pattern_c_prefixo_ord || 0) :
                          b.id === 'n1' ? (found.pattern_c_n1_ord || 0) :
                          b.id === 'n2' ? (found.pattern_c_n2_ord || 0) :
                          b.id === 'n3' ? (found.pattern_c_n3_ord || 0) :
                          b.id === 'n4' ? (found.pattern_c_n4_ord || 0) :
                          b.id === 'sufixo' ? (found.pattern_c_sufixo_ord || 0) :
                          b.id === 'sep1' ? (found.pattern_c_sep1_ord || 0) :
                          b.id === 'sep2' ? (found.pattern_c_sep2_ord || 0) :
                          b.id === 'sep3' ? (found.pattern_c_sep3_ord || 0) :
                          b.id === 'sep4' ? (found.pattern_c_sep4_ord || 0) :
                          b.id === 'sep5' ? (found.pattern_c_sep5_ord || 0) :
                          (found.pattern_c_sep6_ord || 0);
              return ordA - ordB;
            });
            
            setConstructorElements(simpleElements);
            setCompositeConstructorElements(compositeElements);
            
            // Load prefix patterns
            const simplePrefixPatterns = {
              simples: found.pattern_s_prefixo_simples || '',
              mestre: found.pattern_s_prefixo_mestre || '',
              derivado: found.pattern_s_prefixo_derivado || '',
              risco: found.pattern_s_prefixo_risco || ''
            };
            
            const compositePrefixPatterns = {
              composto: found.pattern_c_prefixo_composto || ''
            };
            
            setPrefixPatterns(simplePrefixPatterns);
            setCompositePrefixPatterns(compositePrefixPatterns);
            
            // Load risk prefix pattern
            setRiskPrefixPattern(found.pattern_r_prefixo_risco || '');
            setRiskPrefixPatternMestre(found.pattern_r_prefixo_risco_mestre || '');
            setRiskPrefixPatternDerivado(found.pattern_r_prefixo_risco_derivado || '');
            
            // Load composite field configurations
            const newCompositeFieldConfigs = {
              prefixo: {
                char: found.pattern_c_prefixo_char || 'X',
                qtd: found.pattern_c_prefixo_qtd || 3
              },
              n1: {
                char: found.pattern_c_n1_char || '1',
                qtd: found.pattern_c_n1_qtd || 3
              },
              n2: {
                char: found.pattern_c_n2_char || '2',
                qtd: found.pattern_c_n2_qtd || 3
              },
              n3: {
                char: found.pattern_c_n3_char || '3',
                qtd: found.pattern_c_n3_qtd || 3
              },
              n4: {
                char: found.pattern_c_n4_char || '4',
                qtd: found.pattern_c_n4_qtd || 3
              },
              sufixo: {
                char: found.pattern_c_sufixo_char || 'Z',
                qtd: found.pattern_c_sufixo_qtd || 3
              }
            };
            setCompositeFieldConfigs(newCompositeFieldConfigs);
          }
          
          // Step 4: Fetch indicators for all tables (80%)
          setLoadingProgress(80);
          setLoadingMessage('Carregando indicadores das tabelas...');
          const promises = tablesForClient.map(t => fetchIndicators(t.key).catch((e) => { console.debug('fetchIndicators error', e); }));
          await Promise.all(promises);
          
          // Step 5: Complete (100%)
          setLoadingProgress(100);
          setLoadingMessage('Finalizado!');
        }
      } catch (err) {
        console.error('Erro fetch client', err);
        setLoadingMessage('Erro ao carregar dados');
      } finally { 
        // Small delay to show 100% before hiding
        setTimeout(() => {
          setLoading(false); 
          setInitialLoading(false);
        }, 300);
      }
    })();
  }, [clientId, fetchIndicators, findExactClientByClientId]);

  // Save risk sanitation settings function
  const saveRiskSanitationSettings = useCallback(async () => {
    try {
      if (!client || !client._id) {
        console.log('No client or client._id available for saving risk settings');
        return;
      }

      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      await axios.put(
        `${base}/api/profilegen-clients/${client._id}`,
        {
          san_risk_benchmark: sanRiskBenchmark,
          san_risk_cliente: sanRiskCliente,
          san_risk_attrb_model: sanRiskAttrModel
        },
        { withCredentials: true }
      );
      console.log('Risk sanitation settings saved successfully');
    } catch (err) {
      console.error('Error saving risk sanitation settings:', err);
      setUploadResult({ success: false, message: 'Erro ao salvar configurações de saneamento de risco.' });
      setUploadResultModalOpen(true);
    }
  }, [client, sanRiskBenchmark, sanRiskCliente, sanRiskAttrModel]);

  // Auto-save risk sanitation settings when they change (but not during initial load)
  useEffect(() => {
    // Only save if we have client data and we're not in initial loading state
    if (client && client._id && !initialLoading) {
      saveRiskSanitationSettings();
    }
  }, [sanRiskBenchmark, sanRiskCliente, sanRiskAttrModel, client, initialLoading, saveRiskSanitationSettings]);

  // Initialize and update risk sanitation elements when states change
  useEffect(() => {
    if (client) {
      // Initialize default positions if not present
      initializeDefaultRiskPositions();
      
      const sortedElements = initializeRiskSanitationElementsWithOrder();
      setRiskSanitationElements(sortedElements);
    }
  }, [client, sanRiskBenchmark, sanRiskCliente, sanRiskAttrModel, initializeDefaultRiskPositions, initializeRiskSanitationElementsWithOrder]);

  // update client's phase using existing API
  async function updateClientPhase(newPhase: string) {
    try {
      if (!client || !client._id) return null;
      const url = `${base}/api/profilegen-clients/${client._id}`;
      const res = await axios.put(url, { phase: newPhase }, { withCredentials: true });
      if (res && res.data) {
        setClient(res.data);
        return res.data;
      }
    } catch (err) {
      console.error('updateClientPhase error', err);
    }
    return null;
  }

  async function clearTable(tableKey: string) {
    if (!clientId) return;
    try {
      let url = `${base}/api/profilegen-data/${tableKey}/${clientId}`;
      // for logs table, allow forceful deletion of raw log rows from the server
      if (tableKey === 'logs') url += '?force=1';
      
      // Show toast immediately
      setClearProgress({ table: tableKey, progress: 0, message: 'Iniciando limpeza...', done: false });
      setClearToastOpen(true);
      
      // Start clear operation (responds immediately)
      const res = await axios.delete(url, { withCredentials: true });
      const totalCount = res.data?.totalCount || 0;
      
      // If no records to delete, close immediately
      if (totalCount === 0 || res.data?.deletedCount === 0) {
        setClearProgress({ table: tableKey, progress: 100, message: 'Nenhum registro encontrado', done: true });
        setIndicators(prev => ({ ...prev, [tableKey]: { count: 0 } }));
        setPreviews(prev => ({ ...prev, [tableKey]: [] }));
        setTimeout(() => setClearToastOpen(false), 2000);
        return;
      }
      
      // Start polling for progress
      let clearCompleted = false;
      const progressUrl = `${base}/api/profilegen-data/${tableKey}/${clientId}/clear-progress`;
      
      const pollInterval = setInterval(async () => {
        try {
          const progRes = await axios.get(progressUrl, { withCredentials: true });
          const { progress, message, done, error } = progRes.data;
          
          setClearProgress({ table: tableKey, progress, message, done });
          
          if (done && progress === 100 && !clearCompleted) {
            clearCompleted = true;
            clearInterval(pollInterval);
            
            // Update indicators and previews
            setIndicators(prev => ({ ...prev, [tableKey]: { count: 0 } }));
            setPreviews(prev => ({ ...prev, [tableKey]: [] }));
            
            // Close toast after 3 seconds
            setTimeout(() => {
              setClearToastOpen(false);
              setClearProgress({ table: tableKey, progress: 0, message: '', done: false });
            }, 3000);
          } else if (done && error && !clearCompleted) {
            clearCompleted = true;
            clearInterval(pollInterval);
            
            setUploadResult({ success: false, message: `Erro ao limpar: ${error}`, tableKey });
            setUploadResultModalOpen(true);
            setClearToastOpen(false);
          }
        } catch (pollErr) {
          console.error('Polling error:', pollErr);
        }
      }, 500);
      
    } catch (err) {
      console.error('clearTable', err);
      setClearToastOpen(false);
      setUploadResult({ success: false, message: 'Erro ao limpar a tabela. Verifique o console.', tableKey });
      setUploadResultModalOpen(true);
    }
  }

  // Clear only classifications fields, not the entire records
  async function clearClassifications() {
    if (!clientId) return;
    
    // Show progress modal
    setClearClassificationsProgress(0);
    setClearClassificationsMessage('Iniciando limpeza...');
    setClearClassificationsProgressOpen(true);
    
    try {
      setClearClassificationsProgress(20);
      setClearClassificationsMessage('Conectando ao servidor...');
      
      const url = `${base}/api/profilegen-data/profilegen-logs/clear-classifications/${clientId}`;
      
      setClearClassificationsProgress(40);
      setClearClassificationsMessage('Limpando classificações...');
      
      const res = await axios.patch(url, {}, { withCredentials: true });
      const updated = res && res.data && typeof res.data.modifiedCount === 'number' ? res.data.modifiedCount : null;
      
      setClearClassificationsProgress(70);
      setClearClassificationsMessage('Atualizando estado local...');
      
      // reset local classification-related state so table refreshes consistently
      setBenchmarkResults({});
      setClassifiedFromCustom({});
      setStep4HasClassified(false);
      setSelectedLogCodes(new Set());
      
      setClearClassificationsProgress(90);
      setClearClassificationsMessage('Atualizando indicadores...');
      
      // refresh indicators & previews
      await fetchIndicators('logs');
      
      setClearClassificationsProgress(100);
      setClearClassificationsMessage('Limpeza concluída!');
      
      // Wait a moment to show completion
      setTimeout(() => {
        setClearClassificationsProgressOpen(false);
        setClearClassificationsProgress(0);
        setClearClassificationsMessage('');
        
        setUploadResult({ 
          success: true, 
          message: updated !== null ? `${updated} classificações foram removidas. Os registros de log foram mantidos.` : 'Classificações removidas.', 
          tableKey: 'profilegen-logs' 
        });
        setUploadResultModalOpen(true);
      }, 1500);
      
    } catch (err) {
      console.error('clearClassifications', err);
      setClearClassificationsProgressOpen(false);
      setClearClassificationsProgress(0);
      setClearClassificationsMessage('');
      
      setUploadResult({ success: false, message: 'Erro ao limpar classificações. Verifique o console.' });
      setUploadResultModalOpen(true);
    }
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>, tableKey: string) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async ev => {
      try {
        let rows: any[] = [];
        let headers: string[] = [];
        if (file.name.toLowerCase().endsWith('.json')) {
          const text = String(ev.target?.result || '');
          const parsed = JSON.parse(text);
          rows = Array.isArray(parsed) ? parsed : (parsed.rows || []);
          if (rows && rows.length) headers = normalizeImportedHeaders(Object.keys(rows[0] || {}));
        } else if (/\.(xlsx|xls)$/i.test(file.name)) {
          const parsed = parseExcel(ev.target?.result as ArrayBuffer);
          rows = parsed.rows || [];
          headers = parsed.headers || [];
        } else {
          const text = String(ev.target?.result || '');
          const parsed = parseCSV(text);
          rows = parsed.rows || [];
          headers = parsed.headers || [];
        }
        console.debug(`Parsed ${rows.length} rows for table ${tableKey}`, rows && rows.slice(0,3));
        // validate that rows contain at least one non-empty column other than clientId
        const hasData = rows.length && Object.keys(rows[0] || {}).some(k => k && k !== 'clientId');
        if (!hasData) {
          setUploadResult({ success: false, message: 'Arquivo processado, mas nenhuma coluna de dados reconhecida. Verifique o cabeçalho do CSV (ex: user;code).', tableKey });
          setUploadResultModalOpen(true);
          return;
        }
        // validate headers against expected columns for this table
        const expected = (tables.find(t => t.key === tableKey)?.columns) || [];
        const normalize = (s: string) => String(s || '').toLowerCase().replace(/[^a-z0-9_]/g, '').trim();
        const hset = headers.map(normalize);
        let headerOk = true;
        let expectedMsg = expected.join(',');
        if (tableKey === 'userxpos') {
          // require 'user' and at least one N-level (n1..n4)
          const hasUser = hset.indexOf('user') !== -1;
          const hasN = ['n1','n2','n3','n4'].some(x => hset.indexOf(x) !== -1);
          headerOk = hasUser && hasN;
          expectedMsg = 'user + pelo menos um dos N1,N2,N3,N4 (ex: user,n1,n2)';
        } else if (expected.length) {
          // require all expected columns present (for other tables)
          headerOk = expected.every(col => hset.indexOf(normalize(col)) !== -1);
        }
        if (!headerOk) {
          setUploadResult({ success: false, message: `Cabeçalho inválido para tabela ${tableKey}. Formato esperado: ${expectedMsg}`, tableKey });
          setUploadResultModalOpen(true);
          return;
        }
        try {
          await uploadRows(tableKey, rows);
          // Upload successful - progress modal handles the success feedback
        } catch (err) {
          // Only show error modal for upload errors, not for validation errors
          setUploadResult({ success: false, message: 'Erro ao enviar os dados para o servidor. Verifique console e conexões.', tableKey });
          setUploadResultModalOpen(true);
        }
      } catch (err) {
        console.error(err);
        setUploadResult({ success: false, message: 'Erro ao processar arquivo. Verifique o formato.', tableKey });
        setUploadResultModalOpen(true);
      }
    };
    if (/\.(xlsx|xls)$/i.test(file.name)) {
      reader.readAsArrayBuffer(file);
      return;
    }
    reader.readAsText(file);
  }

  // Clear file input value to allow re-selecting the same file
  function handleFileInputClick(e: React.MouseEvent<HTMLInputElement>) {
    e.currentTarget.value = '';
  }

  // Handle Log Sync for upcasis table
  const handleLogSync = async () => {
    if (!clientId) return;
    
    console.log('🔵 handleLogSync START');
    setLogSyncLoading(true);
    setLogSyncToastOpen(true);
    setLogSyncProgress(0);
    setLogSyncProgressMessage('Iniciando sincronização...');
    setLogSyncResult(null);
    
    let progressPollInterval: NodeJS.Timeout | null = null;
    let syncCompleted = false;
    
    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      
      console.log('🟡 Making axios.post to:', `${base}/api/profilegen-data/upcasis/log-sync/${clientId}`);
      
      // Iniciar polling de progresso ANTES do POST
      progressPollInterval = setInterval(async () => {
        try {
          const progressRes = await axios.get(
            `${base}/api/profilegen-data/upcasis/log-sync-progress/${clientId}`,
            { withCredentials: true }
          );
          if (progressRes.data) {
            const { progress, message, done, result } = progressRes.data;
            console.log(`🔵 Poll: ${progress}% - ${message} - done: ${done}`);
            setLogSyncProgress(progress);
            setLogSyncProgressMessage(message);
            
            // Se o processamento terminou, parar o polling
            if (done && progress === 100) {
              console.log('🟢 Sync completed via polling');
              syncCompleted = true;
              if (progressPollInterval) {
                clearInterval(progressPollInterval);
                progressPollInterval = null;
              }
              
              // Setar resultado se disponível
              if (result) {
                setLogSyncResult(result);
              }
              
              // Refresh indicators
              console.log('🟡 Fetching indicators...');
              await fetchIndicators('upcasis');
              
              // Fechar toast após 3 segundos
              await new Promise(resolve => setTimeout(resolve, 3000));
              setLogSyncToastOpen(false);
              setLogSyncLoading(false);
            }
          }
        } catch (e) {
          console.error('❌ Poll error (non-blocking):', e);
        }
      }, 500); // Poll a cada 500ms
      
      // Fazer a requisição principal - ela retorna IMEDIATAMENTE mas o processamento continua em background
      const response = await axios.post(
        `${base}/api/profilegen-data/upcasis/log-sync/${clientId}`,
        {},
        { 
          withCredentials: true,
          timeout: 600000  // 10 minutos de timeout
        }
      );
      
      console.log('🟢 POST Response received:', response.data);
      // NÃO setar progresso como 100% aqui! O polling vai cuidar disso quando o backend terminar
      
    } catch (error: any) {
      if (progressPollInterval) {
        clearInterval(progressPollInterval);
        progressPollInterval = null;
      }
      
      console.error('❌ Error:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error response:', error.response?.data);
      
      if (error.response?.data?.message === 'no_logs_found') {
        setLogSyncLoading(false);
        setLogSyncToastOpen(false);
        pushToast({
          variant: 'warning',
          title: 'Log de acessos não encontrado',
          message: 'Carregue primeiro o arquivo CSV na seção "Log de acessos cliente" para usar o Log Sync.',
          durationMs: 8000
        });
      } else {
        setLogSyncProgress(0);
        setLogSyncProgressMessage('Erro durante a sincronização');
        setLogSyncResult({
          ok: false,
          error: error.response?.data?.detail || error.message || 'Erro desconhecido ao processar Log Sync'
        });
        
        // Manter toast aberto para ver o erro
        await new Promise(resolve => setTimeout(resolve, 5000));
        setLogSyncToastOpen(false);
        setLogSyncLoading(false);
      }
    }
    // NÃO usar finally aqui porque o polling pode ainda estar rodando
  };

  // Save risk sanitation settings to database
  // Handle risk matrix file upload
  const handleRiskMatrixUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setUploadingRiskMatrix(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const text = String(ev.target?.result || '');
          const { rows, headers } = parseCSV(text);
          
          // Validate required columns
          const requiredColumns = ['risk', 'code'];
          const normalize = (s: string) => String(s || '').toLowerCase().replace(/[^a-z0-9_]/g, '').trim();
          const normalizedHeaders = headers.map(normalize);
          
          const missingColumns = requiredColumns.filter(col => !normalizedHeaders.includes(normalize(col)));
          if (missingColumns.length > 0) {
            setUploadResult({ 
              success: false, 
              message: `Colunas obrigatórias ausentes: ${missingColumns.join(', ')}. Formato esperado: risk,code` 
            });
            setUploadResultModalOpen(true);
            return;
          }

          if (rows.length === 0) {
            setUploadResult({ success: false, message: 'Arquivo está vazio ou não contém dados válidos.' });
            setUploadResultModalOpen(true);
            return;
          }

          // Upload data to server
          const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
          const riskMatrixData = rows.map(row => ({
            clienteId: clientId,
            code: String(row.code || '').trim(),
            risk: String(row.risk || '').trim()
          })).filter(item => item.code && item.risk);

          await axios.post(
            `${base}/api/profilegen-clients/riskmatrix`,
            { 
              items: riskMatrixData,
              clientId: clientId
            },
            { withCredentials: true }
          );

          setUploadResult({ 
            success: true, 
            message: `Matriz de risco carregada com sucesso. ${riskMatrixData.length} registros inseridos.`,
            insertedCount: riskMatrixData.length
          });
          setUploadResultModalOpen(true);

        } catch (err) {
          console.error('Risk matrix upload error:', err);
          setUploadResult({ success: false, message: 'Erro ao processar arquivo de matriz de risco.' });
          setUploadResultModalOpen(true);
        } finally {
          setUploadingRiskMatrix(false);
        }
      };

      reader.readAsText(file);
    } catch (err) {
      console.error('File read error:', err);
      setUploadResult({ success: false, message: 'Erro ao ler arquivo.' });
      setUploadResultModalOpen(true);
      setUploadingRiskMatrix(false);
    }

    // Reset input value to allow re-uploading the same file
    e.target.value = '';
  };

  // Load risk matrix data for visualization
  const loadRiskMatrixData = async () => {
    if (!clientId) return;
    
    setLoadingRiskMatrix(true);
    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const response = await axios.get(
        `${base}/api/profilegen-clients/riskmatrix/${clientId}`,
        { withCredentials: true }
      );
      
      setRiskMatrixData(response.data.items || []);
      setRiskMatrixModalOpen(true);
    } catch (err) {
      console.error('Error loading risk matrix:', err);
      setUploadResult({ success: false, message: 'Erro ao carregar matriz de risco.' });
      setUploadResultModalOpen(true);
    } finally {
      setLoadingRiskMatrix(false);
    }
  };

  // Clear risk matrix data
  const clearRiskMatrixData = async () => {
    if (!clientId) return;
    
    setConfirmClearRiskMatrixModalOpen(true);
  };

  // Confirm and execute clear risk matrix data
  const confirmClearRiskMatrixData = async () => {
    if (!clientId) return;
    
    setConfirmClearRiskMatrixModalOpen(false);
    
    try {
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const response = await axios.delete(
        `${base}/api/profilegen-clients/riskmatrix/${clientId}`,
        { withCredentials: true }
      );
      
      setUploadResult({ 
        success: true, 
        message: response.data.message || 'Matriz de risco limpa com sucesso.'
      });
      setUploadResultModalOpen(true);
    } catch (err) {
      console.error('Error clearing risk matrix:', err);
      setUploadResult({ success: false, message: 'Erro ao limpar matriz de risco.' });
      setUploadResultModalOpen(true);
    }
  };

  // Download codez classifications as CSV (with full data load)
  const downloadCodezCsv = async () => {
    try {
      setUploadResult({ success: false, message: 'Carregando todos os códigos para download...' });
      setUploadResultModalOpen(true);

      // Fetch all codez data to ensure we get everything (not just cached items in memory)
      const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const indicatorsRes = await axios.get(
        `${baseUrl}/api/profilegen-data/profilegen-codez/${clientId}?indicatorsOnly=true`,
        { withCredentials: true, timeout: 10000 }
      );
      
      const totalCount = indicatorsRes.data?.indicators?.count || 0;
      
      if (totalCount === 0) {
        setUploadResult({ success: false, message: 'Nenhuma classificação disponível para download.' });
        setUploadResultModalOpen(true);
        return;
      }

      // Load all codez items in pages
      const PAGE_SIZE = 5000;
      const totalPages = Math.ceil(totalCount / PAGE_SIZE);
      const allItems: any[] = [];

      for (let page = 1; page <= totalPages; page++) {
        const res = await axios.get(
          `${baseUrl}/api/profilegen-data/profilegen-codez/${clientId}?page=${page}&limit=${PAGE_SIZE}`,
          { withCredentials: true, timeout: 30000 }
        );
        
        if (res.data?.items && Array.isArray(res.data.items)) {
          allItems.push(...res.data.items);
        }
      }

      if (allItems.length === 0) {
        setUploadResult({ success: false, message: 'Nenhuma classificação disponível para download.' });
        setUploadResultModalOpen(true);
        return;
      }

      // Create CSV headers
      const headers = ['code', 'description', 'process', 'subprocess', 'activity', 'purpose', 'justification'];
      const csvRows = [headers.join(',')];

      // Add data rows
      for (const item of allItems) {
        const row = [
          `"${(item.code || '').replace(/"/g, '""')}"`,
          `"${(item.description || '').replace(/"/g, '""')}"`,
          `"${(item.process || '').replace(/"/g, '""')}"`,
          `"${(item.subprocess || '').replace(/"/g, '""')}"`,
          `"${(item.activity || '').replace(/"/g, '""')}"`,
          `"${(item.purpose || '').replace(/"/g, '""')}"`,
          `"${(item.justification || '').replace(/"/g, '""')}"`,
        ];
        csvRows.push(row.join(','));
      }

      const csvContent = csvRows.join('\n');
      const bom = '\uFEFF'; // UTF-8 BOM to ensure proper encoding
      const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `codez_classifications_${clientId}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setUploadResult({ success: true, message: `Download realizado com sucesso. ${allItems.length} classificações exportadas.` });
      setUploadResultModalOpen(true);
    } catch (err) {
      console.error('Download CSV error:', err);
      setUploadResult({ success: false, message: 'Erro ao gerar o arquivo CSV. Veja o console.' });
      setUploadResultModalOpen(true);
    }
  };

  // Download logs as CSV
  const _downloadLogsCsv = async () => {
    if (!clientId) return;
    
    try {
      // Check if we have data in previews, if not, load it
      let logsData = previews?.logs || [];
      
      // If previews is empty or small, load all logs data
      const totalLogs = indicators?.logs?.count || 0;
      if (logsData.length < totalLogs) {
        // Load all logs progressively
        const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const CHUNK_SIZE = 5000;
        const totalPages = Math.ceil(totalLogs / CHUNK_SIZE);
        logsData = [];
        
        for (let page = 1; page <= totalPages; page++) {
          const logRes = await axios.get(
            `${base}/api/profilegen-data/profilegen-logs/${clientId}?page=${page}&limit=${CHUNK_SIZE}`,
            { withCredentials: true }
          );
          const items = logRes.data?.items || [];
          logsData = [...logsData, ...items];
        }
      }
      
      if (logsData.length === 0) {
        setUploadResult({ success: false, message: 'Nenhum log disponível para download.' });
        setUploadResultModalOpen(true);
        return;
      }

      // Create CSV headers (include all fields that might be present in logs)
      const headers = ['user', 'code', 'process', 'subprocess', 'activity', 'purpose', 'justification', 'type', 'source'];
      const csvRows = [headers.join(',')];

      // Add data rows
      for (const item of logsData) {
        const row = [
          `"${(item.user || '').replace(/"/g, '""')}"`,
          `"${(item.code || '').replace(/"/g, '""')}"`,
          `"${(item.process || '').replace(/"/g, '""')}"`,
          `"${(item.subprocess || '').replace(/"/g, '""')}"`,
          `"${(item.activity || '').replace(/"/g, '""')}"`,
          `"${(item.purpose || '').replace(/"/g, '""')}"`,
          `"${(item.justification || '').replace(/"/g, '""')}"`,
          `"${(item.type || '').replace(/"/g, '""')}"`,
          `"${(item.source || '').replace(/"/g, '""')}"`,
        ];
        csvRows.push(row.join(','));
      }

      const csvContent = csvRows.join('\n');
      const bom = '\uFEFF'; // UTF-8 BOM to ensure proper encoding
      const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `logs_${clientId}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setUploadResult({ success: true, message: `Download realizado com sucesso. ${logsData.length} registros de log exportados.` });
      setUploadResultModalOpen(true);
    } catch (err) {
      console.error('Download logs CSV error:', err);
      setUploadResult({ success: false, message: 'Erro ao gerar o arquivo CSV de logs. Veja o console.' });
      setUploadResultModalOpen(true);
    }
  };

  // Download any step-1 table data as CSV for the current client
  const downloadTableCsv = async (tableKey: string) => {
    if (!clientId) return;

    try {
      setCsvDownloadProgress(prev => ({ ...prev, [tableKey]: { loading: true, progress: 0 } }));
      const tableMeta = tables.find(t => t.key === tableKey);
      const tableLabel = tableMeta?.title || tableKey;
      const setProgress = (value: number) => {
        const safe = Math.max(0, Math.min(100, value));
        setCsvDownloadProgress(prev => ({ ...prev, [tableKey]: { loading: true, progress: safe } }));
      };

      const indicatorsRes = await axios.get(
        `${base}/api/profilegen-data/${tableKey}/${clientId}?indicatorsOnly=true`,
        { withCredentials: true, timeout: 10000 }
      );
      setProgress(5);

      const totalCount = indicatorsRes.data?.indicators?.count || 0;
      if (totalCount <= 0) {
        setCsvDownloadProgress(prev => ({ ...prev, [tableKey]: { loading: false, progress: 0 } }));
        setUploadResult({ success: false, message: `Nenhum dado disponível para download em "${tableLabel}".` });
        setUploadResultModalOpen(true);
        return;
      }

      let tableData: any[] = (previews && Array.isArray(previews[tableKey])) ? previews[tableKey] : [];
      if (tableData.length < totalCount) {
        const CHUNK_SIZE = 100000;
        const CONCURRENCY = 4;
        const totalPages = Math.ceil(totalCount / CHUNK_SIZE);
        const totalSteps = Math.max(1, totalPages + 2); // pages + geração do CSV + download
        let completedSteps = 0;
        tableData = [];

        for (let startPage = 1; startPage <= totalPages; startPage += CONCURRENCY) {
          const endPage = Math.min(startPage + CONCURRENCY - 1, totalPages);
          const pages: number[] = [];
          for (let page = startPage; page <= endPage; page++) pages.push(page);

          const batchResponses = await Promise.all(
            pages.map((page) =>
              axios.get(
                `${base}/api/profilegen-data/${tableKey}/${clientId}?page=${page}&limit=${CHUNK_SIZE}`,
                { withCredentials: true, timeout: 120000 }
              )
            )
          );

          for (const res of batchResponses) {
            const items = res.data?.items || [];
            tableData.push(...items);
          }

          completedSteps += pages.length;
          const pct = Math.round((completedSteps / totalSteps) * 100);
          setProgress(Math.min(90, Math.max(10, pct)));
        }
      } else {
        setProgress(60);
      }

      if (!tableData.length) {
        setCsvDownloadProgress(prev => ({ ...prev, [tableKey]: { loading: false, progress: 0 } }));
        setUploadResult({ success: false, message: `Nenhum dado disponível para download em "${tableLabel}".` });
        setUploadResultModalOpen(true);
        return;
      }

      setProgress(92);

      const preferredHeaders = (tableMeta?.columns || []).map(col => String(col));
      const dynamicHeaders: string[] = [];

      for (const item of tableData) {
        Object.keys(item || {}).forEach((key) => {
          if (key === '_id' || key === '__v') return;
          if (!preferredHeaders.includes(key) && !dynamicHeaders.includes(key)) {
            dynamicHeaders.push(key);
          }
        });
      }

      const headers = [...preferredHeaders, ...dynamicHeaders];
      const escapeCsv = (value: any) => `"${String(value ?? '').replace(/"/g, '""')}"`;
      const csvRows = [headers.join(',')];

      for (const item of tableData) {
        const row = headers.map((header) => escapeCsv(item?.[header]));
        csvRows.push(row.join(','));
      }

      const csvContent = csvRows.join('\n');
      const bom = '\uFEFF';
      const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
      setProgress(97);
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${tableKey}_${clientId}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setProgress(100);
      setTimeout(() => {
        setCsvDownloadProgress(prev => ({ ...prev, [tableKey]: { loading: false, progress: 0 } }));
      }, 500);

      setUploadResult({ success: true, message: `Download realizado com sucesso. ${tableData.length} registros exportados de "${tableLabel}".` });
      setUploadResultModalOpen(true);
    } catch (err) {
      setCsvDownloadProgress(prev => ({ ...prev, [tableKey]: { loading: false, progress: 0 } }));
      console.error(`Download CSV error for table ${tableKey}:`, err);
      setUploadResult({ success: false, message: 'Erro ao gerar o arquivo CSV. Veja o console.' });
      setUploadResultModalOpen(true);
    }
  };

  // Handle CSV upload for codez classifications
  const handleCodezCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const text = String(ev.target?.result || '');
        const { rows, headers } = parseCSV(text);
        
        // Validate required columns
        const requiredColumns = ['code', 'description', 'process', 'subprocess', 'activity'];
        const normalize = (s: string) => String(s || '').toLowerCase().replace(/[^a-z0-9_]/g, '').trim();
        const normalizedHeaders = headers.map(normalize);
        
        const missingColumns = requiredColumns.filter(col => !normalizedHeaders.includes(normalize(col)));
        if (missingColumns.length > 0) {
          setUploadResult({ 
            success: false, 
            message: `Colunas obrigatórias ausentes: ${missingColumns.join(', ')}. Formato esperado: code,description,process,subprocess,activity` 
          });
          setUploadResultModalOpen(true);
          return;
        }

        if (rows.length === 0) {
          setUploadResult({ success: false, message: 'Arquivo CSV está vazio ou não contém dados válidos.' });
          setUploadResultModalOpen(true);
          return;
        }

        // Fetch all codez IDs from backend to match with upload (handles >1000 lines)
        const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const countRes = await axios.get(
          `${baseUrl}/api/profilegen-data/profilegen-codez/${clientId}?indicatorsOnly=true`,
          { withCredentials: true, timeout: 10000 }
        );
        const totalCount = countRes.data?.indicators?.count || 0;
        
        // Build a map of code -> _id from backend
        const codeToIdMap = new Map<string, string>();
        if (totalCount > 0) {
          const PAGE_SIZE = 5000;
          const totalPages = Math.ceil(totalCount / PAGE_SIZE);
          
          for (let page = 1; page <= totalPages; page++) {
            const res = await axios.get(
              `${baseUrl}/api/profilegen-data/profilegen-codez/${clientId}?page=${page}&limit=${PAGE_SIZE}`,
              { withCredentials: true, timeout: 30000 }
            );
            
            if (res.data?.items && Array.isArray(res.data.items)) {
              res.data.items.forEach((item: any) => {
                const code = String(item.code || '').trim();
                if (code && item._id) {
                  codeToIdMap.set(code, item._id);
                }
              });
            }
          }
        }

        // Process and update classifications
        let updatedCount = 0;
        const errors: string[] = [];

        for (const row of rows) {
          try {
            const code = String(row.code || '').trim();
            if (!code) continue;

            // Look up the item ID from the backend map
            const itemId = codeToIdMap.get(code);
            if (!itemId) {
              errors.push(`Código ${code} não encontrado na base de dados`);
              continue;
            }

            // Update the item via API
            const updatePayload = {
              process: String(row.process || '').trim(),
              subprocess: String(row.subprocess || '').trim(),
              activity: String(row.activity || '').trim(),
              purpose: String(row.purpose || '').trim(),
              justification: String(row.justification || '').trim(),
            };

            await axios.patch(
              `${baseUrl}/api/profilegen-data/codez/item/${itemId}`,
              updatePayload,
              { withCredentials: true }
            );
            updatedCount++;
          } catch (err) {
            console.error('Error updating item:', row.code, err);
            errors.push(`Erro ao atualizar ${row.code}`);
          }
        }

        // Refresh the data
        await fetchIndicators('codez');

        // Show result
        let message = `Upload concluído. ${updatedCount} classificações atualizadas.`;
        if (errors.length > 0) {
          message += ` Erros: ${errors.slice(0, 5).join(', ')}`;
          if (errors.length > 5) message += ` e mais ${errors.length - 5}...`;
        }

        setUploadResult({ 
          success: updatedCount > 0, 
          message,
          tableKey: 'codez',
          insertedCount: updatedCount
        });
        setUploadResultModalOpen(true);

      } catch (err) {
        console.error('CSV upload error:', err);
        setUploadResult({ success: false, message: 'Erro ao processar arquivo CSV. Verifique o formato.' });
        setUploadResultModalOpen(true);
      }
    };

    reader.readAsText(file);
    // Reset input value to allow re-uploading the same file
    e.target.value = '';
  };

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(-30px) scale(0.95);
            max-height: 0;
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
            max-height: 500px;
          }
        }
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(30px) scale(0.95);
            max-height: 0;
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
            max-height: 500px;
          }
        }
        @keyframes slideOutLeft {
          from {
            opacity: 1;
            transform: translateX(0) scale(1);
            max-height: 500px;
          }
          to {
            opacity: 0;
            transform: translateX(-30px) scale(0.95);
            max-height: 0;
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes fadeOut {
          from {
            opacity: 1;
            transform: scale(1);
          }
          to {
            opacity: 0;
            transform: scale(0.95);
          }
        }
        .accordion-content {
          transition: max-height 0.3s ease-out, opacity 0.3s ease-out;
          overflow: hidden;
        }
        .accordion-content.collapsed {
          max-height: 0;
          opacity: 0;
        }
        .accordion-content.expanded {
          max-height: none;
          opacity: 1;
        }
        .nomenclature-accordion-content {
          overflow: visible !important;
        }
        .nomenclature-accordion-content.expanded {
          max-height: none !important;
        }
        .nomenclature-accordion-content.collapsed {
          max-height: 0 !important;
          overflow: hidden !important;
        }
        .nomenclature-table {
          font-size: 0.875rem;
        }
        .nomenclature-table td,
        .nomenclature-table th {
          padding: 0.5rem 0.75rem;
          vertical-align: middle;
          line-height: 1.3;
        }
        .nomenclature-table thead th {
          font-size: 0.875rem;
          font-weight: 600;
        }
        .nomenclature-table .table-danger {
          background-color: rgba(220, 53, 69, 0.15) !important;
        }
        .nomenclature-table .table-danger:hover {
          background-color: rgba(220, 53, 69, 0.25) !important;
        }
        .nomenclature-table .table-danger td {
          border-color: rgba(220, 53, 69, 0.3);
        }
        .accordion-header {
          transition: background-color 0.2s ease;
        }
        .accordion-header:hover {
          background-color: var(--bs-gray-200) !important;
        }
        [data-bs-theme="dark"] .accordion-header:hover {
          background-color: var(--bs-gray-700) !important;
        }
        .accordion-icon {
          transition: transform 0.3s ease;
        }
        .accordion-icon.rotated {
          transform: rotate(180deg);
        }
      `}</style>
      
      <AppToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="container py-5" style={{ marginTop: 80, paddingBottom: 120, overflow: 'visible' }}>
        {initialLoading && (
          <div className="gp-modal-backdrop loading">
            <div className="gp-modal card p-4 text-center" style={{ maxWidth: '420px', width: '90%' }}>
              <div className="d-flex justify-content-center mb-3">
                <div className="spinner-border text-primary" role="status" style={{ width: 48, height: 48 }}>
                  <span className="visually-hidden">Carregando...</span>
                </div>
              </div>
              
              <h5 className="mb-3">Carregando Wizard</h5>
              
              <div className="mb-2">
                <div className="progress" style={{ height: '8px' }}>
                  <div 
                    className="progress-bar progress-bar-striped progress-bar-animated" 
                    role="progressbar" 
                    style={{ width: `${loadingProgress}%` }}
                    aria-valuenow={loadingProgress} 
                    aria-valuemin={0} 
                    aria-valuemax={100}
                  ></div>
                </div>
              </div>
              
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">{loadingMessage}</small>
                <small className="text-primary fw-bold">{loadingProgress}%</small>
              </div>
              
              <div className="mt-2">
                <small className="text-muted">Cliente: <strong>{clientId}</strong></small>
              </div>
            </div>
          </div>
        )}

          {/* Log modal */}
          {showLogModal && (
            <div className="users-overlay">
              <div className="users-overlay-box" style={{ width: '760px', maxWidth: '95%' }} onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" aria-label="Fechar" onClick={() => setShowLogModal(false)}>×</button>
                <h4>Log de Mensagens IA (RAW)</h4>
                <div style={{ maxHeight: 520, overflow: 'auto', border: '1px solid #eee', padding: 8, borderRadius: 6, background: '#fff' }}>
                  {logEntries.length === 0 && (<div style={{ color: '#666' }}>Nenhum log registrado ainda.</div>)}
                  {logEntries.map(entry => (
                    <div key={entry.id} style={{ borderBottom: '1px solid #f0f0f0', padding: '8px 4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{entry.direction === 'sent' ? 'ENVIADO' : 'RECEBIDO'} — {entry.title}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>{entry.ts}</div>
                      </div>
                      <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleLogEntry(entry.id)}>{entry.expanded ? '−' : '+'}</button>
                        <div style={{ flex: 1, fontSize: 13, color: '#333' }}>{typeof entry.raw === 'string' ? entry.raw : (entry.raw && entry.raw.summary ? entry.raw.summary : JSON.stringify(entry.raw).slice(0, 200) + (JSON.stringify(entry.raw).length > 200 ? '...' : ''))}</div>
                        {entry.usage && (
                          <div style={{ marginLeft: 8, fontSize: 12, color: '#444' }}>
                            <div style={{ fontWeight: 700 }}>Tokens</div>
                            <div style={{ fontSize: 12 }}>{entry.usage.total_tokens ? `total: ${entry.usage.total_tokens}` : `${entry.usage.prompt_tokens || 0}p / ${entry.usage.completion_tokens || 0}c`}</div>
                          </div>
                        )}
                      </div>
                      {entry.expanded && (
                        <pre style={{ marginTop: 8, background: '#f8f9fa', padding: 8, borderRadius: 6, overflow: 'auto', maxHeight: 360 }}>{JSON.stringify(entry.raw, null, 2)}</pre>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                  <button className="btn btn-secondary" onClick={() => setLogEntries([])}>Limpar</button>
                  <button className="btn btn-primary" onClick={() => setShowLogModal(false)}>Fechar</button>
                </div>
              </div>
            </div>
          )}

          {/* Classification progress modal (similar to benchmark) */}
          {classifyProgressOpen && (
            <div className="users-overlay">
              <div className="users-overlay-box" style={{ width: 720, maxWidth: '95%' }} onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" aria-label="Fechar" onClick={() => { /* do not allow click-to-close */ }}>
                  ×
                </button>
                <h4>Progresso da classificação IA</h4>
                <div style={{ marginBottom: 8 }}>Cliente: <strong>{clientId}</strong></div>
                <div style={{ height: 12, background: '#eee', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                  <div style={{ width: `${totalCount ? Math.round((processedCount / totalCount) * 100) : 0}%`, height: '100%', background: 'linear-gradient(90deg,var(--phoenix-primary),#4a90e2)' }} />
                </div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>{processedCount}/{totalCount} itens processados ({totalCount ? Math.round((processedCount / totalCount) * 100) : 0}%)</div>
                <div style={{ maxHeight: 260, overflow: 'auto', padding: 8, border: '1px solid #f0f0f0', borderRadius: 6, background: '#fff' }}>
                  {classifyLines.length === 0 && (<div style={{ color: '#666' }}>Aguardando envio...</div>)}
                  {classifyLines.map((ln, idx) => (<div key={idx} style={{ fontSize: 13, marginBottom: 6 }}>{ln}</div>))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                  <button className="btn btn-secondary" onClick={() => {
                    // Cancel ongoing classify
                    if (classifyAbortRef.current) classifyAbortRef.current.abort();
                  }}>Cancelar</button>
                  <button className="btn btn-primary" onClick={() => { /* close after short delay if not active */ setClassifyProgressOpen(false); }}>Fechar</button>
                </div>
              </div>
            </div>
          )}
        {loading && (
          <div className="mb-3">
            <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
            <span>Carregando cliente...</span>
          </div>
        )}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h1 className="mb-0">Gerador de Perfis — Wizard</h1>
            <p className="lead mb-0">Etapas para configurar e gerar perfis do cliente</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-secondary gp-uniform-btn" onClick={() => navigate('/gerador-perfis')}>Voltar</button>
            <button className="btn btn-outline-primary" onClick={() => {
              if (!validationDataLoaded) {
                setDashboardAlertOpen(true);
                pushToast({
                  variant: 'warning',
                  title: 'Passo final pendente',
                  message: `Conclua o passo ${getStepCountForModel(client?.profileModel)} (Validação e Download) para acessar o Dashboard completo.`,
                  durationMs: 8000
                });
              } else {
                setDashboardOpen(true);
              }
            }} title="Abrir Dashboard">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h4v8H3zM10 3h4v18h-4zM17 8h4v13h-4z"/></svg>
              <span style={{ marginLeft: 8 }}>Dashboard</span>
            </button>
          </div>
        </div>

        <StepWizard
          steps={getStepLabelsForModel(client?.profileModel)}
          currentStep={step}
          onStepChange={async (nextStep) => {
            const totalSteps = getStepCountForModel(client?.profileModel);
            const targetStep = Math.max(1, Math.min(totalSteps, nextStep));
            if (targetStep === step) return;

            if (step === 1 && targetStep > 1) {
              if (!requiredUploadsSatisfied()) return;
              await updateClientPhase('Carga de dados realizada');
            }

            setStep(targetStep);
          }}
          infoItems={[
            { label: 'Cliente (ID):', value: clientId },
            { label: 'Nome do cliente:', value: client?.clientName || '-' },
            { label: 'Modelo de perfis:', value: client?.profileModel || '-' },
            { label: 'Fase atual:', value: client?.phase || 'Inicial' }
          ]}
          onPrevious={() => setStep(Math.max(1, step - 1))}
          onNext={async () => {
            const totalSteps = getStepCountForModel(client?.profileModel);
            if (step >= totalSteps) {
              showOrgDerivationAlert('Wizard finalizado!', 'success');
              return;
            }

            if (step === 1) {
              if (!requiredUploadsSatisfied()) return;
              await updateClientPhase('Carga de dados realizada');
              setStep(2);
              return;
            }

            setStep(Math.min(totalSteps, step + 1));
          }}
          disablePrevious={step === 1}
          disableNext={step === 1 && !requiredUploadsSatisfied()}
          nextLabel={step >= getStepCountForModel(client?.profileModel) ? 'Concluir' : 'Próximo'}
        />

            <div className="mt-3 gp-step-card gp-step-surface" style={{ minHeight: 300 }}>
            <h5>{getStepTitleForModel(step, client?.profileModel)}</h5>
          <div style={{ marginTop: 12 }}>
            {step === 1 ? (
              <div className="row gp-upload-row">
            {tables.map(t => (
              <div key={t.key} className={`${tables.length <= 4 ? 'col-md-3' : 'col-md-4 col-xl-3'} mb-3`}>
                    <div className="card p-3 h-100">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1">{t.title}
                            <span className="load-status">
                              <span className={`dot ${(indicators[t.key] && indicators[t.key].count) ? 'green' : 'yellow'}`} />
                              <span className="label">{(indicators[t.key] && indicators[t.key].count) ? `${indicators[t.key].count} registros` : 'Nenhum'}</span>
                            </span>
                          </h6>
                          <small className="text-muted">{t.desc}</small>
                        </div>
                      </div>
                      <div className="mt-3">
                        <input 
                          type="file" 
                          accept=".csv,.json,.xlsx,.xls" 
                          onChange={(e) => handleFileInput(e, t.key)} 
                          onClick={handleFileInputClick}
                          disabled={!!uploading[t.key]} 
                        />
                        <div className="mt-2"><small className="text-muted">Formato esperado: {t.columns.join(',')} em CSV ou Excel</small></div>
                        {uploading[t.key] ? (
                          <div className="mt-2"><small className="text-primary">Enviando...</small></div>
                        ) : null}
                      </div>
                      <div className="mt-3 d-flex justify-content-between align-items-center">
                        <div>
                          <small>Registros: {indicators[t.key]?.count ?? '-'}</small>
                        </div>
                        <div className="text-end">
                          <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => fetchIndicators(t.key)}>Atualizar</button>
                          <button className="btn btn-sm btn-danger" onClick={() => { setConfirmClearTable(t.key); setConfirmClearModalOpen(true); }}>Limpar</button>
                        </div>
                      </div>
                      {t.key === 'upcasis' && (
                        <div className="mt-3 d-flex justify-content-center">
                          <button 
                            className="btn btn-sm btn-outline-info w-100" 
                            onClick={handleLogSync}
                            disabled={logSyncLoading || !indicators[t.key]?.count}
                            title="Sincronizar utilização baseada nos logs de acesso"
                          >
                            {logSyncLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Processando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-sync-alt me-2"></i>
                                Log Sync
                              </>
                            )}
                          </button>
                        </div>
                      )}
                      <div className="mt-3 d-flex justify-content-between align-items-center">
                        <div>
                          <small className="text-muted">Preview:</small>
                        </div>
                        <div className="text-end">
                          <div>
                            {indicators[t.key]?.count > 0 && (
                              (() => {
                                const downloadState = csvDownloadProgress[t.key];
                                const isDownloading = Boolean(downloadState?.loading);
                                const progressValue = downloadState?.progress || 0;
                                return (
                              <button 
                                className="btn btn-sm btn-outline-success me-2 position-relative overflow-hidden"
                                onClick={() => downloadTableCsv(t.key)}
                                title={`Download CSV de ${t.title}`}
                                disabled={isDownloading}
                              >
                                {isDownloading && (
                                  <span
                                    aria-hidden="true"
                                    style={{
                                      position: 'absolute',
                                      left: 0,
                                      bottom: 0,
                                      height: 3,
                                      width: `${progressValue}%`,
                                      background: '#198754',
                                      transition: 'width 0.2s ease'
                                    }}
                                  />
                                )}
                                <span style={{ position: 'relative', zIndex: 1 }}>
                                  {isDownloading ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                      Baixando {progressValue}%
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-download me-1"></i>
                                      Download CSV
                                    </>
                                  )}
                                </span>
                              </button>
                                );
                              })()
                            )}
                            <button className="btn btn-sm btn-outline-primary" onClick={async () => { 
                              setPreviewModalTable(t.key); 
                              setPreviewModalOpen(true);
                              
                              // Check if needs to load all data
                              const totalCount = indicators[t.key]?.count || 0;
                              if (totalCount > 1000) {
                                // Load all data progressively for search functionality
                                setPreviewLoadingAll(true);
                                setPreviewLoadingProgress({ loaded: 0, total: totalCount });
                                
                                try {
                                  await fetchAllDataProgressively(t.key, (data) => {
                                    setPreviews(prev => ({ ...prev, [t.key]: data }));
                                    setPreviewLoadingProgress({ loaded: data.length, total: totalCount });
                                  });
                                } catch (err) {
                                  console.error('Error loading preview data:', err);
                                } finally {
                                  setPreviewLoadingAll(false);
                                }
                              } else {
                                // For smaller datasets, load normally
                                await fetchIndicators(t.key);
                              }
                            }}>Visualizar</button>
                          </div>
                          {t.key === 'userxa' && (
                            <div className="mt-2">
                              <button
                                type="button"
                                className={`btn btn-sm ${userAreaValidationIssues.hasIssues ? 'btn-danger' : 'btn-outline-warning'}`}
                                onClick={showUserAreaDiagnostic}
                                disabled={diagnosticLoadingUserArea}
                                title={userAreaValidationIssues.hasIssues ? 'Inconsistências detectadas! Clique para ver detalhes.' : 'Executar diagnóstico de validação'}
                              >
                                {diagnosticLoadingUserArea ? (
                                  <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                ) : (
                                  <i className={`fas ${userAreaValidationIssues.hasIssues ? 'fa-exclamation-triangle' : 'fa-stethoscope'} me-1`}></i>
                                )}
                                Diagnóstico
                                {userAreaValidationIssues.hasIssues && !diagnosticLoadingUserArea && (
                                  <span className="badge bg-white text-danger ms-1">
                                    {userAreaValidationIssues.usersInAreaNotInLogs.length + userAreaValidationIssues.usersInLogsNotInArea.length + userAreaValidationIssues.usersWithMultipleAreas.length}
                                  </span>
                                )}
                              </button>
                            </div>
                          )}
                          {t.key === 'userxpos' && (
                            <div className="mt-2">
                              <button
                                type="button"
                                className={`btn btn-sm ${userPositionValidationIssues.hasIssues ? 'btn-danger' : 'btn-outline-warning'}`}
                                onClick={showUserPositionDiagnostic}
                                disabled={diagnosticLoadingUserPosition}
                                title={userPositionValidationIssues.hasIssues ? 'Inconsistências detectadas! Clique para ver detalhes.' : 'Executar diagnóstico de validação'}
                              >
                                {diagnosticLoadingUserPosition ? (
                                  <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                ) : (
                                  <i className={`fas ${userPositionValidationIssues.hasIssues ? 'fa-exclamation-triangle' : 'fa-stethoscope'} me-1`}></i>
                                )}
                                Diagnóstico
                                {userPositionValidationIssues.hasIssues && !diagnosticLoadingUserPosition && (
                                  <span className="badge bg-white text-danger ms-1">
                                    {userPositionValidationIssues.usersInPositionNotInLogs.length + userPositionValidationIssues.usersInLogsNotInPosition.length + userPositionValidationIssues.usersWithMultiplePositions.length}
                                  </span>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              ) : step === 2 ? (
                <div>
                  {/* Render sections based on profile model */}
                  {/* Atividade Simples: Only "Perfis Simples/Mestre/Derivados" */}
                  {/* Posição/Cargo Simples: Only "Perfis Compostos" */}
                  {/* Posição Composto + Atividade Simples: Both sections */}
                  
                  {/* Section 1: Padrão de nomenclatura - Perfis Simples/Mestre/Derivados */}
                  {(String(client?.profileModel) === 'Atividade Simples' || String(client?.profileModel) === 'Posição Composto + Atividade Simples') && (
                    <div className="mb-4">
                      {/* Accordion Header for Section 1 */}
                      <div 
                        className="border rounded-top cursor-pointer p-3 bg-body-secondary d-flex justify-content-between align-items-center accordion-header"
                        onClick={() => toggleStep2Section('nomenclaturaSimples')}
                        style={{ cursor: 'pointer' }}
                      >
                        <div>
                          <h5 className="text-primary mb-1 d-flex align-items-center">
                            <i className="fas fa-tag me-2"></i>
                            Padrão de nomenclatura - Perfis Simples/Mestre/Derivados
                          </h5>
                          <p className="text-body-secondary mb-0 small">
                            Configure o padrão de nomenclatura para perfis simples, mestre e derivados
                          </p>
                        </div>
                        <i className={`fas fa-chevron-down text-primary accordion-icon ${step2SectionExpanded.nomenclaturaSimples ? 'rotated' : ''}`}></i>
                      </div>

                      {/* Collapsible Content for Section 1 */}
                      <div className={`accordion-content ${step2SectionExpanded.nomenclaturaSimples ? 'expanded' : 'collapsed'}`}>
                        <div className="border border-top-0 rounded-bottom p-3">

                          {masterRecordsLoading ? (
                            <div className="text-center py-3">
                              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Carregando...</span></div>
                              <p className="text-muted mt-2">Carregando dados consolidados...</p>
                            </div>

                            
                          ) : null}

                      <div className="row">
                        {/* Left Panel - Available Elements */}
                        <div className="col-md-7">
                          <h6 className="mb-3">Elementos Disponíveis</h6>
                          
                          <div className="row">
                            {/* Prefix and Suffix */}
                            <div className="col-md-4">
                              <div className="mb-3">
                                <label className="form-label fw-bold small">Prefixo e Sufixo</label>
                                <div className="d-flex flex-column gap-2">
                                  {/* Prefix */}
                                  <div className="border rounded p-2">
                                    <div className="mb-1 d-flex justify-content-between align-items-center">
                                      <small className="fw-bold">Prefixo</small>
                                      <button 
                                        className="btn btn-primary d-flex align-items-center justify-content-center"
                                        style={{ 
                                          width: '24px', 
                                          height: '24px', 
                                          borderRadius: '50%', 
                                          padding: '0',
                                          margin: '0',
                                          fontSize: '14px',
                                          lineHeight: '1',
                                          fontWeight: 'bold',
                                          border: 'none',
                                          minWidth: '24px',
                                          minHeight: '24px'
                                        }}
                                        onClick={() => setPrefixPatternsModalOpen(true)}
                                        title="Configurar padrões de prefixo para tipos de perfil"
                                      >
                                        +
                                      </button>
                                    </div>
                                    <div className="row mb-2">
                                      <div className="col-6">
                                        <label className="form-label small">Qtd:</label>
                                        <input 
                                          type="number" 
                                          className="form-control form-control-sm" 
                                          value={fieldConfigs.prefixo.qtd}
                                          min="1" 
                                          max={getMaxAvailableLength('prefixo')}
                                          onChange={(e) => {
                                            const newLength = parseInt(e.target.value) || 1;
                                            const newConfig = { ...fieldConfigs.prefixo, qtd: newLength };
                                            setFieldConfigs(prev => ({ ...prev, prefixo: newConfig }));
                                            updateElementInConstructor('prefix', 'prefixo', newLength, fieldConfigs.prefixo.char);
                                          }}
                                        />
                                      </div>
                                      <div className="col-6">
                                        <label className="form-label small">Char:</label>
                                        <input 
                                          type="text" 
                                          className="form-control form-control-sm" 
                                          value={fieldConfigs.prefixo.char}
                                          maxLength={1}
                                          style={{ textAlign: 'center', textTransform: 'uppercase' }}
                                          onChange={(e) => {
                                            const newChar = e.target.value.toUpperCase();
                                            const newConfig = { ...fieldConfigs.prefixo, char: newChar };
                                            setFieldConfigs(prev => ({ ...prev, prefixo: newConfig }));
                                            updateElementInConstructor('prefix', 'prefixo', fieldConfigs.prefixo.qtd, newChar);
                                          }}
                                        />
                                      </div>
                                    </div>
                                    <div 
                                      className="badge bg-primary text-white p-1 w-100 text-center user-select-none"
                                      style={{ cursor: 'grab', fontSize: '0.7em' }}
                                      draggable
                                      onDragStart={(e) => {
                                        e.dataTransfer.setData('text/plain', JSON.stringify({
                                          type: 'prefix',
                                          value: 'prefixo',
                                          label: `Prefixo`,
                                          maxLength: fieldConfigs.prefixo.qtd,
                                          displayChar: fieldConfigs.prefixo.char
                                        }));
                                      }}
                                    >
                                      Prefixo
                                    </div>
                                  </div>

                                  {/* Suffix */}
                                  <div className="border rounded p-2">
                                    <div className="mb-1">
                                      <small className="fw-bold">Sufixo</small>
                                    </div>
                                    <div className="row mb-2">
                                      <div className="col-6">
                                        <label className="form-label small">Qtd:</label>
                                        <input 
                                          type="number" 
                                          className="form-control form-control-sm" 
                                          value={fieldConfigs.sufixo.qtd}
                                          min="1" 
                                          max={getMaxAvailableLength('sufixo')}
                                          onChange={(e) => {
                                            const newLength = parseInt(e.target.value) || 1;
                                            const newConfig = { ...fieldConfigs.sufixo, qtd: newLength };
                                            setFieldConfigs(prev => ({ ...prev, sufixo: newConfig }));
                                            updateElementInConstructor('suffix', 'sufixo', newLength, fieldConfigs.sufixo.char);
                                          }}
                                        />
                                      </div>
                                      <div className="col-6">
                                        <label className="form-label small">Char:</label>
                                        <input 
                                          type="text" 
                                          className="form-control form-control-sm" 
                                          value={fieldConfigs.sufixo.char}
                                          maxLength={1}
                                          style={{ textAlign: 'center', textTransform: 'uppercase' }}
                                          onChange={(e) => {
                                            const newChar = e.target.value.toUpperCase();
                                            const newConfig = { ...fieldConfigs.sufixo, char: newChar };
                                            setFieldConfigs(prev => ({ ...prev, sufixo: newConfig }));
                                            updateElementInConstructor('suffix', 'sufixo', fieldConfigs.sufixo.qtd, newChar);
                                          }}
                                        />
                                      </div>
                                    </div>
                                    <div 
                                      className="badge bg-warning text-dark p-1 w-100 text-center user-select-none"
                                      style={{ cursor: 'grab', fontSize: '0.7em' }}
                                      draggable
                                      onDragStart={(e) => {
                                        e.dataTransfer.setData('text/plain', JSON.stringify({
                                          type: 'suffix',
                                          value: 'sufixo',
                                          label: `Sufixo`,
                                          maxLength: fieldConfigs.sufixo.qtd,
                                          displayChar: fieldConfigs.sufixo.char
                                        }));
                                      }}
                                    >
                                      Sufixo
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Data Elements */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label fw-bold small">Elementos de Dados</label>
                                <div className="row">
                                  {[
                                    { key: 'area', label: 'Área', defaultLength: 4, defaultChar: 'A' },
                                    { key: 'processo', label: 'Processo', defaultLength: 6, defaultChar: 'P' },
                                    { key: 'subprocesso', label: 'Subprocesso', defaultLength: 6, defaultChar: 'S' },
                                    { key: 'atividade', label: 'Atividade', defaultLength: 8, defaultChar: 'T' }
                                  ].map(element => (
                                    <div key={element.key} className="col-6 mb-2">
                                      <div className="border rounded p-2">
                                        <div className="mb-1">
                                          <small className="fw-bold">{element.label}</small>
                                        </div>
                                        <div className="row mb-2">
                                          <div className="col-6">
                                            <label className="form-label small">Qtd:</label>
                                            <input 
                                              type="number" 
                                              className="form-control form-control-sm" 
                                              value={fieldConfigs[element.key as keyof typeof fieldConfigs]?.qtd || element.defaultLength}
                                              min="1" 
                                              max={getMaxAvailableLength(element.key as 'area' | 'processo' | 'subprocesso' | 'atividade')}
                                              onChange={(e) => {
                                                const newLength = parseInt(e.target.value) || 1;
                                                const fieldKey = element.key as keyof typeof fieldConfigs;
                                                const newConfig = { ...fieldConfigs[fieldKey], qtd: newLength };
                                                setFieldConfigs(prev => ({ ...prev, [fieldKey]: newConfig }));
                                                updateElementInConstructor('data', element.key, newLength, fieldConfigs[fieldKey].char);
                                              }}
                                            />
                                          </div>
                                          <div className="col-6">
                                            <label className="form-label small">Char:</label>
                                            <input 
                                              type="text" 
                                              className="form-control form-control-sm" 
                                              value={fieldConfigs[element.key as keyof typeof fieldConfigs]?.char || element.defaultChar}
                                              maxLength={1}
                                              style={{ textAlign: 'center', textTransform: 'uppercase' }}
                                              onChange={(e) => {
                                                const newChar = e.target.value.toUpperCase();
                                                const fieldKey = element.key as keyof typeof fieldConfigs;
                                                const newConfig = { ...fieldConfigs[fieldKey], char: newChar };
                                                setFieldConfigs(prev => ({ ...prev, [fieldKey]: newConfig }));
                                                updateElementInConstructor('data', element.key, fieldConfigs[fieldKey].qtd, newChar);
                                              }}
                                            />
                                          </div>
                                        </div>
                                        <div 
                                          className="badge bg-secondary text-white p-1 w-100 text-center user-select-none"
                                          style={{ cursor: 'grab', fontSize: '0.7em' }}
                                          draggable
                                          onDragStart={(e) => {
                                            const fieldKey = element.key as keyof typeof fieldConfigs;
                                            e.dataTransfer.setData('text/plain', JSON.stringify({
                                              type: 'data',
                                              value: element.key,
                                              label: element.label,
                                              maxLength: fieldConfigs[fieldKey]?.qtd || element.defaultLength,
                                              displayChar: fieldConfigs[fieldKey]?.char || element.defaultChar
                                            }));
                                          }}
                                        >
                                          {element.label}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* Separators */}
                            <div className="col-md-2">
                              <div className="mb-3">
                                <label className="form-label fw-bold small">Separadores</label>
                                <div className="d-flex flex-column gap-1">
                                  {['.', '-', '_', ':'].map(sep => (
                                    <div 
                                      key={sep}
                                      className="badge bg-info text-white p-1 text-center user-select-none"
                                      style={{ cursor: 'grab', fontFamily: 'monospace', fontSize: '0.7em' }}
                                      draggable
                                      onDragStart={(e) => {
                                        e.dataTransfer.setData('text/plain', JSON.stringify({
                                          type: 'separator',
                                          value: sep,
                                          label: sep,
                                          maxLength: 1,
                                          displayChar: sep
                                        }));
                                      }}
                                    >
                                      {sep}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right Panel - Constructor and Preview */}
                        <div className="col-md-5">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Construtor</h6>
                            <div className="d-flex align-items-center gap-1">
                              <label className="form-label mb-0 small">Max:</label>
                              <input 
                                type="number" 
                                className="form-control form-control-sm" 
                                style={{ width: '80px' }}
                                value={maxTotalLength}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value) || 30;
                                  setMaxTotalLength(v);
                                  setPatternNameMaxChar(v);
                                  schedulePersistPatternNameMaxChar(v);
                                }}
                                onBlur={async () => {
                                  if (!clientId) return;
                                  const newVal = Number(patternNameMaxChar || maxTotalLength || 30);
                                  try { await persistPatternNameMaxChar(newVal); } catch (err) { console.warn('Erro ao persistir pattern_name_max_char', err); }
                                }}
                                min="10" 
                                max="64"
                              />
                              <span className="small text-muted">chars</span>
                            </div>
                          </div>

                          {/* Constructor Elements Display */}
                          {constructorElements.length > 0 && (
                            <div className="mb-2">
                              <div className="d-flex flex-wrap gap-1 p-2 border rounded bg-light">
                                {constructorElements.map((element, index) => (
                                  <div 
                                    key={element.id} 
                                    className="d-flex align-items-center"
                                    draggable
                                    onDragStart={(e) => {
                                      setDraggedItemIndex(index);
                                      e.dataTransfer.effectAllowed = 'move';
                                    }}
                                    onDragEnd={() => setDraggedItemIndex(null)}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'move';
                                    }}
                                    onDrop={async (e) => {
                                      e.preventDefault();
                                      if (draggedItemIndex !== null && draggedItemIndex !== index) {
                                        await reorderConstructorElements(draggedItemIndex, index);
                                      }
                                    }}
                                    style={{ 
                                      opacity: draggedItemIndex === index ? 0.5 : 1,
                                      cursor: 'move'
                                    }}
                                  >
                                    <span 
                                      className={`badge p-1 text-white ${
                                        element.type === 'prefix' ? 'bg-primary' : 
                                        element.type === 'suffix' ? 'bg-warning text-dark' :
                                        element.type === 'separator' ? 'bg-info' : 'bg-secondary'
                                      }`}
                                      style={{ fontFamily: element.type === 'separator' ? 'monospace' : 'inherit', fontSize: '0.7em' }}
                                    >
                                      <i className="fas fa-grip-vertical me-1" style={{ fontSize: '0.6em', opacity: 0.7 }}></i>
                                      {element.type === 'separator' ? element.displayChar : element.label}
                                      <button 
                                        className="btn-close btn-close-white ms-1" 
                                        style={{ fontSize: '0.5em' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeElementFromConstructor(element.id);
                                        }}
                                        aria-label="Remover"
                                      ></button>
                                    </span>
                                  </div>
                                ))}
                                <button 
                                  className="btn btn-sm btn-outline-danger py-0 px-1"
                                  style={{ fontSize: '0.7em' }}
                                  onClick={() => setConstructorElements([])}
                                  disabled={constructorElements.length === 0}
                                >
                                  Limpar
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Drop Zone */}
                          <div 
                            className="border rounded p-2 mb-2"
                            style={{ 
                              minHeight: constructorElements.length > 0 ? '40px' : '60px', 
                              backgroundColor: '#f8f9fa',
                              border: '2px dashed #dee2e6'
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.currentTarget.style.backgroundColor = '#e3f2fd';
                              e.currentTarget.style.borderColor = '#2196f3';
                            }}
                            onDragLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                              e.currentTarget.style.borderColor = '#dee2e6';
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                              e.currentTarget.style.borderColor = '#dee2e6';
                              
                              try {
                                const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                                addElementToConstructor({
                                  type: data.type,
                                  value: data.value,
                                  label: data.label,
                                  maxLength: data.maxLength,
                                  displayChar: data.displayChar || 'X'
                                });
                              } catch (err) {
                                console.error('Invalid drop data');
                              }
                            }}
                          >
                            <div className="text-center text-muted">
                              <i className="fas fa-plus-circle mb-1"></i>
                              <p className="mb-0 small">
                                {constructorElements.length === 0 ? 
                                  'Arraste elementos aqui' :
                                  'Arraste mais elementos'
                                }
                              </p>
                            </div>
                          </div>

                          {/* Preview */}
                          <div className="mb-2">
                            <label className="form-label fw-bold small">Preview</label>
                            <div className={`alert p-2 border ${calculateTotalLength() > maxTotalLength ? 'alert-warning' : 'alert-light'}`}>
                              <div className="d-flex flex-column">
                                <code style={{ fontSize: '0.9em', fontWeight: 'bold' }}>
                                  {generatePreviewPattern()}
                                </code>
                                <span className={`small ${calculateTotalLength() > maxTotalLength ? 'text-danger fw-bold' : 'text-muted'}`}>
                                  ({calculateTotalLength()}/{maxTotalLength} chars)
                                </span>
                              </div>
                              {calculateTotalLength() > maxTotalLength && (
                                <small className="text-danger mt-1 d-block">
                                  <i className="fas fa-exclamation-triangle me-1"></i>
                                  Excede tamanho máximo!
                                </small>
                              )}
                            </div>
                          </div>

                          {/* Save Button */}
                          <div className="mt-3">
                            <button 
                              className="btn btn-primary btn-sm w-100"
                              style={{ 
                                fontSize: '0.875rem',
                                padding: '0.25rem 0.5rem',
                                height: '31px',
                                lineHeight: '1.5'
                              }}
                              onClick={savePatternComposition}
                              disabled={constructorElements.length === 0 || calculateTotalLength() > maxTotalLength}
                            >
                              <i className="fas fa-save me-1"></i>
                              Salvar Composição
                            </button>
                            {calculateTotalLength() > maxTotalLength && (
                              <small className="text-danger d-block mt-1 text-center">
                                <i className="fas fa-info-circle me-1"></i>
                                Reduza o comprimento para salvar
                              </small>
                            )}
                          </div>
                        </div>
                      </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 2: Padrão de nomenclatura - Perfis Compostos */}
                  {(String(client?.profileModel) === 'Posição/Cargo Simples' || String(client?.profileModel) === 'Posição Composto + Atividade Simples') && (
                    <div className="mb-4">
                      {/* Accordion Header for Section 2 */}
                      <div 
                        className="border rounded-top cursor-pointer p-3 bg-body-secondary d-flex justify-content-between align-items-center accordion-header"
                        onClick={() => toggleStep2Section('nomenclaturaCompostos')}
                        style={{ cursor: 'pointer' }}
                      >
                        <div>
                          <h5 className="text-success mb-1 d-flex align-items-center">
                            <i className="fas fa-layer-group me-2"></i>
                            {String(client?.profileModel) === 'Posição/Cargo Simples' 
                              ? 'Padrão de nomenclatura - Perfis Simples'
                              : 'Padrão de nomenclatura - Perfis Compostos'
                            }
                          </h5>
                          <p className="text-body-secondary mb-0 small">
                            {String(client?.profileModel) === 'Posição/Cargo Simples'
                              ? 'Configure o padrão de nomenclatura para perfis simples baseados em posições/cargos'
                              : 'Configure o padrão de nomenclatura para perfis compostos baseados em posições/cargos'
                          }
                        </p>
                        </div>
                        <i className={`fas fa-chevron-down text-success accordion-icon ${step2SectionExpanded.nomenclaturaCompostos ? 'rotated' : ''}`}></i>
                      </div>

                      {/* Collapsible Content for Section 2 */}
                      <div className={`accordion-content ${step2SectionExpanded.nomenclaturaCompostos ? 'expanded' : 'collapsed'}`}>
                        <div className="border border-top-0 rounded-bottom p-3">

                      <div className="row">
                        {/* Left Panel - Available Elements */}
                        <div className="col-md-7">
                          <h6 className="mb-3">Elementos Disponíveis</h6>
                          
                          <div className="row">
                            {/* Prefix and Suffix */}
                            <div className="col-md-4">
                              <div className="mb-3">
                                <label className="form-label fw-bold small">Prefixo e Sufixo</label>
                                <div className="d-flex flex-column gap-2">
                                  {/* Prefix */}
                                  <div className="border rounded p-2">
                                    <div className="mb-1 d-flex justify-content-between align-items-center">
                                      <small className="fw-bold">Prefixo</small>
                                      <button 
                                        className="btn btn-success d-flex align-items-center justify-content-center"
                                        style={{ 
                                          width: '24px', 
                                          height: '24px', 
                                          borderRadius: '50%', 
                                          padding: '0',
                                          margin: '0',
                                          fontSize: '14px',
                                          lineHeight: '1',
                                          fontWeight: 'bold',
                                          border: 'none',
                                          minWidth: '24px',
                                          minHeight: '24px'
                                        }}
                                        onClick={() => setCompositePrefixPatternsModalOpen(true)}
                                        title="Configurar padrões de prefixo para tipos de perfil compostos"
                                      >
                                        +
                                      </button>
                                    </div>
                                    <div className="row mb-2">
                                      <div className="col-6">
                                        <label className="form-label small">Qtd:</label>
                                        <input 
                                          type="number" 
                                          className="form-control form-control-sm" 
                                          value={compositeFieldConfigs.prefixo.qtd}
                                          min="1" 
                                          max={getCompositeMaxAvailableLength('prefixo')}
                                          onChange={(e) => {
                                            const newLength = parseInt(e.target.value) || 1;
                                            const newConfig = { ...compositeFieldConfigs.prefixo, qtd: newLength };
                                            setCompositeFieldConfigs(prev => ({ ...prev, prefixo: newConfig }));
                                            updateCompositeElementInConstructor('prefix', 'prefixo', newLength, compositeFieldConfigs.prefixo.char);
                                          }}
                                        />
                                      </div>
                                      <div className="col-6">
                                        <label className="form-label small">Char:</label>
                                        <input 
                                          type="text" 
                                          className="form-control form-control-sm" 
                                          value={compositeFieldConfigs.prefixo.char}
                                          maxLength={1}
                                          style={{ textAlign: 'center', textTransform: 'uppercase' }}
                                          onChange={(e) => {
                                            const newChar = e.target.value.toUpperCase();
                                            const newConfig = { ...compositeFieldConfigs.prefixo, char: newChar };
                                            setCompositeFieldConfigs(prev => ({ ...prev, prefixo: newConfig }));
                                            updateCompositeElementInConstructor('prefix', 'prefixo', compositeFieldConfigs.prefixo.qtd, newChar);
                                          }}
                                        />
                                      </div>
                                    </div>
                                    <div 
                                      className="badge bg-success text-white p-1 w-100 text-center user-select-none"
                                      style={{ cursor: 'grab', fontSize: '0.7em' }}
                                      draggable
                                      onDragStart={(e) => {
                                        e.dataTransfer.setData('text/plain', JSON.stringify({
                                          type: 'prefix',
                                          value: 'prefixo',
                                          label: `Prefixo`,
                                          maxLength: compositeFieldConfigs.prefixo.qtd,
                                          displayChar: compositeFieldConfigs.prefixo.char
                                        }));
                                      }}
                                    >
                                      Prefixo
                                    </div>
                                  </div>

                                  {/* Suffix */}
                                  <div className="border rounded p-2">
                                    <div className="mb-1">
                                      <small className="fw-bold">Sufixo</small>
                                    </div>
                                    <div className="row mb-2">
                                      <div className="col-6">
                                        <label className="form-label small">Qtd:</label>
                                        <input 
                                          type="number" 
                                          className="form-control form-control-sm" 
                                          value={compositeFieldConfigs.sufixo.qtd}
                                          min="1" 
                                          max={getCompositeMaxAvailableLength('sufixo')}
                                          onChange={(e) => {
                                            const newLength = parseInt(e.target.value) || 1;
                                            const newConfig = { ...compositeFieldConfigs.sufixo, qtd: newLength };
                                            setCompositeFieldConfigs(prev => ({ ...prev, sufixo: newConfig }));
                                            updateCompositeElementInConstructor('suffix', 'sufixo', newLength, compositeFieldConfigs.sufixo.char);
                                          }}
                                        />
                                      </div>
                                      <div className="col-6">
                                        <label className="form-label small">Char:</label>
                                        <input 
                                          type="text" 
                                          className="form-control form-control-sm" 
                                          value={compositeFieldConfigs.sufixo.char}
                                          maxLength={1}
                                          style={{ textAlign: 'center', textTransform: 'uppercase' }}
                                          onChange={(e) => {
                                            const newChar = e.target.value.toUpperCase();
                                            const newConfig = { ...compositeFieldConfigs.sufixo, char: newChar };
                                            setCompositeFieldConfigs(prev => ({ ...prev, sufixo: newConfig }));
                                            updateCompositeElementInConstructor('suffix', 'sufixo', compositeFieldConfigs.sufixo.qtd, newChar);
                                          }}
                                        />
                                      </div>
                                    </div>
                                    <div 
                                      className="badge bg-warning text-dark p-1 w-100 text-center user-select-none"
                                      style={{ cursor: 'grab', fontSize: '0.7em' }}
                                      draggable
                                      onDragStart={(e) => {
                                        e.dataTransfer.setData('text/plain', JSON.stringify({
                                          type: 'suffix',
                                          value: 'sufixo',
                                          label: `Sufixo`,
                                          maxLength: compositeFieldConfigs.sufixo.qtd,
                                          displayChar: compositeFieldConfigs.sufixo.char
                                        }));
                                      }}
                                    >
                                      Sufixo
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Data Elements - Níveis */}
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label fw-bold small">Elementos de Dados</label>
                                <div className="row">
                                  {[
                                    { key: 'n1', label: 'Nível 1', defaultLength: 4, defaultChar: '1' },
                                    { key: 'n2', label: 'Nível 2', defaultLength: 6, defaultChar: '2' },
                                    { key: 'n3', label: 'Nível 3', defaultLength: 6, defaultChar: '3' },
                                    { key: 'n4', label: 'Nível 4', defaultLength: 8, defaultChar: '4' }
                                  ].map(element => (
                                    <div key={element.key} className="col-6 mb-2">
                                      <div className="border rounded p-2">
                                        <div className="mb-1">
                                          <small className="fw-bold">{element.label}</small>
                                        </div>
                                        <div className="row mb-2">
                                          <div className="col-6">
                                            <label className="form-label small">Qtd:</label>
                                            <input 
                                              type="number" 
                                              className="form-control form-control-sm" 
                                              value={compositeFieldConfigs[element.key as keyof typeof compositeFieldConfigs]?.qtd || element.defaultLength}
                                              min="1" 
                                              max={getCompositeMaxAvailableLength(element.key as 'n1' | 'n2' | 'n3' | 'n4')}
                                              onChange={(e) => {
                                                const newLength = parseInt(e.target.value) || 1;
                                                const fieldKey = element.key as keyof typeof compositeFieldConfigs;
                                                const newConfig = { ...compositeFieldConfigs[fieldKey], qtd: newLength };
                                                setCompositeFieldConfigs(prev => ({ ...prev, [fieldKey]: newConfig }));
                                                updateCompositeElementInConstructor('data', element.key, newLength, compositeFieldConfigs[fieldKey].char);
                                              }}
                                            />
                                          </div>
                                          <div className="col-6">
                                            <label className="form-label small">Char:</label>
                                            <input 
                                              type="text" 
                                              className="form-control form-control-sm" 
                                              value={compositeFieldConfigs[element.key as keyof typeof compositeFieldConfigs]?.char || element.defaultChar}
                                              maxLength={1}
                                              style={{ textAlign: 'center', textTransform: 'uppercase' }}
                                              onChange={(e) => {
                                                const newChar = e.target.value.toUpperCase();
                                                const fieldKey = element.key as keyof typeof compositeFieldConfigs;
                                                const newConfig = { ...compositeFieldConfigs[fieldKey], char: newChar };
                                                setCompositeFieldConfigs(prev => ({ ...prev, [fieldKey]: newConfig }));
                                                updateCompositeElementInConstructor('data', element.key, compositeFieldConfigs[fieldKey].qtd, newChar);
                                              }}
                                            />
                                          </div>
                                        </div>
                                        <div 
                                          className="badge bg-dark text-white p-1 w-100 text-center user-select-none"
                                          style={{ cursor: 'grab', fontSize: '0.7em' }}
                                          draggable
                                          onDragStart={(e) => {
                                            const fieldKey = element.key as keyof typeof compositeFieldConfigs;
                                            e.dataTransfer.setData('text/plain', JSON.stringify({
                                              type: 'data',
                                              value: element.key,
                                              label: element.label,
                                              maxLength: compositeFieldConfigs[fieldKey]?.qtd || element.defaultLength,
                                              displayChar: compositeFieldConfigs[fieldKey]?.char || element.defaultChar
                                            }));
                                          }}
                                        >
                                          {element.label}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* Separators */}
                            <div className="col-md-2">
                              <div className="mb-3">
                                <label className="form-label fw-bold small">Separadores</label>
                                <div className="d-flex flex-column gap-1">
                                  {['.', '-', '_', ':'].map(sep => (
                                    <div 
                                      key={sep}
                                      className="badge bg-info text-white p-1 text-center user-select-none"
                                      style={{ cursor: 'grab', fontFamily: 'monospace', fontSize: '0.7em' }}
                                      draggable
                                      onDragStart={(e) => {
                                        e.dataTransfer.setData('text/plain', JSON.stringify({
                                          type: 'separator',
                                          value: sep,
                                          label: sep,
                                          maxLength: 1,
                                          displayChar: sep
                                        }));
                                      }}
                                    >
                                      {sep}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right Panel - Constructor and Preview */}
                        <div className="col-md-5">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Construtor</h6>
                            <div className="d-flex align-items-center gap-1">
                              <label className="form-label mb-0 small">Max:</label>
                              <input 
                                type="number" 
                                className="form-control form-control-sm" 
                                style={{ width: '80px' }}
                                value={compositeMaxTotalLength}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value) || 30;
                                  setCompositeMaxTotalLength(v);
                                  setPatternNameMaxChar(v);
                                  schedulePersistPatternNameMaxChar(v);
                                }}
                                onBlur={async () => {
                                  if (!clientId) return;
                                  const newVal = Number(patternNameMaxChar || compositeMaxTotalLength || 30);
                                  try { await persistPatternNameMaxChar(newVal); } catch (err) { console.warn('Erro ao persistir pattern_name_max_char', err); }
                                }}
                                min="10" 
                                max="64"
                              />
                              <span className="small text-muted">chars</span>
                            </div>
                          </div>

                          {/* Constructor Elements Display */}
                          {compositeConstructorElements.length > 0 && (
                            <div className="mb-2">
                              <div className="d-flex flex-wrap gap-1 p-2 border rounded bg-light">
                                {compositeConstructorElements.map((element, index) => (
                                  <div 
                                    key={element.id} 
                                    className="d-flex align-items-center"
                                    draggable
                                    onDragStart={(e) => {
                                      setCompositeDraggedItemIndex(index);
                                      e.dataTransfer.effectAllowed = 'move';
                                    }}
                                    onDragEnd={() => setCompositeDraggedItemIndex(null)}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'move';
                                    }}
                                    onDrop={async (e) => {
                                      e.preventDefault();
                                      if (compositeDraggedItemIndex !== null && compositeDraggedItemIndex !== index) {
                                        await reorderCompositeConstructorElements(compositeDraggedItemIndex, index);
                                      }
                                    }}
                                    style={{ 
                                      opacity: compositeDraggedItemIndex === index ? 0.5 : 1,
                                      cursor: 'move'
                                    }}
                                  >
                                    <span 
                                      className={`badge p-1 text-white ${
                                        element.type === 'prefix' ? 'bg-success' : 
                                        element.type === 'suffix' ? 'bg-warning text-dark' :
                                        element.type === 'separator' ? 'bg-info' : 'bg-dark'
                                      }`}
                                      style={{ fontFamily: element.type === 'separator' ? 'monospace' : 'inherit', fontSize: '0.7em' }}
                                    >
                                      <i className="fas fa-grip-vertical me-1" style={{ fontSize: '0.6em', opacity: 0.7 }}></i>
                                      {element.type === 'separator' ? element.displayChar : element.label}
                                      <button 
                                        className="btn-close btn-close-white ms-1" 
                                        style={{ fontSize: '0.5em' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeCompositeElementFromConstructor(element.id);
                                        }}
                                        aria-label="Remover"
                                      ></button>
                                    </span>
                                  </div>
                                ))}
                                <button 
                                  className="btn btn-sm btn-outline-danger py-0 px-1"
                                  style={{ fontSize: '0.7em' }}
                                  onClick={() => setCompositeConstructorElements([])}
                                  disabled={compositeConstructorElements.length === 0}
                                >
                                  Limpar
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Drop Zone */}
                          <div 
                            className="border rounded p-2 mb-2"
                            style={{ 
                              minHeight: compositeConstructorElements.length > 0 ? '40px' : '60px', 
                              backgroundColor: '#f8f9fa',
                              border: '2px dashed #dee2e6'
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.currentTarget.style.backgroundColor = '#e8f5e8';
                              e.currentTarget.style.borderColor = '#28a745';
                            }}
                            onDragLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                              e.currentTarget.style.borderColor = '#dee2e6';
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                              e.currentTarget.style.borderColor = '#dee2e6';
                              
                              try {
                                const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                                addCompositeElementToConstructor({
                                  type: data.type,
                                  value: data.value,
                                  label: data.label,
                                  maxLength: data.maxLength,
                                  displayChar: data.displayChar || 'X'
                                });
                              } catch (err) {
                                console.error('Invalid drop data');
                              }
                            }}
                          >
                            <div className="text-center text-muted">
                              <i className="fas fa-plus-circle mb-1"></i>
                              <p className="mb-0 small">
                                {compositeConstructorElements.length === 0 ? 
                                  'Arraste elementos aqui' :
                                  'Arraste mais elementos'
                                }
                              </p>
                            </div>
                          </div>

                          {/* Preview */}
                          <div className="mb-2">
                            <label className="form-label fw-bold small">Preview</label>
                            <div className={`alert p-2 border ${calculateCompositeTotalLength() > compositeMaxTotalLength ? 'alert-warning' : 'alert-light'}`}>
                              <div className="d-flex flex-column">
                                <code style={{ fontSize: '0.9em', fontWeight: 'bold' }}>
                                  {generateCompositePreviewPattern()}
                                </code>
                                <span className={`small ${calculateCompositeTotalLength() > compositeMaxTotalLength ? 'text-danger fw-bold' : 'text-muted'}`}>
                                  ({calculateCompositeTotalLength()}/{compositeMaxTotalLength} chars)
                                </span>
                              </div>
                              {calculateCompositeTotalLength() > compositeMaxTotalLength && (
                                <small className="text-danger mt-1 d-block">
                                  <i className="fas fa-exclamation-triangle me-1"></i>
                                  Excede tamanho máximo!
                                </small>
                              )}
                            </div>
                          </div>

                          {/* Save Button */}
                          <div className="mt-3">
                            <button 
                              className="btn btn-success btn-sm w-100"
                              style={{ 
                                fontSize: '0.875rem',
                                padding: '0.25rem 0.5rem',
                                height: '31px',
                                lineHeight: '1.5'
                              }}
                              onClick={saveCompositePatternComposition}
                              disabled={compositeConstructorElements.length === 0 || calculateCompositeTotalLength() > compositeMaxTotalLength}
                            >
                              <i className="fas fa-save me-1"></i>
                              Salvar Composição
                            </button>
                            {calculateCompositeTotalLength() > compositeMaxTotalLength && (
                              <small className="text-danger d-block mt-1 text-center">
                                <i className="fas fa-info-circle me-1"></i>
                                Reduza o comprimento para salvar
                              </small>
                            )}
                          </div>
                        </div>
                      </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Risk Sanitation Settings Section */}
                  <div className="mb-4">
                    {/* Accordion Header for Risk Sanitation */}
                    <div 
                      className="border rounded-top cursor-pointer p-3 bg-body-secondary d-flex justify-content-between align-items-center accordion-header"
                      onClick={() => toggleStep2Section('saneamentoRisco')}
                      style={{ cursor: 'pointer' }}
                    >
                      <div>
                        <h5 className="text-primary mb-1 d-flex align-items-center">
                          <i className="fas fa-shield-alt me-2"></i>
                          Parâmetros de Saneamento de Risco
                        </h5>
                        <p className="text-body-secondary mb-0 small">
                          Configure os parâmetros para saneamento de risco dos perfis gerados
                        </p>
                      </div>
                      <i className={`fas fa-chevron-down text-primary accordion-icon ${step2SectionExpanded.saneamentoRisco ? 'rotated' : ''}`}></i>
                    </div>

                    {/* Collapsible Content for Risk Sanitation */}
                    <div className={`accordion-content ${step2SectionExpanded.saneamentoRisco ? 'expanded' : 'collapsed'}`}>
                      <div className="border border-top-0 rounded-bottom p-3">

                    <div className="row">
                      <div className="col-md-8">
                        {/* Risk Sanitation Elements in Fixed Order */}
                        {riskSanitationElements.map((element, index) => {
                          // Skip conditional elements if they shouldn't be shown
                          if (element.conditional && String(client?.profileModel) !== 'Posição Composto + Atividade Simples') {
                            return null;
                          }
                          
                          // Determine if element should be disabled based on business rules
                          let isDisabled = false;
                          let disabledReason = '';
                          
                          if (element.id === 'san_risk_benchmark') {
                            // Rule 1: Visual disability when cliente is active
                            if (sanRiskCliente) {
                              isDisabled = true;
                              disabledReason = 'Desative primeiro o saneamento por matriz cliente';
                            }
                          } else if (element.id === 'san_risk_cliente') {
                            // Rule 1: Visual disability when benchmark is active
                            if (sanRiskBenchmark) {
                              isDisabled = true;
                              disabledReason = 'Desative primeiro o saneamento por benchmark';
                            }
                          } else if (element.id === 'san_risk_attrb_model') {
                            // Rule 3: san_risk_attrb_model can only be enabled if either benchmark or cliente is active
                            const hasActiveParent = sanRiskBenchmark || sanRiskCliente;
                            if (!hasActiveParent) {
                              isDisabled = true;
                              disabledReason = 'Ative primeiro o saneamento por benchmark ou matriz cliente';
                            }
                          }
                          
                          return (
                            <div key={element.id} className="mb-3">
                              <div className={`form-check form-switch ${isDisabled ? 'opacity-50' : ''}`}>
                                <input 
                                  className="form-check-input" 
                                  type="checkbox" 
                                  id={element.id}
                                  checked={element.enabled}
                                  disabled={isDisabled}
                                  onChange={(e) => {
                                    const newValue = e.target.checked;
                                    
                                    // Apply business rules
                                    if (element.id === 'san_risk_benchmark') {
                                      if (newValue && sanRiskCliente) {
                                        // Rule 2: Cannot have both benchmark and cliente active - skip activation
                                        return;
                                      }
                                      setSanRiskBenchmark(newValue);
                                      
                                      // Rule 3: If disabling benchmark and cliente is also false, disable attrb_model
                                      if (!newValue && !sanRiskCliente && sanRiskAttrModel) {
                                        setSanRiskAttrModel(false);
                                      }
                                    } else if (element.id === 'san_risk_cliente') {
                                      if (newValue && sanRiskBenchmark) {
                                        // Rule 2: Cannot have both benchmark and cliente active - skip activation
                                        return;
                                      }
                                      setSanRiskCliente(newValue);
                                      
                                      // Rule 3: If disabling cliente and benchmark is also false, disable attrb_model
                                      if (!newValue && !sanRiskBenchmark && sanRiskAttrModel) {
                                        setSanRiskAttrModel(false);
                                      }
                                    } else if (element.id === 'san_risk_attrb_model') {
                                      setSanRiskAttrModel(newValue);
                                    }
                                  }}
                                />
                                <label 
                                  className={`form-check-label fw-bold ${isDisabled ? 'text-muted' : ''}`} 
                                  htmlFor={element.id}
                                >
                                  {element.label}
                                </label>
                              </div>
                              <small className={`text-muted ${isDisabled ? 'opacity-75' : ''}`}>
                              {isDisabled && disabledReason ? disabledReason : element.description}
                              </small>
                              
                              {/* Risk Matrix File Upload - Show only for san_risk_cliente when enabled */}
                              {element.id === 'san_risk_cliente' && element.enabled && (
                                <div className="mt-3 p-3 border rounded bg-body-secondary">
                                  <label className="form-label fw-bold small">Carregar Matriz de Risco</label>
                                  <div className="d-flex gap-2 align-items-center">
                                    <input 
                                      type="file" 
                                      accept=".csv,.xlsx" 
                                      onChange={handleRiskMatrixUpload}
                                      className="form-control form-control-sm"
                                      disabled={uploadingRiskMatrix}
                                    />
                                    {uploadingRiskMatrix && (
                                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                                        <span className="visually-hidden">Carregando...</span>
                                      </div>
                                    )}
                                  </div>
                                  <small className="text-muted d-block mt-1">
                                    Formato: CSV com colunas obrigatórias: <strong>risk (Ex.: SODMM01)</strong>, <strong>code (Ex.: ME21N)</strong>
                                  </small>
                                  
                                  {/* Risk Matrix Action Buttons */}
                                  <div className="d-flex gap-2 mt-2">
                                    <button 
                                      type="button"
                                      className="btn btn-outline-primary btn-sm"
                                      onClick={loadRiskMatrixData}
                                      disabled={loadingRiskMatrix}
                                    >
                                      {loadingRiskMatrix ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                          Carregando...
                                        </>
                                      ) : (
                                        <>
                                          <i className="fas fa-eye me-1"></i>
                                          Visualizar
                                        </>
                                      )}
                                    </button>
                                    <button 
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={clearRiskMatrixData}
                                    >
                                      <i className="fas fa-trash me-1"></i>
                                      Limpar
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}

                      </div>
                    </div>
                      </div>
                    </div>
                  </div>

                  {/* Risk Profile Nomenclature Pattern Section - Show only when risk sanitation is enabled */}
                  {(sanRiskBenchmark || sanRiskCliente) && (
                    <div className="mb-4">
                      {/* Accordion Header for Risk Nomenclature */}
                      <div 
                        className="border rounded-top cursor-pointer p-3 bg-body-secondary d-flex justify-content-between align-items-center accordion-header"
                        onClick={() => toggleStep2Section('nomenclaturaRisco')}
                        style={{ cursor: 'pointer' }}
                      >
                        <div>
                          <h5 className="text-danger mb-1 d-flex align-items-center">
                            <i className="fas fa-tags me-2"></i>
                            Padrão de Nomenclatura - Perfis de Risco
                          </h5>
                          <p className="text-body-secondary mb-0 small">
                            Configure o padrão de nomenclatura para perfis de risco gerados pelo saneamento
                          </p>
                        </div>
                        <i className={`fas fa-chevron-down text-danger accordion-icon ${step2SectionExpanded.nomenclaturaRisco ? 'rotated' : ''}`}></i>
                      </div>

                      {/* Collapsible Content for Risk Nomenclature */}
                      <div className={`accordion-content ${step2SectionExpanded.nomenclaturaRisco ? 'expanded' : 'collapsed'}`}>
                        <div className="border border-top-0 rounded-bottom p-3">

                        <div className="row">
                          {/* Left Panel - Available Elements */}
                          <div className="col-md-7">
                            <h6 className="mb-3">Elementos Disponíveis</h6>
                            
                            <div className="row">
                              {/* Prefix and Suffix */}
                              <div className="col-md-4">
                                <div className="mb-3">
                                  <label className="form-label fw-bold small">Prefixo e Sufixo</label>
                                  <div className="d-flex flex-column gap-2">
                                    {/* Prefix */}
                                    <div className="border rounded p-2">
                                      <div className="mb-1 d-flex justify-content-between align-items-center">
                                        <small className="fw-bold">Prefixo</small>
                                        <button 
                                          className="btn btn-danger d-flex align-items-center justify-content-center"
                                          style={{ 
                                            width: '24px', 
                                            height: '24px', 
                                            borderRadius: '50%', 
                                            padding: '0',
                                            margin: '0',
                                            fontSize: '14px',
                                            lineHeight: '1',
                                            fontWeight: 'bold',
                                            border: 'none',
                                            minWidth: '24px',
                                            minHeight: '24px'
                                          }}
                                          onClick={() => setRiskPrefixModalOpen(true)}
                                          title="Configurar padrão de prefixo para perfil de risco"
                                        >
                                          +
                                        </button>
                                      </div>
                                      <div className="row mb-2">
                                        <div className="col-6">
                                          <label className="form-label small">Qtd:</label>
                                          <input 
                                            type="number" 
                                            className="form-control form-control-sm" 
                                            value={riskFieldConfigs.prefixo.qtd}
                                            min="1" 
                                            max="8"
                                            onChange={(e) => updateRiskElementConfig('prefixo', 'qtd', parseInt(e.target.value) || 1)}
                                          />
                                        </div>
                                        <div className="col-6">
                                          <label className="form-label small">Char:</label>
                                          <input 
                                            type="text" 
                                            className="form-control form-control-sm" 
                                            value={riskFieldConfigs.prefixo.char}
                                            maxLength={1}
                                            style={{ textAlign: 'center', textTransform: 'uppercase' }}
                                            onChange={(e) => updateRiskElementConfig('prefixo', 'char', e.target.value.toUpperCase())}
                                          />
                                        </div>
                                      </div>
                                      <div 
                                        className="badge bg-primary text-white p-1 w-100 text-center user-select-none"
                                        style={{ cursor: 'grab', fontSize: '0.7em' }}
                                        draggable
                                        onDragStart={(e) => {
                                          e.dataTransfer.setData('text/plain', JSON.stringify({
                                            type: 'prefix',
                                            value: 'prefixo',
                                            label: 'Prefixo',
                                            maxLength: riskFieldConfigs.prefixo.qtd,
                                            displayChar: riskFieldConfigs.prefixo.char
                                          }));
                                        }}
                                      >
                                        Prefixo
                                      </div>
                                    </div>

                                    {/* Suffix */}
                                    <div className="border rounded p-2">
                                      <div className="mb-1">
                                        <small className="fw-bold">Sufixo</small>
                                      </div>
                                      <div className="row mb-2">
                                        <div className="col-6">
                                          <label className="form-label small">Qtd:</label>
                                          <input 
                                            type="number" 
                                            className="form-control form-control-sm" 
                                            value={riskFieldConfigs.sufixo.qtd}
                                            min="1" 
                                            max="8"
                                            onChange={(e) => updateRiskElementConfig('sufixo', 'qtd', parseInt(e.target.value) || 1)}
                                          />
                                        </div>
                                        <div className="col-6">
                                          <label className="form-label small">Char:</label>
                                          <input 
                                            type="text" 
                                            className="form-control form-control-sm" 
                                            value={riskFieldConfigs.sufixo.char}
                                            maxLength={1}
                                            style={{ textAlign: 'center', textTransform: 'uppercase' }}
                                            onChange={(e) => updateRiskElementConfig('sufixo', 'char', e.target.value.toUpperCase())}
                                          />
                                        </div>
                                      </div>
                                      <div 
                                        className="badge bg-warning text-dark p-1 w-100 text-center user-select-none"
                                        style={{ cursor: 'grab', fontSize: '0.7em' }}
                                        draggable
                                        onDragStart={(e) => {
                                          e.dataTransfer.setData('text/plain', JSON.stringify({
                                            type: 'suffix',
                                            value: 'sufixo',
                                            label: 'Sufixo',
                                            maxLength: riskFieldConfigs.sufixo.qtd,
                                            displayChar: riskFieldConfigs.sufixo.char
                                          }));
                                        }}
                                      >
                                        Sufixo
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Data Elements */}
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="form-label fw-bold small">Elementos de Dados</label>
                                  <div className="row">
                                    {(String(client?.profileModel) === 'Posição/Cargo Simples' ? [
                                      { key: 'n1', label: 'Nível 1', defaultLength: 3, defaultChar: '1' },
                                      { key: 'n2', label: 'Nível 2', defaultLength: 3, defaultChar: '2' },
                                      { key: 'n3', label: 'Nível 3', defaultLength: 3, defaultChar: '3' },
                                      { key: 'n4', label: 'Nível 4', defaultLength: 3, defaultChar: '4' },
                                      { key: 'acao', label: 'Ação', defaultLength: 4, defaultChar: 'C' }
                                    ] : [
                                      { key: 'area', label: 'Área', defaultLength: 2, defaultChar: 'A' },
                                      { key: 'processo', label: 'Processo', defaultLength: 2, defaultChar: 'P' },
                                      { key: 'subprocesso', label: 'Subprocesso', defaultLength: 4, defaultChar: 'S' },
                                      { key: 'atividade', label: 'Atividade', defaultLength: 13, defaultChar: 'T' },
                                      { key: 'acao', label: 'Ação', defaultLength: 4, defaultChar: 'C' }
                                    ]).map(element => (
                                      <div key={element.key} className="col-6 mb-2">
                                        <div className="border rounded p-2">
                                          <div className="mb-1">
                                            <small className="fw-bold">{element.label}</small>
                                          </div>
                                          <div className="row mb-2">
                                            <div className="col-6">
                                              <label className="form-label small">Qtd:</label>
                                              <input 
                                                type="number" 
                                                className="form-control form-control-sm" 
                                                value={riskFieldConfigs[element.key as keyof typeof riskFieldConfigs].qtd}
                                                min="1" 
                                                max="16"
                                                onChange={(e) => updateRiskElementConfig(element.key, 'qtd', parseInt(e.target.value) || 1)}
                                              />
                                            </div>
                                            <div className="col-6">
                                              <label className="form-label small">Char:</label>
                                              <input 
                                                type="text" 
                                                className="form-control form-control-sm" 
                                                value={riskFieldConfigs[element.key as keyof typeof riskFieldConfigs].char}
                                                maxLength={1}
                                                style={{ textAlign: 'center', textTransform: 'uppercase' }}
                                                onChange={(e) => updateRiskElementConfig(element.key, 'char', e.target.value.toUpperCase())}
                                              />
                                            </div>
                                          </div>
                                          <div 
                                            className="badge bg-secondary text-white p-1 w-100 text-center user-select-none"
                                            style={{ cursor: 'grab', fontSize: '0.7em' }}
                                            draggable
                                            onDragStart={(e) => {
                                              const config = riskFieldConfigs[element.key as keyof typeof riskFieldConfigs];
                                              e.dataTransfer.setData('text/plain', JSON.stringify({
                                                type: 'data',
                                                value: element.key,
                                                label: element.label,
                                                maxLength: config.qtd,
                                                displayChar: config.char
                                              }));
                                            }}
                                          >
                                            {element.label}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Separators */}
                              <div className="col-md-2">
                                <div className="mb-3">
                                  <label className="form-label fw-bold small">Separadores</label>
                                  <div className="d-flex flex-column gap-1">
                                    {['.', '-', '_', ':'].map(sep => (
                                      <div 
                                        key={sep}
                                        className="badge bg-info text-white p-1 text-center user-select-none"
                                        style={{ cursor: 'grab', fontFamily: 'monospace', fontSize: '0.7em' }}
                                        draggable
                                        onDragStart={(e) => {
                                          e.dataTransfer.setData('text/plain', JSON.stringify({
                                            type: 'separator',
                                            value: sep,
                                            label: sep,
                                            maxLength: 1,
                                            displayChar: sep
                                          }));
                                        }}
                                      >
                                        {sep}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Right Panel - Constructor and Preview */}
                          <div className="col-md-5">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="mb-0">Construtor</h6>
                              <div className="d-flex align-items-center gap-1">
                                <label className="form-label mb-0 small">Max:</label>
                                <input 
                                  type="number" 
                                  className="form-control form-control-sm" 
                                  style={{ width: '80px' }}
                                  value={riskMaxTotalLength}
                                  onChange={(e) => {
                                    const v = parseInt(e.target.value) || 30;
                                    setRiskMaxTotalLength(v);
                                    setPatternNameMaxChar(v);
                                    schedulePersistPatternNameMaxChar(v);
                                  }}
                                  onBlur={async () => {
                                    if (!clientId) return;
                                    const newVal = Number(patternNameMaxChar || riskMaxTotalLength || 30);
                                    try { await persistPatternNameMaxChar(newVal); } catch (err) { console.warn('Erro ao persistir pattern_name_max_char', err); }
                                  }}
                                  min="10" 
                                  max="64"
                                />
                                <span className="small text-muted">chars</span>
                              </div>
                            </div>

                            {/* Constructor Elements Display */}
                            {riskConstructorElements.length > 0 && (
                              <div className="mb-2">
                                <div className="d-flex flex-wrap gap-1 p-2 border rounded bg-light">
                                  {riskConstructorElements.map((element, index) => (
                                    <div 
                                      key={element.id} 
                                      className="d-flex align-items-center"
                                      draggable
                                      onDragStart={(e) => {
                                        setRiskDraggedItemIndex(index);
                                        e.dataTransfer.effectAllowed = 'move';
                                      }}
                                      onDragEnd={() => setRiskDraggedItemIndex(null)}
                                      onDragOver={(e) => {
                                        e.preventDefault();
                                        e.dataTransfer.dropEffect = 'move';
                                      }}
                                      onDrop={async (e) => {
                                        e.preventDefault();
                                        if (riskDraggedItemIndex !== null && riskDraggedItemIndex !== index) {
                                          await reorderRiskConstructorElements(riskDraggedItemIndex, index);
                                        }
                                      }}
                                      style={{ 
                                        opacity: riskDraggedItemIndex === index ? 0.5 : 1,
                                        cursor: 'move'
                                      }}
                                    >
                                      <span 
                                        className={`badge p-1 text-white ${
                                          element.type === 'prefix' ? 'bg-primary' : 
                                          element.type === 'suffix' ? 'bg-warning text-dark' :
                                          element.type === 'separator' ? 'bg-info' : 'bg-secondary'
                                        }`}
                                        style={{ fontFamily: element.type === 'separator' ? 'monospace' : 'inherit', fontSize: '0.7em' }}
                                      >
                                        <i className="fas fa-grip-vertical me-1" style={{ fontSize: '0.6em', opacity: 0.7 }}></i>
                                        {element.type === 'separator' ? element.displayChar : element.label}
                                        <button 
                                          className="btn-close btn-close-white ms-1" 
                                          style={{ fontSize: '0.5em' }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            removeRiskElementFromConstructor(element.id);
                                          }}
                                          aria-label="Remover"
                                        ></button>
                                      </span>
                                    </div>
                                  ))}
                                  <button 
                                    className="btn btn-sm btn-outline-danger py-0 px-1"
                                    style={{ fontSize: '0.7em' }}
                                    onClick={() => setRiskConstructorElements([])}
                                    disabled={riskConstructorElements.length === 0}
                                  >
                                    Limpar
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Drop Zone */}
                            <div 
                              className="border rounded p-2 mb-2"
                              style={{ 
                                minHeight: riskConstructorElements.length > 0 ? '40px' : '60px', 
                                backgroundColor: '#f8f9fa',
                                border: '2px dashed #dee2e6'
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.currentTarget.style.backgroundColor = '#e3f2fd';
                                e.currentTarget.style.borderColor = '#2196f3';
                              }}
                              onDragLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                e.currentTarget.style.borderColor = '#dee2e6';
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                e.currentTarget.style.borderColor = '#dee2e6';
                                
                                try {
                                  const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                                  addRiskElementToConstructor({
                                    type: data.type,
                                    value: data.value,
                                    label: data.label,
                                    maxLength: data.maxLength,
                                    displayChar: data.displayChar || 'X'
                                  });
                                } catch (err) {
                                  console.error('Invalid drop data');
                                }
                              }}
                            >
                              <div className="text-center text-muted">
                                <i className="fas fa-plus-circle mb-1"></i>
                                <p className="mb-0 small">
                                  {riskConstructorElements.length === 0 ? 
                                    'Arraste elementos aqui' :
                                    'Arraste mais elementos'
                                  }
                                </p>
                              </div>
                            </div>

                            {/* Preview */}
                            <div className="mb-2">
                              <label className="form-label fw-bold small">Preview</label>
                              <div className={`alert p-2 border ${calculateRiskTotalLength() > riskMaxTotalLength ? 'alert-warning' : 'alert-light'}`}>
                                <div className="d-flex flex-column">
                                  <code style={{ fontSize: '0.9em', fontWeight: 'bold' }}>
                                    {generateRiskPreviewPattern()}
                                  </code>
                                  <span className={`small ${calculateRiskTotalLength() > riskMaxTotalLength ? 'text-danger fw-bold' : 'text-muted'}`}>
                                    ({calculateRiskTotalLength()}/{riskMaxTotalLength} chars)
                                  </span>
                                </div>
                                {calculateRiskTotalLength() > riskMaxTotalLength && (
                                  <small className="text-danger mt-1 d-block">
                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                    Excede tamanho máximo!
                                  </small>
                                )}
                              </div>
                            </div>

                            {/* Save Button */}
                            <div className="mt-3">
                              <button 
                                className="btn btn-primary btn-sm w-100"
                                style={{ 
                                  fontSize: '0.875rem',
                                  padding: '0.25rem 0.5rem',
                                  height: '31px',
                                  lineHeight: '1.5'
                                }}
                                onClick={saveRiskPatternComposition}
                                disabled={riskConstructorElements.length === 0 || calculateRiskTotalLength() > riskMaxTotalLength}
                              >
                                <i className="fas fa-save me-1"></i>
                                Salvar Composição
                              </button>
                              {calculateRiskTotalLength() > riskMaxTotalLength && (
                                <small className="text-danger d-block mt-1 text-center">
                                  <i className="fas fa-info-circle me-1"></i>
                                  Reduza o comprimento para salvar
                                </small>
                              )}
                            </div>
                          </div>
                        </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Derivation by Organizational Level Settings Section */}
                  {(String(client?.profileModel) === 'Atividade Simples' || String(client?.profileModel) === 'Posição Composto + Atividade Simples') && (
                    <div className="mb-4">
                      {/* Accordion Header for Organizational Level Derivation */}
                      <div 
                        className="border rounded-top cursor-pointer p-3 bg-body-secondary d-flex justify-content-between align-items-center accordion-header"
                        onClick={() => toggleStep2Section('derivacaoOrganizacional')}
                        style={{ cursor: 'pointer' }}
                      >
                        <div>
                          <h5 className="text-primary mb-1 d-flex align-items-center">
                            <i className="fas fa-sitemap me-2"></i>
                            Parâmetros de derivação por nível organizacional
                          </h5>
                          <p className="text-body-secondary mb-0 small">
                            Configure os parâmetros para derivação de perfis baseada no nível organizacional
                          </p>
                        </div>
                        <i className={`fas fa-chevron-down text-primary accordion-icon ${step2SectionExpanded.derivacaoOrganizacional ? 'rotated' : ''}`}></i>
                      </div>

                      {/* Collapsible Content for Organizational Level Derivation */}
                      <div className={`accordion-content ${step2SectionExpanded.derivacaoOrganizacional ? 'expanded' : 'collapsed'}`}>
                        <div className="border border-top-0 rounded-bottom p-3">

                        <div className="row">
                          <div className="col-md-8">
                            {/* Toggle switch for organizational derivation */}
                            <div className="mb-3">
                              <div className="form-check form-switch">
                                <input 
                                  className="form-check-input" 
                                  type="checkbox" 
                                  id="derivationStatusToggle"
                                  checked={derivationStatus}
                                  onChange={handleDerivationStatusChange}
                                />
                                <label 
                                  className="form-check-label fw-bold" 
                                  htmlFor="derivationStatusToggle"
                                >
                                  Usar derivação por nível organizacional?
                                </label>
                              </div>
                              <small className="text-muted">
                                Ativa a derivação automática de perfis baseada nos níveis organizacionais configurados
                              </small>
                              
                              {/* Button for level selection - Show only when derivation is enabled */}
                              {derivationStatus && (
                                <div className="mt-3 p-3 border rounded bg-body-secondary">
                                  <label className="form-label fw-bold small">Configuração de Níveis Organizacionais</label>
                                  <div className="d-flex gap-2 align-items-center">
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm"
                                      onClick={openOrgLevelModal}
                                    >
                                      <i className="fas fa-sitemap me-1"></i>
                                      Selecionar nível
                                    </button>
                                    {derivationOrgLevel && (
                                      <span className="badge bg-secondary">
                                        {derivationOrgLevel}
                                      </span>
                                    )}
                                  </div>
                                  <small className="text-muted d-block mt-1">
                                    {derivationOrgLevel 
                                      ? `Nível selecionado: ${derivationOrgLevel}` 
                                      : 'Selecione o nível organizacional para derivação automática de perfis'
                                    }
                                  </small>
                                  
                                  {/* CSV Upload section - Show only when level is selected */}
                                  {derivationOrgLevel && (
                                    <div className="mt-3 p-3 border rounded bg-body-secondary">
                                      <label className="form-label fw-bold small">Valores dos Níveis Organizacionais</label>
                                      <div className="d-flex gap-2 align-items-center flex-wrap">
                                        <input
                                          id="orgLevelFileInput"
                                          type="file"
                                          accept=".csv"
                                          onChange={handleOrgLevelFileUpload}
                                          style={{ display: 'none' }}
                                          disabled={orgLevelUploadLoading}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary btn-sm"
                                          onClick={() => {
                                            const fileInput = document.getElementById('orgLevelFileInput') as HTMLInputElement;
                                            fileInput?.click();
                                          }}
                                          disabled={orgLevelUploadLoading}
                                        >
                                          {orgLevelUploadLoading ? (
                                            <>
                                              <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                              Carregando...
                                            </>
                                          ) : (
                                            <>
                                              <i className="fas fa-upload me-1"></i>
                                              Carregar CSV
                                            </>
                                          )}
                                        </button>
                                        {orgLevelValues.length > 0 && (
                                          <>
                                            <button
                                              type="button"
                                              className="btn btn-outline-info btn-sm"
                                              onClick={visualizeOrgLevelValues}
                                            >
                                              <i className="fas fa-eye me-1"></i>
                                              Visualizar ({orgLevelValues.length})
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-outline-danger btn-sm"
                                              onClick={clearOrgLevelValues}
                                            >
                                              <i className="fas fa-trash me-1"></i>
                                              Limpar
                                            </button>
                                          </>
                                        )}
                                      </div>
                                      <small className="text-muted d-block mt-1">
                                        {orgLevelValues.length > 0 
                                          ? `${orgLevelValues.length} valores carregados. Os valores devem ter no máximo ${client?.pattern_s_sufixo_qtd || 'N/A'} caracteres.`
                                          : 'Carregue um arquivo CSV com uma coluna contendo os valores dos níveis organizacionais.'
                                        }
                                      </small>
                                    </div>
                                  )}

                                  {/* User mapping section - Show only when level is selected and values are loaded */}
                                  {derivationOrgLevel && orgLevelValues.length > 0 && (
                                    <div className="mt-3 p-3 border rounded bg-body-secondary">
                                      <div className="mb-3">
                                        <div className="form-check form-switch">
                                          <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            id="derivationUserStatusToggle"
                                            checked={derivationUserStatus}
                                            onChange={handleDerivationUserStatusChange}
                                          />
                                          <label 
                                            className="form-check-label fw-bold" 
                                            htmlFor="derivationUserStatusToggle"
                                          >
                                            Considerar perfis derivados no mapeamento para usuários?
                                          </label>
                                        </div>
                                        <small className="text-muted">
                                          Permite mapear usuários específicos aos valores dos níveis organizacionais
                                        </small>
                                      </div>

                                      {/* CSV Upload section for user mappings - Show only when user mapping is enabled */}
                                      {derivationUserStatus && (
                                        <div className="p-3 border rounded bg-body-secondary">
                                          <label className="form-label fw-bold small">Mapeamento Usuário → Valor Organizacional</label>
                                          <div className="d-flex gap-2 align-items-center flex-wrap">
                                            <input
                                              id="userOrgLevelFileInput"
                                              type="file"
                                              accept=".csv"
                                              onChange={handleUserOrgLevelFileUpload}
                                              style={{ display: 'none' }}
                                              disabled={userOrgLevelUploadLoading}
                                            />
                                            <button
                                              type="button"
                                              className="btn btn-outline-secondary btn-sm"
                                              onClick={() => {
                                                const fileInput = document.getElementById('userOrgLevelFileInput') as HTMLInputElement;
                                                fileInput?.click();
                                              }}
                                              disabled={userOrgLevelUploadLoading}
                                            >
                                              {userOrgLevelUploadLoading ? (
                                                <>
                                                  <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                  Carregando...
                                                </>
                                              ) : (
                                                <>
                                                  <i className="fas fa-upload me-1"></i>
                                                  Carregar CSV
                                                </>
                                              )}
                                            </button>
                                            {userOrgLevelMappings.length > 0 && (
                                              <>
                                                <button
                                                  type="button"
                                                  className="btn btn-outline-info btn-sm"
                                                  onClick={visualizeUserOrgLevelMappings}
                                                >
                                                  <i className="fas fa-eye me-1"></i>
                                                  Visualizar ({userOrgLevelMappings.length})
                                                </button>
                                                <button
                                                  type="button"
                                                  className={`btn btn-sm ${userOrgLevelValidationIssues.hasIssues ? 'btn-danger' : 'btn-outline-secondary'}`}
                                                  onClick={showValidationDiagnostic}
                                                  disabled={diagnosticLoadingUserOrgLevel}
                                                  title={userOrgLevelValidationIssues.hasIssues ? 'Inconsistências detectadas! Clique para ver detalhes.' : 'Executar diagnóstico de validação'}
                                                >
                                                  {diagnosticLoadingUserOrgLevel ? (
                                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                  ) : (
                                                    <i className={`fas ${userOrgLevelValidationIssues.hasIssues ? 'fa-exclamation-triangle' : 'fa-stethoscope'} me-1`}></i>
                                                  )}
                                                  Diagnóstico
                                                  {userOrgLevelValidationIssues.hasIssues && !diagnosticLoadingUserOrgLevel && (
                                                    <span className="badge bg-white text-danger ms-1">
                                                      {userOrgLevelValidationIssues.invalidValues.length + userOrgLevelValidationIssues.missingUsers.length + userOrgLevelValidationIssues.unmappedUsers.length}
                                                    </span>
                                                  )}
                                                </button>
                                                <button
                                                  type="button"
                                                  className="btn btn-outline-danger btn-sm"
                                                  onClick={clearUserOrgLevelMappings}
                                                >
                                                  <i className="fas fa-trash me-1"></i>
                                                  Limpar
                                                </button>
                                              </>
                                            )}
                                          </div>
                                          <small className="text-muted d-block mt-1">
                                            {userOrgLevelMappings.length > 0 
                                              ? `${userOrgLevelMappings.length} mapeamentos carregados. Separadores aceitos: vírgula (,) ou ponto e vírgula (;).`
                                              : 'Carregue um arquivo CSV com duas colunas: user e orglevel_value. Separadores aceitos: vírgula (,) ou ponto e vírgula (;). Cabeçalho opcional.'
                                            }
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : step === 3 && String(client?.profileModel) !== 'Posição/Cargo Simples' ? (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div><h6>Transações/Apps carregados (codez)</h6><small className="text-muted">Cliente: {clientId}</small></div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <button className="btn btn-sm btn-outline-primary" onClick={() => setClassifyMenuOpen(v => !v)}>Classificar ▾</button>
                        {classifyMenuOpen && (
                          <div className="gp-dropdown" style={{ left: 0 }} onClick={e => e.stopPropagation()}>
                            <button className="btn btn-sm btn-link" onClick={() => { setClassifyMenuOpen(false); classifyItems((codezItems || []).map((it: any) => ({ code: it.code, description: it.description, _id: it._id }))); }}>Classificar todos</button>
                            <button className="btn btn-sm btn-link" onClick={() => { setClassifyMenuOpen(false); const selected = (codezItems || []).filter(it => selectedIds.has(it._id)).map(it => ({ code: it.code, description: it.description, _id: it._id })); classifyItems(selected); }} disabled={selectedIds.size === 0}>{selectedIds.size === 0 ? 'Classificar selecionadas (nenhuma selecionada)' : `Classificar selecionadas (${selectedIds.size})`}</button>
                          </div>
                        )}
                      </div>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => downloadCodezCsv()}>Download CSV</button>
                      <div style={{ position: 'relative' }}>
                        <input 
                          type="file" 
                          accept=".csv" 
                          onChange={(e) => handleCodezCsvUpload(e)} 
                          onClick={handleFileInputClick}
                          style={{ display: 'none' }} 
                          id="codez-csv-upload"
                        />
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => document.getElementById('codez-csv-upload')?.click()}>Upload CSV</button>
                      </div>
                      <button className="btn btn-sm btn-outline-primary" onClick={() => fetchIndicators('codez')}>Atualizar</button>
                      <button className="btn btn-sm btn-outline-primary" onClick={() => setShowLogModal(true)}>Log IA</button>
                    </div>
                  </div>
                  <div style={{ maxHeight: '52vh', overflow: 'auto' }}>
                    <DataTable
                      columns={[
                        {
                          key: '_select',
                          label: 'Seleção',
                          sortable: false,
                          minWidth: 50,
                          className: 'gpw-checkbox-center',
                          checkboxColumn: true,
                          allChecked: (displayedCodez || []).length > 0 && (displayedCodez || []).every((d: any) => selectedIds.has(d._id)),
                          onCheckboxHeaderClick: () => toggleSelectAllVisible((displayedCodez || []).map((d: any) => d._id)),
                          render: (_: any, row: any) => (
                            <div className="gpw-checkbox-center-cell">
                              <input type="checkbox" checked={selectedIds.has(row._id)} onChange={() => toggleSelect(row._id)} />
                            </div>
                          )
                        },
                        { key: 'code', label: 'Código', minWidth: 120, render: (_: any, row: any) => row.code || '-' },
                        { key: 'description', label: 'Descrição', minWidth: 220, render: (_: any, row: any) => row.description || '-' },
                        { key: 'process', label: 'Processo', minWidth: 160, render: (_: any, row: any) => row.process || '' },
                        { key: 'subprocess', label: 'Subprocesso', minWidth: 160, render: (_: any, row: any) => row.subprocess || '' },
                        { key: 'activity', label: 'Atividade', minWidth: 160, render: (_: any, row: any) => row.activity || '' },
                        {
                          key: 'actions',
                          label: 'Ações',
                          sortable: false,
                          minWidth: 90,
                          render: (_: any, row: any) => (
                            <ActionMenu
                              actions={[
                                {
                                  key: 'edit',
                                  label: 'Editar',
                                  icon: Icons.Pencil,
                                  variant: 'primary',
                                  onClick: () => setEditingCodez(row)
                                },
                                {
                                  key: 'clear',
                                  label: 'Limpar',
                                  icon: Icons.Trash,
                                  variant: 'danger',
                                  onClick: async () => {
                                    if (!row._id) return;
                                    try {
                                      await axios.patch((process.env.REACT_APP_API_URL || '').replace(/\/$/, '') + `/api/profilegen-data/codez/item/${row._id}`, { process: '', subprocess: '', activity: '', purpose: '', justification: '' }, { withCredentials: true });
                                      await fetchIndicators('codez');
                                    } catch (err) { console.error('clear row', err); }
                                  }
                                }
                              ]}
                            />
                          )
                        }
                      ] as DataTableColumn<any>[]}
                      data={displayedCodez || []}
                      keyExtractor={(row: any, index: number) => row._id || row.code || String(index)}
                      striped
                      hover
                      bordered
                      responsive
                      size="sm"
                      className="gpw-table"
                      pagination
                      clientSidePagination
                      currentPage={codezPage}
                      pageSize={codezPageSize}
                      totalItems={(displayedCodez || []).length}
                      onPageChange={(page: number) => setCodezPage(page)}
                      onPageSizeChange={(size: number) => {
                        setCodezPageSize(size);
                        localStorage.setItem('gpw_codez_pageSize', String(size));
                        setCodezPage(1);
                      }}
                      emptyMessage="Nenhum registro encontrado"
                    />
                  </div>

                  {/* bottom classify buttons removed - use header Classificar dropdown */}
                </div>
              ) : step === 4 && String(client?.profileModel) !== 'Posição/Cargo Simples' && (String(client?.profileModel) === 'Atividade Simples' || String(client?.profileModel) === 'Posição Composto + Atividade Simples') ? (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div><small className="text-muted">Cliente: {clientId}</small></div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <button className="btn btn-sm btn-outline-primary" onClick={() => setClassifyLogsMenuOpen(v => !v)}>Classificar customizadas ▾</button>
            {classifyLogsMenuOpen && (
                          <div className="gp-dropdown" style={{ right: 0 }} onClick={e => e.stopPropagation()}>
              <button className="btn btn-sm btn-link" onClick={() => { setConfirmClassifyMode('custom'); setConfirmClassifyScope('all'); setConfirmClassifyOpen(true); setClassifyLogsMenuOpen(false); }}>Classificar todas</button>
              <button className="btn btn-sm btn-link" onClick={() => { setConfirmClassifyMode('custom'); setConfirmClassifyScope('selected'); setConfirmClassifyOpen(true); setClassifyLogsMenuOpen(false); }} disabled={selectedLogCodes.size === 0}>{selectedLogCodes.size === 0 ? 'Classificar selecionadas (nenhuma selecionada)' : `Classificar selecionadas (${selectedLogCodes.size})`}</button>
                          </div>
                        )}
                      </div>

                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setClassifyBenchmarkMenuOpen(v => !v)} disabled={benchmarkLoading}>Classificar com Benchmark ▾</button>
                        {classifyBenchmarkMenuOpen && (
                          <div className="gp-dropdown" style={{ right: 0, minWidth: 260 }} onClick={e => e.stopPropagation()}>
                            <button className="btn btn-sm btn-link" onClick={() => { setConfirmClassifyMode('benchmark'); setConfirmClassifyScope('all'); setConfirmClassifyOpen(true); setClassifyBenchmarkMenuOpen(false); }} disabled={benchmarkLoading}>Classificar todas</button>
                            <button className="btn btn-sm btn-link" onClick={() => { setConfirmClassifyMode('benchmark'); setConfirmClassifyScope('selected'); setConfirmClassifyOpen(true); setClassifyBenchmarkMenuOpen(false); }} disabled={selectedLogCodes.size === 0 || benchmarkLoading}>{selectedLogCodes.size === 0 ? 'Classificar selecionadas (nenhuma selecionada)' : `Classificar selecionadas (${selectedLogCodes.size})`}</button>
                          </div>
                        )}
                      </div>

                      <button className="btn btn-sm btn-outline-primary" onClick={() => fetchIndicators('logs')}>Atualizar</button>
                      <button className="btn btn-sm btn-danger" onClick={() => setConfirmClearClassificationsOpen(true)}>Limpar classificações</button>
                    </div>
                  </div>
                  <div style={{ overflow: 'visible' }}>
                    {(() => {
                      // Use centralized builder so benchmarkResults and classifiedFromCustom are considered
                      const rows = buildLogRows();
                      const filteredRows = rows;
                      // apply sorting
                      const sorted = (() => {
                        if (!step4SortColumn) return filteredRows;
                        const col = step4SortColumn;
                        const copy = [...filteredRows];
                        copy.sort((a, b) => {
                          const va = (col === 'code' ? String(a.code || '') : col === 'process' ? String(a.proc || '') : col === 'subprocess' ? String(a.sub || '') : col === 'activity' ? String(a.act || '') : col === 'isCustom' ? (a.isCustom ? 'Sim' : 'Nao') : col === 'fonte' ? String(a.fonte || '') : '').toLowerCase();
                          const vb = (col === 'code' ? String(b.code || '') : col === 'process' ? String(b.proc || '') : col === 'subprocess' ? String(b.sub || '') : col === 'activity' ? String(b.act || '') : col === 'isCustom' ? (b.isCustom ? 'Sim' : 'Nao') : col === 'fonte' ? String(b.fonte || '') : '').toLowerCase();
                          if (va < vb) return step4SortDir === 'asc' ? -1 : 1;
                          if (va > vb) return step4SortDir === 'asc' ? 1 : -1;
                          return 0;
                        });
                        return copy;
                      })();
                      const total = sorted.length;
                      const totalPages = Math.max(1, Math.ceil(total / (step4PageSize || 25)));
                      const page = Math.min(Math.max(1, step4Page), totalPages);
                      const start = (page - 1) * (step4PageSize || 25);
                      const pageItems = sorted.slice(start, start + (step4PageSize || 25));
                      return (
                        <>
                          <DataTable
                            columns={[
                              {
                                key: '_select',
                                label: 'Seleção',
                                sortable: false,
                                minWidth: 50,
                                className: 'gpw-checkbox-center',
                                checkboxColumn: true,
                                allChecked: pageItems.length > 0 && pageItems.every((rr: any) => selectedLogCodes.has(rr.code)),
                                onCheckboxHeaderClick: () => toggleSelectAllLogsVisible(pageItems.map((r: any) => r.code)),
                                render: (_: any, row: any) => (
                                  <div className="gpw-checkbox-center-cell">
                                    <input type="checkbox" checked={selectedLogCodes.has(row.code)} onChange={() => toggleSelectLog(row.code)} />
                                  </div>
                                )
                              },
                              { key: 'client', label: 'Cliente', minWidth: 140, sortable: false, render: () => clientId || '-' },
                              { key: 'code', label: 'Código', minWidth: 110, sortable: true, render: (_: any, row: any) => row.code || '-' },
                              { key: 'process', label: 'Processo', minWidth: 170, sortable: true, render: (_: any, row: any) => row.proc || '-' },
                              { key: 'subprocess', label: 'Subprocesso', minWidth: 170, sortable: true, render: (_: any, row: any) => row.sub || '-' },
                              { key: 'activity', label: 'Atividade', minWidth: 170, sortable: true, render: (_: any, row: any) => row.act || '-' },
                              { key: 'isCustom', label: 'Customizada?', minWidth: 120, sortable: true, render: (_: any, row: any) => row.isCustom ? 'Sim' : 'Não' },
                              {
                                key: 'fonte',
                                label: 'Fonte',
                                minWidth: 240,
                                sortable: true,
                                render: (_: any, row: any) => row.fonte || '-'
                              },
                              {
                                key: 'actions',
                                label: 'Ações',
                                minWidth: 90,
                                sortable: false,
                                render: (_: any, row: any) => (
                                  <ActionMenu
                                    actions={[
                                      {
                                        key: 'edit',
                                        label: 'Editar',
                                        icon: Icons.Pencil,
                                        variant: 'primary',
                                        onClick: () => setEditingLogRow({ code: row.code, proc: row.proc, sub: row.sub, act: row.act, fonte: row.fonte, isCustom: row.isCustom })
                                      }
                                    ]}
                                  />
                                )
                              }
                            ] as DataTableColumn<any>[]}
                            data={sorted}
                            keyExtractor={(row: any, index: number) => row.code || String(start + index)}
                            sortColumn={step4SortColumn || null}
                            sortDirection={step4SortDir}
                            onSort={(column: string, direction?: 'asc' | 'desc') => handleStep4Sort(column, direction)}
                            striped
                            hover
                            bordered
                            responsive
                            size="sm"
                            className="gpw-table"
                            pagination
                            clientSidePagination
                            currentPage={step4Page}
                            pageSize={step4PageSize}
                            totalItems={sorted.length}
                            onPageChange={(page: number) => setStep4Page(page)}
                            onPageSizeChange={(size: number) => {
                              setStep4PageSize(size);
                              localStorage.setItem('gpw_step4_pageSize', String(size));
                              setStep4Page(1);
                            }}
                            emptyMessage="Nenhum registro encontrado"
                          />
                      </>);
                    })()}
                  </div>
                </div>
              ) : (step === 3 && String(client?.profileModel) === 'Posição/Cargo Simples') || (step === 5 && String(client?.profileModel) !== 'Posição/Cargo Simples' && (String(client?.profileModel) === 'Atividade Simples' || String(client?.profileModel) === 'Posição Composto + Atividade Simples')) ? (
                <div>
                  {/* Seção: Classificar ações e gerar nomenclatura de perfis simples */}
                  <div className="mb-4">
                    {/* Accordion Header for Risk Classification and Profile Generation */}
                    <div 
                      className="border rounded-top cursor-pointer p-3 bg-body-secondary d-flex justify-content-between align-items-center accordion-header"
                      onClick={() => setNomenclatureSectionExpanded(prev => ({ ...prev, visaoConsolidada: !prev.visaoConsolidada }))}
                    >
                      <div>
                        <h5 className="text-primary mb-1 d-flex align-items-center">
                          <i className="fas fa-table me-2"></i>
                          Classificar ações e gerar nomenclatura de perfis simples
                        </h5>
                        <p className="text-body-secondary mb-0 small">
                          {(String(client?.profileModel) === 'Atividade Simples' || String(client?.profileModel) === 'Posição Composto + Atividade Simples')
                            ? 'Visualização dos dados consolidados, classificação e geração de nomenclatura dos perfis simples.'
                            : 'Visualização dos dados consolidados, classificação e geração de nomenclatura dos perfis simples para posições/cargos.'
                          }
                        </p>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <i className={`fas fa-chevron-down text-primary accordion-icon ${nomenclatureSectionExpanded.visaoConsolidada ? 'rotated' : ''}`}></i>
                      </div>
                    </div>
                    
                    {/* Accordion Content */}
                    <div className={`accordion-content nomenclature-accordion-content ${nomenclatureSectionExpanded.visaoConsolidada ? 'expanded' : 'collapsed'}`}>
                      <div className="border border-top-0 rounded-bottom p-4" style={{ minHeight: 'auto', overflow: 'visible' }}>
                        {(String(client?.profileModel) === 'Atividade Simples' || String(client?.profileModel) === 'Posição Composto + Atividade Simples') ? (
                          <>
                            {/* Section action buttons: moved from header into content, displayed above table */}
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                              <button 
                                className="btn btn-outline-primary btn-sm"
                                onClick={(e) => { e.stopPropagation(); fetchNomenclatureMappingData(); }}
                                disabled={nomenclatureMappingLoading}
                              >
                                {nomenclatureMappingLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Carregando...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-sync-alt me-2"></i>
                                    Atualizar
                                  </>
                                )}
                              </button>

                              <button 
                                className="btn btn-outline-warning btn-sm"
                                onClick={(e) => { e.stopPropagation(); classifyRiskActions(); }}
                                disabled={nomenclatureMappingLoading}
                                title="Classificar códigos como ações de risco"
                              >
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                Classificar ações de risco
                              </button>

                              <button 
                                className="btn btn-outline-primary btn-sm"
                                onClick={(e) => { e.stopPropagation(); classifyMasterDerivedActions(); }}
                                disabled={nomenclatureMappingLoading || masterDerivedLoading}
                                title="Classificar códigos como ações mestre/derivado"
                              >
                                <i className="fas fa-sitemap me-2"></i>
                                Classificar ações mestre/derivado
                              </button>

                              <button 
                                className="btn btn-outline-info btn-sm"
                                onClick={(e) => { e.stopPropagation(); openAiConversionModal(); }}
                                disabled={nomenclatureMappingLoading || !hasConsolidationData || aiModalLoading}
                                title="Converter elementos com IA para nomenclatura"
                              >
                                {aiModalLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Preparando conversão IA...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-robot me-2"></i>
                                    Converter elementos com IA
                                  </>
                                )}
                              </button>

                              <button 
                                className="btn btn-outline-success btn-sm"
                                onClick={(e) => { e.stopPropagation(); generateNomenclature(); }}
                                disabled={nomenclatureMappingLoading || nomenclatureMappingData.length === 0}
                                title="Gerar nomenclatura completa usando valores convertidos pela IA"
                              >
                                <i className="fas fa-magic me-2"></i>
                                Gerar nomenclatura
                              </button>

                              <button 
                                className="btn btn-outline-success btn-sm"
                                onClick={(e) => { e.stopPropagation(); saveCurrentClassification(); }}
                                disabled={nomenclatureMappingLoading || consolidationLoading || nomenclatureMappingData.length === 0}
                                title="Salvar classificação atual na base de dados"
                              >
                                <i className="fas fa-save me-2"></i>
                                Salvar classificação atual
                              </button>

                              <button 
                                className="btn btn-outline-danger btn-sm"
                                onClick={(e) => { e.stopPropagation(); deleteCurrentClassification(); }}
                                disabled={nomenclatureMappingLoading || consolidationLoading || !hasConsolidationData}
                                title="Apagar classificação salva na base de dados"
                              >
                                <i className="fas fa-trash me-2"></i>
                                Apagar classificação atual
                              </button>
                            </div>

                            {aiModalLoading && (
                              <div className="mb-3" style={{ minWidth: 280, maxWidth: 520 }}>
                                <div className="progress" style={{ height: 8 }}>
                                  <div
                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                    role="progressbar"
                                    style={{ width: `${Math.max(8, aiModalLoadingProgress)}%` }}
                                    aria-valuenow={aiModalLoadingProgress}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                  ></div>
                                </div>
                                <small className="text-muted">{aiModalLoadingMessage || 'Carregando elementos para conversão...'}</small>
                              </div>
                            )}

                            {nomenclatureMappingLoading ? (
                              <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="visually-hidden">Carregando...</span>
                                </div>
                                <p className="text-muted mt-3">Carregando dados de mapeamento...</p>
                              </div>
                            ) : nomenclatureMappingData.length > 0 ? (
                              <>
                                {/* Table */}
                                <div className="table-responsive">
                                  {(() => {
                                    const tableData = nomenclatureMappingData || [];
                                    
                                    return (
                                      <>
                                        <DataTable
                                          columns={[
                                            { key: 'area', label: 'Área', minWidth: 140, sortable: true, render: (_: any, row: any) => row.area || '-' },
                                            { key: 'processo', label: 'Processo', minWidth: 180, sortable: true, render: (_: any, row: any) => row.processo || '-' },
                                            { key: 'subprocesso', label: 'Subprocesso', minWidth: 180, sortable: true, render: (_: any, row: any) => row.subprocesso || '-' },
                                            { key: 'atividade', label: 'Atividade', minWidth: 180, sortable: true, render: (_: any, row: any) => row.atividade || '-' },
                                            { key: 'codigo', label: 'Código', minWidth: 120, sortable: true, render: (_: any, row: any) => <code className="text-muted">{row.codigo || '-'}</code> },
                                            {
                                              key: 'acaoRisco',
                                              label: 'Ação de risco?',
                                              minWidth: 130,
                                              sortable: true,
                                              render: (_: any, row: any) => (
                                                isRiskClassificationEnabled && riskClassificationData[row.codigo] ? (
                                                  <span className="badge bg-danger"><i className="fas fa-exclamation-triangle me-1"></i>S</span>
                                                ) : <span className="text-muted">-</span>
                                              )
                                            },
                                            {
                                              key: 'perfilMestre',
                                              label: 'Perfil mestre?',
                                              minWidth: 130,
                                              sortable: true,
                                              render: (_: any, row: any) => (
                                                Boolean(row.nomenclatura_derivado || row.derivedNomenclatura)
                                                  ? <span className="text-muted">&nbsp;</span>
                                                  : ((isMasterDerivedClassificationEnabled && masterDerivedClassificationData[row.codigo])
                                                    ? <span className={`badge ${masterDerivedPropagatedData[row.codigo] ? 'bg-success' : 'bg-primary'}`}><i className="fas fa-sitemap me-1"></i>S</span>
                                                    : <span className="text-muted">-</span>)
                                              )
                                            },
                                            {
                                              key: 'nomenclatura',
                                              label: 'Nomenclatura',
                                              minWidth: 260,
                                              sortable: true,
                                              render: (_: any, row: any) => (
                                                <div className="d-flex align-items-center justify-content-center">
                                                  <span className="text-success fw-bold font-monospace">{row.nomenclatura || '-'}</span>
                                                  {row.nomenclaturaExceedsLimit && row.nomenclatura !== '-' && (
                                                    <i className="fas fa-exclamation-triangle text-warning ms-2" title={`A nomenclatura excede ${Number(patternNameMaxChar || client?.pattern_name_max_char || 30)} caracteres (${row.nomenclatura?.length || 0} caracteres)`} style={{ fontSize: '14px' }}></i>
                                                  )}
                                                </div>
                                              )
                                            },
                                            {
                                              key: 'caracteres',
                                              label: '#',
                                              minWidth: 70,
                                              sortable: false,
                                              render: (_: any, row: any) => (
                                                <span className="badge bg-secondary font-monospace">{row.nomenclatura && row.nomenclatura !== '-' ? row.nomenclatura.length : 0}</span>
                                              )
                                            }
                                          ] as DataTableColumn<any>[]}
                                          data={tableData}
                                          keyExtractor={(_: any, index: number) => String(index)}
                                          sortColumn={nomenclatureSortColumn || null}
                                          sortDirection={nomenclatureSortDirection}
                                          onSort={(column: string, direction?: 'asc' | 'desc') => handleNomenclatureSort(column, direction)}
                                          striped
                                          hover
                                          bordered
                                          responsive
                                          size="sm"
                                          className="gpw-table"
                                          pagination
                                          clientSidePagination
                                          currentPage={nomenclaturePage}
                                          pageSize={nomenclaturePageSize}
                                          totalItems={tableData.length}
                                          onPageChange={(page: number) => setNomenclaturePage(page)}
                                          onPageSizeChange={(size: number) => {
                                            setNomenclaturePageSize(size);
                                            localStorage.setItem('gpw_nomenclature_pageSize', String(size));
                                            setNomenclaturePage(1);
                                          }}
                                          emptyMessage="Nenhum registro encontrado"
                                        />
                                      </>
                                    );
                                  })()}
                                </div>
                              </>
                            ) : (
                              <div className="text-center py-5">
                                {nomenclatureMappingLoading ? (
                                  <>
                                    <div className="spinner-border text-primary" role="status" style={{ width: 48, height: 48 }}>
                                      <span className="visually-hidden">Carregando...</span>
                                    </div>
                                    <p className="text-muted mt-3">Carregando dados de mapeamento...</p>
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-table fa-3x text-muted mb-3"></i>
                                    <h5 className="text-muted">Nenhum dado de mapeamento encontrado</h5>
                                    <p className="text-muted">
                                      Clique em "Atualizar" para carregar os dados de mapeamento das tabelas profilegen-logs e profilegen-userxa
                                    </p>
                                  </>
                                )}
                                {/* Auto-fetch for composite+activity clients when no consolidation exists */}
                                {!nomenclatureMappingLoading && !hasConsolidationData && String(client?.profileModel) === 'Posição Composto + Atividade Simples' && (
                                  (() => { fetchNomenclatureMappingData(); return null; })()
                                )}
                              </div>
                            )}
                          </>
                        ) : String(client?.profileModel) === 'Posição/Cargo Simples' ? (
                          <>
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={async (e) => { 
                                  e.stopPropagation(); 
                                  setSimplePositionsLoading(true); 
                                  try { 
                                    await fetchSimplePositionsAndLogs(); 
                                    // after refreshing positions/logs, reload consolidation to apply saved risk flags and profilenames
                                    await loadConsolidationRiskForSimple();
                                  } catch (err) { /* ignore */ } finally { setSimplePositionsLoading(false); } 
                                }}
                                disabled={simplePositionsLoading}
                              >
                                {simplePositionsLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Carregando...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-sync-alt me-2"></i>
                                    Atualizar
                                  </>
                                )}
                              </button>
                              <button 
                                className="btn btn-outline-warning btn-sm"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  try {
                                    // Build filtered positions same way as table rendering
                                    const q = (simpleSearchQ || '').toLowerCase().trim();
                                    const filteredPositions = positionsWithCodes.filter((p: any) => {
                                      if (!q) return true;
                                      const col = simpleSearchColumn;
                                      if (!col) {
                                        const combined = `${p.n1 || ''} ${p.n2 || ''} ${p.n3 || ''} ${p.n4 || ''} ${(p.users || []).length} ${(p.codes || []).join(' ')}`.toLowerCase();
                                        return combined.indexOf(q) !== -1;
                                      }
                                      if (col === 'n1') return String(p.n1 || '').toLowerCase().indexOf(q) !== -1;
                                      if (col === 'n2') return String(p.n2 || '').toLowerCase().indexOf(q) !== -1;
                                      if (col === 'n3') return String(p.n3 || '').toLowerCase().indexOf(q) !== -1;
                                      if (col === 'n4') return String(p.n4 || '').toLowerCase().indexOf(q) !== -1;
                                      if (col === 'userCount') return String((p.users || []).length).indexOf(q) !== -1;
                                      if (col === 'code') return (p.codes || []).some((c: any) => String(c || '').toLowerCase().indexOf(q) !== -1);
                                      return true;
                                    });

                                    // Flatten codes from filtered positions
                                    const visibleCodes: string[] = [];
                                    filteredPositions.forEach((p: any) => { (p.codes || []).forEach((c: any) => { if (c) visibleCodes.push(String(c)); }); });
                                    classifyRiskActions(visibleCodes);
                                  } catch (err) {
                                    classifyRiskActions();
                                  }
                                }}
                                disabled={simplePositionsLoading || nomenclatureMappingLoading}
                                title="Classificar códigos como ações de risco"
                              >
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                Classificar ações de risco
                              </button>
                              <button 
                                className="btn btn-outline-info btn-sm"
                                onClick={(e) => { e.stopPropagation(); openAiConversionModalPosicoesSimples(); }}
                                disabled={simplePositionsLoading || positionsWithCodes.length === 0 || !hasConsolidationData || aiModalLoading}
                                title="Converter elementos com IA"
                              >
                                {aiModalLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Preparando conversão IA...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-robot me-2"></i>
                                    Converter elementos com IA
                                  </>
                                )}
                              </button>
                              <button
                                className="btn btn-outline-success btn-sm"
                                onClick={(e) => { e.stopPropagation(); generateSimplePositionNomenclature(); }}
                                disabled={simplePositionsLoading || positionsWithCodes.length === 0}
                                title="Gerar nomenclatura"
                              >
                                <i className="fas fa-magic me-2"></i>
                                Gerar nomenclatura
                              </button>
                              <button
                                className="btn btn-outline-success btn-sm"
                                onClick={(e) => { e.stopPropagation(); saveSimpleClassification(); }}
                                disabled={simplePositionsLoading || consolidationLoading}
                                title="Salvar classificação atual"
                              >
                                <i className="fas fa-save me-2"></i>
                                Salvar classificação atual
                              </button>
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={(e) => { e.stopPropagation(); deleteCurrentClassification(); }}
                                disabled={simplePositionsLoading || consolidationLoading}
                                title="Apagar classificação atual"
                              >
                                <i className="fas fa-trash me-2"></i>
                                Apagar classificação atual
                              </button>
                            </div>

                            {aiModalLoading && (
                              <div className="mb-3" style={{ minWidth: 280, maxWidth: 520 }}>
                                <div className="progress" style={{ height: 8 }}>
                                  <div
                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                    role="progressbar"
                                    style={{ width: `${Math.max(8, aiModalLoadingProgress)}%` }}
                                    aria-valuenow={aiModalLoadingProgress}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                  ></div>
                                </div>
                                <small className="text-muted">{aiModalLoadingMessage || 'Carregando elementos para conversão...'}</small>
                              </div>
                            )}

                            {simplePositionsLoading ? (
                              <div className="text-center py-3"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Carregando...</span></div></div>
                            ) : positionsWithCodes.length > 0 ? (
                              <>
                                <div className="table-responsive">
                                  {(() => {
                                    // Flatten positions into rows (one per code) first
                                    const allRows: any[] = [];
                                    (positionsWithCodes || []).forEach((p: any) => {
                                      const codes = p.codes || [];
                                      if (!codes.length) allRows.push({ ...p, code: null });
                                      else codes.forEach((c: any) => allRows.push({ ...p, code: c }));
                                    });

                                    return (
                                      <>
                                        <DataTable
                                          columns={[
                                            { key: 'n1', label: 'N1', minWidth: 140, sortable: true, render: (_: any, row: any) => row.n1 || '-' },
                                            { key: 'n2', label: 'N2', minWidth: 140, sortable: true, render: (_: any, row: any) => row.n2 || '-' },
                                            { key: 'n3', label: 'N3', minWidth: 140, sortable: true, render: (_: any, row: any) => row.n3 || '-' },
                                            { key: 'n4', label: 'N4', minWidth: 140, sortable: true, render: (_: any, row: any) => row.n4 || '-' },
                                            { key: 'userCount', label: 'Usuários', minWidth: 110, sortable: true, render: (_: any, row: any) => (row.users || []).length },
                                            { key: 'code', label: 'Códigos', minWidth: 120, sortable: true, render: (_: any, row: any) => row.code ? <code className="text-monospace text-body">{row.code}</code> : <span className="text-muted">-</span> },
                                            {
                                              key: 'acaoRisco',
                                              label: 'Ação de risco?',
                                              minWidth: 130,
                                              sortable: true,
                                              render: (_: any, row: any) => (
                                                isRiskClassificationEnabled && row.code && riskClassificationData[String(row.code)]
                                                  ? <span className="badge bg-danger"><i className="fas fa-exclamation-triangle me-1"></i>S</span>
                                                  : <span className="text-muted">-</span>
                                              )
                                            },
                                            {
                                              key: 'nomenclatura',
                                              label: 'Nomenclatura',
                                              minWidth: 250,
                                              sortable: true,
                                              render: (_: any, row: any) => {
                                                const key = `${String(row.code || '')}||${String(row.n1 || '').trim()}||${String(row.n2 || '').trim()}||${String(row.n3 || '').trim()}||${String(row.n4 || '').trim()}`;
                                                const value = simplePositionNomenclature && simplePositionNomenclature[key];
                                                if (value && value.nomenclatura) {
                                                  return (
                                                    <div>
                                                      <div className="text-success fw-bold font-monospace">{value.nomenclatura}</div>
                                                      {value.nomenclaturaExceedsLimit && <i className="fas fa-exclamation-triangle text-warning ms-2" title="Nomenclatura excede limite" style={{ fontSize: 12 }}></i>}
                                                    </div>
                                                  );
                                                }
                                                return <span className="text-muted">-</span>;
                                              }
                                            },
                                            {
                                              key: 'len',
                                              label: '#',
                                              minWidth: 70,
                                              sortable: false,
                                              render: (_: any, row: any) => {
                                                const key = `${String(row.code || '')}||${String(row.n1 || '').trim()}||${String(row.n2 || '').trim()}||${String(row.n3 || '').trim()}||${String(row.n4 || '').trim()}`;
                                                const value = simplePositionNomenclature && simplePositionNomenclature[key];
                                                return <span className="badge bg-secondary font-monospace">{value && value.nomenclatura && value.nomenclatura !== '-' ? value.nomenclatura.length : 0}</span>;
                                              }
                                            }
                                          ] as DataTableColumn<any>[]}
                                          data={allRows}
                                          keyExtractor={(row: any, i: number) => `${row.n1}-${row.n2}-${row.n3}-${row.n4}-${i}`}
                                          striped
                                          hover
                                          bordered
                                          responsive
                                          size="sm"
                                          className="gpw-table"
                                          pagination
                                          clientSidePagination
                                          currentPage={simplePage}
                                          pageSize={simplePageSize}
                                          totalItems={allRows.length}
                                          onPageChange={(page: number) => setSimplePage(page)}
                                          onPageSizeChange={(size: number) => {
                                            setSimplePageSize(size);
                                            localStorage.setItem('gpw_simple_pageSize', String(size));
                                            setSimplePage(1);
                                          }}
                                          emptyMessage="Nenhum registro encontrado"
                                        />
                                      </>
                                    );
                                  })()}
                                </div>
                              </>
                            ) : (
                              <div className="text-center py-3">
                                <i className="fas fa-table fa-2x text-muted mb-2"></i>
                                <p className="text-muted mb-0">Nenhuma posição encontrada. Clique em "Atualizar" para carregar profilegen-userxpos e profilegen-logs.</p>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="alert alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            A seção "Classificar ações e gerar nomenclatura de perfis simples" está disponível apenas para clientes com modelo de perfil <strong>Atividade Simples</strong>.
                            <br />
                            Modelo atual: <strong>{String(client?.profileModel)}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Nova Seção: Gerar nomenclatura de perfis derivados */}
                  {(String(client?.profileModel) === 'Atividade Simples' || String(client?.profileModel) === 'Posição Composto + Atividade Simples') && (
                    <div className="mb-4">
                      {/* Accordion Header for Derived Profiles Nomenclature Generation */}
                      <div 
                        className="border rounded-top cursor-pointer p-3 bg-body-secondary d-flex justify-content-between align-items-center accordion-header"
                        onClick={() => setNomenclatureSectionExpanded(prev => ({ ...prev, perfisDerivados: !prev.perfisDerivados }))}
                      >
                        <div>
                          <h5 className="text-primary mb-1 d-flex align-items-center">
                            <i className="fas fa-sitemap me-2"></i>
                            Gerar nomenclatura de perfis derivados
                          </h5>
                          <p className="text-body-secondary mb-0 small">
                            Geração de nomenclatura para perfis derivados baseados nos perfis simples.
                          </p>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <i className={`fas fa-chevron-down text-primary accordion-icon ${nomenclatureSectionExpanded.perfisDerivados ? 'rotated' : ''}`}></i>
                        </div>
                      </div>
                      
                      {/* Accordion Content */}
                      <div className={`accordion-content nomenclature-accordion-content ${nomenclatureSectionExpanded.perfisDerivados ? 'expanded' : 'collapsed'}`}>
                        <div className="border border-top-0 rounded-bottom p-4" style={{ minHeight: 'auto', overflow: 'visible' }}>
                          {masterRecordsLoading ? (
                            <div className="text-center py-3">
                              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Carregando...</span></div>
                            </div>
                          ) : (
                            <>
                              {/* Action buttons (Atualizar) above search controls */}
                              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={async (e) => { e.stopPropagation(); setMasterRecordsLoading(true); try { await checkAndLoadConsolidationData(); } catch (err) { /* ignore */ } finally { setMasterRecordsLoading(false); } }}
                                  disabled={masterRecordsLoading}
                                >
                                  {masterRecordsLoading ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                      Carregando...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-sync-alt me-2"></i>
                                      Atualizar
                                    </>
                                  )}
                                </button>
                                <button
                                  className="btn btn-outline-success btn-sm"
                                  onClick={(e) => { e.stopPropagation(); generateDerivedNomenclature(); }}
                                  disabled={masterRecordsLoading || !masterRecords || masterRecords.length === 0}
                                  title="Gerar nomenclatura para perfis derivados"
                                >
                                  <i className="fas fa-magic me-2"></i>
                                  Gerar nomenclatura (derivados)
                                </button>
                                <button
                                  className="btn btn-outline-success btn-sm"
                                  onClick={(e) => { e.stopPropagation(); saveDerivedClassification(); }}
                                  disabled={masterRecordsLoading || !masterRecords || masterRecords.length === 0}
                                  title="Salvar classificação de derivadas na base de dados"
                                >
                                  <i className="fas fa-save me-2"></i>
                                  Salvar classificação de derivadas
                                </button>
                              </div>

                              {/* Table */}
                              <div className="table-responsive">
                                {(() => {
                                  const filtered = getFilteredAndSortedMasterRecords();
                                  const total = filtered.length;
                                  const totalPages = Math.max(1, Math.ceil(total / (derivedPageSize || 25)));
                                  const page = Math.min(Math.max(1, derivedPage), totalPages);
                                  const start = (page - 1) * (derivedPageSize || 25);
                                  return (
                                    <>
                                      <DataTable
                                        columns={[
                                          { key: 'area', label: 'Área', minWidth: 140, sortable: true, render: (_: any, row: any) => row.area || '-' },
                                          { key: 'processo', label: 'Processo', minWidth: 180, sortable: true, render: (_: any, row: any) => row.processo || '-' },
                                          { key: 'subprocesso', label: 'Subprocesso', minWidth: 180, sortable: true, render: (_: any, row: any) => row.subprocesso || '-' },
                                          { key: 'atividade', label: 'Atividade', minWidth: 180, sortable: true, render: (_: any, row: any) => row.atividade || '-' },
                                          {
                                            key: 'acaoRisco',
                                            label: 'Ação de risco?',
                                            minWidth: 130,
                                            sortable: true,
                                            render: (_: any, row: any) => isRiskClassificationEnabled && riskClassificationData[row.codigo]
                                              ? <span className="badge bg-danger"><i className="fas fa-exclamation-triangle me-1"></i>S</span>
                                              : <span className="text-muted">-</span>
                                          },
                                          {
                                            key: 'perfilMestre',
                                            label: 'Perfil mestre?',
                                            minWidth: 130,
                                            sortable: true,
                                            render: (_: any, row: any) => Boolean(row.nomenclatura_derivado || row.derivedNomenclatura)
                                              ? <span className="text-muted">-</span>
                                              : ((isMasterDerivedClassificationEnabled && masterDerivedClassificationData[row.codigo])
                                                ? <span className={`badge ${masterDerivedPropagatedData[row.codigo] ? 'bg-success' : 'bg-primary'}`}><i className="fas fa-sitemap me-1"></i>S</span>
                                                : <span className="text-muted">-</span>)
                                          },
                                          {
                                            key: 'perfilDerivado',
                                            label: 'Perfil derivado?',
                                            minWidth: 140,
                                            sortable: true,
                                            render: (_: any, row: any) => Boolean(row.nomenclatura_derivado || row.derivedNomenclatura)
                                              ? <span className="badge bg-info"><i className="fas fa-code-branch me-1"></i>S</span>
                                              : <span className="text-muted">-</span>
                                          },
                                          {
                                            key: 'nomenclatura_mestre',
                                            label: 'Nomenclatura (mestre)',
                                            minWidth: 250,
                                            sortable: true,
                                            render: (_: any, row: any) => {
                                              const masterName = row.nomenclatura || '-';
                                              const tableMax = Number(patternNameMaxChar || client?.pattern_name_max_char || 30);
                                              const masterLength = masterName && masterName !== '-' ? masterName.length : 0;
                                              const exceedsLimitMaster = masterLength > tableMax;
                                              return (
                                                <div className="d-flex align-items-center justify-content-center">
                                                  <span className="text-success fw-bold font-monospace">{masterName}</span>
                                                  {exceedsLimitMaster && masterName !== '-' && <i className="fas fa-exclamation-triangle text-warning ms-2" title={`A nomenclatura mestre excede ${tableMax} caracteres (${masterLength} caracteres)`} style={{ fontSize: 14 }}></i>}
                                                </div>
                                              );
                                            }
                                          },
                                          {
                                            key: 'nomenclatura_derivado',
                                            label: 'Nomenclatura (derivado)',
                                            minWidth: 260,
                                            sortable: true,
                                            render: (_: any, row: any) => {
                                              const derivedName = row.nomenclatura_derivado || row.derivedNomenclatura || '-';
                                              const tableMax = Number(patternNameMaxChar || client?.pattern_name_max_char || 30);
                                              const derivedLength = derivedName && derivedName !== '-' ? derivedName.length : 0;
                                              const exceedsLimitDerived = derivedLength > tableMax;
                                              return (
                                                <div className="d-flex align-items-center justify-content-center">
                                                  <span className="text-success fw-bold font-monospace">{derivedName}</span>
                                                  {exceedsLimitDerived && derivedName !== '-' && <i className="fas fa-exclamation-triangle text-warning ms-2" title={`A nomenclatura derivada excede ${tableMax} caracteres (${derivedLength} caracteres)`} style={{ fontSize: 14 }}></i>}
                                                </div>
                                              );
                                            }
                                          },
                                          {
                                            key: 'caracteres',
                                            label: '#',
                                            minWidth: 70,
                                            sortable: false,
                                            render: (_: any, row: any) => {
                                              const derivedName = row.nomenclatura_derivado || row.derivedNomenclatura || '-';
                                              const derivedLength = derivedName && derivedName !== '-' ? derivedName.length : 0;
                                              return <span className="badge bg-secondary font-monospace">{derivedLength}</span>;
                                            }
                                          }
                                        ] as DataTableColumn<any>[]}
                                        data={filtered}
                                        keyExtractor={(row: any, idx: number) => `${row.area}-${row.processo}-${row.subprocesso}-${row.atividade}-${idx}`}
                                        sortColumn={derivedSortColumn || null}
                                        sortDirection={derivedSortDirection}
                                        onSort={(column: string, direction?: 'asc' | 'desc') => {
                                          if (direction) {
                                            setDerivedSortColumn(column);
                                            setDerivedSortDirection(direction);
                                          } else if (derivedSortColumn === column) setDerivedSortDirection((d: 'asc' | 'desc') => d === 'asc' ? 'desc' : 'asc');
                                          else setDerivedSortColumn(column);
                                        }}
                                        striped
                                        hover
                                        bordered
                                        responsive
                                        size="sm"
                                        className="gpw-table"
                                        pagination
                                        clientSidePagination
                                        currentPage={derivedPage}
                                        pageSize={derivedPageSize}
                                        totalItems={filtered.length}
                                        onPageChange={(page: number) => setDerivedPage(page)}
                                        onPageSizeChange={(size: number) => {
                                          setDerivedPageSize(size);
                                          localStorage.setItem('gpw_derived_pageSize', String(size));
                                          setDerivedPage(1);
                                        }}
                                        emptyMessage="Nenhum registro encontrado"
                                      />
                                    </>
                                  );
                                })()}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Nova Seção: Gerar nomenclatura de perfis compostos (apenas modelo combinado) */}
                  {String(client?.profileModel) === 'Posição Composto + Atividade Simples' && (
                    <div className="mb-4">
                      <div 
                        className="border rounded-top cursor-pointer p-3 bg-body-secondary d-flex justify-content-between align-items-center accordion-header"
                        onClick={() => setNomenclatureSectionExpanded(prev => ({ ...prev, perfisCompostos: !prev.perfisCompostos }))}
                      >
                        <div>
                          <h5 className="text-primary mb-1 d-flex align-items-center">
                            <i className="fas fa-layer-group me-2"></i>
                            Gerar nomenclatura de perfis compostos
                          </h5>
                          <p className="text-body-secondary mb-0 small">
                            Geração e persistência de nomenclaturas para perfis compostos (modelo combinado).
                          </p>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <i className={`fas fa-chevron-down text-primary accordion-icon ${nomenclatureSectionExpanded.perfisCompostos ? 'rotated' : ''}`}></i>
                        </div>
                      </div>

                      <div className={`accordion-content nomenclature-accordion-content ${nomenclatureSectionExpanded.perfisCompostos ? 'expanded' : 'collapsed'}`}>
                        <div className="border border-top-0 rounded-bottom p-4">
                          <div className="d-flex gap-2 mb-3">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={async (e) => { e.stopPropagation(); setCompositeUserPositionsLoading(true); try { await fetchCompositeUserPositions(); } catch (err) { /* ignore */ } finally { setCompositeUserPositionsLoading(false); } }}
                              disabled={compositeUserPositionsLoading}
                            >
                              {compositeUserPositionsLoading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Carregando...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-sync-alt me-2"></i>
                                  Atualizar
                                </>
                              )}
                            </button>
                            <button 
                              className="btn btn-outline-info btn-sm"
                              onClick={(e) => { e.stopPropagation(); openAiConversionModalCompostos(); }}
                              disabled={compositeUserPositionsLoading || !compositeUserPositions || compositeUserPositions.length === 0 || aiModalLoading}
                              title="Converter elementos com IA para nomenclatura (compostos)"
                            >
                              {aiModalLoading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Preparando conversão IA...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-robot me-2"></i>
                                  Converter elementos com IA
                                </>
                              )}
                            </button>
                            <button className="btn btn-outline-success btn-sm" onClick={() => generateCompositeNomenclature()}>
                              <i className="fas fa-magic me-2"></i>
                              Gerar nomenclatura (compostos)
                            </button>
                            <button className="btn btn-outline-success btn-sm" onClick={() => saveCompositeClassification()}>
                              <i className="fas fa-save me-2"></i>
                              Salvar classificação de compostos
                            </button>
                            <button 
                              className="btn btn-outline-danger btn-sm"
                              onClick={(e) => { e.stopPropagation(); deleteCompositeClassification(); }}
                              disabled={consolidationLoading || (!compositeNomenclatureGenerated && !(Array.isArray(compositeUserPositions) && compositeUserPositions.some(it => it.composite_name && String(it.composite_name).trim() !== '')))}
                              title="Apagar nomenclaturas compostas salvas para este cliente"
                            >
                              <i className="fas fa-trash me-2"></i>
                              Apagar classificação atual
                            </button>
                          </div>

                          {aiModalLoading && (
                            <div className="mb-3" style={{ minWidth: 280, maxWidth: 520 }}>
                              <div className="progress" style={{ height: 8 }}>
                                <div
                                  className="progress-bar progress-bar-striped progress-bar-animated"
                                  role="progressbar"
                                  style={{ width: `${Math.max(8, aiModalLoadingProgress)}%` }}
                                  aria-valuenow={aiModalLoadingProgress}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                ></div>
                              </div>
                              <small className="text-muted">{aiModalLoadingMessage || 'Carregando elementos para conversão...'}</small>
                            </div>
                          )}

                          {/* keep only action buttons and a refresh control */}
                          
                          <div className="mt-3">
                            {compositeUserPositionsLoading ? (
                              <div className="text-center py-2"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Carregando...</span></div></div>
                            ) : (
                              (() => {
                                const filtered = getFilteredAndSortedCompositePositions();
                                const total = filtered.length;
                                const totalPages = Math.max(1, Math.ceil(total / (compositePageSize || 25)));
                                const page = Math.min(Math.max(1, compositePage), totalPages);
                                const start = (page - 1) * (compositePageSize || 25);
                                return (
                                  <>
                                    <div className="table-responsive">
                                      <DataTable
                                        columns={[
                                          { key: 'n1', label: 'N1', minWidth: 140, sortable: true, render: (_: any, row: any) => row.n1 || '-' },
                                          { key: 'n2', label: 'N2', minWidth: 140, sortable: true, render: (_: any, row: any) => row.n2 || '-' },
                                          { key: 'n3', label: 'N3', minWidth: 140, sortable: true, render: (_: any, row: any) => row.n3 || '-' },
                                          { key: 'n4', label: 'N4', minWidth: 140, sortable: true, render: (_: any, row: any) => row.n4 || '-' },
                                          {
                                            key: 'nomenclatura',
                                            label: 'Nomenclatura',
                                            minWidth: 260,
                                            sortable: true,
                                            render: (_: any, row: any) => {
                                              const name = row.nomenclatura || '-';
                                              const nameLen = name && name !== '-' ? String(name).length : 0;
                                              const maxAllowed = Number(patternNameMaxChar || client?.pattern_name_max_char || 30);
                                              const exceeds = name !== '-' && nameLen > maxAllowed;
                                              return compositeNomenclatureGenerated ? (
                                                <div className="d-flex align-items-center justify-content-center">
                                                  <span className="text-success fw-bold font-monospace">{name}</span>
                                                  {exceeds && <i className="fas fa-exclamation-triangle text-warning ms-2" title={`Nomenclatura excede ${maxAllowed} caracteres (${nameLen})`} style={{ fontSize: 14 }} />}
                                                </div>
                                              ) : <span className="text-muted">-</span>;
                                            }
                                          },
                                          {
                                            key: 'len',
                                            label: '#',
                                            minWidth: 70,
                                            sortable: true,
                                            render: (_: any, row: any) => {
                                              const name = row.nomenclatura || '-';
                                              const nameLen = name && name !== '-' ? String(name).length : 0;
                                              return compositeNomenclatureGenerated ? <span className="badge bg-secondary font-monospace">{nameLen}</span> : <span className="text-muted">-</span>;
                                            }
                                          }
                                        ] as DataTableColumn<any>[]}
                                        data={filtered}
                                        keyExtractor={(row: any, i: number) => `${row.n1 || ''}-${row.n2 || ''}-${row.n3 || ''}-${row.n4 || ''}-${i}`}
                                        sortColumn={compositeSortColumn || null}
                                        sortDirection={compositeSortDirection}
                                        onSort={(column: string, direction?: 'asc' | 'desc') => {
                                          if (direction) {
                                            setCompositeSortColumn(column);
                                            setCompositeSortDirection(direction);
                                          } else if (compositeSortColumn === column) setCompositeSortDirection((d: 'asc' | 'desc') => d === 'asc' ? 'desc' : 'asc');
                                          else setCompositeSortColumn(column);
                                        }}
                                        striped
                                        hover
                                        bordered
                                        responsive
                                        size="sm"
                                        className="gpw-table"
                                        pagination
                                        clientSidePagination
                                        currentPage={compositePage}
                                        pageSize={compositePageSize}
                                        totalItems={filtered.length}
                                        onPageChange={(page: number) => setCompositePage(page)}
                                        onPageSizeChange={(size: number) => {
                                          setCompositePageSize(size);
                                          localStorage.setItem('gpw_composite_pageSize', String(size));
                                          setCompositePage(1);
                                        }}
                                        emptyMessage="Nenhum registro encontrado"
                                      />
                                    </div>
                                  </>
                                );
                              })()
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : step === 4 && String(client?.profileModel) === 'Posição/Cargo Simples' ? (
                <div>
                  <div className="mb-3 text-center">
                    <h4 className="text-primary mb-2"><i className="fas fa-check-circle me-2"></i>Validação e Download</h4>
                    <p className="text-muted mb-0">Visão detalhada dos perfis que cada usuário receberá.</p>
                  </div>

                  {validationSavedLoadInProgress && (
                    <div className="mb-3">
                      <ProgressBar
                        current={validationSavedLoadCurrent}
                        total={Math.max(validationSavedLoadTotal, 1)}
                        message={validationSavedLoadMessage || 'Carregando dados salvos do banco...'}
                      />
                    </div>
                  )}

                  <div className="d-flex gap-2 mb-3">
                    <button className="btn btn-outline-primary btn-sm" onClick={() => loadValidationRows({ withProgress: true })} disabled={validationLoading || validationSaving || validationDeleting || validationSavedLoadInProgress}>
                      {validationLoading ? (<><span className="spinner-border spinner-border-sm me-2"/>Carregando...</>) : (<><i className="fas fa-sync-alt me-2"></i>Carregar</>)}
                    </button>
                    <button className="btn btn-outline-warning btn-sm" onClick={() => saveValidationRows()} disabled={validationSaving || validationLoading || validationDeleting || validationSavedLoadInProgress || validationRows.length === 0}>
                      {validationSaving ? (<><span className="spinner-border spinner-border-sm me-2"/>Salvando...</>) : (<><i className="fas fa-save me-2"></i>Salvar</>)}
                    </button>
                    <button className="btn btn-outline-danger btn-sm" onClick={() => clearSavedValidationRows()} disabled={validationDeleting || validationSaving || validationLoading || validationSavedLoadInProgress}>
                      {validationDeleting ? (<><span className="spinner-border spinner-border-sm me-2"/>Apagando...</>) : (<><i className="fas fa-trash-alt me-2"></i>Apagar salvos</>)}
                    </button>
                    <button className="btn btn-outline-success btn-sm" onClick={() => {
                      // build CSV from validationRows for Posição/Cargo Simples
                      const hdr = ['usuario','n1','n2','n3','n4','codigo','perfil','utilizada'];
                      const rows = validationRows.map(r => hdr.map(h => {
                        let val = String(r[h] || '');
                        return val.replace(/"/g, '""');
                      }).map(v => `"${v}"`).join(','));
                      const csv = [hdr.join(','), ...rows].join('\r\n');
                      // Add UTF-8 BOM to ensure proper encoding for accented characters in Excel
                      const csvWithBOM = '\ufeff' + csv;
                      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `validation_${clientId || 'client'}.csv`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }} disabled={validationRows.length === 0}>
                      <i className="fas fa-download me-2"></i>Download CSV
                    </button>
                    {validationLoadedFromDb && (
                      <span className="badge bg-info-subtle text-info-emphasis align-self-center">Dados carregados do banco</span>
                    )}
                    <div style={{ flex: 1 }} />
                  </div>

                  <div>
                    {validationLoading ? (
                      <div className="text-center py-4"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Carregando...</span></div></div>
                    ) : validationRows.length === 0 ? (
                      validationSavedLoadInProgress ? (
                        <div className="text-center text-muted py-4">Carregando dados salvos do banco. Aguarde a conclusão para visualizar os registros.</div>
                      ) : (
                      <div className="text-center text-muted py-4">Nenhum registro de validação disponível. Clique em "Carregar" para buscar os dados.</div>
                      )
                    ) : (
                      (() => {
                        const filtered = validationRows || [];
                        return (
                          <>
                            <div className="table-responsive">
                              <DataTable
                                columns={[
                                  { key: 'usuario', label: 'Usuário', minWidth: 170, sortable: true, render: (_: any, row: any) => row.usuario || '-' },
                                  { key: 'usuario_name', label: 'Nome Usuário', minWidth: 200, sortable: true, render: (_: any, row: any) => row.usuario_name || '-' },
                                  { key: 'n1', label: 'N1', minWidth: 130, sortable: true, render: (_: any, row: any) => row.n1 || '-' },
                                  { key: 'n2', label: 'N2', minWidth: 130, sortable: true, render: (_: any, row: any) => row.n2 || '-' },
                                  { key: 'n3', label: 'N3', minWidth: 130, sortable: true, render: (_: any, row: any) => row.n3 || '-' },
                                  { key: 'n4', label: 'N4', minWidth: 130, sortable: true, render: (_: any, row: any) => row.n4 || '-' },
                                  { key: 'codigo', label: 'Código', minWidth: 120, sortable: true, render: (_: any, row: any) => <code className="text-muted">{row.codigo || '-'}</code> },
                                  {
                                    key: 'perfil',
                                    label: 'Perfil',
                                    minWidth: 220,
                                    sortable: true,
                                    render: (_: any, row: any) => {
                                      const missingPerfil = !row.perfil || String(row.perfil).trim() === '-';
                                      return missingPerfil ? <span className="text-muted">-</span> : <span className="text-success fw-bold font-monospace">{row.perfil}</span>;
                                    }
                                  },
                                  {
                                    key: 'utilizada',
                                    label: 'Utilizada?',
                                    minWidth: 120,
                                    sortable: true,
                                    render: (_: any, row: any) => <span className={`badge ${row.utilizada === 'S' ? 'bg-success' : 'bg-danger'}`}>{row.utilizada || 'N'}</span>
                                  }
                                ] as DataTableColumn<any>[]}
                                data={filtered}
                                keyExtractor={(_: any, i: number) => String(i)}
                                sortColumn={validationSortColumn || null}
                                sortDirection={validationSortDirection}
                                onSort={(column: string, direction?: 'asc' | 'desc') => {
                                  if (direction) {
                                    setValidationSortColumn(column);
                                    setValidationSortDirection(direction);
                                  } else if (validationSortColumn === column) setValidationSortDirection((d: 'asc' | 'desc') => d === 'asc' ? 'desc' : 'asc');
                                  else { setValidationSortColumn(column); setValidationSortDirection('asc'); }
                                }}
                                striped
                                hover
                                bordered
                                responsive
                                size="sm"
                                className="gpw-table"
                                pagination
                                clientSidePagination
                                currentPage={validationPage}
                                pageSize={validationPageSize}
                                totalItems={filtered.length}
                                onPageChange={(page: number) => setValidationPage(page)}
                                onPageSizeChange={(size: number) => {
                                  setValidationPageSize(size);
                                  localStorage.setItem('gpw_validation_pageSize', String(size));
                                  setValidationPage(1);
                                }}
                                emptyMessage="Nenhum registro encontrado"
                              />
                            </div>
                          </>
                        );
                      })()
                    )}
                  </div>
                </div>
              ) : step === 6 && String(client?.profileModel) !== 'Posição/Cargo Simples' && (String(client?.profileModel) === 'Atividade Simples' || String(client?.profileModel) === 'Posição Composto + Atividade Simples') ? (
                <div>
                  <div className="mb-3 text-center">
                    <h4 className="text-primary mb-2"><i className="fas fa-check-circle me-2"></i>Validação e Download</h4>
                    {String(client?.profileModel) === 'Posição Composto + Atividade Simples' ? (
                      <p className="text-muted mb-0">Visão detalhada dos perfis compostos (por posição) que cada usuário receberá, mostrando a estrutura organizacional (N1-N4) e os perfis simples contidos em cada perfil composto.</p>
                    ) : (
                      <p className="text-muted mb-0">Visão detalhada dos perfis que cada usuário receberá.</p>
                    )}
                  </div>

                  {validationSavedLoadInProgress && (
                    <div className="mb-3">
                      <ProgressBar
                        current={validationSavedLoadCurrent}
                        total={Math.max(validationSavedLoadTotal, 1)}
                        message={validationSavedLoadMessage || 'Carregando dados salvos do banco...'}
                      />
                    </div>
                  )}

                  <div className="d-flex gap-2 mb-3">
                    <button className="btn btn-outline-primary btn-sm" onClick={() => loadValidationRows({ withProgress: true })} disabled={validationLoading || validationSaving || validationDeleting || validationSavedLoadInProgress}>
                      {validationLoading ? (<><span className="spinner-border spinner-border-sm me-2"/>Carregando...</>) : (<><i className="fas fa-sync-alt me-2"></i>Carregar</>)}
                    </button>
                    <button className="btn btn-outline-warning btn-sm" onClick={() => saveValidationRows()} disabled={validationSaving || validationLoading || validationDeleting || validationSavedLoadInProgress || validationRows.length === 0}>
                      {validationSaving ? (<><span className="spinner-border spinner-border-sm me-2"/>Salvando...</>) : (<><i className="fas fa-save me-2"></i>Salvar</>)}
                    </button>
                    <button className="btn btn-outline-danger btn-sm" onClick={() => clearSavedValidationRows()} disabled={validationDeleting || validationSaving || validationLoading || validationSavedLoadInProgress}>
                      {validationDeleting ? (<><span className="spinner-border spinner-border-sm me-2"/>Apagando...</>) : (<><i className="fas fa-trash-alt me-2"></i>Apagar salvos</>)}
                    </button>
                    <button className="btn btn-outline-success btn-sm" onClick={() => {
                      // build CSV from validationRows
                      const isPositionComposite = String(client?.profileModel) === 'Posição Composto + Atividade Simples';
                      const hdr = isPositionComposite 
                        ? ['usuario','area_usuario','n1','n2','n3','n4','perfil_composto','area_perfil','processo','subprocesso','atividade','codigo','perfil','utilizada']
                        : ['usuario','area','processo','subprocesso','atividade','codigo','perfil','utilizada'];
                      const rows = validationRows.map(r => hdr.map(h => {
                        let val = String(r[h] || '');
                        // Replace special marker with readable text
                        if (h === 'perfil_composto' && val === 'DIRECT_ATTRIBUTION') {
                          val = 'Atribuição direta ao usuário';
                        }
                        return val.replace(/"/g, '""');
                      }).map(v => `"${v}"`).join(','));
                      const csv = [hdr.join(','), ...rows].join('\r\n');
                      // Add UTF-8 BOM to ensure proper encoding for accented characters in Excel
                      const csvWithBOM = '\ufeff' + csv;
                      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `validation_${clientId || 'client'}.csv`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }} disabled={validationRows.length === 0}>
                      <i className="fas fa-download me-2"></i>Download CSV
                    </button>
                    {validationLoadedFromDb && (
                      <span className="badge bg-info-subtle text-info-emphasis align-self-center">Dados carregados do banco</span>
                    )}
                    <div style={{ flex: 1 }} />
                  </div>

                  <div>
                    {validationLoading ? (
                      <div className="text-center py-4"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Carregando...</span></div></div>
                    ) : validationRows.length === 0 ? (
                      validationSavedLoadInProgress ? (
                        <div className="text-center text-muted py-4">Carregando dados salvos do banco. Aguarde a conclusão para visualizar os registros.</div>
                      ) : (
                      <div className="text-center text-muted py-4">Nenhum registro de validação disponível. Clique em "Carregar" para buscar os dados.</div>
                      )
                    ) : (
                      (() => {
                        const isPositionComposite = String(client?.profileModel) === 'Posição Composto + Atividade Simples';
                        const filtered = validationRows || [];
                        return (
                          <>
                            <div className="table-responsive">
                              <DataTable
                                columns={(
                                  isPositionComposite
                                    ? [
                                        { key: 'usuario', label: 'Usuário', minWidth: 170, sortable: true, render: (_: any, row: any) => row.usuario || '-' },
                                        { key: 'usuario_name', label: 'Nome Usuário', minWidth: 200, sortable: true, render: (_: any, row: any) => row.usuario_name || '-' },
                                        { key: 'area_usuario', label: 'Área Usuário', minWidth: 160, sortable: true, render: (_: any, row: any) => row.area_usuario || '-' },
                                        { key: 'n1', label: 'N1', minWidth: 120, sortable: true, render: (_: any, row: any) => row.n1 || '-' },
                                        { key: 'n2', label: 'N2', minWidth: 120, sortable: true, render: (_: any, row: any) => row.n2 || '-' },
                                        { key: 'n3', label: 'N3', minWidth: 120, sortable: true, render: (_: any, row: any) => row.n3 || '-' },
                                        { key: 'n4', label: 'N4', minWidth: 120, sortable: true, render: (_: any, row: any) => row.n4 || '-' },
                                        {
                                          key: 'perfil_composto',
                                          label: 'Perfil (Composto)',
                                          minWidth: 220,
                                          sortable: true,
                                          render: (_: any, row: any) => row.perfil_composto === 'DIRECT_ATTRIBUTION'
                                            ? <span className="fw-bold font-monospace" style={{ color: '#ff8c00' }}>Atribuição direta ao usuário</span>
                                            : <span className="text-primary fw-bold font-monospace">{row.perfil_composto || '-'}</span>
                                        },
                                        { key: 'area_perfil', label: 'Área Perfil', minWidth: 150, sortable: true, render: (_: any, row: any) => row.area_perfil || '-' },
                                        { key: 'processo', label: 'Processo', minWidth: 170, sortable: true, render: (_: any, row: any) => row.processo || '-' },
                                        { key: 'subprocesso', label: 'Subprocesso', minWidth: 170, sortable: true, render: (_: any, row: any) => row.subprocesso || '-' },
                                        { key: 'atividade', label: 'Atividade', minWidth: 170, sortable: true, render: (_: any, row: any) => row.atividade || '-' },
                                        { key: 'codigo', label: 'Código', minWidth: 120, sortable: true, render: (_: any, row: any) => <code className="text-muted">{row.codigo || '-'}</code> },
                                        {
                                          key: 'perfil',
                                          label: 'Perfil (Simples)',
                                          minWidth: 240,
                                          sortable: true,
                                          render: (_: any, row: any) => {
                                            const missingPerfil = !row.perfil || String(row.perfil).trim() === '-';
                                            const hasMasterWarning = row.master_sem_orglevel === true;
                                            return missingPerfil ? (
                                              <span className="text-muted">-</span>
                                            ) : (
                                              <span className="text-success fw-bold font-monospace">
                                                {row.perfil}
                                                {hasMasterWarning && <span className="ms-2" style={{ color: '#ff6b6b', fontSize: '1.1em', cursor: 'help' }} title="⚠️ Perfil Master atribuído: Usuário sem mapeamento de nível organizacional na tabela profilegen-userorglevel">⚠️</span>}
                                              </span>
                                            );
                                          }
                                        },
                                        { key: 'utilizada', label: 'Utilizada?', minWidth: 120, sortable: true, render: (_: any, row: any) => <span className={`badge ${row.utilizada === 'S' ? 'bg-success' : 'bg-danger'}`}>{row.utilizada || 'N'}</span> }
                                      ]
                                    : [
                                        { key: 'usuario', label: 'Usuário', minWidth: 170, sortable: true, render: (_: any, row: any) => row.usuario || '-' },
                                        { key: 'usuario_name', label: 'Nome Usuário', minWidth: 200, sortable: true, render: (_: any, row: any) => row.usuario_name || '-' },
                                        { key: 'area', label: 'Área', minWidth: 160, sortable: true, render: (_: any, row: any) => row.area || '-' },
                                        { key: 'processo', label: 'Processo', minWidth: 170, sortable: true, render: (_: any, row: any) => row.processo || '-' },
                                        { key: 'subprocesso', label: 'Subprocesso', minWidth: 170, sortable: true, render: (_: any, row: any) => row.subprocesso || '-' },
                                        { key: 'atividade', label: 'Atividade', minWidth: 170, sortable: true, render: (_: any, row: any) => row.atividade || '-' },
                                        { key: 'codigo', label: 'Código', minWidth: 120, sortable: true, render: (_: any, row: any) => <code className="text-muted">{row.codigo || '-'}</code> },
                                        {
                                          key: 'perfil',
                                          label: 'Perfil',
                                          minWidth: 240,
                                          sortable: true,
                                          render: (_: any, row: any) => {
                                            const missingPerfil = !row.perfil || String(row.perfil).trim() === '-';
                                            const hasMasterWarning = row.master_sem_orglevel === true;
                                            return missingPerfil ? (
                                              <span className="text-muted">-</span>
                                            ) : (
                                              <span className="text-success fw-bold font-monospace">
                                                {row.perfil}
                                                {hasMasterWarning && <span className="ms-2" style={{ color: '#ff6b6b', fontSize: '1.1em', cursor: 'help' }} title="⚠️ Perfil Master atribuído: Usuário sem mapeamento de nível organizacional na tabela profilegen-userorglevel">⚠️</span>}
                                              </span>
                                            );
                                          }
                                        },
                                        { key: 'utilizada', label: 'Utilizada?', minWidth: 120, sortable: true, render: (_: any, row: any) => <span className={`badge ${row.utilizada === 'S' ? 'bg-success' : 'bg-danger'}`}>{row.utilizada || 'N'}</span> }
                                      ]
                                ) as DataTableColumn<any>[]}
                                data={filtered}
                                keyExtractor={(_: any, i: number) => String(i)}
                                sortColumn={validationSortColumn || null}
                                sortDirection={validationSortDirection}
                                onSort={(column: string, direction?: 'asc' | 'desc') => {
                                  if (direction) {
                                    setValidationSortColumn(column);
                                    setValidationSortDirection(direction);
                                  } else if (validationSortColumn === column) setValidationSortDirection((d: 'asc' | 'desc') => d === 'asc' ? 'desc' : 'asc');
                                  else { setValidationSortColumn(column); setValidationSortDirection('asc'); }
                                }}
                                striped
                                hover
                                bordered
                                responsive
                                size="sm"
                                className="gpw-table"
                                pagination
                                clientSidePagination
                                currentPage={validationPage}
                                pageSize={validationPageSize}
                                totalItems={filtered.length}
                                onPageChange={(page: number) => setValidationPage(page)}
                                onPageSizeChange={(size: number) => {
                                  setValidationPageSize(size);
                                  localStorage.setItem('gpw_validation_pageSize', String(size));
                                  setValidationPage(1);
                                }}
                                emptyMessage="Nenhum registro encontrado"
                              />
                            </div>
                          </>
                        );
                      })()
                    )}
                  </div>
                </div>
              ) : (
                <p>Conteúdo do passo {step} para o cliente <strong>{clientId}</strong>. Aqui você poderá executar as ações específicas deste passo.</p>
              )
            }
          </div>
          {/* navigation buttons were moved above the step card to sit under milestones */}
        </div>

        {/* Single common backdrop when any modal/overlay is open */}
        {anyModalOpen && (
          <div className="gp-modal-backdrop" onClick={() => { /* prevent clicks behind */ }} />
        )}

        {/* Edit modal for a codez item (Passo 3 - Classificar ações customizadas) */}
        {editingCodez && (
          <div className="users-overlay" onClick={() => setEditingCodez(null)}>
            <div className="users-overlay-box" style={{ width: 720, maxWidth: '95%' }} onClick={e => e.stopPropagation()}>
              <button className="modal-close-btn" aria-label="Fechar" onClick={() => setEditingCodez(null)}>×</button>
              <h4>Editar classificação — {editingCodez.code}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginTop: 8 }}>
                <div>
                  <label className="form-label">Processo</label>
                  <input className="form-control" value={editingCodez.process || ''} onChange={e => setEditingCodez((prev: any) => ({ ...prev, process: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label">Subprocesso</label>
                  <input className="form-control" value={editingCodez.subprocess || ''} onChange={e => setEditingCodez((prev: any) => ({ ...prev, subprocess: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label">Atividade</label>
                  <input className="form-control" value={editingCodez.activity || ''} onChange={e => setEditingCodez((prev: any) => ({ ...prev, activity: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label">Finalidade</label>
                  <textarea className="form-control" rows={3} value={editingCodez.purpose || ''} onChange={e => setEditingCodez((prev: any) => ({ ...prev, purpose: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label">Justificativa</label>
                  <textarea className="form-control" rows={4} value={editingCodez.justification || ''} onChange={e => setEditingCodez((prev: any) => ({ ...prev, justification: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button className="btn btn-outline-secondary" onClick={() => setEditingCodez(null)}>Cancelar</button>
                <button className="btn btn-primary" onClick={async () => {
                  try {
                    const id = editingCodez._id;
                    if (!id) return;
                    await axios.patch((process.env.REACT_APP_API_URL || '').replace(/\/$/, '') + `/api/profilegen-data/codez/item/${id}`, { process: editingCodez.process || '', subprocess: editingCodez.subprocess || '', activity: editingCodez.activity || '', purpose: editingCodez.purpose || '', justification: editingCodez.justification || '' }, { withCredentials: true });
                    await fetchIndicators('codez');
                    setEditingCodez(null);
                  } catch (err) { console.error('save edit', err); setUploadResult({ success: false, message: 'Erro ao salvar. Veja o console.' }); setUploadResultModalOpen(true); }
                }}>Salvar</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit modal for a log row (persist classification) */}
        {editingLogRow && (
          <div className="users-overlay" onClick={() => setEditingLogRow(null)}>
            <div className="users-overlay-box" style={{ width: 720, maxWidth: '95%' }} onClick={e => e.stopPropagation()}>
              <button className="modal-close-btn" aria-label="Fechar" onClick={() => setEditingLogRow(null)}>×</button>
              <h4>Editar classificação — {editingLogRow.code}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginTop: 8 }}>
                <div>
                  <label className="form-label">Processo</label>
                  <input className="form-control" value={editingLogRow.proc || ''} onChange={e => setEditingLogRow((prev: any) => ({ ...prev, proc: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label">Subprocesso</label>
                  <input className="form-control" value={editingLogRow.sub || ''} onChange={e => setEditingLogRow((prev: any) => ({ ...prev, sub: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label">Atividade</label>
                  <input className="form-control" value={editingLogRow.act || ''} onChange={e => setEditingLogRow((prev: any) => ({ ...prev, act: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button className="btn btn-outline-secondary" onClick={() => setEditingLogRow(null)}>Cancelar</button>
                <button className="btn btn-primary" onClick={async () => {
                  try {
                    const code = editingLogRow.code;
                    const payload: any = { process: editingLogRow.proc || '', subprocess: editingLogRow.sub || '', activity: editingLogRow.act || '', fonte: 'Classificado manualmente pelo usuário', manualEditedAt: new Date().toISOString(), manualEditedBy: 'WEB' };
                    await axios.patch(`${base}/api/profilegen-data/profilegen-logs/by-code/${clientId}/${encodeURIComponent(code)}`, payload, { withCredentials: true });
                    await fetchIndicators('logs');
                    setStep4HasClassified(true);
                    setEditingLogRow(null);
                  } catch (err) { console.error('save log edit', err); setUploadResult({ success: false, message: 'Erro ao salvar classificação. Veja o console.' }); setUploadResultModalOpen(true); }
                }}>Salvar</button>
              </div>
            </div>
          </div>
        )}

        {previewModalOpen && previewModalTable && (
          <AppModal
            show={previewModalOpen && !!previewModalTable}
            title={`Preview — ${tables.find((t) => t.key === previewModalTable)?.title || ''}`}
            onClose={() => setPreviewModalOpen(false)}
            size="lg"
            footer={(
              <button type="button" className="btn btn-secondary" onClick={() => setPreviewModalOpen(false)}>Fechar</button>
            )}
          >
            {previewLoadingAll && (
              <div className="alert alert-info d-flex align-items-center mb-3">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Carregando...</span>
                </div>
                <div className="flex-grow-1">
                  <small>
                    Carregando dados: {previewLoadingProgress.loaded.toLocaleString()} / {previewLoadingProgress.total.toLocaleString()} registros
                    ({Math.round((previewLoadingProgress.loaded / previewLoadingProgress.total) * 100)}%)
                  </small>
                  <div className="progress mt-1" style={{ height: '4px' }}>
                    <div
                      className="progress-bar bg-info progress-bar-striped progress-bar-animated"
                      style={{ width: `${(previewLoadingProgress.loaded / previewLoadingProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="mb-2">
              <small>Cliente: <strong>{clientId}</strong></small>
              <div>
                <small>
                  Colunas carregadas:{' '}
                  <strong>
                    {(previews[previewModalTable] && previews[previewModalTable].length)
                      ? (tables.find((t) => t.key === previewModalTable)?.columns.join(', ') || '-')
                      : '-'}
                  </strong>
                </small>
              </div>
            </div>

            <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
              {(() => {
                const raw = previews[previewModalTable] || [];
                const total = raw.length;
                return (
                  <DataTable
                    columns={[
                      { key: 'clientId', label: 'Cliente', minWidth: 140, sortable: true, render: (_: any, row: any) => row.clientId || clientId },
                      ...(((tables.find((t) => t.key === previewModalTable)?.columns) || []).map((col) => ({
                        key: col,
                        label: col === 'user' ? 'Usuário' : col === 'code' ? 'Código' : col === 'area' ? 'Área' : col === 'description' ? 'Descrição' : col === 'profile' ? 'Perfil' : col,
                        sortable: true,
                        minWidth: 140,
                        render: (_: any, row: any) => row[col]
                      })) as DataTableColumn<any>[]),
                      ...(previewModalTable === 'upcasis'
                        ? [{
                            key: 'usage_log_based',
                            label: 'Utilizada?',
                            sortable: true,
                            minWidth: 120,
                            render: (_: any, row: any) => row.usage_log_based === true
                              ? <span className="badge bg-success">S</span>
                              : row.usage_log_based === false
                                ? <span className="badge bg-danger">N</span>
                                : <span className="badge bg-secondary">-</span>
                          } as DataTableColumn<any>]
                        : [])
                    ] as DataTableColumn<any>[]}
                    data={raw}
                    keyExtractor={(_: any, i: number) => String(i)}
                    striped
                    hover
                    bordered
                    responsive
                    size="sm"
                    className="gpw-table"
                    pagination
                    clientSidePagination
                    currentPage={previewPage}
                    pageSize={previewPageSize}
                    totalItems={total}
                    onPageChange={(page: number) => setPreviewPage(page)}
                    onPageSizeChange={(size: number) => {
                      setPreviewPageSize(size);
                      localStorage.setItem('gpw_preview_pageSize', String(size));
                      setPreviewPage(1);
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    emptyMessage="Nenhum registro encontrado"
                  />
                );
              })()}
            </div>
          </AppModal>
        )}

        {/* Upload result modal (centered) */}
        {uploadResultModalOpen && uploadResult && (
          <AppModal
            show={uploadResultModalOpen && !!uploadResult}
            title={uploadResult.success ? 'Sucesso' : 'Atenção'}
            onClose={() => setUploadResultModalOpen(false)}
            size="sm"
            footer={(
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setUploadResultModalOpen(false)}>
                Entendi
              </button>
            )}
          >
                <div className="text-center p-2">
                  <div className="mb-3">
                    {uploadResult.success ? (
                      <i className="fas fa-check-circle text-success" style={{ fontSize: '2rem' }}></i>
                    ) : (
                      <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '2rem' }}></i>
                    )}
                  </div>
                  <h6 className="mb-3">{uploadResult.success ? 'Sucesso' : 'Atenção'}</h6>
                  <p className="mb-3">{uploadResult.message}</p>
                </div>
          </AppModal>
        )}

        {/* Upload progress modal */}
        {uploadProgressOpen && (
          <AppModal
            show={uploadProgressOpen}
            title={uploadProgressTable || 'Progresso de upload'}
            onClose={() => {}}
            closeDisabled={true}
            size="sm"
          >
                <div className="text-center p-2">
                  <div className="mb-3">
                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  </div>
                  <div className="progress mb-3" style={{ height: '6px' }}>
                    <div 
                      className="progress-bar" 
                      role="progressbar" 
                      style={{ width: `${uploadProgress}%` }}
                      aria-valuenow={uploadProgress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">{uploadProgressMessage}</small>
                    <small className="fw-bold">{Math.round(uploadProgress)}%</small>
                  </div>
                  {uploadProgress === 100 && (
                    <div className="mt-3">
                      <i className="fas fa-check-circle text-success me-2"></i>
                      <span className="text-success">Concluído!</span>
                    </div>
                  )}
                </div>
          </AppModal>
        )}


        {/* Confirm clear modal */}
  {confirmClearModalOpen && confirmClearTable && (
          <AppModal
            show={confirmClearModalOpen && !!confirmClearTable}
            title="Confirmação"
            onClose={() => { setConfirmClearModalOpen(false); setConfirmClearTable(null); }}
            size="sm"
            footer={(
              <>
                <button type="button" className="btn btn-outline-secondary" onClick={() => { setConfirmClearModalOpen(false); setConfirmClearTable(null); }}>Cancelar</button>
                <button type="button" className="btn btn-danger" onClick={async () => { if (confirmClearTable) await clearTable(confirmClearTable); setConfirmClearModalOpen(false); setConfirmClearTable(null); }}>Confirmar</button>
              </>
            )}
          >
                  <p>Confirma limpar todos os registros carregados para este cliente na tabela <strong>{confirmClearTable}</strong>?</p>
                  <div><small>Cliente: <strong>{clientId}</strong></small></div>
          </AppModal>
        )}

        {/* Log Sync - No Logs Alert Modal */}
        {noLogsAlertOpen && (
          <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header bg-warning text-dark">
                  <h5 className="modal-title">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Log de Acessos Não Encontrado
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setNoLogsAlertOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    Não foi possível realizar o <strong>Log Sync</strong> porque não há dados carregados na tabela <strong>Log de acessos cliente</strong>.
                  </p>
                  <div className="alert alert-info mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Para usar o Log Sync:</strong>
                    <ol className="mb-0 mt-2 ps-3">
                      <li>Carregue primeiro o arquivo CSV na seção <strong>"Log de acessos cliente"</strong></li>
                      <li>Depois retorne aqui e clique em <strong>"Log Sync"</strong></li>
                    </ol>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-primary" onClick={() => setNoLogsAlertOpen(false)}>Entendi</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Log Sync Toast - Canto Inferior Direito */}
        {logSyncToastOpen && (
          <div style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 9999,
            minWidth: 380,
            maxWidth: 450,
            background: theme === 'dark' ? 'rgba(30, 41, 59, 0.98)' : 'rgba(255, 255, 255, 0.98)',
            border: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.2)' : '1px solid rgba(203, 213, 225, 0.5)',
            borderRadius: 12,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            animation: 'slideInRight 0.3s ease-out'
          }}>
            <div style={{ padding: '16px 20px' }}>
              {/* Header com botão fechar */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {logSyncProgress === 100 ? (
                    <i className="fas fa-check-circle" style={{ color: '#22c55e', fontSize: 18 }}></i>
                  ) : logSyncResult && !logSyncResult.ok ? (
                    <i className="fas fa-exclamation-circle" style={{ color: '#ef4444', fontSize: 18 }}></i>
                  ) : (
                    <div className="spinner-border spinner-border-sm" role="status" style={{ width: 18, height: 18, borderWidth: 2, color: '#3b82f6' }}>
                      <span className="visually-hidden">Sincronizando...</span>
                    </div>
                  )}
                  <h6 style={{ 
                    margin: 0, 
                    fontSize: 14, 
                    fontWeight: 600,
                    color: theme === 'dark' ? '#f1f5f9' : '#1e293b'
                  }}>
                    {logSyncProgress === 100 ? 'Sincronização Concluída' : 'Sincronizando Logs'}
                  </h6>
                </div>
                <button 
                  type="button" 
                  onClick={() => setLogSyncToastOpen(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    color: theme === 'dark' ? '#94a3b8' : '#64748b',
                    fontSize: 18,
                    lineHeight: 1
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = theme === 'dark' ? '#f1f5f9' : '#1e293b'}
                  onMouseLeave={(e) => e.currentTarget.style.color = theme === 'dark' ? '#94a3b8' : '#64748b'}
                >
                  ×
                </button>
              </div>

              {/* Progress bar */}
              {logSyncLoading && (
                <>
                  <div className="progress mb-2" style={{ height: 6, borderRadius: 3 }}>
                    <div 
                      className="progress-bar progress-bar-striped progress-bar-animated" 
                      role="progressbar" 
                      style={{ 
                        width: `${logSyncProgress}%`,
                        background: logSyncProgress === 100 ? '#22c55e' : 'linear-gradient(90deg, #3b82f6, #8b5cf6)'
                      }}
                      aria-valuenow={Math.round(logSyncProgress)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <small style={{ 
                      fontSize: 11, 
                      color: theme === 'dark' ? '#94a3b8' : '#64748b',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {logSyncProgressMessage}
                    </small>
                    <small style={{ 
                      fontSize: 12, 
                      fontWeight: 600,
                      color: theme === 'dark' ? '#60a5fa' : '#3b82f6',
                      marginLeft: 8
                    }}>
                      {Math.round(logSyncProgress)}%
                    </small>
                  </div>
                </>
              )}

              {/* Resultado */}
              {logSyncResult && logSyncResult.ok && (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: 8, 
                  marginTop: 8 
                }}>
                  <div style={{
                    background: theme === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                    border: theme === 'dark' ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid rgba(59, 130, 246, 0.15)',
                    borderRadius: 6,
                    padding: '8px 10px'
                  }}>
                    <div style={{ fontSize: 9, color: theme === 'dark' ? '#94a3b8' : '#64748b', marginBottom: 2 }}>
                      Logs
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: theme === 'dark' ? '#60a5fa' : '#3b82f6' }}>
                      {logSyncResult.logCount?.toLocaleString() || '0'}
                    </div>
                  </div>
                  <div style={{
                    background: theme === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                    border: theme === 'dark' ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid rgba(59, 130, 246, 0.15)',
                    borderRadius: 6,
                    padding: '8px 10px'
                  }}>
                    <div style={{ fontSize: 9, color: theme === 'dark' ? '#94a3b8' : '#64748b', marginBottom: 2 }}>
                      Registros AS-IS
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: theme === 'dark' ? '#60a5fa' : '#3b82f6' }}>
                      {logSyncResult.totalRecords?.toLocaleString() || '0'}
                    </div>
                  </div>
                  <div style={{
                    background: theme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
                    border: theme === 'dark' ? '1px solid rgba(34, 197, 94, 0.2)' : '1px solid rgba(34, 197, 94, 0.15)',
                    borderRadius: 6,
                    padding: '8px 10px'
                  }}>
                    <div style={{ fontSize: 9, color: theme === 'dark' ? '#94a3b8' : '#64748b', marginBottom: 2 }}>
                      Atualizados
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: theme === 'dark' ? '#4ade80' : '#22c55e' }}>
                      {logSyncResult.updatedCount?.toLocaleString() || '0'}
                    </div>
                  </div>
                  <div style={{
                    background: theme === 'dark' ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.05)',
                    border: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.2)' : '1px solid rgba(148, 163, 184, 0.15)',
                    borderRadius: 6,
                    padding: '8px 10px'
                  }}>
                    <div style={{ fontSize: 9, color: theme === 'dark' ? '#94a3b8' : '#64748b', marginBottom: 2 }}>
                      Já Sincronizados
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: theme === 'dark' ? '#94a3b8' : '#64748b' }}>
                      {logSyncResult.alreadyUpToDate?.toLocaleString() || '0'}
                    </div>
                  </div>
                </div>
              )}

              {/* Erro */}
              {logSyncResult && !logSyncResult.ok && (
                <div style={{
                  background: theme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                  border: theme === 'dark' ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(239, 68, 68, 0.2)',
                  borderRadius: 6,
                  padding: 10,
                  marginTop: 12
                }}>
                  <small style={{ fontSize: 11, color: theme === 'dark' ? '#fca5a5' : '#dc2626' }}>
                    {logSyncResult.error || 'Erro desconhecido'}
                  </small>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Toast de progresso de limpeza */}
        {clearToastOpen && (
          <div style={{
            position: 'fixed',
            bottom: 20,
            left: 20,
            width: 380,
            maxWidth: 'calc(100vw - 40px)',
            zIndex: 9999,
            background: theme === 'dark' ? 'rgba(30, 41, 59, 0.98)' : 'rgba(255, 255, 255, 0.98)',
            border: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.2)' : '1px solid rgba(203, 213, 225, 0.5)',
            borderRadius: 12,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            animation: 'slideInLeft 0.3s ease-out'
          }}>
            <div style={{ padding: '16px 20px' }}>
              {/* Header com botão fechar */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {clearProgress.done && clearProgress.progress === 100 ? (
                    <i className="fas fa-check-circle" style={{ color: '#22c55e', fontSize: 18 }}></i>
                  ) : clearProgress.done ? (
                    <i className="fas fa-exclamation-circle" style={{ color: '#ef4444', fontSize: 18 }}></i>
                  ) : (
                    <div className="spinner-border spinner-border-sm" role="status" style={{ width: 18, height: 18, borderWidth: 2, color: '#ef4444' }}>
                      <span className="visually-hidden">Limpando...</span>
                    </div>
                  )}
                  <h6 style={{ 
                    margin: 0, 
                    fontSize: 14, 
                    fontWeight: 600,
                    color: theme === 'dark' ? '#f1f5f9' : '#1e293b'
                  }}>
                    {clearProgress.done && clearProgress.progress === 100 ? 'Limpeza Concluída' : 'Limpando Registros'}
                  </h6>
                </div>
                <button 
                  type="button" 
                  onClick={() => setClearToastOpen(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    color: theme === 'dark' ? '#94a3b8' : '#64748b',
                    fontSize: 18,
                    lineHeight: 1
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = theme === 'dark' ? '#f1f5f9' : '#1e293b'}
                  onMouseLeave={(e) => e.currentTarget.style.color = theme === 'dark' ? '#94a3b8' : '#64748b'}
                >
                  ×
                </button>
              </div>

              {/* Progress bar */}
              <div className="progress mb-2" style={{ height: 6, borderRadius: 3 }}>
                <div 
                  className="progress-bar progress-bar-striped progress-bar-animated" 
                  role="progressbar" 
                  style={{ 
                    width: `${clearProgress.progress}%`,
                    background: clearProgress.done && clearProgress.progress === 100 ? '#22c55e' : 'linear-gradient(90deg, #ef4444, #f97316)'
                  }}
                  aria-valuenow={Math.round(clearProgress.progress)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                ></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <small style={{ 
                  fontSize: 11, 
                  color: theme === 'dark' ? '#94a3b8' : '#64748b',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {clearProgress.message}
                </small>
                <small style={{ 
                  fontSize: 12, 
                  fontWeight: 600,
                  color: theme === 'dark' ? '#fca5a5' : '#ef4444',
                  marginLeft: 8
                }}>
                  {Math.round(clearProgress.progress)}%
                </small>
              </div>

              {/* Tabela sendo limpa */}
              {clearProgress.table && (
                <div style={{
                  background: theme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                  border: theme === 'dark' ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid rgba(239, 68, 68, 0.15)',
                  borderRadius: 6,
                  padding: '8px 10px',
                  marginTop: 8
                }}>
                  <div style={{ fontSize: 9, color: theme === 'dark' ? '#94a3b8' : '#64748b', marginBottom: 2 }}>
                    Tabela
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: theme === 'dark' ? '#fca5a5' : '#ef4444' }}>
                    {clearProgress.table}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Confirm clear all classifications modal */}
  {confirmClearClassificationsOpen && (
          <AppModal
            show={confirmClearClassificationsOpen}
            title="Limpar classificações"
            onClose={() => setConfirmClearClassificationsOpen(false)}
            size="sm"
            footer={(
              <>
                <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirmClearClassificationsOpen(false)}>Cancelar</button>
                <button type="button" className="btn btn-danger" onClick={async () => {
                  setConfirmClearClassificationsOpen(false);
                  try {
                    await clearClassifications();
                  } catch (err) {
                    console.error('clear classifications', err);
                    setUploadResult({ success: false, message: 'Erro ao limpar classificações. Veja console.' });
                    setUploadResultModalOpen(true);
                  }
                }}>Confirmar</button>
              </>
            )}
          >
                  <p>Confirma limpar apenas as classificações (processo, subprocesso, atividade e fonte) mantendo os registros de log na tabela <strong>profilegen-logs</strong> para o cliente <strong>{clientId}</strong>?</p>
                  <small className="text-muted">Os dados originais dos logs não serão removidos, apenas os campos de classificação serão resetados.</small>
          </AppModal>
        )}

        {/* Confirm classification modal */}
        {confirmClassifyOpen && confirmClassifyMode && (
          <AppModal
            show={confirmClassifyOpen && !!confirmClassifyMode}
            title="Confirmação de classificação"
            onClose={() => setConfirmClassifyOpen(false)}
            size="sm"
            footer={(
              <>
                <button className="btn btn-outline-secondary" onClick={() => setConfirmClassifyOpen(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={async () => {
                  const rows = buildLogRows();
                  const targets = confirmClassifyScope === 'all' ? rows.filter(r => confirmClassifyMode === 'custom' ? r.isCustom : !r.isCustom) : rows.filter(r => selectedLogCodes.has(r.code) && (confirmClassifyMode === 'custom' ? r.isCustom : !r.isCustom));
                  if (!targets.length) {
                    setConfirmClassifyOpen(false);
                    setUploadResult({ success: true, message: 'Nenhuma code correspondente para processar.', tableKey: 'logs' });
                    setUploadResultModalOpen(true);
                    setConfirmClassifyMode(null);
                    return;
                  }
                  setConfirmClassifyOpen(false);
                  if (confirmClassifyMode === 'custom') {
                    await classifyCustomizadas(confirmClassifyScope);
                  } else if (confirmClassifyMode === 'benchmark') {
                    await classifyWithBenchmark(confirmClassifyScope);
                  }
                  setConfirmClassifyMode(null);
                }}>Confirmar</button>
              </>
            )}
          >
                  {(() => {
                    const rows = buildLogRows();
                    const targets = confirmClassifyScope === 'all' ? rows.filter(r => confirmClassifyMode === 'custom' ? r.isCustom : !r.isCustom) : rows.filter(r => selectedLogCodes.has(r.code) && (confirmClassifyMode === 'custom' ? r.isCustom : !r.isCustom));
                    const msgMode = confirmClassifyMode === 'custom' ? 'Classificar customizadas' : 'Classificar com Benchmark';
                    const overwriteScope = confirmClassifyMode === 'custom' ? 'classificações manuais existentes nas codes customizadas' : 'classificações manuais existentes nas codes não-customizadas';
                    return (
                      <div>
                        <p>Você tem <strong>{targets.length}</strong> codes que serão processadas.</p>
                        <p>Ao confirmar, {msgMode} irá sobrescrever quaisquer {overwriteScope}.</p>
                        <small className="text-muted">Deseja prosseguir?</small>
                      </div>
                    );
                  })()}
          </AppModal>
        )}

        {/* Dashboard Alert Modal - Must complete step 6 first */}
        {dashboardAlertOpen && (
          <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header bg-warning text-dark">
                  <h5 className="modal-title">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 8, verticalAlign: 'text-bottom' }}>
                      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                    </svg>
                    Passo Final Pendente
                  </h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setDashboardAlertOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    Para acessar o Dashboard com análises completas, você precisa primeiro concluir o Wizard até o passo final.
                  </p>
                  <div className="alert alert-info mb-0">
                    <strong>Próxima etapa:</strong> Navegue até o <strong>Passo {getStepCountForModel(client?.profileModel)} - Validação e Download</strong> e clique no botão <strong>"Carregar"</strong> para gerar os dados de validação.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setDashboardAlertOpen(false)}>
                    Fechar
                  </button>
                  <button type="button" className="btn btn-primary" onClick={() => {
                    setDashboardAlertOpen(false);
                    setStep(getStepCountForModel(client?.profileModel));
                  }}>
                    Ir para Passo {getStepCountForModel(client?.profileModel)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dashboard modal - full screen overlay */}
        {dashboardOpen && (
          <div className="modal show d-block gp-dashboard-modal" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-fullscreen modal-dialog-centered" role="document">
              <div className="modal-content" style={{ height: '100vh', overflow: 'visible' }}>
                <div className="modal-header">
                  <h5 className="modal-title">Dashboard — Indicadores</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setDashboardOpen(false)}></button>
                </div>
                <div className="modal-body" style={{ overflow: 'visible', padding: 20 }}>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 8 }}>
                    <div><strong>Cliente:</strong> {clientId}</div>
                    <div><strong>Modelo:</strong> {client?.profileModel || '-'}</div>
                    <div style={{ marginLeft: 'auto' }}>
                      <label style={{ marginRight: 8 }}><small>Visão de indicadores:</small></label>
                      <select className="form-select form-select-sm" value={dashboardModelView} onChange={e => setDashboardModelView(e.target.value)} style={{ width: 220 }}>
                        <option value="visao-geral">Visão geral</option>
                      </select>
                    </div>
                  </div>

                  {/* KPIs principais - Hero Section - OCULTA TEMPORARIAMENTE */}
                  {/* <div style={{
                    background: theme === 'dark' 
                      ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.05))'
                      : 'linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(59, 130, 246, 0.02))',
                    borderRadius: 16,
                    padding: 32,
                    marginBottom: 32,
                    border: theme === 'dark' ? '1px solid rgba(139, 92, 246, 0.2)' : '1px solid rgba(139, 92, 246, 0.1)',
                    boxShadow: theme === 'dark' 
                      ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                      : '0 8px 32px rgba(139, 92, 246, 0.1)',
                    animation: 'fadeInUp 0.6s ease-out'
                  }}>
                    <h5 style={{ 
                      marginBottom: 24, 
                      fontSize: 20,
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12
                    }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
                        <rect x="1" y="1" width="14" height="14" rx="2" fill="none" stroke="#8b5cf6" strokeWidth="1.2" />
                        <rect x="4" y="8" width="2" height="4" rx="0.5" fill="#8b5cf6" />
                        <rect x="7" y="6" width="2" height="6" rx="0.5" fill="#8b5cf6" />
                        <rect x="10" y="4" width="2" height="8" rx="0.5" fill="#8b5cf6" />
                      </svg>
                      Indicadores Principais
                    </h5>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                      gap: 20
                    }}>
                      <div style={{ 
                        background: theme === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(255, 255, 255, 0.8)',
                        border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                        borderRadius: 12,
                        padding: 20,
                        transition: 'all 0.3s ease',
                        cursor: 'default'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        <div style={{ 
                          fontSize: 12, 
                          fontWeight: 600,
                          color: theme === 'dark' ? '#94a3b8' : '#64748b',
                          marginBottom: 8 
                        }}>Usuários Carregados</div>
                        <div style={{ 
                          fontSize: 36, 
                          fontWeight: 700,
                          color: theme === 'dark' ? '#f1f5f9' : '#1e293b'
                        }}>{computeDistinctUsersCount()}</div>
                        <div style={{ 
                          fontSize: 11, 
                          color: theme === 'dark' ? '#64748b' : '#94a3b8',
                          marginTop: 4 
                        }}>com base em log</div>
                      </div>
                      <div style={{ 
                        background: theme === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(255, 255, 255, 0.8)',
                        border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                        borderRadius: 12,
                        padding: 20,
                        transition: 'all 0.3s ease',
                        cursor: 'default'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        <div style={{ 
                          fontSize: 12, 
                          fontWeight: 600,
                          color: theme === 'dark' ? '#94a3b8' : '#64748b',
                          marginBottom: 8 
                        }}>Ações Distintas</div>
                        <div style={{ 
                          fontSize: 36, 
                          fontWeight: 700,
                          color: theme === 'dark' ? '#f1f5f9' : '#1e293b'
                        }}>{computeDistinctCodesCount()}</div>
                        <div style={{ 
                          fontSize: 11, 
                          color: theme === 'dark' ? '#64748b' : '#94a3b8',
                          marginTop: 4 
                        }}>carregadas do log</div>
                      </div>
                      <div style={{ 
                        background: theme === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(255, 255, 255, 0.8)',
                        border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                        borderRadius: 12,
                        padding: 20,
                        transition: 'all 0.3s ease',
                        cursor: 'default'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        <div style={{ 
                          fontSize: 12, 
                          fontWeight: 600,
                          color: theme === 'dark' ? '#94a3b8' : '#64748b',
                          marginBottom: 8 
                        }}>Ações Customizadas</div>
                        <div style={{ 
                          fontSize: 36, 
                          fontWeight: 700,
                          color: theme === 'dark' ? '#f1f5f9' : '#1e293b'
                        }}>{computeDistinctCodezCount()}</div>
                        <div style={{ 
                          fontSize: 11, 
                          color: theme === 'dark' ? '#64748b' : '#94a3b8',
                          marginTop: 4 
                        }}>classificadas manualmente</div>
                      </div>
                      {validationDataLoaded && (
                        <>
                          <div style={{ 
                            background: theme === 'dark' 
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(255, 255, 255, 0.8)',
                            border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                            borderRadius: 12,
                            padding: 20,
                            transition: 'all 0.3s ease',
                            cursor: 'default',
                            animation: 'fadeIn 0.6s ease-out 0.2s both'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                          >
                            <div style={{ 
                              fontSize: 12, 
                              fontWeight: 600,
                              color: theme === 'dark' ? '#94a3b8' : '#64748b',
                              marginBottom: 8 
                            }}>Perfis Distintos</div>
                            <div style={{ 
                              fontSize: 36, 
                              fontWeight: 700,
                              color: theme === 'dark' ? '#f1f5f9' : '#1e293b'
                            }}>
                              {(() => {
                                const uniqueProfiles = new Set();
                                validationRows.forEach(r => {
                                  if (r.perfil && r.perfil !== '-') {
                                    uniqueProfiles.add(r.perfil);
                                  }
                                });
                                return uniqueProfiles.size;
                              })()}
                            </div>
                            <div style={{ 
                              fontSize: 11, 
                              color: theme === 'dark' ? '#64748b' : '#94a3b8',
                              marginTop: 4 
                            }}>gerados</div>
                          </div>
                          <div style={{ 
                            background: theme === 'dark' 
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(255, 255, 255, 0.8)',
                            border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                            borderRadius: 12,
                            padding: 20,
                            transition: 'all 0.3s ease',
                            cursor: 'default',
                            animation: 'fadeIn 0.6s ease-out 0.3s both'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                          >
                            <div style={{ 
                              fontSize: 12, 
                              fontWeight: 600,
                              color: theme === 'dark' ? '#94a3b8' : '#64748b',
                              marginBottom: 8 
                            }}>Total Atribuições</div>
                            <div style={{ 
                              fontSize: 36, 
                              fontWeight: 700,
                              color: theme === 'dark' ? '#f1f5f9' : '#1e293b'
                            }}>{validationRows.length}</div>
                            <div style={{ 
                              fontSize: 11, 
                              color: theme === 'dark' ? '#64748b' : '#94a3b8',
                              marginTop: 4 
                            }}>código x perfil</div>
                          </div>
                          <div style={{ 
                            background: theme === 'dark' 
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(255, 255, 255, 0.8)',
                            border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                            borderRadius: 12,
                            padding: 20,
                            transition: 'all 0.3s ease',
                            cursor: 'default',
                            animation: 'fadeIn 0.6s ease-out 0.4s both'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                          >
                            <div style={{ 
                              fontSize: 12, 
                              fontWeight: 600,
                              color: theme === 'dark' ? '#94a3b8' : '#64748b',
                              marginBottom: 8 
                            }}>Usuários com Perfis</div>
                            <div style={{ 
                              fontSize: 36, 
                              fontWeight: 700,
                              color: theme === 'dark' ? '#f1f5f9' : '#1e293b'
                            }}>
                              {(() => {
                                const usersWithProfiles = new Set();
                                validationRows.forEach(r => {
                                  if (r.usuario && r.usuario !== '-') {
                                    usersWithProfiles.add(r.usuario);
                                  }
                                });
                                return usersWithProfiles.size;
                              })()}
                            </div>
                            <div style={{ 
                              fontSize: 11, 
                              color: theme === 'dark' ? '#64748b' : '#94a3b8',
                              marginTop: 4 
                            }}>atribuídos</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div> */}

                  {/* Gráfico de Pizza: Codes Utilizadas vs Não Utilizadas */}
                  {validationDataLoaded && (
                    <div style={{
                      marginTop: 40,
                      background: theme === 'dark' 
                        ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.05))'
                        : 'linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(59, 130, 246, 0.02))',
                      borderRadius: 16,
                      padding: 32,
                      border: theme === 'dark' ? '1px solid rgba(139, 92, 246, 0.2)' : '1px solid rgba(139, 92, 246, 0.1)',
                      boxShadow: theme === 'dark' 
                        ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                        : '0 8px 32px rgba(139, 92, 246, 0.1)',
                      animation: 'fadeInUp 0.6s ease-out'
                    }}>
                      <h5 style={{ 
                        marginBottom: 32, 
                        fontSize: 20,
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                      }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
                          <rect x="1" y="1" width="14" height="14" rx="2" fill="none" stroke="#8b5cf6" strokeWidth="1.2" />
                          <rect x="4" y="8" width="2" height="4" rx="0.5" fill="#8b5cf6" />
                          <rect x="7" y="6" width="2" height="6" rx="0.5" fill="#8b5cf6" />
                          <rect x="10" y="4" width="2" height="8" rx="0.5" fill="#8b5cf6" />
                        </svg>
                        Análise de atribuição de acessos x utilização
                      </h5>

                      {/* Global toggle for deduplicating attributions (applies to both AS-IS and TO-BE) */}
                      <div style={{
                        marginBottom: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        justifyContent: 'flex-start'
                      }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          cursor: 'pointer',
                          fontSize: 13,
                          color: theme === 'dark' ? '#cbd5e1' : '#475569',
                          fontWeight: 500,
                          userSelect: 'none'
                        }}>
                          <input
                            type="checkbox"
                            checked={asisIgnoreDuplicates}
                            onChange={(e) => setAsisIgnoreDuplicates(e.target.checked)}
                            style={{
                              width: 18,
                              height: 18,
                              cursor: 'pointer',
                              accentColor: '#8b5cf6'
                            }}
                          />
                          <span>Desconsiderar atribuições duplicadas</span>
                        </label>
                      </div>

                        {/* Container principal: dois cenários lado a lado */}
                        <div style={{
                          display: 'flex',
                          gap: 32,
                          alignItems: 'stretch'
                        }}>
                          {/* CENÁRIO AS-IS */}
                          <div style={{
                            flex: '1',
                            background: theme === 'dark' 
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(255, 255, 255, 0.8)',
                            border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                            borderRadius: 16,
                            padding: 20,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12
                          }}>
                            {(() => {
                              // AS-IS data from profilegen-upc-asis
                              let effectiveAsisRows = upcasisRows;
                              
                              // If toggle is enabled, remove duplicates (user:code combinations)
                              if (asisIgnoreDuplicates) {
                                const uniqueUserCodeMap = new Map<string, any>();
                                upcasisRows.forEach((row: any) => {
                                  if (row.user && row.code) {
                                    const key = `${row.user}:${row.code}`.toLowerCase();
                                    // Keep only if not already seen, or if current one is "used"
                                    if (!uniqueUserCodeMap.has(key) || row.usage_log_based === true) {
                                      uniqueUserCodeMap.set(key, row);
                                    }
                                  }
                                });
                                effectiveAsisRows = Array.from(uniqueUserCodeMap.values());
                              }
                              
                              const asisUsedRows = effectiveAsisRows.filter(r => r.usage_log_based === true);
                              const asisUnusedRows = effectiveAsisRows.filter(r => r.usage_log_based === false);
                              const asisTotalRows = effectiveAsisRows.length;
                              const asisUsedPercentage = asisTotalRows > 0 ? ((asisUsedRows.length / asisTotalRows) * 100).toFixed(1) : '0';
                              const asisUnusedPercentage = asisTotalRows > 0 ? ((asisUnusedRows.length / asisTotalRows) * 100).toFixed(1) : '0';

                              return (
                                <div style={{
                                  display: 'flex',
                                  gap: 20,
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  position: 'relative'
                                }}>
                                  {/* Indicador de progresso de carregamento AS-IS */}
                                  {asisLoadingProgress && (
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      background: theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                                      border: theme === 'dark' ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid rgba(59, 130, 246, 0.3)',
                                      borderRadius: 8,
                                      padding: '8px 12px',
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: theme === 'dark' ? '#60a5fa' : '#2563eb',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      zIndex: 5,
                                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)',
                                      backdropFilter: 'blur(8px)'
                                    }}>
                                      <div className="spinner-border spinner-border-sm" role="status" style={{ width: '14px', height: '14px', borderWidth: '2px' }}>
                                        <span className="visually-hidden">Carregando...</span>
                                      </div>
                                      <span>
                                        Carregando {asisLoadingProgress.loaded.toLocaleString()} de {asisLoadingProgress.total.toLocaleString()} registros
                                      </span>
                                    </div>
                                  )}
                                  
                                  {/* Título AS-IS */}
                                  <h6 style={{
                                    fontSize: 18,
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    margin: 0,
                                    writingMode: 'vertical-rl',
                                    textOrientation: 'mixed',
                                    transform: 'rotate(180deg)',
                                    letterSpacing: '2px'
                                  }}>
                                    CENÁRIO ATUAL
                                  </h6>
                                  {/* Pizza AS-IS */}
                                  <div style={{
                                    flex: '1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <div style={{ 
                                      position: 'relative', 
                                      width: '260px',
                                      height: '260px'
                                    }}>
                                      {/* Tooltip AS-IS */}
                                      {pieTooltipAsIs.show && (
                                        <div style={{
                                          position: 'absolute',
                                          left: pieTooltipAsIs.x,
                                          top: pieTooltipAsIs.y,
                                          transform: 'translate(-50%, -120%)',
                                          background: 'rgba(0, 0, 0, 0.9)',
                                          color: 'white',
                                          padding: '8px 12px',
                                          borderRadius: 8,
                                          fontSize: 13,
                                          fontWeight: 600,
                                          whiteSpace: 'nowrap',
                                          pointerEvents: 'none',
                                          zIndex: 1000,
                                          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                                        }}>
                                          {pieTooltipAsIs.text}
                                        </div>
                                      )}
                                      
                                      {/* SVG Pie Chart AS-IS */}
                                      <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                                        <defs>
                                          <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" style={{ stopColor: '#4ade80', stopOpacity: 1 }} />
                                            <stop offset="100%" style={{ stopColor: '#22c55e', stopOpacity: 1 }} />
                                          </linearGradient>
                                          <linearGradient id="redGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" style={{ stopColor: '#f87171', stopOpacity: 1 }} />
                                            <stop offset="100%" style={{ stopColor: '#ef4444', stopOpacity: 1 }} />
                                          </linearGradient>
                                        </defs>
                                        
                                        {/* Used transactions (green) */}
                                        <circle
                                          cx="100"
                                          cy="100"
                                          r="75"
                                          fill="none"
                                          stroke="url(#greenGradient)"
                                          strokeWidth="28"
                                          strokeDasharray={`${pieAnimationTriggered && asisTotalRows > 0 ? (asisUsedRows.length / asisTotalRows) * 471.24 : 0} 471.24`}
                                          strokeLinecap="round"
                                          style={{
                                            transition: 'stroke-width 0.3s ease, stroke-dasharray 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            filter: 'drop-shadow(0 2px 8px rgba(34, 197, 94, 0.4))',
                                            cursor: 'pointer'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.strokeWidth = '32';
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const svg = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                            if (svg) {
                                              setPieTooltipAsIs({
                                                show: true,
                                                text: `✅ Utilizadas: ${asisUsedRows.length} (${asisUsedPercentage}%)`,
                                                x: (rect.left + rect.right) / 2 - svg.left,
                                                y: rect.top - svg.top
                                              });
                                            }
                                          }}
                                          onMouseMove={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const svg = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                            if (svg) {
                                              setPieTooltipAsIs(prev => ({
                                                ...prev,
                                                x: (rect.left + rect.right) / 2 - svg.left,
                                                y: rect.top - svg.top
                                              }));
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.strokeWidth = '28';
                                            setPieTooltipAsIs({ show: false, text: '', x: 0, y: 0 });
                                          }}
                                        />
                                        
                                        {/* Unused transactions (red) */}
                                        <circle
                                          cx="100"
                                          cy="100"
                                          r="75"
                                          fill="none"
                                          stroke="url(#redGradient)"
                                          strokeWidth="28"
                                          strokeDasharray={`${pieAnimationTriggered && asisTotalRows > 0 ? (asisUnusedRows.length / asisTotalRows) * 471.24 : 0} 471.24`}
                                          strokeDashoffset={`-${pieAnimationTriggered && asisTotalRows > 0 ? (asisUsedRows.length / asisTotalRows) * 471.24 : 0}`}
                                          strokeLinecap="round"
                                          style={{
                                            transition: 'stroke-width 0.3s ease, stroke-dasharray 1.2s cubic-bezier(0.4, 0, 0.2, 1), stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            filter: 'drop-shadow(0 2px 8px rgba(239, 68, 68, 0.4))',
                                            cursor: 'pointer'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.strokeWidth = '32';
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const svg = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                            if (svg) {
                                              setPieTooltipAsIs({
                                                show: true,
                                                text: `❌ Não Utilizadas: ${asisUnusedRows.length} (${asisUnusedPercentage}%)`,
                                                x: (rect.left + rect.right) / 2 - svg.left,
                                                y: rect.top - svg.top
                                              });
                                            }
                                          }}
                                          onMouseMove={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const svg = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                            if (svg) {
                                              setPieTooltipAsIs(prev => ({
                                                ...prev,
                                                x: (rect.left + rect.right) / 2 - svg.left,
                                                y: rect.top - svg.top
                                              }));
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.strokeWidth = '28';
                                            setPieTooltipAsIs({ show: false, text: '', x: 0, y: 0 });
                                          }}
                                        />
                                  
                                        {/* Center text - Percentage used */}
                                        <text
                                          x="100"
                                          y="100"
                                          textAnchor="middle"
                                          dominantBaseline="middle"
                                          style={{
                                            fontSize: '28px',
                                            fontWeight: 700,
                                            fill: theme === 'dark' ? '#ffffff' : '#1e293b',
                                            transform: 'rotate(90deg)',
                                            transformOrigin: 'center',
                                            textShadow: theme === 'dark' ? '0 2px 10px rgba(0,0,0,0.3)' : '0 2px 10px rgba(255,255,255,0.8)'
                                          }}
                                        >
                                          {asisUsedPercentage}%
                                        </text>
                                      </svg>
                                    </div>
                                  </div>

                                  {/* Cards AS-IS */}
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10,
                                    justifyContent: 'center'
                                  }}>
                                    {/* Total AS-IS */}
                                    <div style={{
                                      background: theme === 'dark' 
                                        ? 'linear-gradient(135deg, rgba(147, 112, 219, 0.3), rgba(147, 112, 219, 0.2))'
                                        : 'linear-gradient(135deg, rgba(147, 112, 219, 0.15), rgba(147, 112, 219, 0.08))',
                                      padding: 12,
                                      borderRadius: 8,
                                      border: theme === 'dark' 
                                        ? '2px solid rgba(147, 112, 219, 0.5)'
                                        : '2px solid rgba(147, 112, 219, 0.3)',
                                      backdropFilter: 'blur(10px)',
                                      textAlign: 'center',
                                      transition: 'all 0.3s ease',
                                      cursor: 'default',
                                      width: 130
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(147, 112, 219, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}>
                                      <div style={{
                                        fontSize: 28,
                                        color: theme === 'dark' ? '#f1f5f9' : '#1e293b',
                                        fontWeight: 900,
                                        lineHeight: 1
                                      }}>
                                        {asisTotalRows}
                                      </div>
                                      <div style={{
                                        fontSize: 10,
                                        color: theme === 'dark' ? '#cbd5e1' : '#475569',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        marginTop: 6
                                      }} dangerouslySetInnerHTML={{ __html: 'Total<br/>atribuições' }} />
                                    </div>

                                    {/* AS-IS Utilizadas */}
                                    <div style={{
                                      background: theme === 'dark'
                                        ? 'linear-gradient(135deg, rgba(74, 222, 128, 0.3), rgba(34, 197, 94, 0.2))'
                                        : 'linear-gradient(135deg, rgba(74, 222, 128, 0.15), rgba(34, 197, 94, 0.08))',
                                      padding: 12,
                                      borderRadius: 8,
                                      border: theme === 'dark'
                                        ? '2px solid rgba(74, 222, 128, 0.5)'
                                        : '2px solid rgba(34, 197, 94, 0.3)',
                                      backdropFilter: 'blur(10px)',
                                      textAlign: 'center',
                                      transition: 'all 0.3s ease',
                                      cursor: 'default',
                                      width: 130
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(74, 222, 128, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}>
                                      <div style={{
                                        fontSize: 22,
                                        color: theme === 'dark' ? '#f1f5f9' : '#1e293b',
                                        fontWeight: 900,
                                        lineHeight: 1
                                      }}>
                                        {asisUsedRows.length}
                                      </div>
                                      <div style={{
                                        fontSize: 10,
                                        color: theme === 'dark' ? '#cbd5e1' : '#475569',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        marginTop: 6
                                      }}>
                                        Utilizadas
                                      </div>
                                      <div style={{
                                        fontSize: 14,
                                        color: theme === 'dark' ? '#a3e635' : '#16a34a',
                                        fontWeight: 700,
                                        marginTop: 4
                                      }}>
                                        {asisUsedPercentage}%
                                      </div>
                                    </div>

                                    {/* AS-IS Não Utilizadas */}
                                    <div style={{
                                      background: theme === 'dark'
                                        ? 'linear-gradient(135deg, rgba(248, 113, 113, 0.3), rgba(239, 68, 68, 0.2))'
                                        : 'linear-gradient(135deg, rgba(248, 113, 113, 0.15), rgba(239, 68, 68, 0.08))',
                                      padding: 12,
                                      borderRadius: 8,
                                      border: theme === 'dark'
                                        ? '2px solid rgba(248, 113, 113, 0.5)'
                                        : '2px solid rgba(239, 68, 68, 0.3)',
                                      backdropFilter: 'blur(10px)',
                                      textAlign: 'center',
                                      transition: 'all 0.3s ease',
                                      cursor: 'default',
                                      width: 130
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(248, 113, 113, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}>
                                      <div style={{
                                        fontSize: 22,
                                        color: theme === 'dark' ? '#f1f5f9' : '#1e293b',
                                        fontWeight: 900,
                                        lineHeight: 1
                                      }}>
                                        {asisUnusedRows.length}
                                      </div>
                                      <div style={{
                                        fontSize: 10,
                                        color: theme === 'dark' ? '#cbd5e1' : '#475569',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        marginTop: 6
                                      }}>
                                        Não Utilizadas
                                      </div>
                                      <div style={{
                                        fontSize: 14,
                                        color: theme === 'dark' ? '#fca5a5' : '#dc2626',
                                        fontWeight: 700,
                                        marginTop: 4
                                      }}>
                                        {asisUnusedPercentage}%
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                            
                            
                          </div>

                          {/* CENÁRIO TO-BE */}
                          <div style={{
                            flex: '1',
                            background: theme === 'dark' 
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(255, 255, 255, 0.8)',
                            border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                            borderRadius: 16,
                            padding: 20,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12
                          }}>
                            {(() => {
                              // Apply optional deduplication (per usuário+area+codigo) when toggle is enabled
                              let effectiveValidationRows = validationRows;
                              if (asisIgnoreDuplicates && Array.isArray(validationRows) && validationRows.length > 0) {
                                const uniqueMap = new Map<string, any>();
                                validationRows.forEach((r: any) => {
                                  const usuario = String(r.usuario || r.user || '').trim();
                                  const area = String(r.area || '').trim();
                                  const codigo = String(r.codigo || r.codigo || r.code || '').trim().toUpperCase();
                                  if (!usuario || !codigo) return;
                                  const key = `${usuario}||${area}||${codigo}`.toLowerCase();
                                  // Prefer rows marked as used ('S') when deduping
                                  const existing = uniqueMap.get(key);
                                  const isUsed = String(r.utilizada || '').toUpperCase() === 'S' || String(r.utilizada || '').toUpperCase() === 'SIM' || String(r.utilizada || '').toUpperCase() === 'TRUE' || String(r.utilizada || '').toUpperCase() === '1' || String(r.utilizada || '').toUpperCase() === 'YES';
                                  if (!existing) {
                                    uniqueMap.set(key, r);
                                  } else if (isUsed) {
                                    // replace with a row that indicates usage if current row is used
                                    uniqueMap.set(key, r);
                                  }
                                });
                                effectiveValidationRows = Array.from(uniqueMap.values());
                              }

                              // Filter by 'utilizada' field (S/N) from effectiveValidationRows
                              const usedRows = effectiveValidationRows.filter(r => {
                                const util = (r.utilizada || '').toString().toUpperCase();
                                return util === 'S' || util === 'SIM' || util === 'TRUE' || util === '1' || util === 'YES';
                              });
                              const unusedRows = effectiveValidationRows.filter(r => {
                                const util = (r.utilizada || '').toString().toUpperCase();
                                return util === 'N' || util === 'NÃO' || util === 'NAO' || util === 'FALSE' || util === '0' || util === 'NO';
                              });
                              const totalRows = effectiveValidationRows.length;
                              const usedPercentage = totalRows > 0 ? ((usedRows.length / totalRows) * 100).toFixed(1) : '0';
                              const unusedPercentage = totalRows > 0 ? ((unusedRows.length / totalRows) * 100).toFixed(1) : '0';

                              return (
                                <div style={{
                                  display: 'flex',
                                  gap: 20,
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  position: 'relative'
                                }}>
                                  {/* Indicador de progresso de carregamento TO-BE */}
                                  {dataLoadingProgress && dataLoadingProgress.table === 'validation' && (
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      right: 0,
                                      background: theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                                      border: theme === 'dark' ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid rgba(59, 130, 246, 0.3)',
                                      borderRadius: 8,
                                      padding: '8px 12px',
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: theme === 'dark' ? '#60a5fa' : '#2563eb',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      zIndex: 5,
                                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)',
                                      backdropFilter: 'blur(8px)'
                                    }}>
                                      <div className="spinner-border spinner-border-sm" role="status" style={{ width: '14px', height: '14px', borderWidth: '2px' }}>
                                        <span className="visually-hidden">Carregando...</span>
                                      </div>
                                      <span>
                                        Carregando {dataLoadingProgress.loaded.toLocaleString()} de {dataLoadingProgress.total.toLocaleString()} registros
                                      </span>
                                    </div>
                                  )}
                                  
                                  {/* Cards TO-BE (na esquerda) */}
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10,
                                    justifyContent: 'center'
                                  }}>
                                    {/* Total TO-BE */}
                                    <div style={{
                                      background: theme === 'dark'
                                        ? 'linear-gradient(135deg, rgba(147, 112, 219, 0.3), rgba(147, 112, 219, 0.2))'
                                        : 'linear-gradient(135deg, rgba(147, 112, 219, 0.15), rgba(147, 112, 219, 0.08))',
                                      padding: 12,
                                      borderRadius: 8,
                                      border: theme === 'dark'
                                        ? '2px solid rgba(147, 112, 219, 0.5)'
                                        : '2px solid rgba(147, 112, 219, 0.3)',
                                      backdropFilter: 'blur(10px)',
                                      textAlign: 'center',
                                      transition: 'all 0.3s ease',
                                      cursor: 'default',
                                      width: 130
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(147, 112, 219, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}>
                                      <div style={{
                                        fontSize: 28,
                                        color: theme === 'dark' ? '#f1f5f9' : '#1e293b',
                                        fontWeight: 900,
                                        lineHeight: 1
                                      }}>
                                        {totalRows}
                                      </div>
                                      <div style={{
                                        fontSize: 10,
                                        color: theme === 'dark' ? '#cbd5e1' : '#475569',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        marginTop: 6
                                      }} dangerouslySetInnerHTML={{ __html: 'Total<br/>atribuições' }} />
                                    </div>

                                    {/* TO-BE Utilizadas */}
                                    <div style={{
                                      background: theme === 'dark'
                                        ? 'linear-gradient(135deg, rgba(74, 222, 128, 0.3), rgba(34, 197, 94, 0.2))'
                                        : 'linear-gradient(135deg, rgba(74, 222, 128, 0.15), rgba(34, 197, 94, 0.08))',
                                      padding: 12,
                                      borderRadius: 8,
                                      border: theme === 'dark'
                                        ? '2px solid rgba(74, 222, 128, 0.5)'
                                        : '2px solid rgba(34, 197, 94, 0.3)',
                                      backdropFilter: 'blur(10px)',
                                      textAlign: 'center',
                                      transition: 'all 0.3s ease',
                                      cursor: 'default',
                                      width: 130
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(74, 222, 128, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}>
                                      <div style={{
                                        fontSize: 22,
                                        color: theme === 'dark' ? '#f1f5f9' : '#1e293b',
                                        fontWeight: 900,
                                        lineHeight: 1
                                      }}>
                                        {usedRows.length}
                                      </div>
                                      <div style={{
                                        fontSize: 10,
                                        color: theme === 'dark' ? '#cbd5e1' : '#475569',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        marginTop: 6
                                      }}>
                                        Utilizadas
                                      </div>
                                      <div style={{
                                        fontSize: 14,
                                        color: theme === 'dark' ? '#a3e635' : '#16a34a',
                                        fontWeight: 700,
                                        marginTop: 4
                                      }}>
                                        {usedPercentage}%
                                      </div>
                                    </div>

                                    {/* TO-BE Não Utilizadas */}
                                    <div style={{
                                      background: theme === 'dark'
                                        ? 'linear-gradient(135deg, rgba(248, 113, 113, 0.3), rgba(239, 68, 68, 0.2))'
                                        : 'linear-gradient(135deg, rgba(248, 113, 113, 0.15), rgba(239, 68, 68, 0.08))',
                                      padding: 12,
                                      borderRadius: 8,
                                      border: theme === 'dark'
                                        ? '2px solid rgba(248, 113, 113, 0.5)'
                                        : '2px solid rgba(239, 68, 68, 0.3)',
                                      backdropFilter: 'blur(10px)',
                                      textAlign: 'center',
                                      transition: 'all 0.3s ease',
                                      cursor: 'default',
                                      width: 130
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(248, 113, 113, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}>
                                      <div style={{
                                        fontSize: 22,
                                        color: theme === 'dark' ? '#f1f5f9' : '#1e293b',
                                        fontWeight: 900,
                                        lineHeight: 1
                                      }}>
                                        {unusedRows.length}
                                      </div>
                                      <div style={{
                                        fontSize: 10,
                                        color: theme === 'dark' ? '#cbd5e1' : '#475569',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        marginTop: 6
                                      }}>
                                        Não Utilizadas
                                      </div>
                                      <div style={{
                                        fontSize: 14,
                                        color: theme === 'dark' ? '#fca5a5' : '#dc2626',
                                        fontWeight: 700,
                                        marginTop: 4
                                      }}>
                                        {unusedPercentage}%
                                      </div>
                                    </div>
                                  </div>

                                  {/* Pizza TO-BE (na direita) */}
                                  <div style={{
                                    flex: '1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <div style={{ 
                                      position: 'relative', 
                                      width: '260px',
                                      height: '260px'
                                    }}>
                                      {/* Tooltip TO-BE */}
                                      {pieTooltipToBe.show && (
                                        <div style={{
                                          position: 'absolute',
                                          left: pieTooltipToBe.x,
                                          top: pieTooltipToBe.y,
                                          transform: 'translate(-50%, -120%)',
                                          background: 'rgba(0, 0, 0, 0.9)',
                                          color: 'white',
                                          padding: '8px 12px',
                                          borderRadius: 8,
                                          fontSize: 13,
                                          fontWeight: 600,
                                          whiteSpace: 'nowrap',
                                          pointerEvents: 'none',
                                          zIndex: 1000,
                                          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                                        }}>
                                          {pieTooltipToBe.text}
                                        </div>
                                      )}
                                      
                                      {/* SVG Pie Chart Novo */}
                                      <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                                        <defs>
                                          <linearGradient id="greenGradientNew" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" style={{ stopColor: '#4ade80', stopOpacity: 1 }} />
                                            <stop offset="100%" style={{ stopColor: '#22c55e', stopOpacity: 1 }} />
                                          </linearGradient>
                                          <linearGradient id="redGradientNew" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" style={{ stopColor: '#f87171', stopOpacity: 1 }} />
                                            <stop offset="100%" style={{ stopColor: '#ef4444', stopOpacity: 1 }} />
                                          </linearGradient>
                                        </defs>
                                        
                                        {/* Used transactions (green) */}
                                        <circle
                                          cx="100"
                                          cy="100"
                                          r="75"
                                          fill="none"
                                          stroke="url(#greenGradientNew)"
                                          strokeWidth="28"
                                          strokeDasharray={`${pieAnimationTriggered && totalRows > 0 ? (usedRows.length / totalRows) * 471.24 : 0} 471.24`}
                                          strokeLinecap="round"
                                          style={{
                                            transition: 'stroke-width 0.3s ease, stroke-dasharray 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            filter: 'drop-shadow(0 2px 8px rgba(34, 197, 94, 0.4))',
                                            cursor: 'pointer'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.strokeWidth = '32';
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const svg = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                            if (svg) {
                                              setPieTooltipToBe({
                                                show: true,
                                                text: `✅ Utilizadas: ${usedRows.length} (${usedPercentage}%)`,
                                                x: (rect.left + rect.right) / 2 - svg.left,
                                                y: rect.top - svg.top
                                              });
                                            }
                                          }}
                                          onMouseMove={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const svg = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                            if (svg) {
                                              setPieTooltipToBe(prev => ({
                                                ...prev,
                                                x: (rect.left + rect.right) / 2 - svg.left,
                                                y: rect.top - svg.top
                                              }));
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.strokeWidth = '28';
                                            setPieTooltipToBe({ show: false, text: '', x: 0, y: 0 });
                                          }}
                                        />
                                        
                                        {/* Unused transactions (red) */}
                                        <circle
                                          cx="100"
                                          cy="100"
                                          r="75"
                                          fill="none"
                                          stroke="url(#redGradientNew)"
                                          strokeWidth="28"
                                          strokeDasharray={`${pieAnimationTriggered && totalRows > 0 ? (unusedRows.length / totalRows) * 471.24 : 0} 471.24`}
                                          strokeDashoffset={`-${pieAnimationTriggered && totalRows > 0 ? (usedRows.length / totalRows) * 471.24 : 0}`}
                                          strokeLinecap="round"
                                          style={{
                                            transition: 'stroke-width 0.3s ease, stroke-dasharray 1.2s cubic-bezier(0.4, 0, 0.2, 1), stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            filter: 'drop-shadow(0 2px 8px rgba(239, 68, 68, 0.4))',
                                            cursor: 'pointer'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.strokeWidth = '32';
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const svg = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                            if (svg) {
                                              setPieTooltipToBe({
                                                show: true,
                                                text: `❌ Não Utilizadas: ${unusedRows.length} (${unusedPercentage}%)`,
                                                x: (rect.left + rect.right) / 2 - svg.left,
                                                y: rect.top - svg.top
                                              });
                                            }
                                          }}
                                          onMouseMove={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const svg = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                            if (svg) {
                                              setPieTooltipToBe(prev => ({
                                                ...prev,
                                                x: (rect.left + rect.right) / 2 - svg.left,
                                                y: rect.top - svg.top
                                              }));
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.strokeWidth = '28';
                                            setPieTooltipToBe({ show: false, text: '', x: 0, y: 0 });
                                          }}
                                        />
                                  
                                        {/* Center text - Percentage used */}
                                        <text
                                          x="100"
                                          y="100"
                                          textAnchor="middle"
                                          dominantBaseline="middle"
                                          style={{
                                            fontSize: '28px',
                                            fontWeight: 700,
                                            fill: theme === 'dark' ? '#ffffff' : '#1e293b',
                                            transform: 'rotate(90deg)',
                                            transformOrigin: 'center',
                                            textShadow: theme === 'dark' ? '0 2px 10px rgba(0,0,0,0.3)' : '0 2px 10px rgba(255,255,255,0.8)'
                                          }}
                                        >
                                          {usedPercentage}%
                                        </text>
                                      </svg>
                                    </div>
                                  </div>

                                  {/* Título TO-BE */}
                                  <h6 style={{
                                    fontSize: 18,
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    margin: 0,
                                    writingMode: 'vertical-rl',
                                    textOrientation: 'mixed',
                                    letterSpacing: '2px'
                                  }}>
                                    CENÁRIO NOVO
                                  </h6>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                    </div>
                  )}

                  {/* Seção removida: Análise de Perfis completa - mantida apenas seção de Indicadores Principais acima */}
                  {false && validationDataLoaded && client?.profileModel === 'Posição Composto + Atividade Simples' && (
                    <div style={{ marginBottom: 24 }}>
                      <style>{`
                        @keyframes fadeIn {
                          from { opacity: 0; }
                          to { opacity: 1; }
                        }
                        @keyframes fadeInUp {
                          from { opacity: 0; transform: translateY(20px); }
                          to { opacity: 1; transform: translateY(0); }
                        }
                        @keyframes slideIn {
                          from { opacity: 0; transform: translateX(-20px); }
                          to { opacity: 1; transform: translateX(0); }
                        }
                        .chart-card {
                          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                          animation: fadeInUp 0.6s ease-out both;
                        }
                        .chart-card:hover {
                          transform: translateY(-4px);
                        }
                        [data-bs-theme="light"] .chart-card:hover {
                          box-shadow: 0 8px 20px rgba(0,0,0,0.12) !important;
                        }
                        [data-bs-theme="dark"] .chart-card:hover {
                          box-shadow: 0 8px 20px rgba(0,0,0,0.4) !important;
                        }
                        .progress-bar-animated {
                          transition: width 0.6s ease-out;
                        }
                        [data-bs-theme="light"] .chart-card {
                          background: var(--bs-body-bg);
                          border: 1px solid #e5e7eb;
                        }
                        [data-bs-theme="dark"] .chart-card {
                          background: var(--bs-body-bg);
                          border: 1px solid #374151;
                        }
                      `}</style>
                      
                      <h5 className="mb-4" style={{ fontWeight: 700, animation: 'slideIn 0.6s ease-out', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
                          <rect x="1" y="1" width="14" height="14" rx="2" fill="none" stroke="#8b5cf6" strokeWidth="1.2" />
                          <rect x="4" y="8" width="2" height="4" rx="0.5" fill="#8b5cf6" />
                          <rect x="7" y="6" width="2" height="6" rx="0.5" fill="#8b5cf6" />
                          <rect x="10" y="4" width="2" height="8" rx="0.5" fill="#8b5cf6" />
                        </svg>
                        Análise de Perfis - Posição Composto + Atividade Simples
                      </h5>
                      
                      {(() => {
                        // Apply filters ONLY to pie chart
                        const filteredRowsForPieChart = validationRows.filter(row => {
                          if (dashboardFilterPosition) {
                            const rowPos = `${row.n1}||${row.n2}||${row.n3}||${row.n4}`;
                            if (rowPos !== dashboardFilterPosition) return false;
                          }
                          if (dashboardFilterArea && row.area_usuario !== dashboardFilterArea) return false;
                          if (dashboardFilterUser && row.usuario !== dashboardFilterUser) return false;
                          return true;
                        });
                        
                        const hasFilters = dashboardFilterPosition || dashboardFilterArea || dashboardFilterUser;
                        
                        return (
                          <>
                            {/* Indicadores Fixos (Não Afetados por Filtros) */}
                            <div className="mb-4" style={{ 
                              padding: '24px',
                              borderRadius: 12,
                              background: theme === 'dark' 
                                ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
                                : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                              border: theme === 'dark' ? '1px solid #334155' : '1px solid #cbd5e1',
                              animation: 'fadeInUp 0.5s ease-out'
                            }}>
                              <h6 className="mb-3" style={{ 
                                fontSize: 14, 
                                fontWeight: 700,
                                color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}>
                                <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
                                  <rect x="1" y="1" width="14" height="14" rx="2" fill="none" stroke="#8b5cf6" strokeWidth="1.2" />
                                  <rect x="4" y="8" width="2" height="4" rx="0.5" fill="#8b5cf6" />
                                  <rect x="7" y="6" width="2" height="6" rx="0.5" fill="#8b5cf6" />
                                  <rect x="10" y="4" width="2" height="8" rx="0.5" fill="#8b5cf6" />
                                </svg>
                                Visão Geral dos Dados
                              </h6>
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                                gap: 16 
                              }}>
                                <div style={{ 
                                  padding: 20,
                                  borderRadius: 10,
                                  background: theme === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(255, 255, 255, 0.8)',
                                  border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                                  transition: 'all 0.3s ease'
                                }}>
                                  <div style={{ 
                                    fontSize: 11, 
                                    color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                    marginBottom: 8,
                                    fontWeight: 600,
                                    textTransform: 'uppercase'
                                  }}>
                                    Total de Perfis Distintos
                                  </div>
                                  <div style={{ 
                                    fontSize: 32, 
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent'
                                  }}>
                                    {(() => {
                                      const uniqueProfiles = new Set();
                                      validationRows.forEach(r => {
                                        if (r.perfil && r.perfil !== '-') {
                                          uniqueProfiles.add(r.perfil);
                                        }
                                      });
                                      return uniqueProfiles.size;
                                    })()}
                                  </div>
                                </div>
                                
                                <div style={{ 
                                  padding: 20,
                                  borderRadius: 10,
                                  background: theme === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(255, 255, 255, 0.8)',
                                  border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                                  transition: 'all 0.3s ease'
                                }}>
                                  <div style={{ 
                                    fontSize: 11, 
                                    color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                    marginBottom: 8,
                                    fontWeight: 600,
                                    textTransform: 'uppercase'
                                  }}>
                                    Total de Atribuições
                                  </div>
                                  <div style={{ 
                                    fontSize: 32, 
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #10b981, #059669)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent'
                                  }}>
                                    {validationRows.length.toLocaleString()}
                                  </div>
                                </div>
                                
                                <div style={{ 
                                  padding: 20,
                                  borderRadius: 10,
                                  background: theme === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(255, 255, 255, 0.8)',
                                  border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                                  transition: 'all 0.3s ease'
                                }}>
                                  <div style={{ 
                                    fontSize: 11, 
                                    color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                    marginBottom: 8,
                                    fontWeight: 600,
                                    textTransform: 'uppercase'
                                  }}>
                                    Usuários com Perfis
                                  </div>
                                  <div style={{ 
                                    fontSize: 32, 
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent'
                                  }}>
                                    {(() => {
                                      const usersWithProfiles = new Set();
                                      validationRows.forEach(r => {
                                        if (r.usuario && r.usuario !== '-') {
                                          usersWithProfiles.add(r.usuario);
                                        }
                                      });
                                      return usersWithProfiles.size;
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Filtros e Gráfico de Pizza lado a lado */}
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'minmax(320px, 400px) 1fr', 
                              gap: 20, 
                              marginBottom: 24,
                              animation: 'fadeInUp 0.6s ease-out 0.2s both'
                            }}>
                              {/* Seção de Filtros */}
                              <div className="card p-4 chart-card" style={{ 
                              borderRadius: 12,
                              boxShadow: theme === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)'
                            }}>
                              <div style={{ marginBottom: 16 }}>
                                <h6 className="mb-2" style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
                                    <rect x="1" y="1" width="14" height="14" rx="2" fill="none" stroke="#8b5cf6" strokeWidth="1.2" />
                                    <rect x="4" y="8" width="2" height="4" rx="0.5" fill="#8b5cf6" />
                                    <rect x="7" y="6" width="2" height="6" rx="0.5" fill="#8b5cf6" />
                                    <rect x="10" y="4" width="2" height="8" rx="0.5" fill="#8b5cf6" />
                                  </svg>
                                  Filtros
                                </h6>
                                <p style={{ 
                                  fontSize: 11, 
                                  color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                  margin: 0
                                }}>
                                  Aplicados apenas ao gráfico de utilização
                                </p>
                                {hasFilters && (
                                  <span className="badge bg-primary mt-2" style={{ fontSize: 10 }}>
                                    {filteredRowsForPieChart.length} de {validationRows.length}
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div>
                                  <label className="form-label" style={{ 
                                    fontSize: 11, 
                                    fontWeight: 600, 
                                    color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                    marginBottom: 6 
                                  }}>
                                    Posição (N1-N2-N3-N4)
                                  </label>
                                  <select 
                                    className="form-select form-select-sm" 
                                    value={dashboardFilterPosition}
                                    onChange={e => setDashboardFilterPosition(e.target.value)}
                                    style={{ borderRadius: 6 }}
                                  >
                                    <option value="">Todas as posições</option>
                                    {(() => {
                                      const positions = new Set<string>();
                                      validationRows.forEach(r => {
                                        if (r.n1 && r.n1 !== '-') {
                                          const posKey = `${r.n1}||${r.n2}||${r.n3}||${r.n4}`;
                                          positions.add(posKey);
                                        }
                                      });
                                      return Array.from(positions).sort().map(pos => {
                                        const parts = pos.split('||');
                                        const label = parts.filter(p => p && p !== '-').join(' > ');
                                        return <option key={pos} value={pos}>{label || pos}</option>;
                                      });
                                    })()}
                                  </select>
                                </div>
                                <div>
                                  <label className="form-label" style={{ 
                                    fontSize: 11, 
                                    fontWeight: 600, 
                                    color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                    marginBottom: 6 
                                  }}>
                                    Área do Usuário
                                  </label>
                                  <select 
                                    className="form-select form-select-sm" 
                                    value={dashboardFilterArea}
                                    onChange={e => setDashboardFilterArea(e.target.value)}
                                    style={{ borderRadius: 6 }}
                                  >
                                    <option value="">Todas as áreas</option>
                                    {(() => {
                                      const areas = new Set<string>();
                                      validationRows.forEach(r => {
                                        if (r.area_usuario && r.area_usuario !== '-') {
                                          areas.add(r.area_usuario);
                                        }
                                      });
                                      return Array.from(areas).sort().map(area => 
                                        <option key={area} value={area}>{area.toUpperCase()}</option>
                                      );
                                    })()}
                                  </select>
                                </div>
                                <div>
                                  <label className="form-label" style={{ 
                                    fontSize: 11, 
                                    fontWeight: 600, 
                                    color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                    marginBottom: 6 
                                  }}>
                                    Usuário
                                  </label>
                                  <select 
                                    className="form-select form-select-sm" 
                                    value={dashboardFilterUser}
                                    onChange={e => setDashboardFilterUser(e.target.value)}
                                    style={{ borderRadius: 6 }}
                                  >
                                    <option value="">Todos os usuários</option>
                                    {(() => {
                                      const users = new Set<string>();
                                      validationRows.forEach(r => {
                                        if (r.usuario && r.usuario !== '-') {
                                          users.add(r.usuario);
                                        }
                                      });
                                      return Array.from(users).sort().map(user => 
                                        <option key={user} value={user}>{user}</option>
                                      );
                                    })()}
                                  </select>
                                </div>
                                {hasFilters && (
                                  <button 
                                    className="btn btn-sm btn-outline-danger mt-2"
                                    onClick={() => {
                                      setDashboardFilterPosition('');
                                      setDashboardFilterArea('');
                                      setDashboardFilterUser('');
                                    }}
                                    style={{ borderRadius: 6 }}
                                  >
                                    ✕ Limpar Filtros
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            {/* Gráfico de Pizza (com filtros) */}
                            <div className="card p-4 chart-card" style={{ 
                              borderRadius: 12,
                              boxShadow: theme === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)'
                            }}>
                              <h6 className="mb-4" style={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
                                  <rect x="1" y="1" width="14" height="14" rx="2" fill="none" stroke="#8b5cf6" strokeWidth="1.2" />
                                  <rect x="4" y="8" width="2" height="4" rx="0.5" fill="#8b5cf6" />
                                  <rect x="7" y="6" width="2" height="6" rx="0.5" fill="#8b5cf6" />
                                  <rect x="10" y="4" width="2" height="8" rx="0.5" fill="#8b5cf6" />
                                </svg>
                                Utilização de Códigos
                              </h6>
                              {(() => {
                                const utilizadas = filteredRowsForPieChart.filter(r => r.utilizada === 'S').length;
                                const naoUtilizadas = filteredRowsForPieChart.filter(r => r.utilizada === 'N').length;
                                const total = utilizadas + naoUtilizadas;
                                
                                if (total === 0) {
                                  return (
                                    <div className="text-center p-5" style={{ color: '#94a3b8' }}>
                                      <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
                                      <div style={{ fontSize: 14, fontWeight: 500 }}>Nenhum dado disponível</div>
                                      <div style={{ fontSize: 12, marginTop: 8 }}>Ajuste os filtros para visualizar dados</div>
                                    </div>
                                  );
                                }
                                
                                const utilizadasPercent = (utilizadas / total) * 100;
                                const naoUtilizadasPercent = (naoUtilizadas / total) * 100;
                                const utilizadasDegrees = (utilizadasPercent / 100) * 360;
                                
                                return (
                                  <div>
                                    <div style={{ display: 'flex', gap: 40, alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
                                      <div 
                                        style={{ 
                                          width: 180, 
                                          height: 180, 
                                          borderRadius: '50%',
                                          background: `conic-gradient(
                                            #10b981 0deg ${utilizadasDegrees}deg,
                                            #ef4444 ${utilizadasDegrees}deg 360deg
                                          )`,
                                          boxShadow: theme === 'dark' 
                                            ? '0 8px 24px rgba(0,0,0,0.5)'
                                            : '0 8px 24px rgba(0,0,0,0.12)',
                                          position: 'relative',
                                          transition: 'transform 0.3s ease',
                                          animation: 'fadeIn 0.8s ease-out'
                                        }}
                                      >
                                        <div style={{
                                          position: 'absolute',
                                          top: '50%',
                                          left: '50%',
                                          transform: 'translate(-50%, -50%)',
                                          backgroundColor: theme === 'dark' ? '#1e293b' : 'white',
                                          width: 110,
                                          height: 110,
                                          borderRadius: '50%',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontWeight: 600,
                                          boxShadow: theme === 'dark'
                                            ? 'inset 0 2px 8px rgba(0,0,0,0.3)'
                                            : 'inset 0 2px 8px rgba(0,0,0,0.08)'
                                        }}>
                                          <div style={{ fontSize: 28, color: '#10b981', fontWeight: 700 }}>{utilizadasPercent.toFixed(1)}%</div>
                                          <div style={{ 
                                            fontSize: 10, 
                                            color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                            marginTop: 4 
                                          }}>Utilizadas</div>
                                        </div>
                                      </div>
                                      
                                      <div style={{ minWidth: 180, flex: 1 }}>
                                        <div style={{ marginBottom: 20, animation: 'slideIn 0.6s ease-out 0.2s both' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                            <div style={{ width: 18, height: 18, backgroundColor: '#10b981', borderRadius: 4, boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)' }}></div>
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>Utilizadas</span>
                                          </div>
                                          <div style={{ fontSize: 28, fontWeight: 700, color: '#10b981', marginLeft: 28 }}>
                                            {utilizadas.toLocaleString()}
                                          </div>
                                          <div style={{ 
                                            fontSize: 11, 
                                            color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                            marginLeft: 28, 
                                            marginTop: 4 
                                          }}>
                                            {utilizadasPercent.toFixed(1)}% do total
                                          </div>
                                        </div>
                                        
                                        <div style={{ animation: 'slideIn 0.6s ease-out 0.3s both' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                            <div style={{ width: 18, height: 18, backgroundColor: '#ef4444', borderRadius: 4, boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)' }}></div>
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>Não Utilizadas</span>
                                          </div>
                                          <div style={{ fontSize: 28, fontWeight: 700, color: '#ef4444', marginLeft: 28 }}>
                                            {naoUtilizadas.toLocaleString()}
                                          </div>
                                          <div style={{ 
                                            fontSize: 11, 
                                            color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                            marginLeft: 28, 
                                            marginTop: 4 
                                          }}>
                                            {naoUtilizadasPercent.toFixed(1)}% do total
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div style={{ 
                                      textAlign: 'center', 
                                      paddingTop: 16, 
                                      marginTop: 20,
                                      borderTop: theme === 'dark' ? '1px solid #374151' : '1px solid #e5e7eb'
                                    }}>
                                      <div style={{ 
                                        fontSize: 13, 
                                        fontWeight: 600,
                                        color: theme === 'dark' ? '#94a3b8' : '#64748b'
                                      }}>
                                        Total: <span style={{ fontSize: 16, fontWeight: 700 }}>{total.toLocaleString()}</span> atribuições
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                            </div>
                            
                            {/* Outros gráficos (sem filtros) */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 20, marginBottom: 20 }}>
                              {/* Gráfico: Distribuição de Perfis por Usuário */}
                              <div className="card p-4 chart-card" style={{ 
                                borderRadius: 12, 
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                border: '1px solid #e9ecef',
                                animationDelay: '0.1s'
                              }}>
                                <h6 className="mb-3" style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                                  👥 Distribuição de Atribuições por Usuário
                                </h6>
                                {(() => {
                                  const userProfileCount = new Map<string, number>();
                                  validationRows.forEach(row => {
                                    const count = userProfileCount.get(row.usuario) || 0;
                                    userProfileCount.set(row.usuario, count + 1);
                                  });
                                  const sortedUsers = Array.from(userProfileCount.entries())
                                    .sort((a, b) => b[1] - a[1])
                                    .slice(0, 10);
                                  
                                  if (sortedUsers.length === 0) {
                                    return <div className="text-center p-4" style={{ color: '#94a3b8' }}>Nenhum dado disponível</div>;
                                  }
                                  
                                  const maxCount = Math.max(...sortedUsers.map(u => u[1]));
                                  
                                  return (
                                    <div>
                                      {sortedUsers.map(([user, count], idx) => (
                                        <div key={user} style={{ marginBottom: 12, animation: `slideIn 0.4s ease-out ${0.1 + idx * 0.05}s both` }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6, alignItems: 'center' }}>
                                            <span style={{ fontFamily: 'monospace', fontWeight: 500, color: '#475569' }}>{user}</span>
                                            <span className="badge bg-primary" style={{ fontSize: 12 }}>{count}</span>
                                          </div>
                                          <div style={{ width: '100%', height: 10, backgroundColor: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                                            <div className="progress-bar-animated" style={{ 
                                              width: `${(count / maxCount) * 100}%`, 
                                              height: '100%', 
                                              background: 'linear-gradient(90deg, #3b82f6, #2563eb)', 
                                              borderRadius: 6,
                                              boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                                            }}></div>
                                          </div>
                                        </div>
                                      ))}
                                      <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef', fontSize: 12, color: '#64748b', textAlign: 'center' }}>
                                        Top 10 usuários com mais atribuições
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Gráfico: Perfis Distintos Mais Atribuídos */}
                              <div className="card p-4 chart-card" style={{ 
                                borderRadius: 12, 
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                border: '1px solid #e9ecef',
                                animationDelay: '0.2s'
                              }}>
                                <h6 className="mb-3" style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                                  📋 Perfis Distintos Mais Atribuídos
                                </h6>
                                {(() => {
                                  const profileCount = new Map<string, number>();
                                  validationRows.forEach(row => {
                                    if (row.perfil && row.perfil !== '-') {
                                      const count = profileCount.get(row.perfil) || 0;
                                      profileCount.set(row.perfil, count + 1);
                                    }
                                  });
                                  const sortedProfiles = Array.from(profileCount.entries())
                                    .sort((a, b) => b[1] - a[1])
                                    .slice(0, 10);
                                  
                                  if (sortedProfiles.length === 0) {
                                    return <div className="text-center p-4" style={{ color: '#94a3b8' }}>Nenhum dado disponível</div>;
                                  }
                                  
                                  const maxCount = Math.max(...sortedProfiles.map(p => p[1]));
                                  
                                  return (
                                    <div>
                                      {sortedProfiles.map(([profile, count], idx) => (
                                        <div key={profile} style={{ marginBottom: 12, animation: `slideIn 0.4s ease-out ${0.1 + idx * 0.05}s both` }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6, alignItems: 'center' }}>
                                            <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 500, color: '#475569' }} title={profile}>
                                              {profile.length > 28 ? profile.substring(0, 28) + '...' : profile}
                                            </span>
                                            <span className="badge bg-success" style={{ fontSize: 11 }}>{count}</span>
                                          </div>
                                          <div style={{ width: '100%', height: 10, backgroundColor: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                                            <div className="progress-bar-animated" style={{ 
                                              width: `${(count / maxCount) * 100}%`, 
                                              height: '100%', 
                                              background: 'linear-gradient(90deg, #10b981, #059669)', 
                                              borderRadius: 6,
                                              boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
                                            }}></div>
                                          </div>
                                        </div>
                                      ))}
                                      <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef', fontSize: 12, color: '#64748b', textAlign: 'center' }}>
                                        Top 10 perfis distintos com mais atribuições
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Gráfico: Distribuição por Área */}
                              <div className="card p-4 chart-card" style={{ 
                                borderRadius: 12, 
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                border: '1px solid #e9ecef',
                                animationDelay: '0.3s'
                              }}>
                                <h6 className="mb-3" style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                                  🏢 Distribuição por Área de Usuário
                                </h6>
                                {(() => {
                                  const areaCount = new Map<string, number>();
                                  validationRows.forEach(row => {
                                    if (row.area_usuario && row.area_usuario !== '-') {
                                      const count = areaCount.get(row.area_usuario) || 0;
                                      areaCount.set(row.area_usuario, count + 1);
                                    }
                                  });
                                  const sortedAreas = Array.from(areaCount.entries())
                                    .sort((a, b) => b[1] - a[1])
                                    .slice(0, 8);
                                  
                                  if (sortedAreas.length === 0) {
                                    return <div className="text-center p-4" style={{ color: '#94a3b8' }}>Nenhum dado disponível</div>;
                                  }
                                  
                                  const maxCount = Math.max(...sortedAreas.map(a => a[1]));
                                  const gradients = [
                                    'linear-gradient(90deg, #3b82f6, #2563eb)',
                                    'linear-gradient(90deg, #8b5cf6, #7c3aed)',
                                    'linear-gradient(90deg, #a855f7, #9333ea)',
                                    'linear-gradient(90deg, #ec4899, #db2777)',
                                    'linear-gradient(90deg, #ef4444, #dc2626)',
                                    'linear-gradient(90deg, #f97316, #ea580c)',
                                    'linear-gradient(90deg, #f59e0b, #d97706)',
                                    'linear-gradient(90deg, #10b981, #059669)'
                                  ];
                                  
                                  return (
                                    <div>
                                      {sortedAreas.map(([area, count], idx) => (
                                        <div key={area} style={{ marginBottom: 12, animation: `slideIn 0.4s ease-out ${0.1 + idx * 0.05}s both` }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6, alignItems: 'center' }}>
                                            <span style={{ textTransform: 'uppercase', fontWeight: 600, color: '#475569', letterSpacing: '0.5px' }}>{area}</span>
                                            <span className="badge" style={{ 
                                              background: gradients[idx % gradients.length],
                                              fontSize: 12,
                                              padding: '4px 10px'
                                            }}>{count}</span>
                                          </div>
                                          <div style={{ width: '100%', height: 10, backgroundColor: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                                            <div className="progress-bar-animated" style={{ 
                                              width: `${(count / maxCount) * 100}%`, 
                                              height: '100%', 
                                              background: gradients[idx % gradients.length], 
                                              borderRadius: 6,
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                                            }}></div>
                                          </div>
                                        </div>
                                      ))}
                                      <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef', fontSize: 12, color: '#64748b', textAlign: 'center' }}>
                                        Atribuições por área de usuário
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Gráfico: Posições com Mais Códigos */}
                              <div className="card p-4 chart-card" style={{ 
                                borderRadius: 12, 
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                border: '1px solid #e9ecef',
                                animationDelay: '0.4s'
                              }}>
                                <h6 className="mb-3" style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                                  📍 Posições com Mais Códigos
                                </h6>
                                {(() => {
                                  const positionCodesCount = new Map<string, Set<string>>();
                                  validationRows.forEach(row => {
                                    if (row.n1 && row.n1 !== '-') {
                                      const posKey = `${row.n1}||${row.n2}||${row.n3}||${row.n4}`;
                                      if (!positionCodesCount.has(posKey)) {
                                        positionCodesCount.set(posKey, new Set());
                                      }
                                      if (row.codigo && row.codigo !== '-') {
                                        positionCodesCount.get(posKey)!.add(row.codigo);
                                      }
                                    }
                                  });
                                  
                                  const sortedPositions = Array.from(positionCodesCount.entries())
                                    .map(([pos, codes]) => [pos, codes.size] as [string, number])
                                    .sort((a, b) => b[1] - a[1])
                                    .slice(0, 8);
                                  
                                  if (sortedPositions.length === 0) {
                                    return <div className="text-center p-4" style={{ color: '#94a3b8' }}>Nenhum dado disponível</div>;
                                  }
                                  
                                  const maxCount = Math.max(...sortedPositions.map(p => p[1]));
                                  
                                  return (
                                    <div>
                                      {sortedPositions.map(([pos, count], idx) => {
                                        const parts = pos.split('||');
                                        const label = parts.filter(p => p && p !== '-').join(' > ');
                                        return (
                                          <div key={pos} style={{ marginBottom: 12, animation: `slideIn 0.4s ease-out ${0.1 + idx * 0.05}s both` }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6, alignItems: 'center' }}>
                                              <span style={{ fontWeight: 500, color: '#475569' }} title={label}>
                                                {label.length > 32 ? label.substring(0, 32) + '...' : label}
                                              </span>
                                              <span className="badge" style={{ 
                                                background: 'linear-gradient(90deg, #8b5cf6, #7c3aed)',
                                                fontSize: 11
                                              }}>{count} códigos</span>
                                            </div>
                                            <div style={{ width: '100%', height: 10, backgroundColor: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                                              <div className="progress-bar-animated" style={{ 
                                                width: `${(count / maxCount) * 100}%`, 
                                                height: '100%', 
                                                background: 'linear-gradient(90deg, #8b5cf6, #7c3aed)', 
                                                borderRadius: 6,
                                                boxShadow: '0 2px 4px rgba(139, 92, 246, 0.3)'
                                              }}></div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                      <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef', fontSize: 12, color: '#64748b', textAlign: 'center' }}>
                                        Top 8 posições com mais códigos distintos
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Gráfico: Taxa de Cobertura de Perfis por Usuário */}
                              <div className="card p-4 chart-card" style={{ 
                                borderRadius: 12, 
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                border: '1px solid #e9ecef',
                                animationDelay: '0.5s'
                              }}>
                                <h6 className="mb-3" style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
                                    <rect x="1" y="1" width="14" height="14" rx="2" fill="none" stroke="#8b5cf6" strokeWidth="1.2" />
                                    <rect x="4" y="8" width="2" height="4" rx="0.5" fill="#8b5cf6" />
                                    <rect x="7" y="6" width="2" height="6" rx="0.5" fill="#8b5cf6" />
                                    <rect x="10" y="4" width="2" height="8" rx="0.5" fill="#8b5cf6" />
                                  </svg>
                                  Taxa de Cobertura de Perfis
                                </h6>
                                {(() => {
                                  const userProfilesCount = new Map<string, Set<string>>();
                                  validationRows.forEach(row => {
                                    if (row.usuario && row.usuario !== '-' && row.perfil && row.perfil !== '-') {
                                      if (!userProfilesCount.has(row.usuario)) {
                                        userProfilesCount.set(row.usuario, new Set());
                                      }
                                      userProfilesCount.get(row.usuario)!.add(row.perfil);
                                    }
                                  });
                                  
                                  const profilesPerUser = Array.from(userProfilesCount.values()).map(profiles => profiles.size);
                                  if (profilesPerUser.length === 0) {
                                    return <div className="text-center p-4" style={{ color: '#94a3b8' }}>Nenhum dado disponível</div>;
                                  }
                                  
                                  const avg = profilesPerUser.reduce((sum, val) => sum + val, 0) / profilesPerUser.length;
                                  const min = Math.min(...profilesPerUser);
                                  const max = Math.max(...profilesPerUser);
                                  
                                  return (
                                    <div>
                                      <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 20, gap: 12 }}>
                                        <div style={{ 
                                          textAlign: 'center', 
                                          padding: 16, 
                                          background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                                          borderRadius: 10,
                                          flex: 1,
                                          animation: 'fadeIn 0.6s ease-out 0.2s both'
                                        }}>
                                          <div style={{ fontSize: 36, fontWeight: 700, color: '#10b981' }}>{avg.toFixed(1)}</div>
                                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, fontWeight: 500 }}>Média de perfis/usuário</div>
                                        </div>
                                        <div style={{ 
                                          textAlign: 'center', 
                                          padding: 16, 
                                          background: 'linear-gradient(135deg, #e0f2fe, #bae6fd)',
                                          borderRadius: 10,
                                          flex: 1,
                                          animation: 'fadeIn 0.6s ease-out 0.3s both'
                                        }}>
                                          <div style={{ fontSize: 36, fontWeight: 700, color: '#0ea5e9' }}>{min}</div>
                                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, fontWeight: 500 }}>Mínimo</div>
                                        </div>
                                        <div style={{ 
                                          textAlign: 'center', 
                                          padding: 16, 
                                          background: 'linear-gradient(135deg, #f3e8ff, #e9d5ff)',
                                          borderRadius: 10,
                                          flex: 1,
                                          animation: 'fadeIn 0.6s ease-out 0.4s both'
                                        }}>
                                          <div style={{ fontSize: 36, fontWeight: 700, color: '#a855f7' }}>{max}</div>
                                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, fontWeight: 500 }}>Máximo</div>
                                        </div>
                                      </div>
                                      <div style={{ 
                                        paddingTop: 16, 
                                        borderTop: '2px solid #f1f5f9',
                                        textAlign: 'center',
                                        background: 'linear-gradient(to bottom, transparent, #f8fafc)',
                                        margin: '0 -16px -16px',
                                        padding: '16px',
                                        borderRadius: '0 0 8px 8px'
                                      }}>
                                        <div style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>
                                          Total de {userProfilesCount.size} usuários com perfis atribuídos
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>

                            </div>
                            
                            {/* Alertas e Notificações */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 20, marginTop: 20 }}>
                              {/* Alertas de Perfis Master sem Org Level */}
                              {(() => {
                                const masterWithoutOrgLevel = validationRows.filter(r => r.master_sem_orglevel === true);
                                if (masterWithoutOrgLevel.length > 0) {
                                  const uniqueUsers = new Set(masterWithoutOrgLevel.map(r => r.usuario));
                                  return (
                                    <div className="card p-4 chart-card" style={{ 
                                      background: 'linear-gradient(135deg, #fffbeb, #fef3c7)', 
                                      border: '2px solid #fbbf24',
                                      borderRadius: 12,
                                      boxShadow: '0 4px 12px rgba(251, 191, 36, 0.2)',
                                      animationDelay: '0.6s'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                                        <div style={{ 
                                          fontSize: 40,
                                          background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                                          WebkitBackgroundClip: 'text',
                                          WebkitTextFillColor: 'transparent',
                                          lineHeight: 1
                                        }}>⚠️</div>
                                        <div style={{ flex: 1 }}>
                                          <h6 className="mb-3" style={{ fontSize: 15, color: '#92400e', fontWeight: 700 }}>
                                            Alertas de Mapeamento Organizacional
                                          </h6>
                                          <div style={{ fontSize: 42, fontWeight: 700, color: '#b45309', marginBottom: 12, lineHeight: 1 }}>
                                            {uniqueUsers.size}
                                          </div>
                                          <div style={{ fontSize: 14, marginBottom: 10, color: '#78350f', fontWeight: 600 }}>
                                            Usuários com perfil Master sem mapeamento de nível organizacional
                                          </div>
                                          <div style={{ fontSize: 13, color: '#92400e', marginBottom: 12 }}>
                                            📌 Total de {masterWithoutOrgLevel.length} atribuições afetadas
                                          </div>
                                          <div style={{ 
                                            fontSize: 12, 
                                            color: '#78350f', 
                                            padding: 12,
                                            background: 'rgba(255, 255, 255, 0.5)',
                                            borderRadius: 8,
                                            borderLeft: '3px solid #f59e0b'
                                          }}>
                                            💡 Recomenda-se configurar mapeamento organizacional no Passo 2 para derivação adequada
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}

                              {/* Perfis com Atribuição Direta (Risco) */}
                              {(() => {
                                const directAttribution = validationRows.filter(r => r.perfil_composto === 'DIRECT_ATTRIBUTION');
                                if (directAttribution.length > 0) {
                                  const uniqueRiskProfiles = new Set(directAttribution.map(r => r.perfil));
                                  return (
                                    <div className="card p-4 chart-card" style={{ 
                                      background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)', 
                                      border: '2px solid #3b82f6',
                                      borderRadius: 12,
                                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                                      animationDelay: '0.7s'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                                        <div style={{ 
                                          fontSize: 40,
                                          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                          WebkitBackgroundClip: 'text',
                                          WebkitTextFillColor: 'transparent',
                                          lineHeight: 1
                                        }}>🛡️</div>
                                        <div style={{ flex: 1 }}>
                                          <h6 className="mb-3" style={{ fontSize: 15, color: '#1e3a8a', fontWeight: 700 }}>
                                            Perfis de Risco - Atribuição Direta
                                          </h6>
                                          <div style={{ fontSize: 42, fontWeight: 700, color: '#1d4ed8', marginBottom: 12, lineHeight: 1 }}>
                                            {uniqueRiskProfiles.size}
                                          </div>
                                          <div style={{ fontSize: 14, marginBottom: 10, color: '#1e40af', fontWeight: 600 }}>
                                            Perfis de risco atribuídos diretamente aos usuários
                                          </div>
                                          <div style={{ fontSize: 13, color: '#1e3a8a', marginBottom: 12 }}>
                                            📌 Total de {directAttribution.length} atribuições diretas
                                          </div>
                                          <div style={{ 
                                            fontSize: 12, 
                                            color: '#1e40af', 
                                            padding: 12,
                                            background: 'rgba(255, 255, 255, 0.5)',
                                            borderRadius: 8,
                                            borderLeft: '3px solid #2563eb'
                                          }}>
                                            ℹ️ Perfis de risco configurados para atribuição direta (san_risk_attrb_model = false)
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* Nova Seção: Analise de volume de transações por perfil */}
                  {validationDataLoaded && (
                    <div style={{
                      marginTop: 40,
                      background: theme === 'dark'
                        ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.05))'
                        : 'linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(59, 130, 246, 0.02))',
                      borderRadius: 16,
                      padding: 32,
                      border: theme === 'dark' ? '1px solid rgba(139, 92, 246, 0.2)' : '1px solid rgba(139, 92, 246, 0.1)',
                      boxShadow: theme === 'dark'
                        ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                        : '0 8px 32px rgba(139, 92, 246, 0.1)',
                      animation: 'fadeInUp 0.6s ease-out'
                    }}>
                      <h5 style={{
                        marginBottom: 24,
                        fontSize: 20,
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                      }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
                          <rect x="1" y="1" width="14" height="14" rx="2" fill="none" stroke="#8b5cf6" strokeWidth="1.2" />
                          <rect x="4" y="8" width="2" height="4" rx="0.5" fill="#8b5cf6" />
                          <rect x="7" y="6" width="2" height="6" rx="0.5" fill="#8b5cf6" />
                          <rect x="10" y="4" width="2" height="8" rx="0.5" fill="#8b5cf6" />
                        </svg>
                        Analise de volume de transações por perfil
                      </h5>

                      {(() => {
                        const pyramidStyles = [
                          {
                            label: '1',
                            colors: theme === 'dark'
                              ? { bg: 'rgba(59, 130, 246, 0.12)', border: 'rgba(59, 130, 246, 0.25)', label: '#93c5fd', value: '#bfdbfe' }
                              : { bg: 'rgba(59, 130, 246, 0.08)', border: 'rgba(59, 130, 246, 0.2)', label: '#1d4ed8', value: '#1e40af' }
                          },
                          {
                            label: '2 - 10',
                            colors: theme === 'dark'
                              ? { bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.25)', label: '#86efac', value: '#bbf7d0' }
                              : { bg: 'rgba(16, 185, 129, 0.08)', border: 'rgba(16, 185, 129, 0.2)', label: '#047857', value: '#065f46' }
                          },
                          {
                            label: '11 - 50',
                            colors: theme === 'dark'
                              ? { bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.25)', label: '#fcd34d', value: '#fde68a' }
                              : { bg: 'rgba(245, 158, 11, 0.08)', border: 'rgba(245, 158, 11, 0.2)', label: '#b45309', value: '#92400e' }
                          },
                          {
                            label: '51 - 100',
                            colors: theme === 'dark'
                              ? { bg: 'rgba(239, 68, 68, 0.12)', border: 'rgba(239, 68, 68, 0.25)', label: '#fca5a5', value: '#fecaca' }
                              : { bg: 'rgba(239, 68, 68, 0.08)', border: 'rgba(239, 68, 68, 0.2)', label: '#b91c1c', value: '#991b1b' }
                          },
                          {
                            label: '100+',
                            colors: theme === 'dark'
                              ? { bg: 'rgba(148, 163, 184, 0.12)', border: 'rgba(148, 163, 184, 0.25)', label: '#cbd5e1', value: '#e2e8f0' }
                              : { bg: 'rgba(100, 116, 139, 0.08)', border: 'rgba(100, 116, 139, 0.2)', label: '#334155', value: '#0f172a' }
                          }
                        ];

                        const asisPyramidLevels = [
                          { key: 'asis-1', label: pyramidStyles[0].label, colors: pyramidStyles[0].colors, value: volumetriaIndicators.currentProfiles1Transaction },
                          { key: 'asis-2-10', label: pyramidStyles[1].label, colors: pyramidStyles[1].colors, value: volumetriaIndicators.currentProfiles2to10Transactions },
                          { key: 'asis-11-50', label: pyramidStyles[2].label, colors: pyramidStyles[2].colors, value: volumetriaIndicators.currentProfiles11to50Transactions },
                          { key: 'asis-51-100', label: pyramidStyles[3].label, colors: pyramidStyles[3].colors, value: volumetriaIndicators.currentProfiles51to100Transactions },
                          { key: 'asis-100plus', label: pyramidStyles[4].label, colors: pyramidStyles[4].colors, value: volumetriaIndicators.currentProfilesOver100Transactions }
                        ];

                        const tobePyramidLevels = [
                          { key: 'tobe-1', label: pyramidStyles[0].label, colors: pyramidStyles[0].colors, value: volumetriaIndicators.profiles1Transaction },
                          { key: 'tobe-2-10', label: pyramidStyles[1].label, colors: pyramidStyles[1].colors, value: volumetriaIndicators.profiles2to10Transactions },
                          { key: 'tobe-11-50', label: pyramidStyles[2].label, colors: pyramidStyles[2].colors, value: volumetriaIndicators.profiles11to50Transactions },
                          { key: 'tobe-51-100', label: pyramidStyles[3].label, colors: pyramidStyles[3].colors, value: volumetriaIndicators.profiles51to100Transactions },
                          { key: 'tobe-100plus', label: pyramidStyles[4].label, colors: pyramidStyles[4].colors, value: volumetriaIndicators.profilesOver100Transactions }
                        ];

                        return (
                          <div style={{
                            display: 'flex',
                            gap: 32,
                            alignItems: 'stretch'
                          }}>
                            <div style={{
                              flex: '1',
                              background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.75)',
                              border: theme === 'dark' ? '1px solid rgba(59,130,246,0.22)' : '1px solid rgba(59,130,246,0.15)',
                              borderRadius: 14,
                              padding: 18
                            }}>
                              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme === 'dark' ? '#93c5fd' : '#1d4ed8' }}>
                                Cenário Atual (AS-IS)
                              </div>
                              <div style={{ fontSize: 22, fontWeight: 800, color: theme === 'dark' ? '#e2e8f0' : '#0f172a', marginBottom: 10 }}>
                                {volumetriaIndicators.currentTotalProfiles.toLocaleString()}
                              </div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: theme === 'dark' ? '#94a3b8' : '#64748b', marginBottom: 14 }}>
                                Total de perfis
                              </div>

                              <div style={{ width: '100%', maxWidth: 440, margin: '0 auto' }}>
                                <svg viewBox="0 0 360 230" style={{ width: '100%', height: 'auto', display: 'block' }} preserveAspectRatio="xMidYMid meet">
                                  {asisPyramidLevels.map((level, index) => {
                                    const topWidth = 44 + (index * 34);
                                    const bottomWidth = topWidth + 24;
                                    const centerX = 190;
                                    const y = 10 + (index * 42);
                                    const h = 30;
                                    const points = `${centerX - topWidth / 2},${y} ${centerX + topWidth / 2},${y} ${centerX + bottomWidth / 2},${y + h} ${centerX - bottomWidth / 2},${y + h}`;

                                    return (
                                      <g key={level.key}>
                                        <polygon points={points} fill={level.colors.bg} stroke={level.colors.border} strokeWidth={1.2} />
                                        <text
                                          x={centerX}
                                          y={y + (h / 2) + 1}
                                          textAnchor="middle"
                                          dominantBaseline="middle"
                                          style={{ fontSize: 15, fontWeight: 800, fill: level.colors.label, letterSpacing: '0.3px' }}
                                        >
                                          {level.label}
                                        </text>
                                        <text
                                          x={12}
                                          y={y + (h / 2) + 1}
                                          textAnchor="start"
                                          dominantBaseline="middle"
                                          style={{ fontSize: 16, fontWeight: 800, fill: level.colors.value }}
                                        >
                                          {level.value.toLocaleString()}
                                        </text>
                                      </g>
                                    );
                                  })}
                                </svg>
                              </div>
                            </div>

                            <div style={{
                              flex: '1',
                              background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.75)',
                              border: theme === 'dark' ? '1px solid rgba(16,185,129,0.22)' : '1px solid rgba(16,185,129,0.15)',
                              borderRadius: 14,
                              padding: 18
                            }}>
                              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme === 'dark' ? '#86efac' : '#047857' }}>
                                Cenário Novo (TO-BE)
                              </div>
                              <div style={{ fontSize: 22, fontWeight: 800, color: theme === 'dark' ? '#e2e8f0' : '#0f172a', marginBottom: 10 }}>
                                {volumetriaIndicators.totalProfiles.toLocaleString()}
                              </div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: theme === 'dark' ? '#94a3b8' : '#64748b', marginBottom: 14 }}>
                                Total de perfis
                              </div>

                              <div style={{ width: '100%', maxWidth: 440, margin: '0 auto' }}>
                                <svg viewBox="0 0 360 230" style={{ width: '100%', height: 'auto', display: 'block' }} preserveAspectRatio="xMidYMid meet">
                                  {tobePyramidLevels.map((level, index) => {
                                    const topWidth = 44 + (index * 34);
                                    const bottomWidth = topWidth + 24;
                                    const centerX = 170;
                                    const y = 10 + (index * 42);
                                    const h = 30;
                                    const points = `${centerX - topWidth / 2},${y} ${centerX + topWidth / 2},${y} ${centerX + bottomWidth / 2},${y + h} ${centerX - bottomWidth / 2},${y + h}`;

                                    return (
                                      <g key={level.key}>
                                        <polygon points={points} fill={level.colors.bg} stroke={level.colors.border} strokeWidth={1.2} />
                                        <text
                                          x={centerX}
                                          y={y + (h / 2) + 1}
                                          textAnchor="middle"
                                          dominantBaseline="middle"
                                          style={{ fontSize: 15, fontWeight: 800, fill: level.colors.label, letterSpacing: '0.3px' }}
                                        >
                                          {level.label}
                                        </text>
                                        <text
                                          x={348}
                                          y={y + (h / 2) + 1}
                                          textAnchor="end"
                                          dominantBaseline="middle"
                                          style={{ fontSize: 16, fontWeight: 800, fill: level.colors.value }}
                                        >
                                          {level.value.toLocaleString()}
                                        </text>
                                      </g>
                                    );
                                  })}
                                </svg>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Nova Seção: Perfis - Volumetria */}
                  {validationDataLoaded && (
                    <div style={{
                      marginTop: 40,
                      background: theme === 'dark' 
                        ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.05))'
                        : 'linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(59, 130, 246, 0.02))',
                      borderRadius: 16,
                      padding: 32,
                      border: theme === 'dark' ? '1px solid rgba(139, 92, 246, 0.2)' : '1px solid rgba(139, 92, 246, 0.1)',
                      boxShadow: theme === 'dark' 
                        ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                        : '0 8px 32px rgba(139, 92, 246, 0.1)',
                      animation: 'fadeInUp 0.6s ease-out'
                    }}>
                      <h5 style={{ 
                        marginBottom: 32, 
                        fontSize: 20,
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                      }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
                          <rect x="1" y="1" width="14" height="14" rx="2" fill="none" stroke="#8b5cf6" strokeWidth="1.2" />
                          <rect x="4" y="8" width="2" height="4" rx="0.5" fill="#8b5cf6" />
                          <rect x="7" y="6" width="2" height="6" rx="0.5" fill="#8b5cf6" />
                          <rect x="10" y="4" width="2" height="8" rx="0.5" fill="#8b5cf6" />
                        </svg>
                        Perfis - Volumetria
                      </h5>
                      
                      {/* Card Único com Total e Subcategorias */}
                      {(() => {
                        const profileModel = String(client?.profileModel || '');
                        const isPositionComposite = profileModel === 'Posição Composto + Atividade Simples';
                        const isActivitySimple = profileModel === 'Atividade Simples';
                        // Check if risk sanitation is enabled
                        const riskBenchmarkEnabled = client?.san_risk_benchmark === true;
                        const riskClientEnabled = client?.san_risk_cliente === true;
                        const showRiskProfiles = riskBenchmarkEnabled || riskClientEnabled;
                        
                        // Check if derivation is enabled
                        const derivationEnabled = client?.derivation_status === true;
                        const showMasterDerived = derivationEnabled;

                        const profileVolumeRows = (() => {
                          const profileMap = new Map<string, {
                            baseTransactionsCount: number;
                            validationTransactions: Set<string>;
                            users: Set<string>;
                            isRisk: boolean;
                            sampleCode: string;
                            n1: string;
                            n2: string;
                            n3: string;
                            n4: string;
                            area: string;
                            processo: string;
                            subprocesso: string;
                            atividade: string;
                          }>();

                          const isPositionSimpleModel = profileModel === 'Posição/Cargo Simples';
                          const isActivityLikeModel = profileModel === 'Atividade Simples' || profileModel === 'Posição Composto + Atividade Simples';

                          const pickValue = (...values: any[]) => {
                            for (const value of values) {
                              const normalized = String(value || '').trim();
                              if (normalized && normalized !== '-') return normalized;
                            }
                            return '';
                          };

                          volumetriaUniverseProfiles.forEach((row) => {
                            profileMap.set(row.perfil, {
                              baseTransactionsCount: row.transactionsCount,
                              validationTransactions: new Set<string>(),
                              users: new Set<string>(),
                              isRisk: Boolean(row.isRisk),
                              sampleCode: pickValue(row.sampleCode),
                              n1: pickValue(row.n1),
                              n2: pickValue(row.n2),
                              n3: pickValue(row.n3),
                              n4: pickValue(row.n4),
                              area: pickValue(row.area),
                              processo: pickValue(row.processo),
                              subprocesso: pickValue(row.subprocesso),
                              atividade: pickValue(row.atividade)
                            });
                          });

                          validationRows.forEach((row: any) => {
                            const perfilRaw = String(row.perfil || row.profile || row.profilename || '').trim();
                            if (!perfilRaw || perfilRaw === '-') return;

                            const perfil = perfilRaw.toUpperCase();
                            const current = profileMap.get(perfil) || {
                              baseTransactionsCount: 0,
                              validationTransactions: new Set<string>(),
                              users: new Set<string>(),
                              isRisk: false,
                              sampleCode: '',
                              n1: '',
                              n2: '',
                              n3: '',
                              n4: '',
                              area: '',
                              processo: '',
                              subprocesso: '',
                              atividade: ''
                            };

                            const codigo = String(row.codigo || row.code || '').trim().toUpperCase();
                            if (codigo && codigo !== '-') {
                              current.validationTransactions.add(codigo);
                              current.sampleCode = current.sampleCode || codigo;
                            }

                            const usuario = String(row.usuario || row.user || '').trim();
                            if (usuario && usuario !== '-') current.users.add(usuario);

                            current.n1 = current.n1 || pickValue(row.n1);
                            current.n2 = current.n2 || pickValue(row.n2);
                            current.n3 = current.n3 || pickValue(row.n3);
                            current.n4 = current.n4 || pickValue(row.n4);
                            current.area = current.area || pickValue(row.area_perfil, row.area);
                            current.processo = current.processo || pickValue(row.processo, row.process);
                            current.subprocesso = current.subprocesso || pickValue(row.subprocesso, row.subprocess);
                            current.atividade = current.atividade || pickValue(row.atividade, row.activity);

                            profileMap.set(perfil, current);
                          });

                          const riskPrefixes = [
                            String(client?.pattern_r_prefixo_risco || '').trim().toUpperCase(),
                            String(client?.pattern_r_prefixo_risco_mestre || '').trim().toUpperCase(),
                            String(client?.pattern_r_prefixo_risco_derivado || '').trim().toUpperCase()
                          ].filter(Boolean);

                          const isRiskByName = (profileName: string) => {
                            const normalized = String(profileName || '').trim().toUpperCase();
                            if (!normalized) return false;
                            if (riskPrefixes.some((prefix) => normalized.startsWith(prefix))) return true;
                            return normalized.startsWith('ZR:') || normalized.startsWith('ZR-') || normalized.startsWith('ZR_');
                          };

                          const getRiskCodeFromProfile = (profileName: string, sampleCode: string) => {
                            const normalized = String(profileName || '').trim().toUpperCase();
                            const explicitCode = pickValue(sampleCode).toUpperCase();
                            if (explicitCode) return explicitCode;

                            const partsAfterColon = normalized.includes(':') ? normalized.split(':').slice(1).join(':') : normalized;
                            const token = partsAfterColon.split('-').map((part) => part.trim()).filter(Boolean).pop() || '';
                            return token;
                          };

                          const getRiskProfileParts = (profileName: string, sampleCode: string) => {
                            const normalized = String(profileName || '').trim().toUpperCase();
                            const explicitCode = pickValue(sampleCode).toUpperCase();

                            const sortedPrefixes = [...riskPrefixes].sort((a, b) => b.length - a.length);
                            const matchedPrefix = sortedPrefixes.find((prefix) => normalized.startsWith(prefix));

                            let remaining = normalized;
                            if (matchedPrefix) {
                              remaining = remaining.slice(matchedPrefix.length);
                            } else if (remaining.startsWith('ZR:') || remaining.startsWith('ZR-') || remaining.startsWith('ZR_')) {
                              remaining = remaining.slice(3);
                            }

                            remaining = remaining.replace(/^[:\-_\s]+/, '').trim();

                            let actionCode = explicitCode;
                            if (actionCode) {
                              const escapedCode = actionCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                              const codeAtEndRegex = new RegExp(`(?:[:\\-_])?${escapedCode}$`);
                              remaining = remaining.replace(codeAtEndRegex, '').trim();
                            }

                            let tokens = remaining.split('-').map((part) => part.trim()).filter(Boolean);

                            if (!actionCode) {
                              actionCode = tokens.length > 0 ? tokens[tokens.length - 1] : '';
                              if (tokens.length > 0) {
                                tokens = tokens.slice(0, -1);
                              }
                            }

                            return {
                              actionCode,
                              intermediateCount: tokens.length
                            };
                          };

                          const riskEnabledFields = {
                            area: Number(client?.pattern_r_area_qtd || 0) > 0,
                            processo: Number(client?.pattern_r_processo_qtd || 0) > 0,
                            subprocesso: Number(client?.pattern_r_subprocesso_qtd || 0) > 0,
                            atividade: Number(client?.pattern_r_atividade_qtd || 0) > 0
                          };

                          const rows = Array.from(profileMap.entries()).map(([perfil, metrics]) => ({
                            isRisk: metrics.isRisk || isRiskByName(perfil),
                            perfil,
                            transactionsCount: Math.max(metrics.baseTransactionsCount, metrics.validationTransactions.size),
                            usersCount: metrics.users.size,
                            detailsLine: (() => {
                              const isRiskProfile = metrics.isRisk || isRiskByName(perfil);

                              if (isRiskProfile) {
                                const { actionCode, intermediateCount } = getRiskProfileParts(perfil, metrics.sampleCode);

                                const orderedRiskValues = isPositionSimpleModel
                                  ? [metrics.n1, metrics.n2, metrics.n3, metrics.n4]
                                      .filter((value) => Boolean(value && String(value).trim()))
                                      .map((value) => String(value).trim())
                                  : [
                                      { enabled: riskEnabledFields.area, value: metrics.area, ord: Number(client?.pattern_r_area_ord || 0) },
                                      { enabled: riskEnabledFields.processo, value: metrics.processo, ord: Number(client?.pattern_r_processo_ord || 0) },
                                      { enabled: riskEnabledFields.subprocesso, value: metrics.subprocesso, ord: Number(client?.pattern_r_subprocesso_ord || 0) },
                                      { enabled: riskEnabledFields.atividade, value: metrics.atividade, ord: Number(client?.pattern_r_atividade_ord || 0) }
                                    ]
                                      .filter((item) => item.enabled)
                                      .sort((a, b) => a.ord - b.ord)
                                      .map((item) => String(item.value || '').trim())
                                      .filter(Boolean);

                                const composedElements = intermediateCount > 0
                                  ? orderedRiskValues.slice(0, intermediateCount)
                                  : [];

                                const fallbackActionCode = getRiskCodeFromProfile(perfil, metrics.sampleCode);
                                const finalActionCode = actionCode || fallbackActionCode;
                                const parts = ['Perfil de Risco', ...composedElements];

                                if (finalActionCode && !parts.some((part) => String(part).trim().toUpperCase() === finalActionCode.toUpperCase())) {
                                  parts.push(finalActionCode);
                                }

                                return parts.join(' | ');
                              }

                              return isPositionSimpleModel
                                ? [metrics.n1, metrics.n2, metrics.n3, metrics.n4].filter((value) => Boolean(value && String(value).trim())).join(' | ')
                                : isActivityLikeModel
                                  ? [metrics.area, metrics.processo, metrics.subprocesso, metrics.atividade].filter((value) => Boolean(value && String(value).trim())).join(' | ')
                                  : '';
                            })()
                          }));

                          return rows.sort((a, b) => {
                            const direction = volumetriaTableSortDirection === 'asc' ? 1 : -1;
                            if (volumetriaTableSortColumn === 'perfil') {
                              return a.perfil.localeCompare(b.perfil, 'pt-BR') * direction;
                            }
                            return ((a[volumetriaTableSortColumn] as number) - (b[volumetriaTableSortColumn] as number)) * direction;
                          });
                        })();

                        const handleVolumetriaSort = (column: 'perfil' | 'transactionsCount' | 'usersCount', direction?: 'asc' | 'desc') => {
                          if (direction) {
                            setVolumetriaTableSortColumn(column);
                            setVolumetriaTableSortDirection(direction);
                            setVolumetriaTablePage(1);
                            return;
                          }
                          if (volumetriaTableSortColumn === column) {
                            setVolumetriaTableSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                            setVolumetriaTablePage(1);
                            return;
                          }
                          setVolumetriaTableSortColumn(column);
                          setVolumetriaTableSortDirection(column === 'perfil' ? 'asc' : 'desc');
                          setVolumetriaTablePage(1);
                        };

                        const totalPages = Math.max(1, Math.ceil(profileVolumeRows.length / (volumetriaTablePageSize || 10)));
                        const currentPage = Math.min(volumetriaTablePage, totalPages);
                        const startIndex = (currentPage - 1) * (volumetriaTablePageSize || 10);
                        return (
                          <div style={{ 
                            background: theme === 'dark' 
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(255, 255, 255, 0.8)',
                            border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                            borderRadius: 16,
                            padding: 32,
                            transition: 'all 0.3s ease',
                            animation: 'fadeIn 0.6s ease-out',
                            boxShadow: theme === 'dark' 
                              ? '0 4px 16px rgba(0, 0, 0, 0.2)'
                              : '0 4px 16px rgba(0, 0, 0, 0.05)'
                          }}>
                            {/* Layout Horizontal: Total à esquerda, Subcategorias à direita */}
                            <div style={{
                              display: 'flex',
                              gap: 32,
                              alignItems: 'stretch'
                            }}>
                              {/* Total de Perfis - Destaque à Esquerda */}
                              <div style={{ 
                                flex: '0 0 auto',
                                minWidth: 280,
                                padding: 32,
                                borderRadius: 12,
                                background: theme === 'dark'
                                  ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(59, 130, 246, 0.1))'
                                  : 'linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(59, 130, 246, 0.05))',
                                border: theme === 'dark' 
                                  ? '2px solid rgba(139, 92, 246, 0.3)'
                                  : '2px solid rgba(139, 92, 246, 0.2)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                              }}>
                                <div style={{ 
                                  fontSize: 14, 
                                  fontWeight: 600,
                                  color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                  marginBottom: 16,
                                  textTransform: 'uppercase',
                                  letterSpacing: '1px'
                                }}>Total de Perfis</div>
                                <div style={{ 
                                  fontSize: 64, 
                                  fontWeight: 800,
                                  background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  lineHeight: 1,
                                  marginBottom: 8
                                }}>
                                  {volumetriaIndicators.totalProfiles.toLocaleString()}
                                </div>
                                <div style={{ 
                                  fontSize: 13, 
                                  color: theme === 'dark' ? '#64748b' : '#94a3b8',
                                  fontWeight: 500
                                }}>perfis distintos gerados</div>
                              </div>
                            
                              {/* Subcategorias em Grid à Direita */}
                              <div style={{
                                flex: 1,
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: 20,
                                alignContent: 'start'
                              }}>
                              {/* Perfis Compostos - Só para Posição Composto */}
                              {isPositionComposite && (
                                <div style={{
                                  padding: 16,
                                  borderRadius: 12,
                                  background: theme === 'dark'
                                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.1))'
                                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(37, 99, 235, 0.05))',
                                  border: theme === 'dark'
                                    ? '1px solid rgba(59, 130, 246, 0.3)'
                                    : '1px solid rgba(59, 130, 246, 0.2)',
                                  transition: 'all 0.3s ease'
                                }}>
                                  <div style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: theme === 'dark' ? '#93c5fd' : '#2563eb',
                                    marginBottom: 8,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                  Perfis Compostos
                                  </div>
                                  <div style={{
                                    fontSize: 32,
                                    fontWeight: 700,
                                    color: theme === 'dark' ? '#60a5fa' : '#1d4ed8',
                                    lineHeight: 1
                                  }}>
                                    {volumetriaIndicators.compositeProfiles.toLocaleString()}
                                  </div>
                                </div>
                              )}
                              
                              {/* Perfis de Risco - Se saneamento estiver habilitado */}
                              {showRiskProfiles && (
                                <div style={{
                                  padding: 16,
                                  borderRadius: 12,
                                  background: theme === 'dark'
                                    ? 'linear-gradient(135deg, rgba(220, 38, 38, 0.15), rgba(185, 28, 28, 0.1))'
                                    : 'linear-gradient(135deg, rgba(220, 38, 38, 0.08), rgba(185, 28, 28, 0.05))',
                                  border: theme === 'dark'
                                    ? '1px solid rgba(220, 38, 38, 0.3)'
                                    : '1px solid rgba(220, 38, 38, 0.2)',
                                  transition: 'all 0.3s ease'
                                }}>
                                  <div style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: theme === 'dark' ? '#fca5a5' : '#dc2626',
                                    marginBottom: 8,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>
                                    Perfis de Risco
                                  </div>
                                  <div style={{
                                    fontSize: 32,
                                    fontWeight: 700,
                                    color: theme === 'dark' ? '#f87171' : '#b91c1c',
                                    lineHeight: 1
                                  }}>
                                    {volumetriaIndicators.riskProfiles.toLocaleString()}
                                  </div>
                                </div>
                              )}
                              
                              {/* Perfis Mestre - Se derivação estiver habilitada */}
                              {showMasterDerived && (
                                <div style={{
                                  padding: 16,
                                  borderRadius: 12,
                                  background: theme === 'dark'
                                    ? 'linear-gradient(135deg, rgba(30, 64, 175, 0.15), rgba(30, 58, 138, 0.1))'
                                    : 'linear-gradient(135deg, rgba(30, 64, 175, 0.08), rgba(30, 58, 138, 0.05))',
                                  border: theme === 'dark'
                                    ? '1px solid rgba(30, 64, 175, 0.3)'
                                    : '1px solid rgba(30, 64, 175, 0.2)',
                                  transition: 'all 0.3s ease'
                                }}>
                                  <div style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: theme === 'dark' ? '#93bbfd' : '#1e40af',
                                    marginBottom: 8,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>
                                    Perfis Mestre
                                  </div>
                                  <div style={{
                                    fontSize: 32,
                                    fontWeight: 700,
                                    color: theme === 'dark' ? '#6090fa' : '#1e3a8a',
                                    lineHeight: 1
                                  }}>
                                    {volumetriaIndicators.masterProfiles.toLocaleString()}
                                  </div>
                                </div>
                              )}
                              
                              {/* Perfis Derivados - Se derivação estiver habilitada */}
                              {showMasterDerived && (
                                <div style={{
                                  padding: 16,
                                  borderRadius: 12,
                                  background: theme === 'dark'
                                    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(79, 70, 229, 0.1))'
                                    : 'linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(79, 70, 229, 0.05))',
                                  border: theme === 'dark'
                                    ? '1px solid rgba(99, 102, 241, 0.3)'
                                    : '1px solid rgba(99, 102, 241, 0.2)',
                                  transition: 'all 0.3s ease'
                                }}>
                                  <div style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: theme === 'dark' ? '#c4b5fd' : '#6366f1',
                                    marginBottom: 8,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>
                                    Perfis Derivados
                                  </div>
                                  <div style={{
                                    fontSize: 32,
                                    fontWeight: 700,
                                    color: theme === 'dark' ? '#a78bfa' : '#4f46e5',
                                    lineHeight: 1
                                  }}>
                                    {volumetriaIndicators.derivedProfiles.toLocaleString()}
                                  </div>
                                </div>
                              )}
                              </div>
                            </div>

                            <div style={{
                              marginTop: 24,
                              paddingTop: 24,
                              borderTop: theme === 'dark'
                                ? '1px solid rgba(255,255,255,0.12)'
                                : '1px solid rgba(0,0,0,0.08)'
                            }}>
                              <div style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: theme === 'dark' ? '#cbd5e1' : '#334155',
                                marginBottom: 12,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Tabela de Perfis e Volumetria
                              </div>

                              <div style={{
                                borderRadius: 12,
                                overflow: 'hidden',
                                border: theme === 'dark' ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)'
                              }}>
                                <DataTable
                                  columns={[
                                    {
                                      key: 'perfil',
                                      label: 'Perfil',
                                      minWidth: 360,
                                      sortable: true,
                                      render: (_: any, row: any) => (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                          <div style={{
                                            width: 30,
                                            height: 30,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            background: theme === 'dark' ? 'rgba(99, 102, 241, 0.22)' : 'rgba(99, 102, 241, 0.14)',
                                            border: theme === 'dark' ? '1px solid rgba(129, 140, 248, 0.45)' : '1px solid rgba(99, 102, 241, 0.28)'
                                          }}>
                                            <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                              <circle cx="8.5" cy="12" r="3.6" fill="none" stroke={theme === 'dark' ? '#c7d2fe' : '#4f46e5'} strokeWidth="1.8" />
                                              <circle cx="8.5" cy="12" r="1.2" fill={theme === 'dark' ? '#c7d2fe' : '#4f46e5'} />
                                              <path d="M12.3 12h8.2" fill="none" stroke={theme === 'dark' ? '#c7d2fe' : '#4f46e5'} strokeWidth="1.8" strokeLinecap="round" />
                                              <path d="M16.9 12v2.1M19.8 12v1.4" fill="none" stroke={theme === 'dark' ? '#c7d2fe' : '#4f46e5'} strokeWidth="1.8" strokeLinecap="round" />
                                            </svg>
                                          </div>
                                          <div>
                                            <div style={{ fontSize: 13, fontWeight: 600 }}>{row.perfil}</div>
                                            {row.detailsLine && <div style={{ fontSize: 11, fontWeight: 500, opacity: 0.78, marginTop: 2 }}>{row.detailsLine}</div>}
                                          </div>
                                        </div>
                                      )
                                    },
                                    {
                                      key: 'transactionsCount',
                                      label: 'Quantidade de transações associadas',
                                      minWidth: 220,
                                      sortable: true,
                                      render: (_: any, row: any) => <span style={{ fontSize: 13, fontWeight: 700, color: theme === 'dark' ? '#bfdbfe' : '#1d4ed8' }}>{row.transactionsCount.toLocaleString()}</span>
                                    },
                                    {
                                      key: 'usersCount',
                                      label: 'Quantidade de usuários associados',
                                      minWidth: 220,
                                      sortable: true,
                                      render: (_: any, row: any) => <span style={{ fontSize: 13, fontWeight: 700, color: theme === 'dark' ? '#bbf7d0' : '#047857' }}>{row.usersCount.toLocaleString()}</span>
                                    }
                                  ] as DataTableColumn<any>[]}
                                  data={profileVolumeRows}
                                  keyExtractor={(row: any, index: number) => `${row.perfil}-${index}`}
                                  sortColumn={volumetriaTableSortColumn || null}
                                  sortDirection={volumetriaTableSortDirection}
                                  onSort={(column: string, direction?: 'asc' | 'desc') => handleVolumetriaSort(column as 'perfil' | 'transactionsCount' | 'usersCount', direction)}
                                  striped
                                  hover
                                  bordered
                                  responsive
                                  size="sm"
                                  className="gpw-table"
                                  pagination
                                  clientSidePagination
                                  currentPage={volumetriaTablePage}
                                  pageSize={volumetriaTablePageSize}
                                  totalItems={profileVolumeRows.length}
                                  onPageChange={(page: number) => setVolumetriaTablePage(page)}
                                  onPageSizeChange={(size: number) => {
                                    setVolumetriaTablePageSize(size);
                                    localStorage.setItem('gpw_volumetria_pageSize', String(size));
                                    setVolumetriaTablePage(1);
                                  }}
                                  pageSizeOptions={[10, 25, 50, 100]}
                                  emptyMessage="Nenhum perfil encontrado para exibir."
                                />
                              </div>
                            </div>

                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Nova Seção: Análise de Atribuição Detalhada */}
                  {validationDataLoaded && (
                    <div style={{ 
                      marginTop: 40,
                      animation: 'fadeInUp 0.6s ease-out both',
                      overflow: 'visible'
                    }}>
                      <div style={{
                        background: theme === 'dark' 
                          ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.05))'
                          : 'linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(59, 130, 246, 0.02))',
                        borderRadius: 16,
                        padding: 32,
                        border: theme === 'dark' ? '1px solid rgba(139, 92, 246, 0.2)' : '1px solid rgba(139, 92, 246, 0.1)',
                        boxShadow: theme === 'dark' 
                          ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                          : '0 8px 32px rgba(139, 92, 246, 0.1)',
                        overflow: 'visible'
                      }}>
                        <h5 style={{ 
                          marginBottom: 24, 
                          fontSize: 20,
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12
                        }}>
                          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
                            <rect x="1" y="1" width="14" height="14" rx="2" fill="none" stroke="#8b5cf6" strokeWidth="1.2" />
                            <rect x="4" y="8" width="2" height="4" rx="0.5" fill="#8b5cf6" />
                            <rect x="7" y="6" width="2" height="6" rx="0.5" fill="#8b5cf6" />
                            <rect x="10" y="4" width="2" height="8" rx="0.5" fill="#8b5cf6" />
                          </svg>
                          Análise de Atribuição Detalhada
                        </h5>

                        {/* Filtro de Usuário */}
                        <div style={{ 
                          marginBottom: 24,
                          padding: 20,
                          borderRadius: 12,
                          background: theme === 'dark' 
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(255, 255, 255, 0.8)',
                          border: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                          overflow: 'visible'
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                          <div style={{ flex: 1 , overflow: 'visible' }}>
                            <label className="form-label" style={{ 
                              fontSize: 12, 
                              fontWeight: 600, 
                              color: theme === 'dark' ? '#94a3b8' : '#64748b',
                              marginBottom: 8 
                            }}>
                              � Selecione usuários para análise detalhada (múltipla seleção)
                            </label>
                            <div style={{ position: 'relative' }}>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Clique e digite para filtrar..."
                                value={userSearchFilter}
                                onChange={e => {
                                  setUserSearchFilter(e.target.value);
                                  setUserDropdownOpen(true);
                                }}
                                onFocus={() => {
                                  setUserDropdownOpen(true);
                                }}
                                onBlur={() => {
                                  // Delay para permitir clique nos itens
                                  setTimeout(() => setUserDropdownOpen(false), 200);
                                }}
                                style={{ borderRadius: 8 }}
                              />
                              {userDropdownOpen && (() => {
                                const users = new Set<string>();
                                validationRows.forEach(r => {
                                  if (r.usuario && r.usuario !== '-') {
                                    users.add(r.usuario);
                                  }
                                });
                                
                                const filteredUsers = Array.from(users)
                                  .sort()
                                  .filter(user => user.toLowerCase().includes(userSearchFilter.toLowerCase()))
                                  .slice(0, 10); // Limitar a 10 usuários
                                
                                if (filteredUsers.length === 0) {
                                  return (
                                    <div style={{
                                      position: 'absolute',
                                      top: '100%',
                                      left: 0,
                                      right: 0,
                                      background: theme === 'dark' ? '#1e293b' : '#ffffff',
                                      border: `1px solid ${theme === 'dark' ? '#475569' : '#e5e7eb'}`,
                                      borderRadius: 8,
                                      marginTop: 4,
                                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                      zIndex: 1000,
                                      padding: 12,
                                      color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                      fontSize: 13,
                                      textAlign: 'center'
                                    }}>
                                      Nenhum usuário encontrado
                                    </div>
                                  );
                                }
                                
                                return (
                                  <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    maxHeight: 320,
                                    overflowY: 'auto',
                                    background: theme === 'dark' ? '#1e293b' : '#ffffff',
                                    border: `1px solid ${theme === 'dark' ? '#475569' : '#e5e7eb'}`,
                                    borderRadius: 8,
                                    marginTop: 4,
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    zIndex: 1000
                                  }}>
                                    {filteredUsers.map(user => {
                                      const isSelected = detailedAnalysisUsers.includes(user);
                                      return (
                                        <div
                                          key={user}
                                          onMouseDown={(e) => {
                                            e.preventDefault(); // Previne blur do input
                                            if (isSelected) {
                                              // Remove usuário
                                              setDetailedAnalysisUsers(prev => prev.filter(u => u !== user));
                                            } else {
                                              // Adiciona usuário
                                              setDetailedAnalysisUsers(prev => [...prev, user]);
                                            }
                                            setUserSearchFilter('');
                                          }}
                                          style={{
                                            padding: '10px 16px',
                                            cursor: 'pointer',
                                            fontSize: 13,
                                            color: theme === 'dark' ? '#f1f5f9' : '#1e293b',
                                            borderBottom: `1px solid ${theme === 'dark' ? '#334155' : '#f1f5f9'}`,
                                            transition: 'background 0.2s ease',
                                            background: isSelected 
                                              ? theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)'
                                              : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8
                                          }}
                                          onMouseEnter={e => {
                                            if (!isSelected) {
                                              e.currentTarget.style.background = theme === 'dark' ? '#334155' : '#f8fafc';
                                            }
                                          }}
                                          onMouseLeave={e => {
                                            e.currentTarget.style.background = isSelected
                                              ? theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)'
                                              : 'transparent';
                                          }}
                                        >
                                          <span style={{ fontSize: 16 }}>
                                            {isSelected ? '✓' : '○'}
                                          </span>
                                          <span>👤 {user}</span>
                                        </div>
                                      );
                                    })}
                                    {filteredUsers.length === 10 && (
                                      <div style={{
                                        padding: '8px 16px',
                                        fontSize: 11,
                                        color: theme === 'dark' ? '#64748b' : '#94a3b8',
                                        textAlign: 'center',
                                        fontStyle: 'italic'
                                      }}>
                                        Continue digitando para refinar...
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {detailedAnalysisUsers.length > 0 && (
                                <div style={{
                                  marginTop: 8,
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 6
                                }}>
                                  {detailedAnalysisUsers.map(user => (
                                    <div
                                      key={user}
                                      style={{
                                        padding: '6px 12px',
                                        background: theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                                        borderRadius: 6,
                                        border: `1px solid ${theme === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
                                        fontSize: 12,
                                        color: theme === 'dark' ? '#93c5fd' : '#2563eb',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8
                                      }}
                                    >
                                      <span>👤 {user}</span>
                                      <button
                                        onClick={() => {
                                          setDetailedAnalysisUsers(prev => prev.filter(u => u !== user));
                                        }}
                                        style={{
                                          background: 'transparent',
                                          border: 'none',
                                          color: theme === 'dark' ? '#93c5fd' : '#2563eb',
                                          cursor: 'pointer',
                                          padding: 0,
                                          fontSize: 14,
                                          fontWeight: 'bold',
                                          lineHeight: 1
                                        }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          {detailedAnalysisUsers.length > 0 && (
                            <>
                              <div style={{ 
                                marginTop: 16,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: 12,
                                background: theme === 'dark' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)',
                                borderRadius: 8,
                                border: `1px solid ${theme === 'dark' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.15)'}`
                              }}>
                                <label style={{ 
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: theme === 'dark' ? '#c4b5fd' : '#7c3aed',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  flex: 1
                                }}>
                                  📋 Exibir detalhes dos perfis
                                </label>
                                <div 
                                  onClick={() => setShowProfileDetails(!showProfileDetails)}
                                  style={{
                                    width: 44,
                                    height: 24,
                                    borderRadius: 12,
                                    background: showProfileDetails 
                                      ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)' 
                                      : theme === 'dark' ? '#374151' : '#d1d5db',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    transition: 'background 0.3s ease',
                                    boxShadow: showProfileDetails 
                                      ? '0 2px 8px rgba(139, 92, 246, 0.4)' 
                                      : '0 2px 4px rgba(0, 0, 0, 0.1)'
                                  }}
                                >
                                  <div style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    background: '#ffffff',
                                    position: 'absolute',
                                    top: 2,
                                    left: showProfileDetails ? 22 : 2,
                                    transition: 'left 0.3s ease',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                                  }} />
                                </div>
                              </div>
                                <div style={{
                                  marginTop: 12,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  padding: 12,
                                  background: theme === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(241, 245, 249, 0.8)',
                                  borderRadius: 8,
                                  border: `1px solid ${theme === 'dark' ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.3)'}`
                                }}>
                                  <label style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: theme === 'dark' ? '#cbd5f5' : '#475569',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    flex: 1
                                  }}>
                                    Visualizacao dos perfis
                                  </label>
                                  <div className="btn-group btn-group-sm" role="group">
                                    <button
                                      type="button"
                                      className={`btn ${detailedProfilesView === 'detailed' ? 'btn-primary' : 'btn-outline-primary'}`}
                                      onClick={() => setDetailedProfilesView('detailed')}
                                    >
                                      Detalhada
                                    </button>
                                    <button
                                      type="button"
                                      className={`btn ${detailedProfilesView === 'clean' ? 'btn-primary' : 'btn-outline-primary'}`}
                                      onClick={() => setDetailedProfilesView('clean')}
                                    >
                                      Clean
                                    </button>
                                  </div>
                                </div>
                              <button 
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => {
                                  setDetailedAnalysisUsers([]);
                                  setUserSearchFilter('');
                                  setExpandedProfiles(new Set());
                                  setExpandedCompositeProfiles(new Set());
                                }}
                                style={{ marginTop: 12, borderRadius: 8 }}
                              >
                                Limpar Todos
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Visualização Hierárquica - Diagrama de Fluxo */}
                      {detailedAnalysisUsers.length > 0 && (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 40
                        }}>
                          {detailedAnalysisUsers.map((detailedAnalysisUser, userIndex) => {
                            const userRows = validationRows.filter(r => r.usuario === detailedAnalysisUser);
                            const isCompositeModel = userRows.some(r => r.perfil_composto && r.perfil_composto !== '-' && r.perfil_composto !== 'DIRECT_ATTRIBUTION');
                            
                            if (userRows.length === 0) {
                              return (
                                <div key={detailedAnalysisUser} className="card p-4 text-center" style={{ 
                                  borderRadius: 12,
                                  background: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc'
                                }}>
                                  <div style={{ color: theme === 'dark' ? '#94a3b8' : '#64748b', fontSize: 14 }}>
                                    Nenhum dado encontrado para {detailedAnalysisUser}
                                  </div>
                                </div>
                              );
                            }
                            
                            return (
                              <div key={detailedAnalysisUser}>
                                {/* Cabeçalho do usuário */}
                                <div style={{
                                  marginTop: userIndex > 0 ? 48 : 0,
                                  marginBottom: 24,
                                  padding: '16px 24px',
                                  borderRadius: 12,
                                  background: theme === 'dark'
                                    ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(59, 130, 246, 0.1))'
                                    : 'linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(59, 130, 246, 0.05))',
                                  border: theme === 'dark' 
                                    ? '1px solid rgba(139, 92, 246, 0.25)'
                                    : '1px solid rgba(139, 92, 246, 0.15)',
                                  boxShadow: theme === 'dark'
                                    ? '0 4px 12px rgba(139, 92, 246, 0.15)'
                                    : '0 4px 12px rgba(139, 92, 246, 0.08)',
                                  animation: `fadeInUp 0.4s ease-out ${userIndex * 0.1}s both`
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12
                                  }}>
                                    <div style={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: '50%',
                                      background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: 20,
                                      boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
                                    }}>
                                      👤
                                    </div>
                                    <div>
                                      <div style={{
                                        fontSize: 18,
                                        fontWeight: 700,
                                        color: theme === 'dark' ? '#e9d5ff' : '#6b21a8',
                                        marginBottom: 2
                                      }}>
                                        {detailedAnalysisUser}
                                      </div>
                                      <div style={{
                                        fontSize: 12,
                                        color: theme === 'dark' ? '#c4b5fd' : '#7c3aed',
                                        fontWeight: 500
                                      }}>
                                        Usuário {userIndex + 1} de {detailedAnalysisUsers.length}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div>
                                {(() => {

                        // Modelo com Perfil Composto
                        if (isCompositeModel) {
                          // Separate direct attribution profiles from composite profiles
                          const compositeProfiles = new Map<string, any[]>();
                          const directProfiles = new Map<string, any[]>();
                          
                          userRows.forEach(row => {
                            const composite = row.perfil_composto || 'DIRECT_ATTRIBUTION';
                            if (composite === 'DIRECT_ATTRIBUTION') {
                              // Direct attribution - group by profile name
                              const profileName = row.perfil || '-';
                              if (!directProfiles.has(profileName)) {
                                directProfiles.set(profileName, []);
                              }
                              directProfiles.get(profileName)!.push(row);
                            } else {
                              // Regular composite profile
                              if (!compositeProfiles.has(composite)) {
                                compositeProfiles.set(composite, []);
                              }
                              compositeProfiles.get(composite)!.push(row);
                            }
                          });

                          if (detailedProfilesView === 'clean') {
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {Array.from(compositeProfiles.entries()).map(([compositeName, rows]) => {
                                  const profilesInComposite = new Map<string, any[]>();
                                  rows.forEach(row => {
                                    const profile = row.perfil || '-';
                                    if (!profilesInComposite.has(profile)) {
                                      profilesInComposite.set(profile, []);
                                    }
                                    profilesInComposite.get(profile)!.push(row);
                                  });

                                  return (
                                    <div key={`clean:${compositeName}`} style={{
                                      borderRadius: 10,
                                      padding: 14,
                                      background: theme === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                                      border: `1px solid ${theme === 'dark' ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.25)'}`,
                                      boxShadow: theme === 'dark' ? '0 6px 16px rgba(0,0,0,0.25)' : '0 6px 16px rgba(148,163,184,0.15)'
                                    }}>
                                      <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        marginBottom: 10
                                      }}>
                                        <span style={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          padding: '2px 6px',
                                          borderRadius: 4,
                                          background: theme === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)',
                                          color: theme === 'dark' ? '#a5b4fc' : '#4f46e5',
                                          letterSpacing: '0.5px'
                                        }}>
                                          COMPOSTO
                                        </span>
                                        <div style={{ fontWeight: 700, fontSize: 13, color: theme === 'dark' ? '#e2e8f0' : '#0f172a' }}>
                                          {compositeName}
                                        </div>
                                        <div style={{ marginLeft: 'auto', fontSize: 11, color: theme === 'dark' ? '#94a3b8' : '#64748b' }}>
                                          {profilesInComposite.size} perfis · {rows.length} transacoes
                                        </div>
                                      </div>
                                      <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                        gap: 10,
                                        alignItems: 'start'
                                      }}>
                                        {Array.from(profilesInComposite.entries()).map(([profileName, profileRows]) => {
                                          const typeInfo = profileTypesMap.get(profileName.toUpperCase());
                                          const chipKey = `clean:${compositeName}:${profileName}`;
                                          const isExpanded = expandedProfiles.has(chipKey);
                                          let chipBg = 'linear-gradient(135deg, #16a34a, #15803d)';
                                          if (typeInfo?.risk) chipBg = 'linear-gradient(135deg, #dc2626, #b91c1c)';
                                          else if (typeInfo?.derived) chipBg = 'linear-gradient(135deg, #3b82f6, #2563eb)';
                                          else if (typeInfo?.master) chipBg = 'linear-gradient(135deg, #1e40af, #1e3a8a)';

                                          return (
                                            <div key={chipKey} style={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              borderRadius: 10,
                                              overflow: 'hidden',
                                              background: theme === 'dark' ? 'rgba(15, 23, 42, 0.75)' : 'rgba(248, 250, 252, 0.95)',
                                              border: `1px solid ${theme === 'dark' ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.35)'}`,
                                              boxShadow: theme === 'dark' ? '0 4px 10px rgba(2,6,23,0.35)' : '0 4px 10px rgba(148,163,184,0.2)'
                                            }}>
                                              <div
                                                onClick={() => {
                                                  const newSet = new Set(expandedProfiles);
                                                  if (isExpanded) newSet.delete(chipKey);
                                                  else newSet.add(chipKey);
                                                  setExpandedProfiles(newSet);
                                                }}
                                                style={{
                                                  padding: '9px 11px',
                                                  background: chipBg,
                                                  color: '#ffffff',
                                                  fontSize: 11,
                                                  fontWeight: 700,
                                                  cursor: 'pointer',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 8,
                                                  justifyContent: 'space-between'
                                                }}
                                              >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ffffff', flexShrink: 0 }} />
                                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profileName}</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                                  <span style={{ opacity: 0.9, fontSize: 10 }}>{profileRows.length}</span>
                                                  <span style={{
                                                    fontSize: 10,
                                                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.2s ease'
                                                  }}>▶</span>
                                                </div>
                                              </div>
                                              {showProfileDetails && (
                                                <div style={{
                                                  padding: '8px 10px',
                                                  fontSize: 9,
                                                  color: theme === 'dark' ? '#cbd5e1' : '#475569',
                                                  background: theme === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(241,245,249,0.6)',
                                                  borderBottom: `1px solid ${theme === 'dark' ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.15)'}`,
                                                  lineHeight: 1.5
                                                }}>
                                                  {(() => {
                                                    const firstRow = profileRows[0];
                                                    const isPositionSimple = String(client?.profileModel) === 'Posição/Cargo Simples';
                                                    if (isPositionSimple) {
                                                      const n1 = firstRow?.n1 || '-';
                                                      const n2 = firstRow?.n2 || '-';
                                                      const n3 = firstRow?.n3 || '-';
                                                      const n4 = firstRow?.n4 || '-';
                                                      return (
                                                        <>
                                                          <div><strong>N1:</strong> {n1}</div>
                                                          <div><strong>N2:</strong> {n2}</div>
                                                          <div><strong>N3:</strong> {n3}</div>
                                                          <div><strong>N4:</strong> {n4}</div>
                                                        </>
                                                      );
                                                    } else {
                                                      const area = firstRow?.area_perfil || firstRow?.area || '-';
                                                      const processo = firstRow?.processo || '-';
                                                      const subprocesso = firstRow?.subprocesso || '-';
                                                      const atividade = firstRow?.atividade || '-';
                                                      return (
                                                        <>
                                                          <div><strong>Área:</strong> {area}</div>
                                                          <div><strong>Proc:</strong> {processo}</div>
                                                          <div><strong>Sub:</strong> {subprocesso}</div>
                                                          <div><strong>Ativ:</strong> {atividade}</div>
                                                        </>
                                                      );
                                                    }
                                                  })()}
                                                </div>
                                              )}
                                              {isExpanded && (
                                                <div style={{
                                                  display: 'grid',
                                                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                                  gap: 6,
                                                  padding: 10,
                                                  maxHeight: 180,
                                                  overflowY: 'auto'
                                                }}>
                                                  {profileRows.map((row: any, idx: number) => (
                                                    <span key={`${chipKey}:${idx}`} style={{
                                                      padding: '4px 8px',
                                                      borderRadius: 6,
                                                      fontSize: 10,
                                                      background: theme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(226, 232, 240, 0.9)',
                                                      color: theme === 'dark' ? '#e2e8f0' : '#334155'
                                                    }}>
                                                      {row.codigo || '-'}
                                                    </span>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}

                                {directProfiles.size > 0 && (
                                  <div style={{
                                    borderRadius: 10,
                                    padding: 14,
                                    background: theme === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                                    border: `1px solid ${theme === 'dark' ? 'rgba(248, 113, 113, 0.25)' : 'rgba(248, 113, 113, 0.25)'}`,
                                    boxShadow: theme === 'dark' ? '0 6px 16px rgba(0,0,0,0.25)' : '0 6px 16px rgba(148,163,184,0.15)'
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 10,
                                      marginBottom: 10
                                    }}>
                                      <span style={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        padding: '2px 6px',
                                        borderRadius: 4,
                                        background: theme === 'dark' ? 'rgba(248, 113, 113, 0.2)' : 'rgba(248, 113, 113, 0.1)',
                                        color: theme === 'dark' ? '#fecaca' : '#dc2626',
                                        letterSpacing: '0.5px'
                                      }}>
                                        DIRETO
                                      </span>
                                      <div style={{ fontWeight: 700, fontSize: 13, color: theme === 'dark' ? '#e2e8f0' : '#0f172a' }}>
                                        Atribuicao Direta
                                      </div>
                                      <div style={{ marginLeft: 'auto', fontSize: 11, color: theme === 'dark' ? '#94a3b8' : '#64748b' }}>
                                        {directProfiles.size} perfis
                                      </div>
                                    </div>
                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                      gap: 10,
                                      alignItems: 'start'
                                    }}>
                                      {Array.from(directProfiles.entries()).map(([profileName, profileRows]) => {
                                        const typeInfo = profileTypesMap.get(profileName.toUpperCase());
                                        const chipKey = `clean:direct:${profileName}`;
                                        const isExpanded = expandedProfiles.has(chipKey);
                                        let chipBg = 'linear-gradient(135deg, #16a34a, #15803d)';
                                        if (typeInfo?.risk) chipBg = 'linear-gradient(135deg, #dc2626, #b91c1c)';
                                        else if (typeInfo?.derived) chipBg = 'linear-gradient(135deg, #3b82f6, #2563eb)';
                                        else if (typeInfo?.master) chipBg = 'linear-gradient(135deg, #1e40af, #1e3a8a)';
                                        
                                        // Debug logging for risk profiles
                                        if (typeInfo?.risk) {
                                          console.log(`[Clean Direct Attribution] Risk profile found: ${profileName}`, typeInfo);
                                        }

                                        return (
                                          <div key={chipKey} style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRadius: 10,
                                            overflow: 'hidden',
                                            background: theme === 'dark' ? 'rgba(15, 23, 42, 0.75)' : 'rgba(248, 250, 252, 0.95)',
                                            border: `1px solid ${theme === 'dark' ? (typeInfo?.risk ? 'rgba(220, 38, 38, 0.35)' : 'rgba(148, 163, 184, 0.25)') : (typeInfo?.risk ? 'rgba(220, 38, 38, 0.4)' : 'rgba(148, 163, 184, 0.35)')}`,
                                            boxShadow: theme === 'dark' ? '0 4px 10px rgba(2,6,23,0.35)' : '0 4px 10px rgba(148,163,184,0.2)'
                                          }}>
                                            <div
                                              onClick={() => {
                                                const newSet = new Set(expandedProfiles);
                                                if (isExpanded) newSet.delete(chipKey);
                                                else newSet.add(chipKey);
                                                setExpandedProfiles(newSet);
                                              }}
                                              style={{
                                                padding: '9px 11px',
                                                background: chipBg,
                                                color: '#ffffff',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                justifyContent: 'space-between'
                                              }}
                                            >
                                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ffffff', flexShrink: 0 }} />
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profileName}</span>
                                              </div>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                                <span style={{ opacity: 0.9, fontSize: 10 }}>{profileRows.length}</span>
                                                <span style={{
                                                  fontSize: 10,
                                                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                  transition: 'transform 0.2s ease'
                                                }}>▶</span>
                                              </div>
                                            </div>
                                            {showProfileDetails && (
                                              <div style={{
                                                padding: '8px 10px',
                                                fontSize: 9,
                                                color: theme === 'dark' ? '#cbd5e1' : '#475569',
                                                background: theme === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(241,245,249,0.6)',
                                                borderBottom: `1px solid ${theme === 'dark' ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.15)'}`,
                                                lineHeight: 1.5
                                              }}>
                                                {(() => {
                                                  const firstRow = profileRows[0];
                                                  const isPositionSimple = String(client?.profileModel) === 'Posição/Cargo Simples';
                                                  if (isPositionSimple) {
                                                    const n1 = firstRow?.n1 || '-';
                                                    const n2 = firstRow?.n2 || '-';
                                                    const n3 = firstRow?.n3 || '-';
                                                    const n4 = firstRow?.n4 || '-';
                                                    return (
                                                      <>
                                                        <div><strong>N1:</strong> {n1}</div>
                                                        <div><strong>N2:</strong> {n2}</div>
                                                        <div><strong>N3:</strong> {n3}</div>
                                                        <div><strong>N4:</strong> {n4}</div>
                                                        <div style={{ marginTop: 4, fontSize: 8, opacity: 0.7 }}>⚡ Direto</div>
                                                      </>
                                                    );
                                                  } else {
                                                    const area = firstRow?.area_perfil || firstRow?.area || '-';
                                                    const processo = firstRow?.processo || '-';
                                                    const subprocesso = firstRow?.subprocesso || '-';
                                                    const atividade = firstRow?.atividade || '-';
                                                    return (
                                                      <>
                                                        <div><strong>Área:</strong> {area}</div>
                                                        <div><strong>Proc:</strong> {processo}</div>
                                                        <div><strong>Sub:</strong> {subprocesso}</div>
                                                        <div><strong>Ativ:</strong> {atividade}</div>
                                                        <div style={{ marginTop: 4, fontSize: 8, opacity: 0.7 }}>⚡ Direto</div>
                                                      </>
                                                    );
                                                  }
                                                })()}
                                              </div>
                                            )}
                                            {isExpanded && (
                                              <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                                gap: 6,
                                                padding: 10,
                                                maxHeight: 180,
                                                overflowY: 'auto'
                                              }}>
                                                {profileRows.map((row: any, idx: number) => (
                                                  <span key={`${chipKey}:${idx}`} style={{
                                                    padding: '4px 8px',
                                                    borderRadius: 6,
                                                    fontSize: 10,
                                                    background: theme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(226, 232, 240, 0.9)',
                                                    color: theme === 'dark' ? '#e2e8f0' : '#334155'
                                                  }}>
                                                    {row.codigo || '-'}
                                                  </span>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }

                          return (
                            <div style={{ 
                              display: 'flex', 
                              flexDirection: 'row', 
                              gap: 40, 
                              alignItems: 'center',
                              overflowX: 'visible',
                              overflowY: 'visible',
                              padding: '20px 0',
                              paddingTop: '80px',
                              marginTop: '-60px'
                            }}>
                              {/* Usuário (Coluna 1) */}
                              <div style={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                minWidth: 150,
                                flexShrink: 0
                              }}>
                                <div style={{
                                  width: 100,
                                  height: 100,
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 40,
                                  color: '#ffffff',
                                  boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)',
                                  marginBottom: 12
                                }}>
                                  👤
                                </div>
                                <div style={{
                                  fontSize: 14,
                                  fontWeight: 700,
                                  color: theme === 'dark' ? '#e9d5ff' : '#6b21a8',
                                  textAlign: 'center',
                                  maxWidth: 150,
                                  wordBreak: 'break-word'
                                }}>
                                  {detailedAnalysisUser}
                                </div>
                                <div style={{
                                  fontSize: 11,
                                  color: theme === 'dark' ? '#c4b5fd' : '#7c3aed',
                                  marginTop: 4
                                }}>
                                  Usuário
                                </div>
                              </div>

                              {/* Conector Flowchart Style - Usuário para Perfis */}
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                position: 'relative',
                                margin: '0 10px',
                                flexShrink: 0
                              }}>
                                <div style={{
                                  width: '40px',
                                  height: '2px',
                                  background: theme === 'dark' ? '#64748b' : '#94a3b8',
                                  position: 'relative'
                                }}>
                                  {/* Seta */}
                                  <div style={{
                                    position: 'absolute',
                                    right: '-6px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: 0,
                                    height: 0,
                                    borderLeft: `6px solid ${theme === 'dark' ? '#64748b' : '#94a3b8'}`,
                                    borderTop: '5px solid transparent',
                                    borderBottom: '5px solid transparent'
                                  }} />
                                </div>
                              </div>

                              {/* Container para Perfis Compostos E Diretos (Coluna 2) */}
                              <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: 20,
                                minWidth: 300
                              }}>
                              {/* Perfis Compostos */}
                              {compositeProfiles.size > 0 && (
                                <div style={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  gap: 20
                                }}>
                              {Array.from(compositeProfiles.entries()).map(([compositeName, rows], compIdx) => {
                                const isExpanded = expandedCompositeProfiles.has(compositeName);
                                const profilesInComposite = new Map<string, any[]>();
                                
                                rows.forEach(row => {
                                  const profile = row.perfil || '-';
                                  if (!profilesInComposite.has(profile)) {
                                    profilesInComposite.set(profile, []);
                                  }
                                  profilesInComposite.get(profile)!.push(row);
                                });

                                return (
                                  <div key={compositeName} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                                    {/* Card Perfil Composto */}
                                    <div
                                      onClick={() => {
                                        const newSet = new Set(expandedCompositeProfiles);
                                        if (isExpanded) {
                                          newSet.delete(compositeName);
                                        } else {
                                          newSet.add(compositeName);
                                        }
                                        setExpandedCompositeProfiles(newSet);
                                      }}
                                      style={{ 
                                        minWidth: 280,
                                        padding: 16,
                                        borderRadius: 10,
                                        background: 'linear-gradient(135deg, #5b21b6, #6d28d9)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        transition: 'all 0.2s ease',
                                        userSelect: 'none',
                                        boxShadow: '0 4px 12px rgba(91, 33, 182, 0.3)',
                                        animation: `fadeIn 0.3s ease-out ${compIdx * 0.1}s both`,
                                        position: 'relative'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(91, 33, 182, 0.4)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(91, 33, 182, 0.3)';
                                      }}
                                    >
                                      <span style={{ 
                                        fontSize: 16, 
                                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
                                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                        color: '#ffffff',
                                        display: 'inline-block'
                                      }}>
                                        ▶
                                      </span>
                                      <div style={{ flex: 1 }}>
                                        <div style={{ 
                                          fontSize: 14, 
                                          fontWeight: 700, 
                                          color: '#ffffff',
                                          marginBottom: 6,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 8
                                        }}>
                                          <div style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            background: '#ffffff',
                                            flexShrink: 0
                                          }} />
                                          {compositeName}
                                        </div>
                                        <div style={{ 
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 8,
                                          marginBottom: 6
                                        }}>
                                          <span style={{
                                            fontSize: 8,
                                            fontWeight: 700,
                                            padding: '2px 6px',
                                            borderRadius: 4,
                                            background: 'rgba(255, 255, 255, 0.25)',
                                            color: '#ffffff',
                                            letterSpacing: '0.5px',
                                            whiteSpace: 'nowrap'
                                          }}>
                                            COMPOSTO
                                          </span>
                                        </div>
                                        <div style={{ 
                                          fontSize: 10, 
                                          color: 'rgba(255, 255, 255, 0.7)'
                                        }}>
                                          {profilesInComposite.size} perfis · {rows.length} transações
                                        </div>
                                        {/* Níveis Organizacionais */}
                                        {showProfileDetails && (
                                          <div style={{
                                            marginTop: 10,
                                            paddingTop: 10,
                                            borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                                            fontSize: 9,
                                            color: 'rgba(255, 255, 255, 0.85)',
                                            lineHeight: 1.6
                                          }}>
                                            {(() => {
                                              const firstRow = rows[0];
                                              const n1 = firstRow?.n1 || '-';
                                              const n2 = firstRow?.n2 || '-';
                                              const n3 = firstRow?.n3 || '-';
                                              const n4 = firstRow?.n4 || '-';
                                              return (
                                                <>
                                                  <div><strong>N1:</strong> {n1}</div>
                                                  <div><strong>N2:</strong> {n2}</div>
                                                  <div><strong>N3:</strong> {n3}</div>
                                                  <div><strong>N4:</strong> {n4}</div>
                                                </>
                                              );
                                            })()}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Seta e Perfis */}
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 20,
                                      maxHeight: isExpanded ? '2000px' : '0',
                                      opacity: isExpanded ? 1 : 0,
                                      overflow: 'hidden',
                                      transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                      visibility: isExpanded ? 'visible' : 'hidden'
                                    }}>
                                      <>
                                        {/* Conector Flowchart Style - Composto para Simples */}
                                        <div style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          position: 'relative',
                                          margin: '0 8px',
                                          flexShrink: 0
                                        }}>
                                          <div style={{
                                            width: '35px',
                                            height: '2px',
                                            background: theme === 'dark' ? '#64748b' : '#94a3b8',
                                            position: 'relative'
                                          }}>
                                            {/* Seta */}
                                            <div style={{
                                              position: 'absolute',
                                              right: '-6px',
                                              top: '50%',
                                              transform: 'translateY(-50%)',
                                              width: 0,
                                              height: 0,
                                              borderLeft: `6px solid ${theme === 'dark' ? '#64748b' : '#94a3b8'}`,
                                              borderTop: '5px solid transparent',
                                              borderBottom: '5px solid transparent'
                                            }} />
                                          </div>
                                        </div>
                                        <div style={{ 
                                          display: 'grid',
                                          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                                          gap: 12,
                                          width: '100%',
                                          minWidth: 0
                                        }}>
                                        {Array.from(profilesInComposite.entries())
                                          .sort(([nameA], [nameB]) => {
                                            // Get profile type info
                                            const typeA = profileTypesMap.get(nameA.toUpperCase());
                                            const typeB = profileTypesMap.get(nameB.toUpperCase());
                                            
                                            // Determine type priority: Simples(0) > Derivado(1) > Mestre(2) > Risco(3)
                                            const getPriority = (type: any) => {
                                              if (!type) return 0; // Pure simple
                                              if (!type.master && !type.derived && !type.risk) return 0; // Simple
                                              if (type.derived) return 1; // Derived
                                              if (type.master) return 2; // Master
                                              if (type.risk) return 3; // Risk
                                              return 0;
                                            };
                                            
                                            const priorityA = getPriority(typeA);
                                            const priorityB = getPriority(typeB);
                                            
                                            // First sort by type priority
                                            if (priorityA !== priorityB) {
                                              return priorityA - priorityB;
                                            }
                                            
                                            // Then sort alphabetically (A-Z)
                                            return nameA.localeCompare(nameB);
                                          })
                                          .map(([profileName, profileRows], profIdx) => {
                                          const profileKey = `${compositeName}:${profileName}`;
                                          const isProfileExpanded = expandedProfiles.has(profileKey);

                                          return (
                                            <div key={profileKey} style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
                                              {/* Card Perfil */}
                                              <div
                                                onClick={() => {
                                                  const newSet = new Set(expandedProfiles);
                                                  if (isProfileExpanded) {
                                                    newSet.delete(profileKey);
                                                  } else {
                                                    newSet.add(profileKey);
                                                  }
                                                  setExpandedProfiles(newSet);
                                                }}
                                                style={(() => {
                                                  // Determine color based on profile type
                                                  const profileKey = profileName.toUpperCase();
                                                  const profileTypeInfo = profileTypesMap.get(profileKey);
                                                  
                                                  let bgGradient = 'linear-gradient(135deg, #16a34a, #15803d)'; // Verde (Simples)
                                                  let shadowColor = 'rgba(22, 163, 74, 0.3)';
                                                  let shadowColorHover = 'rgba(22, 163, 74, 0.4)';
                                                  
                                                  if (profileTypeInfo?.risk) {
                                                    bgGradient = 'linear-gradient(135deg, #dc2626, #b91c1c)'; // Vermelho (Risco)
                                                    shadowColor = 'rgba(220, 38, 38, 0.3)';
                                                    shadowColorHover = 'rgba(220, 38, 38, 0.4)';
                                                  } else if (profileTypeInfo?.derived) {
                                                    bgGradient = 'linear-gradient(135deg, #3b82f6, #2563eb)'; // Azul claro (Derivado)
                                                    shadowColor = 'rgba(59, 130, 246, 0.3)';
                                                    shadowColorHover = 'rgba(59, 130, 246, 0.4)';
                                                  } else if (profileTypeInfo?.master) {
                                                    bgGradient = 'linear-gradient(135deg, #1e40af, #1e3a8a)'; // Azul escuro (Mestre)
                                                    shadowColor = 'rgba(30, 64, 175, 0.3)';
                                                    shadowColorHover = 'rgba(30, 64, 175, 0.4)';
                                                  }
                                                  
                                                  return {
                                                    width: '100%',
                                                    minWidth: 0,
                                                    padding: 14,
                                                    borderRadius: 8,
                                                    background: bgGradient,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 10,
                                                    transition: 'all 0.2s ease',
                                                    userSelect: 'none',
                                                    boxShadow: `0 2px 8px ${shadowColor}`,
                                                    animation: `fadeIn 0.3s ease-out ${(compIdx * 0.1) + (profIdx * 0.05)}s both`,
                                                    position: 'relative',
                                                    '--shadow-hover': shadowColorHover
                                                  } as any;
                                                })()}
                                                onMouseEnter={(e) => {
                                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                                  const hoverShadow = (e.currentTarget.style as any)['--shadow-hover'] || 'rgba(22, 163, 74, 0.4)';
                                                  e.currentTarget.style.boxShadow = `0 4px 12px ${hoverShadow}`;
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.currentTarget.style.transform = 'translateY(0)';
                                                  const profileKey = profileName.toUpperCase();
                                                  const profileTypeInfo = profileTypesMap.get(profileKey);
                                                  let shadowColor = 'rgba(22, 163, 74, 0.3)';
                                                  if (profileTypeInfo?.risk) shadowColor = 'rgba(220, 38, 38, 0.3)';
                                                  else if (profileTypeInfo?.derived) shadowColor = 'rgba(59, 130, 246, 0.3)';
                                                  else if (profileTypeInfo?.master) shadowColor = 'rgba(30, 64, 175, 0.3)';
                                                  e.currentTarget.style.boxShadow = `0 2px 8px ${shadowColor}`;
                                                }}
                                              >
                                                {/* Perfil Header */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                  <span style={{ 
                                                    fontSize: 14, 
                                                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
                                                    transform: isProfileExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                    color: '#ffffff',
                                                    display: 'inline-block'
                                                  }}>
                                                    ▶
                                                  </span>
                                                  <div style={{ flex: 1 }}>
                                                    <div style={{ 
                                                      fontSize: 14, 
                                                      fontWeight: 700, 
                                                      color: '#ffffff',
                                                      marginBottom: 6,
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 8
                                                    }}>
                                                      <div style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        background: '#ffffff',
                                                        flexShrink: 0
                                                      }} />
                                                      {profileName}
                                                    </div>
                                                    <div style={{ 
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 4,
                                                      marginBottom: 6,
                                                      flexWrap: 'wrap'
                                                    }}>
                                                      {(() => {
                                                        // Detect profile type from consolidation data - show relevant labels only
                                                        const profileKey = profileName.toUpperCase();
                                                        const profileTypeInfo = profileTypesMap.get(profileKey);
                                                        
                                                        const labels: Array<{text: string, color: string}> = [];
                                                        
                                                        const hasMaster = profileTypeInfo?.master;
                                                        const hasDerived = profileTypeInfo?.derived;
                                                        const hasRisk = profileTypeInfo?.risk;
                                                        
                                                        // Show SIMPLES only if it's truly simple (no master, no derived)
                                                        if (!hasMaster && !hasDerived) {
                                                          labels.push({text: 'SIMPLES', color: 'rgba(255, 255, 255, 0.25)'});
                                                        }
                                                        
                                                        // Add master/derived (without SIMPLES prefix)
                                                        if (hasDerived) {
                                                          labels.push({text: 'DERIVADO', color: 'rgba(245, 158, 11, 0.4)'});
                                                        }
                                                        if (hasMaster) {
                                                          labels.push({text: 'MESTRE', color: 'rgba(59, 130, 246, 0.4)'});
                                                        }
                                                        
                                                        // Add risk if present
                                                        if (hasRisk) {
                                                          labels.push({text: 'RISCO', color: 'rgba(239, 68, 68, 0.4)'});
                                                        }
                                                        
                                                        return (
                                                          <>
                                                            {labels.map((label, idx) => (
                                                              <span key={idx} style={{
                                                                fontSize: 8,
                                                                fontWeight: 700,
                                                                padding: '2px 6px',
                                                                borderRadius: 4,
                                                                background: label.color,
                                                                color: '#ffffff',
                                                                letterSpacing: '0.5px',
                                                                whiteSpace: 'nowrap'
                                                              }}>
                                                                {label.text}
                                                              </span>
                                                            ))}
                                                          </>
                                                        );
                                                      })()}
                                                    </div>
                                                    <div style={{ 
                                                      fontSize: 10, 
                                                      color: 'rgba(255, 255, 255, 0.7)'
                                                    }}>
                                                      {profileRows.length} {profileRows.length === 1 ? 'transação' : 'transações'}
                                                    </div>
                                                  </div>
                                                </div>
                                                {/* Estrutura Organizacional */}
                                                {showProfileDetails && (
                                                  <div style={{
                                                    paddingTop: 8,
                                                    marginTop: 8,
                                                    borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                                                    fontSize: 9,
                                                    color: 'rgba(255, 255, 255, 0.85)',
                                                    lineHeight: 1.6
                                                  }}>
                                                    {(() => {
                                                      const firstRow = profileRows[0];
                                                      const isPositionSimple = String(client?.profileModel) === 'Posição/Cargo Simples';
                                                      
                                                      if (isPositionSimple) {
                                                        const n1 = firstRow?.n1 || '-';
                                                        const n2 = firstRow?.n2 || '-';
                                                        const n3 = firstRow?.n3 || '-';
                                                        const n4 = firstRow?.n4 || '-';
                                                        return (
                                                          <>
                                                            <div><strong>N1:</strong> {n1}</div>
                                                            <div><strong>N2:</strong> {n2}</div>
                                                            <div><strong>N3:</strong> {n3}</div>
                                                            <div><strong>N4:</strong> {n4}</div>
                                                          </>
                                                        );
                                                      } else {
                                                        const area = firstRow?.area_perfil || firstRow?.area || '-';
                                                        const processo = firstRow?.processo || '-';
                                                        const subprocesso = firstRow?.subprocesso || '-';
                                                        const atividade = firstRow?.atividade || '-';
                                                        return (
                                                          <>
                                                            <div><strong>Área:</strong> {area}</div>
                                                            <div><strong>Processo:</strong> {processo}</div>
                                                            <div><strong>Subproc:</strong> {subprocesso}</div>
                                                            <div><strong>Atividade:</strong> {atividade}</div>
                                                          </>
                                                        );
                                                      }
                                                    })()}
                                                  </div>
                                                )}
                                              </div>

                                              {/* Seta e Transações */}
                                              <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                                gap: 10,
                                                marginTop: 8,
                                                width: '100%',
                                                maxHeight: isProfileExpanded ? '2000px' : '0',
                                                opacity: isProfileExpanded ? 1 : 0,
                                                overflow: 'hidden',
                                                transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                                visibility: isProfileExpanded ? 'visible' : 'hidden'
                                              }}>
                                                <>
                                                  {/* Conector Flowchart Style - Perfil para Transações */}
                                                  <div style={{
                                                    display: 'none'
                                                  }}>
                                                    <div style={{
                                                      width: '30px',
                                                      height: '2px',
                                                      background: theme === 'dark' ? '#64748b' : '#94a3b8',
                                                      position: 'relative'
                                                    }}>
                                                      {/* Seta */}
                                                      <div style={{
                                                        position: 'absolute',
                                                        right: '-6px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        width: 0,
                                                        height: 0,
                                                        borderLeft: `6px solid ${theme === 'dark' ? '#64748b' : '#94a3b8'}`,
                                                        borderTop: '5px solid transparent',
                                                        borderBottom: '5px solid transparent'
                                                      }} />
                                                    </div>
                                                  </div>
                                                  <div style={{ 
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                                    gap: 10,
                                                    width: '100%',
                                                    maxWidth: '100%'
                                                  }}>
                                                    {profileRows.map((row: any, idx: number) => (
                                                      <div key={idx} style={{ 
                                                        padding: 12,
                                                        background: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                                        borderRadius: 6,
                                                        border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                                                        fontSize: 11,
                                                        animation: `fadeIn 0.3s ease-out ${idx * 0.05}s both`,
                                                        minWidth: 180,
                                                        maxWidth: 240,
                                                        flex: '0 1 auto'
                                                      }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                          <div style={{ 
                                                            fontSize: 12,
                                                            fontWeight: 700,
                                                            color: theme === 'dark' ? '#f1f5f9' : '#1e293b'
                                                          }}>
                                                            {row.codigo || '-'}
                                                          </div>
                                                          <div style={{ 
                                                            fontSize: 10,
                                                            color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                                            lineHeight: 1.4
                                                          }}>
                                                            {row.nome || '-'}
                                                          </div>
                                                          {row.utilizada && (
                                                            <div style={{ marginTop: 4 }}>
                                                              <span style={{ 
                                                                padding: '3px 8px',
                                                                borderRadius: 4,
                                                                fontSize: 9,
                                                                fontWeight: 600,
                                                                background: row.utilizada.toUpperCase() === 'S' || row.utilizada.toUpperCase() === 'SIM'
                                                                  ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                                                                  : 'linear-gradient(135deg, #ef4444, #dc2626)',
                                                                color: '#ffffff'
                                                              }}>
                                                                {row.utilizada.toUpperCase() === 'S' || row.utilizada.toUpperCase() === 'SIM' ? '✓ Utilizada' : '✗ Não utilizada'}
                                                              </span>
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    ))}
                                                  </div>
                                                </>
                                              </div>
                                            </div>
                                          );
                                        })}
                                        </div>
                                      </>
                                    </div>
                                  </div>
                                );
                              })}
                                </div>
                              )}

                              {/* Direct Attribution Profiles - shown below composite profiles */}
                              {directProfiles.size > 0 && (
                                <div style={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  gap: 20,
                                  marginTop: compositeProfiles.size > 0 ? 20 : 0
                                }}>
                                  {Array.from(directProfiles.entries())
                                    .sort(([nameA], [nameB]) => {
                                      // Get profile type info
                                      const typeA = profileTypesMap.get(nameA.toUpperCase());
                                      const typeB = profileTypesMap.get(nameB.toUpperCase());
                                      
                                      // Determine type priority: Simples(0) > Derivado(1) > Mestre(2) > Risco(3)
                                      const getPriority = (type: any) => {
                                        if (!type) return 0; // Pure simple
                                        if (!type.master && !type.derived && !type.risk) return 0; // Simple
                                        if (type.derived) return 1; // Derived
                                        if (type.master) return 2; // Master
                                        if (type.risk) return 3; // Risk
                                        return 0;
                                      };
                                      
                                      const priorityA = getPriority(typeA);
                                      const priorityB = getPriority(typeB);
                                      
                                      // First sort by type priority
                                      if (priorityA !== priorityB) {
                                        return priorityA - priorityB;
                                      }
                                      
                                      // Then sort alphabetically (A-Z)
                                      return nameA.localeCompare(nameB);
                                    })
                                    .map(([profileName, profileRows], profIdx) => {
                                    const isExpanded = expandedProfiles.has(`direct:${profileName}`);

                                    return (
                                      <div key={`direct:${profileName}`}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
                                          {/* Card Perfil Direto */}
                                          <div
                                            onClick={() => {
                                              const newSet = new Set(expandedProfiles);
                                              const key = `direct:${profileName}`;
                                              if (isExpanded) {
                                                newSet.delete(key);
                                              } else {
                                                newSet.add(key);
                                              }
                                              setExpandedProfiles(newSet);
                                            }}
                                            style={(() => {
                                              // Detect profile type from consolidation data for dynamic colors
                                              const profileKey = profileName.toUpperCase();
                                              const profileTypeInfo = profileTypesMap.get(profileKey);
                                              
                                              let bgGradient = 'linear-gradient(135deg, #16a34a, #15803d)'; // Verde (Simples)
                                              let shadowColor = 'rgba(22, 163, 74, 0.3)';
                                              
                                              if (profileTypeInfo?.risk) {
                                                bgGradient = 'linear-gradient(135deg, #dc2626, #b91c1c)'; // Vermelho (Risco)
                                                shadowColor = 'rgba(220, 38, 38, 0.3)';
                                              } else if (profileTypeInfo?.derived) {
                                                bgGradient = 'linear-gradient(135deg, #3b82f6, #2563eb)'; // Azul claro (Derivado)
                                                shadowColor = 'rgba(59, 130, 246, 0.3)';
                                              } else if (profileTypeInfo?.master) {
                                                bgGradient = 'linear-gradient(135deg, #1e40af, #1e3a8a)'; // Azul escuro (Mestre)
                                                shadowColor = 'rgba(30, 64, 175, 0.3)';
                                              }
                                              
                                              return { 
                                                width: '100%',
                                                minWidth: 0,
                                                padding: 16,
                                                borderRadius: 10,
                                                background: bgGradient,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 12,
                                                transition: 'all 0.2s ease',
                                                userSelect: 'none',
                                                boxShadow: `0 4px 12px ${shadowColor}`,
                                                animation: `fadeIn 0.3s ease-out ${profIdx * 0.1 + 0.3}s both`,
                                                position: 'relative'
                                              };
                                            })()}
                                            onMouseEnter={(e) => {
                                              const profileKey = profileName.toUpperCase();
                                              const profileTypeInfo = profileTypesMap.get(profileKey);
                                              let shadowColorHover = 'rgba(22, 163, 74, 0.4)';
                                              if (profileTypeInfo?.risk) {
                                                shadowColorHover = 'rgba(220, 38, 38, 0.4)';
                                              } else if (profileTypeInfo?.derived) {
                                                shadowColorHover = 'rgba(59, 130, 246, 0.4)';
                                              } else if (profileTypeInfo?.master) {
                                                shadowColorHover = 'rgba(30, 64, 175, 0.4)';
                                              }
                                              e.currentTarget.style.transform = 'translateY(-2px)';
                                              e.currentTarget.style.boxShadow = `0 6px 16px ${shadowColorHover}`;
                                            }}
                                            onMouseLeave={(e) => {
                                              const profileKey = profileName.toUpperCase();
                                              const profileTypeInfo = profileTypesMap.get(profileKey);
                                              let shadowColor = 'rgba(22, 163, 74, 0.3)';
                                              if (profileTypeInfo?.risk) {
                                                shadowColor = 'rgba(220, 38, 38, 0.3)';
                                              } else if (profileTypeInfo?.derived) {
                                                shadowColor = 'rgba(59, 130, 246, 0.3)';
                                              } else if (profileTypeInfo?.master) {
                                                shadowColor = 'rgba(30, 64, 175, 0.3)';
                                              }
                                              e.currentTarget.style.transform = 'translateY(0)';
                                              e.currentTarget.style.boxShadow = `0 4px 12px ${shadowColor}`;
                                            }}
                                          >
                                          <span style={{ 
                                            fontSize: 16, 
                                            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
                                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                            color: '#ffffff',
                                            display: 'inline-block'
                                          }}>
                                            ▶
                                          </span>
                                          <div style={{ flex: 1 }}>
                                            <div style={{ 
                                              fontSize: 14, 
                                              fontWeight: 700, 
                                              color: '#ffffff',
                                              marginBottom: 6,
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 8
                                            }}>
                                              <div style={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                background: '#ffffff',
                                                flexShrink: 0
                                              }} />
                                              {profileName}
                                            </div>
                                            <div style={{ 
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 4,
                                              marginBottom: 6,
                                              flexWrap: 'wrap'
                                            }}>
                                              {(() => {
                                                // Detect profile type from consolidation data - show relevant labels only
                                                const profileKey = profileName.toUpperCase();
                                                const profileTypeInfo = profileTypesMap.get(profileKey);
                                                
                                                const labels: Array<{text: string, color: string}> = [];
                                                
                                                const hasMaster = profileTypeInfo?.master;
                                                const hasDerived = profileTypeInfo?.derived;
                                                const hasRisk = profileTypeInfo?.risk;
                                                
                                                // Show SIMPLES only if it's truly simple (no master, no derived)
                                                if (!hasMaster && !hasDerived) {
                                                  labels.push({text: 'SIMPLES', color: 'rgba(255, 255, 255, 0.25)'});
                                                }
                                                
                                                // Add master/derived (without SIMPLES prefix)
                                                if (hasDerived) {
                                                  labels.push({text: 'DERIVADO', color: 'rgba(245, 158, 11, 0.4)'});
                                                }
                                                if (hasMaster) {
                                                  labels.push({text: 'MESTRE', color: 'rgba(59, 130, 246, 0.4)'});
                                                }
                                                
                                                // Add risk if present
                                                if (hasRisk) {
                                                  labels.push({text: 'RISCO', color: 'rgba(239, 68, 68, 0.4)'});
                                                }
                                                
                                                return (
                                                  <>
                                                    {labels.map((label, idx) => (
                                                      <span key={idx} style={{
                                                        fontSize: 8,
                                                        fontWeight: 700,
                                                        padding: '2px 6px',
                                                        borderRadius: 4,
                                                        background: label.color,
                                                        color: '#ffffff',
                                                        letterSpacing: '0.5px',
                                                        whiteSpace: 'nowrap'
                                                      }}>
                                                        {label.text}
                                                      </span>
                                                    ))}
                                                  </>
                                                );
                                              })()}
                                            </div>
                                            <div style={{ 
                                              fontSize: 10, 
                                              color: 'rgba(255, 255, 255, 0.7)'
                                            }}>
                                              {profileRows.length} {profileRows.length === 1 ? 'transação' : 'transações'}
                                            </div>
                                            {/* Estrutura Organizacional */}
                                            {showProfileDetails && (
                                              <div style={{
                                                marginTop: 10,
                                                paddingTop: 10,
                                                borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                                                fontSize: 9,
                                                color: 'rgba(255, 255, 255, 0.85)',
                                                lineHeight: 1.6
                                              }}>
                                                {(() => {
                                                  const firstRow = profileRows[0];
                                                  const isPositionSimple = String(client?.profileModel) === 'Posição/Cargo Simples';
                                                  
                                                  if (isPositionSimple) {
                                                    const n1 = firstRow?.n1 || '-';
                                                    const n2 = firstRow?.n2 || '-';
                                                    const n3 = firstRow?.n3 || '-';
                                                    const n4 = firstRow?.n4 || '-';
                                                    return (
                                                      <>
                                                        <div><strong>N1:</strong> {n1}</div>
                                                        <div><strong>N2:</strong> {n2}</div>
                                                        <div><strong>N3:</strong> {n3}</div>
                                                        <div><strong>N4:</strong> {n4}</div>
                                                        <div style={{ marginTop: 4, fontSize: 8, opacity: 0.7 }}>⚡ Atribuição Direta</div>
                                                      </>
                                                    );
                                                  } else {
                                                    const area = firstRow?.area_perfil || firstRow?.area || '-';
                                                    const processo = firstRow?.processo || '-';
                                                    const subprocesso = firstRow?.subprocesso || '-';
                                                    const atividade = firstRow?.atividade || '-';
                                                    return (
                                                      <>
                                                        <div><strong>Área:</strong> {area}</div>
                                                        <div><strong>Processo:</strong> {processo}</div>
                                                        <div><strong>Subproc:</strong> {subprocesso}</div>
                                                        <div><strong>Atividade:</strong> {atividade}</div>
                                                        <div style={{ marginTop: 4, fontSize: 8, opacity: 0.7 }}>⚡ Atribuição Direta</div>
                                                      </>
                                                    );
                                                  }
                                                })()}
                                              </div>
                                            )}
                                          </div>
                                        </div>

                                        {/* Seta e Transações para perfis diretos */}
                                        <div style={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'stretch',
                                          gap: 10,
                                          marginTop: 8,
                                          width: '100%',
                                          maxHeight: isExpanded ? '2000px' : '0',
                                          opacity: isExpanded ? 1 : 0,
                                          overflow: 'hidden',
                                          transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                          visibility: isExpanded ? 'visible' : 'hidden'
                                        }}>
                                          <>
                                            {/* Conector Flowchart Style - Perfil para Transações */}
                                            <div style={{
                                              display: 'none'
                                            }}>
                                              <div style={{
                                                width: '35px',
                                                height: '2px',
                                                background: theme === 'dark' ? '#64748b' : '#94a3b8',
                                                position: 'relative'
                                              }}>
                                                {/* Seta */}
                                                <div style={{
                                                  position: 'absolute',
                                                  right: '-6px',
                                                  top: '50%',
                                                  transform: 'translateY(-50%)',
                                                  width: 0,
                                                  height: 0,
                                                  borderLeft: `6px solid ${theme === 'dark' ? '#64748b' : '#94a3b8'}`,
                                                  borderTop: '5px solid transparent',
                                                  borderBottom: '5px solid transparent'
                                                }} />
                                              </div>
                                            </div>
                                            <div style={{ 
                                              display: 'grid',
                                              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                              gap: 10,
                                              width: '100%',
                                              maxWidth: '100%'
                                            }}>
                                              {profileRows.map((row: any, idx: number) => (
                                                <div key={idx} style={{ 
                                                  padding: 12,
                                                  background: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                                  borderRadius: 6,
                                                  border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                                                  fontSize: 11,
                                                  animation: `fadeIn 0.3s ease-out ${idx * 0.05}s both`,
                                                  minWidth: 180,
                                                  maxWidth: 240,
                                                  flex: '0 1 auto'
                                                }}>
                                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                    <div style={{ 
                                                      fontSize: 12,
                                                      fontWeight: 700,
                                                      color: theme === 'dark' ? '#f1f5f9' : '#1e293b'
                                                    }}>
                                                      {row.codigo || '-'}
                                                    </div>
                                                    <div style={{ 
                                                      fontSize: 10,
                                                      color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                                      lineHeight: 1.4
                                                    }}>
                                                      {row.nome || '-'}
                                                    </div>
                                                    {row.utilizada && (
                                                      <div style={{ marginTop: 4 }}>
                                                        <span style={{ 
                                                          padding: '3px 8px',
                                                          borderRadius: 4,
                                                          fontSize: 9,
                                                          fontWeight: 600,
                                                          background: row.utilizada.toUpperCase() === 'S' || row.utilizada.toUpperCase() === 'SIM'
                                                            ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                                                            : 'linear-gradient(135deg, #ef4444, #dc2626)',
                                                          color: '#ffffff'
                                                        }}>
                                                          {row.utilizada.toUpperCase() === 'S' || row.utilizada.toUpperCase() === 'SIM' ? '✓ Utilizada' : '✗ Não utilizada'}
                                                        </span>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </>
                                        </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              </div>
                            </div>
                          );
                        }

                        // Modelo Simples (sem Perfil Composto)
                        const profiles = new Map<string, any[]>();
                        userRows.forEach(row => {
                          const profile = row.perfil || '-';
                          if (!profiles.has(profile)) {
                            profiles.set(profile, []);
                          }
                          profiles.get(profile)!.push(row);
                        });

                        if (detailedProfilesView === 'clean') {
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                              <div style={{
                                borderRadius: 10,
                                padding: 14,
                                background: theme === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                                border: `1px solid ${theme === 'dark' ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.25)'}`,
                                boxShadow: theme === 'dark' ? '0 6px 16px rgba(0,0,0,0.25)' : '0 6px 16px rgba(148,163,184,0.15)'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  marginBottom: 10
                                }}>
                                  <span style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    background: theme === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)',
                                    color: theme === 'dark' ? '#a5b4fc' : '#4f46e5',
                                    letterSpacing: '0.5px'
                                  }}>
                                    PERFIS
                                  </span>
                                  <div style={{ fontWeight: 700, fontSize: 13, color: theme === 'dark' ? '#e2e8f0' : '#0f172a' }}>
                                    {profiles.size} perfis atribuídos
                                  </div>
                                </div>
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                  gap: 10,
                                  alignItems: 'start'
                                }}>
                                  {Array.from(profiles.entries())
                                    .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
                                    .map(([profileName, profileRows]) => {
                                      const typeInfo = profileTypesMap.get(profileName.toUpperCase());
                                      const chipKey = `clean:${profileName}`;
                                      const isExpanded = expandedProfiles.has(chipKey);
                                      let chipBg = 'linear-gradient(135deg, #16a34a, #15803d)';
                                      if (typeInfo?.risk) chipBg = 'linear-gradient(135deg, #dc2626, #b91c1c)';
                                      else if (typeInfo?.derived) chipBg = 'linear-gradient(135deg, #3b82f6, #2563eb)';
                                      else if (typeInfo?.master) chipBg = 'linear-gradient(135deg, #1e40af, #1e3a8a)';

                                      return (
                                        <div key={chipKey} style={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          borderRadius: 10,
                                          overflow: 'hidden',
                                          background: theme === 'dark' ? 'rgba(15, 23, 42, 0.75)' : 'rgba(248, 250, 252, 0.95)',
                                          border: `1px solid ${theme === 'dark' ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.35)'}`,
                                          boxShadow: theme === 'dark' ? '0 4px 10px rgba(2,6,23,0.35)' : '0 4px 10px rgba(148,163,184,0.2)'
                                        }}>
                                          <div
                                            onClick={() => {
                                              const newSet = new Set(expandedProfiles);
                                              if (isExpanded) newSet.delete(chipKey);
                                              else newSet.add(chipKey);
                                              setExpandedProfiles(newSet);
                                            }}
                                            style={{
                                              padding: '9px 11px',
                                              background: chipBg,
                                              color: '#ffffff',
                                              fontSize: 11,
                                              fontWeight: 700,
                                              cursor: 'pointer',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 8,
                                              justifyContent: 'space-between'
                                            }}
                                          >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ffffff', flexShrink: 0 }} />
                                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profileName}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                              <span style={{ opacity: 0.9, fontSize: 10 }}>{profileRows.length}</span>
                                              <span style={{
                                                fontSize: 10,
                                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease'
                                              }}>▶</span>
                                            </div>
                                          </div>
                                          {showProfileDetails && (
                                            <div style={{
                                              padding: '8px 10px',
                                              fontSize: 9,
                                              color: theme === 'dark' ? '#cbd5e1' : '#475569',
                                              background: theme === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(241,245,249,0.6)',
                                              borderBottom: `1px solid ${theme === 'dark' ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.15)'}`,
                                              lineHeight: 1.5
                                            }}>
                                              {(() => {
                                                const firstRow = profileRows[0];
                                                const isPositionSimple = String(client?.profileModel) === 'Posição/Cargo Simples';
                                                if (isPositionSimple) {
                                                  const n1 = firstRow?.n1 || '-';
                                                  const n2 = firstRow?.n2 || '-';
                                                  const n3 = firstRow?.n3 || '-';
                                                  const n4 = firstRow?.n4 || '-';
                                                  return (
                                                    <>
                                                      <div><strong>N1:</strong> {n1}</div>
                                                      <div><strong>N2:</strong> {n2}</div>
                                                      <div><strong>N3:</strong> {n3}</div>
                                                      <div><strong>N4:</strong> {n4}</div>
                                                    </>
                                                  );
                                                } else {
                                                  const area = firstRow?.area_perfil || firstRow?.area || '-';
                                                  const processo = firstRow?.processo || '-';
                                                  const subprocesso = firstRow?.subprocesso || '-';
                                                  const atividade = firstRow?.atividade || '-';
                                                  return (
                                                    <>
                                                      <div><strong>Área:</strong> {area}</div>
                                                      <div><strong>Proc:</strong> {processo}</div>
                                                      <div><strong>Sub:</strong> {subprocesso}</div>
                                                      <div><strong>Ativ:</strong> {atividade}</div>
                                                    </>
                                                  );
                                                }
                                              })()}
                                            </div>
                                          )}
                                          {isExpanded && (
                                            <div style={{
                                              display: 'grid',
                                              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                              gap: 6,
                                              padding: 10,
                                              maxHeight: 180,
                                              overflowY: 'auto'
                                            }}>
                                              {profileRows.map((row: any, idx: number) => (
                                                <span key={`${chipKey}:${idx}`} style={{
                                                  padding: '4px 8px',
                                                  borderRadius: 6,
                                                  fontSize: 10,
                                                  background: theme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(226, 232, 240, 0.9)',
                                                  color: theme === 'dark' ? '#e2e8f0' : '#334155'
                                                }}>
                                                  {row.codigo || '-'}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'row', 
                            gap: 40, 
                            alignItems: 'center',
                            overflowX: 'auto',
                            overflowY: 'visible',
                            padding: '20px 0',
                            paddingTop: '80px',
                            marginTop: '-60px'
                          }}>
                            {/* Usuário (Coluna 1) */}
                            <div style={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              minWidth: 150
                            }}>
                              <div style={{
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 40,
                                color: '#ffffff',
                                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)',
                                marginBottom: 12
                              }}>
                                👤
                              </div>
                              <div style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: theme === 'dark' ? '#e9d5ff' : '#6b21a8',
                                textAlign: 'center',
                                maxWidth: 150,
                                wordBreak: 'break-word'
                              }}>
                                {detailedAnalysisUser}
                              </div>
                              <div style={{
                                fontSize: 11,
                                color: theme === 'dark' ? '#c4b5fd' : '#7c3aed',
                                marginTop: 4
                              }}>
                                Usuário
                              </div>
                            </div>

                            {/* Conector Flowchart Style */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              position: 'relative',
                              margin: '0 10px'
                            }}>
                              <div style={{
                                width: '40px',
                                height: '2px',
                                background: theme === 'dark' ? '#64748b' : '#94a3b8',
                                position: 'relative'
                              }}>
                                {/* Seta */}
                                <div style={{
                                  position: 'absolute',
                                  right: '-6px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: 0,
                                  height: 0,
                                  borderLeft: `6px solid ${theme === 'dark' ? '#64748b' : '#94a3b8'}`,
                                  borderTop: '5px solid transparent',
                                  borderBottom: '5px solid transparent'
                                }} />
                              </div>
                            </div>

                            {/* Perfis (Coluna 2 - empilhados verticalmente) */}
                            <div style={{ 
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                              gap: 12,
                              minWidth: 300,
                              width: '100%'
                            }}>
                            {Array.from(profiles.entries())
                              .sort(([nameA], [nameB]) => {
                                // Get profile type info
                                const typeA = profileTypesMap.get(nameA.toUpperCase());
                                const typeB = profileTypesMap.get(nameB.toUpperCase());
                                
                                // Determine type priority: Simples(0) > Derivado(1) > Mestre(2) > Risco(3)
                                const getPriority = (type: any) => {
                                  if (!type) return 0; // Pure simple
                                  if (!type.master && !type.derived && !type.risk) return 0; // Simple
                                  if (type.derived) return 1; // Derived
                                  if (type.master) return 2; // Master
                                  if (type.risk) return 3; // Risk
                                  return 0;
                                };
                                
                                const priorityA = getPriority(typeA);
                                const priorityB = getPriority(typeB);
                                
                                // First sort by type priority
                                if (priorityA !== priorityB) {
                                  return priorityA - priorityB;
                                }
                                
                                // Then sort alphabetically (A-Z)
                                return nameA.localeCompare(nameB);
                              })
                              .map(([profileName, profileRows], profIdx) => {
                              const isExpanded = expandedProfiles.has(profileName);

                              return (
                                <div key={profileName} style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
                                  {/* Card Perfil */}
                                  <div
                                    onClick={() => {
                                      const newSet = new Set(expandedProfiles);
                                      if (isExpanded) {
                                        newSet.delete(profileName);
                                      } else {
                                        newSet.add(profileName);
                                      }
                                      setExpandedProfiles(newSet);
                                    }}
                                    style={(() => {
                                      // Detect profile type from consolidation data for dynamic colors
                                      const profileKey = profileName.toUpperCase();
                                      const profileTypeInfo = profileTypesMap.get(profileKey);
                                      
                                      let bgGradient = 'linear-gradient(135deg, #16a34a, #15803d)'; // Verde (Simples)
                                      let shadowColor = 'rgba(22, 163, 74, 0.3)';
                                      
                                      if (profileTypeInfo?.risk) {
                                        bgGradient = 'linear-gradient(135deg, #dc2626, #b91c1c)'; // Vermelho (Risco)
                                        shadowColor = 'rgba(220, 38, 38, 0.3)';
                                      } else if (profileTypeInfo?.derived) {
                                        bgGradient = 'linear-gradient(135deg, #3b82f6, #2563eb)'; // Azul claro (Derivado)
                                        shadowColor = 'rgba(59, 130, 246, 0.3)';
                                      } else if (profileTypeInfo?.master) {
                                        bgGradient = 'linear-gradient(135deg, #1e40af, #1e3a8a)'; // Azul escuro (Mestre)
                                        shadowColor = 'rgba(30, 64, 175, 0.3)';
                                      }
                                      
                                      return { 
                                        width: '100%',
                                        minWidth: 0,
                                        padding: 16,
                                        borderRadius: 10,
                                        background: bgGradient,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        transition: 'all 0.2s ease',
                                        userSelect: 'none',
                                        boxShadow: `0 4px 12px ${shadowColor}`,
                                        animation: `fadeIn 0.3s ease-out ${profIdx * 0.1}s both`,
                                        position: 'relative'
                                      };
                                    })()}
                                    onMouseEnter={(e) => {
                                      const profileKey = profileName.toUpperCase();
                                      const profileTypeInfo = profileTypesMap.get(profileKey);
                                      let shadowColorHover = 'rgba(22, 163, 74, 0.4)';
                                      if (profileTypeInfo?.risk) {
                                        shadowColorHover = 'rgba(220, 38, 38, 0.4)';
                                      } else if (profileTypeInfo?.derived) {
                                        shadowColorHover = 'rgba(59, 130, 246, 0.4)';
                                      } else if (profileTypeInfo?.master) {
                                        shadowColorHover = 'rgba(30, 64, 175, 0.4)';
                                      }
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = `0 6px 16px ${shadowColorHover}`;
                                    }}
                                    onMouseLeave={(e) => {
                                      const profileKey = profileName.toUpperCase();
                                      const profileTypeInfo = profileTypesMap.get(profileKey);
                                      let shadowColor = 'rgba(22, 163, 74, 0.3)';
                                      if (profileTypeInfo?.risk) {
                                        shadowColor = 'rgba(220, 38, 38, 0.3)';
                                      } else if (profileTypeInfo?.derived) {
                                        shadowColor = 'rgba(59, 130, 246, 0.3)';
                                      } else if (profileTypeInfo?.master) {
                                        shadowColor = 'rgba(30, 64, 175, 0.3)';
                                      }
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = `0 4px 12px ${shadowColor}`;
                                    }}
                                  >
                                    <span style={{ 
                                      fontSize: 16, 
                                      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
                                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                      color: '#ffffff',
                                      display: 'inline-block'
                                    }}>
                                      ▶
                                    </span>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ 
                                        fontSize: 14, 
                                        fontWeight: 700, 
                                        color: '#ffffff',
                                        marginBottom: 6,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8
                                      }}>
                                        <div style={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: '50%',
                                          background: '#ffffff',
                                          flexShrink: 0
                                        }} />
                                        {profileName}
                                      </div>
                                      <div style={{ 
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        marginBottom: 6,
                                        flexWrap: 'wrap'
                                      }}>
                                        {(() => {
                                          // Detect profile type from consolidation data - show relevant labels only
                                          const profileKey = profileName.toUpperCase();
                                          const profileTypeInfo = profileTypesMap.get(profileKey);
                                          
                                          const labels: Array<{text: string, color: string}> = [];
                                          
                                          const hasMaster = profileTypeInfo?.master;
                                          const hasDerived = profileTypeInfo?.derived;
                                          const hasRisk = profileTypeInfo?.risk;
                                          
                                          // Show SIMPLES only if it's truly simple (no master, no derived)
                                          if (!hasMaster && !hasDerived) {
                                            labels.push({text: 'SIMPLES', color: 'rgba(255, 255, 255, 0.25)'});
                                          }
                                          
                                          // Add master/derived (without SIMPLES prefix)
                                          if (hasDerived) {
                                            labels.push({text: 'DERIVADO', color: 'rgba(245, 158, 11, 0.4)'});
                                          }
                                          if (hasMaster) {
                                            labels.push({text: 'MESTRE', color: 'rgba(59, 130, 246, 0.4)'});
                                          }
                                          
                                          // Add risk if present
                                          if (hasRisk) {
                                            labels.push({text: 'RISCO', color: 'rgba(239, 68, 68, 0.4)'});
                                          }
                                          
                                          return (
                                            <>
                                              {labels.map((label, idx) => (
                                                <span key={idx} style={{
                                                  fontSize: 8,
                                                  fontWeight: 700,
                                                  padding: '2px 6px',
                                                  borderRadius: 4,
                                                  background: label.color,
                                                  color: '#ffffff',
                                                  letterSpacing: '0.5px',
                                                  whiteSpace: 'nowrap'
                                                }}>
                                                  {label.text}
                                                </span>
                                              ))}
                                            </>
                                          );
                                        })()}
                                      </div>
                                      <div style={{ 
                                        fontSize: 10, 
                                        color: 'rgba(255, 255, 255, 0.7)'
                                      }}>
                                        {profileRows.length} {profileRows.length === 1 ? 'transação' : 'transações'}
                                      </div>
                                      {/* Estrutura Organizacional */}
                                      {showProfileDetails && (
                                        <div style={{
                                          marginTop: 10,
                                          paddingTop: 10,
                                          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                                          fontSize: 9,
                                          color: 'rgba(255, 255, 255, 0.85)',
                                          lineHeight: 1.6
                                        }}>
                                          {(() => {
                                            const firstRow = profileRows[0];
                                            const isPositionSimple = String(client?.profileModel) === 'Posição/Cargo Simples';
                                            
                                            if (isPositionSimple) {
                                              const n1 = firstRow?.n1 || '-';
                                              const n2 = firstRow?.n2 || '-';
                                              const n3 = firstRow?.n3 || '-';
                                              const n4 = firstRow?.n4 || '-';
                                              return (
                                                <>
                                                  <div><strong>N1:</strong> {n1}</div>
                                                  <div><strong>N2:</strong> {n2}</div>
                                                  <div><strong>N3:</strong> {n3}</div>
                                                  <div><strong>N4:</strong> {n4}</div>
                                                </>
                                              );
                                            } else {
                                              const area = firstRow?.area || '-';
                                              const processo = firstRow?.processo || '-';
                                              const subprocesso = firstRow?.subprocesso || '-';
                                              const atividade = firstRow?.atividade || '-';
                                              return (
                                                <>
                                                  <div><strong>Área:</strong> {area}</div>
                                                  <div><strong>Processo:</strong> {processo}</div>
                                                  <div><strong>Subproc:</strong> {subprocesso}</div>
                                                  <div><strong>Atividade:</strong> {atividade}</div>
                                                </>
                                              );
                                            }
                                          })()}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Seta e Transações */}
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    gap: 10,
                                    marginTop: 8,
                                    width: '100%',
                                    maxHeight: isExpanded ? '2000px' : '0',
                                    opacity: isExpanded ? 1 : 0,
                                    overflow: 'hidden',
                                    transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                    visibility: isExpanded ? 'visible' : 'hidden'
                                  }}>
                                    <>
                                      {/* Conector Flowchart Style - Perfil para Transações */}
                                      <div style={{
                                        display: 'none'
                                      }}>
                                        <div style={{
                                          width: '35px',
                                          height: '2px',
                                          background: theme === 'dark' ? '#64748b' : '#94a3b8',
                                          position: 'relative'
                                        }}>
                                          {/* Seta */}
                                          <div style={{
                                            position: 'absolute',
                                            right: '-6px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: 0,
                                            height: 0,
                                            borderLeft: `6px solid ${theme === 'dark' ? '#64748b' : '#94a3b8'}`,
                                            borderTop: '5px solid transparent',
                                            borderBottom: '5px solid transparent'
                                          }} />
                                        </div>
                                      </div>
                                      <div style={{ 
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                        gap: 10,
                                        width: '100%',
                                        maxWidth: '100%'
                                      }}>
                                        {profileRows.map((row: any, idx: number) => (
                                          <div key={idx} style={{ 
                                            padding: 12,
                                            background: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                            borderRadius: 6,
                                            border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                                            fontSize: 11,
                                            animation: `fadeIn 0.3s ease-out ${idx * 0.05}s both`,
                                            minWidth: 180,
                                            maxWidth: 240,
                                            flex: '0 1 auto'
                                          }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                              <div style={{ 
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: theme === 'dark' ? '#f1f5f9' : '#1e293b'
                                              }}>
                                                {row.codigo || '-'}
                                              </div>
                                              <div style={{ 
                                                fontSize: 10,
                                                color: theme === 'dark' ? '#94a3b8' : '#64748b',
                                                lineHeight: 1.4
                                              }}>
                                                {row.nome || '-'}
                                              </div>
                                              {row.utilizada && (
                                                <div style={{ marginTop: 4 }}>
                                                  <span style={{ 
                                                    padding: '3px 8px',
                                                    borderRadius: 4,
                                                    fontSize: 9,
                                                    fontWeight: 600,
                                                    background: row.utilizada.toUpperCase() === 'S' || row.utilizada.toUpperCase() === 'SIM'
                                                      ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                                                      : 'linear-gradient(135deg, #ef4444, #dc2626)',
                                                    color: '#ffffff'
                                                  }}>
                                                    {row.utilizada.toUpperCase() === 'S' || row.utilizada.toUpperCase() === 'SIM' ? '✓ Utilizada' : '✗ Não utilizada'}
                                                  </span>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  </div>
                                </div>
                              );
                            })}
                            </div>
                          </div>
                        );
                                })()}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: 20 }}>
                    <h6>Notas</h6>
                    <p className="text-muted">
                      Visão selecionada: <strong>{dashboardModelView === 'visao-geral' ? 'Visão geral' : dashboardModelView}</strong>. 
                      {validationDataLoaded 
                        ? ` Os gráficos e indicadores são baseados nos dados do passo ${getStepCountForModel(client?.profileModel)} (Validação e Download).` 
                        : ` Complete o passo ${getStepCountForModel(client?.profileModel)} para visualizar análises detalhadas.`}
                    </p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setDashboardOpen(false)}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Step4 classify loading modal with progress bar */}
        {step4ClassifyOpen && (
          <AppModal show={step4ClassifyOpen} title={step4ClassifyText || 'Processando classificação...'} onClose={() => {}} closeDisabled={true} size="sm">
                <div className="text-center p-2">
                  <div className="mb-3">
                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  </div>
                  <h6 className="mb-3">{step4ClassifyText || 'Processando classificação...'}</h6>
                  <div className="progress mb-3" style={{ height: '6px' }}>
                    <div 
                      className="progress-bar" 
                      role="progressbar" 
                      style={{ width: `${step4ClassifyProgress}%` }}
                      aria-valuenow={step4ClassifyProgress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">{step4ClassifyMessage || 'Aguarde enquanto as codes são classificadas.'}</small>
                    <small className="fw-bold">{Math.round(step4ClassifyProgress)}%</small>
                  </div>
                  {step4ClassifyProgress === 100 && (
                    <div className="mt-3">
                      <i className="fas fa-check-circle text-success me-2"></i>
                      <span className="text-success">Concluído!</span>
                    </div>
                  )}
                </div>
          </AppModal>
        )}

        {/* Clear classifications progress modal */}
        {clearClassificationsProgressOpen && (
          <AppModal show={clearClassificationsProgressOpen} title="Limpando Classificações" onClose={() => {}} closeDisabled={true} size="sm">
                <div className="text-center p-2">
                  <div className="mb-3">
                    <div className="spinner-border text-danger mb-3" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
                      <span className="visually-hidden">Limpando...</span>
                    </div>
                  </div>
                  <h6 className="mb-3">Limpando Classificações</h6>
                  <div className="progress mb-3" style={{ height: '6px' }}>
                    <div 
                      className="progress-bar bg-danger" 
                      role="progressbar" 
                      style={{ width: `${clearClassificationsProgress}%` }}
                      aria-valuenow={clearClassificationsProgress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">{clearClassificationsMessage || 'Limpando classificações...'}</small>
                    <small className="fw-bold">{Math.round(clearClassificationsProgress)}%</small>
                  </div>
                  {clearClassificationsProgress === 100 && (
                    <div className="mt-3">
                      <i className="fas fa-check-circle text-success me-2"></i>
                      <span className="text-success">Concluído!</span>
                    </div>
                  )}
                </div>
          </AppModal>
        )}

        {/* Step 2 Error Modal */}
        {step2ErrorModalOpen && (
          <AppModal
            show={step2ErrorModalOpen}
            title="Erro"
            onClose={() => setStep2ErrorModalOpen(false)}
            size="sm"
            footer={<button type="button" className="btn btn-danger" onClick={() => setStep2ErrorModalOpen(false)}>OK</button>}
          >
            <p>{step2ErrorMessage}</p>
          </AppModal>
        )}

        {/* Step 2 Success Modal */}
        {step2SuccessModalOpen && (
          <AppModal
            show={step2SuccessModalOpen}
            title="Sucesso"
            onClose={() => setStep2SuccessModalOpen(false)}
            size="sm"
            footer={<button type="button" className="btn btn-success" onClick={() => setStep2SuccessModalOpen(false)}>OK</button>}
          >
            <p>{step2SuccessMessage}</p>
          </AppModal>
        )}

        {/* Prefix Patterns Configuration Modal */}
        {prefixPatternsModalOpen && (
          <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Configurar Padrões de Prefixo - Perfis Simples</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setPrefixPatternsModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted mb-4">
                    Configure os padrões de prefixo para perfis simples baseados em estrutura organizacional.
                  </p>
                  
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label className="form-label fw-bold">Perfil Simples</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        value={prefixPatterns.simples}
                        maxLength={fieldConfigs.prefixo.qtd}
                        placeholder={`Ex: ${fieldConfigs.prefixo.char.repeat(Math.min(2, fieldConfigs.prefixo.qtd))}`}
                        style={{ textTransform: 'uppercase' }}
                        onChange={(e) => setPrefixPatterns(prev => ({ ...prev, simples: e.target.value.toUpperCase() }))}
                      />
                      <small className="text-muted">
                        {prefixPatterns.simples.length}/{fieldConfigs.prefixo.qtd} caracteres
                      </small>
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="form-label fw-bold">Perfil Mestre</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        value={prefixPatterns.mestre}
                        maxLength={fieldConfigs.prefixo.qtd}
                        placeholder={`Ex: M${fieldConfigs.prefixo.char.repeat(Math.max(0, fieldConfigs.prefixo.qtd - 1))}`}
                        style={{ textTransform: 'uppercase' }}
                        onChange={(e) => setPrefixPatterns(prev => ({ ...prev, mestre: e.target.value.toUpperCase() }))}
                      />
                      <small className="text-muted">
                        {prefixPatterns.mestre.length}/{fieldConfigs.prefixo.qtd} caracteres
                      </small>
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="form-label fw-bold">Perfil Derivado</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        value={prefixPatterns.derivado}
                        maxLength={fieldConfigs.prefixo.qtd}
                        placeholder={`Ex: D${fieldConfigs.prefixo.char.repeat(Math.max(0, fieldConfigs.prefixo.qtd - 1))}`}
                        style={{ textTransform: 'uppercase' }}
                        onChange={(e) => setPrefixPatterns(prev => ({ ...prev, derivado: e.target.value.toUpperCase() }))}
                      />
                      <small className="text-muted">
                        {prefixPatterns.derivado.length}/{fieldConfigs.prefixo.qtd} caracteres
                      </small>
                    </div>
                  </div>
                  
                  <div className="alert alert-info mt-3">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Dica:</strong> Os padrões definidos aqui serão utilizados como valores padrão na geração automática de perfis.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setPrefixPatternsModalOpen(false)}>
                    Cancelar
                  </button>
                  <button type="button" className="btn btn-primary" onClick={savePrefixPatterns}>
                    <i className="fas fa-save me-1"></i>
                    Salvar Padrões
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Prefix Patterns Success Modal */}
        {prefixPatternsSuccessModalOpen && (
          <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Sucesso</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setPrefixPatternsSuccessModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <p>Padrões de prefixo salvos com sucesso!</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-success" onClick={() => setPrefixPatternsSuccessModalOpen(false)}>OK</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Composite Prefix Patterns Modal */}
        {compositePrefixPatternsModalOpen && (
          <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {String(client?.profileModel) === 'Posição/Cargo Simples' 
                      ? 'Configurar Padrões de Prefixo - Perfis Simples'
                      : 'Configurar Padrões de Prefixo - Perfis Compostos'
                    }
                  </h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setCompositePrefixPatternsModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted mb-4">
                    {String(client?.profileModel) === 'Posição/Cargo Simples'
                      ? 'Configure o padrão de prefixo para perfis simples baseados em posições/cargos.'
                      : 'Configure o padrão de prefixo para perfis compostos baseados em posições/cargos.'
                    }
                  </p>
                  
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label className="form-label fw-bold">
                        <i className="fas fa-layer-group me-1 text-success"></i>
                        {String(client?.profileModel) === 'Posição/Cargo Simples' 
                          ? 'Perfil Simples'
                          : 'Perfil Composto'
                        }
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        value={compositePrefixPatterns.composto}
                        maxLength={compositeFieldConfigs.prefixo.qtd}
                        placeholder={`Ex: C${compositeFieldConfigs.prefixo.char.repeat(Math.max(0, compositeFieldConfigs.prefixo.qtd - 1))}`}
                        style={{ textTransform: 'uppercase' }}
                        onChange={(e) => setCompositePrefixPatterns(prev => ({ ...prev, composto: e.target.value.toUpperCase() }))}
                      />
                      <small className="text-muted">
                        {compositePrefixPatterns.composto.length}/{compositeFieldConfigs.prefixo.qtd} caracteres
                      </small>
                    </div>
                  </div>
                  
                  <div className="alert alert-info mt-3">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Dica:</strong> {String(client?.profileModel) === 'Posição/Cargo Simples'
                      ? 'O padrão definido aqui será utilizado como valor padrão na geração automática de perfis simples.'
                      : 'O padrão definido aqui será utilizado como valor padrão na geração automática de perfis compostos.'
                    }
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setCompositePrefixPatternsModalOpen(false)}>
                    Cancelar
                  </button>
                  <button type="button" className="btn btn-success" onClick={saveCompositePrefixPatterns}>
                    <i className="fas fa-save me-1"></i>
                    Salvar Padrão
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Composite Prefix Patterns Success Modal */}
        {compositePrefixPatternsSuccessModalOpen && (
          <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Sucesso</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setCompositePrefixPatternsSuccessModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <p>{String(client?.profileModel) === 'Posição/Cargo Simples'
                    ? 'Padrão de prefixo para perfis simples salvo com sucesso!'
                    : 'Padrão de prefixo para perfis compostos salvo com sucesso!'
                  }</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-success" onClick={() => setCompositePrefixPatternsSuccessModalOpen(false)}>OK</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Risk Prefix Pattern Configuration Modal */}
        {riskPrefixModalOpen && (
          <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Configurar Padrão de Prefixo - Perfil de Risco</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setRiskPrefixModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted mb-4">
                    {String(client?.profileModel) === 'Posição/Cargo Simples' 
                      ? 'Configure o padrão de prefixo para perfis de risco gerados pelo saneamento.'
                      : 'Configure os padrões de prefixo para perfis de risco gerados pelo saneamento.'
                    }
                  </p>
                  
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label className="form-label fw-bold">Perfil de Risco Simples</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        value={riskPrefixPattern}
                        maxLength={riskFieldConfigs.prefixo.qtd}
                        placeholder={`Ex: R${riskFieldConfigs.prefixo.char.repeat(Math.max(0, riskFieldConfigs.prefixo.qtd - 1))}`}
                        style={{ textTransform: 'uppercase' }}
                        onChange={(e) => setRiskPrefixPattern(e.target.value.toUpperCase())}
                      />
                      <small className="text-muted">
                        {riskPrefixPattern.length}/{riskFieldConfigs.prefixo.qtd} caracteres
                      </small>
                    </div>
                  </div>
                  
                  {/* Mostrar campos Master e Derivado apenas para modelos que não sejam "Posição/Cargo Simples" */}
                  {String(client?.profileModel) !== 'Posição/Cargo Simples' && (
                    <>
                      <div className="row">
                        <div className="col-md-12 mb-3">
                          <label className="form-label fw-bold">Perfil de Risco Master</label>
                          <input 
                            type="text" 
                            className="form-control" 
                            value={riskPrefixPatternMestre}
                            maxLength={riskFieldConfigs.prefixo.qtd}
                            placeholder={`Ex: RM${riskFieldConfigs.prefixo.char.repeat(Math.max(0, riskFieldConfigs.prefixo.qtd - 2))}`}
                            style={{ textTransform: 'uppercase' }}
                            onChange={(e) => setRiskPrefixPatternMestre(e.target.value.toUpperCase())}
                          />
                          <small className="text-muted">
                            {riskPrefixPatternMestre.length}/{riskFieldConfigs.prefixo.qtd} caracteres
                          </small>
                        </div>
                      </div>
                      
                      <div className="row">
                        <div className="col-md-12 mb-3">
                          <label className="form-label fw-bold">Perfil de Risco Derivado</label>
                          <input 
                            type="text" 
                            className="form-control" 
                            value={riskPrefixPatternDerivado}
                            maxLength={riskFieldConfigs.prefixo.qtd}
                            placeholder={`Ex: RD${riskFieldConfigs.prefixo.char.repeat(Math.max(0, riskFieldConfigs.prefixo.qtd - 2))}`}
                            style={{ textTransform: 'uppercase' }}
                            onChange={(e) => setRiskPrefixPatternDerivado(e.target.value.toUpperCase())}
                          />
                          <small className="text-muted">
                            {riskPrefixPatternDerivado.length}/{riskFieldConfigs.prefixo.qtd} caracteres
                          </small>
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="alert alert-info mt-3">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Dica:</strong> {String(client?.profileModel) === 'Posição/Cargo Simples' 
                      ? 'O padrão definido aqui será utilizado como valor padrão na geração automática de perfis de risco.'
                      : 'Os padrões definidos aqui serão utilizados como valores padrão na geração automática de perfis de risco.'
                    }
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setRiskPrefixModalOpen(false)}>
                    Cancelar
                  </button>
                  <button type="button" className="btn btn-danger" onClick={saveRiskPrefixPattern}>
                    <i className="fas fa-save me-1"></i>
                    Salvar Padrão
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Risk Prefix Pattern Success Modal */}
        {riskPrefixSuccessModalOpen && (
          <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Sucesso</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setRiskPrefixSuccessModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <p>Padrões de prefixo para perfis de risco salvos com sucesso!</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-success" onClick={() => setRiskPrefixSuccessModalOpen(false)}>OK</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Risk Matrix Visualization Modal */}
        {riskMatrixModalOpen && (
          <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Preview — Matriz de Risco</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setRiskMatrixModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-2">
                    <small>Cliente: <strong>{clientId}</strong></small>
                    <div>
                      <small>Colunas carregadas: <strong>risk, code</strong></small>
                    </div>
                  </div>
                  <div className="d-flex gap-2 mb-2">
                    <select className="form-select form-select-sm" style={{ maxWidth: 220 }} value={riskMatrixFilterColumn} onChange={e => setRiskMatrixFilterColumn(e.target.value)}>
                      <option value="">(Selecione coluna)</option>
                      <option value="risk">Risk</option>
                      <option value="code">Code</option>
                    </select>
                    <input className="form-control form-control-sm" placeholder="Filtrar valor" value={riskMatrixFilterValue} onChange={e => setRiskMatrixFilterValue(e.target.value)} />
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => { /* trigger re-render - filter is applied in table */ }}>Aplicar</button>
                  </div>
                  
                  {riskMatrixData.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted">Nenhum dado de matriz de risco encontrado</p>
                    </div>
                  ) : (
                    <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
                      {(() => {
                        const raw = riskMatrixData || [];
                        const filtered = raw.filter(r => {
                          if (!riskMatrixFilterColumn || !riskMatrixFilterValue) return true;
                          const val = String((r || {})[riskMatrixFilterColumn] || '').toLowerCase();
                          return val.indexOf(riskMatrixFilterValue.toLowerCase()) !== -1;
                        });
                        const total = filtered.length;
                        return (
                          <>
                            <DataTable
                              columns={[
                                { key: 'client', label: 'Cliente', minWidth: 140, sortable: true, render: () => clientId || '-' },
                                { key: 'risk', label: 'Risk', minWidth: 160, sortable: true, render: (_: any, row: any) => row.risk || '-' },
                                { key: 'code', label: 'Code', minWidth: 140, sortable: true, render: (_: any, row: any) => row.code || '-' }
                              ] as DataTableColumn<any>[]}
                              data={filtered}
                              keyExtractor={(_: any, index: number) => String(index)}
                              size="sm"
                              className="gpw-table"
                              striped
                              hover
                              bordered
                              responsive
                              pagination
                              clientSidePagination
                              currentPage={riskMatrixPage}
                              pageSize={PREVIEW_PAGE_SIZE}
                              totalItems={total}
                              onPageChange={(page: number) => setRiskMatrixPage(page)}
                              emptyMessage="Nenhum registro encontrado"
                            />
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setRiskMatrixModalOpen(false)}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Clear Risk Matrix Modal */}
        {confirmClearRiskMatrixModalOpen && (
          <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirmar Limpeza</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setConfirmClearRiskMatrixModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <p>Tem certeza que deseja limpar toda a matriz de risco deste cliente?</p>
                  <p className="text-muted small">Esta ação não pode ser desfeita.</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setConfirmClearRiskMatrixModalOpen(false)}>Cancelar</button>
                  <button type="button" className="btn btn-danger" onClick={confirmClearRiskMatrixData}>Confirmar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Organizational Level Selection Modal */}
        {orgLevelModalOpen && (
          <div className="modal show d-block" style={{ zIndex: 1055 }} tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Selecionar Nível Organizacional</h5>
                  <button type="button" className="btn-close" onClick={() => setOrgLevelModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  {loadingOrgLevels ? (
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <span className="ms-3">Carregando níveis organizacionais...</span>
                    </div>
                  ) : orgLevelsData.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <i className="fas fa-info-circle fa-3x mb-3 opacity-25"></i>
                      <p className="mb-0">Nenhum nível organizacional encontrado.</p>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-2">
                        <small className="text-muted">
                          Selecione um nível organizacional para derivação automática de perfis
                        </small>
                      </div>
                      <div className="table-responsive">
                        <DataTable
                          columns={[
                            {
                              key: '_select',
                              label: 'Seleção',
                              minWidth: 90,
                              sortable: true,
                              render: (_: any, row: any) => (
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="orgLevel"
                                    value={row.orglevel}
                                    checked={selectedOrgLevel === row.orglevel}
                                    onChange={() => setSelectedOrgLevel(row.orglevel)}
                                  />
                                </div>
                              )
                            },
                            { key: 'orglevel', label: 'Nível Organizacional', minWidth: 180, sortable: true, render: (_: any, row: any) => row.orglevel || '-' },
                            { key: 'orgleveldescription', label: 'Descrição', minWidth: 240, sortable: true, render: (_: any, row: any) => <span className="text-muted">{row.orgleveldescription || '-'}</span> }
                          ] as DataTableColumn<any>[]}
                          data={orgLevelsData}
                          keyExtractor={(_: any, index: number) => String(index)}
                          onRowClick={(row: any) => setSelectedOrgLevel(row.orglevel)}
                          size="sm"
                          className="gpw-table"
                          striped
                          hover
                          bordered
                          responsive
                          emptyMessage="Nenhum registro encontrado"
                        />
                      </div>
                      {orgLevelsData.length > 0 && (
                        <div className="mt-2">
                          <small className="text-muted">
                            Total: {orgLevelsData.length} {orgLevelsData.length === 1 ? 'nível organizacional disponível' : 'níveis organizacionais disponíveis'}
                          </small>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setOrgLevelModalOpen(false)}>
                    Cancelar
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={saveSelectedOrgLevel}
                    disabled={!selectedOrgLevel || loadingOrgLevels}
                  >
                    <i className="fas fa-save me-1"></i>
                    Salvar Seleção
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Organizational Level Values Modal */}
        {orgLevelValuesModalOpen && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-list me-2"></i>
                    Valores dos Níveis Organizacionais
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setOrgLevelValuesModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  {orgLevelValues.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <i className="fas fa-info-circle fa-3x mb-3 opacity-25"></i>
                      <p className="mb-0">Nenhum valor carregado ainda.</p>
                      <small>Carregue um arquivo CSV para visualizar os valores.</small>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            Valores para o nível organizacional: <strong>{derivationOrgLevel}</strong>
                          </small>
                          <span className="badge bg-primary">
                            {orgLevelValues.length} {orgLevelValues.length === 1 ? 'valor' : 'valores'}
                          </span>
                        </div>
                        <small className="text-muted d-block">
                          Limite de caracteres por valor: {client?.pattern_s_sufixo_qtd || 'N/A'}
                        </small>
                      </div>
                      
                      {/* Search bar */}
                      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }} className="gp-search-bar">
                        <input 
                          type="text" 
                          className="form-control" 
                          placeholder="Pesquisar" 
                          value={orgLevelValuesSearchQ} 
                          onChange={e => setOrgLevelValuesSearchQ(e.target.value)} 
                          style={{ maxWidth: 360 }} 
                          onKeyDown={e => { if (e.key === 'Enter') setOrgLevelValuesPage(1); }} 
                        />
                        <select 
                          className="form-select form-select-sm" 
                          value={orgLevelValuesSearchColumn} 
                          onChange={e => setOrgLevelValuesSearchColumn(e.target.value)} 
                          style={{ maxWidth: 260 }}
                        >
                          <option value="">Todas as colunas</option>
                          <option value="index">Índice</option>
                          <option value="value">Valor</option>
                        </select>
                        <button 
                          className="btn btn-sm btn-outline-primary" 
                          onClick={() => { setOrgLevelValuesPage(1); }}
                        >
                          Pesquisar
                        </button>
                        <div style={{ flex: 1 }} />
                      </div>
                      
                      <div className="table-responsive" style={{ maxHeight: '400px' }}>
                        <DataTable
                          columns={[
                            { key: 'index', label: '#', minWidth: 70, sortable: true, render: (_: any, row: any) => <span className="text-muted">{row.index}</span> },
                            { key: 'value', label: 'Valor', minWidth: 220, sortable: true, render: (_: any, row: any) => <code className={row.isValid ? 'text-success' : 'text-danger'}>{row.value}</code> },
                            { key: 'length', label: 'Caracteres', minWidth: 110, sortable: true, render: (_: any, row: any) => <span className={`badge ${row.isValid ? 'bg-success' : 'bg-danger'}`}>{row.length}</span> },
                            { key: 'status', label: 'Status', minWidth: 90, sortable: true, render: (_: any, row: any) => row.isValid ? <i className="fas fa-check-circle text-success" title="Válido"></i> : <i className="fas fa-exclamation-triangle text-danger" title="Muito longo"></i> }
                          ] as DataTableColumn<any>[]}
                          data={displayedOrgLevelValues.map((value) => {
                            const originalIndex = orgLevelValues.indexOf(value);
                            const isValid = client?.pattern_s_sufixo_qtd ? value.length <= client.pattern_s_sufixo_qtd : true;
                            return { index: originalIndex + 1, value, length: value.length, isValid, status: isValid ? 'Válido' : 'Inválido' };
                          })}
                          keyExtractor={(row: any) => String(row.index)}
                          size="sm"
                          className="gpw-table"
                          striped
                          hover
                          bordered
                          responsive
                          pagination
                          clientSidePagination
                          currentPage={orgLevelValuesPage}
                          pageSize={orgLevelValuesPageSize}
                          totalItems={displayedOrgLevelValues.length}
                          onPageChange={(page: number) => setOrgLevelValuesPage(page)}
                          onPageSizeChange={(size: number) => {
                            setOrgLevelValuesPageSize(size);
                            setOrgLevelValuesPage(1);
                          }}
                          emptyMessage="Nenhum registro encontrado"
                        />
                      </div>
                      
                      {client?.pattern_s_sufixo_qtd && (
                        <div className="mt-3">
                          <div className="row">
                            <div className="col-md-6">
                              <small className="text-success">
                                <i className="fas fa-check-circle me-1"></i>
                                Válidos: {orgLevelValues.filter(v => v.length <= client.pattern_s_sufixo_qtd!).length}
                              </small>
                            </div>
                            <div className="col-md-6">
                              <small className="text-danger">
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                Inválidos: {orgLevelValues.filter(v => v.length > client.pattern_s_sufixo_qtd!).length}
                              </small>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setOrgLevelValuesModalOpen(false)}>
                    Fechar
                  </button>
                  {orgLevelValues.length > 0 && (
                    <button type="button" className="btn btn-outline-danger" onClick={clearOrgLevelValues}>
                      <i className="fas fa-trash me-1"></i>
                      Limpar Todos
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User-OrgLevel Mappings Modal */}
        {userOrgLevelModalOpen && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-users me-2"></i>
                    Mapeamento Usuário → Valor Organizacional
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setUserOrgLevelModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  {userOrgLevelMappings.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <i className="fas fa-info-circle fa-3x mb-3 opacity-25"></i>
                      <p className="mb-0">Nenhum mapeamento carregado ainda.</p>
                      <small>Carregue um arquivo CSV para visualizar os mapeamentos.</small>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            Mapeamentos para o nível organizacional: <strong>{derivationOrgLevel}</strong>
                          </small>
                          <span className="badge bg-primary">
                            {userOrgLevelMappings.length} {userOrgLevelMappings.length === 1 ? 'mapeamento' : 'mapeamentos'}
                          </span>
                        </div>
                        <small className="text-muted d-block">
                          Limite de caracteres por valor: {client?.pattern_s_sufixo_qtd || 'N/A'}
                        </small>
                      </div>
                      
                      {/* Search bar */}
                      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }} className="gp-search-bar">
                        <input 
                          type="text" 
                          className="form-control" 
                          placeholder="Pesquisar" 
                          value={userOrgLevelSearchQ} 
                          onChange={e => setUserOrgLevelSearchQ(e.target.value)} 
                          style={{ maxWidth: 360 }} 
                          onKeyDown={e => { if (e.key === 'Enter') setUserOrgLevelPage(1); }} 
                        />
                        <select 
                          className="form-select form-select-sm" 
                          value={userOrgLevelSearchColumn} 
                          onChange={e => setUserOrgLevelSearchColumn(e.target.value)} 
                          style={{ maxWidth: 260 }}
                        >
                          <option value="">Todas as colunas</option>
                          <option value="index">Índice</option>
                          <option value="user">Usuário</option>
                          <option value="value">Valor</option>
                        </select>
                        <button 
                          className="btn btn-sm btn-outline-primary" 
                          onClick={() => { setUserOrgLevelPage(1); }}
                        >
                          Pesquisar
                        </button>
                        <div style={{ flex: 1 }} />
                      </div>
                      
                      <div className="table-responsive" style={{ maxHeight: '400px' }}>
                        <DataTable
                          columns={[
                            { key: 'index', label: '#', minWidth: 70, sortable: true, render: (_: any, row: any) => <span className="text-muted">{row.index}</span> },
                            { key: 'user', label: 'Usuário', minWidth: 170, sortable: true, render: (_: any, row: any) => <span className="fw-medium">{row.user}</span> },
                            { key: 'value', label: 'Valor Organizacional', minWidth: 220, sortable: true, render: (_: any, row: any) => <code className={row.isValid ? 'text-success' : 'text-danger'}>{row.value}</code> },
                            { key: 'length', label: 'Caracteres', minWidth: 110, sortable: true, render: (_: any, row: any) => <span className={`badge ${row.isValid ? 'bg-success' : 'bg-danger'}`}>{row.length}</span> },
                            { key: 'status', label: 'Status', minWidth: 90, sortable: true, render: (_: any, row: any) => row.isValid ? <i className="fas fa-check-circle text-success" title="Válido"></i> : <i className="fas fa-exclamation-triangle text-danger" title="Muito longo"></i> }
                          ] as DataTableColumn<any>[]}
                          data={displayedUserOrgLevelMappings.map((mapping) => {
                            const originalIndex = userOrgLevelMappings.indexOf(mapping);
                            const isValid = client?.pattern_s_sufixo_qtd ? mapping.value.length <= client.pattern_s_sufixo_qtd : true;
                            return { index: originalIndex + 1, user: mapping.user, value: mapping.value, length: mapping.value.length, isValid, status: isValid ? 'Válido' : 'Inválido' };
                          })}
                          keyExtractor={(row: any) => String(row.index)}
                          size="sm"
                          className="gpw-table"
                          striped
                          hover
                          bordered
                          responsive
                          pagination
                          clientSidePagination
                          currentPage={userOrgLevelPage}
                          pageSize={userOrgLevelPageSize}
                          totalItems={displayedUserOrgLevelMappings.length}
                          onPageChange={(page: number) => setUserOrgLevelPage(page)}
                          onPageSizeChange={(size: number) => {
                            setUserOrgLevelPageSize(size);
                            setUserOrgLevelPage(1);
                          }}
                          emptyMessage="Nenhum registro encontrado"
                        />
                      </div>
                      
                      {client?.pattern_s_sufixo_qtd && (
                        <div className="mt-3">
                          <div className="row">
                            <div className="col-md-6">
                              <small className="text-success">
                                <i className="fas fa-check-circle me-1"></i>
                                Válidos: {userOrgLevelMappings.filter(m => m.value.length <= client.pattern_s_sufixo_qtd!).length}
                              </small>
                            </div>
                            <div className="col-md-6">
                              <small className="text-danger">
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                Inválidos: {userOrgLevelMappings.filter(m => m.value.length > client.pattern_s_sufixo_qtd!).length}
                              </small>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setUserOrgLevelModalOpen(false)}>
                    Fechar
                  </button>
                  {userOrgLevelMappings.length > 0 && (
                    <button type="button" className="btn btn-outline-danger" onClick={clearUserOrgLevelMappings}>
                      <i className="fas fa-trash me-1"></i>
                      Limpar Todos
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User-Org Level Diagnostic Modal */}
        {userOrgLevelDiagnosticModalOpen && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-stethoscope me-2"></i>
                    Diagnóstico de Validação - Mapeamentos Usuário x Nível Organizacional
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setUserOrgLevelDiagnosticModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  {!userOrgLevelValidationIssues.hasIssues ? (
                    <div className="alert alert-success">
                      <i className="fas fa-check-circle me-2"></i>
                      <strong>Validação Aprovada!</strong>
                      <p className="mb-0 mt-2">
                        Todos os {userOrgLevelMappings.length} mapeamentos estão válidos:
                      </p>
                      <ul className="mb-0 mt-2">
                        <li>Todos os valores organizacionais são válidos</li>
                        <li>Todos os usuários existem nos logs</li>
                        <li>Todos os usuários dos logs estão mapeados</li>
                      </ul>
                    </div>
                  ) : (
                    <>
                      <div className="border border-warning rounded p-3 mb-4 bg-warning bg-opacity-25">
                        <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                        <strong className="text-body">Problemas Detectados!</strong>
                        <p className="mb-0 mt-2">
                          Foram encontrados {(userOrgLevelValidationIssues.invalidValues?.length || 0) + (userOrgLevelValidationIssues.missingUsers?.length || 0) + (userOrgLevelValidationIssues.unmappedUsers?.length || 0)} problemas que precisam ser corrigidos.
                        </p>
                      </div>

                      {/* Invalid Values Section */}
                      {userOrgLevelValidationIssues.invalidValues && userOrgLevelValidationIssues.invalidValues.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-danger">
                            <i className="fas fa-times-circle me-2"></i>
                            Valores Organizacionais Inválidos ({userOrgLevelValidationIssues.invalidValues.length})
                          </h6>
                          <div className="border rounded p-3">
                            <p className="mb-2 small">
                              <strong>Problema:</strong> Os seguintes mapeamentos contêm valores organizacionais que não existem nos valores cadastrados.
                            </p>
                            <p className="mb-3 small">
                              <strong>Solução:</strong> Verifique se os valores estão corretos ou cadastre-os primeiro na seção "Valores do Nível Organizacional".
                            </p>
                            <div className="table-responsive">
                              <DataTable
                                columns={[
                                  { key: 'user', label: 'Usuário', minWidth: 180, sortable: true, render: (_: any, row: any) => <code>{row.user}</code> },
                                  { key: 'value', label: 'Valor Inválido', minWidth: 180, sortable: true, render: (_: any, row: any) => <span className="badge bg-danger">{row.value}</span> }
                                ] as DataTableColumn<any>[]}
                                data={userOrgLevelValidationIssues.invalidValues}
                                keyExtractor={(_: any, index: number) => String(index)}
                                pagination
                                clientSidePagination
                                currentPage={userOrgLevelDiagPage1}
                                pageSize={diagnosticPageSize}
                                totalItems={userOrgLevelValidationIssues.invalidValues.length}
                                onPageChange={(page: number) => setUserOrgLevelDiagPage1(page)}
                                onPageSizeChange={handleDiagnosticPageSizeChange}
                                pageSizeOptions={[10, 25, 50, 100]}
                                size="sm"
                                striped
                                hover
                                bordered
                                responsive
                                className="gpw-table"
                                emptyMessage="Nenhum registro encontrado"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Missing Users Section */}
                      {userOrgLevelValidationIssues.missingUsers && userOrgLevelValidationIssues.missingUsers.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-warning">
                            <i className="fas fa-user-slash me-2"></i>
                            Usuários Não Encontrados nos Logs ({userOrgLevelValidationIssues.missingUsers.length})
                          </h6>
                          <div className="border rounded p-3">
                            <p className="mb-2 small">
                              <strong>Problema:</strong> Os seguintes usuários estão nos mapeamentos mas não foram encontrados nos logs de perfil.
                            </p>
                            <p className="mb-3 small">
                              <strong>Solução:</strong> Verifique se os nomes de usuário estão corretos ou se os logs de perfil foram carregados corretamente.
                            </p>
                            <div className="table-responsive">
                              <DataTable
                                columns={[
                                  { key: 'index', label: '#', minWidth: 70, sortable: true, render: (_: any, row: any) => row.index },
                                  { key: 'user', label: 'Usuário Ausente', minWidth: 180, sortable: true, render: (_: any, row: any) => <code>{row.user}</code> },
                                  { key: 'value', label: 'Valor Atribuído', minWidth: 170, sortable: true, render: (_: any, row: any) => <span className="badge bg-secondary">{row.value}</span> }
                                ] as DataTableColumn<any>[]}
                                data={userOrgLevelValidationIssues.missingUsers.map((user, index) => ({
                                  index: index + 1,
                                  user,
                                  value: userOrgLevelMappings.find(m => m.user === user)?.value || 'N/A'
                                }))}
                                keyExtractor={(row: any) => `${row.user}-${row.index}`}
                                pagination
                                clientSidePagination
                                currentPage={userOrgLevelDiagPage2}
                                pageSize={diagnosticPageSize}
                                totalItems={userOrgLevelValidationIssues.missingUsers.length}
                                onPageChange={(page: number) => setUserOrgLevelDiagPage2(page)}
                                onPageSizeChange={handleDiagnosticPageSizeChange}
                                pageSizeOptions={[10, 25, 50, 100]}
                                size="sm"
                                striped
                                hover
                                bordered
                                responsive
                                className="gpw-table"
                                emptyMessage="Nenhum registro encontrado"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Unmapped Users Section */}
                      {userOrgLevelValidationIssues.unmappedUsers && userOrgLevelValidationIssues.unmappedUsers.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-info">
                            <i className="fas fa-user-question me-2"></i>
                            Usuários dos Logs Sem Mapeamento ({userOrgLevelValidationIssues.unmappedUsers.length})
                          </h6>
                          <div className="border rounded p-3">
                            <p className="mb-2 small">
                              <strong>Problema:</strong> Os seguintes usuários existem nos logs de perfil mas não possuem mapeamento para o nível organizacional.
                            </p>
                            <p className="mb-3 small">
                              <strong>Solução:</strong> Adicione estes usuários ao arquivo CSV de mapeamentos com seus respectivos valores organizacionais.
                            </p>
                            <div className="table-responsive">
                              <DataTable
                                columns={[
                                  { key: 'index', label: '#', minWidth: 70, sortable: true, render: (_: any, row: any) => row.index },
                                  { key: 'user', label: 'Usuário Sem Mapeamento', minWidth: 220, sortable: true, render: (_: any, row: any) => <code>{row.user}</code> },
                                  { key: 'status', label: 'Status', minWidth: 130, sortable: true, render: () => <span className="badge bg-light text-dark">Não Mapeado</span> }
                                ] as DataTableColumn<any>[]}
                                data={userOrgLevelValidationIssues.unmappedUsers.map((user, index) => ({ index: index + 1, user, status: 'Não Mapeado' }))}
                                keyExtractor={(row: any) => `${row.user}-${row.index}`}
                                pagination
                                clientSidePagination
                                currentPage={userOrgLevelDiagPage3}
                                pageSize={diagnosticPageSize}
                                totalItems={userOrgLevelValidationIssues.unmappedUsers.length}
                                onPageChange={(page: number) => setUserOrgLevelDiagPage3(page)}
                                onPageSizeChange={handleDiagnosticPageSizeChange}
                                pageSizeOptions={[10, 25, 50, 100]}
                                size="sm"
                                striped
                                hover
                                bordered
                                responsive
                                className="gpw-table"
                                emptyMessage="Nenhum registro encontrado"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Summary Statistics */}
                  <div className="mt-4 p-3 bg-body-tertiary rounded border">
                    <h6 className="mb-3">
                      <i className="fas fa-chart-bar me-2"></i>
                      Estatísticas de Validação
                    </h6>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-2">
                          <strong>Mapeamentos Carregados:</strong>
                          <span className="badge bg-primary ms-2">{userOrgLevelMappings.length}</span>
                        </div>
                        <div className="mb-2">
                          <strong>Valores Inválidos:</strong>
                          <span className={`badge ms-2 ${userOrgLevelValidationIssues.invalidValues?.length ? 'bg-danger' : 'bg-success'}`}>
                            {userOrgLevelValidationIssues.invalidValues?.length || 0}
                          </span>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-2">
                          <strong>Usuários Não Encontrados:</strong>
                          <span className={`badge ms-2 ${userOrgLevelValidationIssues.missingUsers?.length ? 'bg-warning text-dark' : 'bg-success'}`}>
                            {userOrgLevelValidationIssues.missingUsers?.length || 0}
                          </span>
                        </div>
                        <div className="mb-2">
                          <strong>Usuários Não Mapeados:</strong>
                          <span className={`badge ms-2 ${userOrgLevelValidationIssues.unmappedUsers?.length ? 'bg-info text-dark' : 'bg-success'}`}>
                            {userOrgLevelValidationIssues.unmappedUsers?.length || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setUserOrgLevelDiagnosticModalOpen(false)}>
                    Fechar
                  </button>
                  {userOrgLevelValidationIssues.hasIssues && (
                    <button type="button" className="btn btn-outline-primary" onClick={() => {
                      setUserOrgLevelDiagnosticModalOpen(false);
                      setUserOrgLevelModalOpen(true);
                    }}>
                      <i className="fas fa-eye me-1"></i>
                      Ver Mapeamentos
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User-Area Diagnostic Modal */}
        {userAreaDiagnosticModalOpen && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-stethoscope me-2"></i>
                    Diagnóstico de Validação - Usuário x Área
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setUserAreaDiagnosticModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  {!userAreaValidationIssues.hasIssues ? (
                    <div className="alert alert-success">
                      <i className="fas fa-check-circle me-2"></i>
                      <strong>Validação Aprovada!</strong>
                      <p className="mb-0 mt-2">
                        Todos os dados estão consistentes:
                      </p>
                      <ul className="mb-0 mt-2">
                        <li>Todos os usuários do bloco "Usuário x Área" existem nos logs</li>
                        <li>Todos os usuários dos logs estão mapeados em "Usuário x Área"</li>
                        <li>Nenhum usuário possui múltiplas áreas</li>
                      </ul>
                    </div>
                  ) : (
                    <>
                      <div className="border border-warning rounded p-3 mb-4 bg-warning bg-opacity-25">
                        <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                        <strong className="text-body">Problemas Detectados!</strong>
                        <p className="mb-0 mt-2">
                          Foram encontrados {userAreaValidationIssues.usersInAreaNotInLogs.length + userAreaValidationIssues.usersInLogsNotInArea.length + userAreaValidationIssues.usersWithMultipleAreas.length} problemas que precisam ser corrigidos.
                        </p>
                      </div>

                      {/* Users in Area but not in Logs Section */}
                      {userAreaValidationIssues.usersInAreaNotInLogs && userAreaValidationIssues.usersInAreaNotInLogs.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-danger">
                            <i className="fas fa-user-times me-2"></i>
                            Usuários em "Usuário x Área" não encontrados nos Logs ({userAreaValidationIssues.usersInAreaNotInLogs.length})
                          </h6>
                          <div className="border rounded p-3">
                            <p className="mb-2 small">
                              <strong>Problema:</strong> Os seguintes usuários estão no arquivo "Usuário x Área" mas não foram encontrados nos logs de acesso.
                            </p>
                            <p className="mb-3 small">
                              <strong>Solução:</strong> Verifique se os nomes de usuário estão corretos ou se os logs de acesso foram carregados corretamente.
                            </p>
                            <div className="table-responsive">
                              <DataTable
                                columns={[
                                  { key: 'index', label: '#', minWidth: 70, sortable: true, render: (_: any, row: any) => row.index },
                                  { key: 'user', label: 'Usuário', minWidth: 220, sortable: true, render: (_: any, row: any) => <code>{row.user}</code> }
                                ] as DataTableColumn<any>[]}
                                data={userAreaValidationIssues.usersInAreaNotInLogs.map((user, index) => ({ index: index + 1, user }))}
                                keyExtractor={(row: any) => `${row.user}-${row.index}`}
                                pagination
                                clientSidePagination
                                currentPage={userAreaDiagPage1}
                                pageSize={diagnosticPageSize}
                                totalItems={userAreaValidationIssues.usersInAreaNotInLogs.length}
                                onPageChange={(page: number) => setUserAreaDiagPage1(page)}
                                onPageSizeChange={handleDiagnosticPageSizeChange}
                                pageSizeOptions={[10, 25, 50, 100]}
                                size="sm"
                                striped
                                hover
                                bordered
                                responsive
                                className="gpw-table"
                                emptyMessage="Nenhum registro encontrado"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Users in Logs but not in Area Section */}
                      {userAreaValidationIssues.usersInLogsNotInArea && userAreaValidationIssues.usersInLogsNotInArea.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-warning">
                            <i className="fas fa-user-slash me-2"></i>
                            Usuários nos Logs não encontrados em "Usuário x Área" ({userAreaValidationIssues.usersInLogsNotInArea.length})
                          </h6>
                          <div className="border rounded p-3">
                            <p className="mb-2 small">
                              <strong>Problema:</strong> Os seguintes usuários existem nos logs de acesso mas não estão mapeados no arquivo "Usuário x Área".
                            </p>
                            <p className="mb-3 small">
                              <strong>Solução:</strong> Adicione estes usuários ao arquivo "Usuário x Área" com suas respectivas áreas.
                            </p>
                            <div className="table-responsive">
                              <DataTable
                                columns={[
                                  { key: 'index', label: '#', minWidth: 70, sortable: true, render: (_: any, row: any) => row.index },
                                  { key: 'user', label: 'Usuário', minWidth: 220, sortable: true, render: (_: any, row: any) => <code>{row.user}</code> },
                                  { key: 'status', label: 'Status', minWidth: 130, sortable: true, render: () => <span className="badge bg-light text-dark">Não Mapeado</span> }
                                ] as DataTableColumn<any>[]}
                                data={userAreaValidationIssues.usersInLogsNotInArea.map((user, index) => ({ index: index + 1, user, status: 'Não Mapeado' }))}
                                keyExtractor={(row: any) => `${row.user}-${row.index}`}
                                pagination
                                clientSidePagination
                                currentPage={userAreaDiagPage2}
                                pageSize={diagnosticPageSize}
                                totalItems={userAreaValidationIssues.usersInLogsNotInArea.length}
                                onPageChange={(page: number) => setUserAreaDiagPage2(page)}
                                onPageSizeChange={handleDiagnosticPageSizeChange}
                                pageSizeOptions={[10, 25, 50, 100]}
                                size="sm"
                                striped
                                hover
                                bordered
                                responsive
                                className="gpw-table"
                                emptyMessage="Nenhum registro encontrado"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Users with Multiple Areas Section */}
                      {userAreaValidationIssues.usersWithMultipleAreas && userAreaValidationIssues.usersWithMultipleAreas.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-info">
                            <i className="fas fa-users me-2"></i>
                            Usuários com Múltiplas Áreas ({userAreaValidationIssues.usersWithMultipleAreas.length})
                          </h6>
                          <div className="border rounded p-3">
                            <p className="mb-2 small">
                              <strong>Problema:</strong> Os seguintes usuários estão associados a mais de uma área, o que pode causar conflitos na geração de perfis.
                            </p>
                            <p className="mb-3 small">
                              <strong>Solução:</strong> Revise os mapeamentos e mantenha cada usuário associado a apenas uma área principal.
                            </p>
                            <div className="table-responsive">
                              <DataTable
                                columns={[
                                  { key: 'index', label: '#', minWidth: 70, sortable: true, render: (_: any, row: any) => row.index },
                                  { key: 'user', label: 'Usuário', minWidth: 180, sortable: true, render: (_: any, row: any) => <code>{row.user}</code> },
                                  { key: 'areasText', label: 'Áreas Associadas', minWidth: 260, sortable: true, render: (_: any, row: any) => (
                                    <>
                                      {row.areas.map((area: string, areaIndex: number) => (
                                        <span key={areaIndex} className="badge bg-secondary me-1 mb-1">{area}</span>
                                      ))}
                                    </>
                                  ) },
                                  { key: 'count', label: 'Qtd', minWidth: 90, sortable: true, render: (_: any, row: any) => <span className="badge bg-warning text-dark">{row.count}</span> }
                                ] as DataTableColumn<any>[]}
                                data={userAreaValidationIssues.usersWithMultipleAreas.map((item, index) => ({
                                  index: index + 1,
                                  user: item.user,
                                  areas: item.areas,
                                  areasText: (item.areas || []).join(', '),
                                  count: item.areas.length
                                }))}
                                keyExtractor={(row: any) => `${row.user}-${row.index}`}
                                pagination
                                clientSidePagination
                                currentPage={userAreaDiagPage3}
                                pageSize={diagnosticPageSize}
                                totalItems={userAreaValidationIssues.usersWithMultipleAreas.length}
                                onPageChange={(page: number) => setUserAreaDiagPage3(page)}
                                onPageSizeChange={handleDiagnosticPageSizeChange}
                                pageSizeOptions={[10, 25, 50, 100]}
                                size="sm"
                                striped
                                hover
                                bordered
                                responsive
                                className="gpw-table"
                                emptyMessage="Nenhum registro encontrado"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Summary Statistics */}
                  <div className="mt-4 p-3 bg-body-tertiary rounded border">
                    <h6 className="mb-3">
                      <i className="fas fa-chart-bar me-2"></i>
                      Estatísticas de Validação
                    </h6>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-2">
                          <strong>Usuários em "Usuário x Área":</strong>
                          <span className="badge bg-primary ms-2">{totalUsersInArea}</span>
                        </div>
                        <div className="mb-2">
                          <strong>Usuários em Logs:</strong>
                          <span className="badge bg-primary ms-2">{totalUsersInLogs}</span>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-2">
                          <strong>Usuários Não Encontrados nos Logs:</strong>
                          <span className={`badge ms-2 ${userAreaValidationIssues.usersInAreaNotInLogs?.length ? 'bg-danger' : 'bg-success'}`}>
                            {userAreaValidationIssues.usersInAreaNotInLogs?.length || 0}
                          </span>
                        </div>
                        <div className="mb-2">
                          <strong>Usuários Não Mapeados:</strong>
                          <span className={`badge ms-2 ${userAreaValidationIssues.usersInLogsNotInArea?.length ? 'bg-warning text-dark' : 'bg-success'}`}>
                            {userAreaValidationIssues.usersInLogsNotInArea?.length || 0}
                          </span>
                        </div>
                        <div className="mb-2">
                          <strong>Usuários com Múltiplas Áreas:</strong>
                          <span className={`badge ms-2 ${userAreaValidationIssues.usersWithMultipleAreas?.length ? 'bg-info text-dark' : 'bg-success'}`}>
                            {userAreaValidationIssues.usersWithMultipleAreas?.length || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setUserAreaDiagnosticModalOpen(false)}>
                    Fechar
                  </button>
                  {userAreaValidationIssues.hasIssues && (
                    <button type="button" className="btn btn-outline-primary" onClick={() => {
                      setUserAreaDiagnosticModalOpen(false);
                      setPreviewModalTable('userxa');
                      setPreviewModalOpen(true);
                    }}>
                      <i className="fas fa-eye me-1"></i>
                      Ver Dados
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User-Position Diagnostic Modal */}
        {userPositionDiagnosticModalOpen && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-stethoscope me-2"></i>
                    Diagnóstico de Validação - Usuário x Posição/Cargo (RH)
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setUserPositionDiagnosticModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  {!userPositionValidationIssues.hasIssues ? (
                    <div className="alert alert-success">
                      <i className="fas fa-check-circle me-2"></i>
                      <strong>Validação Aprovada!</strong>
                      <p className="mb-0 mt-2">
                        Todos os dados estão consistentes:
                      </p>
                      <ul className="mb-0 mt-2">
                        <li>Todos os usuários do bloco "Usuário x Posição/Cargo (RH)" existem nos logs</li>
                        <li>Todos os usuários dos logs estão mapeados em "Usuário x Posição/Cargo (RH)"</li>
                        <li>Nenhum usuário possui múltiplas estruturas de cargo</li>
                      </ul>
                    </div>
                  ) : (
                    <>
                      <div className="border border-warning rounded p-3 mb-4 bg-warning bg-opacity-25">
                        <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                        <strong className="text-body">Problemas Detectados!</strong>
                        <p className="mb-0 mt-2">
                          Foram encontrados {userPositionValidationIssues.usersInPositionNotInLogs.length + userPositionValidationIssues.usersInLogsNotInPosition.length + userPositionValidationIssues.usersWithMultiplePositions.length} problemas que precisam ser corrigidos.
                        </p>
                      </div>

                      {/* Users in Position but not in Logs Section */}
                      {userPositionValidationIssues.usersInPositionNotInLogs && userPositionValidationIssues.usersInPositionNotInLogs.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-danger">
                            <i className="fas fa-user-times me-2"></i>
                            Usuários em "Usuário x Posição/Cargo (RH)" não encontrados nos Logs ({userPositionValidationIssues.usersInPositionNotInLogs.length})
                          </h6>
                          <div className="border rounded p-3">
                            <p className="mb-2 small">
                              <strong>Problema:</strong> Os seguintes usuários estão no arquivo "Usuário x Posição/Cargo (RH)" mas não foram encontrados nos logs de acesso.
                            </p>
                            <p className="mb-3 small">
                              <strong>Solução:</strong> Verifique se os nomes de usuário estão corretos ou se os logs de acesso foram carregados corretamente.
                            </p>
                            <div className="table-responsive">
                              <DataTable
                                columns={[
                                  { key: 'index', label: '#', minWidth: 70, sortable: true, render: (_: any, row: any) => row.index },
                                  { key: 'user', label: 'Usuário', minWidth: 220, sortable: true, render: (_: any, row: any) => <code>{row.user}</code> }
                                ] as DataTableColumn<any>[]}
                                data={userPositionValidationIssues.usersInPositionNotInLogs.map((user, index) => ({ index: index + 1, user }))}
                                keyExtractor={(row: any) => `${row.user}-${row.index}`}
                                pagination
                                clientSidePagination
                                currentPage={userPositionDiagPage1}
                                pageSize={diagnosticPageSize}
                                totalItems={userPositionValidationIssues.usersInPositionNotInLogs.length}
                                onPageChange={(page: number) => setUserPositionDiagPage1(page)}
                                onPageSizeChange={handleDiagnosticPageSizeChange}
                                pageSizeOptions={[10, 25, 50, 100]}
                                size="sm"
                                striped
                                hover
                                bordered
                                responsive
                                className="gpw-table"
                                emptyMessage="Nenhum registro encontrado"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Users in Logs but not in Position Section */}
                      {userPositionValidationIssues.usersInLogsNotInPosition && userPositionValidationIssues.usersInLogsNotInPosition.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-warning">
                            <i className="fas fa-user-slash me-2"></i>
                            Usuários nos Logs não encontrados em "Usuário x Posição/Cargo (RH)" ({userPositionValidationIssues.usersInLogsNotInPosition.length})
                          </h6>
                          <div className="border rounded p-3">
                            <p className="mb-2 small">
                              <strong>Problema:</strong> Os seguintes usuários existem nos logs de acesso mas não estão mapeados no arquivo "Usuário x Posição/Cargo (RH)".
                            </p>
                            <p className="mb-3 small">
                              <strong>Solução:</strong> Adicione estes usuários ao arquivo "Usuário x Posição/Cargo (RH)" com suas respectivas estruturas de cargo (N1, N2, N3, N4).
                            </p>
                            <div className="table-responsive">
                              <DataTable
                                columns={[
                                  { key: 'index', label: '#', minWidth: 70, sortable: true, render: (_: any, row: any) => row.index },
                                  { key: 'user', label: 'Usuário', minWidth: 220, sortable: true, render: (_: any, row: any) => <code>{row.user}</code> },
                                  { key: 'status', label: 'Status', minWidth: 130, sortable: true, render: () => <span className="badge bg-light text-dark">Não Mapeado</span> }
                                ] as DataTableColumn<any>[]}
                                data={userPositionValidationIssues.usersInLogsNotInPosition.map((user, index) => ({ index: index + 1, user, status: 'Não Mapeado' }))}
                                keyExtractor={(row: any) => `${row.user}-${row.index}`}
                                pagination
                                clientSidePagination
                                currentPage={userPositionDiagPage2}
                                pageSize={diagnosticPageSize}
                                totalItems={userPositionValidationIssues.usersInLogsNotInPosition.length}
                                onPageChange={(page: number) => setUserPositionDiagPage2(page)}
                                onPageSizeChange={handleDiagnosticPageSizeChange}
                                pageSizeOptions={[10, 25, 50, 100]}
                                size="sm"
                                striped
                                hover
                                bordered
                                responsive
                                className="gpw-table"
                                emptyMessage="Nenhum registro encontrado"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Users with Multiple Positions Section */}
                      {userPositionValidationIssues.usersWithMultiplePositions && userPositionValidationIssues.usersWithMultiplePositions.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-info">
                            <i className="fas fa-users me-2"></i>
                            Usuários com Múltiplas Estruturas de Cargo ({userPositionValidationIssues.usersWithMultiplePositions.length})
                          </h6>
                          <div className="border rounded p-3">
                            <p className="mb-2 small">
                              <strong>Problema:</strong> Os seguintes usuários estão associados a mais de uma estrutura de cargo (valores diferentes de N1, N2, N3, N4), o que pode causar conflitos na geração de perfis.
                            </p>
                            <p className="mb-3 small">
                              <strong>Solução:</strong> Revise os mapeamentos e mantenha cada usuário associado a apenas uma estrutura de cargo principal.
                            </p>
                            <div className="table-responsive">
                              <DataTable
                                columns={[
                                  { key: 'index', label: '#', minWidth: 70, sortable: true, render: (_: any, row: any) => row.index },
                                  { key: 'user', label: 'Usuário', minWidth: 180, sortable: true, render: (_: any, row: any) => <code>{row.user}</code> },
                                  { key: 'positionsText', label: 'Estruturas de Cargo', minWidth: 280, sortable: true, render: (_: any, row: any) => (
                                    <>
                                      {row.positions.map((position: string, posIndex: number) => (
                                        <span key={posIndex} className="badge bg-secondary me-1 mb-1">{position}</span>
                                      ))}
                                    </>
                                  ) },
                                  { key: 'count', label: 'Qtd', minWidth: 90, sortable: true, render: (_: any, row: any) => <span className="badge bg-warning text-dark">{row.count}</span> }
                                ] as DataTableColumn<any>[]}
                                data={userPositionValidationIssues.usersWithMultiplePositions.map((item, index) => ({
                                  index: index + 1,
                                  user: item.user,
                                  positions: item.positions,
                                  positionsText: (item.positions || []).join(', '),
                                  count: item.positions.length
                                }))}
                                keyExtractor={(row: any) => `${row.user}-${row.index}`}
                                pagination
                                clientSidePagination
                                currentPage={userPositionDiagPage3}
                                pageSize={diagnosticPageSize}
                                totalItems={userPositionValidationIssues.usersWithMultiplePositions.length}
                                onPageChange={(page: number) => setUserPositionDiagPage3(page)}
                                onPageSizeChange={handleDiagnosticPageSizeChange}
                                pageSizeOptions={[10, 25, 50, 100]}
                                size="sm"
                                striped
                                hover
                                bordered
                                responsive
                                className="gpw-table"
                                emptyMessage="Nenhum registro encontrado"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Summary Statistics */}
                  <div className="mt-4 p-3 bg-body-tertiary rounded border">
                    <h6 className="mb-3">
                      <i className="fas fa-chart-bar me-2"></i>
                      Estatísticas de Validação
                    </h6>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-2">
                          <strong>Usuários em "Usuário x Posição/Cargo (RH)":</strong>
                          <span className="badge bg-primary ms-2">{totalUsersInPosition}</span>
                        </div>
                        <div className="mb-2">
                          <strong>Usuários em Logs:</strong>
                          <span className="badge bg-primary ms-2">{totalUsersInLogs}</span>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-2">
                          <strong>Usuários Não Encontrados nos Logs:</strong>
                          <span className={`badge ms-2 ${userPositionValidationIssues.usersInPositionNotInLogs?.length ? 'bg-danger' : 'bg-success'}`}>
                            {userPositionValidationIssues.usersInPositionNotInLogs?.length || 0}
                          </span>
                        </div>
                        <div className="mb-2">
                          <strong>Usuários Não Mapeados:</strong>
                          <span className={`badge ms-2 ${userPositionValidationIssues.usersInLogsNotInPosition?.length ? 'bg-warning text-dark' : 'bg-success'}`}>
                            {userPositionValidationIssues.usersInLogsNotInPosition?.length || 0}
                          </span>
                        </div>
                        <div className="mb-2">
                          <strong>Usuários com Múltiplas Estruturas:</strong>
                          <span className={`badge ms-2 ${userPositionValidationIssues.usersWithMultiplePositions?.length ? 'bg-info text-dark' : 'bg-success'}`}>
                            {userPositionValidationIssues.usersWithMultiplePositions?.length || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setUserPositionDiagnosticModalOpen(false)}>
                    Fechar
                  </button>
                  {userPositionValidationIssues.hasIssues && (
                    <button type="button" className="btn btn-outline-primary" onClick={() => {
                      setUserPositionDiagnosticModalOpen(false);
                      setPreviewModalTable('userxpos');
                      setPreviewModalOpen(true);
                    }}>
                      <i className="fas fa-eye me-1"></i>
                      Ver Dados
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Organizational Derivation Alert Modal */}
        {orgDerivationAlertModalOpen && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {orgDerivationAlertType === 'error' ? 'Erro' : orgDerivationAlertType === 'success' ? 'Sucesso' : 'Informação'}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setOrgDerivationAlertModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <p style={{ whiteSpace: 'pre-line' }}>{orgDerivationAlertMessage}</p>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className={`btn ${orgDerivationAlertType === 'error' ? 'btn-danger' : orgDerivationAlertType === 'success' ? 'btn-success' : 'btn-primary'}`}
                    onClick={() => setOrgDerivationAlertModalOpen(false)}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Organizational Derivation Confirmation Modal */}
        {orgDerivationConfirmModalOpen && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirmação</h5>
                  <button type="button" className="btn-close" onClick={() => setOrgDerivationConfirmModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <p style={{ whiteSpace: 'pre-line' }}>{orgDerivationConfirmMessage}</p>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary" 
                    onClick={() => {
                      setOrgDerivationConfirmModalOpen(false);
                      setOrgDerivationConfirmAction(null);
                    }}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={() => {
                      setOrgDerivationConfirmModalOpen(false);
                      if (orgDerivationConfirmAction) {
                        orgDerivationConfirmAction();
                      }
                      setOrgDerivationConfirmAction(null);
                    }}
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Risk Alert Modal */}
      {showRiskAlert && (
        <div className="modal show d-block" tabIndex={-1} role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                  Saneamento de Riscos Não Habilitado
                </h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowRiskAlert(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning d-flex align-items-center mb-3">
                  <i className="fas fa-info-circle me-2"></i>
                  <div>
                    O saneamento de riscos não foi habilitado no <strong>Passo 2 (Definir parâmetros gerais)</strong>.
                  </div>
                </div>
                <p className="mb-0">
                  Para utilizar a classificação de ações de risco, é necessário habilitar pelo menos uma das opções de saneamento:
                </p>
                <ul className="mt-2 mb-0">
                  <li><strong>Matriz de risco benchmark</strong> - utiliza a tabela padrão do sistema</li>
                  <li><strong>Matriz de risco personalizada</strong> - utiliza a matriz específica do cliente</li>
                </ul>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowRiskAlert(false)}>
                  Fechar
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={() => {
                    setShowRiskAlert(false);
                    setStep(2); // Navigate to step 2
                  }}
                >
                  <i className="fas fa-cog me-2"></i>
                  Ir para Parâmetros Gerais
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Consolidation Alert Modal */}
      {consolidationAlertOpen && (
        <div 
          className="modal show d-block" 
          tabIndex={-1} 
          role="dialog"
          style={{ zIndex: aiConversionModalOpen ? 1080 : undefined }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div 
              className="modal-content"
              style={{ zIndex: aiConversionModalOpen ? 1081 : undefined }}
            >
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className={`fas ${consolidationAlertType === 'success' ? 'fa-check-circle text-success' : consolidationAlertType === 'error' ? 'fa-exclamation-triangle text-danger' : 'fa-info-circle text-info'} me-2`}></i>
                  {consolidationAlertType === 'success' ? 'Sucesso' : consolidationAlertType === 'error' ? 'Erro' : 'Informação'}
                </h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setConsolidationAlertOpen(false)}></button>
              </div>
              <div className="modal-body">
                <div className={`alert alert-${consolidationAlertType === 'success' ? 'success' : consolidationAlertType === 'error' ? 'danger' : 'info'} d-flex align-items-center mb-3`}>
                  <i className={`fas ${consolidationAlertType === 'success' ? 'fa-check-circle' : consolidationAlertType === 'error' ? 'fa-exclamation-triangle' : 'fa-info-circle'} me-2`}></i>
                  <div>
                    {consolidationAlertMessage}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setConsolidationAlertOpen(false)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Consolidation Confirm Modal */}
      {consolidationConfirmOpen && (
        <div 
          className="modal show d-block" 
          tabIndex={-1} 
          role="dialog"
          style={{ zIndex: aiConversionModalOpen ? 1080 : undefined }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div 
              className="modal-content"
              style={{ zIndex: aiConversionModalOpen ? 1081 : undefined }}
            >
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                  Confirmar Exclusão
                </h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setConsolidationConfirmOpen(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning d-flex align-items-center mb-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <div>
                    {consolidationConfirmMessage}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setConsolidationConfirmOpen(false)}>
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={() => {
                    setConsolidationConfirmOpen(false);
                    if (consolidationConfirmAction) {
                      consolidationConfirmAction();
                    }
                  }}
                >
                  <i className="fas fa-trash me-2"></i>
                  Confirmar Exclusão
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Conversion Modal */}
      {aiConversionModalOpen && (
        <div className="modal show d-block" tabIndex={-1} role="dialog">
          <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-robot text-info me-2"></i>
                  Converter elementos com IA
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  aria-label="Close" 
                  onClick={() => setAiConversionModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                {/* Tabs Navigation */}
                <ul className="nav nav-tabs mb-3" role="tablist">
                  {(aiConversionMode === 'simple' ? [
                    { key: 'area', label: 'Área' },
                    { key: 'processo', label: 'Processo' },
                    { key: 'subprocesso', label: 'Subprocesso' },
                    { key: 'atividade', label: 'Atividade' }
                  ] : [
                    { key: 'n1', label: 'Nível 1 (N1)' },
                    { key: 'n2', label: 'Nível 2 (N2)' },
                    { key: 'n3', label: 'Nível 3 (N3)' },
                    { key: 'n4', label: 'Nível 4 (N4)' }
                  ]).map(tab => (
                    <li className="nav-item" key={tab.key}>
                      <button
                        className={`nav-link ${aiConversionActiveTab === tab.key ? 'active' : ''}`}
                        onClick={() => setAiConversionActiveTab(tab.key as any)}
                        type="button"
                      >
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Tab Content */}
                <div className="tab-content">
                  {aiConversionData[aiConversionActiveTab] && aiConversionData[aiConversionActiveTab].length > 0 ? (
                    (() => {
                      // Filter data based on search
                      let filteredData = aiConversionData[aiConversionActiveTab];
                      if (aiConversionSearchQ) {
                        const searchTerm = aiConversionSearchQ.toLowerCase();
                        filteredData = filteredData.filter(item => {
                          if (aiConversionSearchColumn) {
                            const fieldValue = aiConversionSearchColumn === 'elemento' ? item.value :
                                             aiConversionSearchColumn === 'tipo' ? item.type :
                                             aiConversionSearchColumn === 'caracteres' ? String(item.characters) :
                                             aiConversionSearchColumn === 'resultado' ? item.resultado :
                                             aiConversionSearchColumn === 'status' ? getStatusText(item) : '';
                            return String(fieldValue).toLowerCase().includes(searchTerm);
                          } else {
                            return String(item.value).toLowerCase().includes(searchTerm) ||
                                   String(item.type).toLowerCase().includes(searchTerm) ||
                                   String(item.characters).toLowerCase().includes(searchTerm) ||
                                   String(item.resultado).toLowerCase().includes(searchTerm) ||
                                   getStatusText(item).toLowerCase().includes(searchTerm);
                          }
                        });
                      }

                      if (showOnlyAiDuplicates) {
                        filteredData = filteredData.filter((item: any) => {
                          const chars = item.characters;
                          const parsed = typeof chars === 'string' ? parseInt(chars || '0', 10) : chars;
                          const normalizedChars = Number.isNaN(parsed) ? chars : parsed;
                          const rowKey = `${String(item.value)}|||${String(item.type)}|||${normalizedChars}`;
                          return aiDuplicateSummary.duplicateKeys.has(rowKey);
                        });
                      }

                      if (showOnlyAiUnconverted) {
                        filteredData = filteredData.filter((item: any) => isAiUnconvertedResult(item?.resultado));
                      }

                      filteredData = sortAiRowsByName(filteredData);

                      // Pagination
                      const total = filteredData.length;
                      const totalPages = Math.max(1, Math.ceil(total / aiConversionPageSize));
                      const page = Math.min(Math.max(1, aiConversionPage), totalPages);
                      const start = (page - 1) * aiConversionPageSize;
                      const pageItems = filteredData.slice(start, start + aiConversionPageSize);

                      return (
                        <>
                          {/* Search Controls */}
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <div className="input-group input-group-sm">
                                <span className="input-group-text">
                                  <i className="fas fa-search"></i>
                                </span>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Pesquisar..."
                                  value={aiConversionSearchQ}
                                  onChange={e => setAiConversionSearchQ(e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <select
                                className="form-select form-select-sm"
                                value={aiConversionSearchColumn}
                                onChange={e => setAiConversionSearchColumn(e.target.value)}
                              >
                                <option value="">Todas as colunas</option>
                                <option value="elemento">{aiConversionMode === 'simple' ? (aiConversionActiveTab.charAt(0).toUpperCase() + aiConversionActiveTab.slice(1)) : (`${aiConversionActiveTab.toUpperCase()}`)}</option>
                                <option value="tipo">Tipo</option>
                                <option value="caracteres">Número de caracteres</option>
                                <option value="resultado">Resultado</option>
                                <option value="status">Status</option>
                              </select>
                            </div>
                          </div>

                          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                            <button
                              type="button"
                              className={`btn btn-sm ${showOnlyAiDuplicates ? 'btn-warning' : 'btn-outline-warning'}`}
                              onClick={() => setShowOnlyAiDuplicates(prev => !prev)}
                              disabled={aiDuplicateSummary.duplicateRowCount === 0 && !showOnlyAiDuplicates}
                              title="Mostrar/ocultar registros com resultado duplicado para nomes diferentes"
                            >
                              <i className="fas fa-clone me-2"></i>
                              {showOnlyAiDuplicates
                                ? `Ocultar duplicidades (${aiDuplicateSummary.duplicateRowCount})`
                                : `Mostrar duplicidades (${aiDuplicateSummary.duplicateRowCount})`}
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                const rows = aiConversionData[aiConversionActiveTab] || [];
                                const sel: {[key: string]: boolean} = {};
                                rows.forEach((item: any) => {
                                  const chars = item.characters;
                                  const parsed = typeof chars === 'string' ? parseInt(chars || '0', 10) : chars;
                                  const normalizedChars = Number.isNaN(parsed) ? chars : parsed;
                                  const key = `${String(item.value)}|||${String(item.type)}|||${normalizedChars}`;
                                  if (aiDuplicateSummary.duplicateKeys.has(key)) {
                                    sel[key] = true;
                                  }
                                });
                                setAiConversionSelected(sel);
                              }}
                              disabled={aiDuplicateSummary.duplicateRowCount === 0 || aiProcessing}
                              title="Selecionar somente registros com duplicidade para reprocessar com IA"
                            >
                              <i className="fas fa-check-double me-2"></i>
                              Selecionar duplicados
                            </button>

                            <button
                              type="button"
                              className={`btn btn-sm ${showOnlyAiUnconverted ? 'btn-secondary' : 'btn-outline-secondary'}`}
                              onClick={() => setShowOnlyAiUnconverted(prev => !prev)}
                              disabled={aiUnconvertedSummary.unconvertedRowCount === 0 && !showOnlyAiUnconverted}
                              title="Mostrar/ocultar elementos não convertidos (resultado vazio)"
                            >
                              <i className="fas fa-filter me-2"></i>
                              {showOnlyAiUnconverted
                                ? `Ocultar não convertidos (${aiUnconvertedSummary.unconvertedRowCount})`
                                : `Mostrar não convertidos (${aiUnconvertedSummary.unconvertedRowCount})`}
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => {
                                const rows = aiConversionData[aiConversionActiveTab] || [];
                                const sel: {[key: string]: boolean} = {};
                                rows.forEach((item: any) => {
                                  if (!isAiUnconvertedResult(item?.resultado) || item?.isInvalid) return;
                                  const chars = item.characters;
                                  const parsed = typeof chars === 'string' ? parseInt(chars || '0', 10) : chars;
                                  const normalizedChars = Number.isNaN(parsed) ? chars : parsed;
                                  const key = `${String(item.value)}|||${String(item.type)}|||${normalizedChars}`;
                                  sel[key] = true;
                                });
                                setAiConversionSelected(sel);
                              }}
                              disabled={aiUnconvertedSummary.unconvertedRowCount === 0 || aiProcessing}
                              title="Selecionar somente elementos não convertidos (resultado vazio)"
                            >
                              <i className="fas fa-check-square me-2"></i>
                              Selecionar não convertidos
                            </button>

                            {aiDuplicateSummary.duplicateRowCount > 0 && (
                              <small className="text-warning-emphasis">
                                {aiDuplicateSummary.duplicateGroupCount} grupos de colisão em {aiDuplicateSummary.duplicateRowCount} linhas.
                              </small>
                            )}
                            {aiUnconvertedSummary.unconvertedRowCount > 0 && (
                              <small className="text-secondary">
                                {aiUnconvertedSummary.unconvertedRowCount} não convertidos.
                              </small>
                            )}
                          </div>

                          {/* Table */}
                          <div className="table-responsive">
                            <table className="table table-striped table-hover table-sm">
                              <thead className="table-dark">
                                <tr>
                                    <th style={{ width: 36, textAlign: 'center' }}>
                                    <input
                                      type="checkbox"
                                      checked={(() => {
                                          const visibleKeys = (pageItems || [])
                                            .filter((item: any) => !item.isInvalid)
                                            .map((item: any) => {
                                              const chars = item.characters;
                                              const itemNum = typeof chars === 'string' ? (chars === 'N/A' ? chars : parseInt(chars || '0', 10)) : chars;
                                              const normalized = Number.isNaN(itemNum) ? chars : itemNum;
                                              return `${item.value}|||${item.type}|||${normalized}`;
                                            });
                                          if (visibleKeys.length === 0) return false;
                                          return visibleKeys.every((k) => !!aiConversionSelected[k]);
                                      })()}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                          setAiConversionSelected(prev => {
                                            const newSel: {[key: string]: boolean} = { ...prev };
                                            (pageItems || []).forEach((item: any) => {
                                              if (item.isInvalid) return;
                                              const chars = item.characters;
                                              const itemNum = typeof chars === 'string' ? (chars === 'N/A' ? chars : parseInt(chars || '0', 10)) : chars;
                                              const normalized = Number.isNaN(itemNum) ? chars : itemNum;
                                              const key = `${item.value}|||${item.type}|||${normalized}`;
                                              if (checked) {
                                                newSel[key] = true;
                                              } else {
                                                delete newSel[key];
                                              }
                                            });
                                            return newSel;
                                        });
                                      }}
                                    />
                                  </th>
                                  <th>{(aiConversionMode === 'simple') ? (aiConversionActiveTab.charAt(0).toUpperCase() + aiConversionActiveTab.slice(1)) : (aiConversionActiveTab.toUpperCase())}</th>
                                  <th>Tipo</th>
                                  <th>Número de caracteres</th>
                                  <th>Resultado</th>
                                  <th>Status</th>
                                  <th>Ações</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pageItems.map((row, index) => (
                                  (() => {
                                    const chars = row.characters;
                                    const parsed = typeof chars === 'string' ? parseInt(chars || '0', 10) : chars;
                                    const normalizedChars = Number.isNaN(parsed) ? chars : parsed;
                                    const duplicateKey = `${String(row.value)}|||${String(row.type)}|||${normalizedChars}`;
                                    const isDuplicateRow = aiDuplicateSummary.duplicateKeys.has(duplicateKey);
                                    return (
                                  <tr 
                                    key={`${row.value}-${row.type}-${index}`} 
                                    className={row.isInvalid ? 'table-danger' : (isDuplicateRow ? 'table-warning' : '')}
                                  >
                                    <td className="text-center">
                                      {row.isInvalid ? (
                                        <input type="checkbox" disabled />
                                      ) : (
                                        <input
                                          type="checkbox"
                                          checked={!!aiConversionSelected[`${row.value}|||${row.type}|||${(() => {
                                            const c = row.characters;
                                            if (typeof c === 'string') {
                                              if (c === 'N/A') return c;
                                              const p = parseInt(c || '0', 10);
                                              return Number.isNaN(p) ? c : p;
                                            }
                                            return c;
                                          })()}`]}
                                          onChange={(e) => {
                                            const c = row.characters;
                                            const charsKey = typeof c === 'string' ? (c === 'N/A' ? c : (Number.isNaN(parseInt(c || '0', 10)) ? c : parseInt(c || '0', 10))) : c;
                                            const key = `${row.value}|||${row.type}|||${charsKey}`;
                                            setAiConversionSelected(prev => ({ ...prev, [key]: e.target.checked }));
                                          }}
                                        />
                                      )}
                                    </td>
                                    <td>{row.value}</td>
                                    <td>
                                      <span className={`badge ${row.type === 'Risco' ? 'bg-danger' : 'bg-primary'}`}>
                                        {row.type}
                                      </span>
                                    </td>
                                    <td className="text-center">
                                      {row.isInvalid ? (
                                        <span className="text-danger fw-bold">N/A</span>
                                      ) : (
                                        <span className="badge bg-secondary">{row.characters}</span>
                                      )}
                                    </td>
                                    <td>
                                      {(() => {
                                        const key = `${row.value}|||${row.type}|||${row.characters}`;
                                        const isEditing = aiEditingItems.hasOwnProperty(key);

                                        if (isEditing) {
                                          return (
                                            <div className="input-group input-group-sm">
                                              <input
                                                type="text"
                                                className="form-control"
                                                value={aiEditingItems[key] || ''}
                                                onChange={(e) => {
                                                  const newValue = e.target.value.toUpperCase();
                                                  setAiEditingItems(prev => ({
                                                    ...prev,
                                                    [key]: newValue
                                                  }));
                                                }}
                                                maxLength={row.characters}
                                                style={{ fontFamily: 'monospace', textTransform: 'uppercase' }}
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    saveEditAiResult(row);
                                                  } else if (e.key === 'Escape') {
                                                    cancelEditAiResult(row);
                                                  }
                                                }}
                                                placeholder="Digite em maiúsculas"
                                                autoFocus
                                              />
                                              <span className="input-group-text">
                                                <small>{(aiEditingItems[key] || '').length}/{row.characters}</small>
                                              </span>
                                            </div>
                                          );
                                        } else {
                                          return <span className="text-muted">{row.resultado || '-'}</span>;
                                        }
                                      })()}
                                    </td>
                                    <td className="text-center">
                                      {(() => {
                                        if (!row.resultado || row.resultado === '-') {
                                          return <span className="text-muted">-</span>;
                                        }
                                        
                                        const resultLength = row.resultado.length;
                                        const maxLength = row.characters;
                                        
                                        if (row.isInvalid) {
                                          return <span className="text-muted">N/A</span>;
                                        }
                                        
                                        if (resultLength <= maxLength) {
                                          return (
                                            <span className="badge bg-success" title={`${resultLength}/${maxLength} caracteres`}>
                                              <i className="fas fa-check me-1"></i>
                                              OK ({resultLength}/{maxLength})
                                            </span>
                                          );
                                        } else {
                                          return (
                                            <span className="badge bg-danger" title={`${resultLength}/${maxLength} caracteres - Excedeu o limite!`}>
                                              <i className="fas fa-exclamation-triangle me-1"></i>
                                              ERRO ({resultLength}/{maxLength})
                                            </span>
                                          );
                                        }
                                      })()}
                                      {isDuplicateRow && (
                                        <div>
                                          <span className="badge bg-warning text-dark mt-1" title="Resultado duplicado para nomes diferentes">
                                            DUPLICADO
                                          </span>
                                        </div>
                                      )}
                                    </td>
                                    <td className="text-center">
                                      {(() => {
                                        const key = `${row.value}|||${row.type}|||${row.characters}`;
                                        const isEditing = aiEditingItems.hasOwnProperty(key);

                                        if (row.isInvalid) {
                                          return <span className="text-muted">-</span>;
                                        }

                                        if (isEditing) {
                                          return (
                                            <div className="btn-group btn-group-sm" style={{ gap: '2px' }}>
                                              <button
                                                type="button"
                                                className="btn btn-success btn-sm"
                                                title="Salvar alterações"
                                                onClick={() => saveEditAiResult(row)}
                                              >
                                                <i className="fas fa-check me-1"></i>
                                                Salvar
                                              </button>
                                              <button
                                                type="button"
                                                className="btn btn-secondary btn-sm"
                                                title="Cancelar edição"
                                                onClick={() => cancelEditAiResult(row)}
                                              >
                                                <i className="fas fa-times me-1"></i>
                                                Cancelar
                                              </button>
                                            </div>
                                          );
                                        } else {
                                          return (
                                            <button
                                              type="button"
                                              className="btn btn-outline-primary btn-sm"
                                              title="Editar resultado manualmente"
                                              onClick={() => startEditAiResult(row)}
                                            >
                                              <i className="fas fa-pen me-1"></i>
                                              Editar
                                            </button>
                                          );
                                        }
                                      })()}
                                    </td>
                                  </tr>
                                    );
                                  })()
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Actions for selection */}
                          <div className="d-flex gap-2 mb-3">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => setAiConversionSelected({})}
                              disabled={aiProcessing}
                            >
                              Limpar seleção
                            </button>
                          </div>

                          {/* Pagination */}
                          <div className="d-flex justify-content-between align-items-center mt-3">
                            <div>
                              <small className="text-muted">
                                Exibindo {total === 0 ? 0 : start + 1} a {start + pageItems.length} de {total} registros
                              </small>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <select 
                                className="form-select form-select-sm" 
                                value={aiConversionPageSize} 
                                onChange={e => { 
                                  const v = Number(e.target.value || '10'); 
                                  setAiConversionPageSize(v); 
                                  setAiConversionPage(1); 
                                }} 
                                style={{ width: 90 }}
                              >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                              </select>
                              <nav aria-label="Paginação">
                                <ul className="pagination pagination-sm mb-0">
                                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                    <button 
                                      className="page-link" 
                                      onClick={() => setAiConversionPage(page - 1)}
                                      disabled={page === 1}
                                    >
                                      Anterior
                                    </button>
                                  </li>
                                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    const startPage = Math.max(1, page - 2);
                                    const pageNum = startPage + i;
                                    if (pageNum > totalPages) return null;
                                    return (
                                      <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                                        <button 
                                          className="page-link" 
                                          onClick={() => setAiConversionPage(pageNum)}
                                        >
                                          {pageNum}
                                        </button>
                                      </li>
                                    );
                                  })}
                                  <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                    <button 
                                      className="page-link" 
                                      onClick={() => setAiConversionPage(page + 1)}
                                      disabled={page === totalPages}
                                    >
                                      Próximo
                                    </button>
                                  </li>
                                </ul>
                              </nav>
                            </div>
                          </div>
                        </>
                      );
                    })()
                  ) : (
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle me-2"></i>
                      Nenhum dado encontrado para {aiConversionActiveTab}.
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  {aiProcessing && (
                    <>
                      <div className="progress" style={{ width: '200px', height: '20px' }}>
                        <div 
                          className="progress-bar" 
                          role="progressbar" 
                          style={{ width: `${aiProcessingProgress}%` }}
                          aria-valuenow={aiProcessingProgress} 
                          aria-valuemin={0} 
                          aria-valuemax={100}
                        >
                          {aiProcessingProgress}%
                        </div>
                      </div>
                      <small className="text-muted">{aiProcessingStatus}</small>
                    </>
                  )}
                </div>
                <div className="d-flex gap-2">
                  {aiLogData && (
                    <button 
                      type="button" 
                      className="btn btn-info btn-sm"
                      onClick={() => setAiLogModalOpen(true)}
                    >
                      <i className="fas fa-file-text me-2"></i>
                      Log IA
                    </button>
                  )}
                  <button 
                    type="button" 
                    className="btn btn-danger btn-sm"
                    onClick={() => showAiConfirm('Tem certeza que deseja limpar todas as conversões de IA para este cliente? Esta ação não pode ser desfeita.', clearAiConversions)}
                    disabled={clearAiConversionsLoading}
                  >
                    {clearAiConversionsLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Limpando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash me-2"></i>
                        Limpar Conversões
                      </>
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setAiConversionModalOpen(false)}
                    disabled={aiProcessing}
                  >
                    Fechar
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={processAlgorithmicConversion}
                    disabled={aiProcessing}
                    title="Converter sem IA usando algoritmo local"
                  >
                    <i className="fas fa-cogs me-2"></i>
                    Processar sem IA
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={processAiConversion}
                    disabled={aiProcessing}
                  >
                    {aiProcessing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Processando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-robot me-2"></i>
                        Processar com IA
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Log Modal */}
      {aiLogModalOpen && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-file-text me-2"></i>
                  Log de Comunicação com IA
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setAiLogModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                {aiLogData ? (
                  <div>
                    <div className="mb-4">
                      <h6 className="text-primary">
                        <i className="fas fa-clock me-2"></i>
                        Timestamp: {new Date(aiLogData.timestamp).toLocaleString()}
                      </h6>
                      {aiLogData.deduplication && (
                        <div className="alert alert-info">
                          <h6 className="mb-2">
                            <i className="fas fa-compress-alt me-2"></i>
                            Deduplicação Aplicada
                          </h6>
                          <div className="row">
                            <div className="col-md-4">
                              <strong>Itens na página:</strong> {aiLogData.deduplication.totalItemsInPage}
                            </div>
                            <div className="col-md-4">
                              <strong>Textos únicos enviados:</strong> {aiLogData.deduplication.uniqueTexts}
                            </div>
                            <div className="col-md-4">
                              <strong>Duplicatas removidas:</strong> {aiLogData.deduplication.duplicatesRemoved}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mb-4">
                      <h6 className="text-success">
                        <i className="fas fa-arrow-up me-2"></i>
                        Requisição Enviada
                      </h6>
                      <div className="bg-light p-3 rounded" style={{ fontSize: '12px' }}>
                        <div className="mb-2">
                          <strong>URL:</strong> {aiLogData.request.url}
                        </div>
                        <div className="mb-2">
                          <strong>Payload:</strong>
                        </div>
                        <pre className="bg-white p-2 rounded border" style={{ maxHeight: '300px', overflow: 'auto' }}>
{JSON.stringify(aiLogData.request.payload, null, 2)}
                        </pre>
                        <div className="mb-2">
                          <strong>Prompt Completo:</strong>
                        </div>
                        <pre className="bg-white p-2 rounded border" style={{ maxHeight: '300px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
{aiLogData.request.prompt}
                        </pre>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h6 className="text-info">
                        <i className="fas fa-arrow-down me-2"></i>
                        Resposta Recebida
                      </h6>
                      <div className="bg-light p-3 rounded" style={{ fontSize: '12px' }}>
                        <div className="mb-2">
                          <strong>Status:</strong> {aiLogData.response.status}
                        </div>
                        <div className="mb-2">
                          <strong>Resposta Completa:</strong>
                        </div>
                        <pre className="bg-white p-2 rounded border" style={{ maxHeight: '400px', overflow: 'auto' }}>
{JSON.stringify(aiLogData.response.data, null, 2)}
                        </pre>
                        <div className="mb-2">
                          <strong>Texto da IA:</strong>
                        </div>
                        <pre className="bg-white p-2 rounded border" style={{ maxHeight: '300px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
{aiLogData.response.assistantText}
                        </pre>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p>Nenhum log disponível. Execute um processamento com IA primeiro.</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setAiLogModalOpen(false)}
                >
                  Fechar
                </button>
                {aiLogData && (
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={() => {
                      const dataStr = JSON.stringify(aiLogData, null, 2);
                      const blob = new Blob([dataStr], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `ai-log-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <i className="fas fa-download me-2"></i>
                    Baixar Log
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Confirmation Modal */}
      {aiConfirmModalOpen && (
        <div 
          className="modal show d-block" 
          tabIndex={-1} 
          role="dialog"
          style={{ zIndex: aiConversionModalOpen ? 1080 : undefined }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div 
              className="modal-content"
              style={{ zIndex: aiConversionModalOpen ? 1081 : undefined }}
            >
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-question-circle text-warning me-2"></i>
                  Confirmação
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  aria-label="Close" 
                  onClick={() => setAiConfirmModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>{aiConfirmMessage}</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setAiConfirmModalOpen(false)}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={() => {
                    setAiConfirmModalOpen(false);
                    if (aiConfirmCallback) {
                      aiConfirmCallback();
                    }
                  }}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de progresso de salvamento (usando estilo padrão do wizard) */}
      {savingModalOpen && savingProgress && (
        <div className="gp-modal-backdrop loading">
          <div className="gp-modal card p-4 text-center" style={{ maxWidth: '420px', width: '90%' }}>
            <div className="d-flex justify-content-center mb-3">
              <div className="spinner-border text-primary" role="status" style={{ width: 48, height: 48 }}>
                <span className="visually-hidden">Salvando...</span>
              </div>
            </div>
            
            <h5 className="mb-3">Salvando Classificação</h5>
            
            <div className="mb-2">
              <div className="progress" style={{ height: '8px' }}>
                <div 
                  className="progress-bar progress-bar-striped progress-bar-animated" 
                  role="progressbar" 
                  style={{ width: `${(savingProgress.current / savingProgress.total) * 100}%` }}
                  aria-valuenow={savingProgress.current} 
                  aria-valuemin={0} 
                  aria-valuemax={savingProgress.total}
                ></div>
              </div>
            </div>
            
            <div className="d-flex justify-content-between align-items-center mb-3">
              <small className="text-muted">{savingProgress.message}</small>
              <small className="text-primary fw-bold">
                {((savingProgress.current / savingProgress.total) * 100).toFixed(1)}%
              </small>
            </div>
            
            <div className="mb-3">
              <small className="text-muted">
                Registros: <strong>{savingProgress.current} / {savingProgress.total}</strong>
              </small>
            </div>

            <button 
              type="button" 
              className="btn btn-danger btn-sm"
              onClick={() => {
                savingCancelRef.current = true;
                setSavingModalOpen(false);
              }}
            >
              <i className="fas fa-times me-2"></i>
              Cancelar Salvamento
            </button>
          </div>
        </div>
      )}

      </div>
    </>
  );
};

export default GeradorPerfisWizard;
